// src/Pages/AdminDashboard/AdminDashboard.jsx
// Route : <Route path="/AdminDashboard" element={<AdminDashboard />} />
//
// Onglets identiques à SuperAdminDashboard.jsx (copie volontaire, cf. règle du repo :
// duplication de code entre dashboards plutôt que composants partagés), à l'exception de
// l'onglet "Gestion des droits" qui garde l'implémentation propre à Admin (portée utilisateur
// distincte du super_admin, cf. onglet plus bas).

import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useTabState } from "../../hooks/useTabState";
import { useNotifications } from "../../hooks/useNotifications";
import toast from "react-hot-toast";
import CloudinaryUpload, { AvatarUpload } from "../../Components/CloudinaryUpload";
import ImageCropModal from "../../Components/ImageCropModal";
import MessagerieTab from "../../Components/MessagerieTab";
import NotificationsTab from "../../Components/NotificationsTab";
import ArchiveTab, { ArchiveModal } from "../../Components/ArchiveTab";
import TestsNiveauTab from "../../Components/TestsNiveauTab";
import { exporterExcelStyle } from "../../utils/exporterExcel";
import NotificationBell from "../../Components/NotificationBell";
import BarreFiltres, { RangeePastilles, RangeeChamps, champFiltre } from "../../Components/BarreFiltres";
import DashboardColorPicker from "../../Components/DashboardColorPicker";
import { libelleTypeCours } from "../../utils/typeCours";
import { supabase } from "../../config/supabase";
import "./temoignages.css";
import { useConfirm, useAlerte } from "../../contexts/ConfirmContext";
import { useLanguage } from "../../contexts/LanguageContext";
import * as XLSX from "xlsx";
import FooterSaaS from "../../Components/FooterSaaS";
import { useLivePermissions } from "../../utils/permissions";
import { assombrirCouleur } from "../../utils/couleurs";
import { openElearningSSO } from "../../utils/ssoElearning";
import { clearAllSessionStorage } from "../../utils/session";
import "../../chartConfig";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { numeroPourLien } from "../../utils/numeroWhatsApp";
import MonProfilModal, { BoutonMonProfil } from "../../Components/MonProfilModal";

import { uploadFichier, maxMoPour } from "../../utils/uploadFichier";
import { fetchUpload } from "../../utils/fetchUpload";
import VisionneuseImage from "../../Components/VisionneuseImage";
import HistoriqueTestsNiveau from "../../Components/HistoriqueTestsNiveau";
import { exporterFicheApprenantPDF, ligneInfo, fmtDatePDF } from "../../utils/fichePdfApprenant";
import { libelleCanal } from "../../utils/canal";
import { noteSur10 } from "../../utils/categorieCoach";
import { hhmm } from "../../utils/heure";

import ModaleAnimee from "../../Components/ModaleAnimee";
const API_URL        = process.env.REACT_APP_API_URL        || "http://localhost:5001";
const FRONTEND_URL   = process.env.REACT_APP_FRONTEND_URL   || "http://localhost:3000";
const ELEARNING_URL  = process.env.REACT_APP_ELEARNING_URL  || "http://localhost:3002";
const authHeaders = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("admin_token")}` });
// Écrit une clé plateforme_config via le backend (protégé par requirePermission("configuration","update"))
// au lieu d'un upsert Supabase direct depuis le frontend, qui contournait tout contrôle de permission.
const savePlateformeConfig = (key, valeur) =>
  fetch(`${API_URL}/api/admin/plateforme-config/${key}`, { method:"PUT", headers:authHeaders(), body:JSON.stringify({ valeur }) })
    .then(r => { if (!r.ok) throw new Error("Échec de la sauvegarde"); return r.json(); });

/* ═══════════════════════════════════════════════════════
   CONSTANTES (chartre BET)
═══════════════════════════════════════════════════════ */
const BET_COLOR    = (() => { try { return JSON.parse(localStorage.getItem("admin_profil")||"{}").couleur||"#0891b2"; } catch { return "#0891b2"; } })();
const BET_DARK     = BET_COLOR;
const BET_LIGHT    = BET_COLOR + "18";
const BET_GRADIENT = `linear-gradient(135deg, #0f172a 0%, ${assombrirCouleur(BET_COLOR)} 100%)`;
const BET_RED      = "#dc2626";

const MOIS_COURT_SA = ["", "Jan","Fév","Mar","Avr","Mai","Juin","Juil","Août","Sep","Oct","Nov","Déc"];
// Les 12 derniers mois glissants (le plus ancien en premier), pour la courbe CA du tableau de bord.
function derniersMoisSA(n = 12) {
  const out = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ key: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`, label: `${MOIS_COURT_SA[d.getMonth()+1]} ${String(d.getFullYear()).slice(2)}` });
  }
  return out;
}
const CHART_COLORS_SA = ["#0891b2","#8b5cf6","#f59e0b","#22c55e","#dc2626","#0369a1","#ca8a04","#64748b"];
const chartOptsCommonSA = { responsive:true, maintainAspectRatio:false, plugins:{ legend:{ display:false } }, scales:{ y:{ ticks:{ font:{ size:10 } }, grid:{ color:"#f1f5f9" } }, x:{ ticks:{ font:{ size:10 } }, grid:{ display:false } } } };

/* ═══════════════════════════════════════════════════════
   COACH PHOTO CARD - composant stable (hors render principal)
═══════════════════════════════════════════════════════ */
const CoachPhotoCard = ({ coach, photoUrl, statut, hasChanges, uploading, saving, onFile, onRemove, onToggleStatut, onUrlChange, onNomChange, onTitreChange, onSave, peut }) => {
  const [hov, setHov] = React.useState(false);
  // Cadrage fixé sur celui de la carte coach du site public (CoachesSection.jsx : 200x240)
  // - recadrer ici garantit que l'aperçu correspond exactement à ce qui s'affichera.
  const [cropSrc, setCropSrc] = React.useState(null);
  const [cropFileName, setCropFileName] = React.useState("photo.jpg");
  const inputId = `coach-upload-${coach.id}`;
  return (
    <div style={{ background:"#fff", borderRadius:14, padding:16, border:`2px solid ${hasChanges?"#f59e0b":statut==="actif"?"#e0f2fe":"#fee2e2"}`, boxShadow:"0 1px 6px rgba(0,0,0,0.05)" }}>

      {/* Avatar + infos */}
      <div style={{ display:"flex", gap:14, alignItems:"flex-start", marginBottom:14 }}>
        <label
          htmlFor={inputId}
          onMouseEnter={() => setHov(true)}
          onMouseLeave={() => setHov(false)}
          style={{ position:"relative", width:80, height:80, borderRadius:12, overflow:"hidden", background:"#f1f5f9", flexShrink:0, border:"2px solid #e5e7eb", cursor:"pointer", display:"block" }}
          title="Cliquer pour changer la photo"
        >
          {photoUrl
            ? <img src={photoUrl} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} onError={e => { e.currentTarget.style.display="none"; }} />
            : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, color:"#cbd5e1" }}>👤</div>
          }
          <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.45)", display:"flex", alignItems:"center", justifyContent:"center", opacity:(hov||uploading)?1:0, transition:"opacity .2s" }}>
            <span style={{ fontSize:22 }}>{uploading ? "⏳" : "📷"}</span>
          </div>
          <input id={inputId} type="file" accept="image/*" style={{ display:"none" }}
            onChange={e => { const f = e.target.files?.[0]; if (f) { setCropFileName(f.name); setCropSrc(URL.createObjectURL(f)); } e.target.value = ""; }}
          />
        </label>
        {cropSrc && (
          <ImageCropModal
            imageSrc={cropSrc}
            aspect={200/240}
            fileName={cropFileName}
            onCancel={() => { URL.revokeObjectURL(cropSrc); setCropSrc(null); }}
            onConfirm={(fichierRecadre) => { onFile(fichierRecadre); URL.revokeObjectURL(cropSrc); setCropSrc(null); }}
          />
        )}
        <div style={{ flex:1, minWidth:0 }}>
          <input
            value={coach.nom || ""}
            onChange={e => onNomChange(e.target.value)}
            placeholder="Nom du coach"
            style={{ width:"100%", padding:"5px 8px", borderRadius:6, border:"1px solid #e2e8f0", fontSize:13, fontWeight:700, color:"#0f172a", boxSizing:"border-box", marginBottom:4 }}
          />
          <input
            value={coach.grade || ""}
            onChange={e => onTitreChange(e.target.value)}
            placeholder="Titre (ex : Coach BET)"
            style={{ width:"100%", padding:"5px 8px", borderRadius:6, border:"1px solid #e2e8f0", fontSize:12, color:"#64748b", boxSizing:"border-box", marginBottom:8 }}
          />
          <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, background:statut==="actif"?"#dcfce7":"#fee2e2", color:statut==="actif"?"#166534":"#991b1b" }}>
            {statut === "actif" ? "✅ Actif" : "❌ Inactif"}
          </span>
        </div>
      </div>

      {/* Boutons upload / retirer */}
      <div style={{ display:"flex", gap:8, marginBottom:10 }}>
        <label htmlFor={inputId} style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:6, padding:"8px 0", background:"#e0f2fe", color:"#0891b2", border:"1px solid #bae6fd", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>
          {uploading ? "⏳ Upload…" : "📷 Ajouter / changer la photo"}
        </label>
        {photoUrl && (
          <button onClick={onRemove} disabled={!peut("configuration","delete")} style={{ padding:"8px 12px", background:"#fee2e2", color:"#dc2626", border:"1px solid #fca5a5", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12, opacity:peut("configuration","delete")?1:0.5 }} title={!peut("configuration","delete")?"🔒 Vous n'avez pas la permission pour cette action":"Retirer la photo"}>
            🗑️ Retirer
          </button>
        )}
      </div>

      {/* URL manuelle */}
      <label style={{ fontSize:11, fontWeight:600, color:"#64748b", display:"block", marginBottom:4 }}>Ou coller une URL</label>
      <input
        value={photoUrl}
        onChange={e => onUrlChange(e.target.value)}
        placeholder="https://… ou /team1.jpeg"
        style={{ width:"100%", padding:"7px 10px", borderRadius:7, border:"1px solid #e2e8f0", fontSize:12, boxSizing:"border-box", marginBottom:10 }}
      />

      {/* Visibilité sur le site */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
        <span style={{ fontSize:12, fontWeight:600, color:"#374151" }}>Afficher sur le site</span>
        <button onClick={onToggleStatut} disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined} style={{ padding:"5px 14px", borderRadius:20, border:"none", cursor:"pointer", fontWeight:700, fontSize:12, background:statut==="actif"?"#dcfce7":"#f1f5f9", color:statut==="actif"?"#166534":"#64748b", opacity:peut("configuration","update")?1:0.5 }}>
          {statut === "actif" ? "✅ Visible" : "🔇 Masqué"}
        </button>
      </div>

      {/* Enregistrer - masqué tant qu'il n'y a rien à sauvegarder (inutile de garder un
          bouton grisé en permanence si aucune modification n'a été faite sur cette carte) */}
      {hasChanges && (
        <button
          onClick={onSave}
          disabled={saving || !peut("configuration","update")}
          title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
          style={{ width:"100%", padding:"9px 0", background:"#0891b2", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:13, transition:"background .2s", opacity:(saving||!peut("configuration","update"))?0.6:1 }}
        >
          {saving ? "⏳ Sauvegarde…" : !peut("configuration","update") ? "🔒 Enregistrer les modifications" : "💾 Enregistrer les modifications"}
        </button>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   PARTENAIRES MANAGER - composant autonome
═══════════════════════════════════════════════════════ */
function PartenairesManager({ apiUrl, authHeaders, peut }) {
  const confirm = useConfirm();
  const alerte = useAlerte();
  const [list,        setList]        = React.useState([]);
  const [loading,     setLoading]     = React.useState(true);
  const [saving,      setSaving]      = React.useState(false);
  const [uploading,   setUploading]   = React.useState(false);
  const [form,        setForm]        = React.useState({ nom:"", logo_url:"", site_web:"", ordre:0 });
  const [editId,      setEditId]      = React.useState(null);
  const [editVals,    setEditVals]    = React.useState({});
  const [searchQ,     setSearchQ]     = React.useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${apiUrl}/api/partenaires`, { headers: authHeaders() });
      if (r.ok) { const d = await r.json(); setList(d); }
    } catch {}
    finally { setLoading(false); }
  };

  React.useEffect(() => { load(); }, []);

  const uploadLogo = async (file, target) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await fetchUpload(`${apiUrl}/api/upload/image`, { method:"POST", headers:{ Authorization:`Bearer ${localStorage.getItem("admin_token")}` }, body:fd });
      const d = await r.json();
      const url = d.file?.url || d.url || d.publicUrl || d.path;
      if (target === "form") setForm(p=>({...p, logo_url:url}));
      else setEditVals(p=>({...p, logo_url:url}));
    } catch { await alerte("Erreur upload", { danger: true }); }
    finally { setUploading(false); }
  };

  const create = async () => {
    if (saving) return;
    if (!form.nom.trim() || !form.logo_url) { await alerte("Nom et logo requis", { danger: true }); return; }
    setSaving(true);
    try {
      const r = await fetch(`${apiUrl}/api/partenaires`, { method:"POST", headers:authHeaders(), body:JSON.stringify(form) });
      if (r.ok) { setForm({ nom:"", logo_url:"", site_web:"", ordre:0 }); await load(); }
    } catch {}
    finally { setSaving(false); }
  };

  const update = async (id) => {
    if (saving) return;
    setSaving(true);
    try {
      await fetch(`${apiUrl}/api/partenaires/${id}`, { method:"PATCH", headers:authHeaders(), body:JSON.stringify(editVals) });
      setEditId(null); setEditVals({});
      await load();
    } catch {}
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!(await confirm("Supprimer ce partenaire ?", { titre: "Supprimer le partenaire" }))) return;
    await fetch(`${apiUrl}/api/partenaires/${id}`, { method:"DELETE", headers:authHeaders() });
    await load();
  };

  const toggle = async (id, actif) => {
    await fetch(`${apiUrl}/api/partenaires/${id}`, { method:"PATCH", headers:authHeaders(), body:JSON.stringify({ actif:!actif }) });
    await load();
  };

  const inputSt = { padding:"8px 10px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:12, outline:"none", width:"100%", boxSizing:"border-box" };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <div>
          <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:"#0f172a" }}>🤝 Configuration Partenaires</h3>
          <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Logos affichés dans le carrousel partenaires de la page d'accueil</p>
        </div>
        <button onClick={load} style={{ padding:"8px 14px", background:"#e0f2fe", color:"#0369a1", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>🔄 Actualiser</button>
      </div>

      {/* Formulaire d'ajout */}
      <div style={{ background:"#f8fafc", borderRadius:14, padding:18, border:"1.5px dashed #cbd5e1", marginBottom:24 }}>
        <div style={{ fontSize:13, fontWeight:700, color:"#374151", marginBottom:12 }}>➕ Ajouter un partenaire</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr auto", gap:10, alignItems:"end" }}>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:"#64748b", display:"block", marginBottom:4 }}>Nom du partenaire *</label>
            <input value={form.nom} onChange={e=>setForm(p=>({...p,nom:e.target.value}))} placeholder="Ex : GIZ" style={inputSt} />
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:"#64748b", display:"block", marginBottom:4 }}>Site web</label>
            <input value={form.site_web} onChange={e=>setForm(p=>({...p,site_web:e.target.value}))} placeholder="https://..." style={inputSt} />
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:"#64748b", display:"block", marginBottom:4 }}>Ordre d'affichage</label>
            <input type="number" value={form.ordre} onChange={e=>setForm(p=>({...p,ordre:Number(e.target.value)}))} style={inputSt} />
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            <label style={{ fontSize:11, fontWeight:700, color:"#64748b" }}>Logo *</label>
            <label style={{ padding:"7px 12px", background:"#0891b2", color:"#fff", borderRadius:7, cursor:"pointer", fontSize:11, fontWeight:700, textAlign:"center", whiteSpace:"nowrap" }}>
              {uploading ? "…" : "📁 Choisir"}
              <input type="file" accept="image/*" style={{ display:"none" }} onChange={e=>e.target.files[0]&&uploadLogo(e.target.files[0],"form")} />
            </label>
          </div>
        </div>
        {form.logo_url && (
          <div style={{ marginTop:10, display:"flex", alignItems:"center", gap:12 }}>
            <img src={form.logo_url} alt="" style={{ height:40, maxWidth:120, objectFit:"contain", background:"#e2e8f0", borderRadius:6, padding:4 }} />
            <span style={{ fontSize:11, color:"#64748b", wordBreak:"break-all" }}>{form.logo_url}</span>
          </div>
        )}
        <button onClick={create} disabled={saving||uploading||!form.nom||!form.logo_url||!peut("configuration","create")}
          title={!peut("configuration","create") ? "🔒 Vous n'avez pas la permission pour cette action" : undefined}
          style={{ marginTop:14, padding:"9px 20px", background:"#0891b2", color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:13, cursor:"pointer", opacity:(saving||uploading||!form.nom||!form.logo_url||!peut("configuration","create"))?0.6:1 }}>
          {saving ? "Ajout…" : !peut("configuration","create") ? "🔒 Ajouter le partenaire" : "➕ Ajouter le partenaire"}
        </button>
      </div>

      {/* Recherche */}
      <input
        value={searchQ}
        onChange={e => setSearchQ(e.target.value)}
        placeholder="🔎 Rechercher un partenaire par nom…"
        style={{ ...inputSt, marginBottom:16 }}
      />

      {/* Liste */}
      {loading ? (
        <div style={{ textAlign:"center", padding:40, color:"#94a3b8" }}>⏳ Chargement…</div>
      ) : list.length === 0 ? (
        <div style={{ textAlign:"center", padding:32, color:"#94a3b8", fontSize:13 }}>Aucun partenaire ajouté pour l'instant.</div>
      ) : list.filter(p => (p.nom||"").toLowerCase().includes(searchQ.toLowerCase().trim())).length === 0 ? (
        <div style={{ textAlign:"center", padding:32, color:"#94a3b8", fontSize:13 }}>Aucun partenaire ne correspond à "{searchQ}".</div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:14 }}>
          {list.filter(p => (p.nom||"").toLowerCase().includes(searchQ.toLowerCase().trim())).map(p => {
            const isEditing = editId === p.id;
            const ev = editVals;
            return (
              <div key={p.id} style={{ background:"#fff", borderRadius:14, border:"1.5px solid #e2e8f0", overflow:"hidden", boxShadow:"0 2px 8px rgba(0,0,0,.05)" }}>
                <div style={{ height:3, background:p.actif?"#22c55e":"#e2e8f0" }} />
                <div style={{ padding:"14px 16px" }}>
                  {/* Logo */}
                  <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
                    <div style={{ width:64, height:40, display:"flex", alignItems:"center", justifyContent:"center", background:"#f8fafc", borderRadius:8, border:"1px solid #e2e8f0", flexShrink:0 }}>
                      <img src={isEditing&&ev.logo_url!==undefined?ev.logo_url:p.logo_url} alt={p.nom}
                        style={{ maxWidth:56, maxHeight:34, objectFit:"contain" }}
                        onError={e=>{ e.currentTarget.parentElement.style.background="#e2e8f0"; e.currentTarget.style.display="none"; }} />
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      {isEditing ? (
                        <input value={ev.nom!==undefined?ev.nom:p.nom} onChange={e=>setEditVals(v=>({...v,nom:e.target.value}))} style={{ ...inputSt, fontSize:13, fontWeight:700 }} />
                      ) : (
                        <div style={{ fontSize:14, fontWeight:800, color:"#0f172a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.nom}</div>
                      )}
                      <div style={{ fontSize:10, color:"#94a3b8", marginTop:2 }}>Ordre : {p.ordre}</div>
                    </div>
                    <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:999, background:p.actif?"#dcfce7":"#f3f4f6", color:p.actif?"#15803d":"#6b7280" }}>
                      {p.actif?"Actif":"Inactif"}
                    </span>
                  </div>

                  {isEditing && (
                    <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:10 }}>
                      <input value={ev.site_web!==undefined?ev.site_web:(p.site_web||"")} onChange={e=>setEditVals(v=>({...v,site_web:e.target.value}))} placeholder="Site web" style={inputSt} />
                      <div style={{ display:"flex", gap:6 }}>
                        <input type="number" value={ev.ordre!==undefined?ev.ordre:p.ordre} onChange={e=>setEditVals(v=>({...v,ordre:Number(e.target.value)}))} placeholder="Ordre" style={{...inputSt,width:80}} />
                        <label style={{ flex:1, padding:"7px 10px", background:"#e0f2fe", color:"#0369a1", borderRadius:7, cursor:"pointer", fontSize:11, fontWeight:700, textAlign:"center" }}>
                          {uploading?"…":"📁 Changer logo"}
                          <input type="file" accept="image/*" style={{ display:"none" }} onChange={e=>e.target.files[0]&&uploadLogo(e.target.files[0],"edit")} />
                        </label>
                      </div>
                    </div>
                  )}

                  {p.site_web && !isEditing && (
                    <div style={{ fontSize:11, color:"#0891b2", marginBottom:10, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>🔗 {p.site_web}</div>
                  )}

                  {/* Actions */}
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {isEditing ? (
                      <>
                        <button onClick={()=>update(p.id)} disabled={saving||!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined} style={{ flex:1, padding:"6px 10px", background:"#22c55e", color:"#fff", border:"none", borderRadius:7, fontWeight:700, fontSize:11, cursor:"pointer", opacity:peut("configuration","update")?1:0.5 }}>{saving?"…":"✓ Enregistrer"}</button>
                        <button onClick={()=>{setEditId(null);setEditVals({});}} style={{ padding:"6px 10px", background:"#e5e7eb", border:"none", borderRadius:7, fontWeight:700, fontSize:11, cursor:"pointer" }}>✕</button>
                      </>
                    ) : (
                      <>
                        <button onClick={()=>{setEditId(p.id);setEditVals({});}} disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined} style={{ flex:1, padding:"6px 10px", background:"#e0f2fe", color:"#0369a1", border:"none", borderRadius:7, fontWeight:700, fontSize:11, cursor:"pointer", opacity:peut("configuration","update")?1:0.5 }}>✏️ Modifier</button>
                        <button onClick={()=>toggle(p.id,p.actif)} disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined} style={{ padding:"6px 10px", background:p.actif?"#fef3c7":"#dcfce7", color:p.actif?"#d97706":"#16a34a", border:"none", borderRadius:7, fontWeight:700, fontSize:11, cursor:"pointer", opacity:peut("configuration","update")?1:0.5 }}>{p.actif?"⏸":"▶"}</button>
                        <button onClick={()=>remove(p.id)} disabled={!peut("configuration","delete")} title={!peut("configuration","delete")?"🔒 Vous n'avez pas la permission pour cette action":undefined} style={{ padding:"6px 10px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:7, fontWeight:700, fontSize:11, cursor:"pointer", opacity:peut("configuration","delete")?1:0.5 }}>🗑️</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   NEW COACH FORM - composant stable (hors render principal)
═══════════════════════════════════════════════════════ */
const NewCoachForm = ({ form, onFormChange, onPhotoFile, photoUploading, onSubmit, saving, peut }) => {
  const [open, setOpen]     = React.useState(false);
  const [hov, setHov]       = React.useState(false);
  const [cropSrc, setCropSrc]         = React.useState(null);
  const [cropFileName, setCropFileName] = React.useState("photo.jpg");
  const previewUrl = form.photo_url;

  return (
    <div style={{ background:"#f8fafc", border:"2px dashed #bae6fd", borderRadius:14, padding:20, marginBottom:24 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:20 }}>➕</span>
          <span style={{ fontWeight:700, fontSize:14, color:"#0f172a" }}>Ajouter un coach</span>
        </div>
        <button onClick={() => setOpen(o => !o)} style={{ padding:"6px 14px", background:open?"#e5e7eb":"#0891b2", color:open?"#374151":"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>
          {open ? "Annuler" : "+ Ajouter"}
        </button>
      </div>

      {open && (
        <div style={{ marginTop:18, display:"flex", gap:16, flexWrap:"wrap" }}>
          {/* Avatar upload */}
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:8 }}>
            <label htmlFor="new-coach-upload"
              onMouseEnter={() => setHov(true)}
              onMouseLeave={() => setHov(false)}
              style={{ position:"relative", width:100, height:100, borderRadius:14, overflow:"hidden", background:"#e0f2fe", border:"2px solid #7dd3fc", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}
              title="Cliquer pour choisir une photo"
            >
              {previewUrl
                ? <img src={previewUrl} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                : <span style={{ fontSize:36, color:"#7dd3fc" }}>👤</span>
              }
              <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.4)", display:"flex", alignItems:"center", justifyContent:"center", opacity:(hov||photoUploading)?1:0, transition:"opacity .2s" }}>
                <span style={{ fontSize:24 }}>{photoUploading ? "⏳" : "📷"}</span>
              </div>
              <input id="new-coach-upload" type="file" accept="image/*" style={{ display:"none" }}
                onChange={e => { const f = e.target.files?.[0]; if (f) { setCropFileName(f.name); setCropSrc(URL.createObjectURL(f)); } e.target.value = ""; }}
              />
            </label>
            {cropSrc && (
              <ImageCropModal
                imageSrc={cropSrc}
                aspect={200/240}
                fileName={cropFileName}
                onCancel={() => { URL.revokeObjectURL(cropSrc); setCropSrc(null); }}
                onConfirm={(fichierRecadre) => { onPhotoFile(fichierRecadre); URL.revokeObjectURL(cropSrc); setCropSrc(null); }}
              />
            )}
            <label htmlFor="new-coach-upload" style={{ fontSize:11, fontWeight:700, color:"#0891b2", cursor:"pointer" }}>
              {photoUploading ? "⏳ Upload…" : "📷 Choisir une photo"}
            </label>
          </div>

          {/* Champs */}
          <div style={{ flex:1, minWidth:200, display:"flex", flexDirection:"column", gap:10 }}>
            <div>
              <label style={{ fontSize:11, fontWeight:600, color:"#64748b", display:"block", marginBottom:3 }}>Nom (optionnel)</label>
              <input value={form.nom} onChange={e => onFormChange("nom", e.target.value)}
                placeholder="Ex : Aminata Koné" style={{ width:"100%", padding:"8px 10px", borderRadius:7, border:"1px solid #e2e8f0", fontSize:13, boxSizing:"border-box" }} />
            </div>
            <div>
              <label style={{ fontSize:11, fontWeight:600, color:"#64748b", display:"block", marginBottom:3 }}>Titre (optionnel)</label>
              <input value={form.titre} onChange={e => onFormChange("titre", e.target.value)}
                placeholder="Ex : Coach TOEIC Certifié" style={{ width:"100%", padding:"8px 10px", borderRadius:7, border:"1px solid #e2e8f0", fontSize:13, boxSizing:"border-box" }} />
            </div>
            <button onClick={onSubmit} disabled={saving || !form.photo_url || !peut("configuration","create")}
              title={!peut("configuration","create") ? "🔒 Vous n'avez pas la permission pour cette action" : undefined}
              style={{ padding:"9px 0", background:form.photo_url?"#0891b2":"#e5e7eb", color:form.photo_url?"#fff":"#9ca3af", border:"none", borderRadius:8, cursor:form.photo_url?"pointer":"default", fontWeight:700, fontSize:13, opacity:(saving||!peut("configuration","create"))?0.6:1 }}>
              {saving ? "⏳ Ajout en cours…" : !peut("configuration","create") ? "🔒 Ajouter cette photo" : form.photo_url ? "✅ Ajouter cette photo" : "⬆️ Uploadez d'abord une photo"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   COMPOSANTS RÉUTILISABLES (identiques à AdminDashboard)
═══════════════════════════════════════════════════════ */
const StatCard = ({ label, value, color, icon, sub, onClick }) => (
  <div onClick={onClick} style={{ background:"#fff", padding:16, borderRadius:12, boxShadow:"0 1px 6px rgba(0,0,0,0.06)", display:"flex", alignItems:"center", gap:12, cursor:onClick?"pointer":"default", transition:"transform .15s", border:"1px solid #f1f5f9" }}>
    <div style={{ width:46, height:46, borderRadius:10, background:color+"18", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>{icon}</div>
    <div style={{ minWidth:0 }}>
      <div style={{ fontSize:11, color:"#9ca3af" }}>{label}</div>
      <div style={{ fontSize:21, fontWeight:800, color, lineHeight:1.2 }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:"#9ca3af", marginTop:1 }}>{sub}</div>}
    </div>
  </div>
);

const ProgressBar = ({ value, color = BET_COLOR, height = 7 }) => (
  <div style={{ height, background:"#e5e7eb", borderRadius:height, overflow:"hidden" }}>
    <div style={{ height:"100%", width:`${Math.min(100, value)}%`, background:color, borderRadius:height, transition:"width .4s" }} />
  </div>
);

const ToggleSwitch = ({ on, onChange, color = BET_COLOR }) => (
  <div onClick={() => onChange(!on)} style={{ width:44, height:24, borderRadius:12, background:on?color:"#cbd5e1", cursor:"pointer", position:"relative", transition:"background .2s", flexShrink:0 }}>
    <div style={{ position:"absolute", width:20, height:20, borderRadius:"50%", background:"#fff", top:2, left:on?22:2, transition:"left .2s", boxShadow:"0 1px 2px rgba(0,0,0,0.1)" }} />
  </div>
);

const RoleBadge = ({ role }) => {
  const roles = {
    super_admin:    { label:"Super Admin",    color:BET_RED,    bg:"#fee2e2", emoji:"👑" },
    admin:          { label:"Administrateur", color:BET_COLOR,  bg:"#e0f2fe", emoji:"🔧" },
    manager:        { label:"Manager",        color:"#10b981",  bg:"#d1fae5", emoji:"👥" },
    commercial:     { label:"Commercial",     color:"#f59e0b",  bg:"#fef3c7", emoji:"📈" },
    gestionnaire:   { label:"Gestionnaire",   color:"#059669",  bg:"#dcfce7", emoji:"🗂️" },
    coach:          { label:"Coach",          color:"#6366f1",  bg:"#ede9fe", emoji:"🎓" },
    data_collector:      { label:"Data Collector",       color:"#64748b", bg:"#f1f5f9", emoji:"📊" },
    assistant_elearning: { label:"Assistant E-learning", color:"#7c3aed", bg:"#ede9fe", emoji:"💻" },
  };
  const r = roles[role] || { label: role || "Inconnu", color:"#64748b", bg:"#f1f5f9", emoji:"👤" };
  return <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, background:r.bg, color:r.color, display:"inline-flex", alignItems:"center", gap:4 }}>{r.emoji} {r.label}</span>;
};

const PermCheckbox = ({ on, onChange, color, disabled }) => (
  <div onClick={disabled ? undefined : () => onChange(!on)} style={{ width:28, height:28, borderRadius:8, background:on?color+"20":"#f3f4f6", border:`2px solid ${on?color:"#e5e7eb"}`, cursor:disabled?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", opacity:disabled?0.5:1 }}>
    {on && <span style={{ fontSize:16, color:color }}>✓</span>}
  </div>
);

const KpiCard = ({ icon, label, value, color, sub, alert, onClick }) => (
  <div onClick={onClick} style={{ background:"#fff", padding:14, borderRadius:12, border:`1px solid ${alert?"#fecaca":"#e5e7eb"}`, cursor:onClick?"pointer":"default", background:alert?"#fff8f8":"#fff" }}>
    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
      <span style={{ fontSize:20 }}>{icon}</span>
      <span style={{ fontSize:11, color:"#9ca3af" }}>{label}</span>
    </div>
    <div style={{ fontSize:24, fontWeight:800, color:color }}>{value}</div>
    {sub && <div style={{ fontSize:10, color:"#9ca3af", marginTop:4 }}>{sub}</div>}
  </div>
);

/* `ouvert` A UNE VALEUR PAR DÉFAUT, ET C'EST CE QUI REND LA CONVERSION SANS RISQUE.
   Les appels de cette coquille s'écrivent `{condition && <Modal …>}` : le parent monte et
   démonte lui-même. `ouvert = true` suffit donc à jouer l'OUVERTURE partout, sans toucher
   un seul appel. La FERMETURE, elle, ne s'animera qu'aux appels qui passeront un jour
   `ouvert={condition}` en retirant le `&&` — et ce changement-là n'est pas mécanique : des
   enfants qui lisent `condition.champ` planteraient pendant la sortie, quand la condition
   est déjà nulle mais que le panneau est encore monté. */
const Modal = ({ title, subtitle, onClose, children, danger, ouvert = true }) => (
  <ModaleAnimee ouvert={ouvert} onFermer={onClose}
    style={{ position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}
    panneauStyle={{ background:"#fff", borderRadius:16, width:"90%", maxWidth:"90vw", maxHeight:"90vh", overflowY:"auto", padding:24 }}>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
      <h3 style={{ margin:0, fontSize:18, fontWeight:800, color:danger?"#dc2626":"#0f172a" }}>{title}</h3>
      <button onClick={onClose} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#9ca3af" }}>✕</button>
    </div>
    {subtitle && <p style={{ margin:"0 0 16px", fontSize:12, color:"#9ca3af" }}>{subtitle}</p>}
    {children}
  </ModaleAnimee>
);

/* ══════════════════════════════════════════════════════
   SYSTÈME DE PROFILS BET
   Chaque profil = Département × Périmètre × Permissions
══════════════════════════════════════════════════════ */

// Les 6 types de profils administratifs BET
const PROFIL_TYPES = {
  super_admin: {
    id:"super_admin", label:"Super Admin", emoji:"👑", color:BET_RED,
    description:"Accès total à toute la plateforme. Un seul par organisation.",
    modules:["dashboard","users","roles","cours","examens","finances","support","audit","plateforme","coachs","apprenants","rh","commercial"],
    permsDefaut:{ create:true, read:true, update:true, delete:true, manage:true },
    unique:true,
  },
  admin_pedagogique: {
    id:"admin_pedagogique", label:"Admin Pédagogique", emoji:"📚", color:"#8b5cf6",
    description:"Gère les cours, coachs, apprenants, évaluations et ressources pédagogiques.",
    modules:["dashboard","cours","examens","coachs","apprenants"],
    permsDefaut:{ create:true, read:true, update:true, delete:false, manage:false },
  },
  admin_financier: {
    id:"admin_financier", label:"Admin Financier", emoji:"💰", color:"#059669",
    description:"Gère la facturation, les paiements, le chiffre d'affaires et les rapports financiers.",
    modules:["dashboard","finances","paiements","ca"],
    permsDefaut:{ create:true, read:true, update:true, delete:false, manage:false },
  },
  admin_rh: {
    id:"admin_rh", label:"Admin RH", emoji:"👤", color:"#d97706",
    description:"Gère les coachs : contrats, absences, paie, absences et remplacements.",
    modules:["dashboard","coachs","rh","absences"],
    permsDefaut:{ create:true, read:true, update:true, delete:false, manage:true },
  },
  admin_commercial: {
    id:"admin_commercial", label:"Admin Commercial", emoji:"📈", color:"#0891b2",
    description:"Gère les entreprises clientes, les prospects, les offres et le CRM.",
    modules:["dashboard","clients","offres","commercial"],
    permsDefaut:{ create:true, read:true, update:true, delete:false, manage:false },
  },
  responsable_centre: {
    id:"responsable_centre", label:"Responsable de Centre", emoji:"🏢", color:"#6366f1",
    description:"Supervise toutes les opérations d'un centre spécifique (pédagogie + planning + présences).",
    modules:["dashboard","cours","examens","coachs","apprenants","planning","presences"],
    permsDefaut:{ create:true, read:true, update:true, delete:false, manage:true },
  },
  observateur: {
    id:"observateur", label:"Observateur", emoji:"👁️", color:"#64748b",
    description:"Accès en lecture seule. Peut consulter mais pas modifier.",
    modules:["dashboard","cours","apprenants"],
    permsDefaut:{ create:false, read:true, update:false, delete:false, manage:false },
  },
};

// Centres BET
const CENTRES_BET = [
  { id:"angre",      label:"BET Angré - Abidjan" },
  { id:"2plateaux",  label:"BET II Plateaux - Abidjan" },
  { id:"yopougon",   label:"BET Yopougon - Abidjan" },
  { id:"koumassi",   label:"BET Koumassi - Abidjan" },
  { id:"abatta",     label:"BET Abatta - Abidjan" },
  { id:"bouake",     label:"BET Bouaké - Bouaké" },
];

// Utilisé par l'onglet "Gestion des droits" (repris tel quel d'AdminDashboard, cf. tableau
// utilisateurs) pour afficher le(s) centre(s) assigné(s) à un compte plutôt que ses ids bruts.
const scopeLabel = (scope) => {
  if (!scope || scope.length === 0 || scope.includes("national")) return null;
  return scope.map(s => CENTRES_BET.find(c => c.id === s)?.label.replace("BET ","") || s).join(", ");
};

// Seuls les rôles opérationnels de centre nécessitent un centre spécifique
// Admin, Manager, Gestionnaire, PA advisor = rôles transversaux/nationaux, pas de centre imposé
// Coach = pas de centre attribué non plus, contrairement à certains profils (les coachs
// interviennent sur plusieurs centres/en ligne, sans rattachement fixe).
const ROLES_AVEC_CENTRE = ["commercial"];

// Liste de tous les utilisateurs/profils
// Utilisateurs chargés depuis l'API (plus de mock)

// Logs globaux (audit super admin)
const GLOBAL_AUDIT = [
  { id:1, acteur:"Kouamé Aya", role:"super_admin", action:"ADMIN_CREE", detail:"Nouvel admin : Ibrahima Diallo", date:"2025-12-10 08:15", ip:"192.168.1.1", statut:"success" },
  { id:2, acteur:"Diallo Ibrahima", role:"admin", action:"PERMISSIONS_MODIFIEES", detail:"Rôle 'manager' modifié", date:"2025-12-09 14:22", ip:"192.168.1.23", statut:"warning" },
  { id:3, acteur:"Système", role:"system", action:"MAINTENANCE", detail:"Mise à jour de la base de données", date:"2025-12-08 03:00", ip:"internal", statut:"success" },
  { id:4, acteur:"Kouamé Aya", role:"super_admin", action:"CONFIGURATION_CHANGEE", detail:"Paramètres de sécurité renforcés", date:"2025-12-07 09:30", ip:"192.168.1.1", statut:"success" },
  { id:5, acteur:"Inconnu", role:"?", action:"TENTATIVE_ACCES", detail:"Tentative d'accès non autorisée", date:"2025-12-06 22:15", ip:"203.0.113.5", statut:"danger" },
];

// Rôles disponibles (étendus)
const ROLES_DEF = {
  super_admin:          { id:"super_admin",          label:"Super Admin",           emoji:"👑", color:BET_RED,   border:"#fecaca", niveau:7, description:"Accès total, toutes permissions, non modifiable" },
  admin:                { id:"admin",                label:"Administrateur",        emoji:"🔧", color:BET_COLOR, border:"#bae6fd", niveau:6, description:"Gestion complète sauf paramètres critiques" },
  manager:              { id:"manager",              label:"Manager",               emoji:"👥", color:"#10b981", border:"#a7f3d0", niveau:5, description:"Supervision et reporting" },
  superviseur:          { id:"superviseur",          label:"Superviseur Global",    emoji:"🔭", color:"#0284c7", border:"#bae6fd", niveau:5, description:"Superviseur global - contrôle opérationnel, supervise les Deputy Superviseurs et les équipes" },
  deputy_superviseur:   { id:"deputy_superviseur",   label:"Deputy Superviseur",    emoji:"🔍", color:"#0369a1", border:"#bae6fd", niveau:4, description:"Sous les ordres du Superviseur Global - supervision opérationnelle déléguée par centre" },
  pedagogical_advisor:  { id:"pedagogical_advisor",  label:"Pedagogical Advisor",   emoji:"🧑‍🏫", color:"#7c3aed", border:"#ede9fe", niveau:4, description:"Classes privées + Validation des honoraires enseignants" },
  commercial:           { id:"commercial",           label:"Commercial",            emoji:"📈", color:"#f59e0b", border:"#fcd34d", niveau:3, description:"CRM, inscriptions et ventes" },
  onboarding:           { id:"onboarding",           label:"Assistant Onboarding",  emoji:"🚀", color:"#0891b2", border:"#a5f3fc", niveau:3, description:"Création de classes (cours en groupe) et attribution d'enseignants" },
  gestionnaire:         { id:"gestionnaire",         label:"Gestionnaire",          emoji:"🗂️", color:"#059669", border:"#6ee7b7", niveau:3, description:"Administratif, finances, planning" },
  rh:                   { id:"rh",                   label:"Espace RH / Paie",      emoji:"👔", color:"#db2777", border:"#fbcfe8", niveau:3, description:"Gestion RH, contrats, fiches de paie et congés" },
  comptable:            { id:"comptable",            label:"Comptable / Trésorier", emoji:"🧾", color:"#d97706", border:"#fde68a", niveau:3, description:"Comptabilité, trésorerie et rapports financiers" },
  coach:                { id:"coach",                label:"Coach",                 emoji:"🎓", color:"#6366f1", border:"#a5b4fc", niveau:2, description:"Pédagogie, cours et examens" },
  customer_care:        { id:"customer_care",        label:"Customer Care",         emoji:"🎧", color:"#0e7490", border:"#a5f3fc", niveau:2, description:"Prise en charge, suivi et résolution des plaintes clients" },
  assistant_elearning:  { id:"assistant_elearning",  label:"Assistant E-learning",  emoji:"💻", color:"#7c3aed", border:"#ede9fe", niveau:2, description:"Gestion de la plateforme e-learning - aucun centre physique attribué" },
  data_collector:       { id:"data_collector",       label:"Data Collector",        emoji:"📊", color:"#64748b", border:"#e2e8f0", niveau:1, description:"Saisie de données uniquement" },
  placement_test:       { id:"placement_test",       label:"Agent Placement Test",  emoji:"🧪", color:"#0d9488", border:"#99f6e4", niveau:2, description:"Réception des dossiers, tests de niveau et placement des apprenants" },
  // ── Rôles BET E-Learning ──
  admin_onboarding:     { id:"admin_onboarding",     label:"Agent Onboarding",       emoji:"📥", color:"#0891b2", border:"#a5f3fc", niveau:3, description:"Suivi des inscriptions et onboarding apprenants e-learning" },
  admin_elearning:      { id:"admin_elearning",      label:"Admin E-Learning",       emoji:"🎓", color:"#d97706", border:"#fde68a", niveau:4, description:"Administration complète de la plateforme e-learning BET" },
  admin_customer_care:  { id:"admin_customer_care",  label:"Customer Care E-Learning",emoji:"🎧", color:"#0e7490", border:"#a5f3fc", niveau:2, description:"Support et suivi des apprenants e-learning" },
  admin_commercial:     { id:"admin_commercial",     label:"Commercial Entreprises", emoji:"🏢", color:"#f59e0b", border:"#fcd34d", niveau:3, description:"Gestion des contrats entreprises et inscriptions B2B" },
  admin_pa_advisor:     { id:"admin_pa_advisor",     label:"PA Advisor",             emoji:"🧑‍🏫", color:"#7c3aed", border:"#c4b5fd", niveau:3, description:"Conseil pédagogique et suivi des apprenants e-learning" },
  admin_rh_paie:        { id:"admin_rh_paie",        label:"RH / Paie E-Learning",  emoji:"👔", color:"#db2777", border:"#fbcfe8", niveau:3, description:"Gestion RH et paie pour la plateforme e-learning" },
  admin_comptable:      { id:"admin_comptable",      label:"Comptable E-Learning",  emoji:"🧾", color:"#d97706", border:"#fde68a", niveau:3, description:"Comptabilité et facturation e-learning" },
};


/* ── Données mock Coachs ── */
const COACHS_MOCK = [
  { id:1, nom:"James Adou",        initiales:"JA", specialite:"TOEIC / Business English", centre:"Angré",       classes:3, apprenants:34, tauxPresence:87, statut:"actif",    prochainCours:"Mer 30/04 · 18h00", certif:"CELTA · TOEIC 990", email:"j.adou@bet.ci",     tel:"+225 07 11 22 33", dateEmb:"2022-03-01",
    planning:[
      { jour:"Lun", horaire:"08h00–10h00", classe:"TOEIC B1→B2",  salle:"Salle A3", apprenants:11 },
      { jour:"Mer", horaire:"18h00–20h00", classe:"TOEIC B1→B2",  salle:"Salle A3", apprenants:11 },
      { jour:"Ven", horaire:"10h00–12h00", classe:"TOEIC B2→C1",  salle:"Salle B1", apprenants:9  },
    ],
    examens:[
      { titre:"Test blanc TOEIC #3", date:"10/04/25", classe:"TOEIC B1→B2", nbParticipants:11, noteMoy:"72/100", statut:"Corrigé" },
      { titre:"Test blanc TOEIC #3", date:"10/04/25", classe:"TOEIC B2→C1", nbParticipants:9,  noteMoy:"81/100", statut:"Corrigé" },
      { titre:"Éval intermédiaire",  date:"25/04/25", classe:"TOEIC B1→B2", nbParticipants:11, noteMoy:"-",      statut:"À venir"  },
    ],
  },
  { id:2, nom:"Aminata Koné",      initiales:"AK", specialite:"Anglais Adultes / Conversation", centre:"II Plateaux", classes:2, apprenants:26, tauxPresence:91, statut:"actif",    prochainCours:"Lun 28/04 · 10h00", certif:"DELTA · DALF", email:"a.kone@bet.ci",       tel:"+225 07 44 55 66", dateEmb:"2021-09-15",
    planning:[
      { jour:"Lun", horaire:"10h00–12h00", classe:"Adultes A2→B1",  salle:"Salle C2", apprenants:14 },
      { jour:"Jeu", horaire:"16h00–18h00", classe:"Conversation B2", salle:"Salle C1", apprenants:12 },
    ],
    examens:[
      { titre:"Éval mensuelle",     date:"15/04/25", classe:"Adultes A2→B1",  nbParticipants:14, noteMoy:"68/100", statut:"Corrigé" },
      { titre:"Éval mensuelle",     date:"26/04/25", classe:"Conversation B2", nbParticipants:12, noteMoy:"-",      statut:"À venir"  },
    ],
  },
  { id:3, nom:"Moussa Bamba",      initiales:"MB", specialite:"Anglais Enfants / Junior", centre:"Bouaké",     classes:4, apprenants:52, tauxPresence:83, statut:"actif",    prochainCours:"Sam 27/04 · 09h00", certif:"TEFL · YL Cert",   email:"m.bamba@bet.ci",      tel:"+225 07 77 88 99", dateEmb:"2023-01-10",
    planning:[
      { jour:"Sam", horaire:"09h00–11h00", classe:"Enfants 8–10 ans", salle:"Salle J1", apprenants:15 },
      { jour:"Sam", horaire:"11h00–13h00", classe:"Enfants 11–13 ans",salle:"Salle J2", apprenants:13 },
      { jour:"Mer", horaire:"14h00–16h00", classe:"Junior A1",        salle:"Salle J1", apprenants:12 },
      { jour:"Mer", horaire:"16h00–18h00", classe:"Junior A2",        salle:"Salle J2", apprenants:12 },
    ],
    examens:[
      { titre:"Contrôle trimestriel", date:"05/04/25", classe:"Enfants 8–10 ans",  nbParticipants:15, noteMoy:"74/100", statut:"Corrigé" },
      { titre:"Contrôle trimestriel", date:"05/04/25", classe:"Junior A1",         nbParticipants:12, noteMoy:"70/100", statut:"Corrigé" },
      { titre:"Contrôle trimestriel", date:"30/04/25", classe:"Enfants 11–13 ans", nbParticipants:13, noteMoy:"-",      statut:"À venir"  },
    ],
  },
  { id:4, nom:"Isabelle Yao",      initiales:"IY", specialite:"IELTS / Séjours linguistiques", centre:"Angré", classes:2, apprenants:18, tauxPresence:94, statut:"actif",    prochainCours:"Mar 29/04 · 17h00", certif:"IELTS 8.5 · CELTA", email:"i.yao@bet.ci",       tel:"+225 07 00 11 22", dateEmb:"2020-06-01",
    planning:[
      { jour:"Mar", horaire:"17h00–19h00", classe:"IELTS Prep B2", salle:"Salle B3", apprenants:10 },
      { jour:"Ven", horaire:"08h00–10h00", classe:"IELTS Prep C1", salle:"Salle B3", apprenants:8  },
    ],
    examens:[
      { titre:"Mock IELTS #2",  date:"12/04/25", classe:"IELTS Prep B2", nbParticipants:10, noteMoy:"6.5/9", statut:"Corrigé" },
      { titre:"Mock IELTS #2",  date:"12/04/25", classe:"IELTS Prep C1", nbParticipants:8,  noteMoy:"7.2/9", statut:"Corrigé" },
      { titre:"Mock IELTS #3",  date:"03/05/25", classe:"IELTS Prep B2", nbParticipants:10, noteMoy:"-",     statut:"À venir"  },
    ],
  },
  { id:5, nom:"David Assoumou",    initiales:"DA", specialite:"Anglais Entreprise / Corporate", centre:"Angré", classes:3, apprenants:48, tauxPresence:78, statut:"conge",   prochainCours:"Retour 05/05",      certif:"MBA · CELTA",      email:"d.assoumou@bet.ci",   tel:"+225 07 33 44 55", dateEmb:"2019-04-20",
    planning:[], examens:[],
  },
  { id:6, nom:"Rosine Ouattara",   initiales:"RO", specialite:"Anglais Adultes / Entreprises", centre:"II Plateaux", classes:1, apprenants:14, tauxPresence:89, statut:"actif",    prochainCours:"Jeu 01/05 · 18h30", certif:"CELTA",             email:"r.ouattara@bet.ci",   tel:"+225 07 66 77 88", dateEmb:"2023-09-01",
    planning:[
      { jour:"Jeu", horaire:"18h30–20h30", classe:"Entreprise Orange CI", salle:"Salle D1", apprenants:14 },
    ],
    examens:[
      { titre:"Éval mensuelle", date:"30/04/25", classe:"Entreprise Orange CI", nbParticipants:14, noteMoy:"-", statut:"À venir" },
    ],
  },
];

// Profil du Super Admin
const SUPER_ADMIN_PROFIL = {
  id:1, nom:"Kouamé", prenom:"Aya", email:"aya@bet.com", role:"super_admin", avatar:"AK", tel:"+225 01 00 00 01", dateEmbauche:"2020-01-01", dernierAcces:"2025-12-14 08:30", permissions:"totales"
};


// ── Helpers stables pour le modal Certifications ──────────────────────────
// Définis au niveau module (pas dans le rendu) pour éviter le re-montage à chaque frappe
// ── Helpers stables pour le modal Aperçu Cours ───────────────────────────
const ApercuStringList = ({ modal, setModal, field, placeholder }) => {
  const list = modal?.data?.[field] || [];
  const setD = updater => setModal(m => ({ ...m, data: updater(m.data) }));
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      {list.map((item, i) => (
        <div key={i} style={{ display:"flex", gap:6, alignItems:"center" }}>
          <input value={item} onChange={e => { const u=[...list]; u[i]=e.target.value; setD(prev => ({...prev,[field]:u})); }}
            placeholder={placeholder} style={{ flex:1, padding:"7px 10px", border:"1px solid #e5e7eb", borderRadius:7, fontSize:12, boxSizing:"border-box" }} />
          <button onClick={() => setD(prev => ({...prev,[field]:list.filter((_,j)=>j!==i)}))}
            style={{ width:28, height:28, border:"none", borderRadius:7, background:"#fee2e2", color:"#dc2626", cursor:"pointer", fontSize:13, flexShrink:0 }}>✕</button>
        </div>
      ))}
      <button onClick={() => setD(prev => ({...prev,[field]:[...list,""]}))}
        style={{ alignSelf:"flex-start", padding:"6px 12px", background:"#f1f5f9", border:"1px dashed #94a3b8", borderRadius:7, fontSize:11, color:"#475569", cursor:"pointer", fontWeight:600 }}>➕ Ajouter</button>
    </div>
  );
};
const ApercuObjList = ({ modal, setModal, field, keys, placeholders }) => {
  const list = modal?.data?.[field] || [];
  const setD = updater => setModal(m => ({ ...m, data: updater(m.data) }));
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      {list.map((item, i) => (
        <div key={i} style={{ background:"#f8fafc", border:"1px solid #e5e7eb", borderRadius:8, padding:"10px 12px" }}>
          <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:6 }}>
            <button onClick={() => setD(prev => ({...prev,[field]:list.filter((_,j)=>j!==i)}))}
              style={{ width:24, height:24, border:"none", borderRadius:6, background:"#fee2e2", color:"#dc2626", cursor:"pointer", fontSize:12 }}>✕</button>
          </div>
          {keys.map((k, ki) => (
            <div key={k} style={{ marginBottom:ki < keys.length-1 ? 8 : 0 }}>
              <label style={{ fontSize:11, fontWeight:600, color:"#374151", display:"block", marginBottom:3 }}>{k}</label>
              <input value={item[k]||""} onChange={e => { const u=list.map((x,j)=>j===i?{...x,[k]:e.target.value}:x); setD(prev => ({...prev,[field]:u})); }}
                placeholder={placeholders?.[ki]||""} style={{ width:"100%", padding:"7px 10px", border:"1px solid #e5e7eb", borderRadius:7, fontSize:12, boxSizing:"border-box" }} />
            </div>
          ))}
        </div>
      ))}
      <button onClick={() => { const empty={}; keys.forEach(k=>empty[k]=""); setD(prev => ({...prev,[field]:[...list,empty]})); }}
        style={{ alignSelf:"flex-start", padding:"6px 12px", background:"#f1f5f9", border:"1px dashed #94a3b8", borderRadius:7, fontSize:11, color:"#475569", cursor:"pointer", fontWeight:600 }}>➕ Ajouter</button>
    </div>
  );
};

// ── Helpers stables pour le modal Certifications ──────────────────────────
const CertifFieldInput = ({ modal, setModal, label, field, path, placeholder, multiline, type="text" }) => {
  const val = path ? (modal?.data?.[path]?.[field] ?? "") : (modal?.data?.[field] ?? "");
  const onChange = e => {
    const v = type === "number" ? Number(e.target.value) : e.target.value;
    if (path) { setModal(m => ({...m, data:{...m.data, [path]:{...m.data[path],[field]:v}}})); }
    else       { setModal(m => ({...m, data:{...m.data,[field]:v}})); }
  };
  return (
    <div>
      <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>{label}</label>
      {multiline
        ? <textarea value={val} onChange={onChange} placeholder={placeholder} rows={3}
            style={{ width:"100%", padding:"9px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, boxSizing:"border-box", resize:"vertical" }} />
        : <input value={val} onChange={onChange} placeholder={placeholder} type={type}
            style={{ width:"100%", padding:"9px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, boxSizing:"border-box" }} />
      }
    </div>
  );
};
const CertifStringList = ({ modal, setModal, field, placeholder }) => {
  const list = modal?.data?.[field] || [];
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      {list.map((item, i) => (
        <div key={i} style={{ display:"flex", gap:6, alignItems:"center" }}>
          <input value={item} onChange={e => { const u=[...list]; u[i]=e.target.value; setModal(m=>({...m,data:{...m.data,[field]:u}})); }}
            placeholder={placeholder} style={{ flex:1, padding:"7px 10px", border:"1px solid #e5e7eb", borderRadius:7, fontSize:12, boxSizing:"border-box" }} />
          <button onClick={() => { const u=list.filter((_,j)=>j!==i); setModal(m=>({...m,data:{...m.data,[field]:u}})); }}
            style={{ width:28, height:28, border:"none", borderRadius:7, background:"#fee2e2", color:"#dc2626", cursor:"pointer", fontSize:13, flexShrink:0 }}>✕</button>
        </div>
      ))}
      <button onClick={() => setModal(m=>({...m,data:{...m.data,[field]:[...list,""]}}))}
        style={{ alignSelf:"flex-start", padding:"6px 12px", background:"#f1f5f9", border:"1px dashed #94a3b8", borderRadius:7, fontSize:11, color:"#475569", cursor:"pointer", fontWeight:600 }}>
        ➕ Ajouter
      </button>
    </div>
  );
};
const CertifObjList = ({ modal, setModal, field, fields }) => {
  const list = modal?.data?.[field] || [];
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      {list.map((item, i) => (
        <div key={i} style={{ background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:10, padding:"10px 12px" }}>
          <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:6 }}>
            <button onClick={() => { const u=list.filter((_,j)=>j!==i); setModal(m=>({...m,data:{...m.data,[field]:u}})); }}
              style={{ padding:"3px 8px", border:"none", borderRadius:5, background:"#fee2e2", color:"#dc2626", cursor:"pointer", fontSize:11, fontWeight:700 }}>🗑️ Supprimer</button>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:8 }}>
            {fields.map(f => (
              <div key={f.key}>
                <label style={{ fontSize:11, fontWeight:600, color:"#374151", display:"block", marginBottom:3 }}>{f.label}</label>
                {f.multiline
                  ? <textarea value={item[f.key]||""} rows={2} onChange={e => { const u=[...list]; u[i]={...u[i],[f.key]:e.target.value}; setModal(m=>({...m,data:{...m.data,[field]:u}})); }}
                      style={{ width:"100%", padding:"6px 9px", border:"1px solid #e5e7eb", borderRadius:6, fontSize:12, resize:"vertical", boxSizing:"border-box" }} />
                  : <input value={item[f.key]||""} onChange={e => { const u=[...list]; u[i]={...u[i],[f.key]:e.target.value}; setModal(m=>({...m,data:{...m.data,[field]:u}})); }}
                      style={{ width:"100%", padding:"6px 9px", border:"1px solid #e5e7eb", borderRadius:6, fontSize:12, boxSizing:"border-box" }} />
                }
              </div>
            ))}
          </div>
        </div>
      ))}
      <button onClick={() => { const empty={}; fields.forEach(f=>{empty[f.key]="";}); setModal(m=>({...m,data:{...m.data,[field]:[...list,empty]}})); }}
        style={{ alignSelf:"flex-start", padding:"6px 12px", background:"#f1f5f9", border:"1px dashed #94a3b8", borderRadius:7, fontSize:11, color:"#475569", cursor:"pointer", fontWeight:600 }}>
        ➕ Ajouter
      </button>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   MODAL DÉTAIL PROFIL ADMINISTRATION
═══════════════════════════════════════════════════════ */
function ModalDetailAdmin({ profil: profilInitial, onFermer, onProfilUpdate }) {
  const alerte = useAlerte();
  const [tab, setTab]           = React.useState("infos");
  const [profil, setProfil]     = React.useState(profilInitial);
  const [editMode, setEditMode] = React.useState(false);
  const [editData, setEditData] = React.useState(null);
  const [saving,   setSaving]   = React.useState(false);
  const [uploadingPhoto, setUploadingPhoto] = React.useState(false);
  const [uploadingCV,    setUploadingCV]    = React.useState(false);
  const [cvUrl,          setCvUrl]          = React.useState(profil.cv_url || null);
  const [coachNotesModal, setCoachNotesModal] = React.useState(null);
  const [equipePage,       setEquipePage]      = React.useState(0);
  const [notesDateFrom,    setNotesDateFrom]   = React.useState("");
  const [notesDateTo,      setNotesDateTo]     = React.useState("");
  const [notesPage,        setNotesPage]       = React.useState(0);
  const [rapportsDateFrom, setRapportsDateFrom] = React.useState("");
  const [rapportsDateTo,   setRapportsDateTo]   = React.useState("");
  const [rapportsPage,     setRapportsPage]     = React.useState(0);

  // Onglet Paie - filtre période, pagination, modal détail
  const [paiePeriode,  setPaiePeriode]  = React.useState("Janvier 2025");
  const [paiePage,     setPaiePage]     = React.useState(0);
  const [paieDetail,   setPaieDetail]   = React.useState(null);
  const PAIE_PER_PAGE = 5;

  // Onglet Contrats RH - filtre type, pagination, modal détail
  const [contratTypeFilter, setContratTypeFilter] = React.useState("Tous");
  const [contratPage,       setContratPage]       = React.useState(0);
  const [contratDetail,     setContratDetail]     = React.useState(null);
  const CONTRAT_PER_PAGE = 5;

  // Onglet Paiements Comptable - filtre période, pagination, modal détail
  const [pmtCPeriode,  setPmtCPeriode]  = React.useState("Tous");
  const [pmtCPage,     setPmtCPage]     = React.useState(0);
  const [pmtCDetail,   setPmtCDetail]   = React.useState(null);
  const PMT_C_PER_PAGE = 5;

  // Onglet Factures Comptable - filtre période, pagination, modal détail
  const [factPeriode,  setFactPeriode]  = React.useState("Tous");
  const [factPage,     setFactPage]     = React.useState(0);
  const [factDetail,   setFactDetail]   = React.useState(null);
  const FACT_PER_PAGE = 5;

  // Onglet Tickets Customer Care - filtre période + statut, pagination, modal détail
  const [ticketPeriode,  setTicketPeriode]  = React.useState("Tous");
  const [ticketStatut,   setTicketStatut]   = React.useState("Tous");
  const [ticketPage,     setTicketPage]     = React.useState(0);
  const [ticketDetail,   setTicketDetail]   = React.useState(null);
  const TICKET_PER_PAGE = 5;

  // Onglet Vue Nationale (superviseur) - filtre période + modal détail centre
  const [vnPeriode,      setVnPeriode]      = React.useState("Juin 2025");
  const [vnCentreDetail, setVnCentreDetail] = React.useState(null);

  // Onglet Deputies (superviseur) - filtre période + statut, pagination, modal détail
  const [depPeriode,  setDepPeriode]  = React.useState("Juin 2025");
  const [depStatut,   setDepStatut]   = React.useState("Tous");
  const [depPage,     setDepPage]     = React.useState(0);
  const [depDetail,   setDepDetail]   = React.useState(null);
  const DEP_PER_PAGE = 6;

  // Onglet Escalades (superviseur) - filtre période + sévérité + statut, pagination, modal détail
  const [escPeriode,  setEscPeriode]  = React.useState("Tous");
  const [escSeverite, setEscSeverite] = React.useState("Tous");
  const [escStatut,   setEscStatut]   = React.useState("Tous");
  const [escPage,     setEscPage]     = React.useState(0);
  const [escDetail,   setEscDetail]   = React.useState(null);
  const ESC_PER_PAGE = 5;

  // Onglet Comptable - données réelles (dash_comptabilite_*), null = pas encore chargé
  const [paiementsCReel,   setPaiementsCReel]   = React.useState(null);
  const [facturesCReel,    setFacturesCReel]    = React.useState(null);
  const [tresorerieCReel,  setTresorerieCReel]  = React.useState(null);
  const [mouvementsCReel,  setMouvementsCReel]  = React.useState(null);

  // Onglets Commercial - données réelles (paiements_parcours + assignations_parcours), null = pas encore chargé
  const [commercialAssignationsReel, setCommercialAssignationsReel] = React.useState(null);
  const [commercialPaiementsReel,    setCommercialPaiementsReel]    = React.useState(null);

  // Onglets RH - données réelles (honoraires_mois, utilisateurs, grille tarifaire), null = pas encore chargé
  const [rhHonorairesReel,    setRhHonorairesReel]    = React.useState(null);
  const [rhUtilisateursReel,  setRhUtilisateursReel]  = React.useState(null);
  const [rhGrilleReel,        setRhGrilleReel]        = React.useState(null);

  // Onglets Onboarding - données réelles (assignations_parcours filtrées par onboarding_id), null = pas encore chargé
  const [onboardingAssignationsReel, setOnboardingAssignationsReel] = React.useState(null);

  // Onglet PA Advisor - cours privés réels (contrats_prives), null = pas encore chargé
  const [paContratsPrivesReel, setPaContratsPrivesReel] = React.useState(null);

  // Onglets Superviseur/Deputy - données réelles (coachs + séances du mois + centres), null = pas encore chargé
  const [svCoachsReel,   setSvCoachsReel]   = React.useState(null);
  const [svSeancesReel,  setSvSeancesReel]  = React.useState(null);
  const [svCentresReel,  setSvCentresReel]  = React.useState(null);
  const [svDeputiesReel, setSvDeputiesReel] = React.useState(null);

  const [dcLeadsReel,      setDcLeadsReel]      = React.useState(null);
  const [adminCentresReel, setAdminCentresReel] = React.useState(null);
  const [adminActiviteReel, setAdminActiviteReel] = React.useState(null);
  const [ccTicketsReel,    setCcTicketsReel]    = React.useState(null);

  const r        = ROLES_DEF[profil.role] || { label: profil.role, emoji: "👤", color: BET_COLOR, description: "" };
  const couleur  = r.color;
  const initials = ((profil.prenom?.[0] || "") + (profil.nom?.[0] || "")).toUpperCase() || "??";
  // Le surnom devient le titre du dashboard ; le nom complet reste juste en dessous et
  // garde sa valeur partout ailleurs, ou l'etat civil fait foi.
  const [surnomPerso, setSurnomPerso] = useState(() => {
    try { return JSON.parse(localStorage.getItem("admin_profil") || "{}").surnom || ""; } catch { return ""; }
  });
  const [monProfilOuvert, setMonProfilOuvert] = useState(false);
  const nomCompletProfil = `${profil.prenom || ""} ${profil.nom || ""}`.trim();
  const nomAffiche = (surnomPerso || "").trim() || nomCompletProfil;
  const dateCreation = profil.date_creation ? new Date(profil.date_creation).toLocaleDateString("fr-FR", { day:"2-digit", month:"short", year:"numeric" }) : "-";
  const dernCnx  = profil.derniere_connexion ? new Date(profil.derniere_connexion).toLocaleDateString("fr-FR", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "-";
  const centreLabel = profil.scope?.length > 0 ? profil.scope.join(", ") : "National";
  const isCommercial    = profil.role === "commercial";
  const isDeputy        = profil.role === "deputy_superviseur";
  const isSuperviseur   = profil.role === "superviseur";
  const isPedagAdvisor  = profil.role === "pedagogical_advisor";
  const isRH            = profil.role === "rh";
  const isOnboarding    = profil.role === "onboarding";
  const isComptable     = profil.role === "comptable";
  const isDataCollector = profil.role === "data_collector";
  const isAdmin         = profil.role === "admin" || profil.role === "manager";
  const isCustomerCare      = profil.role === "customer_care";
  const isAssistantElearning = profil.role === "assistant_elearning";

  const authHdr = () => ({ Authorization:`Bearer ${localStorage.getItem("admin_token")}` });

  React.useEffect(() => {
    if (!isComptable) return;
    if (tab === "paiements_c" && paiementsCReel === null) {
      fetch(`${API_URL}/api/dash-comptabilite/paiements`, { headers: authHdr() })
        .then(r => r.ok ? r.json() : { paiements: [] }).then(d => setPaiementsCReel(d.paiements || []))
        .catch(() => setPaiementsCReel([]));
    }
    if (tab === "factures" && facturesCReel === null) {
      fetch(`${API_URL}/api/dash-comptabilite/factures`, { headers: authHdr() })
        .then(r => r.ok ? r.json() : { factures: [] }).then(d => setFacturesCReel(d.factures || []))
        .catch(() => setFacturesCReel([]));
    }
    if (tab === "tresorerie" && tresorerieCReel === null) {
      Promise.all([
        fetch(`${API_URL}/api/dash-comptabilite/tresorerie/synthese`, { headers: authHdr() }).then(r => r.ok ? r.json() : null),
        fetch(`${API_URL}/api/dash-comptabilite/tresorerie/mouvements`, { headers: authHdr() }).then(r => r.ok ? r.json() : { mouvements: [] }),
      ]).then(([synth, mv]) => { setTresorerieCReel(synth || {}); setMouvementsCReel(mv.mouvements || []); })
        .catch(() => { setTresorerieCReel({}); setMouvementsCReel([]); });
    }
  }, [tab, isComptable]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!isCommercial) return;
    const besoinAssignations = ["perf","pipeline","activite"].includes(tab) && commercialAssignationsReel === null;
    const besoinPaiements    = ["perf","activite"].includes(tab) && commercialPaiementsReel === null;
    if (besoinAssignations) {
      Promise.all([
        fetch(`${API_URL}/api/parcours/assistantes`, { headers: authHdr() }).then(r => r.ok ? r.json() : { assistantes: [] }),
        fetch(`${API_URL}/api/parcours/assignations`, { headers: authHdr() }).then(r => r.ok ? r.json() : { assignations: [] }),
      ]).then(([assData, asgData]) => {
        const moi = (assData.assistantes || []).find(a => a.email === profil.email);
        const mesAssignations = moi ? (asgData.assignations || []).filter(a => a.assistantes?.id === moi.id) : [];
        setCommercialAssignationsReel(mesAssignations);
      }).catch(() => setCommercialAssignationsReel([]));
    }
    if (besoinPaiements) {
      fetch(`${API_URL}/api/paiements/all`, { headers: authHdr() })
        .then(r => r.ok ? r.json() : { paiements: [] })
        .then(d => setCommercialPaiementsReel((d.paiements || []).filter(p => p.commercial_id === profil.id)))
        .catch(() => setCommercialPaiementsReel([]));
    }
  }, [tab, isCommercial]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!isRH) return;
    if (tab === "paie" && rhHonorairesReel === null) {
      fetch(`${API_URL}/api/seances/honoraires-mois`, { headers: authHdr() })
        .then(r => r.ok ? r.json() : { honoraires: [] }).then(d => setRhHonorairesReel(d.honoraires || []))
        .catch(() => setRhHonorairesReel([]));
    }
    if ((tab === "contrats_rh" || tab === "effectifs") && rhUtilisateursReel === null) {
      fetch(`${API_URL}/api/admin/utilisateurs`, { headers: authHdr() })
        .then(r => r.ok ? r.json() : { utilisateurs: [] }).then(d => setRhUtilisateursReel(d.utilisateurs || []))
        .catch(() => setRhUtilisateursReel([]));
    }
    if (tab === "effectifs" && rhGrilleReel === null) {
      fetch(`${API_URL}/api/seances/grille-tarifaire`, { headers: authHdr() })
        .then(r => r.ok ? r.json() : { grille: null }).then(d => setRhGrilleReel(d.grille || {}))
        .catch(() => setRhGrilleReel({}));
    }
  }, [tab, isRH]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!isOnboarding) return;
    if (["apprenants_ob","dossiers_ob","rdv_ob"].includes(tab) && onboardingAssignationsReel === null) {
      fetch(`${API_URL}/api/parcours/assignations`, { headers: authHdr() })
        .then(r => r.ok ? r.json() : { assignations: [] })
        .then(d => setOnboardingAssignationsReel((d.assignations || []).filter(a => a.onboarding_id === profil.id)))
        .catch(() => setOnboardingAssignationsReel([]));
    }
  }, [tab, isOnboarding]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!isPedagAdvisor) return;
    if (tab === "suivi_pedago" && paContratsPrivesReel === null) {
      fetch(`${API_URL}/api/contrats-prives`, { headers: authHdr() })
        .then(r => r.ok ? r.json() : { contrats: [] })
        .then(d => setPaContratsPrivesReel((d.contrats || []).filter(c => c.created_by === profil.id)))
        .catch(() => setPaContratsPrivesReel([]));
    }
  }, [tab, isPedagAdvisor]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!isDeputy && !isSuperviseur) return;
    if (["supervision","vue_nationale","synthese"].includes(tab) && svCoachsReel === null) {
      fetch(`${API_URL}/api/admin/utilisateurs?role=coach`, { headers: authHdr() })
        .then(r => r.ok ? r.json() : { utilisateurs: [] }).then(d => setSvCoachsReel(d.utilisateurs || []))
        .catch(() => setSvCoachsReel([]));
    }
    if (["supervision","vue_nationale","synthese"].includes(tab) && svSeancesReel === null) {
      const now = new Date();
      fetch(`${API_URL}/api/seances/?mois=${now.getMonth()+1}&annee=${now.getFullYear()}`, { headers: authHdr() })
        .then(r => r.ok ? r.json() : { seances: [] }).then(d => setSvSeancesReel(d.seances || []))
        .catch(() => setSvSeancesReel([]));
    }
    if (["vue_nationale","synthese"].includes(tab) && svCentresReel === null) {
      fetch(`${API_URL}/api/centres/admin/all`, { headers: authHdr() })
        .then(r => r.ok ? r.json() : { centres: [] }).then(d => setSvCentresReel(d.centres || []))
        .catch(() => setSvCentresReel([]));
    }
    if (tab === "deputies" && svDeputiesReel === null) {
      fetch(`${API_URL}/api/admin/utilisateurs?role=deputy_superviseur`, { headers: authHdr() })
        .then(r => r.ok ? r.json() : { utilisateurs: [] }).then(d => setSvDeputiesReel(d.utilisateurs || []))
        .catch(() => setSvDeputiesReel([]));
    }
  }, [tab, isDeputy, isSuperviseur]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!isDataCollector) return;
    if (["collectes","perf_dc"].includes(tab) && dcLeadsReel === null) {
      fetch(`${API_URL}/api/leads/all`, { headers: authHdr() })
        .then(r => r.ok ? r.json() : { leads: [] })
        .then(d => setDcLeadsReel(d.leads || []))
        .catch(() => setDcLeadsReel([]));
    }
  }, [tab, isDataCollector]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!isAdmin) return;
    if (tab === "centres_admin" && adminCentresReel === null) {
      fetch(`${API_URL}/api/centres/admin/all`, { headers: authHdr() })
        .then(r => r.ok ? r.json() : { centres: [] })
        .then(d => setAdminCentresReel(d.centres || []))
        .catch(() => setAdminCentresReel([]));
    }
    if (tab === "activite_admin" && adminActiviteReel === null) {
      fetch(`${API_URL}/api/audit/logs?plateforme=dash&limit=20`, { headers: authHdr() })
        .then(r => r.ok ? r.json() : { logs: [] })
        .then(d => setAdminActiviteReel(d.logs || []))
        .catch(() => setAdminActiviteReel([]));
    }
  }, [tab, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!isCustomerCare) return;
    if (["tickets_cc","satisfaction","historique_cc"].includes(tab) && ccTicketsReel === null) {
      fetch(`${API_URL}/api/tickets`, { headers: authHdr() })
        .then(r => r.ok ? r.json() : { tickets: [] })
        .then(d => setCcTicketsReel(d.tickets || []))
        .catch(() => setCcTicketsReel([]));
    }
  }, [tab, isCustomerCare]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = () => {
    setEditData({
      prenom:    profil.prenom    || "",
      nom:       profil.nom       || "",
      email:     profil.email     || "",
      telephone: profil.telephone || "",
      telephone_2: profil.telephone_2 || "",
      role:      profil.role      || "",
      scope:     Array.isArray(profil.scope) ? [...profil.scope] : [],
      actif:     profil.actif !== false,
      couleur:   profil.couleur   || (ROLES_DEF[profil.role]?.color || "#0891b2"),
    });
    setEditMode(true);
    setTab("infos");
  };

  const cancelEdit = () => { setEditMode(false); setEditData(null); };

  const saveEdit = async () => {
    if (!editData || saving) return;
    setSaving(true);
    try {
      const payload = {
        prenom:    editData.prenom.trim(),
        nom:       editData.nom.trim(),
        telephone: editData.telephone.trim(),
        telephone_2: (editData.telephone_2||"").trim(),
        role:      editData.role,
        scope:     editData.scope,
        actif:     editData.actif,
        couleur:   editData.couleur || null,
      };
      const res = await fetch(`${API_URL}/api/admin/utilisateurs/${profil.id}`, {
        method: "PATCH",
        headers: { ...authHdr(), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { await alerte("Erreur lors de la sauvegarde.", { danger: true }); return; }
      const updated = { ...profil, ...payload };
      setProfil(updated);
      onProfilUpdate && onProfilUpdate(updated);
      setEditMode(false);
      setEditData(null);
    } catch { await alerte("Erreur réseau.", { danger: true }); }
    finally { setSaving(false); }
  };

  const toggleScope = (id) => {
    setEditData(d => {
      const has = d.scope.includes(id);
      return { ...d, scope: has ? d.scope.filter(s => s !== id) : [...d.scope, id] };
    });
  };

  const handleUploadPhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r2 = await fetchUpload(`${API_URL}/api/upload/avatar`, { method:"POST", headers: authHdr(), body: fd });
      if (!r2.ok) throw new Error();
      const { url } = await r2.json();
      await fetch(`${API_URL}/api/admin/utilisateurs/${profil.id}`, { method:"PATCH", headers:{ ...authHdr(), "Content-Type":"application/json" }, body: JSON.stringify({ avatar_url: url }) });
      setProfil(p => ({ ...p, avatar_url: url }));
      onProfilUpdate && onProfilUpdate({ ...profil, avatar_url: url });
    } catch { await alerte("Erreur lors de l'upload de la photo.", { danger: true }); }
    finally { setUploadingPhoto(false); }
  };

  const handleUploadCV = async (file) => {
    if (!file) return;
    setUploadingCV(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r2 = await fetchUpload(`${API_URL}/api/upload/document`, { method:"POST", headers: authHdr(), body: fd });
      if (!r2.ok) throw new Error();
      const { url } = await r2.json();
      await fetch(`${API_URL}/api/admin/utilisateurs/${profil.id}`, { method:"PATCH", headers:{ ...authHdr(), "Content-Type":"application/json" }, body: JSON.stringify({ cv_url: url }) });
      setCvUrl(url);
      onProfilUpdate && onProfilUpdate({ ...profil, cv_url: url });
    } catch { await alerte("Erreur lors de l'upload du CV.", { danger: true }); }
    finally { setUploadingCV(false); }
  };

  const ROUTE_MAP = {
    super_admin: "/dashboard/superAdmin", admin: "/AdminDashboard", manager: "/manager-dashboard",
    superviseur: "/superviseur-dashboard", deputy_superviseur: "/superviseur-dashboard",
    pedagogical_advisor: "/pedagogical-advisor-dashboard",
    commercial: "/commercial-dashboard", onboarding: "/onboarding-dashboard",
    gestionnaire: "/gestionnaire-dashboard", rh: "/rh-paie-dashboard",
    comptable: "/comptable-dashboard", placement_test: "/placement-test-dashboard",
    coach: "/espace-professeur", customer_care: "/customer-care-dashboard",
    data_collector: "/datacollector-dashboard",
  };

  const TABS = [
    { id: "infos",    lib: "Informations",  icone: "👤" },
    { id: "docs",     lib: "Documents",     icone: "📎" },
    { id: "securite", lib: "Sécurité",      icone: "🔐" },
    { id: "acces",    lib: "Accès & Rôle",  icone: "🔑" },
    ...(isCommercial ? [
      { id: "perf",       lib: "Performance",  icone: "📈" },
      { id: "pipeline",   lib: "Pipeline",     icone: "🎯" },
      { id: "activite",   lib: "Activité",     icone: "📋" },
    ] : []),
    ...(isDeputy ? [
      { id: "supervision", lib: "Supervision",  icone: "🔍" },
      { id: "incidents",   lib: "Incidents",    icone: "⚠️" },
      { id: "rapports",    lib: "Rapports",     icone: "📋" },
    ] : []),
    ...(isSuperviseur ? [
      { id: "vue_nationale", lib: "Vue Nationale",  icone: "🔭" },
      { id: "deputies",      lib: "Deputies",       icone: "👥" },
      { id: "escalades",     lib: "Escalades",      icone: "🚨" },
      { id: "synthese",      lib: "Synthèse",       icone: "📊" },
    ] : []),
    ...(isPedagAdvisor ? [
      { id: "suivi_pedago",   lib: "Suivi pédago",   icone: "📚" },
      { id: "evaluations_pa", lib: "Évaluations",    icone: "📝" },
      { id: "plans_action",   lib: "Plans d'action", icone: "🎯" },
    ] : []),
    ...(isRH ? [
      { id: "paie",           lib: "Paie",           icone: "💰" },
      { id: "contrats_rh",    lib: "Contrats",       icone: "📋" },
      { id: "effectifs",      lib: "Effectifs",      icone: "👥" },
    ] : []),
    ...(isOnboarding ? [
      { id: "apprenants_ob",  lib: "Apprenants",     icone: "🎓" },
      { id: "dossiers_ob",    lib: "Dossiers",       icone: "📂" },
      { id: "rdv_ob",         lib: "Rendez-vous",    icone: "📅" },
    ] : []),
    ...(isComptable ? [
      { id: "paiements_c",    lib: "Paiements",      icone: "💳" },
      { id: "tresorerie",     lib: "Trésorerie",     icone: "📊" },
      { id: "factures",       lib: "Factures",       icone: "🧾" },
    ] : []),
    ...(isDataCollector ? [
      { id: "collectes",      lib: "Collectes",      icone: "📊" },
      { id: "campagnes_dc",   lib: "Campagnes",      icone: "🎯" },
      { id: "perf_dc",        lib: "Performance",    icone: "📈" },
    ] : []),
    ...(isAdmin ? [
      { id: "centres_admin",  lib: "Centres",        icone: "🏢" },
      { id: "taches_admin",   lib: "Tâches",         icone: "✅" },
      { id: "activite_admin", lib: "Activité",       icone: "📋" },
    ] : []),
    ...(isCustomerCare ? [
      { id: "tickets_cc",     lib: "Tickets",        icone: "🎧" },
      { id: "satisfaction",   lib: "Satisfaction",   icone: "⭐" },
      { id: "historique_cc",  lib: "Historique",     icone: "📋" },
    ] : []),
  ];

  /* ── Comptable - 12 derniers mois glissants (remplace l'ancienne liste figée sur 2025) ── */
  const MOIS_LABELS_C = ["","Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
  const DERNIERS_MOIS_C = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - (11 - i));
    return { label: `${MOIS_LABELS_C[d.getMonth()+1]} ${d.getFullYear()}`, prefix: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}` };
  });
  const PERIODES_C = ["Tous", ...DERNIERS_MOIS_C.map(m => m.label)];
  const PERIODE_C_PREFIX = Object.fromEntries(DERNIERS_MOIS_C.map(m => [m.label, m.prefix]));

  /* ── Filtres période Customer Care (réutilisés pour les vrais tickets) ── */
  const PERIODES_CC = ["Tous","Juin 2025","Mai 2025","Avril 2025","Mars 2025","Février 2025","Janvier 2025"];
  const PERIODE_CC_PREFIX = { "Juin 2025":"2025-06","Mai 2025":"2025-05","Avril 2025":"2025-04","Mars 2025":"2025-03","Février 2025":"2025-02","Janvier 2025":"2025-01" };
  const S = {
    overlay:  { position:"fixed", inset:0, background:"rgba(15,23,42,0.65)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:99990, backdropFilter:"blur(4px)", padding:16 },
    modal:    { background:"#f8fafc", borderRadius:20, width:"90vw", maxWidth:"90vw", maxHeight:"90vh", display:"flex", flexDirection:"column", boxShadow:"0 32px 80px rgba(0,0,0,0.35)", overflow:"hidden" },
    header:   { background:`linear-gradient(135deg, ${couleur}dd 0%, ${couleur} 100%)`, padding:"22px 26px 18px", flexShrink:0 },
    hTop:     { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 },
    btnX:     { background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)", borderRadius:8, width:32, height:32, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:18, color:"#fff", flexShrink:0 },
    stats:    { display:"flex", gap:18, marginTop:4 },
    stat:     { textAlign:"center" },
    statVal:  { fontSize:16, fontWeight:800, color:"#fff" },
    statLbl:  { fontSize:10, color:"rgba(255,255,255,0.6)", textTransform:"uppercase", letterSpacing:"0.05em" },
    sep:      { width:1, background:"rgba(255,255,255,0.2)", alignSelf:"stretch" },
    sidebar:  { width:210, flexShrink:0, background:"#f8fafc", borderRight:"1px solid #e5e7eb", overflowY:"auto", padding:"14px 10px", display:"flex", flexDirection:"column", gap:2 },
    sideTab:  (a) => ({ display:"flex", alignItems:"center", gap:9, padding:"10px 13px", border:"none", borderRadius:10, width:"100%", textAlign:"left", background: a ? couleur+"18" : "transparent", color: a ? couleur : "#4b5563", fontWeight: a ? 700 : 500, fontSize:13, cursor:"pointer", borderLeft: a ? `3px solid ${couleur}` : "3px solid transparent", transition:"background .15s" }),
    body:     { flex:1, overflowY:"auto", padding:"20px 24px" },
    card:     { background:"#fff", borderRadius:10, padding:"12px 16px", border:"1px solid #e2e8f0", marginBottom:10 },
    grid2:    { display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 },
    lbl:      { fontSize:10, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:4 },
    val:      { fontSize:13, fontWeight:600, color:"#0f172a" },
    chip:     (c) => ({ display:"inline-block", padding:"4px 12px", borderRadius:20, background:c+"18", color:c, border:`1px solid ${c}30`, fontSize:12, fontWeight:700 }),
    badge:    (ok) => ({ display:"inline-block", padding:"3px 10px", borderRadius:20, background: ok ? "#d1fae5" : "#fee2e2", color: ok ? "#065f46" : "#991b1b", fontSize:11, fontWeight:700 }),
    footer:   { padding:"14px 22px", borderTop:"1px solid #e2e8f0", display:"flex", gap:10, justifyContent:"flex-end", background:"#fff", flexShrink:0 },
    btnSec:   { padding:"8px 18px", borderRadius:8, border:"1.5px solid #e2e8f0", background:"#fff", color:"#64748b", cursor:"pointer", fontSize:13, fontWeight:600 },
  };

  const photoUrl = profil.avatar_url || profil.photo_url;

  return (
    <div style={S.overlay} onClick={onFermer}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div style={S.header}>
          <div style={S.hTop}>
            <div style={{ display:"flex", alignItems:"center", gap:14 }}>
              {/* Avatar en lecture seule - pas d'upload possible (onglet Administration BET consultatif) */}
              <div style={{ position:"relative", flexShrink:0 }}>
                <div style={{ width:62, height:62, borderRadius:"50%", border:"2.5px solid rgba(255,255,255,0.4)", overflow:"hidden", background:"rgba(255,255,255,0.18)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, fontWeight:800, color:"#fff" }}>
                  {photoUrl
                    ? <img src={photoUrl} alt="avatar" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                    : (r.emoji || initials)
                  }
                </div>
              </div>

              <div>
                <div style={{ fontSize:19, fontWeight:800, color:"#fff" }}>{nomAffiche}<BoutonMonProfil onClick={() => setMonProfilOuvert(true)} /></div>
                {surnomPerso.trim() && nomCompletProfil !== nomAffiche && (
                  <div style={{ fontSize:11, color:"rgba(255,255,255,.6)" }}>{nomCompletProfil}</div>
                )}
                <MonProfilModal ouvert={monProfilOuvert} onClose={() => setMonProfilOuvert(false)}
                  onEnregistre={(v) => setSurnomPerso(v || "")} />
                <div style={{ fontSize:12, color:"rgba(255,255,255,0.75)", marginTop:2 }}>{r.label} · {profil.email}</div>
                <div style={{ marginTop:7, display:"flex", gap:6, flexWrap:"wrap" }}>
                  <span style={S.badge(profil.actif ?? true)}>{(profil.actif ?? true) ? "Actif" : "Inactif"}</span>
                  {profil.twofa && <span style={{ padding:"2px 8px", borderRadius:20, background:"rgba(255,255,255,0.2)", color:"#fff", fontSize:10, fontWeight:700 }}>🔐 2FA</span>}
                  <span style={{ padding:"2px 8px", borderRadius:20, background:"rgba(255,255,255,0.15)", color:"rgba(255,255,255,0.85)", fontSize:10, fontWeight:600 }}>Niv. {r.niveau || "-"}</span>
                </div>
              </div>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <button style={S.btnX} onClick={onFermer}>×</button>
            </div>
          </div>
          <div style={S.stats}>
            <div style={S.stat}><div style={S.statVal}>{dateCreation}</div><div style={S.statLbl}>Création</div></div>
            <div style={S.sep} />
            <div style={S.stat}><div style={S.statVal}>{profil.sessions ?? "-"}</div><div style={S.statLbl}>Sessions</div></div>
            <div style={S.sep} />
            <div style={S.stat}><div style={S.statVal}>{centreLabel.split(",").length > 2 ? `${centreLabel.split(",").length} centres` : centreLabel}</div><div style={S.statLbl}>Périmètre</div></div>
            <div style={S.sep} />
            <div style={S.stat}><div style={S.statVal}>{r.niveau ?? "-"}</div><div style={S.statLbl}>Niveau</div></div>
          </div>
        </div>

        {/* ── Main : sidebar + contenu ── */}
        <div style={{ display:"flex", flex:1, overflow:"hidden" }}>

        {/* Sidebar navigation */}
        <div style={S.sidebar}>
          {TABS.map(t => (
            <button key={t.id} style={S.sideTab(tab === t.id)} onClick={() => setTab(t.id)}>
              <span style={{ fontSize:16, flexShrink:0 }}>{t.icone}</span>
              <span>{t.lib}</span>
            </button>
          ))}
        </div>

        {/* Corps */}
        <div style={S.body}>

          {/* ════ INFOS ════ */}
          {tab === "infos" && !editMode && (
            <>
              <div style={S.grid2}>
                <div style={S.card}><div style={S.lbl}>Prénom</div><div style={S.val}>{profil.prenom || "-"}</div></div>
                <div style={S.card}><div style={S.lbl}>Nom</div><div style={S.val}>{profil.nom || "-"}</div></div>
                <div style={{ ...S.card, gridColumn:"1 / -1" }}><div style={S.lbl}>Email</div><div style={{ ...S.val, color: BET_COLOR }}>{profil.email || "-"}</div></div>
                <div style={S.card}><div style={S.lbl}>Téléphone</div><div style={S.val}>{profil.telephone || "-"}</div></div>
                <div style={S.card}><div style={S.lbl}>Téléphone secondaire</div><div style={S.val}>{profil.telephone_2 || "-"}</div></div>
                <div style={S.card}><div style={S.lbl}>Date de création</div><div style={S.val}>{dateCreation}</div></div>
                <div style={{ ...S.card, gridColumn:"1 / -1" }}><div style={S.lbl}>Adresse</div><div style={{ ...S.val, fontSize:12, lineHeight:1.6 }}>{profil.adresse || "-"}</div></div>
                <div style={{ ...S.card, gridColumn:"1 / -1" }}><div style={S.lbl}>Périmètre d'action</div><div style={{ ...S.val, fontSize:12, lineHeight:1.7 }}>{centreLabel}</div></div>
              </div>
              {/* Couleur dashboard */}
              {(()=>{
                const pc = profil.couleur || null;
                return (
                  <div style={{ ...S.card, padding:0, overflow:"hidden" }}>
                    <div style={{ padding:"10px 14px 8px", borderBottom:"1px solid #f1f5f9" }}>
                      <div style={S.lbl}>🎨 Couleur du dashboard</div>
                    </div>
                    <div style={{ padding:"12px 14px", display:"flex", alignItems:"center", gap:14 }}>
                      {pc ? (
                        <>
                          <div style={{ width:44, height:44, borderRadius:10, background:`linear-gradient(135deg,#0f172a 0%,${pc} 100%)`, flexShrink:0, boxShadow:`0 2px 8px ${pc}55` }} />
                          <div>
                            <div style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>Couleur personnalisée</div>
                            <div style={{ fontSize:12, color:"#64748b", fontFamily:"monospace", marginTop:2 }}>{pc}</div>
                          </div>
                          <div style={{ marginLeft:"auto", padding:"4px 12px", borderRadius:20, background:pc+"18", color:pc, fontSize:12, fontWeight:700, border:`1px solid ${pc}40` }}>Aperçu</div>
                        </>
                      ) : (
                        <>
                          <div style={{ width:44, height:44, borderRadius:10, background:"linear-gradient(135deg,#0f172a 0%,#0891b2 100%)", flexShrink:0, opacity:0.5 }} />
                          <div>
                            <div style={{ fontSize:13, color:"#94a3b8" }}>Couleur par défaut BET</div>
                            <div style={{ fontSize:12, color:"#cbd5e1", fontFamily:"monospace", marginTop:2 }}>#0891b2</div>
                          </div>
                          <div style={{ marginLeft:"auto", fontSize:11, color:"#9ca3af", fontStyle:"italic" }}>Non personnalisée</div>
                        </>
                      )}
                    </div>
                    {pc && (
                      <div style={{ height:6, background:`linear-gradient(135deg,#0f172a 0%,${pc} 100%)` }} />
                    )}
                  </div>
                );
              })()}
              {profil.note && <div style={S.card}><div style={S.lbl}>Note interne</div><div style={{ fontSize:13, color:"#64748b", lineHeight:1.7, marginTop:4 }}>{profil.note}</div></div>}
            </>
          )}

          {/* ════ FORMULAIRE ÉDITION ════ */}
          {editMode && editData && (
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:10, padding:"10px 14px", fontSize:12, color:"#92400e", fontWeight:600 }}>
                ✏️ Mode édition - modifiez les champs puis cliquez sur Enregistrer
              </div>

              {/* Prénom / Nom */}
              <div style={S.grid2}>
                <div>
                  <div style={S.lbl}>Prénom</div>
                  <input value={editData.prenom} onChange={e => setEditData(d => ({...d, prenom: e.target.value}))}
                    style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e2e8f0", fontSize:13, outline:"none", boxSizing:"border-box" }} />
                </div>
                <div>
                  <div style={S.lbl}>Nom</div>
                  <input value={editData.nom} onChange={e => setEditData(d => ({...d, nom: e.target.value}))}
                    style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e2e8f0", fontSize:13, outline:"none", boxSizing:"border-box" }} />
                </div>
              </div>

              {/* Email (readonly) + Téléphone */}
              <div style={S.grid2}>
                <div>
                  <div style={S.lbl}>Email (non modifiable)</div>
                  <input value={editData.email} readOnly
                    style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e2e8f0", fontSize:13, background:"#f8fafc", color:"#94a3b8", boxSizing:"border-box" }} />
                </div>
                <div>
                  <div style={S.lbl}>Téléphone</div>
                  <input value={editData.telephone} onChange={e => setEditData(d => ({...d, telephone: e.target.value}))}
                    placeholder="+224 620 000 000"
                    style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e2e8f0", fontSize:13, outline:"none", boxSizing:"border-box" }} />
                </div>
                <div>
                  <div style={S.lbl}>Téléphone secondaire</div>
                  <input value={editData.telephone_2} onChange={e => setEditData(d => ({...d, telephone_2: e.target.value}))}
                    placeholder="+225 05 …"
                    style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e2e8f0", fontSize:13, outline:"none", boxSizing:"border-box" }} />
                </div>
              </div>

              {/* Adresse */}
              <div>
                <div style={S.lbl}>Adresse</div>
                <textarea value={editData.adresse || ""} onChange={e => setEditData(d => ({...d, adresse: e.target.value}))}
                  placeholder="Ex : Quartier Ratoma, Conakry, Guinée"
                  rows={2}
                  style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e2e8f0", fontSize:13, outline:"none", boxSizing:"border-box", resize:"vertical", fontFamily:"inherit", lineHeight:1.5 }} />
              </div>

              {/* Rôle */}
              <div>
                <div style={S.lbl}>Rôle</div>
                <div style={{ fontSize:11, fontWeight:700, color:"#64748b", letterSpacing:"0.05em", marginBottom:6, textTransform:"uppercase" }}>🏢 Backoffice</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:10 }}>
                  {Object.values(ROLES_DEF).filter(rd=>!rd.id.startsWith("admin_")).map(rd => (
                    <button key={rd.id} onClick={() => setEditData(d => ({...d, role: rd.id}))}
                      style={{ padding:"8px 10px", borderRadius:9, border:`2px solid ${editData.role===rd.id ? rd.color : "#e5e7eb"}`, background: editData.role===rd.id ? rd.color+"12" : "#fff", cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ fontSize:14 }}>{rd.emoji}</span>
                      <span style={{ fontSize:11, fontWeight: editData.role===rd.id ? 700 : 500, color: editData.role===rd.id ? rd.color : "#374151" }}>{rd.label}</span>
                    </button>
                  ))}
                </div>
                <div style={{ fontSize:11, fontWeight:700, color:"#7c3aed", letterSpacing:"0.05em", marginBottom:6, textTransform:"uppercase" }}>🎓 E-Learning BET</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 }}>
                  {Object.values(ROLES_DEF).filter(rd=>rd.id.startsWith("admin_")).map(rd => (
                    <button key={rd.id} onClick={() => setEditData(d => ({...d, role: rd.id}))}
                      style={{ padding:"8px 10px", borderRadius:9, border:`2px solid ${editData.role===rd.id ? rd.color : "#e5e7eb"}`, background: editData.role===rd.id ? rd.color+"12" : "#fff", cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ fontSize:14 }}>{rd.emoji}</span>
                      <span style={{ fontSize:11, fontWeight: editData.role===rd.id ? 700 : 500, color: editData.role===rd.id ? rd.color : "#374151" }}>{rd.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Périmètre */}
              <div>
                <div style={S.lbl}>Périmètre d'action</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                  <button onClick={() => setEditData(d => ({...d, scope:[]}))}
                    style={{ padding:"5px 12px", borderRadius:20, border:`1.5px solid ${editData.scope.length===0?"#0891b2":"#e2e8f0"}`, background: editData.scope.length===0?"#e0f2fe":"#fff", color: editData.scope.length===0?"#0891b2":"#64748b", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                    🌍 National
                  </button>
                  {CENTRES_BET.map(c => (
                    <button key={c.id} onClick={() => toggleScope(c.id)}
                      style={{ padding:"5px 12px", borderRadius:20, border:`1.5px solid ${editData.scope.includes(c.id)?"#0891b2":"#e2e8f0"}`, background: editData.scope.includes(c.id)?"#e0f2fe":"#fff", color: editData.scope.includes(c.id)?"#0891b2":"#64748b", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                      {editData.scope.includes(c.id) ? "✓ " : ""}{c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Couleur du dashboard */}
              {(() => {
                const EDIT_PALETTE = [
                  { hex:"#0891b2", label:"Cyan (BET)" },
                  { hex:"#7c3aed", label:"Violet" },
                  { hex:"#10b981", label:"Émeraude" },
                  { hex:"#f59e0b", label:"Ambre" },
                  { hex:"#dc2626", label:"Rouge" },
                  { hex:"#0d9488", label:"Teal" },
                  { hex:"#d97706", label:"Or" },
                  { hex:"#0284c7", label:"Bleu" },
                  { hex:"#6366f1", label:"Indigo" },
                  { hex:"#db2777", label:"Rose" },
                  { hex:"#b45309", label:"Brun" },
                  { hex:"#0f172a", label:"Ardoise" },
                ];
                const ec = editData.couleur || "#0891b2";
                return (
                  <div style={{ marginBottom:4, padding:"16px 18px", borderRadius:12, background:"#f8fafc", border:"1.5px solid #e5e7eb" }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:10 }}>🎨 Couleur du dashboard</div>
                    <div style={{ borderRadius:10, overflow:"hidden", marginBottom:12, height:38, background:`linear-gradient(135deg,#0f172a 0%,${ec} 100%)`, display:"flex", alignItems:"center", paddingLeft:14 }}>
                      <span style={{ color:"#fff", fontSize:12, fontWeight:600, opacity:.9 }}>Aperçu - header du dashboard</span>
                    </div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:10 }}>
                      {EDIT_PALETTE.map(p => (
                        <button key={p.hex} title={p.label}
                          onClick={() => setEditData(d => ({...d, couleur: p.hex}))}
                          style={{ width:28, height:28, borderRadius:"50%", background:p.hex, border: ec===p.hex ? "3px solid #0f172a" : "3px solid transparent", cursor:"pointer", outline: ec===p.hex ? `2px solid ${p.hex}` : "none", outlineOffset:2, transition:"transform .1s", transform: ec===p.hex ? "scale(1.2)" : "scale(1)" }} />
                      ))}
                      <label title="Couleur personnalisée" style={{ width:28, height:28, borderRadius:"50%", background:"conic-gradient(red,yellow,lime,aqua,blue,magenta,red)", border:"3px solid #e5e7eb", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
                        <input type="color" value={ec} onChange={e => setEditData(d => ({...d, couleur: e.target.value}))}
                          style={{ width:1, height:1, opacity:0, position:"absolute", pointerEvents:"none" }} />
                      </label>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <div style={{ width:20, height:20, borderRadius:4, background:ec, border:"1px solid #e5e7eb" }} />
                      <input value={ec} onChange={e => setEditData(d => ({...d, couleur: e.target.value}))}
                        placeholder="#0891b2" maxLength={7}
                        style={{ flex:1, padding:"6px 10px", borderRadius:7, border:"1.5px solid #e2e8f0", fontSize:12, fontFamily:"monospace", outline:"none" }} />
                    </div>
                  </div>
                );
              })()}

              {/* Statut actif */}
              <div style={S.card}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div>
                    <div style={S.lbl}>Statut du compte</div>
                    <div style={{ fontSize:13, color:"#374151", marginTop:2 }}>{editData.actif ? "✅ Compte actif" : "🔴 Compte désactivé"}</div>
                  </div>
                  <button onClick={() => setEditData(d => ({...d, actif: !d.actif}))}
                    style={{ padding:"7px 16px", borderRadius:8, border:`1.5px solid ${editData.actif?"#fca5a5":"#86efac"}`, background: editData.actif?"#fee2e2":"#dcfce7", color: editData.actif?"#dc2626":"#16a34a", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                    {editData.actif ? "🔴 Désactiver" : "✅ Activer"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ════ DOCUMENTS ════ */}
          {tab === "docs" && (
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              {/* Photo de profil */}
              <div style={S.card}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                  <div style={S.lbl}>📷 Photo de profil</div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:16 }}>
                  <div style={{ width:80, height:80, borderRadius:"50%", border:`3px solid ${couleur}30`, overflow:"hidden", background:couleur+"18", display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, fontWeight:800, color:couleur, flexShrink:0 }}>
                    {photoUrl ? <img src={photoUrl} alt="avatar" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : (r.emoji || initials)}
                  </div>
                  <div>
                    <div style={{ fontSize:13, color:photoUrl?"#16a34a":"#9ca3af", fontWeight:600 }}>
                      {photoUrl ? "✅ Photo chargée" : "Aucune photo - cliquez pour en ajouter une"}
                    </div>
                    {photoUrl && <div style={{ fontSize:11, color:"#9ca3af", marginTop:3, wordBreak:"break-all" }}>{photoUrl.split("/").pop()}</div>}
                  </div>
                </div>
              </div>

              {/* CV */}
              <div style={S.card}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                  <div style={S.lbl}>📄 Curriculum Vitae (CV)</div>
                </div>
                {cvUrl ? (
                  <div style={{ display:"flex", alignItems:"center", gap:12, background:"#f0fdf4", borderRadius:8, padding:"10px 14px" }}>
                    <span style={{ fontSize:28 }}>📄</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:"#16a34a" }}>✅ CV disponible</div>
                      <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>{cvUrl.split("/").pop()}</div>
                    </div>
                    <a href={cvUrl} target="_blank" rel="noreferrer"
                      style={{ padding:"5px 12px", background:couleur, color:"#fff", borderRadius:7, fontSize:11, fontWeight:700, textDecoration:"none" }}>
                      👁 Voir
                    </a>
                  </div>
                ) : (
                  <div style={{ textAlign:"center", padding:"20px 0", color:"#9ca3af" }}>
                    <div style={{ fontSize:32, marginBottom:6 }}>📄</div>
                    <div style={{ fontSize:13 }}>Aucun CV chargé</div>
                    <div style={{ fontSize:11, marginTop:3 }}>Formats acceptés : PDF, DOC, DOCX</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ════ SÉCURITÉ ════ */}
          {tab === "securite" && (
            <>
              <div style={S.grid2}>
                <div style={S.card}><div style={S.lbl}>Double authentification (2FA)</div><div style={{ marginTop:6 }}><span style={{ padding:"4px 12px", borderRadius:20, background: profil.twofa ? "#d1fae5" : "#f1f5f9", color: profil.twofa ? "#065f46" : "#64748b", fontSize:12, fontWeight:700 }}>{profil.twofa ? "✅ Activé" : "⭕ Désactivé"}</span></div></div>
                <div style={S.card}><div style={S.lbl}>Statut du compte</div><div style={{ marginTop:6 }}><span style={S.badge(profil.actif ?? true)}>{(profil.actif ?? true) ? "✅ Actif" : "❌ Inactif"}</span></div></div>
                <div style={S.card}><div style={S.lbl}>Sessions actives</div><div style={{ ...S.val, fontSize:22, fontWeight:900, color:couleur }}>{profil.sessions ?? "-"}</div></div>
                <div style={S.card}><div style={S.lbl}>Dernière connexion</div><div style={{ ...S.val, fontSize:12 }}>{dernCnx}</div></div>
              </div>
              <div style={{ ...S.card, background: profil.twofa ? "#f0fdf4" : "#fff7ed", border:`1px solid ${profil.twofa ? "#bbf7d0" : "#fed7aa"}` }}>
                <div style={{ fontSize:13, color: profil.twofa ? "#15803d" : "#9a3412", fontWeight:600 }}>
                  {profil.twofa ? "🔐 Ce compte est protégé par une double authentification." : "⚠️ Double authentification non activée - recommandez l'activation à cet utilisateur."}
                </div>
              </div>
            </>
          )}

          {/* ════ ACCÈS ════ */}
          {tab === "acces" && (
            <>
              <div style={S.grid2}>
                <div style={S.card}><div style={S.lbl}>Rôle assigné</div><div style={{ marginTop:6 }}><span style={S.chip(couleur)}>{r.emoji} {r.label}</span></div></div>
                <div style={S.card}><div style={S.lbl}>Niveau de permission</div><div style={{ fontSize:26, fontWeight:900, color:couleur, marginTop:4 }}>{r.niveau ?? "-"} / 7</div></div>
                <div style={{ ...S.card, gridColumn:"1 / -1" }}><div style={S.lbl}>Description du rôle</div><div style={{ fontSize:13, color:"#64748b", marginTop:4, lineHeight:1.65 }}>{r.description || "-"}</div></div>
                <div style={{ ...S.card, gridColumn:"1 / -1" }}><div style={S.lbl}>Route / Dashboard assigné</div><div style={{ ...S.val, fontSize:12, fontFamily:"monospace", color:couleur, marginTop:4 }}>{ROUTE_MAP[profil.role] || "/"}</div></div>
              </div>
              <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e2e8f0", overflow:"hidden" }}>
                <div style={{ padding:"10px 14px", background:"#f8fafc", borderBottom:"1px solid #e2e8f0", fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.06em" }}>Périmètre d'accès</div>
                {(profil.scope?.length > 0 ? profil.scope : ["National"]).map((c, i) => (
                  <div key={i} style={{ padding:"10px 14px", display:"flex", alignItems:"center", gap:10, borderBottom: i < (profil.scope?.length || 1) - 1 ? "1px solid #f1f5f9" : "none", fontSize:13 }}>
                    <span style={{ width:8, height:8, borderRadius:"50%", background:couleur, flexShrink:0, display:"inline-block" }} />
                    <span style={{ fontWeight:600, color:"#0f172a" }}>{c}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ════ PERFORMANCE (commercial) ════ */}
          {tab === "perf" && (() => {
            if (commercialAssignationsReel === null || commercialPaiementsReel === null) {
              return <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>⏳ Chargement…</div>;
            }
            const asg  = commercialAssignationsReel;
            const pmts = commercialPaiementsReel;
            const nbProspects   = asg.length;
            const nbConvertis   = asg.filter(a => a.statut === "converti").length;
            const ca            = pmts.reduce((s,p) => s + Number(p.montant_recu||0), 0);
            const tauxConv      = nbProspects ? Math.round((nbConvertis/nbProspects)*100) : 0;
            const KPIS_REELS = [
              { lib:"Prospects",  val:nbProspects, icon:"🎯", color:"#0891b2" },
              { lib:"Conversions",val:nbConvertis, icon:"✅", color:"#16a34a", pct:tauxConv },
              { lib:"CA encaissé",val:`${(ca/1000).toFixed(0)}k`, icon:"💰", color:"#d97706" },
            ];
            const mois = derniersMoisSA(6);
            const historique = mois.map(m => ({
              mois: m.label,
              prospects: asg.filter(a => (a.created_at||"").startsWith(m.key)).length,
              conversions: asg.filter(a => a.statut === "converti" && (a.updated_at||a.created_at||"").startsWith(m.key)).length,
              ca: pmts.filter(p => (p.date_paiement||"").startsWith(m.key)).reduce((s,p)=>s+Number(p.montant_recu||0),0),
            }));
            return (
              <div>
                <div style={{ fontSize:12, color:"#9ca3af", marginBottom:14, fontStyle:"italic" }}>📊 Données réelles - tous prospects/paiements assignés</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:20 }}>
                  {KPIS_REELS.map(k => (
                    <div key={k.lib} style={{ background:"#fff", border:"1.5px solid #e5e7eb", borderRadius:12, padding:"14px 16px" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                        <div>
                          <div style={{ fontSize:10, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:3 }}>{k.lib}</div>
                          <div style={{ fontSize:24, fontWeight:900, color:k.color }}>{k.val}</div>
                          {k.pct != null && <div style={{ fontSize:11, color:"#6b7280", marginTop:2 }}>Taux : {k.pct}%</div>}
                        </div>
                        <span style={{ fontSize:22 }}>{k.icon}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={S.card}>
                  <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:10 }}>Évolution mensuelle (6 derniers mois)</div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:1, background:"#e5e7eb", borderRadius:8, overflow:"hidden" }}>
                    {["Mois","Prospects","Conv.","CA"].map(h => (
                      <div key={h} style={{ background:"#f8fafc", padding:"7px 10px", fontSize:10, fontWeight:700, color:"#6b7280", textTransform:"uppercase" }}>{h}</div>
                    ))}
                    {historique.map((h,i) => ([
                      <div key={`m${i}`} style={{ background:"#fff", padding:"8px 10px", fontSize:12, fontWeight:600, color:"#374151" }}>{h.mois}</div>,
                      <div key={`p${i}`} style={{ background:"#fff", padding:"8px 10px", fontSize:12, color:"#0891b2", fontWeight:700 }}>{h.prospects}</div>,
                      <div key={`c${i}`} style={{ background:"#fff", padding:"8px 10px", fontSize:12, color:"#16a34a", fontWeight:700 }}>{h.conversions}</div>,
                      <div key={`ca${i}`} style={{ background:"#fff", padding:"8px 10px", fontSize:12, color:"#d97706", fontWeight:700 }}>{h.ca.toLocaleString("fr-FR")} F</div>,
                    ]))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ════ PIPELINE (commercial) ════ */}
          {tab === "pipeline" && (() => {
            const STATUTS = {
              en_attente: { label:"En attente",  color:"#6b7280", bg:"#f3f4f6",  icon:"⏳" },
              en_cours:   { label:"En cours",    color:"#0891b2", bg:"#e0f2fe",  icon:"🔄" },
              converti:   { label:"Converti ✅", color:"#16a34a", bg:"#dcfce7",  icon:"🎉" },
              perdu:      { label:"Perdu",       color:"#dc2626", bg:"#fee2e2",  icon:"❌" },
            };
            if (commercialAssignationsReel === null) return <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>⏳ Chargement…</div>;
            const liste = commercialAssignationsReel;
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                <div style={{ fontSize:12, color:"#9ca3af", marginBottom:4, fontStyle:"italic" }}>🎯 {liste.length} prospect{liste.length>1?"s":""} dans le pipeline</div>
                {liste.length === 0 && <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>Aucun prospect assigné</div>}
                {liste.map((p) => {
                  const st = STATUTS[p.statut] || STATUTS.en_attente;
                  return (
                    <div key={p.id} style={{ background:"#fff", border:"1.5px solid #e5e7eb", borderRadius:12, padding:"13px 16px", display:"flex", alignItems:"center", gap:14 }}>
                      <div style={{ width:38, height:38, borderRadius:"50%", background:st.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 }}>{st.icon}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700, fontSize:13, color:"#1e293b" }}>{p.prospect_nom}</div>
                        <div style={{ fontSize:11, color:"#6b7280", marginTop:2 }}>📚 {p.type_cours||"-"} · 📅 {(p.created_at||"").slice(0,10)}</div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <span style={{ background:st.bg, color:st.color, borderRadius:8, padding:"3px 10px", fontSize:11, fontWeight:700, display:"block", marginBottom:4 }}>{st.label}</span>
                        {p.montant_paye != null && <span style={{ fontSize:12, fontWeight:700, color:"#374151" }}>{Number(p.montant_paye).toLocaleString("fr-FR")} F</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* ════ ACTIVITÉ (commercial) ════ */}
          {tab === "activite" && (() => {
            if (commercialAssignationsReel === null || commercialPaiementsReel === null) {
              return <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>⏳ Chargement…</div>;
            }
            const feed = [
              ...commercialAssignationsReel.map(a => ({ type:"prospect", desc:`Nouveau prospect - ${a.prospect_nom} (${a.type_cours||"-"})`, date:a.created_at })),
              ...commercialPaiementsReel.map(p => ({ type:"paiement", desc:`Paiement ${p.statut} - ${p.client} - ${Number(p.montant_recu||0).toLocaleString("fr-FR")} F`, date:p.date_paiement||p.created_at })),
            ].filter(a => a.date).sort((a,b) => new Date(b.date) - new Date(a.date)).slice(0,20);
            const ICONES = { prospect:"🎯", paiement:"💳" };
            const COLORS = { prospect:"#0891b2", paiement:"#16a34a" };
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                <div style={{ fontSize:12, color:"#9ca3af", marginBottom:4, fontStyle:"italic" }}>📋 Dernières activités réelles (prospects + paiements)</div>
                {feed.length === 0 && <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>Aucune activité enregistrée</div>}
                {feed.map((a,i) => (
                  <div key={i} style={{ background:"#fff", border:"1.5px solid #e5e7eb", borderRadius:10, padding:"11px 14px", display:"flex", alignItems:"center", gap:12 }}>
                    <div style={{ width:34, height:34, borderRadius:8, background:(COLORS[a.type]||"#6b7280")+"15", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 }}>
                      {ICONES[a.type] || "📌"}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:"#1e293b" }}>{a.desc}</div>
                    </div>
                    <div style={{ textAlign:"right", flexShrink:0 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:"#374151" }}>{(a.date||"").slice(0,10)}</div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* ════ SUPERVISION (deputy_superviseur) ════ */}
          {tab === "supervision" && (() => {
            if (svCoachsReel === null || svSeancesReel === null) return <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>⏳ Chargement…</div>;
            const monScope = profil.scope || [];
            const equipe = svCoachsReel.filter(c => !monScope.length || (c.scope||[]).some(s => monScope.includes(s)));
            const seancesParCoach = {};
            svSeancesReel.forEach(s => {
              if (!seancesParCoach[s.coach_id]) seancesParCoach[s.coach_id] = { realisees:0, annulees:0, heures:0 };
              if (s.statut === "realisee") { seancesParCoach[s.coach_id].realisees++; seancesParCoach[s.coach_id].heures += Number(s.duree_h||0); }
              else if (s.statut === "annulee") seancesParCoach[s.coach_id].annulees++;
            });
            const PAGE_SIZE = 6;
            const totalPages = Math.max(1, Math.ceil(equipe.length / PAGE_SIZE));
            const pageSlice  = equipe.slice(equipePage * PAGE_SIZE, (equipePage + 1) * PAGE_SIZE);
            return (
              <div>
                {/* Modal séances réelles du coach (mois en cours) */}
                {coachNotesModal && (() => {
                  const mesSeances = svSeancesReel.filter(s => s.coach_id === coachNotesModal.id).sort((a,b)=> (b.date_seance||"").localeCompare(a.date_seance||""));
                  return (
                    <ModaleAnimee ouvert
                      onClicVoile={() => setCoachNotesModal(null)}
                      style={{ position:"fixed", inset:0, background:"rgba(15,23,42,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:199999, backdropFilter:"blur(4px)" }}
                      panneauStyle={{ background:"#fff", borderRadius:16, width:"90vw", maxWidth:800, maxHeight:"85vh", display:"flex", flexDirection:"column", boxShadow:"0 24px 60px rgba(0,0,0,0.3)", overflow:"hidden" }}>
                        <div style={{ background:BET_GRADIENT, padding:"18px 22px", display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
                          <div>
                            <div style={{ fontWeight:800, fontSize:16, color:"#fff" }}>📓 Séances du mois - {coachNotesModal.prenom} {coachNotesModal.nom}</div>
                            <div style={{ fontSize:12, color:"rgba(255,255,255,0.7)", marginTop:3 }}>{mesSeances.length} séance{mesSeances.length!==1?"s":""} ce mois-ci</div>
                          </div>
                          <button onClick={() => setCoachNotesModal(null)}
                            style={{ background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)", borderRadius:8, width:32, height:32, cursor:"pointer", fontSize:18, color:"#fff" }}>×</button>
                        </div>
                        <div style={{ overflowY:"auto", flex:1, padding:"16px 20px" }}>
                          {mesSeances.length === 0 ? (
                            <div style={{ textAlign:"center", padding:"50px 0", color:"#9ca3af", fontSize:14 }}>Aucune séance ce mois-ci.</div>
                          ) : (
                            <table style={{ width:"100%", borderCollapse:"collapse" }}>
                              <thead><tr style={{ background:"#f1f5f9" }}>
                                {["Date","Titre","Durée","Statut"].map(h=><th key={h} style={{ padding:"9px 12px", fontSize:10, fontWeight:700, color:"#64748b", textAlign:"left", textTransform:"uppercase" }}>{h}</th>)}
                              </tr></thead>
                              <tbody>
                                {mesSeances.map(s => (
                                  <tr key={s.id} style={{ borderBottom:"1px solid #f1f5f9" }}>
                                    <td style={{ padding:"9px 12px", fontSize:12, fontWeight:700 }}>{s.date_seance}</td>
                                    <td style={{ padding:"9px 12px", fontSize:12 }}>{s.titre||"-"}</td>
                                    <td style={{ padding:"9px 12px", fontSize:12 }}>{s.duree_h||0}h</td>
                                    <td style={{ padding:"9px 12px" }}>
                                      <span style={{ padding:"2px 8px", borderRadius:8, fontSize:11, fontWeight:700, background: s.statut==="realisee"?"#dcfce7":s.statut==="annulee"?"#fee2e2":"#fef3c7", color: s.statut==="realisee"?"#16a34a":s.statut==="annulee"?"#dc2626":"#d97706" }}>{s.statut}</span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                    </ModaleAnimee>
                  );
                })()}

                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:18 }}>
                  <StatCard label="Coachs de l'équipe" value={equipe.length} icon="👨‍🏫" color="#0369a1" />
                  <StatCard label="Séances réalisées (mois)" value={Object.values(seancesParCoach).reduce((s,c)=>s+c.realisees,0)} icon="✅" color="#16a34a" />
                  <StatCard label="Séances annulées (mois)" value={Object.values(seancesParCoach).reduce((s,c)=>s+c.annulees,0)} icon="⚠️" color="#dc2626" />
                </div>

                <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", overflow:"hidden" }}>
                  <div style={{ padding:"10px 16px", background:"#f8fafc", borderBottom:"1px solid #e2e8f0", fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase" }}>
                    👨‍🏫 Équipe ({equipe.length})
                  </div>
                  {pageSlice.length === 0 && <div style={{ textAlign:"center", padding:30, fontSize:12, color:"#9ca3af" }}>Aucun coach dans ce périmètre</div>}
                  {pageSlice.map((c) => {
                    const st = seancesParCoach[c.id] || { realisees:0, annulees:0, heures:0 };
                    return (
                      <div key={c.id} onClick={() => setCoachNotesModal(c)} style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr", padding:"12px 16px", borderBottom:"1px solid #f1f5f9", alignItems:"center", fontSize:13, cursor:"pointer" }}
                        onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                        <div style={{ fontWeight:700, color:"#1e293b" }}>{c.prenom} {c.nom}</div>
                        <div style={{ fontSize:12, color:c.actif!==false?"#16a34a":"#9ca3af" }}>{c.actif!==false?"● Actif":"● Inactif"}</div>
                        <div style={{ fontSize:12, color:"#16a34a", fontWeight:700 }}>{st.realisees} réalisées</div>
                        <div style={{ fontSize:12, color:st.annulees>0?"#dc2626":"#9ca3af", fontWeight:700 }}>{st.annulees} annulées</div>
                        <div style={{ fontSize:12, color:"#0369a1", fontWeight:700 }}>{st.heures}h</div>
                      </div>
                    );
                  })}
                </div>
                {totalPages > 1 && (
                  <div style={{ display:"flex", justifyContent:"center", gap:6, marginTop:12 }}>
                    {Array.from({length:totalPages},(_,i)=>i).map(n=>(
                      <button key={n} onClick={()=>setEquipePage(n)} style={{ width:28, height:28, border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:11, background:n===equipePage?BET_COLOR:"#f1f5f9", color:n===equipePage?"#fff":"#374151" }}>{n+1}</button>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ════ INCIDENTS (deputy) ════ */}
          {tab === "incidents" && (
            <div style={{ padding:"8px 14px", background:"#e0f2fe", border:"1px solid #bae6fd", borderRadius:8, fontSize:12, color:"#0369a1" }}>
              ℹ️ Aucun système de suivi d'incidents n'est encore en base - cet onglet n'a pas de donnée réelle à afficher pour l'instant.
            </div>
          )}

          {/* ════ RAPPORTS (deputy) ════ */}
          {tab === "rapports" && (
            <div style={{ padding:"8px 14px", background:"#e0f2fe", border:"1px solid #bae6fd", borderRadius:8, fontSize:12, color:"#0369a1" }}>
              ℹ️ Aucun système de rapports hebdomadaires n'est encore en base - cet onglet n'a pas de donnée réelle à afficher pour l'instant.
            </div>
          )}

          {/* ════ VUE NATIONALE (superviseur) ════ */}
          {tab === "vue_nationale" && (() => {
            if (svCentresReel === null || svCoachsReel === null || svSeancesReel === null) return <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>⏳ Chargement…</div>;
            const realisees = svSeancesReel.filter(s=>s.statut==="realisee").length;
            const annulees  = svSeancesReel.filter(s=>s.statut==="annulee").length;
            const parCentre = svCentresReel.map(centre => {
              const coachsCentre = svCoachsReel.filter(c => (c.scope||[]).includes(centre.id));
              const seancesCentre = svSeancesReel.filter(s => coachsCentre.some(c=>c.id===s.coach_id));
              return {
                nom: centre.nom || centre.ville, id: centre.id,
                nbCoachs: coachsCentre.length,
                realisees: seancesCentre.filter(s=>s.statut==="realisee").length,
                annulees: seancesCentre.filter(s=>s.statut==="annulee").length,
              };
            });
            return (
              <div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:18 }}>
                  <StatCard label="Centres" value={svCentresReel.length} icon="🏢" color="#0891b2" />
                  <StatCard label="Coachs actifs" value={svCoachsReel.filter(c=>c.actif!==false).length} icon="👨‍🏫" color="#0369a1" />
                  <StatCard label="Séances réalisées (mois)" value={realisees} icon="✅" color="#16a34a" />
                  <StatCard label="Séances annulées (mois)" value={annulees} icon="⚠️" color="#dc2626" />
                </div>
                <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", overflow:"hidden" }}>
                  <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr", padding:"10px 16px", background:"#f8fafc", borderBottom:"1px solid #e5e7eb", fontSize:10, fontWeight:700, color:"#9ca3af", textTransform:"uppercase" }}>
                    <span>Centre</span><span>Coachs</span><span>Réalisées</span><span>Annulées</span>
                  </div>
                  {parCentre.map(c => (
                    <div key={c.id} style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr", padding:"11px 16px", borderBottom:"1px solid #f1f5f9", alignItems:"center", fontSize:13 }}>
                      <div style={{ fontWeight:700, color:"#1e293b" }}>{c.nom}</div>
                      <div>{c.nbCoachs}</div>
                      <div style={{ color:"#16a34a", fontWeight:700 }}>{c.realisees}</div>
                      <div style={{ color:c.annulees>0?"#dc2626":"#9ca3af", fontWeight:700 }}>{c.annulees}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* ════ DEPUTIES (superviseur) ════ */}
          {tab === "deputies" && (() => {
            if (svDeputiesReel === null) return <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>⏳ Chargement…</div>;
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                <div style={{ fontSize:12, color:"#9ca3af", marginBottom:4, fontStyle:"italic" }}>👥 {svDeputiesReel.length} deputy superviseur{svDeputiesReel.length>1?"s":""}</div>
                {svDeputiesReel.length === 0 && <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>Aucun deputy superviseur</div>}
                {svDeputiesReel.map((d) => (
                  <div key={d.id} style={{ background:"#fff", border:"1.5px solid #e5e7eb", borderRadius:12, padding:"13px 16px", display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:38, height:38, borderRadius:"50%", background:"#e0f2fe", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0, fontWeight:800, color:"#0369a1" }}>
                      {(d.prenom?.[0]||"")}{(d.nom?.[0]||"")}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:13, color:"#1e293b" }}>{d.prenom} {d.nom}</div>
                      <div style={{ fontSize:11, color:"#6b7280", marginTop:2 }}>{d.email} · {d.scope?.length ? d.scope.join(", ") : "National"}</div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <span style={{ background:d.actif!==false?"#dcfce7":"#f3f4f6", color:d.actif!==false?"#16a34a":"#6b7280", borderRadius:8, padding:"3px 10px", fontSize:11, fontWeight:700 }}>{d.actif!==false?"Actif":"Inactif"}</span>
                      <div style={{ fontSize:10, color:"#9ca3af", marginTop:4 }}>{d.derniere_connexion ? new Date(d.derniere_connexion).toLocaleDateString("fr-FR") : "-"}</div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* ════ ESCALADES (superviseur) ════ */}
          {tab === "escalades" && (
            <div style={{ padding:"8px 14px", background:"#e0f2fe", border:"1px solid #bae6fd", borderRadius:8, fontSize:12, color:"#0369a1" }}>
              ℹ️ Aucun système d'escalades n'est encore en base - cet onglet n'a pas de donnée réelle à afficher pour l'instant.
            </div>
          )}

          {/* ════ SYNTHESE (superviseur) ════ */}
          {tab === "synthese" && (() => {
            if (svCentresReel === null || svCoachsReel === null || svSeancesReel === null) return <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>⏳ Chargement…</div>;
            return (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:14 }}>
                <StatCard label="Centres actifs" value={svCentresReel.length} icon="🏢" color="#0891b2" />
                <StatCard label="Coachs actifs" value={svCoachsReel.filter(c=>c.actif!==false).length} icon="👨‍🏫" color="#0369a1" />
                <StatCard label="Séances réalisées (mois)" value={svSeancesReel.filter(s=>s.statut==="realisee").length} icon="✅" color="#16a34a" />
                <StatCard label="Séances annulées (mois)" value={svSeancesReel.filter(s=>s.statut==="annulee").length} icon="⚠️" color="#dc2626" />
              </div>
            );
          })()}

          {/* ════ SUIVI PEDAGO (pedagogical_advisor) ════ */}
          {tab === "suivi_pedago" && (() => {
            const STA_PA = { en_attente:{bg:"#fef3c7",c:"#d97706",l:"En attente"}, en_cours:{bg:"#e0f2fe",c:"#0369a1",l:"En cours"}, termine:{bg:"#dcfce7",c:"#16a34a",l:"Terminé"}, annule:{bg:"#fee2e2",c:"#dc2626",l:"Annulé"} };
            if (paContratsPrivesReel === null) return <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>⏳ Chargement…</div>;
            const contrats = paContratsPrivesReel;
            const KPIS_REELS = [
              { lib:"Cours privés actifs", val:contrats.filter(c=>c.statut==="en_cours").length, icon:"📚", color:"#7c3aed" },
              { lib:"Coaches distincts",   val:new Set(contrats.map(c=>c.coach_id)).size,        icon:"👨‍🏫", color:"#0891b2" },
              { lib:"En attente",          val:contrats.filter(c=>c.statut==="en_attente").length,icon:"⏳", color:"#d97706" },
              { lib:"Terminés",            val:contrats.filter(c=>c.statut==="termine").length,   icon:"✅", color:"#16a34a" },
            ];
            return (
              <div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:18 }}>
                  {KPIS_REELS.map(k => (
                    <div key={k.lib} style={{ background:"#fff", border:"1.5px solid #e5e7eb", borderRadius:12, padding:"14px 12px", textAlign:"center" }}>
                      <div style={{ fontSize:22 }}>{k.icon}</div>
                      <div style={{ fontSize:20, fontWeight:900, color:k.color, margin:"6px 0 2px" }}>{k.val}</div>
                      <div style={{ fontSize:10, color:"#9ca3af", fontWeight:700, textTransform:"uppercase", lineHeight:1.3 }}>{k.lib}</div>
                    </div>
                  ))}
                </div>
                <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", overflow:"hidden" }}>
                  <div style={{ padding:"10px 16px", background:"#f8fafc", borderBottom:"1px solid #e2e8f0", fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase" }}>
                    🎓 Cours privés suivis ({contrats.length})
                  </div>
                  {contrats.length === 0 && <div style={{ textAlign:"center", padding:30, fontSize:12, color:"#9ca3af" }}>Aucun cours privé créé par ce conseiller</div>}
                  {contrats.map((a) => {
                    const sta = STA_PA[a.statut]||STA_PA.en_attente;
                    return (
                      <div key={a.id} style={{ display:"grid", gridTemplateColumns:"2fr 1.5fr 1fr 1fr 110px", padding:"11px 16px", borderBottom:"1px solid #f1f5f9", alignItems:"center", fontSize:13 }}>
                        <div style={{ fontWeight:700, color:"#1e293b" }}>{a.apprenant_prenom} {a.apprenant_nom}</div>
                        <div style={{ fontSize:12, color:"#6b7280" }}>{a.niveau||"-"}</div>
                        <div style={{ fontSize:12, color:"#6b7280" }}>{a.type_contrat}</div>
                        <span style={{ background:sta.bg, color:sta.c, borderRadius:8, padding:"2px 8px", fontSize:11, fontWeight:700, width:"fit-content" }}>{sta.l}</span>
                        <div style={{ fontSize:11, color:"#9ca3af", textAlign:"right" }}>📅 {a.date_debut||"-"}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ════ EVALUATIONS PA ════ */}
          {tab === "evaluations_pa" && (
            <div style={{ padding:"8px 14px", background:"#e0f2fe", border:"1px solid #bae6fd", borderRadius:8, fontSize:12, color:"#0369a1" }}>
              ℹ️ Aucun système de bilan/évaluation n'est suivi en base pour les cours privés - cet onglet n'a pas de donnée réelle à afficher pour l'instant.
            </div>
          )}

          {/* ════ PLANS ACTION PA ════ */}
          {tab === "plans_action" && (
            <div style={{ padding:"8px 14px", background:"#e0f2fe", border:"1px solid #bae6fd", borderRadius:8, fontSize:12, color:"#0369a1" }}>
              ℹ️ Aucun système de plan d'action pédagogique n'est suivi en base - cet onglet n'a pas de donnée réelle à afficher pour l'instant.
            </div>
          )}
          {/* ════ PAIE (rh) ════ */}
          {tab === "paie" && (() => {
            const STA = { paye:{bg:"#dcfce7",c:"#16a34a",l:"Payé"}, transmis:{bg:"#e0f2fe",c:"#0369a1",l:"Transmis"}, valide:{bg:"#f3e8ff",c:"#7c3aed",l:"Validé"}, en_attente:{bg:"#fef3c7",c:"#d97706",l:"En attente"}, litige:{bg:"#fee2e2",c:"#dc2626",l:"Litige"} };
            if (rhHonorairesReel === null) return <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>⏳ Chargement…</div>;
            const mois6 = derniersMoisSA(6);
            const periodeLabelToKey = Object.fromEntries(mois6.map(m => [m.label, m]));
            const periodeActuelle = periodeLabelToKey[paiePeriode] || mois6[mois6.length-1];
            const lignes = rhHonorairesReel.filter(h => h.mois === periodeActuelle.mois && h.annee === periodeActuelle.annee);
            const totalBrut = lignes.reduce((s,h)=>s+Number(h.montant_total||0),0);
            const totalNet  = lignes.reduce((s,h)=>s+Number(h.montant_net||0),0);
            const staffAPayer = lignes.filter(h=>h.statut!=="paye").length;
            const lignesTotal = lignes.length;
            const totalPages  = Math.max(1, Math.ceil(lignesTotal / PAIE_PER_PAGE));
            const lignesPaged = lignes.slice(paiePage * PAIE_PER_PAGE, (paiePage + 1) * PAIE_PER_PAGE);
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                {/* Filtre période */}
                <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                  <select value={paiePeriode} onChange={e=>{ setPaiePeriode(e.target.value); setPaiePage(0); }}
                    style={{ padding:"6px 10px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:12, background:"#fff", fontWeight:600 }}>
                    {mois6.map(m=><option key={m.label} value={m.label}>{m.label}</option>)}
                  </select>
                  <div style={{ fontSize:11, color:"#9ca3af", marginLeft:"auto" }}>{lignesTotal} ligne{lignesTotal>1?"s":""}</div>
                </div>

                {/* KPIs période */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
                  <div style={{ background:"#f0fdf4", borderRadius:12, padding:"12px 14px", textAlign:"center" }}>
                    <div style={{ fontSize:10, color:"#9ca3af", fontWeight:700, textTransform:"uppercase", marginBottom:3 }}>Total brut</div>
                    <div style={{ fontSize:17, fontWeight:900, color:"#16a34a" }}>{(totalBrut/1000000).toFixed(2)} M</div>
                  </div>
                  <div style={{ background:"#f0f9ff", borderRadius:12, padding:"12px 14px", textAlign:"center" }}>
                    <div style={{ fontSize:10, color:"#9ca3af", fontWeight:700, textTransform:"uppercase", marginBottom:3 }}>Total net</div>
                    <div style={{ fontSize:17, fontWeight:900, color:"#0284c7" }}>{(totalNet/1000000).toFixed(2)} M</div>
                  </div>
                  <div style={{ background:"#fef3c7", borderRadius:12, padding:"12px 14px", textAlign:"center" }}>
                    <div style={{ fontSize:10, color:"#9ca3af", fontWeight:700, textTransform:"uppercase", marginBottom:3 }}>Staff à payer</div>
                    <div style={{ fontSize:17, fontWeight:900, color:"#d97706" }}>{staffAPayer}</div>
                  </div>
                </div>

                {/* Tableau */}
                <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", overflow:"hidden" }}>
                  <div style={{ padding:"10px 16px", background:"#f8fafc", borderBottom:"1px solid #e2e8f0", fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase" }}>
                    💰 Paie {paiePeriode}
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 90px", padding:"8px 16px", background:"#f8fafc", borderBottom:"1px solid #e5e7eb", fontSize:10, fontWeight:700, color:"#9ca3af", textTransform:"uppercase" }}>
                    <span>Coach</span><span>Séances</span><span>Brut</span><span>Net</span><span>Statut</span>
                  </div>
                  {lignesPaged.length === 0 && <div style={{ textAlign:"center", padding:30, fontSize:12, color:"#9ca3af" }}>Aucune fiche pour cette période</div>}
                  {lignesPaged.map((l) => {
                    const sta = STA[l.statut]||STA.en_attente;
                    const nomCoach = l.coach ? `${l.coach.prenom||""} ${l.coach.nom||""}`.trim() : "-";
                    return (
                      <div key={l.id}
                        onClick={()=>setPaieDetail(l)}
                        style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 90px", padding:"11px 16px", borderBottom:"1px solid #f1f5f9", alignItems:"center", fontSize:13, cursor:"pointer", transition:"background .12s" }}
                        onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                        onMouseLeave={e=>e.currentTarget.style.background=""}>
                        <div style={{ fontWeight:700, color:"#1e293b" }}>{nomCoach}</div>
                        <div style={{ fontSize:12, color:"#374151" }}>{l.nb_seances||0} séances</div>
                        <div style={{ fontSize:12, fontWeight:700, color:"#374151" }}>{(Number(l.montant_total||0)/1000).toFixed(0)}k</div>
                        <div style={{ fontSize:12, fontWeight:700, color:"#374151" }}>{(Number(l.montant_net||0)/1000).toFixed(0)}k</div>
                        <span style={{ background:sta.bg, color:sta.c, borderRadius:8, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{sta.l}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:6 }}>
                    <button onClick={()=>setPaiePage(p=>Math.max(0,p-1))} disabled={paiePage===0} style={{ padding:"5px 10px", border:"1px solid #e2e8f0", borderRadius:7, cursor:paiePage===0?"default":"pointer", background:"#fff", color:paiePage===0?"#d1d5db":"#374151", fontSize:11, fontWeight:600 }}>← Préc.</button>
                    {Array.from({length:totalPages},(_,i)=>i).map(n=>(
                      <button key={n} onClick={()=>setPaiePage(n)} style={{ width:28, height:28, border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:11, background:n===paiePage?BET_COLOR:"#f1f5f9", color:n===paiePage?"#fff":"#374151" }}>{n+1}</button>
                    ))}
                    <button onClick={()=>setPaiePage(p=>Math.min(totalPages-1,p+1))} disabled={paiePage===totalPages-1} style={{ padding:"5px 10px", border:"1px solid #e2e8f0", borderRadius:7, cursor:paiePage===totalPages-1?"default":"pointer", background:"#fff", color:paiePage===totalPages-1?"#d1d5db":"#374151", fontSize:11, fontWeight:600 }}>Suiv. →</button>
                  </div>
                )}

                {/* Modal détail ligne paie */}
                {paieDetail && (() => {
                  const sta = STA[paieDetail.statut]||STA.en_attente;
                  const nomCoach = paieDetail.coach ? `${paieDetail.coach.prenom||""} ${paieDetail.coach.nom||""}`.trim() : "-";
                  const historique = rhHonorairesReel.filter(h => h.coach_id === paieDetail.coach_id).sort((a,b)=> (b.annee*12+b.mois) - (a.annee*12+a.mois));
                  return (
                    <ModaleAnimee ouvert
                      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:10000, padding:16 }}
                      panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:"90vw", padding:24, boxShadow:"0 24px 64px rgba(0,0,0,.2)", maxHeight:"90vh", overflowY:"auto" }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
                          <div style={{ fontWeight:800, fontSize:15, color:"#1e293b" }}>💰 Détail paie - {nomCoach}</div>
                          <button onClick={()=>setPaieDetail(null)} style={{ background:"#f1f5f9", border:"none", borderRadius:8, width:30, height:30, fontSize:15, cursor:"pointer" }}>✕</button>
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
                          {[
                            { l:"Période",   v:`${MOIS_LABELS_C[paieDetail.mois]} ${paieDetail.annee}` },
                            { l:"Séances",   v:paieDetail.nb_seances||0 },
                            { l:"Heures",    v:`${paieDetail.total_heures||0}h` },
                            { l:"Brut",      v:`${(Number(paieDetail.montant_total||0)/1000).toFixed(0)}k FCFA`, hi:true },
                            { l:"Net",       v:`${(Number(paieDetail.montant_net||0)/1000).toFixed(0)}k FCFA`, hi:true },
                            { l:"Statut",    v:sta.l },
                            { l:"Mode paiement", v:paieDetail.mode_paiement||"-" },
                            { l:"Référence",  v:paieDetail.reference_paiement||"-" },
                          ].map(f=>(
                            <div key={f.l} style={{ background:"#f8fafc", borderRadius:8, padding:"8px 10px" }}>
                              <div style={{ fontSize:10, color:"#9ca3af", fontWeight:700, marginBottom:2 }}>{f.l}</div>
                              <div style={{ fontSize:13, fontWeight:f.hi?900:700, color:f.hi?"#16a34a":"#1e293b" }}>{f.v}</div>
                            </div>
                          ))}
                        </div>
                        {historique.length > 1 && (
                          <>
                            <div style={{ fontWeight:700, fontSize:12, color:"#374151", marginBottom:8 }}>📅 Historique - {nomCoach}</div>
                            <div style={{ background:"#f8fafc", borderRadius:10, overflow:"hidden" }}>
                              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                                <thead><tr style={{ background:"#f1f5f9" }}>
                                  {["Période","Séances","Net","Statut"].map(h=><th key={h} style={{ padding:"7px 10px", fontSize:10, fontWeight:700, color:"#6b7280", textAlign:"left", borderBottom:"1px solid #e5e7eb", textTransform:"uppercase" }}>{h}</th>)}
                                </tr></thead>
                                <tbody>
                                  {historique.map(h=>{
                                    const s = STA[h.statut]||STA.en_attente;
                                    return (
                                      <tr key={h.id} style={{ background:h.id===paieDetail.id?"#f0f9ff":"" }}>
                                        <td style={{ padding:"7px 10px", fontSize:12, fontWeight:h.id===paieDetail.id?700:400 }}>{MOIS_LABELS_C[h.mois]} {h.annee}{h.id===paieDetail.id?" ◀":""}</td>
                                        <td style={{ padding:"7px 10px", fontSize:12 }}>{h.nb_seances||0}</td>
                                        <td style={{ padding:"7px 10px", fontSize:12, fontWeight:700, color:"#16a34a" }}>{(Number(h.montant_net||0)/1000).toFixed(0)}k</td>
                                        <td style={{ padding:"7px 10px" }}><span style={{ background:s.bg, color:s.c, borderRadius:6, padding:"2px 7px", fontSize:10, fontWeight:700 }}>{s.l}</span></td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                        <div style={{ display:"flex", justifyContent:"flex-end", marginTop:14 }}>
                          <button onClick={()=>setPaieDetail(null)} style={{ padding:"7px 16px", border:"1px solid #e2e8f0", borderRadius:8, cursor:"pointer", background:"#fff", fontSize:12, fontWeight:600 }}>Fermer</button>
                        </div>
                    </ModaleAnimee>
                  );
                })()}
              </div>
            );
          })()}

          {/* ════ CONTRATS RH ════ */}
          {tab === "contrats_rh" && (() => {
            const STA_C  = { true:{bg:"#dcfce7",c:"#16a34a",l:"Actif"}, false:{bg:"#f3f4f6",c:"#6b7280",l:"Inactif"} };
            if (rhUtilisateursReel === null) return <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>⏳ Chargement…</div>;
            const roster = rhUtilisateursReel;
            const roles = ["Tous", ...new Set(roster.map(u=>u.role))];
            const filtered = roster.filter(c => contratTypeFilter === "Tous" || c.role === contratTypeFilter);
            const totalPg  = Math.max(1, Math.ceil(filtered.length / CONTRAT_PER_PAGE));
            const paged    = filtered.slice(contratPage * CONTRAT_PER_PAGE, (contratPage + 1) * CONTRAT_PER_PAGE);
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                <div style={{ padding:"8px 14px", background:"#e0f2fe", border:"1px solid #bae6fd", borderRadius:8, fontSize:11, color:"#0369a1" }}>
                  ℹ️ Aucun type de contrat (CDI/CDD) ni tarif horaire n'est suivi en base pour le staff - seuls le rôle et le statut réels sont affichés ici.
                </div>
                {/* Filtres */}
                <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                  <select value={contratTypeFilter} onChange={e=>{ setContratTypeFilter(e.target.value); setContratPage(0); }}
                    style={{ padding:"6px 10px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:12, background:"#fff", fontWeight:600 }}>
                    {roles.map(r=><option key={r} value={r}>{r==="Tous"?"Tous rôles":(ROLES_DEF[r]?.label||r)}</option>)}
                  </select>
                  <div style={{ fontSize:11, color:"#9ca3af", marginLeft:"auto" }}>{filtered.length} profil{filtered.length>1?"s":""}</div>
                </div>

                {/* Tableau */}
                <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", overflow:"hidden" }}>
                  <div style={{ padding:"10px 16px", background:"#f8fafc", borderBottom:"1px solid #e2e8f0", fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase" }}>
                    📋 Effectif staff ({filtered.length})
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"2fr 1.5fr 1.1fr 70px 40px", padding:"8px 16px", background:"#f8fafc", borderBottom:"1px solid #e5e7eb", fontSize:10, fontWeight:700, color:"#9ca3af", textTransform:"uppercase" }}>
                    <span>Nom</span><span>Rôle</span><span>Depuis</span><span>Statut</span><span></span>
                  </div>
                  {paged.map((c) => {
                    const rd  = ROLES_DEF[c.role] || { label:c.role, emoji:"👤", color:"#64748b" };
                    const sta = STA_C[String(c.actif !== false)];
                    return (
                      <div key={c.id}
                        onClick={()=>setContratDetail(c)}
                        style={{ display:"grid", gridTemplateColumns:"2fr 1.5fr 1.1fr 70px 40px", padding:"11px 16px", borderBottom:"1px solid #f1f5f9", alignItems:"center", fontSize:13, cursor:"pointer", transition:"background .12s" }}
                        onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                        onMouseLeave={e=>e.currentTarget.style.background=""}>
                        <div style={{ fontWeight:700, color:"#1e293b" }}>{c.prenom} {c.nom}</div>
                        <span style={{ background:rd.color+"18", color:rd.color, borderRadius:8, padding:"2px 8px", fontSize:11, fontWeight:700, width:"fit-content" }}>{rd.emoji} {rd.label}</span>
                        <div style={{ fontSize:12, color:"#6b7280" }}>{c.date_creation ? new Date(c.date_creation).toLocaleDateString("fr-FR") : "-"}</div>
                        <span style={{ background:sta.bg, color:sta.c, borderRadius:8, padding:"2px 7px", fontSize:11, fontWeight:700 }}>{sta.l}</span>
                        <span style={{ fontSize:11, color:BET_COLOR, fontWeight:600 }}>→</span>
                      </div>
                    );
                  })}
                  {filtered.length === 0 && (
                    <div style={{ textAlign:"center", padding:30, fontSize:12, color:"#9ca3af" }}>Aucun profil pour ce filtre</div>
                  )}
                </div>

                {/* Pagination */}
                {totalPg > 1 && (
                  <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:6 }}>
                    <button onClick={()=>setContratPage(p=>Math.max(0,p-1))} disabled={contratPage===0} style={{ padding:"5px 10px", border:"1px solid #e2e8f0", borderRadius:7, cursor:contratPage===0?"default":"pointer", background:"#fff", color:contratPage===0?"#d1d5db":"#374151", fontSize:11, fontWeight:600 }}>← Préc.</button>
                    {Array.from({length:totalPg},(_,i)=>i).map(n=>(
                      <button key={n} onClick={()=>setContratPage(n)} style={{ width:28, height:28, border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:11, background:n===contratPage?BET_COLOR:"#f1f5f9", color:n===contratPage?"#fff":"#374151" }}>{n+1}</button>
                    ))}
                    <button onClick={()=>setContratPage(p=>Math.min(totalPg-1,p+1))} disabled={contratPage===totalPg-1} style={{ padding:"5px 10px", border:"1px solid #e2e8f0", borderRadius:7, cursor:contratPage===totalPg-1?"default":"pointer", background:"#fff", color:contratPage===totalPg-1?"#d1d5db":"#374151", fontSize:11, fontWeight:600 }}>Suiv. →</button>
                  </div>
                )}

                {/* Modal détail profil */}
                {contratDetail && (() => {
                  const c   = contratDetail;
                  const rd  = ROLES_DEF[c.role] || { label:c.role, emoji:"👤", color:"#64748b" };
                  const sta = STA_C[String(c.actif !== false)];
                  return (
                    <ModaleAnimee ouvert
                      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:10000, padding:16 }}
                      panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:"90vw", padding:24, boxShadow:"0 24px 64px rgba(0,0,0,.2)", maxHeight:"90vh", overflowY:"auto" }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
                          <div style={{ fontWeight:800, fontSize:15, color:"#1e293b" }}>👤 Fiche profil - {c.prenom} {c.nom}</div>
                          <button onClick={()=>setContratDetail(null)} style={{ background:"#f1f5f9", border:"none", borderRadius:8, width:30, height:30, fontSize:15, cursor:"pointer" }}>✕</button>
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
                          {[
                            { l:"Nom",       v:`${c.prenom} ${c.nom}` },
                            { l:"Email",     v:c.email },
                            { l:"Rôle",      v:<span style={{ background:rd.color+"18", color:rd.color, borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{rd.emoji} {rd.label}</span> },
                            { l:"Périmètre", v:c.scope?.length ? c.scope.join(", ") : "National" },
                            { l:"Statut",    v:<span style={{ background:sta.bg, color:sta.c, borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{sta.l}</span> },
                            { l:"Depuis",    v:c.date_creation ? new Date(c.date_creation).toLocaleDateString("fr-FR") : "-" },
                            { l:"Dernière connexion", v:c.derniere_connexion ? new Date(c.derniere_connexion).toLocaleDateString("fr-FR") : "-" },
                            ...(c.coach_info?.filiere ? [{ l:"Filière", v:c.coach_info.filiere }] : []),
                            ...(c.coach_info?.niveaux_enseignes?.length ? [{ l:"Niveaux enseignés", v:c.coach_info.niveaux_enseignes.join(", ") }] : []),
                          ].map(f=>(
                            <div key={f.l} style={{ background:"#f8fafc", borderRadius:8, padding:"8px 10px" }}>
                              <div style={{ fontSize:10, color:"#9ca3af", fontWeight:700, marginBottom:2 }}>{f.l}</div>
                              <div style={{ fontSize:13, fontWeight:700, color:"#1e293b" }}>{f.v}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{ display:"flex", justifyContent:"flex-end" }}>
                          <button onClick={()=>setContratDetail(null)} style={{ padding:"7px 16px", border:"1px solid #e2e8f0", borderRadius:8, cursor:"pointer", background:"#fff", fontSize:12, fontWeight:600 }}>Fermer</button>
                        </div>
                    </ModaleAnimee>
                  );
                })()}
              </div>
            );
          })()}

          {/* ════ EFFECTIFS RH ════ */}
          {tab === "effectifs" && (() => {
            if (rhUtilisateursReel === null) return <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>⏳ Chargement…</div>;
            const roster = rhUtilisateursReel;
            const total = roster.length;
            const parRole = Object.entries(
              roster.reduce((acc,u)=>{ acc[u.role] = (acc[u.role]||0)+1; return acc; }, {})
            ).map(([role,count]) => ({ role, count, def: ROLES_DEF[role] || { label:role, color:"#64748b" } }))
             .sort((a,b) => b.count - a.count);
            const grille = rhGrilleReel || {};
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
                <div style={{ display:"flex", gap:10 }}>
                  <div style={{ background:"#f0f9ff", borderRadius:12, padding:"16px 20px", flex:1, textAlign:"center" }}>
                    <div style={{ fontSize:32, fontWeight:900, color:"#0284c7" }}>{total}</div>
                    <div style={{ fontSize:11, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>Total effectif</div>
                  </div>
                  <div style={{ flex:2, display:"flex", flexDirection:"column", gap:8, justifyContent:"center" }}>
                    {parRole.map((r2) => (
                      <div key={r2.role} style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ width:150, fontSize:12, color:"#374151", fontWeight:600 }}>{r2.def.emoji} {r2.def.label}</div>
                        <div style={{ flex:1, height:16, background:"#e5e7eb", borderRadius:8, overflow:"hidden" }}>
                          <div style={{ height:"100%", width:`${Math.round(r2.count/total*100)}%`, background:r2.def.color, borderRadius:8 }} />
                        </div>
                        <div style={{ width:30, fontSize:12, fontWeight:800, color:r2.def.color, textAlign:"right" }}>{r2.count}</div>
                      </div>
                    ))}
                  </div>
                </div>
                {grille.tarifs && (
                  <div>
                    <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:10 }}>💰 Grille tarifaire coachs (FCFA / heure)</div>
                    <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", overflow:"hidden" }}>
                      <div style={{ display:"grid", gridTemplateColumns:"1.2fr 1fr 1fr 1fr", padding:"8px 16px", background:"#f8fafc", borderBottom:"1px solid #e5e7eb", fontSize:10, fontWeight:700, color:"#9ca3af", textTransform:"uppercase" }}>
                        <span>Type contrat</span><span>En ligne</span><span>Présentiel</span><span>Domicile</span>
                      </div>
                      {Object.entries(grille.tarifs).map(([type,t],i,arr) => (
                        <div key={type} style={{ display:"grid", gridTemplateColumns:"1.2fr 1fr 1fr 1fr", padding:"10px 16px", borderBottom:i<arr.length-1?"1px solid #f1f5f9":"none", fontSize:13 }}>
                          <div style={{ fontWeight:700, color:"#1e293b" }}>{type}</div>
                          <div>{Number(t.en_ligne||0).toLocaleString("fr-FR")}</div>
                          <div>{Number(t.presentiel||0).toLocaleString("fr-FR")}</div>
                          <div>{Number(t.domicile||0).toLocaleString("fr-FR")}</div>
                        </div>
                      ))}
                    </div>
                    {grille.multiplicateurs && (
                      <div style={{ marginTop:10, fontSize:11, color:"#6b7280" }}>
                        Multiplicateurs par niveau : {Object.entries(grille.multiplicateurs).map(([n,m])=>`${n} ×${m}`).join(" · ")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
          {tab === "apprenants_ob" && (() => {
            const STA = {
              en_attente: {bg:"#fef3c7",c:"#d97706",l:"En attente"},
              en_cours:   {bg:"#e0f2fe",c:"#0369a1",l:"En cours"},
              converti:   {bg:"#dcfce7",c:"#16a34a",l:"Converti ✓"},
              perdu:      {bg:"#fee2e2",c:"#dc2626",l:"Perdu"},
            };
            if (onboardingAssignationsReel === null) return <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>⏳ Chargement…</div>;
            const liste = onboardingAssignationsReel;
            return (
              <div>
                <div style={{ display:"flex", gap:10, marginBottom:14 }}>
                  {[["Convertis","#16a34a",liste.filter(a=>a.statut==="converti").length],["En cours","#0369a1",liste.filter(a=>a.statut==="en_cours").length],["En attente","#d97706",liste.filter(a=>a.statut==="en_attente").length]].map(([l,c,v])=>(
                    <div key={l} style={{ background:"#fff", border:`1.5px solid ${c}30`, borderRadius:10, padding:"10px 16px", flex:1, textAlign:"center" }}>
                      <div style={{ fontSize:20, fontWeight:900, color:c }}>{v}</div>
                      <div style={{ fontSize:10, color:"#6b7280", fontWeight:700 }}>{l}</div>
                    </div>
                  ))}
                </div>
                <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", overflow:"hidden" }}>
                  {liste.length === 0 && <div style={{ textAlign:"center", padding:30, fontSize:12, color:"#9ca3af" }}>Aucun apprenant assigné</div>}
                  {liste.map((a, i) => {
                    const sta = STA[a.statut]||STA.en_attente;
                    return (
                      <div key={a.id} style={{ display:"grid", gridTemplateColumns:"2fr 2fr 160px 1.2fr", padding:"12px 16px", borderBottom:i<liste.length-1?"1px solid #f1f5f9":"none", alignItems:"center", fontSize:13 }}>
                        <div style={{ fontWeight:700, color:"#1e293b" }}>{a.prospect_nom}</div>
                        <div style={{ fontSize:12, color:"#6b7280" }}>{a.type_cours||"-"}</div>
                        <span style={{ background:sta.bg, color:sta.c, borderRadius:8, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{sta.l}</span>
                        <div style={{ fontSize:11, color:"#9ca3af", textAlign:"right" }}>📅 {(a.created_at||"").slice(0,10)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ════ DOSSIERS ONBOARDING ════ */}
          {tab === "dossiers_ob" && (() => {
            if (onboardingAssignationsReel === null) return <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>⏳ Chargement…</div>;
            const incomplets = onboardingAssignationsReel.filter(a => !(a.documents_dossier?.length > 0) && a.statut !== "converti" && a.statut !== "perdu");
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                <div style={{ fontSize:12, color:"#9ca3af", marginBottom:4, fontStyle:"italic" }}>📂 Dossiers sans aucun document déposé (documents_dossier vide)</div>
                {incomplets.length === 0 && <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>Aucun dossier incomplet</div>}
                {incomplets.map((d) => (
                  <div key={d.id} style={{ background:"#fff", border:"1.5px solid #fcd34d30", borderRadius:12, padding:"13px 16px" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <span>⚠️</span>
                        <div style={{ fontWeight:700, fontSize:13, color:"#1e293b" }}>{d.prospect_nom}</div>
                      </div>
                      <span style={{ background:"#fef3c7", color:"#d97706", borderRadius:8, padding:"2px 10px", fontSize:11, fontWeight:700 }}>{d.type_cours||"-"}</span>
                    </div>
                    <div style={{ fontSize:12, color:"#6b7280" }}>📎 Aucun document déposé</div>
                    <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>📅 Assigné le {(d.created_at||"").slice(0,10)}</div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* ════ RDV ONBOARDING ════ */}
          {tab === "rdv_ob" && (
            <div style={{ padding:"8px 14px", background:"#e0f2fe", border:"1px solid #bae6fd", borderRadius:8, fontSize:12, color:"#0369a1" }}>
              ℹ️ Aucun système de prise de rendez-vous n'est suivi en base pour l'onboarding - cet onglet n'a pas de donnée réelle à afficher pour l'instant.
            </div>
          )}

          {/* ════ PAIEMENTS COMPTABLE ════ */}
          {tab === "paiements_c" && (() => {
            const STA  = { recu:{bg:"#dcfce7",c:"#16a34a",l:"Reçu"}, en_attente:{bg:"#fef3c7",c:"#d97706",l:"En attente"} };
            const METH = { virement:"🏦", especes:"💵", mobile_money:"📱", carte:"💳", cheque:"🏦" };
            const source   = paiementsCReel || [];
            const prefix   = PERIODE_C_PREFIX[pmtCPeriode];
            const filtered = source.filter(p => !prefix || (p.date_paiement||"").startsWith(prefix));
            const totalPg  = Math.max(1, Math.ceil(filtered.length / PMT_C_PER_PAGE));
            const paged    = filtered.slice(pmtCPage * PMT_C_PER_PAGE, (pmtCPage + 1) * PMT_C_PER_PAGE);
            const total_recu   = filtered.filter(p=>p.statut==="recu").reduce((s,p)=>s+Number(p.montant||0),0);
            const total_attente = filtered.filter(p=>p.statut!=="recu").reduce((s,p)=>s+Number(p.montant||0),0);
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                {/* Filtre période */}
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <select value={pmtCPeriode} onChange={e=>{ setPmtCPeriode(e.target.value); setPmtCPage(0); }}
                    style={{ padding:"6px 10px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:12, background:"#fff", fontWeight:700, color:BET_COLOR, borderColor:BET_COLOR }}>
                    {PERIODES_C.map(p=><option key={p}>{p}</option>)}
                  </select>
                  <span style={{ fontSize:11, color:"#9ca3af", marginLeft:"auto" }}>{filtered.length} paiement{filtered.length>1?"s":""}</span>
                </div>

                {/* KPIs */}
                <div style={{ display:"flex", gap:10 }}>
                  <div style={{ background:"#f0fdf4", borderRadius:10, padding:"12px 14px", flex:1, textAlign:"center" }}>
                    <div style={{ fontSize:16, fontWeight:900, color:"#16a34a" }}>{(total_recu/1000000).toFixed(2)} M</div>
                    <div style={{ fontSize:10, color:"#6b7280", fontWeight:700 }}>Encaissé</div>
                  </div>
                  <div style={{ background:"#fef3c7", borderRadius:10, padding:"12px 14px", flex:1, textAlign:"center" }}>
                    <div style={{ fontSize:16, fontWeight:900, color:"#d97706" }}>{(total_attente/1000000).toFixed(2)} M</div>
                    <div style={{ fontSize:10, color:"#6b7280", fontWeight:700 }}>En attente</div>
                  </div>
                  <div style={{ background:"#f0f9ff", borderRadius:10, padding:"12px 14px", flex:1, textAlign:"center" }}>
                    <div style={{ fontSize:16, fontWeight:900, color:"#0284c7" }}>{filtered.length}</div>
                    <div style={{ fontSize:10, color:"#6b7280", fontWeight:700 }}>Total lignes</div>
                  </div>
                </div>

                {/* Tableau */}
                <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", overflow:"hidden" }}>
                  <div style={{ display:"grid", gridTemplateColumns:"2fr 1.5fr 1fr 120px 100px 30px", padding:"8px 16px", background:"#f8fafc", borderBottom:"1px solid #e5e7eb", fontSize:10, fontWeight:700, color:"#9ca3af", textTransform:"uppercase" }}>
                    <span>Client</span><span>Type</span><span>Montant</span><span>Date</span><span>Statut</span><span></span>
                  </div>
                  {paiementsCReel === null ? (
                    <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>⏳ Chargement…</div>
                  ) : paged.map((p, i) => {
                    const sta = STA[p.statut]||STA.en_attente;
                    return (
                      <div key={p.id}
                        onClick={()=>setPmtCDetail(p)}
                        style={{ display:"grid", gridTemplateColumns:"2fr 1.5fr 1fr 120px 100px 30px", padding:"11px 16px", borderBottom:i<paged.length-1?"1px solid #f1f5f9":"none", alignItems:"center", fontSize:13, cursor:"pointer", transition:"background .12s" }}
                        onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                        onMouseLeave={e=>e.currentTarget.style.background=""}>
                        <div style={{ fontWeight:700, color:"#1e293b" }}>{p.client_nom}</div>
                        <div style={{ fontSize:12, color:"#6b7280" }}>{p.client_type||"-"}</div>
                        <div style={{ fontSize:13, fontWeight:800, color:"#059669" }}>{(Number(p.montant||0)/1000).toFixed(0)}k</div>
                        <div style={{ fontSize:12, color:"#6b7280" }}>{p.date_paiement}</div>
                        <span style={{ background:sta.bg, color:sta.c, borderRadius:8, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{sta.l}</span>
                        <span style={{ color:BET_COLOR, fontSize:11, fontWeight:600 }}>→</span>
                      </div>
                    );
                  })}
                  {paiementsCReel !== null && filtered.length === 0 && <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>Aucun paiement pour cette période</div>}
                </div>

                {/* Pagination */}
                {totalPg > 1 && (
                  <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:6 }}>
                    <button onClick={()=>setPmtCPage(p=>Math.max(0,p-1))} disabled={pmtCPage===0}
                      style={{ padding:"4px 10px", border:"1px solid #e2e8f0", borderRadius:7, cursor:pmtCPage===0?"default":"pointer", background:"#fff", color:pmtCPage===0?"#d1d5db":"#374151", fontSize:11, fontWeight:600 }}>← Préc.</button>
                    {Array.from({length:totalPg},(_,i)=>i).map(n=>(
                      <button key={n} onClick={()=>setPmtCPage(n)} style={{ width:26, height:26, border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:11, background:n===pmtCPage?BET_COLOR:"#f1f5f9", color:n===pmtCPage?"#fff":"#374151" }}>{n+1}</button>
                    ))}
                    <button onClick={()=>setPmtCPage(p=>Math.min(totalPg-1,p+1))} disabled={pmtCPage===totalPg-1}
                      style={{ padding:"4px 10px", border:"1px solid #e2e8f0", borderRadius:7, cursor:pmtCPage===totalPg-1?"default":"pointer", background:"#fff", color:pmtCPage===totalPg-1?"#d1d5db":"#374151", fontSize:11, fontWeight:600 }}>Suiv. →</button>
                  </div>
                )}

                {/* Modal détail paiement */}
                {pmtCDetail && (() => {
                  const p   = pmtCDetail;
                  const sta = STA[p.statut]||STA.en_attente;
                  const historique = source.filter(x => x.client_nom === p.client_nom && x.id !== p.id);
                  return (
                    <ModaleAnimee ouvert
                      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:10000, padding:16 }}
                      panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:"90vw", padding:24, boxShadow:"0 24px 64px rgba(0,0,0,.2)", maxHeight:"90vh", overflowY:"auto" }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                          <div style={{ fontWeight:800, fontSize:15, color:"#1e293b" }}>💳 Paiement - {p.client_nom}</div>
                          <button onClick={()=>setPmtCDetail(null)} style={{ background:"#f1f5f9", border:"none", borderRadius:8, width:30, height:30, fontSize:14, cursor:"pointer" }}>✕</button>
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
                          {[
                            { l:"Client",      v:p.client_nom },
                            { l:"Type client", v:p.client_type||"-" },
                            { l:"Méthode",     v:`${METH[p.mode_paiement]||"💳"} ${(p.mode_paiement||"-").replace("_"," ")}` },
                            { l:"Date",        v:p.date_paiement },
                            { l:"Statut",      v:<span style={{ background:sta.bg, color:sta.c, borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{sta.l}</span> },
                            { l:"Montant",     v:`${(Number(p.montant||0)/1000).toFixed(0)}k FCFA`, hi:true },
                          ].map(f=>(
                            <div key={f.l} style={{ background:"#f8fafc", borderRadius:8, padding:"8px 10px" }}>
                              <div style={{ fontSize:10, color:"#9ca3af", fontWeight:700, marginBottom:2 }}>{f.l}</div>
                              <div style={{ fontSize:13, fontWeight:f.hi?900:700, color:f.hi?"#059669":"#1e293b" }}>{f.v}</div>
                            </div>
                          ))}
                        </div>
                        {p.notes && (
                          <div style={{ padding:"8px 12px", background:"#fef3c7", borderRadius:8, fontSize:12, color:"#92400e", marginBottom:14 }}>📝 {p.notes}</div>
                        )}
                        {historique.length > 0 && (
                          <>
                            <div style={{ fontWeight:700, fontSize:12, color:"#374151", marginBottom:8 }}>📅 Autres paiements - {p.client_nom}</div>
                            <div style={{ background:"#f8fafc", borderRadius:10, overflow:"hidden", marginBottom:14 }}>
                              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                                <thead><tr style={{ background:"#f1f5f9" }}>
                                  {["Type","Date","Montant","Statut"].map(h=><th key={h} style={{ padding:"6px 10px", fontSize:10, fontWeight:700, color:"#6b7280", textAlign:"left", borderBottom:"1px solid #e5e7eb", textTransform:"uppercase" }}>{h}</th>)}
                                </tr></thead>
                                <tbody>
                                  {historique.map(x=>{
                                    const xs=STA[x.statut]||STA.en_attente;
                                    return (
                                      <tr key={x.id}>
                                        <td style={{ padding:"6px 10px", fontSize:12 }}>{x.client_type||"-"}</td>
                                        <td style={{ padding:"6px 10px", fontSize:12 }}>{x.date_paiement}</td>
                                        <td style={{ padding:"6px 10px", fontSize:12, fontWeight:700, color:"#059669" }}>{(Number(x.montant||0)/1000).toFixed(0)}k</td>
                                        <td style={{ padding:"6px 10px" }}><span style={{ background:xs.bg, color:xs.c, borderRadius:6, padding:"2px 7px", fontSize:10, fontWeight:700 }}>{xs.l}</span></td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                        <div style={{ display:"flex", justifyContent:"flex-end" }}>
                          <button onClick={()=>setPmtCDetail(null)} style={{ padding:"7px 16px", border:"1px solid #e2e8f0", borderRadius:8, cursor:"pointer", background:"#fff", fontSize:12, fontWeight:600 }}>Fermer</button>
                        </div>
                    </ModaleAnimee>
                  );
                })()}
              </div>
            );
          })()}

          {/* ════ TRÉSORERIE COMPTABLE ════ */}
          {tab === "tresorerie" && (() => {
            if (tresorerieCReel === null) return <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>⏳ Chargement…</div>;
            const tr  = tresorerieCReel || {};
            const mvt = mouvementsCReel || [];
            const now = new Date();
            const debutMois = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`;
            const entreesMois = mvt.filter(m => m.type === "entree" && m.date_mouvement >= debutMois);
            const sortiesMois = mvt.filter(m => m.type === "sortie" && m.date_mouvement >= debutMois);
            return (
              <div>
                <div style={{ fontSize:12, color:"#9ca3af", marginBottom:14, fontStyle:"italic" }}>📊 Trésorerie - mois en cours</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:18 }}>
                  {[[tr.solde,"Solde actuel","#0284c7"],[tr.entrees_mois,"Entrées (mois)","#16a34a"],[tr.sorties_mois,"Sorties (mois)","#dc2626"]].map(([v,l,c])=>(
                    <div key={l} style={{ background:"#fff", border:"1.5px solid #e5e7eb", borderRadius:12, padding:"14px 12px", textAlign:"center" }}>
                      <div style={{ fontSize:10, color:"#9ca3af", fontWeight:700, textTransform:"uppercase", marginBottom:6 }}>{l}</div>
                      <div style={{ fontSize:16, fontWeight:900, color:c }}>{(Number(v||0)/1000000).toFixed(2)} M</div>
                      <div style={{ fontSize:10, color:"#9ca3af" }}>FCFA</div>
                    </div>
                  ))}
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", overflow:"hidden" }}>
                    <div style={{ padding:"10px 14px", background:"#f0fdf4", borderBottom:"1px solid #e2e8f0", fontSize:11, fontWeight:700, color:"#16a34a", textTransform:"uppercase" }}>📥 Entrées (mois)</div>
                    {entreesMois.length === 0 && <div style={{ padding:"10px 14px", fontSize:12, color:"#9ca3af" }}>Aucune entrée ce mois-ci</div>}
                    {entreesMois.map((e,i)=>(
                      <div key={e.id||i} style={{ display:"flex", justifyContent:"space-between", padding:"10px 14px", borderBottom:i<entreesMois.length-1?"1px solid #f1f5f9":"none", fontSize:12 }}>
                        <span style={{ color:"#374151" }}>{e.description || e.categorie}</span>
                        <span style={{ fontWeight:700, color:"#16a34a" }}>{(Number(e.montant||0)/1000000).toFixed(2)} M</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", overflow:"hidden" }}>
                    <div style={{ padding:"10px 14px", background:"#fff1f2", borderBottom:"1px solid #e2e8f0", fontSize:11, fontWeight:700, color:"#dc2626", textTransform:"uppercase" }}>📤 Sorties (mois)</div>
                    {sortiesMois.length === 0 && <div style={{ padding:"10px 14px", fontSize:12, color:"#9ca3af" }}>Aucune sortie ce mois-ci</div>}
                    {sortiesMois.map((e,i)=>(
                      <div key={e.id||i} style={{ display:"flex", justifyContent:"space-between", padding:"10px 14px", borderBottom:i<sortiesMois.length-1?"1px solid #f1f5f9":"none", fontSize:12 }}>
                        <span style={{ color:"#374151" }}>{e.description || e.categorie}</span>
                        <span style={{ fontWeight:700, color:"#dc2626" }}>{(Number(e.montant||0)/1000000).toFixed(2)} M</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ════ FACTURES COMPTABLE ════ */}
          {tab === "factures" && (() => {
            const STA = { payee:{bg:"#dcfce7",c:"#16a34a",l:"Payée"}, envoyee:{bg:"#e0f2fe",c:"#0369a1",l:"Envoyée"}, partiellement_payee:{bg:"#fef3c7",c:"#d97706",l:"Partielle"}, en_retard:{bg:"#fee2e2",c:"#dc2626",l:"En retard"}, brouillon:{bg:"#f1f5f9",c:"#64748b",l:"Brouillon"}, annulee:{bg:"#f1f5f9",c:"#94a3b8",l:"Annulée"} };
            const source    = facturesCReel || [];
            const prefixF   = PERIODE_C_PREFIX[factPeriode];
            const filteredF = source.filter(f => !prefixF || (f.date_emission||"").startsWith(prefixF));
            const totalPgF  = Math.max(1, Math.ceil(filteredF.length / FACT_PER_PAGE));
            const pagedF    = filteredF.slice(factPage * FACT_PER_PAGE, (factPage + 1) * FACT_PER_PAGE);
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                {/* Filtre période */}
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <select value={factPeriode} onChange={e=>{ setFactPeriode(e.target.value); setFactPage(0); }}
                    style={{ padding:"6px 10px", border:`1px solid ${BET_COLOR}`, borderRadius:8, fontSize:12, background:"#fff", fontWeight:700, color:BET_COLOR }}>
                    {PERIODES_C.map(p=><option key={p}>{p}</option>)}
                  </select>
                  <span style={{ fontSize:11, color:"#9ca3af", marginLeft:"auto" }}>{filteredF.length} facture{filteredF.length>1?"s":""}</span>
                </div>

                {/* KPIs */}
                <div style={{ display:"flex", gap:10 }}>
                  {[["Payées","#16a34a","payee"],["En cours","#d97706","partiellement_payee"],["En retard","#dc2626","en_retard"]].map(([l,c,s])=>(
                    <div key={s} style={{ background:"#fff", border:`1.5px solid ${c}30`, borderRadius:10, padding:"10px 14px", flex:1, textAlign:"center" }}>
                      <div style={{ fontSize:18, fontWeight:900, color:c }}>{filteredF.filter(f=>f.statut===s).length}</div>
                      <div style={{ fontSize:10, color:"#6b7280", fontWeight:700 }}>{l}</div>
                      <div style={{ fontSize:11, fontWeight:700, color:c, marginTop:2 }}>{(filteredF.filter(f=>f.statut===s).reduce((acc,f)=>acc+Number(f.montant_ttc||0),0)/1000000).toFixed(2)} M</div>
                    </div>
                  ))}
                </div>

                {/* Tableau */}
                <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", overflow:"hidden" }}>
                  <div style={{ display:"grid", gridTemplateColumns:"100px 1.5fr 1.5fr 1fr 1fr 100px 30px", padding:"8px 16px", background:"#f8fafc", borderBottom:"1px solid #e5e7eb", fontSize:10, fontWeight:700, color:"#9ca3af", textTransform:"uppercase" }}>
                    <span>N°</span><span>Client</span><span>Type</span><span>Montant</span><span>Échéance</span><span>Statut</span><span></span>
                  </div>
                  {facturesCReel === null ? (
                    <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>⏳ Chargement…</div>
                  ) : pagedF.map((f, i) => {
                    const sta = STA[f.statut]||STA.brouillon;
                    return (
                      <div key={f.id}
                        onClick={()=>setFactDetail(f)}
                        style={{ display:"grid", gridTemplateColumns:"100px 1.5fr 1.5fr 1fr 1fr 100px 30px", padding:"11px 16px", borderBottom:i<pagedF.length-1?"1px solid #f1f5f9":"none", alignItems:"center", fontSize:12, cursor:"pointer", transition:"background .12s" }}
                        onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                        onMouseLeave={e=>e.currentTarget.style.background=""}>
                        <div style={{ fontWeight:700, color:"#0369a1", fontSize:11 }}>{f.numero}</div>
                        <div style={{ color:"#374151", fontWeight:600 }}>{f.client_nom}</div>
                        <div style={{ color:"#6b7280" }}>{f.client_type||"-"}</div>
                        <div style={{ fontWeight:800, color:"#059669" }}>{(Number(f.montant_ttc||0)/1000).toFixed(0)}k</div>
                        <div style={{ color:"#9ca3af" }}>{f.date_echeance}</div>
                        <span style={{ background:sta.bg, color:sta.c, borderRadius:8, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{sta.l}</span>
                        <span style={{ color:BET_COLOR, fontSize:11, fontWeight:600 }}>→</span>
                      </div>
                    );
                  })}
                  {facturesCReel !== null && filteredF.length === 0 && <div style={{ textAlign:"center", padding:28, fontSize:12, color:"#9ca3af" }}>Aucune facture pour cette période</div>}
                </div>

                {/* Pagination */}
                {totalPgF > 1 && (
                  <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:6 }}>
                    <button onClick={()=>setFactPage(p=>Math.max(0,p-1))} disabled={factPage===0}
                      style={{ padding:"4px 10px", border:"1px solid #e2e8f0", borderRadius:7, cursor:factPage===0?"default":"pointer", background:"#fff", color:factPage===0?"#d1d5db":"#374151", fontSize:11, fontWeight:600 }}>← Préc.</button>
                    {Array.from({length:totalPgF},(_,i)=>i).map(n=>(
                      <button key={n} onClick={()=>setFactPage(n)} style={{ width:26, height:26, border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:11, background:n===factPage?BET_COLOR:"#f1f5f9", color:n===factPage?"#fff":"#374151" }}>{n+1}</button>
                    ))}
                    <button onClick={()=>setFactPage(p=>Math.min(totalPgF-1,p+1))} disabled={factPage===totalPgF-1}
                      style={{ padding:"4px 10px", border:"1px solid #e2e8f0", borderRadius:7, cursor:factPage===totalPgF-1?"default":"pointer", background:"#fff", color:factPage===totalPgF-1?"#d1d5db":"#374151", fontSize:11, fontWeight:600 }}>Suiv. →</button>
                  </div>
                )}

                {/* Modal détail facture */}
                {factDetail && (() => {
                  const f   = factDetail;
                  const sta = STA[f.statut]||STA.brouillon;
                  const historiqueClient = source.filter(x => x.client_nom === f.client_nom && x.id !== f.id);
                  return (
                    <ModaleAnimee ouvert
                      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:10000, padding:16 }}
                      panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:"90vw", padding:24, boxShadow:"0 24px 64px rgba(0,0,0,.2)", maxHeight:"90vh", overflowY:"auto" }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                          <div style={{ fontWeight:800, fontSize:15, color:"#1e293b" }}>🧾 Facture {f.numero}</div>
                          <button onClick={()=>setFactDetail(null)} style={{ background:"#f1f5f9", border:"none", borderRadius:8, width:30, height:30, fontSize:14, cursor:"pointer" }}>✕</button>
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
                          {[
                            { l:"Numéro",     v:f.numero },
                            { l:"Client",     v:f.client_nom },
                            { l:"Type client",v:f.client_type||"-" },
                            { l:"Émission",   v:f.date_emission },
                            { l:"Échéance",   v:f.date_echeance },
                            { l:"Statut",     v:<span style={{ background:sta.bg, color:sta.c, borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{sta.l}</span> },
                            { l:"Montant TTC",v:`${(Number(f.montant_ttc||0)/1000).toFixed(0)}k FCFA`, hi:true },
                            { l:"Déjà payé",  v:`${(Number(f.montant_paye||0)/1000).toFixed(0)}k FCFA` },
                          ].map(fi=>(
                            <div key={fi.l} style={{ background:"#f8fafc", borderRadius:8, padding:"8px 10px" }}>
                              <div style={{ fontSize:10, color:"#9ca3af", fontWeight:700, marginBottom:2 }}>{fi.l}</div>
                              <div style={{ fontSize:13, fontWeight:fi.hi?900:700, color:fi.hi?"#059669":"#1e293b" }}>{fi.v}</div>
                            </div>
                          ))}
                        </div>
                        {f.notes && (
                          <div style={{ padding:"8px 12px", background:"#fef3c7", borderRadius:8, fontSize:12, color:"#92400e", marginBottom:14 }}>📝 {f.notes}</div>
                        )}
                        {historiqueClient.length > 0 && (
                          <>
                            <div style={{ fontWeight:700, fontSize:12, color:"#374151", marginBottom:8 }}>📄 Autres factures - {f.client_nom}</div>
                            <div style={{ background:"#f8fafc", borderRadius:10, overflow:"hidden", marginBottom:14 }}>
                              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                                <thead><tr style={{ background:"#f1f5f9" }}>
                                  {["N°","Échéance","Montant","Statut"].map(h=><th key={h} style={{ padding:"6px 10px", fontSize:10, fontWeight:700, color:"#6b7280", textAlign:"left", borderBottom:"1px solid #e5e7eb", textTransform:"uppercase" }}>{h}</th>)}
                                </tr></thead>
                                <tbody>
                                  {historiqueClient.map(x=>{
                                    const xs=STA[x.statut]||STA.brouillon;
                                    return (
                                      <tr key={x.id}>
                                        <td style={{ padding:"6px 10px", fontSize:11, color:"#0369a1", fontWeight:700 }}>{x.numero}</td>
                                        <td style={{ padding:"6px 10px", fontSize:12 }}>{x.date_echeance}</td>
                                        <td style={{ padding:"6px 10px", fontSize:12, fontWeight:700, color:"#059669" }}>{(Number(x.montant_ttc||0)/1000).toFixed(0)}k</td>
                                        <td style={{ padding:"6px 10px" }}><span style={{ background:xs.bg, color:xs.c, borderRadius:6, padding:"2px 7px", fontSize:10, fontWeight:700 }}>{xs.l}</span></td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                                <tfoot>
                                  <tr style={{ background:"#f0f9ff" }}>
                                    <td colSpan={2} style={{ padding:"6px 10px", fontWeight:800, fontSize:11 }}>Total client</td>
                                    <td style={{ padding:"6px 10px", fontWeight:900, fontSize:13, color:"#059669" }}>{(source.filter(x=>x.client_nom===f.client_nom).reduce((s,x)=>s+Number(x.montant_ttc||0),0)/1000).toFixed(0)}k</td>
                                    <td />
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </>
                        )}
                        <div style={{ display:"flex", justifyContent:"flex-end" }}>
                          <button onClick={()=>setFactDetail(null)} style={{ padding:"7px 16px", border:"1px solid #e2e8f0", borderRadius:8, cursor:"pointer", background:"#fff", fontSize:12, fontWeight:600 }}>Fermer</button>
                        </div>
                    </ModaleAnimee>
                  );
                })()}
              </div>
            );
          })()}

          {/* ════ COLLECTES DATA COLLECTOR ════ */}
          {tab === "collectes" && (() => {
            if (dcLeadsReel === null) return <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af", fontSize:13 }}>Chargement…</div>;
            const now = new Date();
            const moisPrefix = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;
            const ceMois = dcLeadsReel.filter(l => (l.created_at||"").startsWith(moisPrefix)).length;
            const nouveaux = dcLeadsReel.filter(l => l.statut === "nouveau").length;
            const parNiveau = {};
            dcLeadsReel.forEach(l => { const k = l.niveau || "Non renseigné"; parNiveau[k] = (parNiveau[k]||0)+1; });
            const totalNiveau = dcLeadsReel.length || 1;
            const niveauRows = Object.entries(parNiveau).sort((a,b)=>b[1]-a[1]);
            return (
              <div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:18 }}>
                  {[["📇",dcLeadsReel.length,"Leads enregistrés","#0891b2"],["🆕",nouveaux,"Nouveaux","#7c3aed"],["📅",ceMois,"Ce mois-ci","#16a34a"]].map(([icon,val,lib,color])=>(
                    <div key={lib} style={{ background:"#fff", border:"1.5px solid #e5e7eb", borderRadius:12, padding:"14px 12px", textAlign:"center" }}>
                      <div style={{ fontSize:22 }}>{icon}</div>
                      <div style={{ fontSize:20, fontWeight:900, color, margin:"6px 0 2px" }}>{val}</div>
                      <div style={{ fontSize:10, color:"#9ca3af", fontWeight:700, textTransform:"uppercase", lineHeight:1.3 }}>{lib}</div>
                    </div>
                  ))}
                </div>
                <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", padding:"14px 16px" }}>
                  <div style={{ fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase", marginBottom:12 }}>📊 Répartition par niveau déclaré</div>
                  {niveauRows.length === 0 ? (
                    <div style={{ fontSize:12, color:"#9ca3af", fontStyle:"italic" }}>Aucun lead enregistré.</div>
                  ) : (
                    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                      {niveauRows.map(([nom,count])=>{
                        const pct = Math.round(count/totalNiveau*100);
                        return (
                          <div key={nom} style={{ display:"flex", alignItems:"center", gap:10 }}>
                            <div style={{ width:140, fontSize:12, color:"#374151", fontWeight:600 }}>{nom}</div>
                            <div style={{ flex:1, height:14, background:"#e5e7eb", borderRadius:7, overflow:"hidden" }}>
                              <div style={{ height:"100%", width:`${pct}%`, background:"#0891b2", borderRadius:7 }} />
                            </div>
                            <div style={{ width:60, fontSize:12, fontWeight:800, color:"#0891b2", textAlign:"right" }}>{count} ({pct}%)</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ════ CAMPAGNES DATA COLLECTOR ════ */}
          {tab === "campagnes_dc" && (
            <div style={{ background:"#f8fafc", border:"1.5px dashed #cbd5e1", borderRadius:12, padding:"20px 18px", textAlign:"center", color:"#64748b", fontSize:13 }}>
              ℹ️ Aucun système de suivi de campagnes n'est encore en base - cet onglet n'a pas de donnée réelle à afficher pour l'instant.
            </div>
          )}

          {/* ════ PERF DATA COLLECTOR ════ */}
          {tab === "perf_dc" && (() => {
            if (dcLeadsReel === null) return <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af", fontSize:13 }}>Chargement…</div>;
            const MOIS_LABELS = ["","Jan","Fév","Mar","Avr","Mai","Juin","Juil","Août","Sep","Oct","Nov","Déc"];
            const derniersMois = Array.from({ length: 6 }, (_, i) => {
              const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - (5 - i));
              return { label: `${MOIS_LABELS[d.getMonth()+1]} ${d.getFullYear()}`, prefix: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}` };
            });
            const parMois = derniersMois.map(m => ({ ...m, count: dcLeadsReel.filter(l => (l.created_at||"").startsWith(m.prefix)).length }));
            const maxCount = Math.max(1, ...parMois.map(m=>m.count));
            return (
              <div>
                <div style={{ fontSize:12, color:"#9ca3af", marginBottom:14, fontStyle:"italic" }}>📈 Historique mensuel des leads collectés (6 derniers mois)</div>
                <div style={{ display:"grid", gridTemplateColumns:`repeat(${parMois.length},1fr)`, gap:10 }}>
                  {parMois.map((m,i)=>(
                    <div key={i} style={{ background:"#fff", border:"1.5px solid #e5e7eb", borderRadius:12, padding:"14px 12px", textAlign:"center" }}>
                      <div style={{ fontSize:11, fontWeight:700, color:"#64748b", marginBottom:8 }}>{m.label}</div>
                      <div style={{ fontSize:22, fontWeight:900, color:"#0891b2" }}>{m.count}</div>
                      <div style={{ margin:"8px 0 0", height:6, background:"#e5e7eb", borderRadius:3, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${Math.round(m.count/maxCount*100)}%`, background:"#0891b2", borderRadius:3 }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* ════ CENTRES ADMIN ════ */}
          {tab === "centres_admin" && (() => {
            if (adminCentresReel === null) return <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af", fontSize:13 }}>Chargement…</div>;
            if (adminCentresReel.length === 0) return <div style={{ textAlign:"center", padding:"24px 0", color:"#9ca3af", fontSize:13 }}>Aucun centre enregistré.</div>;
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                {adminCentresReel.map((c,i)=>{
                  const nbAssistantes = (c.assistantes||[]).filter(a=>a.actif).length;
                  return (
                    <div key={c.id||i} style={{ background:"#fff", border:`1.5px solid ${c.actif?"#e5e7eb":"#fca5a5"}`, borderRadius:12, padding:"14px 16px" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                        <div style={{ fontWeight:800, fontSize:14, color:"#1e293b" }}>🏢 {c.nom}</div>
                        <span style={{ background:c.actif?"#dcfce7":"#fee2e2", color:c.actif?"#16a34a":"#dc2626", borderRadius:8, padding:"3px 12px", fontSize:12, fontWeight:700 }}>{c.actif?"Actif":"Inactif"}</span>
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:8 }}>
                        {[[c.ville||"-","Ville","#0284c7"],[nbAssistantes,"Assistantes actives","#16a34a"]].map(([v,l,col])=>(
                          <div key={l} style={{ background:"#f8fafc", borderRadius:8, padding:"10px", textAlign:"center" }}>
                            <div style={{ fontSize:18, fontWeight:900, color:col }}>{v}</div>
                            <div style={{ fontSize:10, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{l}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* ════ TÂCHES ADMIN ════ */}
          {tab === "taches_admin" && (
            <div style={{ background:"#f8fafc", border:"1.5px dashed #cbd5e1", borderRadius:12, padding:"20px 18px", textAlign:"center", color:"#64748b", fontSize:13 }}>
              ℹ️ Aucun système de gestion de tâches n'est encore en base - cet onglet n'a pas de donnée réelle à afficher pour l'instant.
            </div>
          )}

          {/* ════ ACTIVITE ADMIN ════ */}
          {tab === "activite_admin" && (() => {
            if (adminActiviteReel === null) return <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af", fontSize:13 }}>Chargement…</div>;
            if (adminActiviteReel.length === 0) return <div style={{ textAlign:"center", padding:"24px 0", color:"#9ca3af", fontSize:13 }}>Aucune action enregistrée.</div>;
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                <div style={{ fontSize:12, color:"#9ca3af", marginBottom:4, fontStyle:"italic" }}>📋 Dernières actions enregistrées (journal d'audit)</div>
                {adminActiviteReel.map((a,i)=>{
                  const d = a.created_at ? new Date(a.created_at) : null;
                  return (
                    <div key={a.id||i} style={{ background:"#fff", border:`1.5px solid ${a.statut==="danger"?"#fca5a5":a.statut==="warning"?"#fde68a":"#e5e7eb"}`, borderRadius:12, padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div>
                        <div style={{ fontSize:13, color:"#1e293b", fontWeight:600 }}>{a.detail || a.action_type}</div>
                        <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>{a.acteur_nom || "Système"} · {a.module}</div>
                      </div>
                      <div style={{ textAlign:"right", flexShrink:0, marginLeft:12 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:"#374151" }}>{d ? d.toLocaleDateString("fr-FR") : "-"}</div>
                        <div style={{ fontSize:11, color:"#9ca3af" }}>{d ? d.toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit"}) : ""}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* ════ TICKETS CUSTOMER CARE ════ */}
          {tab === "tickets_cc" && (() => {
            if (ccTicketsReel === null) return <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af", fontSize:13 }}>Chargement…</div>;
            const PRIO = { haute:{bg:"#fee2e2",c:"#dc2626"}, moyenne:{bg:"#fef3c7",c:"#d97706"}, normale:{bg:"#fef3c7",c:"#d97706"}, basse:{bg:"#f1f5f9",c:"#64748b"} };
            const STA  = { nouveau:{bg:"#fee2e2",c:"#dc2626",l:"Nouveau"}, en_cours:{bg:"#fef3c7",c:"#d97706",l:"En cours"}, resolu:{bg:"#dcfce7",c:"#16a34a",l:"Résolu"}, ferme:{bg:"#dcfce7",c:"#16a34a",l:"Fermé"} };
            const prefix = PERIODE_CC_PREFIX[ticketPeriode] || null;
            const ticketsFiltered = ccTicketsReel.filter(t => {
              const matchPeriode = !prefix || (t.created_at||"").startsWith(prefix);
              const matchStatut  = ticketStatut === "Tous" || t.statut === ticketStatut;
              return matchPeriode && matchStatut;
            });
            const totalPages = Math.max(1, Math.ceil(ticketsFiltered.length / TICKET_PER_PAGE));
            const safePage   = Math.min(ticketPage, totalPages - 1);
            const ticketsPaged = ticketsFiltered.slice(safePage * TICKET_PER_PAGE, (safePage + 1) * TICKET_PER_PAGE);
            return (
              <div>
                {/* Filtres */}
                <div style={{ display:"flex", gap:8, marginBottom:12, flexWrap:"wrap", alignItems:"center" }}>
                  <select value={ticketPeriode} onChange={e=>{setTicketPeriode(e.target.value);setTicketPage(0);}}
                    style={{ padding:"6px 10px", borderRadius:8, border:"1.5px solid #e2e8f0", fontSize:12, fontWeight:600, color:"#374151", background:"#fff", cursor:"pointer" }}>
                    {PERIODES_CC.map(p=><option key={p} value={p}>{p}</option>)}
                  </select>
                  <select value={ticketStatut} onChange={e=>{setTicketStatut(e.target.value);setTicketPage(0);}}
                    style={{ padding:"6px 10px", borderRadius:8, border:"1.5px solid #e2e8f0", fontSize:12, fontWeight:600, color:"#374151", background:"#fff", cursor:"pointer" }}>
                    {["Tous","nouveau","en_cours","resolu"].map(s=><option key={s} value={s}>{s==="Tous"?"Tous statuts":STA[s]?.l||s}</option>)}
                  </select>
                  <span style={{ fontSize:11, color:"#9ca3af", marginLeft:"auto" }}>{ticketsFiltered.length} ticket{ticketsFiltered.length>1?"s":""}</span>
                </div>
                {/* KPIs */}
                <div style={{ display:"flex", gap:10, marginBottom:14 }}>
                  {[["Nouveaux","#dc2626","nouveau"],["En cours","#d97706","en_cours"],["Résolus","#16a34a","resolu"]].map(([l,c,s])=>(
                    <div key={s} style={{ background:"#fff", border:`1.5px solid ${c}30`, borderRadius:10, padding:"10px 16px", flex:1, textAlign:"center" }}>
                      <div style={{ fontSize:20, fontWeight:900, color:c }}>{ticketsFiltered.filter(t=>t.statut===s).length}</div>
                      <div style={{ fontSize:10, color:"#6b7280", fontWeight:700 }}>{l}</div>
                    </div>
                  ))}
                </div>
                {/* Liste */}
                <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
                  {ticketsPaged.length === 0 ? (
                    <div style={{ textAlign:"center", padding:"24px 0", color:"#9ca3af", fontSize:13 }}>Aucun ticket pour cette période.</div>
                  ) : ticketsPaged.map((t,i)=>{
                    const p = PRIO[t.priorite]||PRIO.normale;
                    const s = STA[t.statut]||STA.en_cours;
                    const contact = (t.contact_prenom||t.contact_nom) ? `${t.contact_prenom||""} ${t.contact_nom||""}`.trim() : (t.apprenant_nom || t.entreprise_nom || "-");
                    return (
                      <div key={t.id||i} onClick={()=>setTicketDetail(t)}
                        style={{ background:"#fff", border:"1.5px solid #e5e7eb", borderRadius:12, padding:"12px 16px", cursor:"pointer", transition:"box-shadow .15s,border-color .15s" }}
                        onMouseEnter={e=>{e.currentTarget.style.boxShadow="0 2px 12px rgba(0,0,0,0.08)";e.currentTarget.style.borderColor=couleur+"60";}}
                        onMouseLeave={e=>{e.currentTarget.style.boxShadow="none";e.currentTarget.style.borderColor="#e5e7eb";}}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                          <div>
                            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
                              <span style={{ fontSize:10, fontWeight:700, color:"#9ca3af" }}>{t.numero}</span>
                              <span style={{ background:p.bg, color:p.c, borderRadius:6, padding:"1px 8px", fontSize:10, fontWeight:700 }}>{(t.priorite||"normale").charAt(0).toUpperCase()+(t.priorite||"normale").slice(1)}</span>
                            </div>
                            <div style={{ fontWeight:700, fontSize:13, color:"#1e293b" }}>{t.sujet}</div>
                          </div>
                          <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
                            <span style={{ background:s.bg, color:s.c, borderRadius:8, padding:"2px 10px", fontSize:11, fontWeight:700 }}>{s.l}</span>
                            <span style={{ fontSize:10, color:couleur, fontWeight:600 }}>Voir détail →</span>
                          </div>
                        </div>
                        <div style={{ fontSize:11, color:"#6b7280" }}>👤 {contact} · 📡 {t.canal||"-"} · 📅 {t.created_at ? new Date(t.created_at).toLocaleDateString("fr-FR") : "-"}</div>
                      </div>
                    );
                  })}
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:6, marginTop:14 }}>
                    <button onClick={()=>setTicketPage(p=>Math.max(0,p-1))} disabled={safePage===0}
                      style={{ padding:"4px 12px", borderRadius:7, border:"1.5px solid #e2e8f0", background:"#fff", cursor:safePage===0?"default":"pointer", color:safePage===0?"#d1d5db":"#374151", fontWeight:700, fontSize:12 }}>‹</button>
                    {Array.from({length:totalPages},(_,i)=>(
                      <button key={i} onClick={()=>setTicketPage(i)}
                        style={{ padding:"4px 10px", borderRadius:7, border:`1.5px solid ${i===safePage?couleur:"#e2e8f0"}`, background:i===safePage?couleur+"18":"#fff", color:i===safePage?couleur:"#374151", fontWeight:700, fontSize:12, cursor:"pointer" }}>{i+1}</button>
                    ))}
                    <button onClick={()=>setTicketPage(p=>Math.min(totalPages-1,p+1))} disabled={safePage===totalPages-1}
                      style={{ padding:"4px 12px", borderRadius:7, border:"1.5px solid #e2e8f0", background:"#fff", cursor:safePage===totalPages-1?"default":"pointer", color:safePage===totalPages-1?"#d1d5db":"#374151", fontWeight:700, fontSize:12 }}>›</button>
                  </div>
                )}
                {/* Modal détail ticket */}
                {ticketDetail && (
                  <ModaleAnimee ouvert
                    onClicVoile={()=>setTicketDetail(null)}
                    style={{ position:"fixed", inset:0, background:"rgba(15,23,42,0.55)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:99999, padding:16 }}
                    panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:"90vw", maxHeight:"90vh", overflowY:"auto", boxShadow:"0 24px 60px rgba(0,0,0,0.25)", padding:"24px 28px" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18 }}>
                        <div>
                          <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", marginBottom:4 }}>{ticketDetail.numero}</div>
                          <div style={{ fontSize:17, fontWeight:800, color:"#0f172a" }}>{ticketDetail.sujet}</div>
                        </div>
                        <button onClick={()=>setTicketDetail(null)} style={{ background:"#f1f5f9", border:"none", borderRadius:8, width:30, height:30, fontSize:16, cursor:"pointer", color:"#64748b", display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
                        {[["👤 Contact",(ticketDetail.contact_prenom||ticketDetail.contact_nom)?`${ticketDetail.contact_prenom||""} ${ticketDetail.contact_nom||""}`.trim():(ticketDetail.apprenant_nom||ticketDetail.entreprise_nom||"-")],["📧 Email",ticketDetail.contact_email||"-"],["📡 Canal",ticketDetail.canal||"-"],["📅 Date",ticketDetail.created_at?new Date(ticketDetail.created_at).toLocaleDateString("fr-FR"):"-"],["🔴 Priorité",(ticketDetail.priorite||"normale").charAt(0).toUpperCase()+(ticketDetail.priorite||"normale").slice(1)],["🏫 Entreprise",ticketDetail.entreprise_nom||"-"]].map(([l,v])=>(
                          <div key={l} style={{ background:"#f8fafc", borderRadius:8, padding:"10px 12px", border:"1px solid #e2e8f0" }}>
                            <div style={{ fontSize:10, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", marginBottom:3 }}>{l}</div>
                            <div style={{ fontSize:13, fontWeight:600, color:"#0f172a" }}>{v}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ background:"#f8fafc", borderRadius:8, padding:"10px 12px", border:"1px solid #e2e8f0", marginBottom:14 }}>
                        <div style={{ fontSize:10, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", marginBottom:6 }}>📝 Description</div>
                        <div style={{ fontSize:13, color:"#374151", lineHeight:1.6 }}>{ticketDetail.description||"-"}</div>
                      </div>
                      {ticketDetail.reponse_texte && (
                        <div style={{ background:"#f0fdf4", borderRadius:8, padding:"10px 12px", border:"1px solid #bbf7d0", marginBottom:14 }}>
                          <div style={{ fontSize:10, fontWeight:700, color:"#16a34a", textTransform:"uppercase", marginBottom:6 }}>✅ Réponse{ticketDetail.date_resolution?` - ${new Date(ticketDetail.date_resolution).toLocaleDateString("fr-FR")}`:""}</div>
                          <div style={{ fontSize:13, color:"#374151", lineHeight:1.6 }}>{ticketDetail.reponse_texte}</div>
                        </div>
                      )}
                      <div style={{ display:"flex", justifyContent:"flex-end", gap:8 }}>
                        <button onClick={()=>setTicketDetail(null)} style={{ padding:"8px 18px", borderRadius:8, border:"1.5px solid #e2e8f0", background:"#fff", color:"#64748b", cursor:"pointer", fontSize:13, fontWeight:600 }}>Fermer</button>
                      </div>
                  </ModaleAnimee>
                )}
              </div>
            );
          })()}

          {/* ════ SATISFACTION CUSTOMER CARE ════ */}
          {tab === "satisfaction" && (
            <div style={{ background:"#f8fafc", border:"1.5px dashed #cbd5e1", borderRadius:12, padding:"20px 18px", textAlign:"center", color:"#64748b", fontSize:13 }}>
              ℹ️ Aucun système de notation/CSAT n'est encore en base (les tickets n'ont pas de champ de satisfaction) - cet onglet n'a pas de donnée réelle à afficher pour l'instant.
            </div>
          )}

          {/* ════ HISTORIQUE CUSTOMER CARE ════ */}
          {tab === "historique_cc" && (
            <div style={{ background:"#f8fafc", border:"1.5px dashed #cbd5e1", borderRadius:12, padding:"20px 18px", textAlign:"center", color:"#64748b", fontSize:13 }}>
              ℹ️ Aucun journal d'interactions distinct (appels/emails) n'est encore en base - le statut et la réponse de chaque ticket sont déjà consultables dans l'onglet « Tickets ».
            </div>
          )}

        </div>
        </div>{/* fin flex main (sidebar + corps) */}

        <div style={S.footer}>
          <button style={S.btnSec} onClick={onFermer}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   EMOJI PICKER PARTAGÉ
═══════════════════════════════════════════════════════ */
const SHARED_EMOJIS = [
  "🏢","🌐","⚡","💡","🎯","📅","🏆","📜","💼","💬","✍️","🤝","💻","🎤","📱","🏠","👤","👥","🎓","🚀",
  "✅","📊","📈","🔑","🌍","📝","🔔","💰","⭐","🛡️","🎁","🔧","📌","🏅","🌟","🤖","📡","🎪","🏋️","📚",
  "✈️","🚌","🏫","🎨","🔬","⚙️","🌱","💎","🔥","❄️","🌈","🎵","🏄","🎭","🌺","🦁","🐉","🌙","☀️","🎉",
  "📢","🗣️","📣","🎁","🏷️","🛍️","💸","🪙","📩","✉️","📞","📲","🖥️","🖨️","📋","📂","🗂️","🗓️","⏰","⏳",
];

function EmojiPickerWidget({ value, onChange, inpStyle }) {
  const [open, setOpen] = React.useState(false);
  const [pos,  setPos]  = React.useState({ top:0, left:0 });
  const btnRef = React.useRef(null);
  const popRef = React.useRef(null);

  const POPOVER_W = 292;
  const POPOVER_H = 270;

  const handleOpen = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const top  = spaceBelow >= POPOVER_H ? r.bottom + 6 : r.top - POPOVER_H - 6;
    const left = Math.min(r.left, window.innerWidth - POPOVER_W - 8);
    setPos({ top, left: Math.max(8, left) });
    setOpen(o => !o);
  };

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div style={{ position:"relative" }}>
      <button ref={btnRef} type="button" onClick={handleOpen} title="Choisir un emoji"
        style={{ width:"100%", height:42, borderRadius:8, border:`1.5px solid ${open ? "#1B3080" : "#d1d5db"}`, background: open ? "#eef2ff" : "#fff", fontSize:22, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", transition:"border-color .15s" }}>
        {value || <span style={{ fontSize:12, color:"#9ca3af" }}>🎯</span>}
      </button>
      {open && (
        <div ref={popRef} style={{ position:"fixed", top:pos.top, left:pos.left, zIndex:99999, background:"#fff", border:"1.5px solid #e5e7eb", borderRadius:12, boxShadow:"0 8px 32px rgba(0,0,0,0.18)", padding:10, width:POPOVER_W }}>
          <div style={{ fontSize:10, fontWeight:700, color:"#9ca3af", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em" }}>Choisir un emoji</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(10,1fr)", gap:2 }}>
            {SHARED_EMOJIS.map(e => (
              <button key={e} type="button" onClick={() => { onChange(e); setOpen(false); }} title={e}
                style={{ fontSize:18, background: value===e ? "#eef2ff" : "transparent", border: value===e ? "1.5px solid #1B3080" : "1.5px solid transparent", borderRadius:6, cursor:"pointer", padding:"4px 2px", lineHeight:1.3, transition:"background .1s" }}>
                {e}
              </button>
            ))}
          </div>
          <div style={{ borderTop:"1px solid #f1f5f9", marginTop:8, paddingTop:8 }}>
            <input value={value} onChange={e => onChange(e.target.value)} placeholder="Ou coller un emoji…" maxLength={4}
              style={{ ...(inpStyle || {}), fontSize:15, textAlign:"center", padding:"5px 8px", border:"1.5px solid #d1d5db", borderRadius:8, outline:"none", width:"100%", boxSizing:"border-box", background:"#fff" }} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Sélecteur d'emoji inline (pour insertion dans un champ texte) ── */
function EmojiInsertWidget({ onInsert }) {
  const [open, setOpen] = React.useState(false);
  const [pos,  setPos]  = React.useState({ top:0, left:0 });
  const btnRef = React.useRef(null);
  const popRef = React.useRef(null);

  const POPOVER_W = 292;
  const POPOVER_H = 230;

  const handleOpen = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const top  = spaceBelow >= POPOVER_H ? r.bottom + 6 : r.top - POPOVER_H - 6;
    const left = Math.min(r.right - POPOVER_W, window.innerWidth - POPOVER_W - 8);
    setPos({ top, left: Math.max(8, left) });
    setOpen(o => !o);
  };

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div style={{ display:"inline-block" }}>
      <button ref={btnRef} type="button" onClick={handleOpen} title="Insérer un emoji"
        style={{ padding:"5px 9px", borderRadius:7, border:`1.5px solid ${open ? "#1B3080" : "#e2e8f0"}`, background: open ? "#eef2ff" : "#f8fafc", fontSize:15, cursor:"pointer", lineHeight:1 }}>
        😊 <span style={{ fontSize:11, color:"#6b7280" }}>emoji</span>
      </button>
      {open && (
        <div ref={popRef} style={{ position:"fixed", top:pos.top, left:pos.left, zIndex:99999, background:"#fff", border:"1.5px solid #e5e7eb", borderRadius:12, boxShadow:"0 8px 32px rgba(0,0,0,0.18)", padding:10, width:POPOVER_W }}>
          <div style={{ fontSize:10, fontWeight:700, color:"#9ca3af", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em" }}>Insérer un emoji dans le texte</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(10,1fr)", gap:2 }}>
            {SHARED_EMOJIS.map(e => (
              <button key={e} type="button" onClick={() => { onInsert(e); setOpen(false); }} title={e}
                style={{ fontSize:18, background:"transparent", border:"1.5px solid transparent", borderRadius:6, cursor:"pointer", padding:"4px 2px", lineHeight:1.3 }}
                onMouseEnter={ev => ev.currentTarget.style.background="#f1f5f9"}
                onMouseLeave={ev => ev.currentTarget.style.background="transparent"}>
                {e}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   SECTION CARD - Parcours Entreprise (module level pour
   éviter le remontage à chaque render du parent)
═══════════════════════════════════════════════════════ */
const PE_INP = { fontSize:13, color:"#0f172a", border:"1.5px solid #d1d5db", borderRadius:8, padding:"7px 11px", background:"#fff", outline:"none", width:"100%", boxSizing:"border-box" };
const PE_BET_GRADIENT = "linear-gradient(135deg,#1e4080,#0d1a4a)";
const PE_BET_COLOR    = "#1e4080";
const PE_BET_LIGHT    = "#eef2ff";

function ParcoursSectionCard({ title, sectionIcon, items, setItems, sectionKey }) {
  const [editingId,  setEditingId]  = React.useState(null);
  const [editDraft,  setEditDraft]  = React.useState({});
  const [addingNew,  setAddingNew]  = React.useState(false);
  const [newDraft,   setNewDraft]   = React.useState({ label:"", desc:"", icon:"", actif:true });
  const [confirmDel, setConfirmDel] = React.useState(null);

  const startEdit  = (item) => { setEditingId(item.id); setEditDraft({ label:item.label, desc:item.desc||"", icon:item.icon||"" }); };
  const cancelEdit = () => { setEditingId(null); setEditDraft({}); };
  const saveEdit   = (id) => { setItems(prev => prev.map(x => x.id === id ? { ...x, ...editDraft } : x)); setEditingId(null); };
  const toggle     = (id) => setItems(prev => prev.map(x => x.id === id ? { ...x, actif: !x.actif } : x));
  const remove     = (id) => { setItems(prev => prev.filter(x => x.id !== id)); setConfirmDel(null); };
  const addItem    = () => {
    if (!newDraft.label.trim()) return;
    setItems(prev => [...prev, { id:`${sectionKey}_${Date.now()}`, ...newDraft }]);
    setNewDraft({ label:"", desc:"", icon:"", actif:true });
    setAddingNew(false);
  };

  return (
    <div style={{ background:"#fff", borderRadius:14, border:"1px solid #e5e7eb", overflow:"hidden", marginBottom:20 }}>
      {/* Header */}
      <div style={{ background:PE_BET_GRADIENT, padding:"16px 22px", display:"flex", alignItems:"center", gap:10 }}>
        <span style={{ fontSize:18 }}>{sectionIcon}</span>
        <h3 style={{ margin:0, fontSize:15, fontWeight:800, color:"#fff" }}>{title}</h3>
        <span style={{ marginLeft:"auto", fontSize:12, color:"rgba(255,255,255,0.7)", marginRight:12 }}>
          {items.filter(i=>i.actif).length}/{items.length} actifs
        </span>
        <button onClick={() => { setAddingNew(true); setEditingId(null); }} style={{ padding:"6px 14px", background:"rgba(255,255,255,0.2)", border:"1.5px solid rgba(255,255,255,0.4)", borderRadius:8, color:"#fff", fontWeight:700, fontSize:12, cursor:"pointer" }}>
          ＋ Ajouter
        </button>
      </div>

      <div style={{ padding:20, display:"flex", flexDirection:"column", gap:8 }}>
        {/* Formulaire ajout */}
        {addingNew && (
          <div style={{ background:"#f0f9ff", border:`1.5px dashed ${PE_BET_COLOR}`, borderRadius:10, padding:"14px 16px", marginBottom:4 }}>
            <div style={{ fontSize:12, fontWeight:700, color:PE_BET_COLOR, marginBottom:10 }}>➕ Nouvelle option</div>
            <div style={{ display:"grid", gridTemplateColumns:"60px 1fr 1fr", gap:8, marginBottom:8 }}>
              <EmojiPickerWidget value={newDraft.icon} onChange={v=>setNewDraft(p=>({...p,icon:v}))} inpStyle={PE_INP} />
              <input value={newDraft.label} onChange={e=>setNewDraft(p=>({...p,label:e.target.value}))} placeholder="Nom de l'option *" style={{ ...PE_INP, fontWeight:700 }} autoFocus />
              <input value={newDraft.desc}  onChange={e=>setNewDraft(p=>({...p,desc:e.target.value}))}  placeholder="Description (optionnel)" style={PE_INP} />
            </div>
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
              <button onClick={() => setAddingNew(false)} style={{ padding:"6px 14px", background:"#e5e7eb", border:"none", borderRadius:7, cursor:"pointer", fontWeight:600, fontSize:12, color:"#374151" }}>Annuler</button>
              <button onClick={addItem} disabled={!newDraft.label.trim()} style={{ padding:"6px 16px", background:newDraft.label.trim()?PE_BET_COLOR:"#d1d5db", color:"#fff", border:"none", borderRadius:7, cursor:newDraft.label.trim()?"pointer":"default", fontWeight:700, fontSize:12 }}>✓ Ajouter</button>
            </div>
          </div>
        )}

        {items.map(item => (
          <div key={item.id}>
            {/* Confirmation suppression */}
            {confirmDel === item.id && (
              <div style={{ background:"#fff1f2", border:"1.5px solid #fca5a5", borderRadius:10, padding:"12px 16px", marginBottom:4, display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
                <span style={{ fontSize:13, color:"#b91c1c", fontWeight:600 }}>⚠️ Supprimer <strong>"{item.label}"</strong> ?</span>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={() => setConfirmDel(null)} style={{ padding:"5px 12px", background:"#e5e7eb", border:"none", borderRadius:7, cursor:"pointer", fontWeight:600, fontSize:12 }}>Annuler</button>
                  <button onClick={() => remove(item.id)} style={{ padding:"5px 12px", background:"#dc2626", color:"#fff", border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12 }}>Supprimer</button>
                </div>
              </div>
            )}

            {editingId === item.id ? (
              <div style={{ background:"#f8fafc", border:`1.5px solid ${PE_BET_COLOR}`, borderRadius:10, padding:"14px 16px" }}>
                <div style={{ fontSize:12, fontWeight:700, color:PE_BET_COLOR, marginBottom:10 }}>✏️ Modifier l'option</div>
                <div style={{ display:"grid", gridTemplateColumns:"60px 1fr 1fr", gap:8, marginBottom:10 }}>
                  <EmojiPickerWidget value={editDraft.icon} onChange={v=>setEditDraft(p=>({...p,icon:v}))} inpStyle={PE_INP} />
                  <input value={editDraft.label} onChange={e=>setEditDraft(p=>({...p,label:e.target.value}))} placeholder="Nom *" style={{ ...PE_INP, fontWeight:700 }} autoFocus />
                  <input value={editDraft.desc}  onChange={e=>setEditDraft(p=>({...p,desc:e.target.value}))}  placeholder="Description" style={PE_INP} />
                </div>
                <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
                  <button onClick={cancelEdit} style={{ padding:"6px 14px", background:"#e5e7eb", border:"none", borderRadius:7, cursor:"pointer", fontWeight:600, fontSize:12, color:"#374151" }}>Annuler</button>
                  <button onClick={() => saveEdit(item.id)} style={{ padding:"6px 16px", background:PE_BET_COLOR, color:"#fff", border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12 }}>✓ Enregistrer</button>
                </div>
              </div>
            ) : (
              <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", borderRadius:10, border:`1.5px solid ${item.actif?PE_BET_COLOR+"33":"#e5e7eb"}`, background:item.actif?PE_BET_LIGHT:"#f8fafc", transition:"all .2s" }}>
                <button onClick={() => toggle(item.id)} title={item.actif?"Désactiver":"Activer"} style={{ width:38, height:22, borderRadius:999, border:"none", cursor:"pointer", background:item.actif?PE_BET_COLOR:"#d1d5db", position:"relative", flexShrink:0, transition:"background .2s" }}>
                  <span style={{ position:"absolute", top:3, left:item.actif?19:3, width:16, height:16, borderRadius:"50%", background:"#fff", transition:"left .2s", display:"block" }} />
                </button>
                <span style={{ fontSize:19, flexShrink:0, minWidth:24, textAlign:"center" }}>{item.icon||"·"}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:item.actif?"#0f172a":"#9ca3af" }}>{item.label}</div>
                  {item.desc && <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>{item.desc}</div>}
                </div>
                <span style={{ fontSize:11, fontWeight:700, padding:"2px 9px", borderRadius:999, whiteSpace:"nowrap", background:item.actif?"#dcfce7":"#fee2e2", color:item.actif?"#166534":"#b91c1c" }}>
                  {item.actif ? "✓ Actif" : "✗ Masqué"}
                </span>
                <button onClick={() => { startEdit(item); setAddingNew(false); }} style={{ padding:"5px 11px", background:"#eef2ff", color:PE_BET_COLOR, border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12, whiteSpace:"nowrap" }}>✏️ Modifier</button>
                <button onClick={() => setConfirmDel(item.id)} style={{ padding:"5px 11px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12, whiteSpace:"nowrap" }}>🗑️ Supprimer</button>
              </div>
            )}
          </div>
        ))}

        {items.length === 0 && (
          <div style={{ textAlign:"center", padding:"24px 0", color:"#9ca3af", fontSize:13 }}>Aucune option - cliquez sur ＋ Ajouter</div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   IMPORT SCHEMAS - un schéma par rôle importable
══════════════════════════════════════════════════════════════ */
const _mapCentreGlobal = (val) => {
  if (!val) return null;
  const n = String(val).toLowerCase()
    .replace(/[éèêë]/g,"e").replace(/[àâä]/g,"a").replace(/[ùûü]/g,"u").replace(/[îï]/g,"i").replace(/[ôö]/g,"o");
  const CENTRES = [
    { id:"angre",     mots:["angre","cocody"] },
    { id:"2plateaux", mots:["2plateaux","ii plateau","deux plateau","2 plateau","plateau"] },
    { id:"yopougon",  mots:["yopougon"] },
    { id:"koumassi",  mots:["koumassi"] },
    { id:"abatta",    mots:["abatta"] },
    { id:"bouake",    mots:["bouake"] },
  ];
  for (const c of CENTRES) { if (c.mots.some(m => n.includes(m))) return c.id; }
  if (CENTRES.map(c=>c.id).includes(val.trim().toLowerCase())) return val.trim().toLowerCase();
  return null;
};

const SA_IMPORT_SCHEMAS = {
  coach: {
    label:"Coach", emoji:"🏫", couleur:"#059669", bg:"#f0fdf4",
    // Coach n'a pas de sélecteur de centre dans le formulaire de création réel
    // (ROLES_AVEC_CENTRE = ["commercial"] uniquement) - "Centre BET" ici n'est qu'une
    // note libre reprise dans `departement`, pas une vraie liaison à un centre.
    avecCentre:false,
    colonnes:[
      { key:"prenom",          header:"Prénom",            requis:true  },
      { key:"nom",             header:"Nom",               requis:true  },
      { key:"email",           header:"Email",             requis:true  },
      { key:"telephone",       header:"Téléphone",         requis:false },
      { key:"centre_bet",      header:"Centre BET",        requis:false },
      { key:"filiere",         header:"Filière",           requis:false },
      { key:"matricule",       header:"Matricule",         requis:false },
      { key:"specialite",      header:"Spécialité",        requis:false },
      { key:"niveaux",         header:"Niveaux enseignés", requis:false },
      { key:"type_contrat",    header:"Type de contrat",   requis:false },
      { key:"lieu_habitation", header:"Lieu d'habitation", requis:false },
      { key:"date_debut_bet",  header:"Date début BET",    requis:false },
      { key:"certifications",  header:"Certifications",    requis:false },
    ],
    exemple:["Aminata","Koné","a.kone@bet-coaching.ci","+225 07 01 23 45","angre","Business English","BET-C001","Communication","B1,B2,C1","CDI","Cocody","2022-03-01","CELTA,TOEIC"],
    buildPayload:(f,scope)=>({ prenom:f.prenom, nom:f.nom, email:f.email, telephone:f.telephone||null, role:"coach", scope, departement:f.centre_bet||"Pédagogie", coach_info:{ filiere:f.filiere||"", matricule:f.matricule||"", specialite:f.specialite||"", niveaux:f.niveaux||"", type_contrat:f.type_contrat||"", lieu_habitation:f.lieu_habitation||"", date_debut_bet:f.date_debut_bet||"", certifications:f.certifications?f.certifications.split(",").map(s=>s.trim()).filter(Boolean):[] } }),
    extraInfo:(f)=>[f.filiere,f.matricule].filter(Boolean).join(" · ")||"-",
    extraLabel:"Filière · Matricule",
  },
  commercial: {
    label:"Commercial", emoji:"💼", couleur:"#2563eb", bg:"#eff6ff",
    avecCentre:true,
    colonnes:[
      { key:"prenom",    header:"Prénom",    requis:true  },
      { key:"nom",       header:"Nom",       requis:true  },
      { key:"email",     header:"Email",     requis:true  },
      { key:"telephone", header:"Téléphone", requis:false },
      { key:"centre_bet",header:"Centre BET",requis:false },
      { key:"zone",      header:"Zone",      requis:false },
    ],
    exemple:["Kouassi","Bamba","k.bamba@bet-coaching.ci","+225 05 12 34 56","yopougon","Zone Sud"],
    buildPayload:(f,scope)=>({ prenom:f.prenom, nom:f.nom, email:f.email, telephone:f.telephone||null, role:"commercial", scope, zone:f.zone||"" }),
    extraInfo:(f)=>f.zone||"-",
    extraLabel:"Zone",
  },
  superviseur: {
    label:"Superviseur", emoji:"👁️", couleur:"#0891b2", bg:"#f0f9ff",
    avecCentre:false,
    colonnes:[
      { key:"prenom",    header:"Prénom",    requis:true  },
      { key:"nom",       header:"Nom",       requis:true  },
      { key:"email",     header:"Email",     requis:true  },
      { key:"telephone", header:"Téléphone", requis:false },
      { key:"zone",      header:"Zone",      requis:false },
    ],
    exemple:["Moussa","Coulibaly","m.coulibaly@bet-coaching.ci","+225 07 11 22 33","Zone Centre"],
    buildPayload:(f)=>({ prenom:f.prenom, nom:f.nom, email:f.email, telephone:f.telephone||null, role:"superviseur", scope:["national"], zone:f.zone||"" }),
    extraInfo:(f)=>f.zone||"-",
    extraLabel:"Zone",
  },
  deputy_superviseur: {
    label:"Deputy Superviseur", emoji:"🔎", couleur:"#7c3aed", bg:"#f5f3ff",
    // Même remarque que pour Coach : pas de sélecteur de centre réel à la création.
    avecCentre:false,
    colonnes:[
      { key:"prenom",    header:"Prénom",    requis:true  },
      { key:"nom",       header:"Nom",       requis:true  },
      { key:"email",     header:"Email",     requis:true  },
      { key:"telephone", header:"Téléphone", requis:false },
      { key:"centre_bet",header:"Centre BET",requis:false },
    ],
    exemple:["Fatou","Diallo","f.diallo@bet-coaching.ci","+225 01 23 45 67","2plateaux"],
    buildPayload:(f,scope)=>({ prenom:f.prenom, nom:f.nom, email:f.email, telephone:f.telephone||null, role:"deputy_superviseur", scope }),
    extraInfo:()=>"-",
    extraLabel:"Info",
  },
  manager: {
    label:"Manager", emoji:"📋", couleur:"#059669", bg:"#f0fdf4",
    avecCentre:false,
    colonnes:[
      { key:"prenom",    header:"Prénom",    requis:true  },
      { key:"nom",       header:"Nom",       requis:true  },
      { key:"email",     header:"Email",     requis:true  },
      { key:"telephone", header:"Téléphone", requis:false },
      { key:"note",      header:"Note",      requis:false },
    ],
    exemple:["Marie","Dupont","m.dupont@bet-coaching.ci","+225 07 89 12 34","Responsable pédagogie"],
    buildPayload:(f)=>({ prenom:f.prenom, nom:f.nom, email:f.email, telephone:f.telephone||null, role:"manager", scope:["national"], note:f.note||"" }),
    extraInfo:(f)=>f.note||"-",
    extraLabel:"Note",
  },
  gestionnaire: {
    label:"Gestionnaire", emoji:"⚙️", couleur:"#0891b2", bg:"#f0f9ff",
    avecCentre:false,
    colonnes:[
      { key:"prenom",    header:"Prénom",    requis:true  },
      { key:"nom",       header:"Nom",       requis:true  },
      { key:"email",     header:"Email",     requis:true  },
      { key:"telephone", header:"Téléphone", requis:false },
      { key:"note",      header:"Note",      requis:false },
    ],
    exemple:["Sita","Coulibaly","s.coulibaly@bet-coaching.ci","+225 05 67 89 01","Gestion administrative"],
    buildPayload:(f)=>({ prenom:f.prenom, nom:f.nom, email:f.email, telephone:f.telephone||null, role:"gestionnaire", scope:["national"], note:f.note||"" }),
    extraInfo:(f)=>f.note||"-",
    extraLabel:"Note",
  },
  admin: {
    label:"Administrateur", emoji:"🛡️", couleur:"#1B3080", bg:"#eff6ff",
    avecCentre:false,
    colonnes:[
      { key:"prenom",    header:"Prénom",    requis:true  },
      { key:"nom",       header:"Nom",       requis:true  },
      { key:"email",     header:"Email",     requis:true  },
      { key:"telephone", header:"Téléphone", requis:false },
      { key:"note",      header:"Note",      requis:false },
    ],
    exemple:["Jean","Martin","j.martin@bet-coaching.ci","+225 07 45 67 89","Admin pédagogie"],
    buildPayload:(f)=>({ prenom:f.prenom, nom:f.nom, email:f.email, telephone:f.telephone||null, role:"admin", scope:["national"], note:f.note||"" }),
    extraInfo:(f)=>f.note||"-",
    extraLabel:"Note",
  },
  pedagogical_advisor: {
    label:"PA Advisor", emoji:"🎓", couleur:"#d97706", bg:"#fffbeb",
    avecCentre:false,
    colonnes:[
      { key:"prenom",    header:"Prénom",    requis:true  },
      { key:"nom",       header:"Nom",       requis:true  },
      { key:"email",     header:"Email",     requis:true  },
      { key:"telephone", header:"Téléphone", requis:false },
      { key:"note",      header:"Note",      requis:false },
    ],
    exemple:["Adjoua","Traoré","a.traore@bet-coaching.ci","+225 01 45 23 67","Pédagogie avancée"],
    buildPayload:(f)=>({ prenom:f.prenom, nom:f.nom, email:f.email, telephone:f.telephone||null, role:"pedagogical_advisor", scope:["national"], note:f.note||"" }),
    extraInfo:(f)=>f.note||"-",
    extraLabel:"Note",
  },
  onboarding: {
    label:"Assistante Onboarding", emoji:"🎯", couleur:"#e11d48", bg:"#fff1f2",
    avecCentre:false,
    colonnes:[
      { key:"prenom",    header:"Prénom",    requis:true  },
      { key:"nom",       header:"Nom",       requis:true  },
      { key:"email",     header:"Email",     requis:true  },
      { key:"telephone", header:"Téléphone", requis:false },
      { key:"note",      header:"Note",      requis:false },
    ],
    exemple:["Nadia","Konaté","n.konate@bet-coaching.ci","+225 05 34 56 78","Accueil apprenants"],
    buildPayload:(f)=>({ prenom:f.prenom, nom:f.nom, email:f.email, telephone:f.telephone||null, role:"onboarding", scope:["national"], note:f.note||"" }),
    extraInfo:(f)=>f.note||"-",
    extraLabel:"Note",
  },
  rh: {
    label:"Espace RH / Paie", emoji:"👔", couleur:"#db2777", bg:"#fdf2f8",
    avecCentre:false,
    colonnes:[
      { key:"prenom",    header:"Prénom",    requis:true  },
      { key:"nom",       header:"Nom",       requis:true  },
      { key:"email",     header:"Email",     requis:true  },
      { key:"telephone", header:"Téléphone", requis:false },
      { key:"note",      header:"Note",      requis:false },
    ],
    exemple:["Sarah","Kouyaté","s.kouyate@bet-coaching.ci","+225 07 56 78 90","Gestion paie coachs"],
    buildPayload:(f)=>({ prenom:f.prenom, nom:f.nom, email:f.email, telephone:f.telephone||null, role:"rh", scope:["national"], note:f.note||"" }),
    extraInfo:(f)=>f.note||"-",
    extraLabel:"Note",
  },
  comptable: {
    label:"Comptable / Trésorier", emoji:"🧾", couleur:"#d97706", bg:"#fffbeb",
    avecCentre:false,
    colonnes:[
      { key:"prenom",    header:"Prénom",    requis:true  },
      { key:"nom",       header:"Nom",       requis:true  },
      { key:"email",     header:"Email",     requis:true  },
      { key:"telephone", header:"Téléphone", requis:false },
      { key:"note",      header:"Note",      requis:false },
    ],
    exemple:["Didier","Amani","d.amani@bet-coaching.ci","+225 05 90 12 34","Trésorerie centres"],
    buildPayload:(f)=>({ prenom:f.prenom, nom:f.nom, email:f.email, telephone:f.telephone||null, role:"comptable", scope:["national"], note:f.note||"" }),
    extraInfo:(f)=>f.note||"-",
    extraLabel:"Note",
  },
  customer_care: {
    label:"Customer Care", emoji:"🎧", couleur:"#0e7490", bg:"#ecfeff",
    avecCentre:false,
    colonnes:[
      { key:"prenom",    header:"Prénom",    requis:true  },
      { key:"nom",       header:"Nom",       requis:true  },
      { key:"email",     header:"Email",     requis:true  },
      { key:"telephone", header:"Téléphone", requis:false },
      { key:"note",      header:"Note",      requis:false },
    ],
    exemple:["Estelle","Gouré","e.goure@bet-coaching.ci","+225 01 23 67 89","Support apprenants"],
    buildPayload:(f)=>({ prenom:f.prenom, nom:f.nom, email:f.email, telephone:f.telephone||null, role:"customer_care", scope:["national"], note:f.note||"" }),
    extraInfo:(f)=>f.note||"-",
    extraLabel:"Note",
  },
  assistant_elearning: {
    label:"Assistant E-learning", emoji:"💻", couleur:"#7c3aed", bg:"#f5f3ff",
    avecCentre:false,
    colonnes:[
      { key:"prenom",    header:"Prénom",    requis:true  },
      { key:"nom",       header:"Nom",       requis:true  },
      { key:"email",     header:"Email",     requis:true  },
      { key:"telephone", header:"Téléphone", requis:false },
      { key:"note",      header:"Note",      requis:false },
    ],
    exemple:["Junior","Ehui","j.ehui@bet-coaching.ci","+225 07 34 56 12","Suivi plateforme e-learning"],
    buildPayload:(f)=>({ prenom:f.prenom, nom:f.nom, email:f.email, telephone:f.telephone||null, role:"assistant_elearning", scope:["national"], note:f.note||"" }),
    extraInfo:(f)=>f.note||"-",
    extraLabel:"Note",
  },
  data_collector: {
    label:"Data Collector", emoji:"📊", couleur:"#64748b", bg:"#f8fafc",
    avecCentre:false,
    colonnes:[
      { key:"prenom",    header:"Prénom",    requis:true  },
      { key:"nom",       header:"Nom",       requis:true  },
      { key:"email",     header:"Email",     requis:true  },
      { key:"telephone", header:"Téléphone", requis:false },
      { key:"note",      header:"Note",      requis:false },
    ],
    exemple:["Landry","Boti","l.boti@bet-coaching.ci","+225 05 45 67 23","Saisie données terrain"],
    buildPayload:(f)=>({ prenom:f.prenom, nom:f.nom, email:f.email, telephone:f.telephone||null, role:"data_collector", scope:["national"], note:f.note||"" }),
    extraInfo:(f)=>f.note||"-",
    extraLabel:"Note",
  },
  placement_test: {
    label:"Agent Placement Test", emoji:"🧪", couleur:"#0d9488", bg:"#f0fdfa",
    avecCentre:false,
    colonnes:[
      { key:"prenom",    header:"Prénom",    requis:true  },
      { key:"nom",       header:"Nom",       requis:true  },
      { key:"email",     header:"Email",     requis:true  },
      { key:"telephone", header:"Téléphone", requis:false },
      { key:"note",      header:"Note",      requis:false },
    ],
    exemple:["Vanessa","Kadio","v.kadio@bet-coaching.ci","+225 01 67 89 45","Tests de niveau"],
    buildPayload:(f)=>({ prenom:f.prenom, nom:f.nom, email:f.email, telephone:f.telephone||null, role:"placement_test", scope:["national"], note:f.note||"" }),
    extraInfo:(f)=>f.note||"-",
    extraLabel:"Note",
  },
};

/* ═══════════════════════════════════════════════════════
   COMPOSANT PRINCIPAL
═══════════════════════════════════════════════════════ */
export default function AdminDashboard() {
  const { lang, toggleLang } = useLanguage();
  const confirm = useConfirm();
  const alerte = useAlerte();
  const handleLogout = async () => {
    if (!(await confirm('Vous allez être déconnecté(e) de votre session.', { titre: 'Se déconnecter ?', confirmLabel: 'Se déconnecter', danger: false }))) return;
    clearAllSessionStorage();
    window.location.replace("/bet-staff-access");
  };

  // Profil connecté depuis la session
  const profilInitial = (() => {
    try {
      const stored = localStorage.getItem("admin_profil");
      if (stored) return JSON.parse(stored);
    } catch {}
    return SUPER_ADMIN_PROFIL;
  })();
  // Droits effectifs de la personne connectée (rôle + ses exceptions perso), rafraîchis
  // périodiquement - voir utils/permissions.js
  const { profil: profilConnecte, peut } = useLivePermissions(profilInitial);
  const permBtnStyle = (style, allowed) => allowed ? style : { ...style, opacity:0.45, cursor:"not-allowed", filter:"grayscale(70%)" };
  const permBtnTitle = (label, allowed) => allowed ? label : "🔒 Vous n'avez pas la permission pour cette action";

  const [activeTab, setActiveTab]   = useTabState("overview");
  const [saAvatarUrl, setSaAvatarUrl] = useState(profilConnecte?.avatar_url || null);
  // Cliquer sur la photo la montre en grand ; le bouton 📷 garde le remplacement.
  const [photoProfilOuverte, setPhotoProfilOuverte] = useState(false);
  const handleSaAvatarSuccess = async (file) => {
    setSaAvatarUrl(file.url);
    try {
      const r = await fetch(`${API_URL}/api/admin/me`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ avatar_url: file.url }),
      });
      if (r.ok) {
        const stored = (() => { try { return JSON.parse(localStorage.getItem("admin_profil")||"{}"); } catch { return {}; } })();
        localStorage.setItem("admin_profil", JSON.stringify({ ...stored, avatar_url: file.url }));
      }
    } catch {}
  };
  const [loadingUsers, setLoadingUsers] = useState(true);

  // ── Stats plateforme réelles ──────────────────────────
  const [plateformeStats, setPlateformeStats] = useState({
    total_apprenants: null,
    ca_annuel: null,
    total_certifications: null,
    taux_reussite_certifications: null,
  });
  useEffect(() => {
    fetch(`${API_URL}/api/admin/plateforme-stats`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setPlateformeStats(d); })
      .catch(() => {});
  }, []);

  // ── Onglet Administration BET ──────────────────────────
  const [adminSearch,     setAdminSearch]     = useState("");
  const [adminRoleFilter, setAdminRoleFilter] = useState("tous");
  const [adminDetail,     setAdminDetail]     = useState(null);

  // ── Sondages acquisition ──────────────────────────────
  const [sondagesAll,     setSondagesAll]     = useState([]);
  const [sondageSrcStats, setSondageSrcStats] = useState({});
  const [sondageUtmStats, setSondageUtmStats] = useState({});
  const [visitStats,      setVisitStats]      = useState({});
  const [totalVisits,     setTotalVisits]     = useState(0);
  const [sondagesLoading, setSondagesLoading] = useState(false);

  const fetchSondagesAll = async () => {
    setSondagesLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sondage/all`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setSondagesAll(d.sondages || []);
      setSondageSrcStats(d.source_stats || {});
      setSondageUtmStats(d.utm_stats || {});
      setVisitStats(d.visit_stats || {});
      setTotalVisits(d.total_visits || 0);
    } catch (e) { console.error("Sondages SA:", e); }
    finally { setSondagesLoading(false); }
  };

  useEffect(() => { fetchSondagesAll(); }, []);

  // États Gestion des droits - alimentés par l'API
  const [users, setUsers]           = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [demandes] = useState([]); // toujours vide : aucune UI ne peuple les demandes d'accès pour ce rôle
  const [auditLog, setAuditLog] = useState(GLOBAL_AUDIT);
  const [filtreRole, setFiltreRole] = useState("Tous");
  const [filtreStatut, setFiltreStatut] = useState("Tous");
  const [filtreCentre, setFiltreCentre] = useState("Tous");
  const [searchUser, setSearchUser] = useState("");
  const [filtreAudit, setFiltreAudit] = useState("Tous");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false); // empêche le double-clic création utilisateur
  const [showImportRoleModal, setShowImportRoleModal] = useState(false);
  const [importRole,          setImportRole]          = useState("coach");
  const [importPreviewRows,   setImportPreviewRows]   = useState([]);
  const [showImportPreview,   setShowImportPreview]   = useState(false);
  const [importConfirming,    setImportConfirming]    = useState(false);
  const [importResultats,     setImportResultats]     = useState([]);
  const [showImportResultat,  setShowImportResultat]  = useState(false);
  const importFileRef = useRef(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [tempPasswords, setTempPasswords] = useState({}); // { [userId]: mdp }
  const [inviteForm, setInviteForm] = useState({ nom:"", email:"", telephone:"", role:"manager", centre_id:"", accessTemp:"", note:"", type_cours:"en_ligne", quota_jour:10, jours_travail:["lundi","mardi","mercredi","jeudi","vendredi"], profil_assistante:"b2c", photo:"", coach_matricule:"", coach_filiere:"", coach_lieu_habitation:"", coach_date_debut:"", coach_nb_contrats:0, coach_certifications:[], coach_certif_input:"", coach_niveaux:[], onboarding_id:"", couleur:"#10b981" });
  const [onboardingList, setOnboardingList] = useState([]);
  const [invitePhotoUploading, setInvitePhotoUploading] = useState(false);
  const [assistanteProfilFilter, setAssistanteProfilFilter] = useState("b2c"); // "b2c" | "b2b"
  const [editingUser, setEditingUser] = useState(null);
  const [editingUserGroupes, setEditingUserGroupes] = useState([]);
  const [userToRevoke, setUserToRevoke] = useState(null);

  // ── Témoignages ──────────────────────────────────────────
  const [assistantesAdmin, setAssistantesAdmin]     = useState([]);
  const [assistantesLoading, setAssistantesLoading] = useState(false);
  const [diagOnboarding, setDiagOnboarding]         = useState(null);
  const [diagLoading, setDiagLoading]               = useState(false);
  const [assistantesDrafts, setAssistantesDrafts]   = useState({}); // { [id]: {field:val,...} }
  const [assistantesSaving, setAssistantesSaving]   = useState({}); // { [id]: bool }
  const [centreTab, setCentreTab]                   = useState("Angré");
  const [centresList, setCentresList]               = useState([]); // centres réels depuis Supabase [{id,nom,ville}]

  const runDiagOnboarding = async () => {
    if (diagLoading) return;
    setDiagLoading(true);
    try {
      // Auto-fix utilisateur_id manquants avant le diagnostic
      await fetch(`${API_URL}/api/admin/fix-assistantes-utilisateur-id`, { method:"POST", headers: authHeaders() }).catch(()=>{});
      const r = await fetch(`${API_URL}/api/admin/diagnostic-onboarding`, { headers: authHeaders() });
      const d = await r.json();
      setDiagOnboarding(d);
    } catch (e) { setDiagOnboarding({ ok: false, error: e.message }); }
    finally { setDiagLoading(false); }
  };

  const fetchAssistantesAdmin = async () => {
    setAssistantesLoading(true);
    setAssistantesDrafts({});
    try {
      const res = await fetch(`${API_URL}/api/parcours/assistantes/gestion`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setAssistantesAdmin(d.assistantes || []);
    } catch { toast.error("Erreur chargement assistantes"); }
    finally { setAssistantesLoading(false); }
  };

  const fetchCentresList = async () => {
    try {
      const res = await fetch(`${API_URL}/api/parcours/centres`);
      const d = await res.json();
      setCentresList(d.centres || []);
    } catch {}
  };

  // Retourne la version draft-merged d'une assistante
  const getDraftedA = (a) => ({ ...a, ...(assistantesDrafts[a.id] || {}) });

  // Met à jour le draft local (sans appel API)
  const setDraft = (id, updates) => {
    setAssistantesDrafts(prev => ({ ...prev, [id]: { ...(prev[id]||{}), ...updates } }));
  };

  const toggleJourTravailDraft = (assistanteId, jour) => {
    const a = assistantesAdmin.find(x => x.id === assistanteId);
    if (!a) return;
    const base = (assistantesDrafts[assistanteId]?.jours_travail) ?? (a.jours_travail || ["lundi","mardi","mercredi","jeudi","vendredi"]);
    const nouveau = base.includes(jour) ? base.filter(j => j !== jour) : [...base, jour];
    setDraft(assistanteId, { jours_travail: nouveau });
  };

  // Enregistre le draft d'une assistante vers l'API
  const saveDraftAssistante = async (id) => {
    if (assistantesSaving[id]) return;
    const draft = assistantesDrafts[id];
    if (!draft || Object.keys(draft).length === 0) return;
    setAssistantesSaving(prev => ({ ...prev, [id]: true }));
    try {
      const res = await fetch(`${API_URL}/api/parcours/assistantes/${id}`, {
        method: "PATCH", headers: authHeaders(), body: JSON.stringify(draft),
      });
      if (!res.ok) throw new Error();
      setAssistantesAdmin(prev => prev.map(a => a.id === id ? { ...a, ...draft } : a));
      setAssistantesDrafts(prev => { const n = { ...prev }; delete n[id]; return n; });
      toast.success("Planning enregistré ✓");
    } catch { toast.error("Erreur lors de l'enregistrement"); }
    finally { setAssistantesSaving(prev => ({ ...prev, [id]: false })); }
  };

  // ── Avis offres ──────────────────────────────────────────────────────────
  const [avisOffres,      setAvisOffres]      = useState([]);
  const [avisLoading,     setAvisLoading]     = useState(false);
  const [avisFiltre,      setAvisFiltre]      = useState("tous"); // "tous"|"cours"|"certification"
  const fetchAvisOffres = async () => {
    setAvisLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/avis`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setAvisOffres(d.avis || []);
    } catch { toast.error("Erreur chargement des avis"); }
    finally { setAvisLoading(false); }
  };
  const toggleAvis = async (id, actif) => {
    try {
      await fetch(`${API_URL}/api/avis/${id}`, { method:"PATCH", headers:authHeaders(), body:JSON.stringify({actif}) });
      setAvisOffres(prev => prev.map(a => a.id===id ? {...a,actif} : a));
      toast.success(actif ? "Avis réactivé" : "Avis désactivé");
    } catch { toast.error("Erreur"); }
  };
  const deleteAvis = async (id) => {
    if (!(await confirm("Supprimer définitivement cet avis ?", { titre: "Supprimer l'avis" }))) return;
    try {
      await fetch(`${API_URL}/api/avis/${id}`, { method:"DELETE", headers:authHeaders() });
      setAvisOffres(prev => prev.filter(a => a.id !== id));
      toast.success("Avis supprimé");
    } catch { toast.error("Erreur"); }
  };

  const [temos, setTemos]                   = useState([]);
  const [temosLoading, setTemosLoading]     = useState(false);
  const [temoFiltre, setTemoFiltre]         = useState("tous");
  const [temoForm, setTemoForm]             = useState({ nom:"", role:"", score:"", certType:"", certScore:"", texte:"", avatar:"🎓", couleur:"#1e4080", etoiles:5, ordre:0, photo_url:"", video_url:"" });
  const [temoUploading, setTemoUploading]   = useState(null); // "photo" | "video" | null
  const [temoUploadPct, setTemoUploadPct]   = useState(0);
  const temoFileRef = React.useRef(null);
  const [temoFileTarget, setTemoFileTarget] = useState(null); // "photo" | "video"
  const [temoFormOpen,   setTemoFormOpen]   = useState(false);
  const [temoSaving,     setTemoSaving]     = useState(false); // empêche le double-clic créer/modifier témoignage
  const [temoEditId,     setTemoEditId]     = useState(null);
  const [temoRejetId,    setTemoRejetId]    = useState(null);
  const [temoLightbox,   setTemoLightbox]   = useState(null); // { type:"photo"|"video", url, nom }
  const [temoMotif, setTemoMotif]           = useState("");
  const [temoPage,   setTemoPage]           = useState(1);
  const TEMO_PER_PAGE = 9;

  const fetchTemos = async () => {
    setTemosLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/temoignages`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      setTemos(await res.json());
    } catch { toast.error("Erreur chargement témoignages"); }
    finally { setTemosLoading(false); }
  };

  useEffect(() => { if (activeTab === "assistantes") fetchAssistantesAdmin(); }, [activeTab]);
  useEffect(() => { fetchCentresList(); }, []);

  const temoAction = async (id, updates) => {
    try {
      const res = await fetch(`${API_URL}/api/temoignages/${id}`, {
        method: "PATCH", headers: authHeaders(), body: JSON.stringify(updates),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `Erreur ${res.status}`);
      setTemos(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
      toast.success("Témoignage mis à jour");
    } catch (e) { toast.error(e.message || "Erreur mise à jour"); }
  };

  const temoDelete = async (id) => {
    if (!(await confirm("Supprimer définitivement ?", { titre: "Supprimer le témoignage" }))) return;
    try {
      await fetch(`${API_URL}/api/temoignages/${id}?hard=1`, { method: "DELETE", headers: authHeaders() });
      setTemos(prev => prev.filter(t => t.id !== id));
      toast.success("Supprimé");
    } catch { toast.error("Erreur suppression"); }
  };

  const TEMO_FORM_INIT = { nom:"", role:"", score:"", certType:"", certScore:"", texte:"", avatar:"🎓", couleur:"#1e4080", etoiles:5, ordre:0, photo_url:"", video_url:"" };

  const temoCreate = async () => {
    if (temoSaving) return;
    if (!temoForm.nom || !temoForm.texte) return toast.error("Nom et texte requis");
    setTemoSaving(true);
    try {
      if (temoEditId) {
        const res = await fetch(`${API_URL}/api/temoignages/${temoEditId}`, {
          method: "PATCH", headers: authHeaders(), body: JSON.stringify(temoForm),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || `Erreur ${res.status}`);
        setTemos(prev => prev.map(t => t.id === temoEditId ? { ...t, ...temoForm } : t));
        toast.success("Témoignage mis à jour");
      } else {
        const res = await fetch(`${API_URL}/api/temoignages`, {
          method: "POST", headers: authHeaders(), body: JSON.stringify(temoForm),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || `Erreur ${res.status}`);
        setTemos(prev => [data, ...prev]);
        toast.success("Témoignage créé");
      }
      setTemoForm(TEMO_FORM_INIT);
      setTemoFormOpen(false);
      setTemoEditId(null);
    } catch { toast.error(temoEditId ? "Erreur modification" : "Erreur création"); }
    finally { setTemoSaving(false); }
  };

  const temoApprouver = async (id) => {
    await temoAction(id, { statut: "actif", actif: true });
  };

  const temoRejeter = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/temoignages/${id}`, {
        method: "PATCH", headers: authHeaders(),
        body: JSON.stringify({ statut: "rejete", actif: false, motif_rejet: temoMotif }),
      });
      if (!res.ok) throw new Error();
      setTemos(prev => prev.map(t => t.id === id ? { ...t, statut: "rejete", actif: false, motif_rejet: temoMotif } : t));
      setTemoRejetId(null); setTemoMotif("");
      toast.success("Rejeté");
    } catch { toast.error("Erreur"); }
  };

  const temoUploadMedia = async (file, target) => {
    if (!file) return;
    const VIDEO_EXTS = ["mp4", "mov", "avi", "mkv", "webm", "m4v"];
    const ext = file.name?.split(".").pop()?.toLowerCase() || "";
    const isVideo = target === "video" || file.type.startsWith("video/") || VIDEO_EXTS.includes(ext);
    const endpoint = isVideo ? "/api/upload/video" : "/api/upload/image";
    setTemoUploading(target); setTemoUploadPct(0);
    try {
      const formData = new FormData();
      // Force le bon type MIME si le navigateur / OS l'a mal détecté
      if (isVideo && !file.type.startsWith("video/")) {
        formData.append("file", new File([file], file.name, { type: "video/mp4" }));
      } else {
        formData.append("file", file);
      }
      // Envoi direct vers Cloudinary : le relais backend plafonnait a 4,5 Mo (Vercel),
      // ce qui rendait impossible tout temoignage video. uploadFichier valide la taille
      // avant le reseau et remonte un message lisible.
      const { url } = await uploadFichier(formData.get("file"), isVideo ? "video" : "image", { onProgress: setTemoUploadPct });
      if (!url) throw new Error("URL non reçue");
      setTemoForm(f => ({ ...f, [target === "photo" ? "photo_url" : "video_url"]: url }));
      toast.success(`${target === "photo" ? "Photo" : "Vidéo"} uploadée !`);
    } catch (e) { toast.error(e.message); }
    finally { setTemoUploading(null); setTemoUploadPct(0); }
  };

  const temosPending = temos.filter(t => t.statut === "en_attente").length;

  // ── Messagerie interne ───────────────────────────────────
  const [msgConvs, setMsgConvs]         = useState([]);
  const [msgMessages, setMsgMessages]   = useState([]);
  const [msgActiveId, setMsgActiveId]   = useState(null);
  const [msgInput, setMsgInput]         = useState("");
  const [msgContacts, setMsgContacts]   = useState([]);
  const [showNewConv, setShowNewConv]   = useState(false);

  const fetchConvs = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/messages/conversations`, { headers: authHeaders() });
      if (!r.ok) return;
      const { conversations } = await r.json();
      setMsgConvs(conversations || []);
    } catch {}
  }, []);

  const fetchMsgMessages = useCallback(async (convId) => {
    try {
      const r = await fetch(`${API_URL}/api/messages/conversations/${convId}/messages`, { headers: authHeaders() });
      if (!r.ok) return;
      const { messages } = await r.json();
      setMsgMessages(messages || []);
      await fetch(`${API_URL}/api/messages/conversations/${convId}/read`, { method:"PATCH", headers: authHeaders() });
      setMsgConvs(prev => prev.map(c => c.id===convId ? {...c, non_lus:0} : c));
    } catch {}
  }, []);

  const fetchMsgContacts = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/messages/contacts`, { headers: authHeaders() });
      if (!r.ok) return;
      const { contacts } = await r.json();
      setMsgContacts(contacts || []);
    } catch {}
  }, []);

  const startConv = async (toId) => {
    try {
      const r = await fetch(`${API_URL}/api/messages/conversations`, {
        method:"POST", headers: authHeaders(), body: JSON.stringify({ to_id: toId })
      });
      if (!r.ok) return;
      const { conversation } = await r.json();
      await fetchConvs();
      setShowNewConv(false);
      setMsgActiveId(conversation.id);
      await fetchMsgMessages(conversation.id);
    } catch {}
  };

  const sendMsg = async () => {
    if (!msgInput.trim() || !msgActiveId) return;
    const content = msgInput.trim();
    setMsgInput("");
    try {
      await fetch(`${API_URL}/api/messages/conversations/${msgActiveId}/messages`, {
        method:"POST", headers: authHeaders(), body: JSON.stringify({ content })
      });
      await fetchMsgMessages(msgActiveId);
      await fetchConvs();
    } catch {}
  };

  useEffect(() => { fetchConvs(); }, [fetchConvs]);
  useEffect(() => {
    if (!msgActiveId) return;
    const t = setInterval(() => fetchMsgMessages(msgActiveId), 6000);
    return () => clearInterval(t);
  }, [msgActiveId, fetchMsgMessages]);

  const msgNonLuTotal = msgConvs.reduce((s,c) => s+(c.non_lus||0), 0);
  const saMyId = JSON.parse(localStorage.getItem("admin_profil")||"{}")?.id || "";
  const saPartnerName = (conv) => conv.user1_id===saMyId ? conv.user2_name : conv.user1_name;
  const saPartnerRole = (conv) => conv.user1_id===saMyId ? conv.user2_role : conv.user1_role;
  const msgActiveConv = msgConvs.find(c => c.id===msgActiveId);
  const ROLE_META_SA = {
    super_admin:"SuperAdmin", admin:"Admin", manager:"Manager",
    commercial:"Commercial",
    gestionnaire:"Gestionnaire", coach:"Coach", data_collector:"Data"
  };

  // ── Config centres WhatsApp ─────────────────────────────────────────────────
  const WA_LS_KEY = "bet_centers_config";
  const WA_DEFAULTS = [
    { key:"angre",    name:"Angré",       color:"#25d366", assistantes:[
      { nom:"Assistante 1", phone:"2250700000001", message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET Angré." },
      { nom:"Assistante 2", phone:"2250700000011", message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET Angré." },
    ]},
    { key:"bouake",   name:"Bouaké",      color:"#facc15", assistantes:[
      { nom:"Assistante 1", phone:"2250700000002", message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET Bouaké." },
      { nom:"Assistante 2", phone:"2250700000022", message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET Bouaké." },
    ]},
    { key:"plateaux", name:"II Plateaux", color:"#0891b2", assistantes:[
      { nom:"Assistante 1", phone:"2250700000003", message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET II Plateaux." },
      { nom:"Assistante 2", phone:"2250700000033", message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET II Plateaux." },
    ]},
    { key:"yopougon", name:"Yopougon",    color:"#a855f7", assistantes:[
      { nom:"Assistante 1", phone:"2250700000004", message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET Yopougon." },
      { nom:"Assistante 2", phone:"2250700000044", message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET Yopougon." },
    ]},
    { key:"koumassi", name:"Koumassi",    color:"#f97316", assistantes:[
      { nom:"Assistante 1", phone:"2250700000005", message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET Koumassi." },
      { nom:"Assistante 2", phone:"2250700000055", message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET Koumassi." },
    ]},
    { key:"abatta",   name:"Abatta",      color:"#ef4444", assistantes:[
      { nom:"Assistante 1", phone:"2250700000006", message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET Abatta." },
      { nom:"Assistante 2", phone:"2250700000066", message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET Abatta." },
    ]},
  ];

  const readLSCenters = () => {
    try { const s = localStorage.getItem(WA_LS_KEY); return s ? JSON.parse(s) : WA_DEFAULTS; }
    catch { return WA_DEFAULTS; }
  };

  const [waCenters,      setWaCenters]      = useState(readLSCenters);
  const [waLoading,      setWaLoading]      = useState(false);
  const [waSavingIdx,    setWaSavingIdx]    = useState(null);

  // Config contact centrale (bouton flottant, footer, page contact)
  const [contactConfig,     setContactConfig]     = useState({ whatsapp_number:"", whatsapp_number_2:"", whatsapp_number_3:"", whatsapp_message:"", email_central:"", localisation:"", maps_embed_url:"" });
  const [contactConfigLoad, setContactConfigLoad] = useState(false);
  const [contactConfigSave, setContactConfigSave] = useState(false);

  useEffect(() => {
    const load = async () => {
      setContactConfigLoad(true);
      try {
        const r = await fetch(`${API_URL}/api/config-contact`);
        if (r.ok) { const d = await r.json(); setContactConfig(d); }
      } catch {}
      setContactConfigLoad(false);
    };
    load();
  }, []); // eslint-disable-line

  const saveContactConfig = async () => {
    setContactConfigSave(true);
    try {
      const token = localStorage.getItem("admin_token");
      const r = await fetch(`${API_URL}/api/config-contact`, {
        method: "PUT",
        headers: { "Content-Type":"application/json", Authorization:`Bearer ${token}` },
        body: JSON.stringify(contactConfig),
      });
      if (r.ok) {
        toast.success("✓ Configuration de contact sauvegardée");
        // Notifie le frontend pour rechargement immédiat
        localStorage.setItem("bet_contact_config", JSON.stringify(contactConfig));
        window.dispatchEvent(new StorageEvent("storage", { key:"bet_contact_config", newValue:JSON.stringify(contactConfig) }));
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } catch { toast.error("Erreur réseau"); }
    setContactConfigSave(false);
  };

  // Charger depuis Supabase au montage (Supabase = source de vérité)
  useEffect(() => {
    const fetch = async () => {
      setWaLoading(true);
      const { data, error } = await supabase
        .from("plateforme_config")
        .select("valeur")
        .eq("key", "centres_wa")
        .maybeSingle();
      if (!error && data?.valeur?.length) {
        setWaCenters(data.valeur);
        localStorage.setItem(WA_LS_KEY, JSON.stringify(data.valeur)); // sync cache
      }
      setWaLoading(false);
    };
    fetch();
  }, []); // eslint-disable-line

  const updateWAField = (cIdx, aIdx, field, value) => {
    setWaCenters(prev => prev.map((c, ci) => ci !== cIdx ? c : {
      ...c,
      assistantes: c.assistantes.map((a, ai) => ai !== aIdx ? a : { ...a, [field]: value }),
    }));
  };

  // Sauvegarder un seul centre (l'état complet est écrit en base pour rester cohérent)
  const saveWACentre = async (cIdx) => {
    setWaSavingIdx(cIdx);
    try {
      await savePlateformeConfig("centres_wa", waCenters);
      localStorage.setItem(WA_LS_KEY, JSON.stringify(waCenters));
      window.dispatchEvent(new StorageEvent("storage", { key: WA_LS_KEY, newValue: JSON.stringify(waCenters) }));
      toast.success(`✓ BET ${waCenters[cIdx]?.name} sauvegardé`);
    } catch (e) {
      toast.error("Erreur : " + e.message);
    } finally {
      setWaSavingIdx(null);
    }
  };

  const resetWACentre = async (cIdx) => {
    const updated = waCenters.map((c, i) => i === cIdx ? WA_DEFAULTS[cIdx] : c);
    setWaCenters(updated);
    try {
      await savePlateformeConfig("centres_wa", updated);
      localStorage.setItem(WA_LS_KEY, JSON.stringify(updated));
      window.dispatchEvent(new StorageEvent("storage", { key: WA_LS_KEY, newValue: JSON.stringify(updated) }));
      toast(`↺ BET ${waCenters[cIdx]?.name} réinitialisé`);
    } catch (e) {
      toast.error("Erreur : " + e.message);
    }
  };

  // Chargement des utilisateurs réels
  const chargerUtilisateurs = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const res  = await fetch(`${API_URL}/api/admin/utilisateurs`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) setUsers((data.utilisateurs || []).map(u => ({ ...u, twofa: u.twofa_active ?? false })));
    } catch (e) { console.error("Erreur chargement utilisateurs", e); }
    finally { setLoadingUsers(false); }
  }, []);

  // Chargement de la liste des assistantes onboarding (pour assignation aux commerciales)
  const chargerOnboardingList = useCallback(async () => {
    try {
      const res  = await fetch(`${API_URL}/api/admin/utilisateurs?role=onboarding`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) setOnboardingList(data.utilisateurs || []);
    } catch (e) { console.error("Erreur chargement onboarding", e); }
  }, []);

  useEffect(() => {
    chargerUtilisateurs();
    chargerOnboardingList();
  }, [chargerUtilisateurs, chargerOnboardingList]);

  // Rafraîchir la liste des assistantes onboarding à chaque ouverture du modal - sinon une
  // assistante onboarding créée après le chargement initial de la page reste invisible tant
  // que la page n'est pas rechargée.
  useEffect(() => {
    if (showInviteModal) chargerOnboardingList();
  }, [showInviteModal, chargerOnboardingList]);

  const formatDate = (d) => new Date(d).toLocaleDateString("fr-FR", { day:"numeric", month:"short", year:"numeric" });
  const formatMoney = (val) => val.toLocaleString("fr-FR") + " FCFA";

  const stats = useMemo(() => ({
    actifs: users.filter(u=>u.actif).length,
    totalSessions: users.reduce((s,u)=>s+u.sessions,0),
    sans2fa: users.filter(u=>u.actif && !u.twofa).length,
    enAttente: demandes.filter(d=>d.statut==="en_attente").length,
    alertes: auditLog.filter(a=>a.statut==="danger").length,
    tempAccess: users.filter(u=>u.accessTemp && new Date(u.accessTemp)>new Date()).length,
  }), [users, demandes, auditLog]);

  const usersFiltres = useMemo(() => {
    let r = [...users];
    if (filtreRole !== "Tous") r = r.filter(u => u.role === filtreRole);
    if (filtreCentre !== "Tous") {
      if (filtreCentre === "national") {
        r = r.filter(u => !u.scope || u.scope.includes("national") || u.role === "super_admin");
      } else {
        r = r.filter(u => u.scope && u.scope.includes(filtreCentre));
      }
    }
    if (filtreStatut === "Actifs") r = r.filter(u => u.actif);
    if (filtreStatut === "Inactifs") r = r.filter(u => !u.actif);
    if (filtreStatut === "Sans 2FA") r = r.filter(u => !u.twofa);
    if (filtreStatut === "En ligne") r = r.filter(u => onlineUsers.includes(u.id));
    if (searchUser) r = r.filter(u => u.nom.toLowerCase().includes(searchUser.toLowerCase()) || u.email.toLowerCase().includes(searchUser.toLowerCase()));
    return r;
  }, [users, filtreRole, filtreCentre, filtreStatut, searchUser, onlineUsers]);

  const auditFiltres = useMemo(() => {
    if (filtreAudit === "Tous") return auditLog;
    return auditLog.filter(a => a.statut === filtreAudit);
  }, [auditLog, filtreAudit]);

  const addAuditEntry = (action, detail, statut="success") => {
    const newEntry = { id: auditLog.length+1, acteur:"Super Admin", role:"super_admin", action, detail, date:new Date().toLocaleString(), ip:"127.0.0.1", statut };
    setAuditLog([newEntry, ...auditLog]);
  };

  const toggleUserStatus = async (userId) => {
    const target = users.find(u => u.id === userId);
    if (!target) return;
    const newActif = !target.actif;
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, actif: newActif } : u));
    try {
      const res = await fetch(`${API_URL}/api/admin/utilisateurs/${userId}`, {
        method: "PATCH", headers: authHeaders(),
        body: JSON.stringify({ actif: newActif }),
      });
      if (!res.ok) {
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, actif: target.actif } : u));
        toast.error("Erreur lors du changement de statut");
      } else {
        addAuditEntry("STATUT_UTILISATEUR", `${target.prenom} ${target.nom} ${newActif ? "activé" : "désactivé"}`, "warning");
        toast.success(`${target.prenom} ${target.nom} ${newActif ? "✅ activé" : "🔴 désactivé"}`);
      }
    } catch {
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, actif: target.actif } : u));
      toast.error("Impossible de joindre le serveur");
    }
  };

  const toggle2FA = async (userId, newVal) => {
    const userName = users.find(u => u.id === userId)?.prenom || "l'utilisateur";
    // Mise à jour optimiste de l'UI
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, twofa: newVal } : u));
    try {
      const res = await fetch(`${API_URL}/api/admin/utilisateurs/${userId}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ twofa_active: newVal }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Erreur ${res.status}`);
      }
      addAuditEntry("2FA_MODIFIE", `${userName} : 2FA ${newVal ? "activé" : "désactivé"}`, "warning");
      toast.success(`2FA ${newVal ? "activé 🔐 - l'utilisateur devra configurer son authenticator" : "désactivé"} pour ${userName}`);
    } catch (e) {
      // Rollback si erreur
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, twofa: !newVal } : u));
      toast.error(`❌ Erreur 2FA : ${e.message}`);
    }
  };

  const revokeSession = (userId) => {
    setUsers(users.map(u => u.id===userId ? {...u, sessions:0} : u));
    addAuditEntry("SESSION_REVOQUEE", `Toutes les sessions de l'utilisateur ${userId} ont été révoquées`, "warning");
    toast.success("Sessions révoquées");
    setShowRevokeModal(false);
  };

  const sendInvite = async () => {
    if (sendingInvite) return;
    const parts = inviteForm.nom.trim().split(" ");
    const prenom = parts[0] || "";
    const nom    = parts.slice(1).join(" ") || prenom;
    if (!prenom || !inviteForm.email) { toast.error("Veuillez remplir le nom et l'email"); return; }
    const isCorporateB2B = inviteForm.role === "commercial" && inviteForm.profil_assistante === "b2b";
    const needsCentre = ROLES_AVEC_CENTRE.includes(inviteForm.role) &&
      !(inviteForm.role === "commercial" && inviteForm.type_cours === "en_ligne") &&
      !isCorporateB2B;
    if (needsCentre && !inviteForm.centre_id) {
      toast.error("Veuillez sélectionner le centre BET de cet utilisateur");
      return;
    }
    if (inviteForm.role === "commercial" && inviteForm.jours_travail.length === 0) {
      toast.error("Sélectionnez au moins un jour de service pour l'assistante");
      return;
    }
    if (inviteForm.role === "commercial" && inviteForm.profil_assistante !== "b2b" && inviteForm.type_cours !== "presentiel" && !inviteForm.onboarding_id) {
      toast.error("Sélectionnez une assistante onboarding pour cette commerciale en ligne");
      return;
    }
    if (!(await confirm(`Créer l'utilisateur ${prenom} ${nom} (${inviteForm.email}) avec le rôle "${inviteForm.role}" ?`, { titre: "Créer l'utilisateur", confirmLabel: "Créer" }))) return;
    setSendingInvite(true);
    try {
      const scope = inviteForm.centre_id ? [inviteForm.centre_id] : ["national"];
      const payload = {
        nom, prenom,
        email: inviteForm.email,
        telephone: inviteForm.telephone || null,
        role: inviteForm.role,
        scope,
        note: inviteForm.note,
        avatar_url: inviteForm.photo || null,
        couleur: inviteForm.couleur || null,
      };
      // Pour les coachs : inclure les infos spécifiques
      if (inviteForm.role === "coach") {
        payload.coach_info = {
          photo_url:           inviteForm.photo                || null,
          matricule:           inviteForm.coach_matricule      || null,
          filiere:             inviteForm.coach_filiere        || null,
          lieu_habitation:     inviteForm.coach_lieu_habitation|| null,
          date_debut_bet:      inviteForm.coach_date_debut     || null,
          nb_contrats_actifs:  inviteForm.coach_nb_contrats    || 0,
          certifications:      inviteForm.coach_certifications.length ? inviteForm.coach_certifications : [],
          niveaux_enseignes:   inviteForm.coach_niveaux.length ? inviteForm.coach_niveaux : [],
        };
      }
      // Pour les assistantes commerciales : inclure le planning
      if (inviteForm.role === "commercial") {
        payload.planning = {
          centre_id: inviteForm.centre_id || null,
          type_cours: inviteForm.type_cours,
          type_semaine: inviteForm.jours_travail.some(j => ["samedi","dimanche"].includes(j)) &&
                        inviteForm.jours_travail.some(j => ["lundi","mardi","mercredi","jeudi","vendredi"].includes(j))
                        ? "les_deux"
                        : inviteForm.jours_travail.some(j => ["samedi","dimanche"].includes(j)) ? "weekend" : "semaine",
          quota_jour: inviteForm.quota_jour,
          jours_travail: inviteForm.jours_travail,
          profil: inviteForm.profil_assistante || "b2c",
          onboarding_id: inviteForm.onboarding_id || null,
        };
      }
      const res  = await fetch(`${API_URL}/api/admin/utilisateurs`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.error || "Erreur création"); return; }
      if (data.assistante_warning) {
        toast.error(`⚠️ Utilisateur créé mais le profil assistante n'a pas pu être enregistré : ${data.assistante_warning}`);
      }
      if (data.utilisateur?.id && data.mdp_temporaire) {
        setTempPasswords(prev => ({ ...prev, [data.utilisateur.id]: data.mdp_temporaire }));
        setCreatedCredentials({ nom: inviteForm.nom, email: inviteForm.email, mdp: data.mdp_temporaire, role: inviteForm.role });
        setShowCredentialsModal(true);
      }
      await chargerUtilisateurs();
      if (inviteForm.role === "commercial") fetchAssistantesAdmin();
      setShowInviteModal(false);
      setInviteForm({ nom:"", email:"", telephone:"", role:"manager", centre_id:"", accessTemp:"", note:"", type_cours:"en_ligne", quota_jour:10, jours_travail:["lundi","mardi","mercredi","jeudi","vendredi"], profil_assistante:"b2c", photo:"", coach_matricule:"", coach_filiere:"", coach_lieu_habitation:"", coach_date_debut:"", coach_nb_contrats:0, coach_certifications:[], coach_certif_input:"", coach_niveaux:[], onboarding_id:"", couleur:"#10b981" });
    } catch { toast.error("Impossible de joindre le serveur"); }
    finally { setSendingInvite(false); }
  };

  const uploadInvitePhoto = async (file) => {
    setInvitePhotoUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await fetchUpload(`${API_URL}/api/upload/avatar`, { method:"POST", headers:{ Authorization:`Bearer ${localStorage.getItem("admin_token")}` }, body:fd });
      const d = await r.json();
      if (!r.ok) { toast.error(d.error || `Erreur ${r.status} - upload photo`); return; }
      const url = d.file?.url || d.url || "";
      if (url) setInviteForm(p => ({ ...p, photo: url }));
      else toast.error("Upload échoué - URL manquante");
    } catch (e) { toast.error("Erreur réseau upload photo"); }
    finally { setInvitePhotoUploading(false); }
  };

  const openCoachModal = (coach) => {
    setSelectedCoach(coach);
    setCoachModalTab("infos");
    setCoachContrats([]);
    setCoachGroupes([]);
    setCoachEvals([]);
    setCoachDispoData([]);
    setCoachEvalExpanded(new Set());
    setShowContratForm(false);
    setContratEditId(null);
    fetchCoachContrats(coach.id);
    fetchCoachGroupes(coach.id);
    fetchCoachEvals(coach.id);
  };

  const fetchCoachContrats = async (coachId) => {
    setCoachContratsLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/contrats-prives?coach_id=${coachId}`, { headers: authHeaders() });
      const d = await r.json();
      setCoachContrats(d.contrats || []);
    } catch {}
    finally { setCoachContratsLoading(false); }
  };

  const fetchCoachGroupes = async (coachId) => {
    setCoachGroupesLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/groupes?coach_id=${coachId}`, { headers: authHeaders() });
      const d = await r.json();
      setCoachGroupes(d.groupes || []);
    } catch {}
    finally { setCoachGroupesLoading(false); }
  };

  const fetchCoachEvals = async (coachId) => {
    setCoachEvalsLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/coachs/${coachId}/evaluations`, { headers: authHeaders() });
      const d = await r.json();
      setCoachEvals(d.evaluations || []);
    } catch {}
    finally { setCoachEvalsLoading(false); }
  };

  const fetchCoachDispos = async (coachId) => {
    setCoachDispoLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/coachs/${coachId}/disponibilites`, { headers: authHeaders() });
      const d = r.ok ? await r.json() : { disponibilites: [] };
      setCoachDispoData(d.disponibilites || []);
    } catch {}
    finally { setCoachDispoLoading(false); }
  };

  const openCoachGroupeModal = async (groupe) => {
    setCoachGroupeModal(groupe);
    setCoachGroupeModalSubTab("apprenants");
    setCoachGroupeModalCours([]);
    setCoachGroupeModalPresences([]);
    setCoachGroupeModalDetail({ apprenants:[], fichiers:[] });
    try {
      const r = await fetch(`${API_URL}/api/groupes/${groupe.id}`, { headers: authHeaders() });
      const d = await r.json();
      setCoachGroupeModalDetail({ apprenants: d.apprenants || [], fichiers: d.fichiers || [] });
    } catch {}
  };

  const fetchCoachGroupeModalCours = async (groupeId, mois, annee) => {
    setCoachGroupeModalCoursLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/groupes/${groupeId}/cours?mois=${mois}&annee=${annee}`, { headers: authHeaders() });
      const d = await r.json();
      setCoachGroupeModalCours(d.cours || []);
    } catch {} finally { setCoachGroupeModalCoursLoading(false); }
  };

  const fetchCoachGroupeModalPresences = async (groupeId) => {
    try {
      const r = await fetch(`${API_URL}/api/groupes/${groupeId}/presences`, { headers: authHeaders() });
      const d = await r.json();
      setCoachGroupeModalPresences(d.presences || []);
    } catch {}
  };

  const saveContrat = async () => {
    if (contratSaving) return;
    if (!contratForm.apprenant_nom || !contratForm.prix_h) {
      toast.error("Nom apprenant et prix/h sont requis"); return;
    }
    setContratSaving(true);
    try {
      const url = contratEditId
        ? `${API_URL}/api/contrats-prives/${contratEditId}`
        : `${API_URL}/api/contrats-prives`;
      const method = contratEditId ? "PATCH" : "POST";
      const body = contratEditId
        ? contratForm
        : { ...contratForm, coach_id: selectedCoach.id };
      const r = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(body) });
      if (!r.ok) throw new Error();
      toast.success(contratEditId ? "Contrat mis à jour ✓" : "Contrat créé ✓");
      setShowContratForm(false);
      setContratEditId(null);
      setContratForm({ apprenant_nom:"", apprenant_prenom:"", apprenant_email:"", apprenant_telephone:"", type_contrat:"en_ligne", niveau:"B1", objectif:"", prix_h:"", nb_seances_total:"", duree_seance_h:"1.5", date_debut:"", date_fin:"", note:"" });
      fetchCoachContrats(selectedCoach.id);
    } catch { toast.error("Erreur sauvegarde contrat"); }
    finally { setContratSaving(false); }
  };

  const patchContrat = async (id, updates) => {
    try {
      const r = await fetch(`${API_URL}/api/contrats-prives/${id}`, { method:"PATCH", headers: authHeaders(), body: JSON.stringify(updates) });
      if (!r.ok) throw new Error();
      fetchCoachContrats(selectedCoach.id);
    } catch { toast.error("Erreur mise à jour"); }
  };

  const deleteContrat = async (id) => {
    if (!(await confirm("Supprimer ce contrat ?", { titre: "Supprimer le contrat" }))) return;
    try {
      await fetch(`${API_URL}/api/contrats-prives/${id}`, { method:"DELETE", headers: authHeaders() });
      toast.success("Contrat supprimé");
      fetchCoachContrats(selectedCoach.id);
    } catch { toast.error("Erreur suppression"); }
  };

  const renvoyerAcces = async (userId) => {
    try {
      const res  = await fetch(`${API_URL}/api/admin/utilisateurs/${userId}/renvoyer-acces`, {
        method: "POST", headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.error || "Erreur"); return; }
      setTempPasswords(prev => ({ ...prev, [userId]: data.mdp_initial }));
      toast.success(`Accès de ${data.nom} affichés dans le tableau`);
    } catch { toast.error("Impossible de joindre le serveur"); }
  };

  const syncAssistante = async (userId) => {
    try {
      const isB2B = await confirm(
        "Cette assistante est-elle une Corporate (B2B - entreprises) ou une B2C (particuliers) ?",
        { titre: "Profil de l'assistante", confirmLabel: "🏢 Corporate B2B", cancelLabel: "👤 B2C" }
      );
      const profil = isB2B ? "b2b" : "b2c";
      const res = await fetch(`${API_URL}/api/admin/utilisateurs/${userId}/sync-assistante`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          type_cours: "en_ligne",
          quota_jour: 10,
          jours_travail: ["lundi","mardi","mercredi","jeudi","vendredi"],
          profil,
        }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.error || "Erreur sync assistante"); return; }
      toast.success(`✅ Profil assistante ${data.action === "created" ? "créé" : "mis à jour"} - vérifiez l'onglet Planning assistantes`);
      fetchAssistantesAdmin();
    } catch { toast.error("Impossible de joindre le serveur"); }
  };

  const exportUsers = () => {
    exporterExcelStyle({
      nomFichier: `superadmin_utilisateurs_${new Date().toISOString().slice(0,10)}`,
      titre: '👥 Utilisateurs - Super Admin',
      sousTitre: `${users.length} utilisateur${users.length>1?'s':''} · Généré le ${new Date().toLocaleDateString('fr-FR')}`,
      couleurEntete: '6366F1',
      colonnes: [
        { label: 'Nom', width: 24, bold: true }, { label: 'Email', width: 26 },
        { label: 'Rôle', width: 16 }, { label: 'Statut', width: 12, align: 'center' },
        { label: '2FA', width: 10, align: 'center' }, { label: 'Sessions', width: 12, align: 'center' },
        { label: 'Dernier accès', width: 18 },
      ],
      lignes: users.map(u => [u.nom, u.email, u.role, u.actif?"Actif":"Inactif", u.twofa?"Oui":"Non", u.sessions, u.dernConn]),
    });
    toast.success("Export Excel effectué");
  };

  /* ── Télécharger le modèle Excel pour le rôle sélectionné ── */
  const telechargerModeleImport = (roleKey) => {
    const rk  = roleKey || importRole;
    const sch = SA_IMPORT_SCHEMAS[rk];
    if (!sch) return;
    const headers = sch.colonnes.map(c => c.header);
    const ws = XLSX.utils.aoa_to_sheet([headers, sch.exemple || headers.map(()=>"")]);
    ws["!cols"] = headers.map(() => ({ wch:18 }));
    const wb2 = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb2, ws, `Import ${sch.label}`);
    XLSX.writeFile(wb2, `modele_import_${rk}_bet.xlsx`);
    toast.success(`Modèle ${sch.label} téléchargé ✓`);
  };

  /* ── Phase 1 : lecture, validation, prévisualisation (multi-profil) ── */
  const lireEtPrevisualiser = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const sch = SA_IMPORT_SCHEMAS[importRole];
    if (!sch) { toast.error("Profil non reconnu"); return; }

    try {
      const buf  = await file.arrayBuffer();
      const wb2  = XLSX.read(buf, { type:"array" });
      const ws   = wb2.Sheets[wb2.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval:"" });
      if (!rows.length) { toast.error("Fichier vide ou non reconnu"); return; }

      const norm = (s) => String(s||"").toLowerCase().trim()
        .replace(/[éèêë]/g,"e").replace(/[àâä]/g,"a").replace(/[ùûü]/g,"u").replace(/[îï]/g,"i").replace(/[ôö]/g,"o")
        .replace(/[^a-z0-9]/g,"_").replace(/_+/g,"_").replace(/^_|_$/g,"");

      const isEmailOk = (em) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(em);
      const emailsBase  = new Set(users.map(u => (u.email||"").toLowerCase().trim()));
      const emailsBatch = {};

      rows.forEach(row => {
        const r = {}; Object.keys(row).forEach(k => { r[norm(k)] = String(row[k]||"").trim(); });
        const em = (r.email || r.e_mail || r.courriel || "").toLowerCase().trim();
        if (em) emailsBatch[em] = (emailsBatch[em] || 0) + 1;
      });

      const emailsVusDansBatch = {};
      const lignes = rows.map((row, idx) => {
        const r = {}; Object.keys(row).forEach(k => { r[norm(k)] = String(row[k]||"").trim(); });

        const prenom = (r.prenom || r.firstname || "").trim();
        const nom    = (r.nom    || r.lastname  || "").trim();
        const email  = (r.email  || r.e_mail    || r.courriel || "").toLowerCase().trim();
        const tel    = r.telephone || r.tel || "";
        const note   = r.note || r.commentaire || r.comment || "";

        const centreRaw = r.centre_bet || r.centre || r.departement___centre || r.departement || "";
        const centre_id = sch.avecCentre ? _mapCentreGlobal(centreRaw) : null;
        const scope     = centre_id ? [centre_id] : ["national"];

        const extra = { note };
        if (importRole === "coach") {
          extra.filiere         = r.filiere         || r.fili_re     || "";
          extra.matricule       = r.matricule       || "";
          extra.specialite      = r.specialite      || r.sp_cialite  || "";
          extra.niveaux         = r.niveaux_enseignes || r.niveaux   || "";
          extra.type_contrat    = r.type_de_contrat || r.type_contrat || r.contrat || "";
          extra.lieu_habitation = r.lieu_d_habitation || r.lieu_habitation || "";
          extra.date_debut_bet  = r.date_debut_bet  || r.date_debut  || "";
          extra.certifications  = r.certifications  || "";
        }
        if (importRole === "commercial" || importRole === "superviseur") {
          extra.zone = r.zone || "";
        }

        const erreurs = [], avertissements = [];
        if (!prenom) erreurs.push("Prénom manquant");
        if (!nom)    erreurs.push("Nom manquant");
        if (!email)  erreurs.push("Email manquant");
        else if (!isEmailOk(email)) erreurs.push("Format email invalide");
        else {
          if (emailsBase.has(email)) erreurs.push("Email déjà existant en base");
          if (emailsBatch[email] > 1 && emailsVusDansBatch[email]) erreurs.push("Email en doublon dans le fichier");
        }
        if (email) emailsVusDansBatch[email] = true;
        if (!tel) avertissements.push("Téléphone manquant");
        if (importRole === "coach" && !extra.matricule) avertissements.push("Matricule non renseigné");
        if (sch.avecCentre && !centre_id) avertissements.push("Centre BET non renseigné → périmètre national");

        const fields  = { prenom, nom, email, telephone:tel, centre_bet:centreRaw, ...extra };
        const payload = sch.buildPayload(fields, scope);

        return {
          _idx: idx + 2, prenom, nom, email, tel, centre_id, scope, ...extra,
          _erreurs: erreurs, _avertissements: avertissements,
          _statut: erreurs.length > 0 ? "erreur" : avertissements.length > 0 ? "avertissement" : "ok",
          _payload: payload,
        };
      }).filter(l => l.prenom || l.nom || l.email);

      if (!lignes.length) { toast.error("Aucune ligne exploitable dans le fichier"); return; }
      setImportPreviewRows(lignes);
      setShowImportPreview(true);
    } catch (err) { toast.error("Erreur lecture fichier : " + err.message); }
  };

  /* ── Phase 2 : envoi en base des lignes valides ── */
  const confirmerImport = async () => {
    if (importConfirming) return;
    const lignesValides = importPreviewRows.filter(l => l._statut !== "erreur");
    if (!lignesValides.length) return;
    setImportConfirming(true);
    const getToken = () => localStorage.getItem("admin_token") || "";
    let ok = 0, fail = 0, resultats = [];
    for (const l of lignesValides) {
      try {
        const res = await fetch(`${API_URL}/api/admin/utilisateurs`, {
          method: "POST",
          headers: { "Content-Type":"application/json", Authorization:`Bearer ${getToken()}` },
          body: JSON.stringify(l._payload),
        });
        if (res.ok) {
          const d = await res.json().catch(()=>({}));
          ok++; resultats.push({ prenom:l.prenom, nom:l.nom, email:l.email, mdp: d.mdp_temporaire||"-", statut:"ok" });
        } else {
          const d = await res.json().catch(()=>({}));
          fail++; resultats.push({ prenom:l.prenom, nom:l.nom, email:l.email, mdp:"-", statut:"erreur", detail: d.error||res.statusText });
        }
      } catch (err) {
        fail++; resultats.push({ prenom:l.prenom, nom:l.nom, email:l.email, mdp:"-", statut:"erreur", detail: err.message });
      }
    }
    setImportConfirming(false);
    setShowImportPreview(false);
    setImportPreviewRows([]);
    const roleLabel = SA_IMPORT_SCHEMAS[importRole]?.label || importRole;
    if (ok > 0) {
      toast.success(`${ok} ${roleLabel}(s) créé(s)${fail ? ` · ${fail} erreur(s)` : ""} ✓`);
      chargerUtilisateurs();
      setImportResultats(resultats);
      setShowImportResultat(true);
    } else {
      toast.error(`Aucun ${roleLabel} créé - ${fail} erreur(s).`);
    }
  };

  // ── Boutique ─────────────────────────────────────────────
  const [boutiqueSubTab,    setBoutiqueSubTab]    = useState("produits");
  const [produits,          setProduits]          = useState([]);
  const [prodLoading,       setProdLoading]       = useState(false);
  const [commandes,         setCommandes]         = useState([]);
  const [cmdLoading,        setCmdLoading]        = useState(false);
  const [cmdFiltreStatut,   setCmdFiltreStatut]   = useState("tous");
  const [selectedProduit,   setSelectedProduit]   = useState(null);
  const PROD_FORM_INIT = { nom:"", description:"", prix:"", stock:"0", categorie:"Autre", images:[] };
  const [prodForm,          setProdForm]          = useState(PROD_FORM_INIT);
  const [prodFormOpen,      setProdFormOpen]      = useState(false);
  const [prodSaving,        setProdSaving]        = useState(false);
  const [prodImageUploading,setProdImageUploading]= useState(false);
  const [prodPage,          setProdPage]          = useState(1);
  const PROD_PER_PAGE = 12;

  const CATEGORIES_BOUTIQUE = ["Autre","Vêtements","Accessoires","Fournitures","Livres","Goodies"];
  const STATUTS_CMD = [
    { key:"tous",       label:"Toutes",     color:"#6b7280" },
    { key:"en_attente", label:"En attente", color:"#d97706" },
    { key:"confirmee",  label:"Confirmée",  color:"#2563eb" },
    { key:"livree",     label:"Livrée",     color:"#059669" },
    { key:"annulee",    label:"Annulée",    color:"#dc2626" },
  ];

  const fetchProduits = async () => {
    setProdLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/boutique/produits`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      setProduits(await res.json());
    } catch { toast.error("Erreur chargement produits"); }
    finally { setProdLoading(false); }
  };

  const fetchCommandes = async () => {
    setCmdLoading(true);
    try {
      const url = cmdFiltreStatut === "tous"
        ? `${API_URL}/api/boutique/commandes`
        : `${API_URL}/api/boutique/commandes?statut=${cmdFiltreStatut}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      setCommandes(await res.json());
    } catch { toast.error("Erreur chargement commandes"); }
    finally { setCmdLoading(false); }
  };

  const saveProduit = async () => {
    if (prodSaving) return;
    if (!prodForm.nom || !prodForm.prix) return toast.error("Nom et prix requis");
    setProdSaving(true);
    try {
      const isEdit = !!selectedProduit;
      const url = isEdit
        ? `${API_URL}/api/boutique/produits/${selectedProduit.id}`
        : `${API_URL}/api/boutique/produits`;
      const res = await fetch(url, {
        method: isEdit ? "PATCH" : "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ ...prodForm, prix: Number(prodForm.prix), stock: Number(prodForm.stock), image_url: prodForm.images[0] || null }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      toast.success(isEdit ? "Produit mis à jour" : "Produit créé");
      setProdFormOpen(false);
      setSelectedProduit(null);
      setProdForm(PROD_FORM_INIT);
      setProdPage(1);
      fetchProduits();
    } catch (e) { toast.error(e.message || "Erreur sauvegarde"); }
    finally { setProdSaving(false); }
  };

  const toggleProduitActif = async (id, actif) => {
    try {
      await fetch(`${API_URL}/api/boutique/produits/${id}`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ actif: !actif }),
      });
      setProduits(p => p.map(x => x.id === id ? { ...x, actif: !actif } : x));
    } catch { toast.error("Erreur"); }
  };

  const deleteProduit = async (id) => {
    if (!(await confirm("Supprimer ce produit ?", { titre: "Supprimer le produit" }))) return;
    try {
      await fetch(`${API_URL}/api/boutique/produits/${id}`, { method:"DELETE", headers: authHeaders() });
      toast.success("Produit supprimé");
      setProduits(p => p.filter(x => x.id !== id));
    } catch { toast.error("Erreur suppression"); }
  };

  const updateCmdStatut = async (id, statut) => {
    try {
      await fetch(`${API_URL}/api/boutique/commandes/${id}`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ statut }),
      });
      setCommandes(c => c.map(x => x.id === id ? { ...x, statut } : x));
      toast.success("Statut mis à jour");
    } catch { toast.error("Erreur"); }
  };

  const uploadProduitImage = async (file) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image trop lourde - maximum 10 Mo");
      return;
    }
    setProdImageUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetchUpload(`${API_URL}/api/upload/image`, { method:"POST", headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }, body: fd });
      if (res.status === 413) throw new Error("Fichier trop volumineux pour le serveur (max 10 Mo)");
      let d;
      try { d = await res.json(); } catch { throw new Error(`Erreur serveur (${res.status})`); }
      if (!res.ok) throw new Error(d.error || `Erreur ${res.status}`);
      setProdForm(f => ({ ...f, images: [...(f.images||[]), d.file.url] }));
      toast.success("Image ajoutée");
    } catch (e) { toast.error(e.message || "Erreur upload image"); }
    finally { setProdImageUploading(false); }
  };

  // ── Gestion Articles Blog ────────────────────────────────
  const BLOG_CATEGORIES = ["Actualités","Conseils","Certifications","Entreprises","Réussite","Événements","Général"];
  const BLOG_FORM_INIT  = { titre:"", extrait:"", contenu:"", categorie:"Actualités", auteur:"Admin", read_time:"", image_url:"", video_url:"", publie:false };
  const [blogInnerTab,     setBlogInnerTab]     = useState("articles");
  const [blogArticles,     setBlogArticles]     = useState([]);
  const [blogLoading,      setBlogLoading]      = useState(false);
  const [blogForm,         setBlogForm]         = useState(BLOG_FORM_INIT);
  const [blogFormOpen,     setBlogFormOpen]     = useState(false);
  const [blogEditId,       setBlogEditId]       = useState(null);
  const [blogSaving,       setBlogSaving]       = useState(false);
  const [blogSearch,       setBlogSearch]       = useState("");
  const [blogUploading,    setBlogUploading]    = useState(null);
  const [blogUploadPct,    setBlogUploadPct]    = useState(0);
  const [blogFileTarget,   setBlogFileTarget]   = useState("image");
  const blogFileRef = useRef(null);

  const fetchBlogArticles = async () => {
    setBlogLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/blog/admin/all`, { headers: authHeaders() });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setBlogArticles(d.articles || []);
    } catch { toast.error("Erreur chargement articles"); }
    finally { setBlogLoading(false); }
  };

  const blogSave = async () => {
    if (blogSaving) return;
    if (!blogForm.titre.trim()) return toast.error("Titre requis");
    setBlogSaving(true);
    try {
      const method = blogEditId ? "PUT" : "POST";
      const url    = blogEditId ? `${API_URL}/api/blog/${blogEditId}` : `${API_URL}/api/blog`;
      const r = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(blogForm) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Erreur");
      toast.success(blogEditId ? "Article mis à jour" : "Article créé");
      setBlogFormOpen(false); setBlogEditId(null); setBlogForm(BLOG_FORM_INIT);
      fetchBlogArticles();
    } catch (e) { toast.error(e.message); }
    finally { setBlogSaving(false); }
  };

  const blogTogglePublie = async (id, publie) => {
    try {
      await fetch(`${API_URL}/api/blog/${id}/publie`, { method:"PATCH", headers: authHeaders(), body: JSON.stringify({ publie: !publie }) });
      setBlogArticles(p => p.map(a => a.id===id ? {...a, publie:!publie} : a));
    } catch { toast.error("Erreur"); }
  };

  const blogDelete = async (id) => {
    if (!(await confirm("Supprimer cet article définitivement ?", { titre: "Supprimer l'article" }))) return;
    try {
      await fetch(`${API_URL}/api/blog/${id}`, { method:"DELETE", headers: authHeaders() });
      setBlogArticles(p => p.filter(a => a.id !== id));
      toast.success("Article supprimé");
    } catch { toast.error("Erreur suppression"); }
  };

  const blogUploadMedia = async (file, target) => {
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    const endpoint = isVideo ? "/api/upload/video" : "/api/upload/image";
    setBlogUploading(target); setBlogUploadPct(0);
    try {
      const formData = new FormData();
      formData.append("file", file);
      // Envoi direct vers Cloudinary : le relais backend plafonnait a 4,5 Mo (Vercel),
      // ce qui rendait impossible tout temoignage video. uploadFichier valide la taille
      // avant le reseau et remonte un message lisible.
      const { url } = await uploadFichier(formData.get("file"), isVideo ? "video" : "image", { onProgress: setBlogUploadPct });
      if (!url) throw new Error("URL non reçue");
      setBlogForm(f => ({ ...f, [target === "video" ? "video_url" : "image_url"]: url }));
      toast.success(`${target === "video" ? "Vidéo" : "Image"} uploadée !`);
    } catch (e) { toast.error(e.message); }
    finally { setBlogUploading(null); setBlogUploadPct(0); }
  };

  // ── Commentaires Blog ────────────────────────────────────
  const [blogComments,        setBlogComments]        = useState([]);
  const [blogCommentsLoading, setBlogCommentsLoading] = useState(false);
  const [blogCommentsSearch,  setBlogCommentsSearch]  = useState("");
  const [blogExpandedArticle, setBlogExpandedArticle] = useState(null);

  const fetchBlogComments = async () => {
    setBlogCommentsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/blog/admin/commentaires/all`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setBlogComments(d.commentaires || []);
    } catch { toast.error("Erreur chargement commentaires"); }
    finally { setBlogCommentsLoading(false); }
  };

  const deleteBlogComment = async (id) => {
    if (!(await confirm("Supprimer ce commentaire définitivement ?", { titre: "Supprimer le commentaire" }))) return;
    try {
      await fetch(`${API_URL}/api/blog/commentaires/${id}`, { method:"DELETE", headers: authHeaders() });
      setBlogComments(c => c.filter(x => x.id !== id));
      toast.success("Commentaire supprimé");
    } catch { toast.error("Erreur suppression"); }
  };

  const fetchApprenants = async () => {
    setLoadingApprenants(true);
    try {
      const [resAssign, resPrives] = await Promise.all([
        fetch(`${API_URL}/api/parcours/assignations`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/contrats-prives`,       { headers: authHeaders() }),
      ]);
      const dataAssign = resAssign.ok  ? await resAssign.json()  : { assignations:[] };
      const dataPrives = resPrives.ok  ? await resPrives.json()  : { contrats:[] };

      const fromAssignations = (dataAssign.assignations || []).map(a => ({
        id:              a.id,
        nom:             a.prospect_nom       || "-",
        email:           a.prospect_email     || "-",
        telephone:       a.prospect_telephone || "-",
        type_cours:      a.type_coaching === "prive" ? "cours_prive" : (a.type_cours || "-"),
        type_coaching:   a.type_coaching      || "-",
        centre_id:       a.centre_id          || null,
        centre_nom:      a.centre_nom         || null,
        statut:          a.statut             || "nouveau",
        statut_paiement: a.statut_paiement    || null,
        mode_paiement:   a.mode_paiement      || null,
        commercial:      a.assistante_nom     || "-",
        created_at:      a.created_at,
        _source:         "assignation",
      }));

      const fromPrives = (dataPrives.contrats || []).map(c => ({
        id:              `cp-${c.id}`,
        nom:             `${c.apprenant_prenom||""} ${c.apprenant_nom||""}`.trim() || "-",
        email:           c.apprenant_email    || "-",
        telephone:       c.apprenant_telephone|| "-",
        type_cours:      "cours_prive",
        type_coaching:   c.type_coaching      || c.type_cours || "Cours privé",
        niveau:          c.niveau             || "-",
        centre_id:       c.centre_id          || null,
        centre_nom:      c.centre_nom         || null,
        statut:          c.statut             || "actif",
        statut_paiement: c.statut_paiement    || null,
        mode_paiement:   c.mode_paiement      || null,
        coach:           c.coach_nom          || "-",
        commercial:      c.commercial_nom     || "-",
        date_debut:      c.date_debut         || null,
        date_fin:        c.date_fin           || null,
        created_at:      c.created_at         || c.date_debut,
        prix_heure:      c.prix_heure         || null,
        nb_seances:      c.nb_seances         || null,
        _source:         "cours_prive",
      }));

      setApprenants([...fromAssignations, ...fromPrives]);
    } catch(e) { console.error("Erreur chargement apprenants", e); }
    finally { setLoadingApprenants(false); }
  };
  useEffect(() => { if (activeTab === "suivi_apprenants") fetchApprenants(); }, [activeTab]);

  const fetchOffresData = async () => {
    setOffresLoading(true);
    try {
      const [resAssign, resCat] = await Promise.all([
        fetch(`${API_URL}/api/parcours/assignations`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/admin/catalogue-offres`, { headers: authHeaders() }),
      ]);
      const dA = resAssign.ok ? await resAssign.json() : { assignations: [] };
      const dC = resCat.ok    ? await resCat.json()    : { offres: [] };
      setOffresAssignations(dA.assignations || []);
      setOffresCatalogue(dC.offres || []);
    } catch(e) { console.error("Erreur offres", e); }
    finally { setOffresLoading(false); }
  };
  useEffect(() => { if (activeTab === "offres") fetchOffresData(); }, [activeTab]); // eslint-disable-line

  const fetchClientsAssignations = async () => {
    setClientsLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/parcours/assignations`, { headers: authHeaders() });
      if (r.ok) { const d = await r.json(); setClientsAssignations(d.assignations || []); }
    } catch(e) { console.error("Erreur chargement clients:", e); }
    finally { setClientsLoading(false); }
  };
  useEffect(() => { if (activeTab === "clients" || activeTab === "overview") fetchClientsAssignations(); }, [activeTab]);

  // États pour les onglets Coachs & Apprenants
  const [selectedCoach,     setSelectedCoach]     = useState(null);
  const [coachModalTab,     setCoachModalTab]     = useState("infos");
  const [coachContrats,     setCoachContrats]     = useState([]);
  const [coachContratsLoading, setCoachContratsLoading] = useState(false);
  const [coachGroupes,      setCoachGroupes]      = useState([]);
  const [coachGroupesLoading, setCoachGroupesLoading] = useState(false);
  const [coachEvals,        setCoachEvals]        = useState([]);
  const [coachEvalsLoading, setCoachEvalsLoading] = useState(false);
  const [coachDispoData,    setCoachDispoData]    = useState([]);
  const [coachDispoLoading, setCoachDispoLoading] = useState(false);
  const [coachEvalExpanded, setCoachEvalExpanded] = useState(new Set());
  const [coachEvalPeriod,   setCoachEvalPeriod]   = useState("toutes");
  const [coachContratDateFrom, setCoachContratDateFrom] = useState("");
  const [coachContratDateTo,   setCoachContratDateTo]   = useState("");
  const [coachContratPage,     setCoachContratPage]     = useState(0);
  const [coachContratExpanded, setCoachContratExpanded] = useState(new Set());
  const [coachSessionDateFrom,  setCoachSessionDateFrom]  = useState("");
  const [coachSessionDateTo,    setCoachSessionDateTo]    = useState("");
  const [coachSessionPage,      setCoachSessionPage]      = useState(0);
  const [coachSessionExpanded,  setCoachSessionExpanded]  = useState(new Set());
  const [coachContratEditOpen,  setCoachContratEditOpen]  = useState(false);
  const [savingCoachContratType, setSavingCoachContratType] = useState(false); // empêche le double-clic enregistrer type de contrat
  const [coachContratTypeEdit,  setCoachContratTypeEdit]  = useState(null);
  const [coachGroupeDateFrom,   setCoachGroupeDateFrom]   = useState("");
  const [coachGroupeDateTo,     setCoachGroupeDateTo]     = useState("");
  const [coachGroupePage,       setCoachGroupePage]       = useState(0);
  const [coachGroupeDetail,          setCoachGroupeDetail]          = useState(null);
  const [coachGroupeDetailLoading,   setCoachGroupeDetailLoading]   = useState(false);
  const [coachGroupeModal,      setCoachGroupeModal]      = useState(null);
  const [coachGroupeModalDetail,setCoachGroupeModalDetail]= useState({ apprenants:[], fichiers:[] });
  const [coachGroupeModalSubTab,setCoachGroupeModalSubTab]= useState("apprenants");
  const [coachGroupeModalCours, setCoachGroupeModalCours] = useState([]);
  const [coachGroupeModalCoursLoading, setCoachGroupeModalCoursLoading] = useState(false);
  const [coachGroupeModalPresences, setCoachGroupeModalPresences] = useState([]);
  const [coachGroupeModalCoursFiltreMois, setCoachGroupeModalCoursFiltreMois] = useState(() => new Date().getMonth()+1);
  const [coachGroupeModalCoursAnnee,     setCoachGroupeModalCoursAnnee]      = useState(() => new Date().getFullYear());
  const [showContratForm,   setShowContratForm]   = useState(false);
  const [contratEditId,     setContratEditId]     = useState(null);
  const [contratSaving,     setContratSaving]     = useState(false);
  const [contratForm,       setContratForm]       = useState({
    apprenant_nom:"", apprenant_prenom:"", apprenant_email:"", apprenant_telephone:"",
    type_contrat:"en_ligne", niveau:"B1", objectif:"", prix_h:"",
    nb_seances_total:"", duree_seance_h:"1.5", date_debut:"", date_fin:"", note:"",
  });
  const [selectedApprenant, setSelectedApprenant] = useState(null);
  const [saApprenantTab,      setSaApprenantTab]      = useState("profil");
  const [saAccesInfo,         setSaAccesInfo]         = useState(null);
  const [saAccesLoad,         setSaAccesLoad]         = useState(false);
  const [saSeancesPrivees,    setSaSeancesPrivees]    = useState([]);
  const [saPresencesPrivees,  setSaPresencesPrivees]  = useState([]);
  const [saPaiementsPrivees,  setSaPaiementsPrivees]  = useState([]);
  const [saLoadingSeances,    setSaLoadingSeances]    = useState(false);
  const [saLoadingPaiements,  setSaLoadingPaiements]  = useState(false);
  const [traficStats,       setTraficStats]       = useState(null);
  const [traficLoading,     setTraficLoading]     = useState(false);
  const [traficPeriode,     setTraficPeriode]     = useState("30j");
  const [coachSubTab,       setCoachSubTab]       = useState("liste");
  const [adminCoachsReel,        setAdminCoachsReel]        = useState([]);
  const [adminCoachsReelLoading, setAdminCoachsReelLoading] = useState(false);
  const [adminCoachSearch,       setAdminCoachSearch]       = useState("");
  const [apprenantFiltre,   setApprenantFiltre]   = useState({ type_cours:"Tous", centre:"Tous", statut:"Tous", statut_paiement:"Tous", periode:"Tous", search:"" });
  const [apprenants,        setApprenants]        = useState([]);
  const [loadingApprenants, setLoadingApprenants] = useState(false);

  // ── Onglet Offres & Formations ──────────────────────────────────────────────
  const [offresCatalogue,        setOffresCatalogue]        = useState([]);
  const [offresAssignations,     setOffresAssignations]     = useState([]);
  const [offresLoading,          setOffresLoading]          = useState(false);
  const [offresCatFiltre,        setOffresCatFiltre]        = useState("tous");
  const [offresSearchVal,        setOffresSearchVal]        = useState("");
  const [offresPeriode,          setOffresPeriode]          = useState("tout");
  const [offresAnnee,            setOffresAnnee]            = useState(new Date().getFullYear());
  const [clientsAssignations, setClientsAssignations] = useState([]);
  const [clientsLoading,      setClientsLoading]      = useState(false);
  const [clientsFiltreStatut, setClientsFiltreStatut] = useState("tous");
  const [clientsFiltreAssist, setClientsFiltreAssist] = useState("tous");
  const [clientsSearch,       setClientsSearch]       = useState("");
  const [clientsDateDebut,    setClientsDateDebut]    = useState("");
  const [clientsDateFin,      setClientsDateFin]      = useState("");
  const [clientsPage,         setClientsPage]         = useState(1);
  const CLIENTS_PER_PAGE = 20;
  const [apprenantPage,     setApprenantPage]     = useState(1);
  const APPRENANTS_PER_PAGE = 20;

  // États Paiements
  const [paiements,        setPaiements]        = useState([]);
  const [loadingPaiements, setLoadingPaiements] = useState(false);
  const [apprenantSubTab,  setApprenantSubTab]  = useState("liste");

  // Répartition réelle par niveau CECRL (onglet Apprenants > Progression) - comptage des
  // apprenants actifs par niveau, à partir de groupes_apprenants (aucune "note moyenne" ou
  // "assiduité" n'existe réellement dans le schéma dash, contrairement à l'ancien mock).
  const [niveauxApprenants, setNiveauxApprenants] = useState(null); // null = pas encore chargé
  const fetchNiveauxApprenants = async () => {
    try {
      const r = await fetch(`${API_URL}/api/groupes/apprenants`, { headers: authHeaders() });
      const d = r.ok ? await r.json() : { apprenants: [] };
      const counts = {};
      (d.apprenants || []).forEach(a => { const n = a.niveau || "-"; counts[n] = (counts[n]||0) + 1; });
      setNiveauxApprenants(counts);
    } catch { setNiveauxApprenants({}); }
  };
  useEffect(() => { if (activeTab === "suivi_apprenants" && apprenantSubTab === "progression") fetchNiveauxApprenants(); }, [activeTab, apprenantSubTab]);
  const [paiementPage,     setPaiementPage]     = useState(1);
  const [paiementFiltre,   setPaiementFiltre]   = useState({ statut:"Tous", mode:"Tous", centre:"Tous", search:"", dateDebut:"", dateFin:"" });
  const [paiementModal,       setPaiementModal]       = useState(null);
  const [paiementModalHisto,  setPaiementModalHisto]  = useState([]); // versements liés à la même assignation
  const [paiementModalLoading,setPaiementModalLoading] = useState(false);
  const [caPeriode,        setCaPeriode]        = useState("annee"); // "semaine"|"mois"|"trimestre"|"annee"|"tout"
  const [caAnnee,          setCaAnnee]          = useState(new Date().getFullYear());
  const PAIEMENTS_PER_PAGE = 25;

  const fetchPaiements = async () => {
    setLoadingPaiements(true);
    try {
      const res  = await fetch(`${API_URL}/api/paiements/all`, { headers: authHeaders() });
      const data = await res.json();
      setPaiements(data.paiements || []);
    } catch(e) { console.error("Erreur chargement paiements", e); }
    finally { setLoadingPaiements(false); }
  };
  useEffect(() => { if (activeTab === "paiements" || activeTab === "ca" || activeTab === "overview") fetchPaiements(); }, [activeTab]);

  /* ── Graphiques réels du Tableau de bord (onglet "overview") ── */
  const caEvolutionSA = useMemo(() => {
    const mois = derniersMoisSA(12);
    const parMois = {};
    mois.forEach(m => { parMois[m.key] = 0; });
    paiements.forEach(p => {
      const key = (p.date_paiement || "").slice(0,7);
      if (key in parMois) parMois[key] += Number(p.montant_recu || 0);
    });
    return {
      labels: mois.map(m => m.label),
      datasets: [{
        label: "CA encaissé",
        data: mois.map(m => parMois[m.key]),
        borderColor: BET_COLOR, backgroundColor: BET_COLOR + "22",
        fill: true, tension: 0.35, pointRadius: 3, pointBackgroundColor: BET_COLOR,
      }],
    };
  }, [paiements]);

  const rolesRepartitionSA = useMemo(() => {
    const entries = Object.values(ROLES_DEF)
      .map(r => ({ label: `${r.emoji} ${r.label}`, count: users.filter(u => u.role === r.id).length, color: r.color }))
      .filter(r => r.count > 0);
    return {
      labels: entries.map(e => e.label),
      datasets: [{ data: entries.map(e => e.count), backgroundColor: entries.map((e,i) => e.color || CHART_COLORS_SA[i % CHART_COLORS_SA.length]), borderWidth: 0 }],
    };
  }, [users]);

  const clientsFunnelSA = useMemo(() => {
    if (!clientsAssignations.length) return { labels: [], datasets: [] };
    const enAttente = clientsAssignations.filter(a => a.statut === "en_attente").length;
    const enCours   = clientsAssignations.filter(a => a.statut === "en_cours").length;
    const converti  = clientsAssignations.filter(a => a.statut === "converti").length;
    const perdu     = clientsAssignations.filter(a => a.statut === "perdu").length;
    return {
      labels: ["En attente","En cours","Convertis","Perdus"],
      datasets: [{ data: [enAttente, enCours, converti, perdu], backgroundColor: ["#f87171","#a78bfa","#34d399","#94a3b8"], borderRadius: 4 }],
    };
  }, [clientsAssignations]);

  // ── Analytics / Trafic web ──────────────────────────────────────────────
  const fetchTraficStats = async (periode = traficPeriode) => {
    setTraficLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/analytics/stats?periode=${periode}`, { headers: authHeaders() });
      if (r.ok) setTraficStats(await r.json());
    } catch(e) { console.error("Erreur trafic stats", e); }
    finally { setTraficLoading(false); }
  };
  useEffect(() => { if (activeTab === "trafic") fetchTraficStats(traficPeriode); }, [activeTab, traficPeriode]);

  // ── Paiements TouchPoint (en ligne) ─────────────────────────────────────
  const [paiementsSubTab,         setPaiementsSubTab]         = useState("manuel");
  const [touchpointPaiements,     setTouchpointPaiements]     = useState([]);
  const [touchpointLoading,       setTouchpointLoading]       = useState(false);
  const [touchpointSearch,        setTouchpointSearch]        = useState("");
  const [touchpointStatutFiltre,  setTouchpointStatutFiltre]  = useState("tous");
  const [touchpointDetailModal,  setTouchpointDetailModal]   = useState(null);
  const fetchTouchpointPaiements = async () => {
    setTouchpointLoading(true);
    try {
      const res  = await fetch(`${API_URL}/api/touchpoint/admin/all`, { headers: authHeaders() });
      const data = await res.json();
      setTouchpointPaiements(data.paiements || []);
    } catch(e) { console.error("Erreur chargement touchpoint", e); }
    finally { setTouchpointLoading(false); }
  };

  useEffect(() => {
    if (activeTab === "paiements" && paiementsSubTab === "cinetpay") {
      fetchTouchpointPaiements();
    }
  }, [activeTab, paiementsSubTab]); // eslint-disable-line

  // ── Audit global ─────────────────────────────────────────────────────────
  const [auditLogs,         setAuditLogs]         = useState([]);
  const [auditStats,        setAuditStats]        = useState(null);
  const [purgingLogs,       setPurgingLogs]        = useState(false); // empêche le double-clic purge des logs
  const [forcing2FA,        setForcing2FA]         = useState(false); // empêche le double-clic forcer 2FA en masse
  const [auditLoading,      setAuditLoading]      = useState(false);
  const [auditTotal,        setAuditTotal]        = useState(0);
  const [auditPage,         setAuditPage]         = useState(1);
  const [auditView,         setAuditView]         = useState("logs"); // "logs" | "stats" | "profil" | "centre"
  const [auditSelectedProfile, setAuditSelectedProfile] = useState(null);
  const [auditSelectedCentre, setAuditSelectedCentre]  = useState(null);
  const [auditProfileLogs, setAuditProfileLogs]  = useState([]);
  const [auditCentreLogs,  setAuditCentreLogs]   = useState([]);
  const [auditDetailLog,   setAuditDetailLog]    = useState(null); // log sélectionné pour le modal
  const AUDIT_LIMIT = 50;
  const [auditFilters, setAuditFilters] = useState({
    module: "", action_type: "", statut: "", centre: "", search: "",
    date_debut: "", date_fin: "",
  });

  const fetchAuditLogs = useCallback(async (page = 1, filters = auditFilters) => {
    setAuditLoading(true);
    try {
      const params = new URLSearchParams({
        page, limit: AUDIT_LIMIT, plateforme: 'dash',
        ...(filters.module     && { module: filters.module }),
        ...(filters.action_type && { action_type: filters.action_type }),
        ...(filters.statut     && { statut: filters.statut }),
        ...(filters.centre     && { centre: filters.centre }),
        ...(filters.search     && { search: filters.search }),
        ...(filters.date_debut && { date_debut: filters.date_debut }),
        ...(filters.date_fin   && { date_fin: filters.date_fin }),
      });
      const res = await fetch(`${API_URL}/api/audit/logs?${params}`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setAuditLogs(data.logs || []);
      setAuditTotal(data.total || 0);
    } catch { toast.error("Erreur chargement audit"); }
    finally { setAuditLoading(false); }
  }, [auditFilters]); // eslint-disable-line

  const fetchAuditStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/audit/stats?plateforme=dash`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      setAuditStats(await res.json());
    } catch {}
  }, []); // eslint-disable-line

  const fetchAuditByProfile = async (acteur_id) => {
    try {
      const res = await fetch(`${API_URL}/api/audit/by-profile/${acteur_id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      const d = await res.json(); setAuditProfileLogs(Array.isArray(d) ? d : []);
    } catch { toast.error("Erreur"); }
  };

  const fetchAuditByCentre = async (centre) => {
    try {
      const res = await fetch(`${API_URL}/api/audit/by-centre/${centre}`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      const d = await res.json(); setAuditCentreLogs(Array.isArray(d) ? d : []);
    } catch { toast.error("Erreur"); }
  };

  useEffect(() => {
    if (activeTab === "audit") { fetchAuditLogs(1); fetchAuditStats(); }
  }, [activeTab]); // eslint-disable-line

  // ── Groupes admin ────────────────────────────────────────────────────────
  const [adminGroupes, setAdminGroupes]         = useState([]);
  const [adminGroupesLoading, setAdminGroupesLoading] = useState(false);
  const [adminSelectedGroupe, setAdminSelectedGroupe] = useState(null);
  const [adminGroupeDetail, setAdminGroupeDetail]     = useState({ apprenants:[], fichiers:[] });
  const [adminCoursListe, setAdminCoursListe]   = useState([]);
  const [adminCoursLoading, setAdminCoursLoading] = useState(false);
  const [adminPresences, setAdminPresences]     = useState([]);
  const [adminGroupeSubTab, setAdminGroupeSubTab] = useState("apprenants");
  const [adminCoursFiltreMois, setAdminCoursFiltreMois] = useState(() => new Date().getMonth()+1);
  const [adminCoursAnnee, setAdminCoursAnnee]   = useState(() => new Date().getFullYear());
  const [adminFiltreCoach, setAdminFiltreCoach] = useState("tous");
  const [adminFiltreTypeGroupe, setAdminFiltreTypeGroupe] = useState("tous");
  const [adminGroupeSearch, setAdminGroupeSearch] = useState("");
  const [adminGroupePage, setAdminGroupePage] = useState(0);
  const GROUPES_PER_PAGE = 15;
  const [adminGroupeApprenantDetail, setAdminGroupeApprenantDetail] = useState(null);
  const [adminGroupeApprenantTab, setAdminGroupeApprenantTab] = useState("informations");
  const [adminApprenantAcces, setAdminApprenantAcces]         = useState(null);
  const [adminApprenantAccesLoad, setAdminApprenantAccesLoad] = useState(false);
  const [adminBulkLoad, setAdminBulkLoad] = useState(false);

  // États onglet Cours (sous-onglets Cours privés + Groupes cours)
  const [coursSubTab,           setCoursSubTab]           = useState("groupes");
  const [adminAllContrats,      setAdminAllContrats]      = useState([]);
  const [adminAllContratsLoad,  setAdminAllContratsLoad]  = useState(false);
  const [adminContratFiltre,    setAdminContratFiltre]    = useState({ coach:"tous", statut:"tous", type:"tous", search:"" });
  const [adminContratPage,      setAdminContratPage]      = useState(1);
  const CONTRATS_PER_PAGE = 20;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (coachModalTab === "dispos" && selectedCoach) fetchCoachDispos(selectedCoach.id);
  }, [coachModalTab, selectedCoach]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab !== "cours") return;
    const token = localStorage.getItem("admin_token");
    setAdminGroupesLoading(true);
    fetch(`${API_URL}/api/groupes`, { headers:{ Authorization:`Bearer ${token}` } })
      .then(r=>r.ok?r.json():{groupes:[]})
      .then(d=>setAdminGroupes(d.groupes||[]))
      .catch(()=>{})
      .finally(()=>setAdminGroupesLoading(false));
  }, [activeTab]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab !== "cours" || coursSubTab !== "cours_prives") return;
    const token = localStorage.getItem("admin_token");
    setAdminAllContratsLoad(true);
    fetch(`${API_URL}/api/parcours/assignations`, { headers:{ Authorization:`Bearer ${token}` } })
      .then(r=>r.ok?r.json():{assignations:[]})
      .then(d=>{
        const privees = (d.assignations||[]).filter(a=>
          a.type_coaching === "prive" || a.type_cours === "domicile" || a.type_cours === "cours_prive" || a.type_cours === "prive"
        );
        setAdminAllContrats(privees);
      })
      .catch(()=>{})
      .finally(()=>setAdminAllContratsLoad(false));
  }, [activeTab, coursSubTab]);

  const fetchAdminGroupeDetail = async (groupe) => {
    const token = localStorage.getItem("admin_token");
    setAdminSelectedGroupe(groupe);
    setAdminGroupeSubTab("apprenants");
    setAdminCoursListe([]); setAdminPresences([]);
    try {
      const r = await fetch(`${API_URL}/api/groupes/${groupe.id}`, { headers:{ Authorization:`Bearer ${token}` } });
      const d = await r.json();
      // Mettre à jour le groupe sélectionné avec les infos enrichies (coach, centre…)
      if (d.groupe) setAdminSelectedGroupe(prev => ({ ...prev, ...d.groupe }));
      setAdminGroupeDetail({ apprenants: d.apprenants||[], fichiers: d.fichiers||[] });
    } catch {}
  };

  const fetchAdminCours = async (groupeId, mois, annee) => {
    const token = localStorage.getItem("admin_token");
    setAdminCoursLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/groupes/${groupeId}/cours?mois=${mois}&annee=${annee}`, { headers:{ Authorization:`Bearer ${token}` } });
      const d = await r.json();
      setAdminCoursListe(d.cours||[]);
    } catch {} finally { setAdminCoursLoading(false); }
  };

  const fetchAdminPresences = async (groupeId) => {
    const token = localStorage.getItem("admin_token");
    try {
      const r = await fetch(`${API_URL}/api/groupes/${groupeId}/presences`, { headers:{ Authorization:`Bearer ${token}` } });
      const d = await r.json();
      setAdminPresences(d.presences||[]);
    } catch {}
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!adminSelectedGroupe) return;
    if (adminGroupeSubTab === "cours") fetchAdminCours(adminSelectedGroupe.id, adminCoursFiltreMois, adminCoursAnnee);
    if (adminGroupeSubTab === "presences") fetchAdminPresences(adminSelectedGroupe.id);
  }, [adminGroupeSubTab, adminSelectedGroupe, adminCoursFiltreMois, adminCoursAnnee]);

  // Onglets principaux (élargis)
  const { notifications: toutesNotifsAD, nbNonLues: notifNonLuesAD, marquerLue: marquerNotifLueAD } = useNotifications(profilConnecte?.id);
  const [banderoleFermeeAD, setBanderoleFermeeAD] = useState(false);
  const notifsBanderoleAD = toutesNotifsAD.filter(n => !n.lu).slice(0, 3);

  // ── "Mes tâches" - agrège tous les compteurs "à traiter" déjà calculés pour
  // chaque onglet, pour que l'admin ait un point d'entrée unique au lieu de
  // devoir parcourir chaque onglet pour savoir ce qui reste à faire.
  const tachesBoutique    = commandes.filter(c=>c.statut==="en_attente").length;
  const tachesAssistantes = assistantesAdmin.filter(a=>!a.actif).length;
  const tachesPaiements   = touchpointPaiements.filter(p=>p.statut==="en_attente").length;
  const tachesAudit       = auditStats?.alertes_today || 0;
  const tachesContenu     = blogArticles.filter(a=>!a.publie).length + temosPending;
  const tachesAdministration = demandes.filter(d=>d.statut==="en_attente").length;
  const tachesTotal = tachesBoutique + tachesAssistantes + tachesPaiements + tachesAudit + tachesContenu + tachesAdministration;

  const tabs = [
    { key: "taches",        label: "Mes tâches",     icon: "✅", badge: tachesTotal||null, danger: tachesTotal>0 },
    { key: "overview",      label: "Vue d'ensemble", icon: "🏠" },
    { key: "platform",      label: "Plateforme",     icon: "🏢" },
    /* Remonté en premier niveau le 27/10/2026 : le panneau existait déjà, mais sous
       Plateforme → Offres & Contenus → BET for Business — trois niveaux, introuvable. */
    { key: "bet_business",  label: "BET for Business", icon: "🏢" },
    { key: "trafic",        label: "Trafic web",             icon: "🌐" },
    { key: "clients",       label: "Clients & Prospects",    icon: "👥" },
    { key: "suivi_apprenants", label: "Apprenants", icon: "🎓" },
    // { key: "archives",         label: "Archives",   icon: "📦" },
    { key: "coachs",        label: "Professeurs",            icon: "👨‍🏫" },
    { key: "cours",         label: "Groupes",                icon: "👥" },
    { key: "offres",        label: "Offres & Formations",    icon: "🎓" },
    { key: "assistantes",   label: "Planning assistantes",   icon: "📅", badge: assistantesAdmin.filter(a=>!a.actif).length||null, danger: assistantesAdmin.filter(a=>!a.actif).length>0 },
    { key: "sondages",      label: "Sondages",               icon: "🎯" },
    { key: "ca",            label: "Chiffre d'affaires",     icon: "💰" },
    { key: "paiements",     label: "Paiements",              icon: "💳", badge: touchpointPaiements.filter(p=>p.statut==="en_attente").length||null, danger: false },
     { key: "administration", label: "Administration BET",     icon: "🏛️" },
    { key: "audit",         label: "Audit global",   icon: "📜", badge: auditStats?.alertes_today||null, danger: (auditStats?.alertes_today||0)>0 },
    { key: "messages",      label: "Messages",               icon: "💬",  badge: msgNonLuTotal||null, danger: msgNonLuTotal>0 },
    { key: "notifications",  label: "Notifications",          icon: "🔔", badge: notifNonLuesAD||null, danger: notifNonLuesAD>0 },

  ];


  const [platformSubTab, setPlatformSubTab] = useState("partenaires");

  // ── Newsletter ───────────────────────────────────────────
  const [nlSubscribers,  setNlSubscribers]  = useState([]);
  const [nlLoading,      setNlLoading]      = useState(false);
  const [nlSearch,       setNlSearch]       = useState("");
  const [nlCampaignOpen, setNlCampaignOpen] = useState(false);
  const [nlSending,      setNlSending]      = useState(false);
  const [nlCampaign,     setNlCampaign]     = useState({ sujet:"", contenu:"" });
  // Historique des campagnes (persistées côté serveur) - liste paginée + filtres + détail/stats.
  const [nlCampagnes,        setNlCampagnes]        = useState([]);
  const [nlCampagnesLoading, setNlCampagnesLoading] = useState(false);
  const [nlPage,             setNlPage]             = useState(1);
  const [nlTotalPages,       setNlTotalPages]       = useState(1);
  const [nlTotalCampagnes,   setNlTotalCampagnes]   = useState(0);
  const [nlFiltreDateDebut,  setNlFiltreDateDebut]  = useState("");
  const [nlFiltreDateFin,    setNlFiltreDateFin]    = useState("");
  const [nlFiltreStatut,     setNlFiltreStatut]     = useState("");
  const [nlKpis,             setNlKpis]             = useState(null); // { total, brouillon, programmee, envoyee, annulee, total_envoyes, total_echecs, total_destinataires }
  const [nlDetail,           setNlDetail]           = useState(null); // { campagne, stats }
  const [nlDetailLoading,    setNlDetailLoading]    = useState(false);
  const [nlEditingId,        setNlEditingId]        = useState(null); // id en cours de modification, sinon null = création
  const [nlMode,             setNlMode]             = useState("send"); // "send" | "schedule" | "draft"
  const [nlDateProgrammee,   setNlDateProgrammee]   = useState("");    // datetime-local
  // Destinataires : abonnés newsletter (défaut) | segment coaching groupe/privé | un apprenant précis.
  const [nlDestMode,       setNlDestMode]       = useState("newsletter"); // "newsletter" | "segment" | "apprenant"
  const [nlDestType,       setNlDestType]       = useState("groupe");     // pour mode "segment"
  const [nlApprenants,     setNlApprenants]     = useState([]);
  const [nlApprenantsLoading, setNlApprenantsLoading] = useState(false);
  const [nlApprenantSearch,   setNlApprenantSearch]   = useState("");
  const [nlApprenantChoisi,   setNlApprenantChoisi]   = useState(null); // { id, nom, email }
  const [nlSegmentCounts,     setNlSegmentCounts]     = useState({ groupe: 0, prive: 0 });
  // Pièce jointe - upload Cloudinary via /api/upload/document, puis URL passée à Brevo.
  const [nlAttachment,     setNlAttachment]     = useState(null); // { url, nom }
  const [nlAttachUploading, setNlAttachUploading] = useState(false);

  const fetchNlSubscribers = async () => {
    setNlLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/newsletter/subscribers`, { headers: authHeaders() });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setNlSubscribers(d.subscribers || []);
    } catch { toast.error("Erreur chargement abonnés"); }
    finally { setNlLoading(false); }
  };

  const fetchNlApprenants = async (typeCoaching) => {
    setNlApprenantsLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/newsletter/apprenants${typeCoaching ? `?type_coaching=${typeCoaching}` : ""}`, { headers: authHeaders() });
      if (!r.ok) throw new Error();
      const d = await r.json();
      return d.apprenants || [];
    } catch { toast.error("Erreur chargement apprenants"); return []; }
    finally { setNlApprenantsLoading(false); }
  };

  // Charge les compteurs des 2 segments dès l'ouverture du modal, et la liste complète des
  // apprenants (pour la recherche) dès que le mode "apprenant" est choisi.
  useEffect(() => {
    if (!nlCampaignOpen) return;
    (async () => {
      const [g, p] = await Promise.all([fetchNlApprenants("groupe"), fetchNlApprenants("prive")]);
      setNlSegmentCounts({ groupe: g.length, prive: p.length });
      if (nlDestMode === "apprenant") setNlApprenants([...g, ...p]);
    })();
  }, [nlCampaignOpen]); // eslint-disable-line

  useEffect(() => {
    if (!nlCampaignOpen || nlDestMode !== "apprenant" || nlApprenants.length) return;
    (async () => {
      const [g, p] = await Promise.all([fetchNlApprenants("groupe"), fetchNlApprenants("prive")]);
      setNlApprenants([...g, ...p]);
    })();
  }, [nlDestMode, nlCampaignOpen]); // eslint-disable-line

  // En édition, l'apprenant choisi n'est connu que par son id (assignation_id) tant que la
  // liste complète n'est pas chargée - on complète nom/email dès qu'elle arrive.
  useEffect(() => {
    if (nlApprenantChoisi && !nlApprenantChoisi.nom && nlApprenants.length) {
      const found = nlApprenants.find(a => a.id === nlApprenantChoisi.id);
      if (found) setNlApprenantChoisi(found);
    }
  }, [nlApprenants]); // eslint-disable-line

  const fetchNlCampagnes = async (page = 1) => {
    setNlCampagnesLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 10 });
      if (nlFiltreDateDebut) params.set("date_debut", nlFiltreDateDebut);
      if (nlFiltreDateFin)   params.set("date_fin", nlFiltreDateFin);
      if (nlFiltreStatut)    params.set("statut", nlFiltreStatut);
      const r = await fetch(`${API_URL}/api/newsletter/campaigns?${params}`, { headers: authHeaders() });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setNlCampagnes(d.campagnes || []);
      setNlTotalPages(d.total_pages || 1);
      setNlTotalCampagnes(d.total || 0);
      setNlPage(d.page || page);
      setNlKpis(d.kpis || null);
    } catch { toast.error("Erreur chargement des campagnes"); }
    finally { setNlCampagnesLoading(false); }
  };

  useEffect(() => {
    if (activeTab === "platform" && platformSubTab === "newsletter") fetchNlCampagnes(1);
  }, [activeTab, platformSubTab, nlFiltreDateDebut, nlFiltreDateFin, nlFiltreStatut]); // eslint-disable-line

  const nlOuvrirDetail = async (id) => {
    setNlDetailLoading(true);
    setNlDetail({ campagne: null, stats: null });
    try {
      const r = await fetch(`${API_URL}/api/newsletter/campaigns/${id}`, { headers: authHeaders() });
      if (!r.ok) throw new Error();
      setNlDetail(await r.json());
    } catch { toast.error("Erreur chargement du détail"); setNlDetail(null); }
    finally { setNlDetailLoading(false); }
  };

  const nlAnnulerCampagne = async (id) => {
    if (!(await confirm("Annuler cette campagne ?", { titre: "Annuler la campagne" }))) return;
    try {
      const r = await fetch(`${API_URL}/api/newsletter/campaigns/${id}/annuler`, { method:"POST", headers: authHeaders() });
      if (!r.ok) throw new Error();
      toast.success("Campagne annulée");
      setNlDetail(null);
      fetchNlCampagnes(nlPage);
    } catch { toast.error("Erreur lors de l'annulation"); }
  };

  const nlEnvoyerMaintenant = async (id) => {
    if (!(await confirm("Envoyer cette campagne maintenant ?", { titre: "Envoyer la campagne" }))) return;
    try {
      const r = await fetch(`${API_URL}/api/newsletter/campaigns/${id}/envoyer`, { method:"POST", headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Erreur");
      toast.success(`Campagne envoyée à ${d.sent} destinataire(s)`);
      setNlDetail(null);
      fetchNlCampagnes(nlPage);
    } catch (e) { toast.error(e.message || "Erreur lors de l'envoi"); }
  };

  const nlOuvrirModif = (c) => {
    setNlEditingId(c.id);
    setNlCampaign({ sujet: c.sujet, contenu: c.contenu });
    setNlAttachment(c.piece_jointe_url ? { url: c.piece_jointe_url, nom: c.piece_jointe_nom || "pièce jointe" } : null);
    const dest = c.destinataires || { mode: "newsletter" };
    setNlDestMode(dest.mode || "newsletter");
    setNlDestType(dest.type_coaching || "groupe");
    setNlApprenantChoisi(dest.mode === "apprenant" && dest.assignation_id ? { id: dest.assignation_id, nom: "", email: "" } : null);
    setNlMode(c.date_programmee ? "schedule" : "draft");
    setNlDateProgrammee(c.date_programmee ? c.date_programmee.slice(0, 16) : "");
    setNlCampaignOpen(true);
  };

  const nlFermerModal = () => {
    setNlCampaignOpen(false);
    setNlCampaign({ sujet:"", contenu:"" });
    setNlAttachment(null);
    setNlDestMode("newsletter");
    setNlApprenantChoisi(null);
    setNlEditingId(null);
    setNlMode("send");
    setNlDateProgrammee("");
  };

  const nlDelete = async (id) => {
    if (!(await confirm("Supprimer cet abonné ?", { titre: "Supprimer l'abonné" }))) return;
    try {
      const r = await fetch(`${API_URL}/api/newsletter/${id}`, { method:"DELETE", headers: authHeaders() });
      if (!r.ok) throw new Error();
      setNlSubscribers(p => p.filter(s => s.id !== id));
      toast.success("Abonné supprimé");
    } catch { toast.error("Erreur suppression"); }
  };

  // Nombre de destinataires réels selon le mode choisi - utilisé pour le libellé du bouton
  // et le message de confirmation, cohérent avec ce que /send calculera réellement.
  const nlDestinatairesCount = () => {
    if (nlDestMode === "segment") return nlSegmentCounts[nlDestType] || 0;
    if (nlDestMode === "apprenant") return nlApprenantChoisi ? 1 : 0;
    return nlSubscribers.length;
  };
  const nlDestinatairesPayload = () => {
    if (nlDestMode === "segment") return { mode: "segment", type_coaching: nlDestType };
    if (nlDestMode === "apprenant") return { mode: "apprenant", assignation_id: nlApprenantChoisi?.id };
    return { mode: "newsletter" };
  };

  const nlUploadAttachment = async (file) => {
    const MAX_NL_ATTACH = 5 * 1024 * 1024;
    if (file.size > MAX_NL_ATTACH) {
      return toast.error(`Fichier trop volumineux (${(file.size/1024/1024).toFixed(1)} Mo) - 5 Mo maximum pour une pièce jointe de campagne.`);
    }
    setNlAttachUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const endpoint = file.type?.startsWith("image/") ? "/api/upload/image" : "/api/upload/document";
      const r = await fetchUpload(`${API_URL}${endpoint}`, { method:"POST", headers:{ Authorization:`Bearer ${localStorage.getItem("admin_token")}` }, body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Erreur upload");
      setNlAttachment({ url: d.file?.url, nom: file.name });
      toast.success("Pièce jointe ajoutée");
    } catch (e) { toast.error(e.message || "Erreur upload"); }
    finally { setNlAttachUploading(false); }
  };

  const nlValiderCampagne = async () => {
    if (nlSending) return;
    if (!nlCampaign.sujet.trim() || !nlCampaign.contenu.trim()) {
      return toast.error("Sujet et contenu requis");
    }
    if (nlDestMode === "apprenant" && !nlApprenantChoisi) {
      return toast.error("Choisissez un apprenant");
    }
    if (nlMode === "schedule" && !nlDateProgrammee) {
      return toast.error("Choisissez une date de programmation");
    }
    const nbDest = nlDestinatairesCount();
    const messageConfirm =
      nlMode === "send"    ? `Envoyer "${nlCampaign.sujet}" à ${nbDest} destinataire(s) ? Cette action est irréversible.`
      : nlMode === "schedule" ? `Programmer l'envoi de "${nlCampaign.sujet}" à ${nbDest} destinataire(s) le ${new Date(nlDateProgrammee).toLocaleString("fr-FR")} ?`
      : `Enregistrer "${nlCampaign.sujet}" en brouillon ?`;
    if (!(await confirm(messageConfirm, { titre: "Confirmer" }))) return;

    setNlSending(true);
    try {
      const dateISO = nlMode === "schedule" ? new Date(nlDateProgrammee).toISOString() : null;
      const payload = { ...nlCampaign, piece_jointe_url: nlAttachment?.url || null, piece_jointe_nom: nlAttachment?.nom || null, destinataires: nlDestinatairesPayload() };

      if (nlEditingId) {
        const r = await fetch(`${API_URL}/api/newsletter/campaigns/${nlEditingId}`, {
          method: "PATCH", headers: authHeaders(),
          body: JSON.stringify({ ...payload, date_programmee: nlMode === "send" ? undefined : dateISO }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Erreur");
        if (nlMode === "send") {
          const r2 = await fetch(`${API_URL}/api/newsletter/campaigns/${nlEditingId}/envoyer`, { method:"POST", headers: authHeaders() });
          const d2 = await r2.json();
          if (!r2.ok) throw new Error(d2.error || "Erreur d'envoi");
          toast.success(`Campagne envoyée à ${d2.sent} destinataire(s)`);
        } else {
          toast.success(nlMode === "schedule" ? "Campagne programmée" : "Brouillon mis à jour");
        }
      } else {
        const r = await fetch(`${API_URL}/api/newsletter/campaigns`, {
          method: "POST", headers: authHeaders(),
          body: JSON.stringify({ ...payload, action: nlMode, date_programmee: nlMode === "schedule" ? dateISO : undefined }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Erreur");
        if (nlMode === "send") toast.success(`Campagne envoyée à ${d.sent} destinataire(s)`);
        else toast.success(nlMode === "schedule" ? "Campagne programmée" : "Brouillon enregistré");
      }
      nlFermerModal();
      fetchNlCampagnes(1);
    } catch (e) { toast.error(e.message); }
    finally { setNlSending(false); }
  };

  useEffect(() => {
    if (activeTab === "platform" && platformSubTab === "newsletter") fetchNlSubscribers();
  }, [activeTab, platformSubTab]); // eslint-disable-line

  const [catalogueSubTab, setCatalogueSubTab] = useState("centres");
  const CATALOGUE_TABS = [
    { key:"centres",          label:"Nos centres",      icon:"📍" },
    { key:"offres_en_ligne",  label:"Offres En ligne",  icon:"💻" },
    { key:"offres_domicile",  label:"Offres À domicile",icon:"🏠" },
    { key:"certifications",   label:"Certifications",   icon:"🏆" },
    { key:"interpretariat",   label:"Interprétariat",   icon:"🌍" },
    { key:"traduction",       label:"Traduction",        icon:"📄" },
    { key:"catalogue_pdf",    label:"Catalogue PDF",    icon:"📥" },
    /* Retiré le 27/10/2026 : « BET for Business » est désormais un onglet de premier
       niveau. Deux chemins vers le même panneau, c'est une question de plus à se poser. */
  ];

  // ── Médias pages (offre_media) ──────────────────────────────────
  const OFFRE_MEDIA_TYPES = [
    { key:"en-ligne",     label:"Cours en ligne",    icon:"💻", color:"#1e3a8a" },
    { key:"cabinet",      label:"Cours en cabinet",  icon:"🏫", color:"#0891b2" },
    { key:"domicile",     label:"Cours à domicile",  icon:"🏠", color:"#059669" },
    { key:"toeic",        label:"TOEIC",             icon:"🏆", color:"#7c3aed" },
    { key:"toefl",        label:"TOEFL",             icon:"🎓", color:"#b45309" },
    { key:"ielts",        label:"IELTS",             icon:"🌍", color:"#dc2626" },
    { key:"temoignages",  label:"Témoignages",        icon:"💬", color:"#e93747" },
    { key:"blog",         label:"Blog / Newsletters", icon:"📰", color:"#0891b2" },
  ];
  /* LES CINQ CHAMPS DE CONTENU rejoignent le modèle vide : `saveOffreMedia` diffuse le
     formulaire entier, ils partent donc sans qu'on ait à toucher à l'envoi. Les panneaux des
     autres offres les laissent à vide et le serveur les enregistre en `null`.
     Colonnes posées par `sql/offre_media_contenu_slide.sql`. */
  const OFFRE_MEDIA_BLANK = {
    type:"video", url:"", titre:"", description:"", actif:true,
    surtitre:"", bouton1_libelle:"", bouton1_lien:"", bouton2_libelle:"", bouton2_lien:"",
  };
  const [offreMediaType,       setOffreMediaType]       = useState("toeic");
  const [offreMediaList,       setOffreMediaList]       = useState([]);
  const [offreMediaLoading,    setOffreMediaLoading]    = useState(false);
  const [offreMediaForm,       setOffreMediaForm]       = useState(OFFRE_MEDIA_BLANK);
  const [offreMediaEditingId,  setOffreMediaEditingId]  = useState(null);
  const [offreMediaSaving,     setOffreMediaSaving]     = useState(false);
  const [offreMediaUploading,  setOffreMediaUploading]  = useState(false);
  const [offreMediaUploadPct,  setOffreMediaUploadPct]  = useState(0);
  const [offreMediaDragOver,   setOffreMediaDragOver]   = useState(false);
  const offreMediaFileRef = React.useRef(null);
  const platformTabs = [
    { key:"partenaires",   label:"Config. Partenaires", icon:"🤝" },
    { key:"whatsapp",      label:"Contact & WhatsApp",  icon:"📞" },
    { key:"coachs_photos", label:"Équipe Coachs",       icon:"👨‍🏫" },
    { key:"marquee",       label:"Marquee",             icon:"📢" },
    { key:"catalogue",     label:"Offres & Contenus",   icon:"📦" },
    { key:"codes_promo",   label:"Code promo",          icon:"🎟️" },
    { key:"avis_offres",   label:"Avis offres",         icon:"💬", badge: avisOffres.filter(a=>!a.actif).length||null },
    { key:"boutique",      label:"Boutique",            icon:"🛍️", badge: commandes.filter(c=>c.statut==="en_attente").length||null, danger: commandes.filter(c=>c.statut==="en_attente").length>0 },
    { key:"contenu",       label:"Blog & Témoignages",  icon:"📋", badge: (blogArticles.filter(a=>!a.publie).length + temosPending)||null, danger: temosPending>0 },
    { key:"newsletter",    label:"Newsletter",           icon:"📩" },
    { key:"faq",                 label:"FAQ",                  icon:"❓" },
    { key:"carrousel",           label:"Carrousel & Médias",   icon:"🖼️" },
    /* ══ « PARCOURS ENTREPRISE » — MIS EN SOMMEIL le 27/10/2026 ═══════════════════════
       Retiré de la barre à la demande : l'écran est incomplet et personne ne le trouvait.

       MAIS SA CONFIGURATION, ELLE, RESTE VIVANTE. `plateforme_config.parcours_entreprise`
       est lu par le site public — `Frontend/src/Pages/Parcours/EntrepriseParcoursModal.jsx`
       l'interroge à chaque ouverture du tunnel d'inscription B2B. Le réglage enregistré
       continue donc de piloter ce que voient les entreprises ; ce qui disparaît, c'est le
       seul moyen de le MODIFIER. Tant que la refonte de la page BET for Business n'a pas
       repris ce bloc, un changement de parcours B2B demandera une écriture directe en base.

       POUR LE RÉACTIVER : décommenter la ligne ci-dessous. Rien d'autre n'est à défaire —
       le panneau (`platformSubTab === "parcours_entreprise"`, plus bas), son chargement et
       sa sauvegarde sont intacts, simplement devenus inatteignables. Même procédé que
       « Tableau de bord » et « Cockpit KPI ».

           { key:"parcours_entreprise", label:"Parcours Entreprise",  icon:"🏢" },
       ══════════════════════════════════════════════════════════════════════════════════ */
    { key:"test_niveau",         label:"Test de niveau",       icon:"🧪" },
  ];
  // ── Parcours Entreprise config ────────────────────────────
  const DEFAULT_PE_FORMATS = [
    { id:"presentiel", label:"Présentiel",      icon:"🏢", desc:"Dans nos locaux à Abidjan",     actif:true },
    { id:"distanciel", label:"À distance",      icon:"🌐", desc:"Sessions Zoom / Teams",          actif:true },
    { id:"hybride",    label:"Hybride",         icon:"⚡", desc:"Mix présentiel & en ligne",      actif:true },
    { id:"elearning",  label:"E-learning seul", icon:"💡", desc:"100% autonome, à votre rythme", actif:true },
  ];
  const DEFAULT_PE_RYTHMES = [
    { id:"intensif", label:"Intensif",      desc:"5 sessions/semaine",   actif:true },
    { id:"regulier", label:"Régulier",      desc:"3 sessions/semaine",   actif:true },
    { id:"leger",    label:"Modéré",        desc:"2 sessions/semaine",   actif:true },
    { id:"weekend",  label:"Week-end only", desc:"Sam + Dim uniquement", actif:true },
  ];
  const DEFAULT_PE_GROUPES = [
    { id:"individuel", label:"Individuel",    desc:"Cours 100% personnalisé", actif:true },
    { id:"duo",        label:"Duo",           desc:"2 participants",           actif:true },
    { id:"petit",      label:"Petit groupe",  desc:"3 à 6 participants",       actif:true },
    { id:"groupe",     label:"Groupe",        desc:"7 à 15 participants",      actif:true },
    { id:"classe",     label:"Grande classe", desc:"16 à 25 participants",     actif:true },
  ];
  const DEFAULT_PE_NIVEAUX = [
    { id:"A1", label:"A1 - Débutant",          icon:"🔵", desc:"Niveau débutant complet",          actif:true },
    { id:"A2", label:"A2 - Élémentaire",        icon:"🟡", desc:"Bases acquises",                   actif:true },
    { id:"B1", label:"B1 - Intermédiaire",      icon:"🟣", desc:"Peut communiquer en anglais",      actif:true },
    { id:"B2", label:"B2 - Interm. supérieur",  icon:"🔷", desc:"Bonne aisance à l'oral et écrit",  actif:true },
    { id:"C1", label:"C1 - Avancé",             icon:"🟢", desc:"Maîtrise professionnelle",          actif:true },
    { id:"C2", label:"C2 - Maîtrise",           icon:"🔴", desc:"Niveau quasi-bilingue",             actif:true },
  ];
  const DEFAULT_PE_OBJECTIFS = [
    { id:"communication", label:"Communication quotidienne",  icon:"💬", actif:true },
    { id:"business",      label:"Anglais des affaires",       icon:"💼", actif:true },
    { id:"toeic",         label:"Certification TOEIC",        icon:"🏆", actif:true },
    { id:"ielts",         label:"Certification IELTS",        icon:"📜", actif:true },
    { id:"presentations", label:"Présentations & réunions",   icon:"🎤", actif:true },
    { id:"redaction",     label:"Rédaction professionnelle",  icon:"✍️", actif:true },
    { id:"negociation",   label:"Négociation commerciale",    icon:"🤝", actif:true },
    { id:"technique",     label:"Anglais technique / IT",     icon:"💻", actif:true },
  ];
  const [peFormats,   setPeFormats]   = React.useState(DEFAULT_PE_FORMATS);
  const [peRythmes,   setPeRythmes]   = React.useState(DEFAULT_PE_RYTHMES);
  const [peGroupes,   setPeGroupes]   = React.useState(DEFAULT_PE_GROUPES);
  const [peNiveaux,   setPeNiveaux]   = React.useState(DEFAULT_PE_NIVEAUX);
  const [peObjectifs, setPeObjectifs] = React.useState(DEFAULT_PE_OBJECTIFS);
  const [peSaving,    setPeSaving]    = React.useState(false);
  const [peSaveOk,    setPeSaveOk]    = React.useState(false);
  const [peSaveErr,   setPeSaveErr]   = React.useState("");
  const [peLoading,   setPeLoading]   = React.useState(false);
  const [peDbState,   setPeDbState]   = React.useState(null); // snapshot de ce qui est réellement en BDD

  const applyPeData = React.useCallback((v) => {
    if (v.formats)   setPeFormats(v.formats);
    if (v.rythmes)   setPeRythmes(v.rythmes);
    if (v.groupes)   setPeGroupes(v.groupes);
    if (v.niveaux)   setPeNiveaux(v.niveaux);
    if (v.objectifs) setPeObjectifs(v.objectifs);
    setPeDbState(v);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPeConfig = React.useCallback(async () => {
    setPeLoading(true);
    try {
      const { data, error } = await supabase
        .from("plateforme_config").select("valeur")
        .eq("key","parcours_entreprise").maybeSingle();
      if (error) throw new Error(error.message);
      if (data?.valeur) {
        applyPeData(data.valeur);
      } else {
        // Pas encore en BDD - on travaille avec les defaults, on initialise peDbState à null
        setPeDbState(null);
      }
    } catch (e) {
      setPeSaveErr(`Erreur chargement : ${e.message}`);
    } finally {
      setPeLoading(false);
    }
  }, [applyPeData]); // eslint-disable-line react-hooks/exhaustive-deps

  const savePeConfig = async () => {
    setPeSaving(true); setPeSaveErr(""); setPeSaveOk(false);
    try {
      const valeur = {
        formats:   peFormats,
        rythmes:   peRythmes,
        groupes:   peGroupes,
        niveaux:   peNiveaux,
        objectifs: peObjectifs,
      };
      await savePlateformeConfig("parcours_entreprise", valeur);
      // Recharge depuis Supabase pour confirmer que la donnée est bien en BDD
      const { data: check, error: err2 } = await supabase
        .from("plateforme_config").select("valeur")
        .eq("key","parcours_entreprise").maybeSingle();
      if (err2) throw new Error(err2.message);
      if (check?.valeur) applyPeData(check.valeur);
      setPeSaveOk(true);
      setTimeout(() => setPeSaveOk(false), 3000);
    } catch (e) {
      setPeSaveErr(e.message || "Erreur lors de la sauvegarde");
      setTimeout(() => setPeSaveErr(""), 8000);
    } finally {
      setPeSaving(false);
    }
  };

  useEffect(() => { if (activeTab === "platform" && platformSubTab === "parcours_entreprise") loadPeConfig(); }, [activeTab, platformSubTab]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeTab === "platform" && platformSubTab === "catalogue" && ["offres_en_ligne","offres_domicile","centres"].includes(catalogueSubTab)) loadGrillePrivee(); }, [activeTab, platformSubTab, catalogueSubTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // useEffects dépendant de platformSubTab (déclarés après useState)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeTab === "platform" && platformSubTab === "avis_offres") fetchAvisOffres(); }, [activeTab, platformSubTab]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === "platform" && platformSubTab === "contenu") {
      fetchBlogArticles(); fetchTemos(); fetchBlogComments();
    }
  }, [activeTab, platformSubTab]); // eslint-disable-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === "platform" && platformSubTab === "contenu") {
      if (blogInnerTab === "temoignages") fetchOffreMedia("temoignages");
      if (blogInnerTab === "articles")    fetchOffreMedia("blog");
    }
  }, [activeTab, platformSubTab, blogInnerTab]); // eslint-disable-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeTab === "platform" && platformSubTab === "boutique") { fetchProduits(); fetchCommandes(); } }, [activeTab, platformSubTab]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeTab === "platform" && platformSubTab === "boutique" && boutiqueSubTab === "commandes") fetchCommandes(); }, [cmdFiltreStatut]);

  // ── Marquee ─────────────────────────────────────────────────────────────
  const [marqueeMessages, setMarqueeMessages] = useState([]);
  const [marqueeLoading,  setMarqueeLoading]  = useState(false);
  const [marqueeForm,     setMarqueeForm]     = useState(null); // null=fermé
  const [marqueeSavingId, setMarqueeSavingId] = useState(null);
  const [marqueePage,     setMarqueePage]     = useState(1);

  // ── FAQ ─────────────────────────────────────────────────────────────────────
  const [faqItems,      setFaqItems]      = useState([]);
  const [faqLoading,    setFaqLoading]    = useState(false);
  const [faqForm,       setFaqForm]       = useState(null);
  const [faqSavingId,   setFaqSavingId]   = useState(null);
  const [faqSearch,     setFaqSearch]     = useState("");
  const [faqCatFilter,  setFaqCatFilter]  = useState("Tous");
  /* « BET for Business » EST UNE CATÉGORIE À PART, et non une variante d'« Entreprises ».
     Les deux parlent du B2B, mais ne s'affichent pas au même endroit : « Entreprises »
     alimente la page publique /faq, « BET for Business » alimente la page /bet-for-business
     et en est exclue (cf. `routes/faq.js`, paramètre `?exclure=`). Les confondre ferait
     apparaître une section « BET for Business » dans la FAQ générale. */
  const CATEGORIE_BFB = "BET for Business";
  const FAQ_CATEGORIES = ["Cours & formations","Certifications","Tarifs & paiement","Nos centres","Entreprises",CATEGORIE_BFB,"Compte & espace personnel","Général"];
  const MARQUEE_PER_PAGE = 5;

  // ── Centres master (source de vérité partagée) ───────────────────────────────
  const CENTRES_MASTER_KEY = "bet_centres_master";
  const CENTRES_BET_COLORS = ["#25d366","#facc15","#0891b2","#a855f7","#f97316","#ef4444","#8b5cf6","#ec4899","#14b8a6","#f59e0b"];
  const DEFAULT_OFFRES = [
    { id:"cours_groupe",  label:"Cours en groupe",          actif:true,  prix:"30 000 F/mois", duration:"Sans engagement", desc:"Sessions hebdomadaires en petits groupes de 4–8 apprenants.", brochure_url:"", brochure_nom:"", type_coaching:"groupe" },
    { id:"cours_prives",  label:"Cours privés",             actif:true,  prix:"50 000 F/mois", duration:"Sans engagement", desc:"Coaching 1-pour-1 avec un formateur certifié.",               brochure_url:"", brochure_nom:"", type_coaching:"prive"  },
    { id:"toeic_prep",    label:"Préparation TOEIC",        actif:true,  prix:"45 000 F/mois", duration:"2 mois",          desc:"Programme intensif pour viser 750+ dès le 1er passage.",     brochure_url:"", brochure_nom:"", type_coaching:"groupe" },
    { id:"ielts_prep",    label:"Préparation IELTS",        actif:false, prix:"45 000 F/mois", duration:"2 mois",          desc:"Entraînement complet aux 4 compétences IELTS.",               brochure_url:"", brochure_nom:"", type_coaching:"groupe" },
    { id:"toefl_prep",    label:"Préparation TOEFL",        actif:false, prix:"45 000 F/mois", duration:"2 mois",          desc:"Préparation ciblée au TOEFL iBT.",                            brochure_url:"", brochure_nom:"", type_coaching:"groupe" },
    { id:"enfants",       label:"Cours enfants (6–17 ans)", actif:true,  prix:"20 000 F/mois", duration:"Sans engagement", desc:"Méthodes ludiques adaptées à chaque tranche d'âge.",          brochure_url:"", brochure_nom:"", type_coaching:"groupe" },
    { id:"entreprise",    label:"Formation entreprises",    actif:true,  prix:"Sur devis",      duration:"Sur mesure",      desc:"Programmes sur-mesure pour vos équipes (min. 5 employés).",   brochure_url:"", brochure_nom:"", type_coaching:"groupe" },
  ];
  const DEFAULT_CTA = { rdv:{ actif:true, lien:"/contact" }, inscrire:{ actif:true, lien:"/parcours/particulier" }, contact:{ actif:true, lien:"/contact" } };
  const DEFAULT_ADVANTAGES = ["Groupes de 6 personnes maximum","Matériel pédagogique fourni","Certification officielle en fin de stage","Suivi personnalisé par un formateur certifié"];
  const DEFAULT_CABINET = { subtitle:"", hero_image:"", advantages:[...DEFAULT_ADVANTAGES], testimonials:[], faq:[] };
  const CENTRES_MASTER_DEFAULT = [
    { key:"angre",    name:"BET Angré",       ville:"Abidjan",     addr:"Angré 7ème Tranche, Abidjan",    lat:5.3699, lng:-3.9674, color:"#25d366", actif:true, description:"Centre phare d'Abidjan-Cocody, dans un cadre moderne et accessible.",           horaires:"Lun–Ven : 08h–19h | Sam : 09h–17h", telephone:"+225 07 00 000 001", email:"angre@bet-ci.com",    photos:[], brochure_url:"", brochure_nom:"Brochure BET Angré.pdf",       maps_url:"", maps_embed:"", cta:{...DEFAULT_CTA}, offres:[...DEFAULT_OFFRES], assistantes:[{nom:"Assistante 1",phone:"2250700000001",message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET Angré."},{nom:"Assistante 2",phone:"2250700000011",message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET Angré."}], ...DEFAULT_CABINET },
    { key:"bouake",   name:"BET Bouaké",      ville:"Bouaké",      addr:"Centre-Ville, Bouaké",            lat:7.6936, lng:-5.0232, color:"#facc15", actif:true, description:"Le seul centre BET hors Abidjan - au cœur de la capitale économique du centre.", horaires:"Lun–Ven : 08h–18h30 | Sam : 09h–15h", telephone:"+225 07 00 000 002", email:"bouake@bet-ci.com",   photos:[], brochure_url:"", brochure_nom:"Brochure BET Bouaké.pdf",      maps_url:"", maps_embed:"", cta:{...DEFAULT_CTA}, offres:[...DEFAULT_OFFRES], assistantes:[{nom:"Assistante 1",phone:"2250700000002",message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET Bouaké."},{nom:"Assistante 2",phone:"2250700000022",message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET Bouaké."}], ...DEFAULT_CABINET },
    { key:"plateaux", name:"BET II Plateaux", ville:"Abidjan",     addr:"Riviera II Plateaux, Abidjan",   lat:5.3611, lng:-4.0103, color:"#0891b2", actif:true, description:"Situé au cœur des Deux-Plateaux, quartier résidentiel et d'affaires.",             horaires:"Lun–Ven : 08h–19h | Sam : 09h–17h", telephone:"+225 07 00 000 003", email:"plateaux@bet-ci.com", photos:[], brochure_url:"", brochure_nom:"Brochure BET II Plateaux.pdf", maps_url:"", maps_embed:"", cta:{...DEFAULT_CTA}, offres:[...DEFAULT_OFFRES], assistantes:[{nom:"Assistante 1",phone:"2250700000003",message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET II Plateaux."},{nom:"Assistante 2",phone:"2250700000033",message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET II Plateaux."}], ...DEFAULT_CABINET },
    { key:"yopougon", name:"BET Yopougon",    ville:"Abidjan",     addr:"Yopougon Sicogi, Abidjan",       lat:5.3264, lng:-4.0709, color:"#a855f7", actif:true, description:"Desservant le plus grand quartier populaire d'Afrique de l'Ouest.",               horaires:"Lun–Ven : 08h–18h30 | Sam : 09h–15h", telephone:"+225 07 00 000 004", email:"yopougon@bet-ci.com", photos:[], brochure_url:"", brochure_nom:"Brochure BET Yopougon.pdf",    maps_url:"", maps_embed:"", cta:{...DEFAULT_CTA}, offres:[...DEFAULT_OFFRES], assistantes:[{nom:"Assistante 1",phone:"2250700000004",message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET Yopougon."},{nom:"Assistante 2",phone:"2250700000044",message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET Yopougon."}], ...DEFAULT_CABINET },
    { key:"koumassi", name:"BET Koumassi",    ville:"Abidjan",     addr:"Koumassi Remblai, Abidjan",      lat:5.3001, lng:-3.9500, color:"#f97316", actif:true, description:"Centre stratégique desservant les communes du sud d'Abidjan.",                    horaires:"Lun–Ven : 08h–18h30 | Sam : 09h–15h", telephone:"+225 07 00 000 005", email:"koumassi@bet-ci.com", photos:[], brochure_url:"", brochure_nom:"Brochure BET Koumassi.pdf",   maps_url:"", maps_embed:"", cta:{...DEFAULT_CTA}, offres:[...DEFAULT_OFFRES], assistantes:[{nom:"Assistante 1",phone:"2250700000005",message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET Koumassi."},{nom:"Assistante 2",phone:"2250700000055",message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET Koumassi."}], ...DEFAULT_CABINET },
    { key:"abatta",   name:"BET Abatta",      ville:"Grand-Bassam",addr:"Abatta, Grand-Bassam",            lat:5.2667, lng:-3.8333, color:"#ef4444", actif:true, description:"Centre BET desservant la zone côtière Grand-Bassam / Assinie.",                   horaires:"Lun–Ven : 08h–18h | Sam : 09h–14h",   telephone:"+225 07 00 000 006", email:"abatta@bet-ci.com",   photos:[], brochure_url:"", brochure_nom:"Brochure BET Abatta.pdf",     maps_url:"", maps_embed:"", cta:{...DEFAULT_CTA}, offres:[...DEFAULT_OFFRES], assistantes:[{nom:"Assistante 1",phone:"2250700000006",message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET Abatta."},{nom:"Assistante 2",phone:"2250700000066",message:"Bonjour, je souhaite avoir des informations sur les cours d'anglais chez BET Abatta."}], ...DEFAULT_CABINET },
  ];

  const readCentresMaster = () => {
    try {
      const s = localStorage.getItem(CENTRES_MASTER_KEY);
      if (s) {
        const parsed = JSON.parse(s);
        // Patch type_coaching manquant depuis DEFAULT_OFFRES (par id)
        const patchOffres = (offres) => (offres || []).map(o => {
          if (o.type_coaching) return o;
          const def = DEFAULT_OFFRES.find(d => d.id === o.id);
          return def?.type_coaching ? { ...o, type_coaching: def.type_coaching } : o;
        });
        return CENTRES_MASTER_DEFAULT.map(def => {
          const saved = parsed.find(c => c.key === def.key);
          return saved ? { ...def, ...saved, offres: patchOffres(saved.offres || def.offres), assistantes: saved.assistantes || def.assistantes, advantages: saved.advantages || def.advantages, testimonials: saved.testimonials || def.testimonials, faq: saved.faq || def.faq } : def;
        }).concat(parsed.filter(c => !CENTRES_MASTER_DEFAULT.find(d => d.key === c.key)));
      }
      return CENTRES_MASTER_DEFAULT;
    } catch { return CENTRES_MASTER_DEFAULT; }
  };

  const saveCentresMaster = async (data) => {
    // 1. localStorage local (même onglet)
    localStorage.setItem(CENTRES_MASTER_KEY, JSON.stringify(data));
    const waCompat = data.filter(c => c.actif !== false).map(c => ({ key:c.key, name:c.name.replace("BET ",""), color:c.color, commerciaux:[], assistantes:c.assistantes||[] }));
    localStorage.setItem("bet_centers_config", JSON.stringify(waCompat));
    window.dispatchEvent(new StorageEvent("storage", { key: CENTRES_MASTER_KEY, newValue: JSON.stringify(data) }));
    window.dispatchEvent(new StorageEvent("storage", { key: "bet_centers_config", newValue: JSON.stringify(waCompat) }));

    // 2. Backend (source de vérité partagée - accessible par le Frontend)
    try {
      await savePlateformeConfig("centres_master", data);
      await savePlateformeConfig("centres_wa", waCompat);
    } catch (e) { console.error("Sync centres:", e); }
  };

  const [centresMaster,      setCentresMaster]      = useState(readCentresMaster);
  useEffect(() => {
    supabase.from("plateforme_config").select("valeur").eq("key","centres_master").maybeSingle()
      .then(({ data, error }) => {
        if (!error && Array.isArray(data?.valeur) && data.valeur.length) {
          localStorage.setItem(CENTRES_MASTER_KEY, JSON.stringify(data.valeur));
          setCentresMaster(data.valeur);
        }
      });
  }, []); // eslint-disable-line
  const [centreEditKey,       setCentreEditKey]       = useState(null);
  const [centreEditData,      setCentreEditData]      = useState(null);
  const [centresSaving,       setCentresSaving]       = useState(false);
  const [newPhotoUrl,         setNewPhotoUrl]         = useState("");
  // ── Offres En ligne & À domicile ────────────────────────────────────────────
  const DEFAULT_OFFRES_EN_LIGNE = [
    { id:"el_groupe", label:"Coaching de groupe",     prix:"25 000 F/mois", duration:"Sans engagement", desc:"Sessions Zoom hebdomadaires, groupes de 5 apprenants max.",             actif:true,  icon:"👥", type_coaching:"groupe",  routing:"assistante"  },
    { id:"el_prive",  label:"Coaching privé",          prix:"45 000 F/mois", duration:"Sans engagement", desc:"Suivi individuel en visio avec un formateur certifié.",                actif:true,  icon:"👤", type_coaching:"prive",   routing:"pa_advisor"  },
    { id:"el_toeic",  label:"Prépa TOEIC en ligne",    prix:"40 000 F/mois", duration:"2 mois",          desc:"Programme intensif pour viser 750+ dès le 1er passage.",              actif:false, icon:"🎯", type_coaching:"groupe",  routing:"assistante"  },
    { id:"el_enfant", label:"Cours enfants en ligne",   prix:"20 000 F/mois", duration:"Sans engagement", desc:"Méthodes ludiques adaptées à chaque tranche d'âge (6–17 ans).",      actif:false, icon:"🎓", type_coaching:"groupe",  routing:"assistante"  },
  ];
  const DEFAULT_OFFRES_DOMICILE = [
    { id:"dom_prive",  label:"Cours privé à domicile",  prix:"Sur devis", duration:"Sur mesure", desc:"Un coach certifié se déplace chez vous. Tarif selon zone géographique.", actif:true,  icon:"🏠", type_coaching:"prive"  },
    { id:"dom_enfant", label:"Cours enfant à domicile",  prix:"Sur devis", duration:"Sur mesure", desc:"Coach spécialisé jeunesse, programme adapté à l'âge de l'enfant.",       actif:true,  icon:"👧", type_coaching:"prive"  },
  ];
  const DEFAULT_CERTIF_CONFIG = {
    toeic: {
      name:"TOEIC", fullName:"Test of English for International Communication",
      tagline:"La certification anglais n°1 en entreprise - reconnue par 14 000 employeurs",
      heroImage:"https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=1600&q=80",
      level:"B1 → C1", duration:"6 semaines", price:"390 000 FCFA", oldPrice:"500 000 FCFA", discount:"22%",
      rating:4.9, ratingCount:1248, students:5200,
      description:"Le TOEIC est la référence mondiale pour évaluer vos compétences en anglais professionnel. Un score TOEIC reconnu ouvre les portes des entreprises internationales.",
      whatYouLearn:["Maîtriser le Listening (200 points)","Stratégies Reading avancées","Vocabulaire professionnel ciblé","15 tests blancs corrigés","Techniques anti-stress d'examen","Score garanti 700+"],
      examStructure:[
        {section:"Listening",duration:"45 min",questions:"100 questions",desc:"Photographies, questions/réponses, courtes conversations, discours"},
        {section:"Reading",duration:"75 min",questions:"100 questions",desc:"Phrases incomplètes, textes à compléter, lecture de passages"},
      ],
      preparationProgram:{weeks:6,hoursPerWeek:8,sessions:48,details:"6 semaines intensives. 2 sessions de 4h par semaine. Accès illimité aux ressources + 15 tests blancs + coaching individuel."},
      benefits:["Reconnu par 14 000 entreprises dans le monde","Améliore votre CV immédiatement","Exigé pour de nombreux postes internationaux","Valable 2 ans - renouvelable","Score précis de 10 à 990"],
      includes:[{icon:"🎥",label:"48 sessions de cours"},{icon:"📝",label:"15 tests blancs complets"},{icon:"📄",label:"Guide de stratégies PDF"},{icon:"📱",label:"Application mobile"},{icon:"∞",label:"Accès illimité à vie"},{icon:"🏆",label:"Certificat BET + TOEIC officiel"},{icon:"👤",label:"Coach personnel dédié"}],
      whyChoose:"BET est centre officiel ETS pour le TOEIC en Côte d'Ivoire. Nos apprenants obtiennent en moyenne 780 points dès leur premier passage. Taux de réussite au score cible : 95%.",
      faq:[
        {q:"Quelle est la durée de validité du TOEIC ?",a:"Le TOEIC est valable 2 ans à partir de la date de l'examen."},
        {q:"Quel score pour être compétitif ?",a:"Un score de 785+ est considéré professionnel. Pour le management international, 900+ est recommandé."},
        {q:"L'examen se passe-t-il chez BET ?",a:"Oui, BET est centre officiel ETS. L'examen a lieu dans nos locaux dans des conditions officielles."},
      ],
    },
    ielts: {
      name:"IELTS", fullName:"International English Language Testing System",
      tagline:"La certification anglais pour étudier et migrer dans les pays anglophones",
      heroImage:"https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=1200&q=80",
      level:"B1 → C2", duration:"6 à 10 semaines", price:"210 000 FCFA", oldPrice:"", discount:"",
      rating:4.8, ratingCount:856, students:3400,
      description:"L'IELTS est le test d'anglais le plus populaire pour étudier, travailler ou migrer dans les pays anglophones (Royaume-Uni, Australie, Canada).",
      whatYouLearn:["Maîtriser les 4 compétences","Techniques de Reading académique","Speaking avec locuteurs natifs","Writing Task 1 & 2","Listening strategies","Band 7+ garanti"],
      examStructure:[
        {section:"Listening",duration:"30 min",questions:"40 questions",desc:"Enregistrements variés - conversations et monologues"},
        {section:"Reading",duration:"60 min",questions:"40 questions",desc:"3 passages longs - 500 à 900 mots chacun"},
        {section:"Writing",duration:"60 min",questions:"2 tâches",desc:"Task 1 (rapport/lettre) + Task 2 (essai argumentatif)"},
        {section:"Speaking",duration:"11–14 min",questions:"3 parties",desc:"Interview individuelle, long turn, discussion thématique"},
      ],
      preparationProgram:{weeks:8,hoursPerWeek:9,sessions:36,details:"8 semaines de préparation complète avec simulations d'examen et corrections détaillées."},
      benefits:["Reconnu par plus de 10 000 organisations dans le monde","Idéal pour l'immigration au Canada, Australie, NZ","Deux versions : Academic et General Training","Évaluation de l'anglais réel (accents variés)"],
      includes:[{icon:"🎥",label:"36 sessions de cours"},{icon:"📝",label:"8 simulations complètes"},{icon:"👤",label:"Coach personnel"},{icon:"📄",label:"Fiches de stratégies"},{icon:"🏆",label:"Certificat BET + IELTS officiel"}],
      whyChoose:"Nous proposons une préparation IELTS avec des examinateurs certifiés. Bénéficiez de 8 simulations complètes et d'un plan de progression personnalisé. Taux de réussite : 95%.",
      faq:[
        {q:"IELTS Academic vs General Training ?",a:"Academic pour les études universitaires. General Training pour l'immigration et la formation professionnelle."},
        {q:"Combien de fois puis-je passer l'IELTS ?",a:"Autant de fois que nécessaire, sans délai minimum entre les passages."},
      ],
    },
    toefl: {
      name:"TOEFL", fullName:"Test of English as a Foreign Language",
      tagline:"La certification académique pour intégrer les meilleures universités du monde",
      heroImage:"https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=1200&q=80",
      level:"B2 → C2", duration:"8 semaines", price:"220 000 FCFA", oldPrice:"", discount:"",
      rating:4.7, ratingCount:632, students:2100,
      description:"Le TOEFL est le test d'anglais académique le plus reconnu pour entrer dans les universités américaines, canadiennes et européennes.",
      whatYouLearn:["Reading académique avancé","Listening académique","Speaking structuré","Writing intégré","Gestion du temps en examen","Score 100+ iBT garanti"],
      examStructure:[
        {section:"Reading",duration:"54–72 min",questions:"30–40 questions",desc:"3–4 passages académiques de 700 mots chacun"},
        {section:"Listening",duration:"41–57 min",questions:"28–39 questions",desc:"Conférences et conversations académiques enregistrées"},
        {section:"Speaking",duration:"17 min",questions:"4 tâches",desc:"Integrated et Independent speaking tasks chronométrés"},
        {section:"Writing",duration:"50 min",questions:"2 tâches",desc:"Integrated writing + Independent essay argumentatif"},
      ],
      preparationProgram:{weeks:8,hoursPerWeek:10,sessions:40,details:"8 semaines intensives avec accès à une plateforme exclusive (+500 exercices), corrigés types et sessions de speaking en petit groupe avec des natifs."},
      benefits:["Admission dans plus de 11 000 universités dans le monde","Test complet évaluant les 4 compétences","Reconnu par les gouvernements pour l'immigration","Valable 2 ans"],
      includes:[{icon:"🎥",label:"40 sessions de cours"},{icon:"📝",label:"6 tests blancs complets"},{icon:"💻",label:"Accès plateforme exclusive"},{icon:"🗣️",label:"Sessions speaking natifs"},{icon:"🏆",label:"Certificat BET + TOEFL officiel"}],
      whyChoose:"Notre préparation TOEFL vous offre un accès à une plateforme exclusive avec +500 exercices, des corrigés types, et des sessions de speaking en petit groupe avec des natifs.",
      faq:[
        {q:"TOEFL iBT vs TOEFL Essentials ?",a:"Le TOEFL iBT est la version standard pour les universités. TOEFL Essentials est plus court et flexible."},
        {q:"Quel score pour les top universités ?",a:"100+ iBT pour la plupart des grandes universités. Certaines (Harvard, MIT) demandent 110+."},
      ],
    },
  };
  const readOffresEnLigne  = () => { try { const s=localStorage.getItem("bet_offres_en_ligne");  return s ? JSON.parse(s) : DEFAULT_OFFRES_EN_LIGNE;  } catch { return DEFAULT_OFFRES_EN_LIGNE;  } };
  const readOffresDomicile = () => { try { const s=localStorage.getItem("bet_offres_domicile"); return s ? JSON.parse(s) : DEFAULT_OFFRES_DOMICILE; } catch { return DEFAULT_OFFRES_DOMICILE; } };
  const saveOffresEnLigne  = async (data) => {
    localStorage.setItem("bet_offres_en_ligne", JSON.stringify(data));
    window.dispatchEvent(new StorageEvent("storage", { key:"bet_offres_en_ligne", newValue:JSON.stringify(data) }));
    try { await savePlateformeConfig("offres_en_ligne", data); } catch(e) { console.error("offres_en_ligne:", e); }
  };
  const saveOffresDomicile = async (data) => {
    localStorage.setItem("bet_offres_domicile", JSON.stringify(data));
    window.dispatchEvent(new StorageEvent("storage", { key:"bet_offres_domicile", newValue:JSON.stringify(data) }));
    try { await savePlateformeConfig("offres_domicile", data); } catch(e) { console.error("offres_domicile:", e); }
  };
  const [offresEnLigne,  setOffresEnLigne]  = useState(readOffresEnLigne);
  const [offresDomicile, setOffresDomicile] = useState(readOffresDomicile);
  useEffect(() => {
    supabase.from("plateforme_config").select("valeur").eq("key","offres_en_ligne").maybeSingle()
      .then(({ data, error }) => {
        if (!error && Array.isArray(data?.valeur) && data.valeur.length) {
          localStorage.setItem("bet_offres_en_ligne", JSON.stringify(data.valeur));
          setOffresEnLigne(data.valeur);
        }
      });
    supabase.from("plateforme_config").select("valeur").eq("key","offres_domicile").maybeSingle()
      .then(({ data, error }) => {
        if (!error && Array.isArray(data?.valeur) && data.valeur.length) {
          localStorage.setItem("bet_offres_domicile", JSON.stringify(data.valeur));
          setOffresDomicile(data.valeur);
        }
      });
  }, []); // eslint-disable-line
  const [offreEditModal, setOffreEditModal] = useState(null); // { mode:"en_ligne"|"domicile", idx, data }

  const DEFAULT_SERVICE_INTERP = {
    description: "Services d'interprétation professionnelle assurés par des interprètes certifiés pour vos événements, conférences et réunions d'affaires.",
    tagline: "Conférences · Réunions · Événements",
    details: [
      { icon:"🎤", label:"Types", val:"Simultané · Consécutif · Liaison" },
      { icon:"🌐", label:"Langues", val:"Anglais · Français · et autres" },
      { icon:"📍", label:"Déplacement", val:"Abidjan & déplacements sur demande" },
      { icon:"📅", label:"Disponibilité", val:"7j/7 · Sur réservation" },
      { icon:"📜", label:"Certification", val:"Interprètes certifiés AIIC" },
      { icon:"⚡", label:"Délai", val:"Devis sous 24h" },
    ],
    plans: [
      { nom:"Demi-journée", prix:"Sur devis", detail:"Jusqu'à 4h · 1 interprète" },
      { nom:"Journée",      prix:"Sur devis", detail:"Journée complète · 1-2 interprètes", popular:true },
      { nom:"Événement",    prix:"Sur devis", detail:"Multi-jours · Équipe dédiée" },
    ],
  };
  const DEFAULT_SERVICE_TRAD = {
    description: "Service de traduction professionnelle de documents juridiques, commerciaux et techniques. Traductions certifiées disponibles pour vos démarches officielles.",
    tagline: "Documents · Contrats · Certifiée",
    details: [
      { icon:"📋", label:"Documents", val:"Contrats · Actes · Brochures · Sites web" },
      { icon:"🌐", label:"Langues", val:"Anglais ↔ Français (+ autres sur demande)" },
      { icon:"📜", label:"Certifiée", val:"Traductions certifiées disponibles" },
      { icon:"⚡", label:"Délais", val:"Express 24h · Standard 3-5 jours" },
      { icon:"🔒", label:"Confidentialité", val:"NDA disponible sur demande" },
      { icon:"💬", label:"Révisions", val:"1 révision gratuite incluse" },
    ],
    plans: [
      { nom:"Standard",  prix:"Sur devis", detail:"3-5 jours ouvrés · Tarif /mot" },
      { nom:"Express",   prix:"Sur devis", detail:"24-48h · Majoration urgence", popular:true },
      { nom:"Certifiée", prix:"Sur devis", detail:"Avec cachet officiel · Légalisation" },
    ],
  };
  const readServiceInterp = () => { try { const s=localStorage.getItem("bet_service_interpretariat"); return s ? JSON.parse(s) : DEFAULT_SERVICE_INTERP; } catch { return DEFAULT_SERVICE_INTERP; } };
  const readServiceTrad   = () => { try { const s=localStorage.getItem("bet_service_traduction");    return s ? JSON.parse(s) : DEFAULT_SERVICE_TRAD;  } catch { return DEFAULT_SERVICE_TRAD;  } };
  const saveServiceInterp = async (data) => {
    localStorage.setItem("bet_service_interpretariat", JSON.stringify(data));
    window.dispatchEvent(new StorageEvent("storage", { key:"bet_service_interpretariat", newValue:JSON.stringify(data) }));
    try { await savePlateformeConfig("service_interpretariat", data); } catch(e) { console.error("service_interpretariat:", e); }
  };
  const saveServiceTrad = async (data) => {
    localStorage.setItem("bet_service_traduction", JSON.stringify(data));
    window.dispatchEvent(new StorageEvent("storage", { key:"bet_service_traduction", newValue:JSON.stringify(data) }));
    try { await savePlateformeConfig("service_traduction", data); } catch(e) { console.error("service_traduction:", e); }
  };
  const [serviceInterp, setServiceInterp] = useState(readServiceInterp);
  const [serviceTrad,   setServiceTrad]   = useState(readServiceTrad);

  // ── Catalogue PDF ─────────────────────────────────────────────────────────
  // Grille tarifaire coaching privé
  const GRILLE_DEFAUT = {
    classique: { label:"Classique", frequence:"3 séances de 2 heures / semaine", cabinet:150000, domicile:200000 },
    intensive:  { label:"Intensive",  frequence:"5 séances / semaine",             cabinet:200000, domicile:300000 },
  };
  const [grillePrivee,    setGrillePrivee]    = useState(GRILLE_DEFAUT);
  const [grilleSaving,    setGrilleSaving]    = useState(false);
  const [grilleMsg,       setGrilleMsg]       = useState(null);

  const loadGrillePrivee = async () => {
    try {
      const { data } = await supabase.from("plateforme_config").select("valeur").eq("key","coaching_prive_config").maybeSingle();
      if (data?.valeur) setGrillePrivee(data.valeur);
    } catch {}
  };
  const saveGrillePrivee = async () => {
    setGrilleSaving(true);
    try {
      await savePlateformeConfig("coaching_prive_config", grillePrivee);
      setGrilleMsg({ type:"success", text:"Grille tarifaire enregistrée ✓" });
      setTimeout(()=>setGrilleMsg(null), 3000);
    } catch { setGrilleMsg({ type:"error", text:"Erreur lors de l'enregistrement" }); }
    finally { setGrilleSaving(false); }
  };

  const [cataloguePdfUrl,       setCataloguePdfUrl]       = useState(() => { try { const s=localStorage.getItem("bet_catalogue_pdf"); return s?JSON.parse(s)?.url||"":""} catch{return "";} });
  const [cataloguePdfNom,       setCataloguePdfNom]       = useState(() => { try { const s=localStorage.getItem("bet_catalogue_pdf"); return s?JSON.parse(s)?.nom||"":"";} catch{return "";} });
  const [cataloguePdfUploading, setCataloguePdfUploading] = useState(false);
  const [cataloguePdfMsg,       setCataloguePdfMsg]       = useState(null);

  const saveCataloguePdf = async (url, nom) => {
    const val = { url, nom };
    localStorage.setItem("bet_catalogue_pdf", JSON.stringify(val));
    window.dispatchEvent(new StorageEvent("storage", { key:"bet_catalogue_pdf", newValue:JSON.stringify(val) }));
    try { await savePlateformeConfig("catalogue_pdf", val); } catch(e) { console.error("catalogue_pdf:", e); }
  };

  const handleCataloguePdfUpload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { setCataloguePdfMsg({ type:"error", text:"Seuls les fichiers PDF sont acceptés." }); return; }
    if (file.size > maxMoPour("document", file.type) * 1024 * 1024) { setCataloguePdfMsg({ type:"error", text:"❌ Fichier trop volumineux - taille maximale autorisée : 50 Mo." }); return; }
    setCataloguePdfUploading(true); setCataloguePdfMsg(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await fetchUpload(`${API_URL}/api/upload/document`, { method:"POST", headers:{ Authorization:`Bearer ${localStorage.getItem("admin_token")}` }, body:fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Erreur upload");
      const url = d.file?.url || d.url;
      const nom = file.name;
      setCataloguePdfUrl(url); setCataloguePdfNom(nom);
      await saveCataloguePdf(url, nom);
      setCataloguePdfMsg({ type:"success", text:"✅ Catalogue mis à jour avec succès !" });
    } catch(e) { setCataloguePdfMsg({ type:"error", text:"❌ " + e.message }); }
    finally { setCataloguePdfUploading(false); }
  };

  useEffect(() => {
    supabase.from("plateforme_config").select("valeur").eq("key","service_interpretariat").maybeSingle()
      .then(({ data, error }) => { if (!error && data?.valeur) { localStorage.setItem("bet_service_interpretariat", JSON.stringify(data.valeur)); setServiceInterp(data.valeur); } });
    supabase.from("plateforme_config").select("valeur").eq("key","service_traduction").maybeSingle()
      .then(({ data, error }) => { if (!error && data?.valeur) { localStorage.setItem("bet_service_traduction", JSON.stringify(data.valeur)); setServiceTrad(data.valeur); } });
    supabase.from("plateforme_config").select("valeur").eq("key","catalogue_pdf").maybeSingle()
      .then(({ data, error }) => { if (!error && data?.valeur) { const v=data.valeur; localStorage.setItem("bet_catalogue_pdf", JSON.stringify(v)); setCataloguePdfUrl(v.url||""); setCataloguePdfNom(v.nom||""); } });
  }, []); // eslint-disable-line
  const [serviceEditModal, setServiceEditModal] = useState(null);

  // ── Config page Aperçu (CourseDetail) ────────────────────────────────────
  const DEFAULT_COURSE_APERCU = {
    en_ligne: {
      description:"Nos cours en ligne vous offrent une flexibilité totale. Suivez vos leçons en direct ou en replay, accédez à une plateforme interactive et progressez avec un suivi personnalisé.",
      advantages:["Accès 24/7 à la plateforme e-learning","Classes en visio avec des professeurs natifs","Exercices interactifs et corrections automatiques","Certification de fin de formation incluse"],
      whatYouLearn:["Maîtriser la communication professionnelle en anglais","Préparer et réussir TOEIC, TOEFL ou IELTS","Rédiger des emails, rapports et présentations en anglais","Conduire des réunions et négociations internationales"],
      includes:[{icon:"🎥",label:"Sessions vidéo en direct"},{icon:"📱",label:"Accès mobile & tablette"},{icon:"∞",label:"Accès illimité à vie"},{icon:"🏆",label:"Certificat de fin de formation"}],
      requirements:["Niveau A2 minimum en anglais","Un ordinateur avec webcam et connexion internet stable","30 minutes par jour de disponibilité"],
      targetAudience:["Professionnels souhaitant évoluer dans un contexte international","Étudiants visant une certification TOEIC, TOEFL ou IELTS","Toute personne souhaitant apprendre à son rythme"],
      faq:[{q:"Quel matériel est nécessaire ?",a:"Un ordinateur avec webcam et une connexion internet stable."},{q:"Peut-on annuler un cours ?",a:"Oui, jusqu'à 24h à l'avance sans frais."}],
    },
    domicile: {
      description:"Nos cours à domicile sont conçus pour s'adapter parfaitement à votre vie. Un enseignant se déplace chez vous aux horaires que vous choisissez.",
      advantages:["Horaires 100% flexibles","Programme personnalisé selon vos objectifs","Suivi hebdomadaire des progrès","Tarifs dégressifs selon la formule choisie"],
      whatYouLearn:["Anglais adapté à vos objectifs personnels","Communication orale et écrite","Préparation aux certifications si souhaité","Grammaire et vocabulaire ciblés"],
      includes:[{icon:"🏠",label:"Cours à votre domicile"},{icon:"👤",label:"Coach personnel dédié"},{icon:"📋",label:"Programme personnalisé"},{icon:"🏆",label:"Certification BET incluse"}],
      requirements:["Espace disponible pour les cours","Engagement minimum de 4 semaines"],
      targetAudience:["Personnes avec un emploi du temps chargé","Parents souhaitant des cours pour leurs enfants","Professionnels en reconversion"],
      faq:[{q:"Dans quelles zones intervenez-vous ?",a:"Nous couvrons tout le grand Abidjan et plusieurs villes de l'intérieur."},{q:"Peut-on changer de professeur ?",a:"Oui, sans frais si vous n'êtes pas satisfait."}],
    },
  };
  const readCourseApercu  = () => { try { const s=localStorage.getItem("bet_course_apercu_config"); return s ? JSON.parse(s) : DEFAULT_COURSE_APERCU; } catch { return DEFAULT_COURSE_APERCU; } };
  const saveCourseApercu  = async (data) => {
    localStorage.setItem("bet_course_apercu_config", JSON.stringify(data));
    window.dispatchEvent(new StorageEvent("storage", { key:"bet_course_apercu_config", newValue:JSON.stringify(data) }));
    try { await savePlateformeConfig("course_apercu_config", data); } catch(e) { console.error("course_apercu_config:", e); }
  };
  const [courseApercu,          setCourseApercu]          = useState(readCourseApercu);
  useEffect(() => {
    supabase.from("plateforme_config").select("valeur").eq("key","course_apercu_config").maybeSingle()
      .then(({ data, error }) => {
        if (!error && data?.valeur && typeof data.valeur === "object") {
          localStorage.setItem("bet_course_apercu_config", JSON.stringify(data.valeur));
          setCourseApercu(data.valeur);
        }
      });
  }, []); // eslint-disable-line
  const [courseApercuEditModal, setCourseApercuEditModal] = useState(null); // { type:"en_ligne"|"domicile", data:{} }
  const [courseApercuSection,   setCourseApercuSection]   = useState("general");
  const readCertifConfig = () => { try { const s=localStorage.getItem("bet_certifications_config"); return s ? JSON.parse(s) : DEFAULT_CERTIF_CONFIG; } catch { return DEFAULT_CERTIF_CONFIG; } };
  const saveCertifConfig = async (data) => {
    localStorage.setItem("bet_certifications_config", JSON.stringify(data));
    window.dispatchEvent(new StorageEvent("storage", { key:"bet_certifications_config", newValue:JSON.stringify(data) }));
    try { await savePlateformeConfig("certifications_config", data); } catch(e) { console.error("certifications_config:", e); }
  };
  const [certifConfig,      setCertifConfig]      = useState(readCertifConfig);
  useEffect(() => {
    supabase.from("plateforme_config").select("valeur").eq("key","certifications_config").maybeSingle()
      .then(({ data, error }) => {
        if (!error && data?.valeur && typeof data.valeur === "object") {
          localStorage.setItem("bet_certifications_config", JSON.stringify(data.valeur));
          setCertifConfig(data.valeur);
        }
      });
  }, []); // eslint-disable-line
  const [certifEditModal,   setCertifEditModal]   = useState(null);
  const [certifEditSection, setCertifEditSection] = useState("general");

  const [showAddCentre,       setShowAddCentre]       = useState(false);
  const [newCentreForm,       setNewCentreForm]       = useState({ name:"", ville:"", addr:"", telephone:"", email:"", color:"#0891b2", lat:"", lng:"" });

  const fetchMarquee = async () => {
    setMarqueeLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const r = await fetch(`${API_URL}/api/marquee`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setMarqueeMessages(d.messages || []);
      setMarqueePage(1);
    } catch { toast.error("Erreur chargement marquee"); }
    finally { setMarqueeLoading(false); }
  };

  const saveMarquee = async (form) => {
    setMarqueeSavingId(form.id || "__new");
    try {
      const token = localStorage.getItem("admin_token");
      const isEdit = !!form.id;
      const url  = isEdit ? `${API_URL}/api/marquee/${form.id}` : `${API_URL}/api/marquee`;
      const method = isEdit ? "PATCH" : "POST";
      const body = {
        texte: form.texte, code_promo: form.code_promo || null,
        lien_url: form.lien_url || null, lien_label: form.lien_label || null,
        date_expiration: form.date_expiration || null,
        actif: form.actif !== false,
        ordre: form.ordre != null ? Number(form.ordre) : undefined,
      };
      const r = await fetch(url, { method, headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error((await r.json()).error || "Erreur");
      toast.success(isEdit ? "Message mis à jour" : "Message créé");
      setMarqueeForm(null);
      fetchMarquee();
    } catch (e) { toast.error(e.message); }
    finally { setMarqueeSavingId(null); }
  };

  const deleteMarquee = async (id) => {
    if (!(await confirm("Supprimer ce message ?", { titre: "Supprimer le message" }))) return;
    try {
      const token = localStorage.getItem("admin_token");
      await fetch(`${API_URL}/api/marquee/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      toast.success("Message supprimé");
      fetchMarquee();
    } catch { toast.error("Erreur suppression"); }
  };

  const toggleMarqueeActif = async (msg) => {
    try {
      const token = localStorage.getItem("admin_token");
      await fetch(`${API_URL}/api/marquee/${msg.id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ actif: !msg.actif }),
      });
      setMarqueeMessages(prev => prev.map(m => m.id === msg.id ? { ...m, actif: !m.actif } : m));
    } catch { toast.error("Erreur"); }
  };
  const fetchFaq = async () => {
    setFaqLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/faq`, { headers: authHeaders() });
      const d = await r.json();
      setFaqItems(d.items || []);
    } catch { toast.error("Erreur chargement FAQ"); }
    finally { setFaqLoading(false); }
  };

  const saveFaq = async (form) => {
    setFaqSavingId(form.id || "__new");
    try {
      const isEdit = !!form.id;
      const url    = isEdit ? `${API_URL}/api/faq/${form.id}` : `${API_URL}/api/faq`;
      const method = isEdit ? "PATCH" : "POST";
      const r = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify({
          question:  form.question,
          reponse:   form.reponse,
          categorie: form.categorie || "Général",
          ordre:     form.ordre != null ? Number(form.ordre) : undefined,
          actif:     form.actif !== false,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).error || "Erreur");
      toast.success(isEdit ? "FAQ mise à jour" : "FAQ créée");
      setFaqForm(null);
      fetchFaq();
    } catch (e) { toast.error(e.message); }
    finally { setFaqSavingId(null); }
  };

  const deleteFaq = async (id) => {
    if (!(await confirm("Supprimer cette FAQ ?", { titre: "Supprimer la FAQ" }))) return;
    try {
      await fetch(`${API_URL}/api/faq/${id}`, { method: "DELETE", headers: authHeaders() });
      toast.success("FAQ supprimée");
      fetchFaq();
    } catch { toast.error("Erreur suppression"); }
  };

  const toggleFaqActif = async (item) => {
    try {
      await fetch(`${API_URL}/api/faq/${item.id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ actif: !item.actif }),
      });
      setFaqItems(prev => prev.map(f => f.id === item.id ? { ...f, actif: !f.actif } : f));
    } catch { toast.error("Erreur"); }
  };

  const [coachsList, setCoachsList]           = useState([]);
  const [coachsLoading, setCoachsLoading]     = useState(false);
  const [coachsEdits, setCoachsEdits]         = useState({});
  const [coachsSaving, setCoachsSaving]       = useState({});
  const [coachSearchQ, setCoachSearchQ]       = useState("");
  const [coachsUploading, setCoachsUploading] = useState({});
  const [newCoachForm, setNewCoachForm]       = useState({ nom:"", titre:"", photo_url:"" });
  const [newCoachUploading, setNewCoachUploading] = useState(false);
  const [newCoachSaving, setNewCoachSaving]   = useState(false);

  const uploadToCloudinary = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetchUpload(`${API_URL}/api/upload/avatar`, {
      method: "POST",
      headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
      body: fd,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Erreur upload");
    return data.file.url;
  };

  const uploadCoachPhoto = async (coach, file) => {
    setCoachsUploading(p => ({ ...p, [coach.id]: true }));
    try {
      const url = await uploadToCloudinary(file);
      setCoachsEdits(p => ({ ...p, [coach.id]: { ...(p[coach.id] || {}), photo_url: url } }));
      toast.success("Photo uploadée - cliquez Enregistrer pour sauvegarder");
    } catch (err) {
      toast.error("Erreur upload : " + (err.message || "réessayez"));
    } finally {
      setCoachsUploading(p => ({ ...p, [coach.id]: false }));
    }
  };

  const uploadNewCoachPhoto = async (file) => {
    setNewCoachUploading(true);
    try {
      const url = await uploadToCloudinary(file);
      setNewCoachForm(p => ({ ...p, photo_url: url }));
      toast.success("Photo chargée");
    } catch (err) {
      toast.error("Erreur upload : " + (err.message || "réessayez"));
    } finally {
      setNewCoachUploading(false);
    }
  };

  const createDisplayCoach = async () => {
    if (!newCoachForm.photo_url) { toast.error("Veuillez d'abord uploader une photo"); return; }
    setNewCoachSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/equipe-photos`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(newCoachForm),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || "Erreur"); }
      toast.success("Photo ajoutée !");
      setNewCoachForm({ nom:"", titre:"", photo_url:"" });
      fetchCoachs();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setNewCoachSaving(false);
    }
  };

  const fetchCoachs = useCallback(async () => {
    setCoachsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/equipe-photos`, { headers: authHeaders() });
      const data = await res.json();
      setCoachsList(Array.isArray(data) ? data : []);
      setCoachsEdits({});
    } catch { toast.error("Erreur chargement photos équipe"); }
    finally { setCoachsLoading(false); }
  }, []);

  useEffect(() => { if (platformSubTab === "coachs_photos") fetchCoachs(); }, [platformSubTab, fetchCoachs]);

  const fetchAdminCoachsReel = useCallback(async () => {
    setAdminCoachsReelLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/utilisateurs?role=coach`, { headers: authHeaders() });
      if (r.ok) { const d = await r.json(); setAdminCoachsReel(d.utilisateurs || []); }
    } catch {}
    finally { setAdminCoachsReelLoading(false); }
  }, []);
  useEffect(() => { if (activeTab === "coachs") fetchAdminCoachsReel(); }, [activeTab, fetchAdminCoachsReel]);
  useEffect(() => { if (platformSubTab === "marquee") fetchMarquee(); }, [platformSubTab]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeTab === "platform" && platformSubTab === "faq") fetchFaq(); }, [activeTab, platformSubTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Carrousel ──────────────────────────────────────────────────────────────
  const CAROUSEL_BLANK = { type:"image", url:"", titre:"", description:"", link_url:"", link_label:"", ordre:0, actif:true, duree:5 };
  const [carouselSlides,     setCarouselSlides]     = useState([]);
  const [carouselLoading,    setCarouselLoading]    = useState(false);
  const [carouselForm,       setCarouselForm]       = useState(null);
  const [carouselSaving,     setCarouselSaving]     = useState(false);
  const [carouselUploading,  setCarouselUploading]  = useState(false);
  const [carouselUploadPct,  setCarouselUploadPct]  = useState(0);
  const [carouselDragOver,   setCarouselDragOver]   = useState(false);
  const carouselFileRef = React.useRef(null);

  const fetchCarousel = async () => {
    setCarouselLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/carousel`, { headers: authHeaders() });
      const d = await r.json();
      setCarouselSlides(d.slides || []);
    } catch { toast.error("Erreur chargement carrousel"); }
    finally { setCarouselLoading(false); }
  };

  const saveCarousel = async () => {
    if (!carouselForm?.url?.trim()) return toast.error("L'URL est requise");
    setCarouselSaving(true);
    try {
      const isEdit = !!carouselForm.id;
      const url    = isEdit ? `${API_URL}/api/carousel/${carouselForm.id}` : `${API_URL}/api/carousel`;
      const method = isEdit ? "PATCH" : "POST";
      const r = await fetch(url, {
        method,
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(carouselForm),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || "Erreur"); }
      toast.success(isEdit ? "Slide mise à jour" : "Slide ajoutée");
      setCarouselForm(null);
      fetchCarousel();
    } catch (e) { toast.error(e.message); }
    finally { setCarouselSaving(false); }
  };

  const deleteCarousel = async (id) => {
    if (!(await confirm("Supprimer cette slide ?", { titre: "Supprimer la slide" }))) return;
    try {
      await fetch(`${API_URL}/api/carousel/${id}`, { method: "DELETE", headers: authHeaders() });
      toast.success("Slide supprimée");
      fetchCarousel();
    } catch { toast.error("Erreur suppression"); }
  };

  const toggleCarouselActif = async (item) => {
    try {
      await fetch(`${API_URL}/api/carousel/${item.id}`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ actif: !item.actif }),
      });
      fetchCarousel();
    } catch { toast.error("Erreur"); }
  };

  const moveCarouselSlide = async (slideId, direction) => {
    const idx = carouselSlides.findIndex(s => s.id === slideId);
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= carouselSlides.length) return;
    // Réordonner localement puis normaliser les valeurs d'ordre (0,1,2,...)
    const reordered = [...carouselSlides];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(targetIdx, 0, moved);
    try {
      await Promise.all(reordered.map((s, i) =>
        fetch(`${API_URL}/api/carousel/${s.id}`, {
          method: "PATCH",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ ordre: i }),
        })
      ));
      fetchCarousel();
    } catch { toast.error("Erreur réorganisation"); }
  };

  const uploadCarouselFile = async (file) => {
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    const isImage = file.type.startsWith("image/");
    if (!isVideo && !isImage) return toast.error("Fichier non supporté (image ou vidéo uniquement)");
    const maxMb = isVideo ? 200 : 10;
    if (file.size > maxMb * 1024 * 1024) return toast.error(`Fichier trop lourd (max ${maxMb} Mo)`);

    setCarouselUploading(true);
    setCarouselUploadPct(0);

    const formData = new FormData();
    formData.append("file", file);

    try {
      // Envoi direct vers Cloudinary (le relais backend etait plafonne a 4,5 Mo).
      const { url } = await uploadFichier(file, isVideo ? "video" : "image", { onProgress: setCarouselUploadPct });
      setCarouselForm(p => ({ ...p, url, type: isVideo ? "video" : "image" }));
      toast.success(isVideo ? "Vidéo uploadée ✓" : "Image uploadée ✓");
    } catch (e) { toast.error(e.message); }
    finally { setCarouselUploading(false); setCarouselUploadPct(0); }
  };

  useEffect(() => { if (activeTab === "platform" && platformSubTab === "carrousel") fetchCarousel(); }, [activeTab, platformSubTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Offre Média ────────────────────────────────────────────────────────────
  const fetchOffreMedia = useCallback(async (offreType) => {
    setOffreMediaLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/offre-media/${offreType}`, { headers: authHeaders() });
      const d = await r.json();
      setOffreMediaList(d.media || []);
    } catch { toast.error("Erreur chargement médias"); }
    finally { setOffreMediaLoading(false); }
  }, []);

  /* Sous-onglets de « BET for Business » — hero & carrousel, étude de cas, FAQ.
     DÉCLARÉ ICI, avant `getActiveOffreMediaType` qui le lit : une `const` n'existe pas avant
     sa ligne, et la laisser plus bas ne tenait que parce que la fonction n'est appelée qu'au
     rendu. Ce genre d'équilibre se casse au premier déplacement de code. */
  const [bfbSubTab, setBfbSubTab] = useState("hero");
  /* La modale d'édition d'une slide. `null` = fermée, "new" = création, sinon l'identifiant
     de la slide modifiée. Un seul état pour les deux cas : le formulaire est le même, seul
     son contenu de départ change. */
  const [slideModal, setSlideModal] = useState(null);

  const getActiveOffreMediaType = () => {
    /* ── « BET FOR BUSINESS » EST UN ONGLET DE PREMIER NIVEAU DEPUIS LE 27/10/2026 ──
       Et ce test doit venir EN PREMIER. Cette fonction décide sur quelle offre le média
       sera écrit, et elle le déduisait uniquement de `catalogueSubTab` — le sous-sous-onglet
       où le panneau vivait auparavant. Hors de ce contexte, `catalogueSubTab` garde la
       valeur du dernier sous-onglet visité : un média ajouté depuis le nouvel onglet serait
       donc enregistré sur « Cours en ligne » ou « Centres », silencieusement, et
       apparaîtrait sur la mauvaise page publique.
       Les trois sauvegardes, la suppression et le réordonnancement passent tous par ici :
       c'est le seul endroit à corriger, mais il est obligatoire. */
    /* LE SOUS-ONGLET COMPTE AUTANT QUE L'ONGLET. « Étude de cas » gère les médias de Wave
       (`bet_business_wave`), pas le carrousel du hero : sans ce test, une photo de la
       cérémonie déposée là serait enregistrée sur le hero et s'afficherait en grand en haut
       de la page. Même faute que celle corrigée en remontant l'onglet au premier niveau. */
    if (activeTab === "bet_business") {
      return bfbSubTab === "contenus" ? "bet_business_wave" : "bet_business";
    }
    if (platformSubTab === "contenu" && blogInnerTab === "temoignages") return "temoignages";
    if (platformSubTab === "contenu" && blogInnerTab === "articles")    return "blog";
    const typeMap = { offres_en_ligne:"en-ligne", offres_domicile:"domicile", centres:"cabinet", bet_business:"bet_business" };
    return typeMap[catalogueSubTab] || offreMediaType;
  };

  const saveOffreMedia = async () => {
    if (!offreMediaForm.url.trim()) return toast.error("URL requise");
    const activeType = getActiveOffreMediaType();
    setOffreMediaSaving(true);
    try {
      const r = offreMediaEditingId
        ? await fetch(`${API_URL}/api/offre-media/${offreMediaEditingId}`, {
            method: "PATCH",
            headers: authHeaders(),
            body: JSON.stringify(offreMediaForm),
          })
        : await fetch(`${API_URL}/api/offre-media`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({ ...offreMediaForm, offre_type: activeType }),
          });
      if (!r.ok) throw new Error((await r.json()).error);
      toast.success(offreMediaEditingId ? "Média modifié" : "Média ajouté");
      setOffreMediaForm(OFFRE_MEDIA_BLANK);
      setOffreMediaEditingId(null);
      fetchOffreMedia(activeType);
    } catch (e) { toast.error(e.message); }
    finally { setOffreMediaSaving(false); }
  };

  const deleteOffreMedia = async (id) => {
    if (!(await confirm("Supprimer ce média ?", { titre: "Supprimer le média" }))) return;
    const activeType = getActiveOffreMediaType();
    try {
      await fetch(`${API_URL}/api/offre-media/${id}`, { method:"DELETE", headers: authHeaders() });
      toast.success("Supprimé");
      fetchOffreMedia(activeType);
    } catch { toast.error("Erreur suppression"); }
  };

  const toggleOffreMediaActif = async (item) => {
    const activeType = getActiveOffreMediaType();
    try {
      await fetch(`${API_URL}/api/offre-media/${item.id}`, {
        method:"PATCH", headers: authHeaders(),
        body: JSON.stringify({ actif: !item.actif }),
      });
      fetchOffreMedia(activeType);
    } catch { toast.error("Erreur"); }
  };

  const moveOffreMedia = async (itemId, direction) => {
    const list = [...offreMediaList];
    const idx  = list.findIndex(s => s.id === itemId);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= list.length) return;
    [list[idx], list[swapIdx]] = [list[swapIdx], list[idx]];
    const reordered = list.map((s, i) => ({ ...s, ordre: i }));
    setOffreMediaList(reordered);
    try {
      await Promise.all(reordered.map(s =>
        fetch(`${API_URL}/api/offre-media/${s.id}`, {
          method:"PATCH", headers: authHeaders(),
          body: JSON.stringify({ ordre: s.ordre }),
        })
      ));
    } catch { toast.error("Erreur réordonnancement"); }
  };

  const uploadOffreMediaFile = async (file) => {
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    // La limite vient de la table partagée (Cloudinary : 100 Mo pour une vidéo) ; 200 Mo
    // etait une promesse que ni Vercel ni Cloudinary n'ont jamais tenue.
    if (file.size > maxMoPour(isVideo ? "video" : "image", file.type) * 1024 * 1024)
      return toast.error(`Fichier trop lourd (max ${maxMoPour(isVideo ? "video" : "image", file.type)} Mo)`);
    setOffreMediaUploading(true); setOffreMediaUploadPct(0);
    try {
      const endpoint = isVideo ? "/api/upload/video" : "/api/upload/image";
      const formData = new FormData();
      formData.append("file", file);
      // Envoi direct vers Cloudinary : le relais backend plafonnait a 4,5 Mo (Vercel),
      // ce qui rendait impossible tout temoignage video. uploadFichier valide la taille
      // avant le reseau et remonte un message lisible.
      const { url } = await uploadFichier(formData.get("file"), isVideo ? "video" : "image", { onProgress: setOffreMediaUploadPct });
      if (!url) throw new Error("URL non reçue du serveur");
      setOffreMediaForm(f => ({ ...f, url, type: isVideo ? "video" : "image" }));
      toast.success("Fichier uploadé !");
    } catch (e) { toast.error(e.message); }
    finally { setOffreMediaUploading(false); setOffreMediaUploadPct(0); }
  };

  useEffect(() => {
    if (activeTab !== "platform" || platformSubTab !== "catalogue") return;
    const typeMap = { offres_en_ligne:"en-ligne", offres_domicile:"domicile", centres:"cabinet", bet_business:"bet_business" };
    if (typeMap[catalogueSubTab]) fetchOffreMedia(typeMap[catalogueSubTab]);
    else if (catalogueSubTab === "certifications") fetchOffreMedia(offreMediaType);
  }, [activeTab, platformSubTab, catalogueSubTab, offreMediaType, fetchOffreMedia]); // eslint-disable-line react-hooks/exhaustive-deps

  /* L'onglet « BET for Business » charge ses médias lui-même : il ne dépend plus du
     sous-onglet Catalogue, qui les chargeait pour lui auparavant. */
  useEffect(() => {
    if (activeTab === "bet_business" && bfbSubTab !== "contenus") fetchOffreMedia("bet_business");
    if (activeTab === "bet_business" && bfbSubTab === "contenus") fetchOffreMedia("bet_business_wave");
  }, [activeTab, bfbSubTab, fetchOffreMedia]);

  /* ══ CONTENU DU HERO DE /bet-for-business ══════════════════════════════════════════
     Stocké dans `plateforme_config` sous `bet_business_hero`, et non dans `offre_media` :
     ces textes appartiennent à la PAGE. Le hero étant un carrousel, les loger sur chaque
     média ferait changer l'accroche et les boutons à chaque défilement. ══ */
  const BFB_HERO_VIDE = {
    surtitre: "", titre: "", texte: "",
    bouton1: { libelle: "", lien: "" },
    bouton2: { libelle: "", lien: "" },
    defilement: 6,
  };

  const [bfbHero,        setBfbHero]        = useState(BFB_HERO_VIDE);
  const [bfbHeroCharge,  setBfbHeroCharge]  = useState(false);
  const [bfbHeroSaving,  setBfbHeroSaving]  = useState(false);

  const chargerBfbHero = useCallback(async () => {
    setBfbHeroCharge(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/plateforme-config/bet_business_hero`, { headers: authHeaders() });
      /* 404 = jamais enregistré. Ce n'est pas une erreur : on laisse le formulaire vide,
         et la page publique continue d'afficher ses textes par défaut. */
      if (r.ok) { const d = await r.json(); if (d?.valeur) setBfbHero(v => ({ ...v, ...d.valeur })); }
    } catch { /* le formulaire reste vide, rien n'est perdu */ }
    finally { setBfbHeroCharge(false); }
  }, []);
  useEffect(() => { if (activeTab === "bet_business") chargerBfbHero(); }, [activeTab, chargerBfbHero]);
  /* La FAQ est chargée entière (`/api/faq`), puis filtrée à l'affichage : c'est la même
     route que l'écran FAQ général, et la liste tient en quelques dizaines de lignes. */
  useEffect(() => { if (activeTab === "bet_business" && bfbSubTab === "faq") fetchFaq(); }, [activeTab, bfbSubTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const enregistrerBfbHero = async () => {
    if (bfbHeroSaving) return;
    setBfbHeroSaving(true);
    try {
      await savePlateformeConfig("bet_business_hero", {
        ...bfbHero,
        /* Bornes identiques à celles du site : une valeur aberrante ne doit ni figer le
           carrousel ni le faire clignoter. */
        defilement: Math.min(30, Math.max(2, Number(bfbHero.defilement) || 6)),
      });
      toast.success("Contenu du hero enregistré ✓");
    } catch (e) {
      toast.error(e.message || "Échec de l'enregistrement");
    } finally { setBfbHeroSaving(false); }
  };

  const champBfb = (k, v) => setBfbHero(h => ({ ...h, [k]: v }));
  const champBfbBouton = (b, k, v) => setBfbHero(h => ({ ...h, [b]: { ...h[b], [k]: v } }));

  /* ══ CONTENUS ÉDITORIAUX DE /bet-for-business ══════════════════════════════════════
     Clé `bet_business_contenus`, distincte de `bet_business_hero` : le hero se modifie
     souvent, ces blocs rarement mais ils pèsent davantage. Deux écrans qui partagent une
     même clé finissent par s'écraser l'un l'autre. */
  const BFB_CONTENUS_VIDE = {
    wave: { eyebrow:"", titre:"", blocs:[], ctaTitre:"" },
    drh:  { titre:"", texte:"", bouton:"", telephone:"" },
    credibilite: [],
  };
  const [bfbCont,       setBfbCont]       = useState(BFB_CONTENUS_VIDE);
  const [bfbContCharge, setBfbContCharge] = useState(false);
  const [bfbContSaving, setBfbContSaving] = useState(false);

  const chargerBfbContenus = useCallback(async () => {
    setBfbContCharge(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/plateforme-config/bet_business_contenus`, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        if (d?.valeur) setBfbCont(v => ({ ...v, ...d.valeur }));
      }
      /* 404 = jamais enregistré. Le formulaire reste vide et la page publique continue
         d'afficher ses textes par défaut : il n'y a rien à réparer. */
    } catch { /* idem */ }
    finally { setBfbContCharge(false); }
  }, []);
  useEffect(() => {
    if (activeTab === "bet_business" && bfbSubTab === "contenus") chargerBfbContenus();
  }, [activeTab, bfbSubTab, chargerBfbContenus]);

  const enregistrerBfbContenus = async () => {
    if (bfbContSaving) return;
    setBfbContSaving(true);
    try {
      await savePlateformeConfig("bet_business_contenus", bfbCont);
      toast.success("Contenus enregistrés ✓");
    } catch (e) { toast.error(e.message || "Échec de l'enregistrement"); }
    finally { setBfbContSaving(false); }
  };

  const champWave = (k, v) => setBfbCont(c => ({ ...c, wave: { ...c.wave, [k]: v } }));
  const champWaveBloc = (i, k, v) => setBfbCont(c => {
    const blocs = [...(c.wave.blocs || [])];
    blocs[i] = { ...blocs[i], [k]: v };
    return { ...c, wave: { ...c.wave, blocs } };
  });
  const champDrh = (k, v) => setBfbCont(c => ({ ...c, drh: { ...c.drh, [k]: v } }));
  const champCred = (i, k, v) => setBfbCont(c => {
    const l = [...(c.credibilite || [])];
    l[i] = { ...l[i], [k]: v };
    return { ...c, credibilite: l };
  });


  const lblBfb = { display:"block", fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:".04em", marginBottom:5 };
  const inpBfb = { width:"100%", padding:"9px 12px", borderRadius:8, border:"1px solid #e2e8f0", fontSize:12.5, color:"#0f172a", background:"#fff", boxSizing:"border-box" };



  // ── Codes Promo ────────────────────────────────────────────────────────────
  const PROMO_BLANK = { code:"", description:"", type_reduction:"pourcentage", valeur:"", applicable_a:["tous"], centres_ids:[], date_expiration:"", usage_max:"", actif:true };
  const OFFRE_TYPES_PROMO = [
    { key:"en_ligne",      label:"En ligne" },
    { key:"centres",       label:"Centres (Présentiel)" },
    { key:"domicile",      label:"À domicile" },
    { key:"certifications",label:"Certifications" },
  ];
  const [codesPromo,       setCodesPromo]       = useState([]);
  const [codesPromoLoading,setCodesPromoLoading] = useState(false);
  const [promoForm,        setPromoForm]         = useState(PROMO_BLANK);
  const [promoEditing,     setPromoEditing]      = useState(null); // id en édition
  const [promoSaving,      setPromoSaving]       = useState(false);
  const [promoShowForm,    setPromoShowForm]      = useState(false);

  const fetchCodesPromo = useCallback(async () => {
    setCodesPromoLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/codes-promo`, { headers: authHeaders() });
      const d = await r.json();
      setCodesPromo(d.codes || []);
    } catch { toast.error("Erreur chargement codes promo"); }
    finally { setCodesPromoLoading(false); }
  }, []);

  useEffect(() => {
    if (platformSubTab === "codes_promo") fetchCodesPromo();
  }, [platformSubTab, fetchCodesPromo]); // eslint-disable-line react-hooks/exhaustive-deps

  const savePromoCode = async () => {
    if (promoSaving) return;
    if (!promoForm.code.trim()) return toast.error("Le code est requis");
    if (!promoForm.valeur || isNaN(Number(promoForm.valeur))) return toast.error("La valeur est invalide");
    setPromoSaving(true);
    try {
      const payload = {
        ...promoForm,
        valeur:    Number(promoForm.valeur),
        usage_max: promoForm.usage_max ? Number(promoForm.usage_max) : null,
        date_expiration: promoForm.date_expiration || null,
        applicable_a: promoForm.applicable_a.length ? promoForm.applicable_a : ["tous"],
      };
      const url    = promoEditing ? `${API_URL}/api/codes-promo/${promoEditing}` : `${API_URL}/api/codes-promo`;
      const method = promoEditing ? "PUT" : "POST";
      const r = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Erreur");
      toast.success(promoEditing ? "Code mis à jour ✓" : "Code créé ✓");
      setPromoForm(PROMO_BLANK);
      setPromoEditing(null);
      setPromoShowForm(false);
      fetchCodesPromo();
    } catch (e) { toast.error(e.message); }
    finally { setPromoSaving(false); }
  };

  const deletePromoCode = async (id) => {
    if (!(await confirm("Supprimer ce code promo ?", { titre: "Supprimer le code promo" }))) return;
    try {
      const r = await fetch(`${API_URL}/api/codes-promo/${id}`, { method:"DELETE", headers: authHeaders() });
      if (!r.ok) throw new Error();
      toast.success("Code supprimé");
      fetchCodesPromo();
    } catch { toast.error("Erreur suppression"); }
  };

  const togglePromoActif = async (code) => {
    try {
      const r = await fetch(`${API_URL}/api/codes-promo/${code.id}`, { method:"PUT", headers: authHeaders(), body: JSON.stringify({ actif: !code.actif }) });
      if (!r.ok) throw new Error();
      fetchCodesPromo();
    } catch { toast.error("Erreur"); }
  };

  // ── Panneau médias intégré dans chaque onglet offre ────────────────────────
  const renderOffreMediaPanel = (fixedType, accentCol = BET_COLOR, label = "") => (
    <div style={{ marginTop:28, borderTop:"1.5px dashed #e2e8f0", paddingTop:22 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
        <span style={{ fontSize:16 }}>🎬</span>
        <div>
          <h4 style={{ margin:0, fontSize:13, fontWeight:800, color:"#0f172a" }}>Vidéo / image de présentation{label ? ` - ${label}` : ""}</h4>
          <p style={{ margin:0, fontSize:11, color:"#9ca3af" }}>Affichée dans le hero de la page. YouTube, Vimeo ou fichier uploadé.</p>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, alignItems:"start" }}>
        {/* ── Formulaire ajout ── */}
        <div style={{ background:"#f8fafc", borderRadius:12, border:"1px solid #e5e7eb", padding:14 }}>
          <div style={{ background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:7, padding:"6px 10px", marginBottom:12, fontSize:10, color:"#1e40af" }}>
            📐 Images : 1280×720 px · Vidéos : MP4 max {maxMoPour("video","video/mp4")} Mo - ou URL YouTube/Vimeo
          </div>

          {/* Zone upload */}
          <div
            onDragOver={e => { e.preventDefault(); setOffreMediaDragOver(true); }}
            onDragLeave={() => setOffreMediaDragOver(false)}
            onDrop={e => { e.preventDefault(); setOffreMediaDragOver(false); const f=e.dataTransfer.files[0]; if(f) uploadOffreMediaFile(f); }}
            onClick={() => !offreMediaUploading && offreMediaFileRef.current?.click()}
            style={{ border:`2px dashed ${offreMediaDragOver?"#1e3a8a":"#cbd5e1"}`, borderRadius:9, padding:"12px 10px", textAlign:"center", cursor:"pointer", background:offreMediaDragOver?"#eff6ff":"#fff", marginBottom:10, transition:"all .2s" }}
          >
            <input ref={offreMediaFileRef} type="file" accept="image/*,video/*" style={{ display:"none" }}
              onChange={e => { const f=e.target.files?.[0]; if(f) uploadOffreMediaFile(f); e.target.value=""; }}
            />
            {offreMediaUploading ? (
              <div>
                <div style={{ fontSize:11, color:"#1e3a8a", marginBottom:5 }}>Upload… {offreMediaUploadPct}%</div>
                <div style={{ height:5, background:"#e2e8f0", borderRadius:3 }}>
                  <div style={{ height:"100%", width:`${offreMediaUploadPct}%`, background:accentCol, borderRadius:3, transition:"width .3s" }} />
                </div>
              </div>
            ) : (
              <div style={{ fontSize:11, color:"#64748b" }}>
                <div style={{ fontSize:18, marginBottom:3 }}>☁️</div>
                Glissez ou <span style={{ color:accentCol, fontWeight:700, textDecoration:"underline" }}>parcourir</span>
              </div>
            )}
          </div>

          <div style={{ textAlign:"center", fontSize:10, color:"#94a3b8", marginBottom:8 }}>- ou URL -</div>
          <input
            value={offreMediaForm.url}
            onChange={e => setOffreMediaForm(f => ({ ...f, url: e.target.value }))}
            placeholder="https://youtube.com/... ou https://..."
            style={{ width:"100%", padding:"8px 10px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:12, marginBottom:8, boxSizing:"border-box", outline:"none" }}
          />
          {offreMediaForm.url && offreMediaForm.type === "image" && (
            <img src={offreMediaForm.url} alt="" onError={e => e.target.style.display="none"}
              style={{ width:"100%", height:80, objectFit:"cover", borderRadius:7, marginBottom:8 }} />
          )}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
            <select value={offreMediaForm.type} onChange={e => setOffreMediaForm(f => ({ ...f, type: e.target.value }))}
              style={{ padding:"7px 8px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:11, outline:"none" }}>
              <option value="video">🎬 Vidéo</option>
              <option value="image">🖼️ Image</option>
            </select>
            <input value={offreMediaForm.titre} onChange={e => setOffreMediaForm(f => ({ ...f, titre: e.target.value }))}
              placeholder="Titre (optionnel)"
              style={{ padding:"7px 8px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:11, boxSizing:"border-box", outline:"none" }} />
          </div>
          <textarea value={offreMediaForm.description} onChange={e => setOffreMediaForm(f => ({ ...f, description: e.target.value }))}
            placeholder="Description affichée dans le hero (optionnel)"
            rows={2}
            style={{ width:"100%", padding:"7px 8px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:11, boxSizing:"border-box", outline:"none", resize:"vertical", fontFamily:"inherit", marginBottom:8 }} />
          <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:"#374151", marginBottom:10, cursor:"pointer" }}>
            <input type="checkbox" checked={offreMediaForm.actif} onChange={e => setOffreMediaForm(f => ({ ...f, actif: e.target.checked }))} />
            Visible sur le site
          </label>
          <div style={{ display:"flex", gap:8 }}>
            {offreMediaEditingId && (
              <button onClick={() => { setOffreMediaForm(OFFRE_MEDIA_BLANK); setOffreMediaEditingId(null); }} style={{
                padding:"8px 14px", background:"#f1f5f9", color:"#374151", border:"none", borderRadius:7, fontWeight:700, fontSize:12, cursor:"pointer",
              }}>
                Annuler
              </button>
            )}
            <button onClick={saveOffreMedia} disabled={offreMediaSaving || !offreMediaForm.url.trim() || !peut("configuration", offreMediaEditingId ? "update" : "create")}
              title={permBtnTitle(offreMediaEditingId ? "Enregistrer les modifications" : "Ajouter", peut("configuration", offreMediaEditingId ? "update" : "create"))}
              style={permBtnStyle({
                flex:1, padding:"8px", background: offreMediaForm.url.trim() ? accentCol : "#e5e7eb",
                color:"#fff", border:"none", borderRadius:7, fontWeight:800, fontSize:12, cursor: offreMediaForm.url.trim() ? "pointer" : "default",
              }, peut("configuration", offreMediaEditingId ? "update" : "create"))}>
              {offreMediaSaving ? "Enregistrement…" : !peut("configuration", offreMediaEditingId ? "update" : "create") ? (offreMediaEditingId ? "🔒 Enregistrer" : "🔒 Ajouter") : offreMediaEditingId ? "💾 Enregistrer les modifications" : "💾 Ajouter"}
            </button>
          </div>
        </div>

        {/* ── Liste ── */}
        <div>
          {offreMediaLoading && <div style={{ textAlign:"center", padding:20, color:"#94a3b8", fontSize:12 }}>Chargement…</div>}
          {!offreMediaLoading && offreMediaList.length === 0 && (
            <div style={{ textAlign:"center", padding:"20px 12px", background:"#f8fafc", borderRadius:10, border:"1.5px dashed #e2e8f0" }}>
              <div style={{ fontSize:24, marginBottom:6 }}>🎬</div>
              <p style={{ margin:0, fontSize:11, color:"#94a3b8" }}>Aucun média configuré</p>
            </div>
          )}
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {offreMediaList.map((item, idx) => (
              <div key={item.id} style={{ background:"#fff", borderRadius:10, border:`1.5px solid ${item.actif?"#e0f2fe":"#fee2e2"}`, padding:10, display:"flex", gap:8, alignItems:"flex-start" }}>
                <div style={{ width:60, height:40, borderRadius:5, overflow:"hidden", flexShrink:0, background:"#0f172a", position:"relative" }}>
                  {item.type === "image"
                    ? <img src={item.url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} onError={e => e.target.style.display="none"} />
                    : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>🎬</div>
                  }
                  <div style={{ position:"absolute", top:1, left:1, background:item.type==="video"?"#dc2626":"#1e3a8a", color:"#fff", fontSize:7, fontWeight:800, borderRadius:2, padding:"1px 3px" }}>
                    {item.type === "video" ? "VID" : "IMG"}
                  </div>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:11, color:"#0f172a", marginBottom:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {item.titre || `Média #${idx + 1}`}
                  </div>
                  {item.description && (
                    <div style={{ fontSize:9, color:"#475569", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", marginBottom:1, fontStyle:"italic" }}>{item.description}</div>
                  )}
                  <div style={{ fontSize:9, color:"#94a3b8", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{item.url}</div>
                  <div style={{ display:"flex", gap:3, marginTop:5, flexWrap:"wrap" }}>
                    <button onClick={() => { setOffreMediaForm({ type:item.type, url:item.url, titre:item.titre||"", description:item.description||"", actif:item.actif }); setOffreMediaEditingId(item.id); }}
                      disabled={!peut("configuration","update")} title={permBtnTitle("Modifier",peut("configuration","update"))}
                      style={permBtnStyle({ padding:"2px 6px", borderRadius:5, border:"1px solid #bfdbfe", background:"#eff6ff", color:"#1e40af", cursor:"pointer", fontSize:10, fontWeight:700 }, peut("configuration","update"))}>
                      ✏️
                    </button>
                    <button onClick={() => toggleOffreMediaActif(item)}
                      disabled={!peut("configuration","update")} title={permBtnTitle(item.actif?"Désactiver":"Activer",peut("configuration","update"))}
                      style={permBtnStyle({ padding:"2px 6px", borderRadius:999, border:"none", cursor:"pointer", fontSize:9, fontWeight:700, background:item.actif?"#d1fae5":"#fee2e2", color:item.actif?"#065f46":"#991b1b" }, peut("configuration","update"))}>
                      {item.actif ? "✅" : "❌"}
                    </button>
                    <button onClick={() => moveOffreMedia(item.id, "up")} disabled={idx===0 || !peut("configuration","update")}
                      title={permBtnTitle("Monter",peut("configuration","update"))}
                      style={permBtnStyle({ padding:"2px 6px", borderRadius:5, border:"1px solid #e5e7eb", background:"#f8fafc", cursor:idx===0?"default":"pointer", fontSize:10, color:idx===0?"#cbd5e1":"#374151" }, peut("configuration","update"))}>↑</button>
                    <button onClick={() => moveOffreMedia(item.id, "down")} disabled={idx===offreMediaList.length-1 || !peut("configuration","update")}
                      title={permBtnTitle("Descendre",peut("configuration","update"))}
                      style={permBtnStyle({ padding:"2px 6px", borderRadius:5, border:"1px solid #e5e7eb", background:"#f8fafc", cursor:idx===offreMediaList.length-1?"default":"pointer", fontSize:10, color:idx===offreMediaList.length-1?"#cbd5e1":"#374151" }, peut("configuration","update"))}>↓</button>
                    <button onClick={() => deleteOffreMedia(item.id)}
                      disabled={!peut("configuration","delete")} title={permBtnTitle("Supprimer",peut("configuration","delete"))}
                      style={permBtnStyle({ padding:"2px 6px", borderRadius:5, border:"none", background:"#fee2e2", color:"#dc2626", cursor:"pointer", fontSize:10, fontWeight:700 }, peut("configuration","delete"))}>🗑️</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // ── Onglet centralisé "Code promo" (Plateforme) - remplace les 4 panneaux qui étaient
  // dispersés dans chaque onglet d'offre (Centres/En ligne/Domicile/Certifications).
  const renderCodesPromoTab = (accentCol = BET_COLOR) => {
    const formKey = "promo-actif-global";
    const showForm = promoShowForm;
    const toggleApplicable = (key) => {
      setPromoForm(p => {
        if (key === "tous") return { ...p, applicable_a: ["tous"] };
        const sans_tous = (p.applicable_a || []).filter(a => a !== "tous");
        const has = sans_tous.includes(key);
        const next = has ? sans_tous.filter(a => a !== key) : [...sans_tous, key];
        return { ...p, applicable_a: next.length ? next : ["tous"], centres_ids: next.includes("centres") ? p.centres_ids : [] };
      });
    };
    const toggleCentre = (id) => {
      setPromoForm(p => {
        const has = (p.centres_ids || []).includes(id);
        return { ...p, centres_ids: has ? p.centres_ids.filter(c => c !== id) : [...(p.centres_ids || []), id] };
      });
    };

    const openNew = () => {
      setPromoForm(PROMO_BLANK);
      setPromoEditing(null);
      setPromoShowForm(true);
    };
    const openEdit = (c) => {
      setPromoForm({ code:c.code, description:c.description||"", type_reduction:c.type_reduction, valeur:c.valeur, applicable_a:c.applicable_a||["tous"], centres_ids:c.centres_ids||[], date_expiration:c.date_expiration?c.date_expiration.slice(0,16):"", usage_max:c.usage_max||"", actif:c.actif });
      setPromoEditing(c.id);
      setPromoShowForm(true);
    };
    const closeForm = () => { setPromoShowForm(false); setPromoEditing(null); setPromoForm(PROMO_BLANK); };
    const applA = promoForm.applicable_a || ["tous"];

    return (
      <div>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div>
            <h4 style={{ margin:0, fontSize:16, fontWeight:800, color:"#0f172a" }}>🎟️ Codes promo</h4>
            <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Tous les codes, quelle que soit l'offre ou le centre concerné · Expirables ou illimités</p>
          </div>
          <button onClick={openNew}
            style={{ padding:"8px 16px", background:accentCol, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>
            ＋ Nouveau code
          </button>
        </div>

        {showForm && (
          <div style={{ background:"#f8fafc", border:"1.5px solid #e2e8f0", borderRadius:12, padding:18, marginBottom:16 }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
              <div>
                <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:3 }}>Code *</label>
                <input value={promoForm.code} onChange={e => setPromoForm(p=>({...p,code:e.target.value.toUpperCase()}))}
                  placeholder="Ex : BET2025"
                  style={{ width:"100%", padding:"8px 11px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:12, fontFamily:"monospace", boxSizing:"border-box" }} />
              </div>
              <div>
                <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:3 }}>Description</label>
                <input value={promoForm.description} onChange={e => setPromoForm(p=>({...p,description:e.target.value}))}
                  placeholder="Ex : Rentrée 2025"
                  style={{ width:"100%", padding:"8px 11px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:12, boxSizing:"border-box" }} />
              </div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:10, marginBottom:14 }}>
              <div>
                <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:3 }}>Type</label>
                <select value={promoForm.type_reduction} onChange={e => setPromoForm(p=>({...p,type_reduction:e.target.value}))}
                  style={{ width:"100%", padding:"8px 11px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:12, boxSizing:"border-box" }}>
                  <option value="pourcentage">% Pourcentage</option>
                  <option value="montant_fixe">FCFA Montant fixe</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:3 }}>
                  Valeur * {promoForm.type_reduction === "pourcentage" ? "(%)" : "(FCFA)"}
                </label>
                <input value={promoForm.valeur} onChange={e => setPromoForm(p=>({...p,valeur:e.target.value}))}
                  type="number" min="0" placeholder={promoForm.type_reduction==="pourcentage"?"Ex : 20":"Ex : 50000"}
                  style={{ width:"100%", padding:"8px 11px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:12, boxSizing:"border-box" }} />
              </div>
              <div>
                <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:3 }}>Expiration <span style={{ color:"#9ca3af",fontWeight:400 }}>(vide=illimité)</span></label>
                <input value={promoForm.date_expiration} onChange={e => setPromoForm(p=>({...p,date_expiration:e.target.value}))}
                  type="datetime-local"
                  style={{ width:"100%", padding:"8px 11px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:12, boxSizing:"border-box" }} />
              </div>
              <div>
                <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:3 }}>Utilisations max <span style={{ color:"#9ca3af",fontWeight:400 }}>(vide=illimité)</span></label>
                <input value={promoForm.usage_max} onChange={e => setPromoForm(p=>({...p,usage_max:e.target.value}))}
                  type="number" min="1" placeholder="Ex : 100"
                  style={{ width:"100%", padding:"8px 11px", border:"1.5px solid #e2e8f0", borderRadius:7, fontSize:12, boxSizing:"border-box" }} />
              </div>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:6 }}>Applicable à</label>
              <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
                <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#374151", cursor:"pointer" }}>
                  <input type="checkbox" checked={applA.includes("tous")} onChange={()=>toggleApplicable("tous")} style={{ width:14,height:14,accentColor:accentCol,cursor:"pointer" }} />
                  Toutes les offres
                </label>
                {OFFRE_TYPES_PROMO.map(o => (
                  <label key={o.key} style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#374151", cursor:"pointer" }}>
                    <input type="checkbox" checked={applA.includes(o.key)} onChange={()=>toggleApplicable(o.key)} style={{ width:14,height:14,accentColor:accentCol,cursor:"pointer" }} />
                    {o.label}
                  </label>
                ))}
              </div>
            </div>

            {applA.includes("centres") && (
              <div style={{ marginBottom:14, background:"#fff", border:"1px solid #e2e8f0", borderRadius:8, padding:12 }}>
                <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:6 }}>Centre(s) concerné(s) <span style={{ color:"#9ca3af",fontWeight:400 }}>(aucun coché = tous les centres)</span></label>
                <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
                  {centresList.map(c => (
                    <label key={c.id} style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#374151", cursor:"pointer" }}>
                      <input type="checkbox" checked={(promoForm.centres_ids||[]).includes(c.id)} onChange={()=>toggleCentre(c.id)} style={{ width:14,height:14,accentColor:accentCol,cursor:"pointer" }} />
                      {c.nom}
                    </label>
                  ))}
                  {centresList.length === 0 && <span style={{ fontSize:11, color:"#9ca3af" }}>Aucun centre configuré</span>}
                </div>
              </div>
            )}

            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
              <input type="checkbox" id={formKey} checked={!!promoForm.actif} onChange={e=>setPromoForm(p=>({...p,actif:e.target.checked}))} style={{ width:14,height:14,accentColor:accentCol,cursor:"pointer" }} />
              <label htmlFor={formKey} style={{ fontSize:12, fontWeight:600, color:"#374151", cursor:"pointer" }}>Code actif</label>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={closeForm} style={{ padding:"7px 16px", background:"#f1f5f9", border:"none", borderRadius:7, cursor:"pointer", fontWeight:600, fontSize:12, color:"#374151" }}>Annuler</button>
              <button onClick={savePromoCode} disabled={promoSaving}
                style={{ padding:"7px 18px", background:accentCol, color:"#fff", border:"none", borderRadius:7, cursor:"pointer", fontWeight:800, fontSize:12, opacity:promoSaving?.7:1 }}>
                {promoSaving ? "⏳ Enregistrement…" : "💾 Enregistrer"}
              </button>
            </div>
          </div>
        )}

        {codesPromoLoading ? (
          <div style={{ padding:20, textAlign:"center", color:"#94a3b8", fontSize:12 }}>⏳ Chargement…</div>
        ) : codesPromo.length === 0 ? (
          <div style={{ padding:"18px 0", textAlign:"center", color:"#94a3b8", fontSize:12, border:"1.5px dashed #e2e8f0", borderRadius:10 }}>
            Aucun code promo pour le moment - cliquez sur "Nouveau code" pour en créer un.
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {codesPromo.map(c => {
              const expired = c.date_expiration && new Date(c.date_expiration) < new Date();
              const maxed   = c.usage_max != null && c.usage_count >= c.usage_max;
              const valid   = c.actif && !expired && !maxed;
              const applic  = c.applicable_a || ["tous"];
              const cids    = c.centres_ids || [];
              return (
                <div key={c.id} style={{ background:"#fff", border:`1.5px solid ${valid?"#bae6fd":expired||maxed?"#fecaca":"#fde68a"}`, borderRadius:10, padding:"10px 14px", display:"flex", alignItems:"center", gap:14 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3, flexWrap:"wrap" }}>
                      <span style={{ fontFamily:"monospace", fontWeight:900, fontSize:13, color:"#0f172a", background:"#f1f5f9", padding:"2px 9px", borderRadius:5, letterSpacing:1 }}>{c.code}</span>
                      <span style={{ padding:"1px 8px", borderRadius:20, fontSize:10, fontWeight:700,
                        background:valid?"#dcfce7":expired?"#fee2e2":maxed?"#fef3c7":"#fef3c7",
                        color:valid?"#166534":expired?"#991b1b":maxed?"#92400e":"#92400e" }}>
                        {valid?"✓ Actif":expired?"⏰ Expiré":maxed?"🚫 Épuisé":"⏸ Inactif"}
                      </span>
                      <span style={{ padding:"1px 8px", borderRadius:20, fontSize:10, fontWeight:700, background:"#ede9fe", color:"#6d28d9" }}>
                        {applic.includes("tous") ? "Toutes les offres" : applic.map(a => OFFRE_TYPES_PROMO.find(o=>o.key===a)?.label || a).join(", ")}
                      </span>
                      {applic.includes("centres") && cids.length > 0 && (
                        <span style={{ padding:"1px 8px", borderRadius:20, fontSize:10, fontWeight:700, background:"#e0f2fe", color:"#0369a1" }}>
                          {cids.map(id => centresList.find(ce=>ce.id===id)?.nom || id).join(", ")}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize:11, color:"#475569" }}>
                      <strong style={{ color:accentCol }}>
                        {c.type_reduction==="pourcentage"?`-${c.valeur}%`:`-${Number(c.valeur).toLocaleString("fr-FR")} FCFA`}
                      </strong>
                      {c.description && <span style={{ marginLeft:6 }}>· {c.description}</span>}
                      <span style={{ marginLeft:6, color:"#94a3b8" }}>· {c.usage_count||0} util.{c.usage_max?`/${c.usage_max}`:""}</span>
                      {c.date_expiration && <span style={{ marginLeft:6, color:expired?"#ef4444":"#94a3b8" }}>· Exp. {new Date(c.date_expiration).toLocaleDateString("fr-FR")}</span>}
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                    <button onClick={() => togglePromoActif(c)} style={{ padding:"5px 10px", border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:10, background:c.actif?"#fee2e2":"#dcfce7", color:c.actif?"#991b1b":"#166534" }}>
                      {c.actif?"Désactiver":"Activer"}
                    </button>
                    <button onClick={() => openEdit(c)} style={{ padding:"5px 10px", border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:10, background:"#e0f2fe", color:"#0891b2" }}>✏️</button>
                    <button onClick={() => deletePromoCode(c.id)} style={{ padding:"5px 10px", border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:10, background:"#f1f5f9", color:"#ef4444" }}>🗑️</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const saveCoach = async (photo) => {
    const edits = coachsEdits[photo.id] || {};
    setCoachsSaving(p => ({ ...p, [photo.id]: true }));
    try {
      const res = await fetch(`${API_URL}/api/equipe-photos/${photo.id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify(edits),
      });
      if (!res.ok) throw new Error();
      toast.success("Photo mise à jour");
      setCoachsEdits(p => { const n = { ...p }; delete n[photo.id]; return n; });
      fetchCoachs();
    } catch { toast.error("Erreur sauvegarde"); }
    finally { setCoachsSaving(p => ({ ...p, [photo.id]: false })); }
  };

  return (
    <>
    <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", background:"#f0f9ff" }}>
      <div style={{ display:"flex", flexDirection:"column", flex:"1 1 auto", padding:0, overflowX:"hidden" }}>

        {/* HERO HEADER (Super Admin) */}
        <div style={{ flexShrink:0, background:BET_GRADIENT, padding:"28px 32px 0", color:"#fff", position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute", top:-40, right:-40, width:200, height:200, borderRadius:"50%", background:"rgba(255,255,255,0.04)", zIndex:0 }} />
          <div style={{ position:"absolute", bottom:-60, right:80, width:120, height:120, borderRadius:"50%", background:"rgba(255,255,255,0.03)", zIndex:0 }} />
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:28, position:"relative", zIndex:2 }}>
            <div style={{ display:"flex", alignItems:"center", gap:20 }}>
              <div style={{ position:"relative", flexShrink:0 }} onClick={()=>{ if (saAvatarUrl) setPhotoProfilOuverte(true); }}>
                <div style={{ width:64, height:64, borderRadius:"50%", background:"rgba(255,255,255,0.15)", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:22, color:"#fff", border:"3px solid rgba(255,255,255,0.3)", overflow:"hidden" , cursor: saAvatarUrl ? "zoom-in" : "default"}}>
                  {saAvatarUrl
                    ? <img src={saAvatarUrl} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                    : (profilConnecte.prenom?.[0] || "S") + (profilConnecte.nom?.[0] || "A")}
                </div>
                {photoProfilOuverte && <VisionneuseImage url={saAvatarUrl} onClose={()=>setPhotoProfilOuverte(false)} />}
                <label title="Changer la photo" onClick={e=>e.stopPropagation()} style={{ position:"absolute", bottom:0, right:0, width:22, height:22, borderRadius:"50%", background:"rgba(0,0,0,0.55)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:12, border:"2px solid #fff" }}>
                  📷
                  <input type="file" accept="image/*" style={{ display:"none" }} onChange={(e) => { const f=e.target.files?.[0]; if(f){ uploadFichier(f, "avatar").then(handleSaAvatarSuccess).catch(e=>{}); } e.target.value=""; }} />
                </label>
              </div>
              <div>
                <div style={{ fontSize:11, color:"#7dd3fc", fontWeight:600, letterSpacing:"0.08em" }}>{ROLES_DEF[profilConnecte.role]?.label || "Administrateur"} {ROLES_DEF[profilConnecte.role]?.emoji || "🔧"}</div>
                <h1 style={{ margin:0, fontSize:22, fontWeight:800, color:"#fff" }}>{profilConnecte.prenom} {profilConnecte.nom}</h1>
                <div style={{ fontSize:12, color:"#bae6fd", marginTop:3 }}>{profilConnecte.email} · {ROLES_DEF[profilConnecte.role]?.description || "Gestion complète"}</div>
              </div>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <NotificationBell userId={saMyId} />
              <DashboardColorPicker />
              <button onClick={toggleLang} style={{ display:"flex", alignItems:"center", gap:5, padding:"8px 14px", background:"rgba(255,255,255,0.1)", border:"1px solid rgba(255,255,255,0.2)", borderRadius:10, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer", transition:"background .2s", letterSpacing:"0.06em" }}
                onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.2)"}
                onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.1)"}>
                {lang === "FR" ? "🇫🇷 FR" : "🇬🇧 EN"}
              </button>
              {profilConnecte.acces_elearning && (
                <button
                  type="button"
                  onClick={() => openElearningSSO(ELEARNING_URL)}
                  style={{ display:"flex", alignItems:"center", gap:7, padding:"10px 18px", background:"rgba(8,145,178,0.25)", border:"1.5px solid rgba(8,145,178,0.5)", borderRadius:10, color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer", transition:"background .2s", fontFamily:"inherit" }}
                  onMouseEnter={e => e.currentTarget.style.background="rgba(8,145,178,0.45)"}
                  onMouseLeave={e => e.currentTarget.style.background="rgba(8,145,178,0.25)"}
                  title="Ouvrir la plateforme e-learning"
                >
                  <span style={{ fontSize:16 }}>🎓</span> E-Learning
                </button>
              )}
              <button
                type="button"
                onClick={handleLogout}
                style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 18px", background:"rgba(255,255,255,0.1)", border:"1.5px solid rgba(255,255,255,0.2)", borderRadius:10, color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer", transition:"background .2s", fontFamily:"inherit" }}
                onMouseEnter={e => e.currentTarget.style.background="rgba(220,38,38,0.35)"}
                onMouseLeave={e => e.currentTarget.style.background="rgba(255,255,255,0.1)"}
              >
                <span style={{ fontSize:16 }}>🚪</span> Déconnexion
              </button>
            </div>
          </div>
          <div style={{ display:"flex", gap:0, background:"rgba(0,0,0,0.15)", borderRadius:"12px 12px 0 0", overflow:"hidden" }}>
            {(() => {
              // montant_du est le total dû répété sur CHAQUE versement d'un même dossier, jamais
              // un montant par versement - le sommer brut double-comptait dès qu'un dossier avait
              // plusieurs versements dans la période (KPI "Total dû"/"Taux d'encaissement" faussés).
              const _paieParDossier = {};
              paiements.forEach(p => {
                const key = p.assignation_id || `noass_${(p.email||p.client||"").toLowerCase()}`;
                if (!_paieParDossier[key]) _paieParDossier[key] = { du:0, recu:0 };
                _paieParDossier[key].du = Math.max(_paieParDossier[key].du, p.montant_du||0);
                _paieParDossier[key].recu += (p.montant_recu||0);
              });
              const _tPaie    = Object.values(_paieParDossier).reduce((s,g)=>s+g.recu, 0);
              const _tDu      = Object.values(_paieParDossier).reduce((s,g)=>s+g.du, 0);
              const _txEnc    = _tDu > 0 ? Math.round(_tPaie / _tDu * 100) : 0;
              const _cnNT     = touchpointPaiements.filter(p=>p.statut==="en_attente").length;
              const _tCli     = clientsAssignations.length;
              const _convCli  = clientsAssignations.filter(a=>a.statut==="converti").length;
              const _txCli    = _tCli > 0 ? Math.round(_convCli/_tCli*100) : 0;
              const _roles    = [...new Set(users.map(u=>u.role))].length;
              const STAT_MAP  = {
                overview:         [ { l:"Admins actifs",        v:`${stats.actifs}/${users.length}`,                                        c:"#38bdf8" }, { l:"Apprenants",          v:plateformeStats.total_apprenants != null ? plateformeStats.total_apprenants.toLocaleString("fr-FR") : "…",  c:"#34d399" }, { l:"CA annuel",           v:plateformeStats.ca_annuel != null ? formatMoney(plateformeStats.ca_annuel) : "…",       c:"#f87171" } ],
                platform:         [ { l:"Admins actifs",        v:`${stats.actifs}/${users.length}`,                                        c:"#38bdf8" }, { l:"Apprenants",          v:plateformeStats.total_apprenants != null ? plateformeStats.total_apprenants.toLocaleString("fr-FR") : "…",  c:"#34d399" }, { l:"CA annuel",           v:plateformeStats.ca_annuel != null ? formatMoney(plateformeStats.ca_annuel) : "…",       c:"#f87171" } ],
                administration:   [ { l:"Utilisateurs actifs",  v:`${stats.actifs}/${users.length}`,                                        c:"#38bdf8" }, { l:"En attente",          v:stats.enAttente,                                           c:"#f87171" }, { l:"Rôles distincts",     v:_roles,                                                    c:"#a78bfa" }, { l:"Total comptes",       v:users.length,                                              c:"#34d399" } ],
                audit:            [ { l:"Alertes aujourd'hui",  v:auditStats?.alertes_today||0,                                             c:"#f87171" }, { l:"Total événements",    v:(auditTotal||0).toLocaleString("fr-FR"),                    c:"#38bdf8" }, { l:"Admins actifs",       v:`${stats.actifs}/${users.length}`,                         c:"#34d399" }, { l:"CA annuel",           v:plateformeStats.ca_annuel != null ? formatMoney(plateformeStats.ca_annuel) : "…",       c:"#a78bfa" } ],
                trafic:           [ { l:"Pages vues",           v:traficStats ? traficStats.total_vues.toLocaleString("fr-FR") : "…",        c:"#38bdf8" }, { l:"Sessions uniques",    v:traficStats ? traficStats.total_sessions.toLocaleString("fr-FR") : "…", c:"#a78bfa" }, { l:"Bounce rate",         v:traficStats ? `${traficStats.bounce_rate}%` : "…",                         c:"#f87171" }, { l:"Pages/session",       v:traficStats ? traficStats.pages_par_session : "…",                         c:"#34d399" } ],
                clients:          [ { l:"Total prospects",       v:_tCli,                                                                    c:"#38bdf8" }, { l:"En attente",          v:clientsAssignations.filter(a=>a.statut==="en_attente").length, c:"#f87171" }, { l:"En cours",            v:clientsAssignations.filter(a=>a.statut==="en_cours").length, c:"#a78bfa" }, { l:"Convertis",           v:`${_convCli} (${_txCli}%)`,                                c:"#34d399" } ],
                offres:           [ { l:"Admins actifs",         v:`${stats.actifs}/${users.length}`,                                        c:"#38bdf8" }, { l:"Apprenants",          v:plateformeStats.total_apprenants != null ? plateformeStats.total_apprenants.toLocaleString("fr-FR") : "…",  c:"#34d399" }, { l:"CA annuel",           v:plateformeStats.ca_annuel != null ? formatMoney(plateformeStats.ca_annuel) : "…",       c:"#f87171" } ],
                ca:               [ { l:"Total encaissé",        v:formatMoney(_tPaie),                                                      c:"#34d399" }, { l:"Total dû",            v:formatMoney(_tDu),                                         c:"#38bdf8" }, { l:"Taux encaissement",   v:`${_txEnc}%`,                                              c:"#a78bfa" }, { l:"TouchPoint en attente",v:_cnNT,                                                     c:"#f87171" } ],
                paiements:        [ { l:"Total encaissé",        v:formatMoney(_tPaie),                                                      c:"#34d399" }, { l:"Total dû",            v:formatMoney(_tDu),                                         c:"#38bdf8" }, { l:"Taux encaissement",   v:`${_txEnc}%`,                                              c:"#a78bfa" }, { l:"TouchPoint en attente",v:_cnNT,                                                     c:"#f87171" } ],
                suivi_apprenants: [ { l:"Total apprenants",      v:apprenants.length,                                                        c:"#34d399" }, { l:"Assignations",        v:apprenants.filter(a=>a._source==="assignation").length,    c:"#38bdf8" }, { l:"Cours privés",        v:apprenants.filter(a=>a._source==="cours_prive").length,    c:"#a78bfa" }, { l:"Convertis",           v:apprenants.filter(a=>a.statut==="converti").length,        c:"#f87171" } ],
                assistantes:      [ { l:"Actives",               v:assistantesAdmin.filter(a=>a.actif).length,                               c:"#34d399" }, { l:"Inactives",           v:assistantesAdmin.filter(a=>!a.actif).length,               c:"#f87171" }, { l:"Total assistantes",   v:assistantesAdmin.length,                                   c:"#38bdf8" }, { l:"Admins actifs",       v:`${stats.actifs}/${users.length}`,                         c:"#a78bfa" } ],
                cours:            [ { l:"Admins actifs",         v:`${stats.actifs}/${users.length}`,                                        c:"#38bdf8" }, { l:"Apprenants",          v:plateformeStats.total_apprenants != null ? plateformeStats.total_apprenants.toLocaleString("fr-FR") : "…",  c:"#34d399" }, { l:"CA annuel",           v:plateformeStats.ca_annuel != null ? formatMoney(plateformeStats.ca_annuel) : "…",       c:"#f87171" } ],
                sondages:         [ { l:"Sondages totaux",       v:sondagesAll.length,                                                       c:"#38bdf8" }, { l:"Apprenants",          v:plateformeStats.total_apprenants != null ? plateformeStats.total_apprenants.toLocaleString("fr-FR") : "…",  c:"#34d399" }, { l:"CA annuel",           v:plateformeStats.ca_annuel != null ? formatMoney(plateformeStats.ca_annuel) : "…",       c:"#f87171" } ],
                messages:         [ { l:"Messages non lus",      v:msgNonLuTotal||0,                                                         c:"#f87171" }, { l:"Admins actifs",       v:`${stats.actifs}/${users.length}`,                         c:"#38bdf8" }, { l:"Apprenants",          v:plateformeStats.total_apprenants != null ? plateformeStats.total_apprenants.toLocaleString("fr-FR") : "…",  c:"#34d399" }, { l:"CA annuel",           v:plateformeStats.ca_annuel != null ? formatMoney(plateformeStats.ca_annuel) : "…",       c:"#a78bfa" } ],
                notifications:    [ { l:"Admins actifs",         v:`${stats.actifs}/${users.length}`,                                        c:"#38bdf8" }, { l:"Apprenants",          v:plateformeStats.total_apprenants != null ? plateformeStats.total_apprenants.toLocaleString("fr-FR") : "…",  c:"#34d399" }, { l:"CA annuel",           v:plateformeStats.ca_annuel != null ? formatMoney(plateformeStats.ca_annuel) : "…",       c:"#f87171" } ],
              };
              const activeStats = STAT_MAP[activeTab] || STAT_MAP.overview;
              return activeStats.map((s, i, arr) => (
                <div key={s.l} style={{ flex:1, textAlign:"center", padding:"14px 8px", borderRight: i < arr.length-1 ? "1px solid rgba(255,255,255,0.08)" : "none", transition:"all .3s" }}>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)", marginBottom:3 }}>{s.l}</div>
                  <div style={{ fontSize:20, fontWeight:800, color:s.c }}>{s.v}</div>
                </div>
              ));
            })()}
          </div>
        </div>

        <div style={{ flex:"1 1 auto", padding:"0 24px 24px" }}>
          {/* Tabs principaux */}
          <div style={{ display:"flex", gap:3, marginBottom:0, flexWrap:"wrap", paddingTop:20 }}>
            {tabs.map(tab => {
              const isActive = activeTab === tab.key;
              return (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                  padding:"10px 16px", borderRadius:"8px 8px 0 0", border:"none", cursor:"pointer",
                  fontWeight:600, fontSize:13,
                  background: isActive ? "#fff" : BET_LIGHT,
                  color: isActive ? BET_COLOR : "#0369a1",
                  boxShadow: isActive ? "0 -2px 8px rgba(8,145,178,0.15)" : "none",
                  display:"flex", alignItems:"center", justifyContent:"center", gap:6,
                  flex:"1 1 150px", whiteSpace:"nowrap",
                }}>
                  <span style={{ fontSize:14 }}>{tab.icon}</span>
                  {tab.label}
                  {tab.badge !== undefined && tab.badge !== null && (
                    <span style={{ padding:"1px 7px", borderRadius:10, fontSize:11, fontWeight:700,
                      background: tab.danger ? "#fee2e2" : BET_LIGHT,
                      color: tab.danger ? "#dc2626" : BET_COLOR }}>
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {!banderoleFermeeAD && notifsBanderoleAD.length > 0 && (
            <div style={{ padding:"10px 20px", background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:8, marginTop:10, display:"flex", alignItems:"flex-start", gap:10 }}>
              <span style={{ fontSize:16, flexShrink:0, marginTop:1 }}>🔔</span>
              <div style={{ flex:1, display:"flex", flexDirection:"column", gap:4 }}>
                {notifsBanderoleAD.map(n => (
                  <div key={n.id} onClick={() => { marquerNotifLueAD(n.id); setActiveTab("notifications"); }}
                    style={{ cursor:"pointer", display:"flex", flexDirection:"column" }}>
                    <span style={{ fontWeight:700, color:"#9a3412", fontSize:12 }}>{n.titre}</span>
                    <span style={{ color:"#78716c", fontSize:11 }}>{n.message}</span>
                  </div>
                ))}
                {notifNonLuesAD > notifsBanderoleAD.length && (
                  <button onClick={()=>setActiveTab("notifications")} style={{ alignSelf:"flex-start", background:"none", border:"none", color:"#9a3412", fontWeight:700, fontSize:11, cursor:"pointer", padding:0 }}>
                    +{notifNonLuesAD - notifsBanderoleAD.length} autre{notifNonLuesAD - notifsBanderoleAD.length > 1 ? "s" : ""} →
                  </button>
                )}
              </div>
              <button onClick={()=>setBanderoleFermeeAD(true)} title="Masquer" style={{ background:"none", border:"none", cursor:"pointer", color:"#9a3412", fontSize:14, flexShrink:0 }}>✕</button>
            </div>
          )}

          {/* Carte principale */}
          <div style={{ background:"#fff", padding:24, borderRadius:"0 12px 12px 12px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)" }}>

            {/* ================= ONGLET MES TÂCHES ================= */}
            {activeTab === "taches" && (() => {
              const taches = [
                { icon:"🛍️", label:"Commandes boutique en attente",   n:tachesBoutique,    action:() => { setActiveTab("platform"); setPlatformSubTab("boutique"); } },
                { icon:"📅", label:"Assistantes inactives",           n:tachesAssistantes, action:() => setActiveTab("assistantes") },
                { icon:"💳", label:"Paiements TouchPoint en attente", n:tachesPaiements,   action:() => setActiveTab("paiements") },
                { icon:"📜", label:"Alertes audit du jour",           n:tachesAudit,       action:() => setActiveTab("audit") },
                { icon:"📋", label:"Blog & témoignages à traiter",    n:tachesContenu,     action:() => { setActiveTab("platform"); setPlatformSubTab("contenu"); } },
                { icon:"🏛️", label:"Demandes d'accès en attente",     n:tachesAdministration, action:() => setActiveTab("administration") },
              ];
              const enCours = taches.filter(t => t.n > 0);
              return (
                <div>
                  <div style={{ fontSize:15, fontWeight:800, color:"#0f172a", marginBottom:4 }}>✅ Mes tâches</div>
                  <div style={{ fontSize:12, color:"#64748b", marginBottom:18 }}>
                    {enCours.length > 0
                      ? `${tachesTotal} élément${tachesTotal>1?"s":""} à traiter au total.`
                      : "Rien à traiter pour le moment - tout est à jour."}
                  </div>
                  {enCours.length === 0 ? (
                    <div style={{ textAlign:"center", padding:"50px 0", color:"#94a3b8" }}>
                      <div style={{ fontSize:"2.4rem", marginBottom:8 }}>🎉</div>
                      Aucune tâche en attente.
                    </div>
                  ) : (
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))", gap:14 }}>
                      {enCours.map((t,i) => (
                        <div key={i} onClick={t.action}
                          style={{ background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:14, padding:"16px 18px", cursor:"pointer", display:"flex", alignItems:"center", gap:14, transition:"border-color .15s" }}
                          onMouseEnter={e=>e.currentTarget.style.borderColor=BET_COLOR}
                          onMouseLeave={e=>e.currentTarget.style.borderColor="#e2e8f0"}>
                          <div style={{ fontSize:"1.6rem" }}>{t.icon}</div>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>{t.label}</div>
                            <div style={{ fontSize:11, color:"#94a3b8", marginTop:2 }}>Cliquer pour ouvrir</div>
                          </div>
                          <div style={{ background:"#ef4444", color:"#fff", borderRadius:99, fontSize:13, fontWeight:800, padding:"3px 11px", minWidth:26, textAlign:"center" }}>{t.n}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ================= ONGLET VUE D'ENSEMBLE ================= */}
            {activeTab === "overview" && (
              <div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
                  <div><h2 style={{ margin:0, fontSize:17, fontWeight:700, color:"#0f172a" }}>🏠 Tableau de bord {ROLES_DEF[profilConnecte.role]?.label || "Administrateur"}</h2><p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Indicateurs clés de la plateforme BET</p></div>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(200px,1fr))", gap:16, marginBottom:24 }}>
                  <StatCard label="Admins actifs" value={`${stats.actifs}/${users.length}`} color={BET_COLOR} icon="👥" sub="dont 1 super admin" />
                  <StatCard label="Apprenants actifs" value={plateformeStats.total_apprenants ?? "…"} color="#22c55e" icon="👩‍🎓" sub="via groupes de coaching" />
                  <StatCard
                    label="Certifications"
                    value={plateformeStats.total_certifications ?? "…"}
                    color="#8b5cf6" icon="🏅"
                    sub={plateformeStats.taux_reussite_certifications != null ? `Taux validation ${plateformeStats.taux_reussite_certifications}%` : "Aucune donnée"}
                  />
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16, marginBottom:16 }}>
                  <div style={{ background:"#f8fafc", borderRadius:12, padding:16 }}>
                    <div style={{ fontSize:13, fontWeight:600, marginBottom:12 }}>📈 Évolution du CA encaissé (12 mois)</div>
                    <div style={{ height:220 }}>
                      <Line data={caEvolutionSA} options={{ ...chartOptsCommonSA, plugins:{ ...chartOptsCommonSA.plugins, tooltip:{ callbacks:{ label:(ctx)=>formatMoney(ctx.parsed.y) } } } }} />
                    </div>
                    {plateformeStats.ca_annuel != null && (
                      <div style={{ marginTop:10, fontSize:12, color:"#374151" }}>CA annuel réel : <strong>{formatMoney(plateformeStats.ca_annuel)}</strong></div>
                    )}
                  </div>
                  <div style={{ background:"#f8fafc", borderRadius:12, padding:16 }}>
                    <div style={{ fontSize:13, fontWeight:600, marginBottom:12 }}>🥧 Répartition des utilisateurs par rôle</div>
                    <div style={{ height:220 }}>
                      {rolesRepartitionSA.labels.length === 0 ? (
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", color:"#94a3b8", fontSize:13 }}>Aucun utilisateur</div>
                      ) : (
                        <Doughnut data={rolesRepartitionSA} options={{ responsive:true, maintainAspectRatio:false, plugins:{ legend:{ position:"bottom", labels:{ boxWidth:10, font:{ size:10 } } }, tooltip:{ callbacks:{ label:(ctx)=>`${ctx.label} : ${ctx.parsed}` } } } }} />
                      )}
                    </div>
                  </div>
                </div>
                <div style={{ background:"#f8fafc", borderRadius:12, padding:16 }}>
                  <div style={{ fontSize:13, fontWeight:600, marginBottom:12 }}>🎯 Conversion des prospects (assignations commerciales)</div>
                  <div style={{ height:200 }}>
                    {clientsFunnelSA.labels.length === 0 ? (
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", color:"#94a3b8", fontSize:13 }}>Aucun prospect assigné</div>
                    ) : (
                      <Bar data={clientsFunnelSA} options={{ ...chartOptsCommonSA, indexAxis:"y", plugins:{ legend:{ display:false }, tooltip:{ callbacks:{ label:(ctx)=>`${ctx.parsed.x}` } } } }} />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ================= ONGLET PLATEFORME ================= */}
            {activeTab === "platform" && (
              <div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
                  <div><h2 style={{ margin:0, fontSize:17, fontWeight:700, color:"#0f172a" }}>🏢 Configuration de la plateforme</h2><p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Paramètres globaux, modules activés, maintenance</p></div>
                </div>

                {/* ── Sous-onglets Plateforme ── */}
                <div style={{ display:"flex", gap:3, marginBottom:20, borderBottom:"1px solid #e5e7eb", paddingBottom:8 }}>
                  {platformTabs.map(tab => {
                    const isActive = platformSubTab === tab.key;
                    return (
                      <button key={tab.key} onClick={() => setPlatformSubTab(tab.key)} style={{ padding:"8px 14px", borderRadius:"8px 8px 0 0", border:"none", cursor:"pointer", fontWeight:600, fontSize:12, background:isActive?BET_LIGHT:"transparent", color:isActive?BET_COLOR:"#6b7280", display:"flex", alignItems:"center", gap:5 }}>
                        <span style={{ fontSize:13 }}>{tab.icon}</span>{tab.label}
                      </button>
                    );
                  })}
                </div>

                {/* ── Sous-onglet : Configuration Partenaires ── */}
                {platformSubTab === "partenaires" && (
                  <PartenairesManager apiUrl={API_URL} authHeaders={authHeaders} peut={peut} />
                )}

                {/* ── Sous-onglet : Messages WhatsApp ── */}
                {/* ── Sous-onglet : Équipe Coachs ── */}
                {platformSubTab === "coachs_photos" && (
                  <div>
                    {/* En-tête */}
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
                      <div>
                        <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:"#0f172a" }}>👨‍🏫 Équipe Coachs</h3>
                        <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Ajoutez et gérez les photos des coachs affichées sur le site</p>
                      </div>
                      <button onClick={fetchCoachs} style={{ padding:"8px 14px", background:BET_LIGHT, color:BET_COLOR, border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>🔄 Actualiser</button>
                    </div>

                    {/* ── Formulaire d'ajout ── */}
                    <NewCoachForm
                      form={newCoachForm}
                      onFormChange={(key, val) => setNewCoachForm(p => ({ ...p, [key]: val }))}
                      onPhotoFile={uploadNewCoachPhoto}
                      photoUploading={newCoachUploading}
                      onSubmit={createDisplayCoach}
                      saving={newCoachSaving}
                      peut={peut}
                    />

                    {/* ── Recherche ── */}
                    <input
                      value={coachSearchQ}
                      onChange={e => setCoachSearchQ(e.target.value)}
                      placeholder="🔎 Rechercher un coach par nom…"
                      style={{ width:"100%", padding:"9px 12px", borderRadius:9, border:"1px solid #e2e8f0", fontSize:13, boxSizing:"border-box", margin:"16px 0" }}
                    />

                    {/* ── Liste des coachs existants ── */}
                    {coachsLoading ? (
                      <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>⏳ Chargement…</div>
                    ) : coachsList.length === 0 ? (
                      <div style={{ textAlign:"center", padding:32, color:"#9ca3af", fontSize:13 }}>Aucun coach ajouté pour l'instant.</div>
                    ) : coachsList.filter(photo => (photo.nom||"").toLowerCase().includes(coachSearchQ.toLowerCase().trim())).length === 0 ? (
                      <div style={{ textAlign:"center", padding:32, color:"#9ca3af", fontSize:13 }}>Aucun coach ne correspond à "{coachSearchQ}".</div>
                    ) : (
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:16 }}>
                        {coachsList.filter(photo => (photo.nom||"").toLowerCase().includes(coachSearchQ.toLowerCase().trim())).map(photo => {
                          const edits      = coachsEdits[photo.id] || {};
                          const photoUrl   = edits.photo_url !== undefined ? edits.photo_url : (photo.photo_url || "");
                          const isActif    = edits.actif    !== undefined ? edits.actif     : (photo.actif !== false);
                          const statut     = isActif ? "actif" : "inactif";
                          const hasChanges = Object.keys(edits).length > 0;
                          const nomActuel   = edits.nom   !== undefined ? edits.nom   : (photo.nom   || "");
                          const titreActuel = edits.titre !== undefined ? edits.titre : (photo.titre || "");
                          const fakeCoach  = { id: photo.id, nom: nomActuel, prenom: "", grade: titreActuel };
                          return (
                            <CoachPhotoCard
                              key={photo.id}
                              coach={fakeCoach}
                              photoUrl={photoUrl}
                              statut={statut}
                              hasChanges={hasChanges}
                              uploading={!!coachsUploading[photo.id]}
                              saving={!!coachsSaving[photo.id]}
                              onFile={f => uploadCoachPhoto(fakeCoach, f)}
                              onRemove={async () => { if (await confirm("Supprimer cette photo ?", { titre: "Supprimer la photo" })) setCoachsEdits(p => ({ ...p, [photo.id]: { ...(p[photo.id]||{}), photo_url: "" } })); }}
                              onToggleStatut={() => setCoachsEdits(p => ({ ...p, [photo.id]: { ...(p[photo.id]||{}), actif: !isActif } }))}
                              onUrlChange={val => setCoachsEdits(p => ({ ...p, [photo.id]: { ...(p[photo.id]||{}), photo_url: val } }))}
                              onNomChange={val => setCoachsEdits(p => ({ ...p, [photo.id]: { ...(p[photo.id]||{}), nom: val } }))}
                              onTitreChange={val => setCoachsEdits(p => ({ ...p, [photo.id]: { ...(p[photo.id]||{}), titre: val } }))}
                              onSave={() => saveCoach(photo)}
                              peut={peut}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {platformSubTab === "whatsapp" && (
                  <div>

                    {/* ── CONFIG CENTRALE ── */}
                    <div style={{ background:"#fff", borderRadius:14, border:"1.5px solid #6366f1", padding:24, marginBottom:28 }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18 }}>
                        <div>
                          <h3 style={{ margin:0, fontSize:15, fontWeight:800, color:"#0f172a" }}>📞 Coordonnées centrales BET</h3>
                          <p style={{ margin:"3px 0 0", fontSize:12, color:"#6b7280" }}>Ces informations s'affichent sur le bouton WhatsApp flottant, le footer et la page Contact.</p>
                        </div>
                        {contactConfigLoad && <span style={{ fontSize:12, color:"#9ca3af" }}>⏳ Chargement…</span>}
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
                        <div>
                          <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:5 }}>✉️ Email central</label>
                          <input
                            value={contactConfig.email_central||""}
                            onChange={e=>setContactConfig(p=>({...p,email_central:e.target.value}))}
                            placeholder="contact@bet-ci.com"
                            style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e5e7eb", fontSize:13, boxSizing:"border-box" }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:5 }}>📱 Numéro WhatsApp principal (sans +)</label>
                          <input
                            value={contactConfig.whatsapp_number||""}
                            onChange={e=>setContactConfig(p=>({...p,whatsapp_number:e.target.value}))}
                            placeholder="2250700000000"
                            style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e5e7eb", fontSize:13, boxSizing:"border-box" }}
                          />
                          {/* Deux lignes de plus, affichées cote a cote avec la principale dans
                              le footer et sur la page Contact. Laisser vide pour n'en montrer
                              qu'une : un champ vide n'apparait nulle part cote site. */}
                          <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", margin:"12px 0 5px" }}>📱 2ᵉ numéro WhatsApp <span style={{ fontWeight:400, color:"#9ca3af" }}>(optionnel)</span></label>
                          <input
                            value={contactConfig.whatsapp_number_2||""}
                            onChange={e=>setContactConfig(p=>({...p,whatsapp_number_2:e.target.value}))}
                            placeholder="2250700000001"
                            style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e5e7eb", fontSize:13, boxSizing:"border-box" }}
                          />
                          <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", margin:"12px 0 5px" }}>📱 3ᵉ numéro WhatsApp <span style={{ fontWeight:400, color:"#9ca3af" }}>(optionnel)</span></label>
                          <input
                            value={contactConfig.whatsapp_number_3||""}
                            onChange={e=>setContactConfig(p=>({...p,whatsapp_number_3:e.target.value}))}
                            placeholder="2250700000002"
                            style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e5e7eb", fontSize:13, boxSizing:"border-box" }}
                          />
                        </div>
                      </div>
                      <div style={{ marginBottom:16 }}>
                        <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:5 }}>📍 Adresse / Localisation (affichée dans la carte Maps de la page Contact)</label>
                        <input
                          value={contactConfig.localisation||""}
                          onChange={e=>setContactConfig(p=>({...p,localisation:e.target.value}))}
                          placeholder="Angré 7ème Tranche, Immeuble Le Palace, Abidjan"
                          style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e5e7eb", fontSize:13, boxSizing:"border-box" }}
                        />
                      </div>
                      <div style={{ marginBottom:16 }}>
                        <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:5 }}>🗺️ URL d'intégration Google Maps (iframe embed)</label>
                        <input
                          value={contactConfig.maps_embed_url||""}
                          onChange={e=>setContactConfig(p=>({...p,maps_embed_url:e.target.value}))}
                          placeholder="https://www.google.com/maps/embed?pb=..."
                          style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e5e7eb", fontSize:13, boxSizing:"border-box" }}
                        />
                        <p style={{ margin:"4px 0 0", fontSize:11, color:"#9ca3af" }}>Depuis Google Maps → Partager → Intégrer une carte → copier le lien src de l'iframe</p>
                      </div>
                      <div style={{ marginBottom:16 }}>
                        <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:5 }}>💬 Message WhatsApp pré-rempli</label>
                        <textarea
                          value={contactConfig.whatsapp_message||""}
                          onChange={e=>setContactConfig(p=>({...p,whatsapp_message:e.target.value}))}
                          rows={3}
                          placeholder="Bonjour ! Je souhaite avoir des informations sur les cours d'anglais chez BET."
                          style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e5e7eb", fontSize:13, resize:"vertical", boxSizing:"border-box", fontFamily:"inherit" }}
                        />
                      </div>
                      {contactConfig.whatsapp_number && (
                        <div style={{ marginBottom:14, padding:"8px 12px", borderRadius:8, background:"#f0fdf4", border:"1px solid #bbf7d0", fontSize:12, color:"#166534" }}>
                          🔗 Aperçu lien : <strong>https://wa.me/{numeroPourLien(contactConfig.whatsapp_number)}</strong>
                          {[contactConfig.whatsapp_number_2, contactConfig.whatsapp_number_3].filter(Boolean).map((n, i) => (
                            <div key={i} style={{ marginTop:4 }}>🔗 <strong>https://wa.me/{numeroPourLien(n)}</strong></div>
                          ))}
                        </div>
                      )}
                      <button
                        onClick={saveContactConfig}
                        disabled={contactConfigSave || !peut("configuration","update")}
                        title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                        style={{ padding:"10px 22px", background:"#6366f1", color:"#fff", border:"none", borderRadius:9, cursor:"pointer", fontWeight:700, fontSize:13, opacity:(contactConfigSave||!peut("configuration","update"))?0.6:1 }}
                      >
                        {contactConfigSave ? "Sauvegarde…" : !peut("configuration","update") ? "🔒 Enregistrer les coordonnées" : "💾 Enregistrer les coordonnées"}
                      </button>
                    </div>

                    {/* ── RÉSEAUX SOCIAUX ── */}
                    {(() => {
                      const SOCIALS = [
                        { key:"social_facebook",  label:"Facebook",    icon:"📘", bg:"#1877f2",  previewLabel:"f",  placeholder:"https://facebook.com/binnieset" },
                        { key:"social_instagram", label:"Instagram",   icon:"📸", bg:"linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)", previewLabel:"📷", placeholder:"https://instagram.com/binnieset" },
                        { key:"social_linkedin",  label:"LinkedIn",    icon:"💼", bg:"#0077b5",  previewLabel:"in", placeholder:"https://linkedin.com/company/binnieset" },
                        { key:"social_tiktok",    label:"TikTok",      icon:"🎵", bg:"#010101",  previewLabel:"♪",  placeholder:"https://tiktok.com/@binnieset" },
                        { key:"social_twitter",   label:"X (Twitter)", icon:"🐦", bg:"#000",     previewLabel:"𝕏",  placeholder:"https://x.com/binnieset" },
                      ];
                      const visibleCount = SOCIALS.filter(s => contactConfig[s.key] && contactConfig[`${s.key}_visible`] !== false).length;
                      return (
                        <div style={{ background:"#fff", borderRadius:14, border:"1.5px solid #e2e8f0", padding:24 }}>
                          <div style={{ marginBottom:18 }}>
                            <h3 style={{ margin:0, fontSize:15, fontWeight:800, color:"#0f172a" }}>🌐 Réseaux sociaux</h3>
                            <p style={{ margin:"3px 0 0", fontSize:12, color:"#6b7280" }}>Configurez les liens et activez/désactivez leur affichage dans le footer.</p>
                          </div>

                          <div style={{ display:"flex", flexDirection:"column", gap:12, marginBottom:20 }}>
                            {SOCIALS.map(({ key, label, icon, placeholder }) => {
                              const visKey  = `${key}_visible`;
                              const url     = contactConfig[key] || "";
                              const visible = contactConfig[visKey] !== false;
                              const hasUrl  = !!url.trim();
                              return (
                                <div key={key} style={{
                                  display:"grid", gridTemplateColumns:"1fr auto",
                                  gap:12, alignItems:"center",
                                  padding:"12px 14px", borderRadius:10,
                                  border:`1.5px solid ${hasUrl && visible ? "#86efac" : hasUrl ? "#fde68a" : "#e5e7eb"}`,
                                  background: hasUrl && visible ? "#f0fdf4" : hasUrl ? "#fffbeb" : "#fafafa",
                                  transition:"all .2s",
                                }}>
                                  {/* Colonne gauche : label + input */}
                                  <div>
                                    <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                                      <span>{icon}</span>{label}
                                      {hasUrl && (
                                        <a href={url} target="_blank" rel="noopener noreferrer"
                                          style={{ marginLeft:"auto", fontSize:11, color:BET_COLOR, fontWeight:600, textDecoration:"none" }}>
                                          Tester ↗
                                        </a>
                                      )}
                                    </label>
                                    <input
                                      value={url}
                                      onChange={e => setContactConfig(p => ({ ...p, [key]: e.target.value }))}
                                      placeholder={placeholder}
                                      style={{ width:"100%", padding:"8px 11px", borderRadius:7, border:"1.5px solid #e2e8f0", fontSize:13, boxSizing:"border-box", background:"#fff" }}
                                    />
                                  </div>
                                  {/* Colonne droite : toggle visibilité */}
                                  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4, minWidth:60 }}>
                                    <ToggleSwitch
                                      on={hasUrl && visible}
                                      onChange={v => setContactConfig(p => ({ ...p, [visKey]: v }))}
                                      color="#22c55e"
                                    />
                                    <span style={{ fontSize:10, fontWeight:700, color: hasUrl && visible ? "#16a34a" : "#9ca3af", whiteSpace:"nowrap" }}>
                                      {hasUrl && visible ? "Visible" : hasUrl ? "Masqué" : "Vide"}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Aperçu footer */}
                          <div style={{ marginBottom:18, padding:"12px 16px", borderRadius:10, background:"#f8fafc", border:"1px solid #e2e8f0" }}>
                            <p style={{ margin:"0 0 10px", fontSize:11, fontWeight:700, color:"#6b7280", letterSpacing:".05em" }}>APERÇU FOOTER</p>
                            <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                              {SOCIALS.map(({ key, bg, previewLabel }) => {
                                const url     = contactConfig[key];
                                const visible = contactConfig[`${key}_visible`] !== false;
                                const active  = url && visible;
                                return (
                                  <div key={key} style={{
                                    width:34, height:34, borderRadius:"50%",
                                    background: active ? bg : "#e5e7eb",
                                    display:"flex", alignItems:"center", justifyContent:"center",
                                    color: active ? "#fff" : "#9ca3af",
                                    fontSize:13, fontWeight:700,
                                    cursor: active ? "pointer" : "default",
                                    opacity: url && !visible ? 0.3 : active ? 1 : 0.45,
                                    transition:"all .2s",
                                    position:"relative",
                                  }}
                                    title={active ? url : url ? "Masqué" : "Non configuré"}
                                    onClick={() => active && window.open(url, "_blank")}
                                  >
                                    {previewLabel}
                                    {url && !visible && (
                                      <span style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14 }}>🚫</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <p style={{ margin:"8px 0 0", fontSize:11, color:"#9ca3af" }}>
                              {visibleCount} / 5 réseau{visibleCount !== 1 ? "x" : ""} visible{visibleCount !== 1 ? "s" : ""} sur le site
                            </p>
                          </div>

                          <button
                            onClick={saveContactConfig}
                            disabled={contactConfigSave || !peut("configuration","update")}
                            title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                            style={{ padding:"10px 22px", background:"#6366f1", color:"#fff", border:"none", borderRadius:9, cursor:"pointer", fontWeight:700, fontSize:13, opacity:(contactConfigSave||!peut("configuration","update")) ? 0.6 : 1 }}
                          >
                            {contactConfigSave ? "Sauvegarde…" : !peut("configuration","update") ? "🔒 Enregistrer les réseaux sociaux" : "💾 Enregistrer les réseaux sociaux"}
                          </button>
                        </div>
                      );
                    })()}

                    {/* ── CONFIG PAR CENTRE (désactivé temporairement) ── */}
                    {/*
                    <div style={{ marginBottom:20 }}>
                      <h3>💬 Messages WhatsApp - Centres BET</h3>
                      ...
                    </div>
                    */}
                  </div>
                )}
                {/* ── Sous-onglet : Marquee ── */}
                {platformSubTab === "marquee" && (
                  <div>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
                      <div>
                        <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:"#0f172a" }}>📢 Messages défilants (Marquee)</h3>
                        <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Ces messages s'affichent en bandeau sur la page d'accueil. Seuls les messages actifs et non expirés sont visibles.</p>
                      </div>
                      <div style={{ display:"flex", gap:8 }}>
                        <button onClick={fetchMarquee} style={{ padding:"8px 14px", background:BET_LIGHT, color:BET_COLOR, border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>🔄 Actualiser</button>
                        <button onClick={() => setMarqueeForm({ texte:"", code_promo:"", lien_url:"", lien_label:"", date_expiration:"", actif:true })} disabled={!peut("configuration","create")} title={!peut("configuration","create")?"🔒 Vous n'avez pas la permission pour cette action":undefined} style={{ padding:"8px 16px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12, opacity:peut("configuration","create")?1:0.5 }}>{peut("configuration","create")?"➕ Nouveau message":"🔒 Nouveau message"}</button>
                      </div>
                    </div>

                    {/* Aperçu live */}
                    {marqueeMessages.filter(m => m.actif).length > 0 && (
                      <div style={{ marginBottom:16, background:"#0f172a", borderRadius:10, padding:"10px 16px", overflow:"hidden", position:"relative" }}>
                        <div style={{ fontSize:11, color:"#64748b", marginBottom:6, fontWeight:600 }}>APERÇU BANDEAU</div>
                        <div style={{ display:"flex", gap:20, overflow:"hidden", whiteSpace:"nowrap" }}>
                          {marqueeMessages.filter(m => m.actif).map((m, i) => (
                            <span key={i} style={{ fontSize:12, color:"#e2e8f0", flexShrink:0 }}>
                              {m.texte}
                              {m.code_promo && <span style={{ marginLeft:6, background:"#fbbf24", color:"#78350f", padding:"1px 6px", borderRadius:4, fontWeight:700, fontSize:11 }}>{m.code_promo}</span>}
                              {m.lien_url && <span style={{ marginLeft:6, color:"#38bdf8", textDecoration:"underline", fontSize:11 }}>{m.lien_label || m.lien_url}</span>}
                              <span style={{ marginLeft:12, color:"#334155" }}>·</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Formulaire création / édition */}
                    {marqueeForm && (
                      <div style={{ background:"#f8fafc", border:`1.5px solid ${BET_COLOR}33`, borderRadius:12, padding:20, marginBottom:20 }}>
                        <h4 style={{ margin:"0 0 14px", fontSize:14, fontWeight:700, color:"#0f172a" }}>
                          {marqueeForm.id ? "✏️ Modifier le message" : "➕ Nouveau message"}
                        </h4>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
                          <div style={{ gridColumn:"1 / -1" }}>
                            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                              <label style={{ fontSize:12, fontWeight:600, color:"#374151" }}>Texte du message *</label>
                              <EmojiInsertWidget onInsert={e => setMarqueeForm(p => ({ ...p, texte: (p.texte || "") + e }))} />
                            </div>
                            <input value={marqueeForm.texte} onChange={e => setMarqueeForm(p => ({ ...p, texte: e.target.value }))}
                              placeholder="🎁 Offre spéciale · -15% sur l'inscription en ligne !"
                              style={{ width:"100%", padding:"8px 10px", borderRadius:7, border:"1px solid #e2e8f0", fontSize:13, boxSizing:"border-box" }} />
                          </div>
                          <div>
                            <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Code promo (optionnel)</label>
                            <input value={marqueeForm.code_promo} onChange={e => setMarqueeForm(p => ({ ...p, code_promo: e.target.value }))}
                              placeholder="BET2024"
                              style={{ width:"100%", padding:"8px 10px", borderRadius:7, border:"1px solid #e2e8f0", fontSize:13, boxSizing:"border-box" }} />
                          </div>
                          <div>
                            <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Lien de redirection (optionnel)</label>
                            <input value={marqueeForm.lien_url} onChange={e => setMarqueeForm(p => ({ ...p, lien_url: e.target.value }))}
                              placeholder="https://bet-languages.com/offre"
                              style={{ width:"100%", padding:"8px 10px", borderRadius:7, border:"1px solid #e2e8f0", fontSize:13, boxSizing:"border-box" }} />
                          </div>
                          <div>
                            <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Texte du lien</label>
                            <input value={marqueeForm.lien_label} onChange={e => setMarqueeForm(p => ({ ...p, lien_label: e.target.value }))}
                              placeholder="En savoir plus →"
                              style={{ width:"100%", padding:"8px 10px", borderRadius:7, border:"1px solid #e2e8f0", fontSize:13, boxSizing:"border-box" }} />
                          </div>
                          <div>
                            <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Date d'expiration (optionnel)</label>
                            <input type="datetime-local" value={marqueeForm.date_expiration}
                              onChange={e => setMarqueeForm(p => ({ ...p, date_expiration: e.target.value }))}
                              style={{ width:"100%", padding:"8px 10px", borderRadius:7, border:"1px solid #e2e8f0", fontSize:13, boxSizing:"border-box" }} />
                          </div>
                          <div>
                            <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Ordre d'affichage</label>
                            <input type="number" value={marqueeForm.ordre ?? ""} onChange={e => setMarqueeForm(p => ({ ...p, ordre: e.target.value }))}
                              placeholder="0"
                              style={{ width:"100%", padding:"8px 10px", borderRadius:7, border:"1px solid #e2e8f0", fontSize:13, boxSizing:"border-box" }} />
                          </div>
                          <div style={{ display:"flex", alignItems:"center", gap:10, paddingTop:20 }}>
                            <ToggleSwitch on={marqueeForm.actif !== false} onChange={v => setMarqueeForm(p => ({ ...p, actif: v }))} color={BET_COLOR} />
                            <span style={{ fontSize:13, color:"#374151" }}>Actif (visible sur le site)</span>
                          </div>
                        </div>
                        <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
                          <button onClick={() => setMarqueeForm(null)} style={{ padding:"8px 16px", background:"#f1f5f9", color:"#374151", border:"none", borderRadius:7, cursor:"pointer", fontWeight:600, fontSize:13 }}>Annuler</button>
                          <button
                            onClick={() => saveMarquee(marqueeForm)}
                            disabled={!marqueeForm.texte?.trim() || !!marqueeSavingId || !peut("configuration", marqueeForm.id?"update":"create")}
                            title={!peut("configuration", marqueeForm.id?"update":"create")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                            style={{ padding:"8px 18px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:13, opacity:(!marqueeForm.texte?.trim() || !!marqueeSavingId || !peut("configuration", marqueeForm.id?"update":"create")) ? 0.6 : 1 }}>
                            {marqueeSavingId ? "⏳ Enregistrement…" : !peut("configuration", marqueeForm.id?"update":"create") ? "🔒 Enregistrer" : "💾 Enregistrer"}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Liste des messages */}
                    {marqueeLoading ? (
                      <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>⏳ Chargement…</div>
                    ) : marqueeMessages.length === 0 ? (
                      <div style={{ textAlign:"center", padding:32, color:"#9ca3af", fontSize:13 }}>Aucun message. Créez-en un ci-dessus.</div>
                    ) : (() => {
                      const totalPages = Math.ceil(marqueeMessages.length / MARQUEE_PER_PAGE);
                      const page       = Math.min(marqueePage, totalPages);
                      const slice      = marqueeMessages.slice((page - 1) * MARQUEE_PER_PAGE, page * MARQUEE_PER_PAGE);
                      return (
                        <>
                          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                            {slice.map(msg => {
                              const isExpired = msg.date_expiration && new Date(msg.date_expiration) < new Date();
                              return (
                                <div key={msg.id} style={{ background:"#fff", borderRadius:10, padding:"14px 16px", border:`1px solid ${isExpired ? "#fecaca" : msg.actif ? "#d1fae5" : "#e5e7eb"}`, display:"flex", alignItems:"flex-start", gap:14, boxShadow:"0 1px 3px rgba(0,0,0,0.04)" }}>
                                  {/* Ordre + toggle */}
                                  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6, minWidth:36 }}>
                                    <span style={{ fontSize:11, fontWeight:700, color:"#9ca3af" }}>#{msg.ordre}</span>
                                    <ToggleSwitch on={msg.actif} onChange={() => peut("configuration","update") && toggleMarqueeActif(msg)} color={peut("configuration","update")?BET_COLOR:"#cbd5e1"} />
                                  </div>
                                  {/* Contenu */}
                                  <div style={{ flex:1, minWidth:0 }}>
                                    <div style={{ fontSize:13, fontWeight:600, color:"#0f172a", marginBottom:4 }}>{msg.texte}</div>
                                    <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                                      {msg.code_promo && (
                                        <span style={{ background:"#fef3c7", color:"#92400e", padding:"2px 8px", borderRadius:5, fontSize:11, fontWeight:700 }}>🏷️ {msg.code_promo}</span>
                                      )}
                                      {msg.lien_url && (
                                        <span style={{ background:"#e0f2fe", color:"#0369a1", padding:"2px 8px", borderRadius:5, fontSize:11 }}>🔗 {msg.lien_label || msg.lien_url}</span>
                                      )}
                                      {msg.date_expiration && (
                                        <span style={{ background: isExpired ? "#fee2e2" : "#f0fdf4", color: isExpired ? "#dc2626" : "#166534", padding:"2px 8px", borderRadius:5, fontSize:11 }}>
                                          {isExpired ? "⛔ Expiré" : "⏳ Exp."} {new Date(msg.date_expiration).toLocaleDateString("fr-FR", { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" })}
                                        </span>
                                      )}
                                      {!msg.actif && !isExpired && <span style={{ background:"#f1f5f9", color:"#64748b", padding:"2px 8px", borderRadius:5, fontSize:11 }}>Inactif</span>}
                                    </div>
                                  </div>
                                  {/* Actions */}
                                  <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                                    <button onClick={() => setMarqueeForm({ ...msg, date_expiration: msg.date_expiration ? msg.date_expiration.slice(0,16) : "" })}
                                      disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                                      style={{ padding:"5px 10px", background:BET_LIGHT, color:BET_COLOR, border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:12, opacity:peut("configuration","update")?1:0.5 }}>✏️</button>
                                    <button onClick={() => deleteMarquee(msg.id)}
                                      disabled={!peut("configuration","delete")} title={!peut("configuration","delete")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                                      style={{ padding:"5px 10px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:12, opacity:peut("configuration","delete")?1:0.5 }}>🗑️</button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* ── Pagination ── */}
                          {totalPages > 1 && (
                            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:16, padding:"10px 0" }}>
                              <span style={{ fontSize:12, color:"#6b7280" }}>
                                {(page - 1) * MARQUEE_PER_PAGE + 1}–{Math.min(page * MARQUEE_PER_PAGE, marqueeMessages.length)} sur {marqueeMessages.length} messages
                              </span>
                              <div style={{ display:"flex", gap:4 }}>
                                <button onClick={() => setMarqueePage(1)} disabled={page === 1}
                                  style={{ padding:"5px 9px", borderRadius:6, border:"1px solid #e5e7eb", background: page===1?"#f9fafb":"#fff", color: page===1?"#d1d5db":"#374151", cursor: page===1?"default":"pointer", fontSize:12, fontWeight:600 }}>«</button>
                                <button onClick={() => setMarqueePage(p => Math.max(1, p - 1))} disabled={page === 1}
                                  style={{ padding:"5px 9px", borderRadius:6, border:"1px solid #e5e7eb", background: page===1?"#f9fafb":"#fff", color: page===1?"#d1d5db":"#374151", cursor: page===1?"default":"pointer", fontSize:12, fontWeight:600 }}>‹</button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                                  <button key={p} onClick={() => setMarqueePage(p)}
                                    style={{ padding:"5px 10px", borderRadius:6, border:`1px solid ${p===page ? BET_COLOR : "#e5e7eb"}`, background: p===page ? BET_COLOR : "#fff", color: p===page ? "#fff" : "#374151", cursor:"pointer", fontSize:12, fontWeight:700 }}>{p}</button>
                                ))}
                                <button onClick={() => setMarqueePage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                  style={{ padding:"5px 9px", borderRadius:6, border:"1px solid #e5e7eb", background: page===totalPages?"#f9fafb":"#fff", color: page===totalPages?"#d1d5db":"#374151", cursor: page===totalPages?"default":"pointer", fontSize:12, fontWeight:600 }}>›</button>
                                <button onClick={() => setMarqueePage(totalPages)} disabled={page === totalPages}
                                  style={{ padding:"5px 9px", borderRadius:6, border:"1px solid #e5e7eb", background: page===totalPages?"#f9fafb":"#fff", color: page===totalPages?"#d1d5db":"#374151", cursor: page===totalPages?"default":"pointer", fontSize:12, fontWeight:600 }}>»</button>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* ── Sous-onglets internes : Offres & Contenus ── */}
                {platformSubTab === "catalogue" && (
                  <div style={{ display:"flex", gap:2, marginBottom:16, background:"#f8fafc", borderRadius:10, padding:4, border:"1px solid #e5e7eb" }}>
                    {CATALOGUE_TABS.map(t => {
                      const active = catalogueSubTab === t.key;
                      return (
                        <button key={t.key} onClick={() => setCatalogueSubTab(t.key)} style={{
                          flex:1, padding:"8px 10px", borderRadius:8, border:"none", cursor:"pointer",
                          fontWeight:700, fontSize:12,
                          background: active ? "#fff" : "transparent",
                          color: active ? BET_COLOR : "#6b7280",
                          boxShadow: active ? "0 1px 4px rgba(0,0,0,.08)" : "none",
                          display:"flex", alignItems:"center", justifyContent:"center", gap:5,
                          transition:"all .18s",
                        }}>
                          <span style={{ fontSize:14 }}>{t.icon}</span>{t.label}
                        </button>
                      );
                    })}
                  </div>
                )}

                {platformSubTab === "catalogue" && catalogueSubTab === "centres" && (
                  <div>
                    {/* Header */}
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18 }}>
                      <div>
                        <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:"#0f172a" }}>📍 Nos centres & leurs offres</h3>
                        <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Source de vérité partagée - Navbar, parcours et frontend se synchronisent automatiquement.</p>
                      </div>
                      <div style={{ display:"flex", gap:8 }}>
                        <button onClick={() => setCentresMaster(readCentresMaster())} style={{ padding:"8px 14px", background:BET_LIGHT, color:BET_COLOR, border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>🔄 Actualiser</button>
                        <button onClick={() => setShowAddCentre(true)} disabled={!peut("configuration","create")} title={!peut("configuration","create")?"🔒 Vous n'avez pas la permission pour cette action":undefined} style={{ padding:"8px 16px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12, opacity:peut("configuration","create")?1:0.5 }}>{peut("configuration","create")?"➕ Nouveau centre":"🔒 Nouveau centre"}</button>
                      </div>
                    </div>

                    {/* Grille des centres */}
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:14 }}>
                      {centresMaster.map(c => (
                        <div key={c.key} style={{ background:"#fff", borderRadius:14, border:`2px solid ${c.actif ? c.color+"33" : "#f1f5f9"}`, padding:16, boxShadow:"0 1px 6px rgba(0,0,0,.05)", opacity:c.actif?1:.65 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                            <div style={{ width:42, height:42, borderRadius:10, background:c.color+"22", border:`2px solid ${c.color}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0, overflow:"hidden" }}>
                              {c.photos?.[0] ? <img src={c.photos[0]} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : "📍"}
                            </div>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontWeight:700, fontSize:13, color:"#0f172a" }}>{c.name}</div>
                              <div style={{ fontSize:11, color:"#64748b" }}>{c.ville}</div>
                            </div>
                            <span style={{ padding:"3px 8px", borderRadius:20, fontSize:10, fontWeight:700, background:c.actif?"#dcfce7":"#fee2e2", color:c.actif?"#166534":"#991b1b", flexShrink:0 }}>{c.actif ? "Actif" : "Inactif"}</span>
                          </div>
                          <div style={{ marginBottom:8 }}>
                            <div style={{ fontSize:11, fontWeight:600, color:"#64748b", marginBottom:4 }}>Offres actives</div>
                            <div style={{ display:"flex", flexWrap:"wrap", gap:3 }}>
                              {(c.offres||[]).filter(o => o.actif).map(o => <span key={o.id} style={{ padding:"2px 7px", borderRadius:20, fontSize:10, fontWeight:600, background:BET_LIGHT, color:BET_COLOR }}>{o.label}</span>)}
                              {(c.offres||[]).filter(o => o.actif).length === 0 && <span style={{ fontSize:11, color:"#94a3b8", fontStyle:"italic" }}>Aucune</span>}
                            </div>
                          </div>
                          <div style={{ fontSize:11, color:"#64748b", marginBottom:8 }}>🕐 {c.horaires}</div>
                          {c.brochure_url && <div style={{ fontSize:11, color:"#0891b2", marginBottom:8 }}>📄 Brochure configurée</div>}
                          <div style={{ display:"flex", gap:6 }}>
                            <button onClick={() => { setCentreEditKey(c.key); setCentreEditData(JSON.parse(JSON.stringify(c))); }}
                              disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                              style={{ flex:1, padding:"8px 0", background:BET_COLOR, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12, opacity:peut("configuration","update")?1:0.5 }}>
                              ✏️ Modifier
                            </button>
                            <button onClick={async () => { if(await confirm(`Supprimer ${c.name} ?`, { titre: "Supprimer le centre" })) { const u=centresMaster.filter(x=>x.key!==c.key); saveCentresMaster(u); setCentresMaster(u); toast.success("Centre supprimé"); } }}
                              disabled={!peut("configuration","delete")} title={!peut("configuration","delete")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                              style={{ padding:"8px 10px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12, opacity:peut("configuration","delete")?1:0.5 }}>
                              🗑️
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Modal Ajouter un centre */}
                    {showAddCentre && (
                      <ModaleAnimee ouvert
                        onClicVoile={e => { if(e.target===e.currentTarget) setShowAddCentre(false); }}
                        style={{ position:"fixed", inset:0, background:"rgba(15,23,42,0.55)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
                        panneauStyle={{ background:"#fff", borderRadius:20, width:"100%", maxWidth:"90vw", boxShadow:"0 24px 60px rgba(0,0,0,0.25)", overflow:"hidden" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:12, padding:"18px 24px", borderBottom:"1px solid #f1f5f9" }}>
                            <h3 style={{ margin:0, fontSize:15, fontWeight:800, color:"#0f172a", flex:1 }}>➕ Nouveau centre BET</h3>
                            <button onClick={() => setShowAddCentre(false)} style={{ width:30, height:30, borderRadius:8, border:"none", background:"#f1f5f9", cursor:"pointer", fontSize:16, color:"#64748b" }}>✕</button>
                          </div>
                          <div style={{ padding:"20px 24px", display:"flex", flexDirection:"column", gap:12 }}>
                            {[
                              { field:"name",      label:"Nom du centre",      placeholder:"BET Cocody" },
                              { field:"ville",     label:"Ville",              placeholder:"Abidjan" },
                              { field:"addr",      label:"Adresse physique",   placeholder:"Cocody Danga, Abidjan" },
                              { field:"telephone", label:"Téléphone",          placeholder:"+225 07 00 000 00" },
                              { field:"email",     label:"E-mail",             placeholder:"nouveau@bet-ci.com" },
                            ].map(f => (
                              <div key={f.field}>
                                <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>{f.label}</label>
                                <input value={newCentreForm[f.field]} onChange={e => setNewCentreForm(x => ({...x,[f.field]:e.target.value}))}
                                  placeholder={f.placeholder} style={{ width:"100%", padding:"9px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, color:"#0f172a", background:"#fff", boxSizing:"border-box" }} />
                              </div>
                            ))}
                            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                              <div>
                                <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Latitude</label>
                                <input value={newCentreForm.lat} onChange={e => setNewCentreForm(x=>({...x,lat:e.target.value}))} placeholder="5.3742" style={{ width:"100%", padding:"9px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, boxSizing:"border-box" }} />
                              </div>
                              <div>
                                <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Longitude</label>
                                <input value={newCentreForm.lng} onChange={e => setNewCentreForm(x=>({...x,lng:e.target.value}))} placeholder="-3.9832" style={{ width:"100%", padding:"9px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, boxSizing:"border-box" }} />
                              </div>
                            </div>
                            <div>
                              <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:6 }}>Couleur</label>
                              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                                {CENTRES_BET_COLORS.map(col => (
                                  <button key={col} onClick={() => setNewCentreForm(x=>({...x,color:col}))} type="button"
                                    style={{ width:28, height:28, borderRadius:"50%", background:col, border:`3px solid ${newCentreForm.color===col?"#0f172a":"transparent"}`, cursor:"pointer" }} />
                                ))}
                              </div>
                            </div>
                          </div>
                          <div style={{ display:"flex", gap:10, justifyContent:"flex-end", padding:"16px 24px", borderTop:"1px solid #f1f5f9", background:"#fafafa" }}>
                            <button onClick={() => setShowAddCentre(false)} style={{ padding:"10px 20px", background:"#f1f5f9", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:13, color:"#374151" }}>Annuler</button>
                            <button onClick={() => {
                              if (!newCentreForm.name.trim() || !newCentreForm.ville.trim()) { toast.error("Nom et ville requis"); return; }
                              const key = newCentreForm.name.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g,"").replace(/[^a-z0-9]/g,"_").replace(/_+/g,"_");
                              const nouveau = {
                                key, name:newCentreForm.name.trim(), ville:newCentreForm.ville.trim(),
                                addr:newCentreForm.addr.trim(), lat:parseFloat(newCentreForm.lat)||0, lng:parseFloat(newCentreForm.lng)||0,
                                color:newCentreForm.color, actif:true,
                                description:"", horaires:"Lun–Ven : 08h–19h | Sam : 09h–17h",
                                telephone:newCentreForm.telephone.trim(), email:newCentreForm.email.trim(),
                                photos:[], brochure_url:"", brochure_nom:`Brochure ${newCentreForm.name.trim()}.pdf`,
                                maps_url:"", maps_embed:"",
                                cta:{ rdv:{actif:true,lien:"/contact"}, inscrire:{actif:true,lien:"/parcours/particulier"}, contact:{actif:true,lien:"/contact"} },
                                offres:[...DEFAULT_OFFRES],
                                assistantes:[
                                  { nom:"Assistante 1", phone:"", message:`Bonjour, je souhaite avoir des informations sur les cours d'anglais chez ${newCentreForm.name.trim()}.` },
                                ],
                              };
                              const updated = [...centresMaster, nouveau];
                              saveCentresMaster(updated);
                              setCentresMaster(updated);
                              setShowAddCentre(false);
                              setNewCentreForm({ name:"", ville:"", addr:"", telephone:"", email:"", color:"#0891b2", lat:"", lng:"" });
                              toast.success(`Centre ${nouveau.name} créé`);
                            }} disabled={!peut("configuration","create")} title={!peut("configuration","create")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                              style={{ padding:"10px 24px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:800, fontSize:13, opacity:peut("configuration","create")?1:0.5 }}>
                              {peut("configuration","create")?"✅ Créer":"🔒 Créer"}
                            </button>
                          </div>
                      </ModaleAnimee>
                    )}

                    {/* Modal Modifier un centre */}
                    {centreEditKey && centreEditData && (() => {
                      const updateField = (field, val) => setCentreEditData(d => ({ ...d, [field]: val }));
                      const updateOffre  = (id, field, val) => setCentreEditData(d => ({ ...d, offres: d.offres.map(o => o.id === id ? { ...o, [field]: val } : o) }));
                      const removeOffre  = (id) => setCentreEditData(d => ({ ...d, offres: (d.offres||[]).filter(o => o.id !== id) }));
                      const addOffre     = () => setCentreEditData(d => { const newId = `offre_${Date.now()}`; return { ...d, offres: [...(d.offres||[]), { id:newId, label:"Nouvelle offre", actif:true, prix:"", duration:"", desc:"", brochure_url:"", brochure_nom:"", type_coaching:"groupe" }] }; });
                      const updateCta   = (btn, field, val) => setCentreEditData(d => ({ ...d, cta:{ ...(d.cta||{}), [btn]:{ ...(d.cta?.[btn]||{}), [field]:val } } }));
                      const addPhoto    = () => { if (!newPhotoUrl.trim()) return; setCentreEditData(d => ({ ...d, photos:[...(d.photos||[]), newPhotoUrl.trim()] })); setNewPhotoUrl(""); };
                      const removePhoto = (idx) => setCentreEditData(d => ({ ...d, photos:(d.photos||[]).filter((_,i) => i!==idx) }));
                      const updateAsst  = (idx, field, val) => setCentreEditData(d => ({ ...d, assistantes:d.assistantes.map((a,i) => i===idx ? {...a,[field]:val} : a) }));
                      const addAsst     = () => setCentreEditData(d => ({ ...d, assistantes:[...(d.assistantes||[]), { nom:`Assistante ${(d.assistantes||[]).length+1}`, phone:"", message:`Bonjour, je souhaite avoir des informations sur les cours d'anglais chez ${d.name}.` }] }));
                      const removeAsst  = (idx) => setCentreEditData(d => ({ ...d, assistantes:(d.assistantes||[]).filter((_,i) => i!==idx) }));
                      const updateAdvantage  = (idx, val) => setCentreEditData(d => ({ ...d, advantages:(d.advantages||[]).map((a,i) => i===idx ? val : a) }));
                      const addAdvantage     = () => setCentreEditData(d => ({ ...d, advantages:[...(d.advantages||[]), ""] }));
                      const removeAdvantage  = (idx) => setCentreEditData(d => ({ ...d, advantages:(d.advantages||[]).filter((_,i) => i!==idx) }));
                      const updateTestimonial= (idx, field, val) => setCentreEditData(d => ({ ...d, testimonials:(d.testimonials||[]).map((t,i) => i===idx ? {...t,[field]:val} : t) }));
                      const addTestimonial   = () => setCentreEditData(d => ({ ...d, testimonials:[...(d.testimonials||[]), { name:"", text:"", avatar:"👤" }] }));
                      const removeTestimonial= (idx) => setCentreEditData(d => ({ ...d, testimonials:(d.testimonials||[]).filter((_,i) => i!==idx) }));
                      const updateFaq        = (idx, field, val) => setCentreEditData(d => ({ ...d, faq:(d.faq||[]).map((f,i) => i===idx ? {...f,[field]:val} : f) }));
                      const addFaq           = () => setCentreEditData(d => ({ ...d, faq:[...(d.faq||[]), { q:"", a:"" }] }));
                      const removeFaq        = (idx) => setCentreEditData(d => ({ ...d, faq:(d.faq||[]).filter((_,i) => i!==idx) }));
                      const handleSave  = () => {
                        setCentresSaving(true);
                        const updated = centresMaster.map(c => c.key === centreEditKey ? centreEditData : c);
                        saveCentresMaster(updated);
                        setCentresMaster(updated);
                        setCentreEditKey(null); setCentreEditData(null); setNewPhotoUrl("");
                        setCentresSaving(false);
                        toast.success(`Centre ${centreEditData.name} mis à jour - Navbar & parcours synchronisés`);
                      };
                      const closeModal = () => { setCentreEditKey(null); setCentreEditData(null); setNewPhotoUrl(""); };
                      const iStyle = { width:"100%", padding:"9px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, color:"#0f172a", background:"#fff", boxSizing:"border-box" };
                      const lStyle = { fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 };
                      const sectionTitle = (t) => <div style={{ fontSize:12, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:".06em", marginBottom:12, paddingBottom:6, borderBottom:"1px solid #f1f5f9" }}>{t}</div>;
                      const commercialesAffilies = (users||[]).filter(u => u.role==="commercial" && (u.scope||[]).includes(centreEditData.key));
                      return (
                        <ModaleAnimee ouvert
                          onClicVoile={e => { if(e.target===e.currentTarget) closeModal(); }}
                          style={{ position:"fixed", inset:0, background:"rgba(15,23,42,0.55)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
                          panneauStyle={{ background:"#fff", borderRadius:20, width:"100%", maxWidth:"90vw", maxHeight:"90vh", display:"flex", flexDirection:"column", boxShadow:"0 24px 60px rgba(0,0,0,0.25)", overflow:"hidden" }}>

                            {/* Header */}
                            <div style={{ display:"flex", alignItems:"center", gap:12, padding:"18px 24px", borderBottom:"1px solid #f1f5f9", flexShrink:0 }}>
                              <div style={{ width:36, height:36, borderRadius:10, background:centreEditData.color+"22", border:`2px solid ${centreEditData.color}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>📍</div>
                              <div style={{ flex:1 }}>
                                <h3 style={{ margin:0, fontSize:15, fontWeight:800, color:"#0f172a" }}>{centreEditData.name}</h3>
                                <span style={{ fontSize:11, color:"#64748b" }}>{centreEditData.ville}</span>
                              </div>
                              <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                                <div>
                                  {CENTRES_BET_COLORS.map(col => (
                                    <button key={col} onClick={() => updateField("color",col)} type="button"
                                      style={{ width:18, height:18, borderRadius:"50%", background:col, border:`2px solid ${centreEditData.color===col?"#0f172a":"transparent"}`, cursor:"pointer", margin:"0 2px" }} />
                                  ))}
                                </div>
                                <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, background:centreEditData.actif?"#dcfce7":"#fee2e2", color:centreEditData.actif?"#166534":"#991b1b" }}>
                                  {centreEditData.actif ? "Actif" : "Inactif"}
                                </span>
                              </div>
                              <button onClick={closeModal} style={{ width:30, height:30, borderRadius:8, border:"none", background:"#f1f5f9", cursor:"pointer", fontSize:16, color:"#64748b", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>✕</button>
                            </div>

                            {/* Corps scrollable */}
                            <div style={{ overflowY:"auto", flex:1, padding:"20px 24px", display:"flex", flexDirection:"column", gap:22 }}>

                              {/* Infos générales */}
                              <div>
                                {sectionTitle("Informations générales")}
                                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                                  <div><label style={lStyle}>Nom affiché</label><input style={iStyle} value={centreEditData.name} onChange={e => updateField("name",e.target.value)} /></div>
                                  <div><label style={lStyle}>Ville</label><input style={iStyle} value={centreEditData.ville} onChange={e => updateField("ville",e.target.value)} /></div>
                                  <div><label style={lStyle}>Horaires</label><input style={iStyle} value={centreEditData.horaires} onChange={e => updateField("horaires",e.target.value)} placeholder="Lun–Ven : 08h–19h | Sam : 09h–17h" /></div>
                                  <div><label style={lStyle}>Téléphone</label><input style={iStyle} value={centreEditData.telephone} onChange={e => updateField("telephone",e.target.value)} /></div>
                                  <div><label style={lStyle}>E-mail</label><input style={iStyle} value={centreEditData.email} onChange={e => updateField("email",e.target.value)} type="email" /></div>
                                  <div style={{ display:"flex", alignItems:"center", gap:10, paddingTop:20 }}>
                                    <label style={{ ...lStyle, margin:0 }}>Visible</label>
                                    <button onClick={() => updateField("actif",!centreEditData.actif)} type="button" style={{ padding:"5px 14px", borderRadius:20, border:"none", cursor:"pointer", fontWeight:700, fontSize:12, background:centreEditData.actif?"#dcfce7":"#fee2e2", color:centreEditData.actif?"#166534":"#991b1b" }}>
                                      {centreEditData.actif ? "✅ Actif" : "❌ Inactif"}
                                    </button>
                                  </div>
                                  <div style={{ gridColumn:"1/-1" }}><label style={lStyle}>Description</label><textarea style={{ ...iStyle, minHeight:64, resize:"vertical" }} value={centreEditData.description} onChange={e => updateField("description",e.target.value)} /></div>
                                  <div><label style={lStyle}>Latitude</label><input style={iStyle} type="number" step="0.0001" value={centreEditData.lat||""} onChange={e => updateField("lat",parseFloat(e.target.value)||0)} placeholder="5.3699" /></div>
                                  <div><label style={lStyle}>Longitude</label><input style={iStyle} type="number" step="0.0001" value={centreEditData.lng||""} onChange={e => updateField("lng",parseFloat(e.target.value)||0)} placeholder="-3.9674" /></div>
                                </div>
                              </div>

                              {/* Adresse & Maps */}
                              <div>
                                {sectionTitle("📍 Adresse & Google Maps")}
                                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                                  <div><label style={lStyle}>Adresse physique</label><input style={iStyle} value={centreEditData.addr} onChange={e => updateField("addr",e.target.value)} placeholder="Ex : Angré 7ème Tranche, Abidjan" /></div>
                                  <div><label style={lStyle}>Lien Google Maps direct</label><input style={iStyle} value={centreEditData.maps_url||""} onChange={e => updateField("maps_url",e.target.value)} placeholder="https://maps.google.com/?q=…" /></div>
                                  <div>
                                    <label style={lStyle}>URL embed iframe</label>
                                    <input style={iStyle} value={centreEditData.maps_embed||""} onChange={e => updateField("maps_embed",e.target.value)} placeholder="https://www.google.com/maps/embed?pb=…" />
                                    <p style={{ fontSize:11, color:"#94a3b8", margin:"4px 0 0" }}>Google Maps → Partager → Intégrer une carte → copier le src de l'iframe</p>
                                  </div>
                                  {centreEditData.maps_embed && (
                                    <div style={{ borderRadius:10, overflow:"hidden", border:"1px solid #e5e7eb", height:160 }}>
                                      <iframe title="maps-preview" src={centreEditData.maps_embed} width="100%" height="160" style={{ border:0, display:"block" }} allowFullScreen loading="lazy" />
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Brochure PDF */}
                              <div>
                                {sectionTitle("📄 Brochure PDF (téléchargeable par le client)")}
                                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                                  <div><label style={lStyle}>URL du PDF</label><input style={iStyle} value={centreEditData.brochure_url||""} onChange={e => updateField("brochure_url",e.target.value)} placeholder="https://… ou /brochures/angre.pdf" /></div>
                                  <div><label style={lStyle}>Nom affiché du fichier</label><input style={iStyle} value={centreEditData.brochure_nom||""} onChange={e => updateField("brochure_nom",e.target.value)} placeholder="Brochure BET Angré.pdf" /></div>
                                  {centreEditData.brochure_url && (
                                    <a href={centreEditData.brochure_url} target="_blank" rel="noreferrer"
                                      style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"9px 16px", background:"#f0fdf4", color:"#166534", border:"1px solid #86efac", borderRadius:8, fontSize:12, fontWeight:700, textDecoration:"none" }}>
                                      📄 Aperçu - {centreEditData.brochure_nom||"brochure.pdf"}
                                    </a>
                                  )}
                                </div>
                              </div>

                              {/* Galerie photos */}
                              <div>
                                {sectionTitle("🖼️ Galerie photos")}
                                <div style={{ display:"flex", gap:8, marginBottom:10 }}>
                                  <input style={{ ...iStyle, flex:1 }} value={newPhotoUrl} onChange={e => setNewPhotoUrl(e.target.value)} placeholder="URL photo (https://…)" onKeyDown={e => e.key==="Enter" && addPhoto()} />
                                  <button onClick={addPhoto} type="button" style={{ padding:"9px 16px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12, flexShrink:0 }}>＋ Ajouter</button>
                                </div>
                                {(centreEditData.photos||[]).length === 0 && <p style={{ fontSize:12, color:"#94a3b8", fontStyle:"italic", margin:0 }}>Aucune photo.</p>}
                                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))", gap:8 }}>
                                  {(centreEditData.photos||[]).map((url, idx) => (
                                    <div key={idx} style={{ position:"relative", borderRadius:8, overflow:"hidden", aspectRatio:"4/3", background:"#f1f5f9" }}>
                                      <img src={url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} onError={e => { e.currentTarget.style.opacity=".3"; }} />
                                      <button onClick={() => removePhoto(idx)} type="button" style={{ position:"absolute", top:4, right:4, width:22, height:22, borderRadius:"50%", background:"rgba(220,38,38,.9)", color:"#fff", border:"none", cursor:"pointer", fontSize:12, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700 }}>✕</button>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Page Cabinet (CourseDetail) */}
                              <div>
                                {sectionTitle("🎓 Contenu de la page Cabinet (CourseDetail)")}
                                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
                                  <div style={{ gridColumn:"1/-1" }}>
                                    <label style={lStyle}>Sous-titre (affiché sous le titre)</label>
                                    <input style={iStyle} value={centreEditData.subtitle||""} onChange={e => updateField("subtitle",e.target.value)} placeholder="Apprenez en petit groupe dans nos centres modernes" />
                                  </div>
                                  <div style={{ gridColumn:"1/-1" }}>
                                    <label style={lStyle}>Image de couverture (URL)</label>
                                    <input style={iStyle} value={centreEditData.hero_image||""} onChange={e => updateField("hero_image",e.target.value)} placeholder="https://images.unsplash.com/…" />
                                    {centreEditData.hero_image && <img src={centreEditData.hero_image} alt="" style={{ marginTop:8, width:"100%", height:100, objectFit:"cover", borderRadius:8 }} onError={e => { e.currentTarget.style.display="none"; }} />}
                                  </div>
                                </div>

                                {/* Avantages */}
                                <label style={{ ...lStyle, marginBottom:8 }}>✅ Avantages exclusifs (section "Aperçu")</label>
                                <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:14 }}>
                                  {(centreEditData.advantages||[]).map((adv, idx) => (
                                    <div key={idx} style={{ display:"flex", gap:8, alignItems:"center" }}>
                                      <input style={{ ...iStyle, flex:1, fontSize:12 }} value={adv} onChange={e => updateAdvantage(idx, e.target.value)} placeholder="Ex : Groupes de 6 personnes maximum" />
                                      <button onClick={() => removeAdvantage(idx)} type="button" style={{ padding:"9px 10px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, flexShrink:0 }}>🗑️</button>
                                    </div>
                                  ))}
                                  <button onClick={addAdvantage} type="button" style={{ padding:"8px 0", background:"#f0fdf4", color:"#166534", border:"1.5px dashed #86efac", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>＋ Ajouter un avantage</button>
                                </div>

                                {/* Témoignages */}
                                <label style={{ ...lStyle, marginBottom:8 }}>💬 Témoignages (onglet "Avis")</label>
                                <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:14 }}>
                                  {(centreEditData.testimonials||[]).map((t, idx) => (
                                    <div key={idx} style={{ background:"#f8fafc", border:"1.5px solid #e5e7eb", borderRadius:10, padding:"12px 14px" }}>
                                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto", gap:8, alignItems:"end", marginBottom:8 }}>
                                        <div><label style={lStyle}>Nom</label><input style={{ ...iStyle, fontSize:12 }} value={t.name} onChange={e => updateTestimonial(idx,"name",e.target.value)} placeholder="Fatou S." /></div>
                                        <div><label style={lStyle}>Avatar (emoji)</label><input style={{ ...iStyle, fontSize:12 }} value={t.avatar} onChange={e => updateTestimonial(idx,"avatar",e.target.value)} placeholder="👩‍🎓" /></div>
                                        <button onClick={() => removeTestimonial(idx)} type="button" style={{ padding:"9px 10px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, alignSelf:"end" }}>🗑️</button>
                                      </div>
                                      <div><label style={lStyle}>Témoignage</label><textarea style={{ ...iStyle, fontSize:12, minHeight:48, resize:"none" }} value={t.text} onChange={e => updateTestimonial(idx,"text",e.target.value)} placeholder="Mon expérience chez BET…" /></div>
                                    </div>
                                  ))}
                                  <button onClick={addTestimonial} type="button" style={{ padding:"9px 0", background:"#f0f9ff", color:"#0369a1", border:"1.5px dashed #7dd3fc", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>＋ Ajouter un témoignage</button>
                                </div>

                                {/* FAQ */}
                                <label style={{ ...lStyle, marginBottom:8 }}>❓ FAQ (onglet "FAQ")</label>
                                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                                  {(centreEditData.faq||[]).map((item, idx) => (
                                    <div key={idx} style={{ background:"#f8fafc", border:"1.5px solid #e5e7eb", borderRadius:10, padding:"12px 14px" }}>
                                      <div style={{ display:"flex", gap:8, alignItems:"flex-start", marginBottom:8 }}>
                                        <div style={{ flex:1 }}><label style={lStyle}>Question</label><input style={{ ...iStyle, fontSize:12 }} value={item.q} onChange={e => updateFaq(idx,"q",e.target.value)} placeholder="Où sont situés les cabinets ?" /></div>
                                        <button onClick={() => removeFaq(idx)} type="button" style={{ padding:"9px 10px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, marginTop:20 }}>🗑️</button>
                                      </div>
                                      <div><label style={lStyle}>Réponse</label><textarea style={{ ...iStyle, fontSize:12, minHeight:52, resize:"vertical" }} value={item.a} onChange={e => updateFaq(idx,"a",e.target.value)} placeholder="Nous avons des centres à Abidjan…" /></div>
                                    </div>
                                  ))}
                                  <button onClick={addFaq} type="button" style={{ padding:"9px 0", background:"#fdf4ff", color:"#7e22ce", border:"1.5px dashed #d8b4fe", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>＋ Ajouter une question FAQ</button>
                                </div>
                              </div>

                              {/* Offres = Formules & Tarifs sur le frontend */}
                              <div>
                                {sectionTitle("📋 Offres disponibles - affiché comme « Formules & Tarifs » sur le site")}
                                <p style={{ fontSize:11, color:"#64748b", marginTop:-6, marginBottom:10 }}>Les offres actives apparaissent dans l'onglet <strong>Formules & Tarifs</strong> de la page Cabinet côté client.</p>
                                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                                  {(centreEditData.offres||[]).map(o => (
                                    <div key={o.id} style={{ background:o.actif?"#f0fdf4":"#fafafa", border:`1.5px solid ${o.actif?"#86efac":"#e5e7eb"}`, borderRadius:10, padding:"11px 13px" }}>
                                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:o.actif?10:0 }}>
                                        <button onClick={() => updateOffre(o.id,"actif",!o.actif)} type="button" style={{ width:34, height:20, borderRadius:10, border:"none", cursor:"pointer", flexShrink:0, background:o.actif?"#22c55e":"#d1d5db", position:"relative", transition:"background .2s" }}>
                                          <div style={{ width:14, height:14, borderRadius:"50%", background:"#fff", position:"absolute", top:3, left:o.actif?17:3, transition:"left .2s" }} />
                                        </button>
                                        <input
                                          value={o.label}
                                          onChange={e => updateOffre(o.id,"label",e.target.value)}
                                          style={{ flex:1, padding:"5px 9px", border:"1px solid #e5e7eb", borderRadius:7, fontSize:13, fontWeight:700, color:o.actif?"#166534":"#6b7280", background:"transparent", outline:"none" }}
                                          placeholder="Nom de l'offre"
                                        />
                                        <button onClick={() => removeOffre(o.id)} type="button" title="Supprimer cette offre" style={{ padding:"5px 8px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:7, cursor:"pointer", fontSize:13, flexShrink:0 }}>🗑️</button>
                                      </div>
                                      {o.actif && (
                                        <>
                                          {/* Type de coaching */}
                                          <div style={{ marginBottom:8 }}>
                                            <label style={lStyle}>Type de coaching</label>
                                            <div style={{ display:"flex", gap:6 }}>
                                              {[{ v:"groupe", label:"👥 Groupe", bg:"#f0fdf4", border:"#86efac", color:"#166534", activeBg:"#22c55e" },
                                                { v:"prive",  label:"🧑‍💼 Privé",  bg:"#eff6ff", border:"#93c5fd", color:"#1e40af", activeBg:"#0c33ac" }].map(opt => {
                                                const active = (o.type_coaching||"groupe") === opt.v;
                                                return (
                                                  <button key={opt.v} type="button" onClick={() => updateOffre(o.id,"type_coaching",opt.v)}
                                                    style={{ flex:1, padding:"7px 8px", borderRadius:8, border:`1.5px solid ${active ? opt.activeBg : opt.border}`, background:active ? opt.activeBg : opt.bg, color:active ? "#fff" : opt.color, fontWeight:700, fontSize:11, cursor:"pointer", transition:"all .15s" }}>
                                                    {opt.label}
                                                  </button>
                                                );
                                              })}
                                            </div>
                                            {(o.type_coaching||"groupe") === "prive" && (
                                              <p style={{ fontSize:10, color:"#3b82f6", margin:"4px 0 0" }}>💡 Le tarif sera calculé depuis la grille tarifaire (pack Classique/Intensive ci-dessous)</p>
                                            )}
                                          </div>
                                          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
                                            <div>
                                              <label style={lStyle}>Prix {(o.type_coaching||"groupe")==="prive" ? "(indicatif)" : ""}</label>
                                              <input value={o.prix} onChange={e => updateOffre(o.id,"prix",e.target.value)} style={{ ...iStyle, fontSize:12 }} placeholder={(o.type_coaching||"groupe")==="prive" ? "Ex : À partir de 150 000 F" : "30 000 F/mois"} />
                                            </div>
                                            <div>
                                              <label style={lStyle}>Durée</label>
                                              <input value={o.duration||""} onChange={e => updateOffre(o.id,"duration",e.target.value)} style={{ ...iStyle, fontSize:12 }} placeholder="Sans engagement" />
                                            </div>
                                          </div>
                                          <textarea value={o.desc} onChange={e => updateOffre(o.id,"desc",e.target.value)} style={{ width:"100%", padding:"6px 10px", border:"1px solid #e2e8f0", borderRadius:7, fontSize:12, resize:"none", minHeight:40, color:"#374151", boxSizing:"border-box" }} placeholder="Description courte affichée sous le prix…" />
                                          {/* Brochure PDF par offre */}
                                          <div style={{ marginTop:8 }}>
                                            <label style={{ fontSize:11, fontWeight:600, color:"#374151", display:"block", marginBottom:5 }}>📄 Brochure PDF (téléchargeable par le client)</label>
                                            {o.brochure_url ? (
                                              <div style={{ display:"flex", alignItems:"center", gap:8, background:"#eff6ff", border:"1.5px solid #bae6fd", borderRadius:8, padding:"7px 11px" }}>
                                                <span style={{ fontSize:15 }}>📄</span>
                                                <a href={o.brochure_url} target="_blank" rel="noopener noreferrer" style={{ flex:1, fontSize:11, fontWeight:600, color:"#1e3a8a", textDecoration:"none", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{o.brochure_nom || "Brochure.pdf"}</a>
                                                <CloudinaryUpload type="document" compact label="Remplacer" onSuccess={f => updateOffre(o.id,"brochure_url",f.url) || updateOffre(o.id,"brochure_nom",f.original_name)} style={{ flexShrink:0 }} />
                                                <button onClick={() => { updateOffre(o.id,"brochure_url",""); updateOffre(o.id,"brochure_nom",""); }} type="button" style={{ width:24, height:24, borderRadius:6, border:"none", background:"#fee2e2", color:"#dc2626", cursor:"pointer", fontSize:12, flexShrink:0 }}>✕</button>
                                              </div>
                                            ) : (
                                              <CloudinaryUpload type="document" label="Uploader la brochure PDF" onSuccess={f => { updateOffre(o.id,"brochure_url",f.url); updateOffre(o.id,"brochure_nom",f.original_name); }} />
                                            )}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  ))}
                                  <button onClick={addOffre} type="button" style={{ padding:"9px 0", background:"#fffbeb", color:"#92400e", border:"1.5px dashed #fcd34d", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>＋ Ajouter une offre</button>
                                </div>
                              </div>

                              {/* Grille tarifaire coaching privé - centres */}
                              <div>
                                {sectionTitle("💰 Grille tarifaire - Coaching privé (cabinet & domicile)")}
                                <p style={{ fontSize:11, color:"#64748b", marginTop:-6, marginBottom:10 }}>Tarifs mensuels utilisés pour les offres de coaching privé dans ce centre et dans le PA Advisor.</p>
                                <div style={{ background:"#fff", borderRadius:8, border:"1px solid #e5e7eb", overflow:"hidden", marginBottom:12 }}>
                                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                                    <thead>
                                      <tr style={{ background:"#1e3a5f" }}>
                                        {["Formule","Fréquence","🏢 Cabinet","🏠 Domicile"].map(h=>(
                                          <th key={h} style={{ padding:"7px 12px", color:"#fff", fontWeight:700, textAlign:"left", fontSize:11 }}>{h}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {Object.entries(grillePrivee).map(([key, g], gi) => (
                                        <tr key={key} style={{ background:gi%2===0?"#fff":"#f8fafc", borderBottom:"1px solid #f1f5f9" }}>
                                          <td style={{ padding:"7px 12px", fontWeight:700 }}>{key==="classique"?"🔵":"🔴"} {g.label}</td>
                                          <td style={{ padding:"7px 12px", fontSize:11, color:"#6b7280" }}>{g.frequence}</td>
                                          <td style={{ padding:"7px 12px", fontWeight:700, color:"#1e3a8a" }}>{Number(g.cabinet).toLocaleString("fr-FR")} F / mois</td>
                                          <td style={{ padding:"7px 12px", fontWeight:700, color:"#d97706" }}>{Number(g.domicile).toLocaleString("fr-FR")} F / mois</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                                  {Object.entries(grillePrivee).map(([key, g]) => (
                                    <div key={key} style={{ background:"#f8fafc", borderRadius:8, padding:"10px 12px", border:"1px solid #e5e7eb" }}>
                                      <div style={{ fontSize:11, fontWeight:800, color:"#0f172a", marginBottom:8 }}>{key==="classique"?"🔵":"🔴"} {g.label}</div>
                                      <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr", gap:8 }}>
                                        <div>
                                          <label style={lStyle}>Fréquence</label>
                                          <input value={g.frequence} onChange={e=>setGrillePrivee(prev=>({...prev,[key]:{...prev[key],frequence:e.target.value}}))}
                                            style={{ ...iStyle, fontSize:11 }} />
                                        </div>
                                        <div>
                                          <label style={lStyle}>Cabinet (F/mois)</label>
                                          <input type="number" value={g.cabinet} onChange={e=>setGrillePrivee(prev=>({...prev,[key]:{...prev[key],cabinet:Number(e.target.value)}}))}
                                            style={{ ...iStyle, fontSize:11 }} />
                                        </div>
                                        <div>
                                          <label style={lStyle}>Domicile (F/mois)</label>
                                          <input type="number" value={g.domicile} onChange={e=>setGrillePrivee(prev=>({...prev,[key]:{...prev[key],domicile:Number(e.target.value)}}))}
                                            style={{ ...iStyle, fontSize:11 }} />
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                {grilleMsg && (
                                  <div style={{ marginTop:10, padding:"7px 10px", borderRadius:6, background:grilleMsg.type==="success"?"#d1fae5":"#fee2e2", color:grilleMsg.type==="success"?"#065f46":"#dc2626", fontSize:11, fontWeight:600 }}>
                                    {grilleMsg.text}
                                  </div>
                                )}
                                <button onClick={saveGrillePrivee} disabled={grilleSaving} type="button"
                                  style={{ marginTop:10, padding:"7px 18px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:7, fontWeight:700, fontSize:11, cursor:"pointer", opacity:grilleSaving?0.7:1 }}>
                                  {grilleSaving ? "⏳…" : "💾 Enregistrer la grille tarifaire"}
                                </button>
                              </div>

                              {/* Assistantes WhatsApp */}
                              <div>
                                {sectionTitle("💬 Assistantes WhatsApp (contact client)")}
                                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                                  {(centreEditData.assistantes||[]).map((a, idx) => (
                                    <div key={idx} style={{ background:"#f8fafc", border:"1.5px solid #e5e7eb", borderRadius:10, padding:"12px 14px" }}>
                                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto", gap:8, alignItems:"end" }}>
                                        <div><label style={lStyle}>Nom</label><input value={a.nom} onChange={e => updateAsst(idx,"nom",e.target.value)} style={{ ...iStyle, fontSize:12 }} /></div>
                                        <div><label style={lStyle}>Téléphone WhatsApp</label><input value={a.phone} onChange={e => updateAsst(idx,"phone",e.target.value)} placeholder="225XXXXXXXXX" style={{ ...iStyle, fontSize:12 }} /></div>
                                        <button onClick={() => removeAsst(idx)} style={{ padding:"9px 10px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, alignSelf:"end" }}>🗑️</button>
                                      </div>
                                      <div style={{ marginTop:8 }}><label style={lStyle}>Message pré-rempli</label><textarea value={a.message} onChange={e => updateAsst(idx,"message",e.target.value)} style={{ ...iStyle, fontSize:12, minHeight:44, resize:"none" }} /></div>
                                    </div>
                                  ))}
                                  <button onClick={addAsst} style={{ padding:"9px 0", background:"#f0fdf4", color:"#166534", border:"1.5px dashed #86efac", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>＋ Ajouter une assistante</button>
                                </div>
                              </div>

                              {/* Commerciales affiliées */}
                              <div>
                                {sectionTitle("👤 Commerciales affiliées à ce centre")}
                                {commercialesAffilies.length === 0 ? (
                                  <p style={{ fontSize:12, color:"#94a3b8", fontStyle:"italic" }}>Aucune commerciale affiliée. Affectez-les depuis Gestion des utilisateurs en assignant le scope "{centreEditData.key}".</p>
                                ) : (
                                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                                    {commercialesAffilies.map(u => (
                                      <div key={u.id} style={{ display:"flex", alignItems:"center", gap:12, background:"#f8fafc", border:"1px solid #e5e7eb", borderRadius:10, padding:"10px 14px" }}>
                                        <div style={{ width:34, height:34, borderRadius:"50%", background:BET_LIGHT, color:BET_COLOR, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:13, flexShrink:0 }}>
                                          {((u.prenom||"")[0]||"")}{((u.nom||"")[0]||"")}
                                        </div>
                                        <div style={{ flex:1 }}>
                                          <div style={{ fontWeight:700, fontSize:13, color:"#0f172a" }}>{u.prenom} {u.nom}</div>
                                          <div style={{ fontSize:11, color:"#64748b" }}>{u.email}</div>
                                        </div>
                                        <span style={{ padding:"3px 8px", borderRadius:20, fontSize:10, fontWeight:700, background:u.actif?"#dcfce7":"#fee2e2", color:u.actif?"#166534":"#991b1b" }}>{u.actif?"Actif":"Inactif"}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* CTA */}
                              <div>
                                {sectionTitle("🔘 Boutons d'action (CTA)")}
                                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                                  {[
                                    { key:"rdv",      label:"Prendre RDV",  emoji:"📅", color:"#0891b2" },
                                    { key:"inscrire", label:"S'inscrire",   emoji:"✍️", color:"#22c55e" },
                                    { key:"contact",  label:"Me contacter", emoji:"💬", color:"#f97316" },
                                  ].map(btn => {
                                    const val = centreEditData.cta?.[btn.key] || { actif:true, lien:"" };
                                    return (
                                      <div key={btn.key} style={{ background:val.actif?"#f8fafc":"#fafafa", border:`1.5px solid ${val.actif?btn.color+"55":"#e5e7eb"}`, borderRadius:10, padding:"11px 14px" }}>
                                        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:val.actif?8:0 }}>
                                          <button onClick={() => updateCta(btn.key,"actif",!val.actif)} type="button" style={{ width:34, height:20, borderRadius:10, border:"none", cursor:"pointer", flexShrink:0, background:val.actif?"#22c55e":"#d1d5db", position:"relative", transition:"background .2s" }}>
                                            <div style={{ width:14, height:14, borderRadius:"50%", background:"#fff", position:"absolute", top:3, left:val.actif?17:3, transition:"left .2s" }} />
                                          </button>
                                          <span style={{ fontSize:16 }}>{btn.emoji}</span>
                                          <span style={{ fontWeight:700, fontSize:13, color:val.actif?"#0f172a":"#6b7280", flex:1 }}>{btn.label}</span>
                                        </div>
                                        {val.actif && <input value={val.lien} onChange={e => updateCta(btn.key,"lien",e.target.value)} style={{ ...iStyle, fontSize:12, padding:"7px 10px" }} placeholder="Lien (/contact, /parcours/particulier, https://…)" />}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                            </div>

                            {/* Footer */}
                            <div style={{ display:"flex", gap:10, justifyContent:"flex-end", padding:"16px 24px", borderTop:"1px solid #f1f5f9", flexShrink:0, background:"#fafafa" }}>
                              <button onClick={closeModal} style={{ padding:"10px 20px", background:"#f1f5f9", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:13, color:"#374151" }}>Annuler</button>
                              <button onClick={handleSave} disabled={centresSaving||!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined} style={{ padding:"10px 24px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:800, fontSize:13, opacity:(centresSaving||!peut("configuration","update"))?.6:1 }}>
                                {centresSaving ? "Enregistrement…" : !peut("configuration","update") ? "🔒 Enregistrer & synchroniser" : "💾 Enregistrer & synchroniser"}
                              </button>
                            </div>
                        </ModaleAnimee>
                      );
                    })()}
                  {renderOffreMediaPanel("cabinet", BET_COLOR, "Cours en cabinet")}
                  </div>
                )}

                {/* ── Sous-onglet : Offres En ligne ── */}
                {platformSubTab === "catalogue" && catalogueSubTab === "offres_en_ligne" && (() => {
                  const saveAndSet = async (data) => { setOffresEnLigne(data); await saveOffresEnLigne(data); toast.success("Offres en ligne synchronisées"); };
                  const ICONS = ["👥","👤","🎯","🎓","💻","📱","🌐","⚡","🏆","📋"];
                  return (
                    <div>
                      {/* Header */}
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18 }}>
                        <div>
                          <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:"#0f172a" }}>💻 Offres En ligne</h3>
                          <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Formules affichées sur <code>/cours/en-ligne</code> et dans le parcours d'inscription en ligne.</p>
                        </div>
                        <div style={{ display:"flex", gap:8 }}>
                          <a href="/cours/en-ligne" target="_blank" rel="noopener noreferrer" style={{ padding:"8px 14px", background:"#f1f5f9", color:"#374151", borderRadius:8, fontWeight:700, fontSize:12, textDecoration:"none", display:"flex", alignItems:"center", gap:5 }}>🔗 Voir la page</a>
                          <button onClick={() => setOffreEditModal({ mode:"en_ligne", idx:-1, data:{ id:`el_${Date.now()}`, label:"Nouvelle offre", prix:"", duration:"", desc:"", actif:true, icon:"📋", type_coaching:"groupe", routing:"assistante" } })}
                            disabled={!peut("configuration","create")} title={!peut("configuration","create")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                            style={{ padding:"8px 16px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12, opacity:peut("configuration","create")?1:0.5 }}>{peut("configuration","create")?"➕ Nouvelle offre":"🔒 Nouvelle offre"}</button>
                        </div>
                      </div>
                      {/* Config page Aperçu */}
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"#eff6ff", border:"1.5px solid #bfdbfe", borderRadius:12, padding:"12px 16px", marginBottom:16 }}>
                        <div>
                          <div style={{ fontWeight:700, fontSize:13, color:"#1e3a8a" }}>📄 Contenu de l'onglet "Aperçu"</div>
                          <div style={{ fontSize:11, color:"#3b82f6", marginTop:2 }}>Description, avantages, programme, FAQ affichés sur la page du cours en ligne.</div>
                        </div>
                        <button onClick={() => { setCourseApercuEditModal({ type:"en_ligne", data: JSON.parse(JSON.stringify(courseApercu.en_ligne || {})) }); setCourseApercuSection("general"); }}
                          style={{ padding:"8px 16px", background:"#1e3a8a", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12, flexShrink:0 }}>✏️ Modifier</button>
                      </div>
                      {/* Grid */}
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:14, marginBottom:20 }}>
                        {offresEnLigne.map((o, idx) => (
                          <div key={o.id} style={{ background:"#fff", borderRadius:14, border:`2px solid ${o.actif ? BET_COLOR+"33" : "#f1f5f9"}`, padding:16, boxShadow:"0 1px 6px rgba(0,0,0,.05)", opacity:o.actif?1:.6 }}>
                            <div style={{ display:"flex", alignItems:"flex-start", gap:10, marginBottom:10 }}>
                              <div style={{ width:40, height:40, borderRadius:10, background:BET_LIGHT, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>{o.icon||"📋"}</div>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontWeight:700, fontSize:13, color:"#0f172a", lineHeight:1.3 }}>{o.label}</div>
                                <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>{o.prix}{o.duration ? ` · ${o.duration}` : ""}</div>
                              </div>
                              <span style={{ padding:"3px 8px", borderRadius:20, fontSize:10, fontWeight:700, flexShrink:0, background:o.actif?"#dcfce7":"#fee2e2", color:o.actif?"#166534":"#991b1b" }}>{o.actif?"Actif":"Inactif"}</span>
                            </div>
                            {o.desc && <div style={{ fontSize:12, color:"#475569", lineHeight:1.5, marginBottom:8, minHeight:36 }}>{o.desc}</div>}
                            <div style={{ marginBottom:10 }}>
                              <span style={{ padding:"3px 8px", borderRadius:20, fontSize:10, fontWeight:700, background: o.type_coaching==="prive" ? "#eff6ff" : "#f0fdf4", color: o.type_coaching==="prive" ? "#1e40af" : "#166534", border: `1px solid ${o.type_coaching==="prive" ? "#bfdbfe" : "#bbf7d0"}` }}>
                                {o.type_coaching==="prive" ? "🧑‍💼 PA Advisor" : "👥 Assistante en ligne"}
                              </span>
                            </div>
                            <div style={{ display:"flex", gap:6 }}>
                              <button onClick={() => setOffreEditModal({ mode:"en_ligne", idx, data:{...o} })} disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined} style={{ flex:1, padding:"7px 0", background:BET_COLOR, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:11, opacity:peut("configuration","update")?1:0.5 }}>✏️ Modifier</button>
                              <button onClick={() => { const u=[...offresEnLigne]; u[idx]={...u[idx],actif:!u[idx].actif}; saveAndSet(u); }}
                                disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":(o.actif?"Désactiver":"Activer")}
                                style={{ padding:"7px 10px", background:"#f1f5f9", color:"#374151", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:11, opacity:peut("configuration","update")?1:0.5 }}>{o.actif?"⏸":"▶️"}</button>
                              <button onClick={async () => { if(!(await confirm(`Supprimer "${o.label}" ?`, { titre: "Supprimer l'offre" }))) return; saveAndSet(offresEnLigne.filter((_,i)=>i!==idx)); }}
                                disabled={!peut("configuration","delete")} title={!peut("configuration","delete")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                                style={{ padding:"7px 10px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:11, opacity:peut("configuration","delete")?1:0.5 }}>🗑️</button>
                            </div>
                          </div>
                        ))}
                      </div>
                      {/* Modal édition offre en ligne */}
                      {offreEditModal?.mode === "en_ligne" && (
                        <ModaleAnimee ouvert
                          onClicVoile={e => { if(e.target===e.currentTarget) setOffreEditModal(null); }}
                          style={{ position:"fixed", inset:0, background:"rgba(15,23,42,.55)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
                          panneauStyle={{ background:"#fff", borderRadius:20, width:"100%", maxWidth:"90vw", maxHeight:"90vh", boxShadow:"0 24px 60px rgba(0,0,0,.25)", overflow:"hidden", display:"flex", flexDirection:"column" }}>
                            <div style={{ display:"flex", alignItems:"center", gap:12, padding:"18px 24px", borderBottom:"1px solid #f1f5f9", flexShrink:0 }}>
                              <h3 style={{ margin:0, fontSize:15, fontWeight:800, color:"#0f172a", flex:1 }}>{offreEditModal.idx === -1 ? "➕ Nouvelle offre en ligne" : "✏️ Modifier l'offre"}</h3>
                              <button onClick={() => setOffreEditModal(null)} style={{ width:30, height:30, borderRadius:8, border:"none", background:"#f1f5f9", cursor:"pointer", fontSize:16, color:"#64748b" }}>✕</button>
                            </div>
                            <div style={{ padding:"20px 24px", display:"flex", flexDirection:"column", gap:12, overflowY:"auto", flex:1 }}>
                              <div>
                                <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Icône</label>
                                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                                  {ICONS.map(ic => <button key={ic} onClick={() => setOffreEditModal(m => ({...m, data:{...m.data, icon:ic}}))} style={{ width:34, height:34, borderRadius:8, border:`2px solid ${offreEditModal.data.icon===ic ? BET_COLOR : "#e5e7eb"}`, background:offreEditModal.data.icon===ic ? BET_LIGHT : "#fff", fontSize:18, cursor:"pointer" }}>{ic}</button>)}
                                </div>
                              </div>
                              {[
                                { field:"label",    label:"Intitulé *",     placeholder:"Ex : Coaching de groupe" },
                                { field:"prix",     label:"Prix",           placeholder:"Ex : 25 000 F/mois" },
                                { field:"duration", label:"Durée",          placeholder:"Ex : Sans engagement" },
                              ].map(f => (
                                <div key={f.field}>
                                  <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>{f.label}</label>
                                  <input value={offreEditModal.data[f.field]||""} onChange={e => setOffreEditModal(m => ({...m, data:{...m.data, [f.field]:e.target.value}}))}
                                    placeholder={f.placeholder} style={{ width:"100%", padding:"9px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, boxSizing:"border-box" }} />
                                </div>
                              ))}
                              <div>
                                <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Description</label>
                                <textarea value={offreEditModal.data.desc||""} onChange={e => setOffreEditModal(m => ({...m, data:{...m.data, desc:e.target.value}}))}
                                  placeholder="Courte description visible par le client…" rows={3}
                                  style={{ width:"100%", padding:"9px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, boxSizing:"border-box", resize:"vertical" }} />
                              </div>
                              <div>
                                <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:6 }}>🖼️ Image de l'offre</label>
                                {offreEditModal.data.img ? (
                                  <div style={{ position:"relative", borderRadius:10, overflow:"hidden", border:"1.5px solid #bae6fd", marginBottom:0 }}>
                                    <img src={offreEditModal.data.img} alt="aperçu" style={{ width:"100%", height:120, objectFit:"cover", display:"block" }} />
                                    <div style={{ position:"absolute", top:6, right:6, display:"flex", gap:6 }}>
                                      <CloudinaryUpload type="image" compact label="Changer" onSuccess={f => setOffreEditModal(m => ({...m, data:{...m.data, img:f.url}}))} />
                                      <button onClick={() => setOffreEditModal(m => ({...m, data:{...m.data, img:""}}))} style={{ width:26, height:26, borderRadius:6, border:"none", background:"rgba(220,38,38,.9)", color:"#fff", cursor:"pointer", fontSize:13 }}>✕</button>
                                    </div>
                                  </div>
                                ) : (
                                  <CloudinaryUpload type="image" label="Uploader une image" onSuccess={f => setOffreEditModal(m => ({...m, data:{...m.data, img:f.url}}))} />
                                )}
                              </div>
                              <div>
                                <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:6 }}>📄 Brochure PDF</label>
                                {offreEditModal.data.brochure_url ? (
                                  <div style={{ display:"flex", alignItems:"center", gap:8, background:"#eff6ff", border:"1.5px solid #bae6fd", borderRadius:8, padding:"8px 12px" }}>
                                    <span style={{ fontSize:16 }}>📄</span>
                                    <a href={offreEditModal.data.brochure_url} target="_blank" rel="noopener noreferrer" style={{ flex:1, fontSize:12, fontWeight:600, color:BET_COLOR, textDecoration:"none", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{offreEditModal.data.brochure_nom || "Brochure.pdf"}</a>
                                    <CloudinaryUpload type="document" compact label="Remplacer" onSuccess={f => setOffreEditModal(m => ({...m, data:{...m.data, brochure_url:f.url, brochure_nom:f.original_name}}))} style={{ flexShrink:0 }} />
                                    <button onClick={() => setOffreEditModal(m => ({...m, data:{...m.data, brochure_url:"", brochure_nom:""}}))} style={{ width:26, height:26, borderRadius:6, border:"none", background:"#fee2e2", color:"#dc2626", cursor:"pointer", fontSize:13, flexShrink:0 }}>✕</button>
                                  </div>
                                ) : (
                                  <CloudinaryUpload type="document" label="Uploader la brochure PDF" onSuccess={f => setOffreEditModal(m => ({...m, data:{...m.data, brochure_url:f.url, brochure_nom:f.original_name}}))} />
                                )}
                              </div>
                              {/* Routage : champ clé pour orienter vers assistante ou PA Advisor */}
                              <div>
                                <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>🔀 Type de coaching <span style={{ color:"#ef4444" }}>*</span></label>
                                <select value={offreEditModal.data.type_coaching||"groupe"} onChange={e => {
                                  const tc = e.target.value;
                                  setOffreEditModal(m => ({...m, data:{...m.data, type_coaching:tc, routing: tc==="prive" ? "pa_advisor" : "assistante"}}));
                                }} style={{ width:"100%", padding:"9px 12px", border:"1.5px solid #e5e7eb", borderRadius:8, fontSize:13, boxSizing:"border-box", cursor:"pointer" }}>
                                  <option value="groupe">👥 Coaching de groupe - géré par une Assistante en ligne</option>
                                  <option value="prive">🧑‍💼 Coaching privé - géré par un PA Advisor</option>
                                </select>
                                <p style={{ fontSize:11, color:"#6b7280", margin:"4px 0 0", lineHeight:1.4 }}>
                                  {(offreEditModal.data.type_coaching||"groupe")==="prive"
                                    ? "⚠️ Cette offre sera routée vers un PA Advisor, quel que soit son intitulé."
                                    : "ℹ️ Cette offre sera assignée à une Assistante en ligne, quel que soit son intitulé."}
                                </p>
                              </div>
                              {/* Grille tarifaire - si coaching privé */}
                              {(offreEditModal.data.type_coaching||"groupe") === "prive" && (
                                <div style={{ background:"#eff6ff", borderRadius:12, border:"1.5px solid #bfdbfe", padding:16 }}>
                                  <div style={{ fontSize:12, fontWeight:800, color:"#1e3a8a", marginBottom:10 }}>💰 Grille tarifaire - Coaching privé</div>
                                  <div style={{ background:"#fff", borderRadius:8, border:"1px solid #bfdbfe", overflow:"hidden", marginBottom:12 }}>
                                    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                                      <thead>
                                        <tr style={{ background:"#1e3a5f" }}>
                                          {["Formule","Fréquence","🏢 Cabinet","🏠 Domicile"].map(h=>(
                                            <th key={h} style={{ padding:"7px 12px", color:"#fff", fontWeight:700, textAlign:"left", fontSize:11 }}>{h}</th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {Object.entries(grillePrivee).map(([key, g], gi) => (
                                          <tr key={key} style={{ background:gi%2===0?"#fff":"#f8fafc", borderBottom:"1px solid #f1f5f9" }}>
                                            <td style={{ padding:"7px 12px", fontWeight:700 }}>{key==="classique"?"🔵":"🔴"} {g.label}</td>
                                            <td style={{ padding:"7px 12px", fontSize:11, color:"#6b7280" }}>{g.frequence}</td>
                                            <td style={{ padding:"7px 12px", fontWeight:700, color:"#1e3a8a" }}>{Number(g.cabinet).toLocaleString("fr-FR")} F</td>
                                            <td style={{ padding:"7px 12px", fontWeight:700, color:"#d97706" }}>{Number(g.domicile).toLocaleString("fr-FR")} F</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                  {Object.entries(grillePrivee).map(([key, g]) => (
                                    <div key={key} style={{ background:"#fff", borderRadius:8, padding:"10px 12px", border:"1px solid #bfdbfe", marginBottom:8 }}>
                                      <div style={{ fontSize:11, fontWeight:800, color:"#0f172a", marginBottom:8 }}>{key==="classique"?"🔵":"🔴"} {g.label}</div>
                                      <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr", gap:8 }}>
                                        <div>
                                          <label style={{ fontSize:10, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3 }}>Fréquence</label>
                                          <input value={g.frequence} onChange={e=>setGrillePrivee(prev=>({...prev,[key]:{...prev[key],frequence:e.target.value}}))}
                                            style={{ width:"100%", padding:"5px 8px", border:"1.5px solid #bfdbfe", borderRadius:6, fontSize:11, boxSizing:"border-box" }} />
                                        </div>
                                        <div>
                                          <label style={{ fontSize:10, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3 }}>Cabinet (F/mois)</label>
                                          <input type="number" value={g.cabinet} onChange={e=>setGrillePrivee(prev=>({...prev,[key]:{...prev[key],cabinet:Number(e.target.value)}}))}
                                            style={{ width:"100%", padding:"5px 8px", border:"1.5px solid #bfdbfe", borderRadius:6, fontSize:11, boxSizing:"border-box" }} />
                                        </div>
                                        <div>
                                          <label style={{ fontSize:10, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3 }}>Domicile (F/mois)</label>
                                          <input type="number" value={g.domicile} onChange={e=>setGrillePrivee(prev=>({...prev,[key]:{...prev[key],domicile:Number(e.target.value)}}))}
                                            style={{ width:"100%", padding:"5px 8px", border:"1.5px solid #bfdbfe", borderRadius:6, fontSize:11, boxSizing:"border-box" }} />
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                  {grilleMsg && (
                                    <div style={{ padding:"7px 10px", borderRadius:6, marginBottom:8, background:grilleMsg.type==="success"?"#d1fae5":"#fee2e2", color:grilleMsg.type==="success"?"#065f46":"#dc2626", fontSize:11, fontWeight:600 }}>
                                      {grilleMsg.text}
                                    </div>
                                  )}
                                  <button onClick={saveGrillePrivee} disabled={grilleSaving}
                                    style={{ padding:"6px 16px", background:"#1e3a8a", color:"#fff", border:"none", borderRadius:6, fontWeight:700, fontSize:11, cursor:"pointer", opacity:grilleSaving?0.7:1 }}>
                                    {grilleSaving ? "⏳…" : "💾 Enregistrer la grille"}
                                  </button>
                                </div>
                              )}
                              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                                <label style={{ fontSize:12, fontWeight:600, color:"#374151" }}>Actif</label>
                                <input type="checkbox" checked={!!offreEditModal.data.actif} onChange={e => setOffreEditModal(m => ({...m, data:{...m.data, actif:e.target.checked}}))} style={{ width:16, height:16, accentColor:BET_COLOR, cursor:"pointer" }} />
                              </div>
                            </div>
                            <div style={{ display:"flex", gap:10, justifyContent:"flex-end", padding:"14px 24px", borderTop:"1px solid #f1f5f9", background:"#fafafa" }}>
                              <button onClick={() => setOffreEditModal(null)} style={{ padding:"9px 18px", background:"#f1f5f9", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:13, color:"#374151" }}>Annuler</button>
                              <button onClick={() => {
                                if (!offreEditModal.data.label?.trim()) { toast.error("Intitulé requis"); return; }
                                if (!offreEditModal.data.type_coaching) { toast.error("Type de coaching requis"); return; }
                                let u;
                                if (offreEditModal.idx === -1) { u = [...offresEnLigne, offreEditModal.data]; }
                                else { u = offresEnLigne.map((o,i) => i === offreEditModal.idx ? offreEditModal.data : o); }
                                saveAndSet(u); setOffreEditModal(null);
                              }} disabled={!peut("configuration", offreEditModal.idx===-1?"create":"update")}
                                title={!peut("configuration", offreEditModal.idx===-1?"create":"update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                                style={{ padding:"9px 22px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:800, fontSize:13, opacity:peut("configuration", offreEditModal.idx===-1?"create":"update")?1:0.5 }}>
                                {peut("configuration", offreEditModal.idx===-1?"create":"update")?"💾 Enregistrer":"🔒 Enregistrer"}
                              </button>
                            </div>
                        </ModaleAnimee>
                      )}
                      {renderOffreMediaPanel("en-ligne", BET_COLOR, "Cours en ligne")}
                    </div>
                  );
                })()}

                {/* ── Sous-onglet : Offres À domicile ── */}
                {platformSubTab === "catalogue" && catalogueSubTab === "offres_domicile" && (() => {
                  const saveAndSet = async (data) => { setOffresDomicile(data); await saveOffresDomicile(data); toast.success("Offres à domicile synchronisées"); };
                  const ICONS = ["🏠","👧","👥","👤","🎯","🎓","⭐","🏆","📋","✨"];
                  return (
                    <div>
                      {/* Header */}
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18 }}>
                        <div>
                          <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:"#0f172a" }}>🏠 Offres À domicile</h3>
                          <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Formules affichées sur <code>/cours/domicile</code> - un coach se déplace chez le client.</p>
                        </div>
                        <div style={{ display:"flex", gap:8 }}>
                          <a href="/cours/domicile" target="_blank" rel="noopener noreferrer" style={{ padding:"8px 14px", background:"#f1f5f9", color:"#374151", borderRadius:8, fontWeight:700, fontSize:12, textDecoration:"none", display:"flex", alignItems:"center", gap:5 }}>🔗 Voir la page</a>
                          <button onClick={() => setOffreEditModal({ mode:"domicile", idx:-1, data:{ id:`dom_${Date.now()}`, label:"Nouvelle offre", prix:"Sur devis", duration:"Sur mesure", desc:"", actif:true, icon:"🏠", type_coaching:"prive" } })}
                            disabled={!peut("configuration","create")} title={!peut("configuration","create")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                            style={{ padding:"8px 16px", background:"#059669", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12, opacity:peut("configuration","create")?1:0.5 }}>{peut("configuration","create")?"➕ Nouvelle offre":"🔒 Nouvelle offre"}</button>
                        </div>
                      </div>
                      {/* Config page Aperçu */}
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"#f0fdf4", border:"1.5px solid #bbf7d0", borderRadius:12, padding:"12px 16px", marginBottom:16 }}>
                        <div>
                          <div style={{ fontWeight:700, fontSize:13, color:"#14532d" }}>📄 Contenu de l'onglet "Aperçu"</div>
                          <div style={{ fontSize:11, color:"#059669", marginTop:2 }}>Description, avantages, programme, FAQ affichés sur la page du cours à domicile.</div>
                        </div>
                        <button onClick={() => { setCourseApercuEditModal({ type:"domicile", data: JSON.parse(JSON.stringify(courseApercu.domicile || {})) }); setCourseApercuSection("general"); }}
                          style={{ padding:"8px 16px", background:"#059669", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12, flexShrink:0 }}>✏️ Modifier</button>
                      </div>
                      {/* Grid */}
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:14, marginBottom:20 }}>
                        {offresDomicile.map((o, idx) => (
                          <div key={o.id} style={{ background:"#fff", borderRadius:14, border:`2px solid ${o.actif ? "#05996933" : "#f1f5f9"}`, padding:16, boxShadow:"0 1px 6px rgba(0,0,0,.05)", opacity:o.actif?1:.6 }}>
                            <div style={{ display:"flex", alignItems:"flex-start", gap:10, marginBottom:10 }}>
                              <div style={{ width:40, height:40, borderRadius:10, background:"#f0fdf4", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>{o.icon||"🏠"}</div>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontWeight:700, fontSize:13, color:"#0f172a", lineHeight:1.3 }}>{o.label}</div>
                                <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>{o.prix}{o.duration ? ` · ${o.duration}` : ""}</div>
                              </div>
                              <span style={{ padding:"3px 8px", borderRadius:20, fontSize:10, fontWeight:700, flexShrink:0, background:o.actif?"#dcfce7":"#fee2e2", color:o.actif?"#166534":"#991b1b" }}>{o.actif?"Actif":"Inactif"}</span>
                            </div>
                            {o.desc && <div style={{ fontSize:12, color:"#475569", lineHeight:1.5, marginBottom:12, minHeight:36 }}>{o.desc}</div>}
                            <div style={{ display:"flex", gap:6 }}>
                              <button onClick={() => setOffreEditModal({ mode:"domicile", idx, data:{...o} })} disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined} style={{ flex:1, padding:"7px 0", background:"#059669", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:11, opacity:peut("configuration","update")?1:0.5 }}>✏️ Modifier</button>
                              <button onClick={() => { const u=[...offresDomicile]; u[idx]={...u[idx],actif:!u[idx].actif}; saveAndSet(u); }}
                                disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":(o.actif?"Désactiver":"Activer")}
                                style={{ padding:"7px 10px", background:"#f1f5f9", color:"#374151", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:11, opacity:peut("configuration","update")?1:0.5 }}>{o.actif?"⏸":"▶️"}</button>
                              <button onClick={async () => { if(!(await confirm(`Supprimer "${o.label}" ?`, { titre: "Supprimer l'offre" }))) return; saveAndSet(offresDomicile.filter((_,i)=>i!==idx)); }}
                                disabled={!peut("configuration","delete")} title={!peut("configuration","delete")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                                style={{ padding:"7px 10px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:11, opacity:peut("configuration","delete")?1:0.5 }}>🗑️</button>
                            </div>
                          </div>
                        ))}
                      </div>
                      {/* Note pédagogique */}
                      <div style={{ display:"flex", gap:10, background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10, padding:"12px 16px" }}>
                        <span style={{ flexShrink:0 }}>💡</span>
                        <p style={{ margin:0, fontSize:12, color:"#166534", lineHeight:1.6 }}>
                          Les cours à domicile sont gérés par un <strong>Responsable pédagogique</strong> qui contacte le client sous 24–48h après sa demande. Les offres affichées ici sont indicatives.
                        </p>
                      </div>
                      {/* Modal édition offre domicile */}
                      {offreEditModal?.mode === "domicile" && (
                        <ModaleAnimee ouvert
                          onClicVoile={e => { if(e.target===e.currentTarget) setOffreEditModal(null); }}
                          style={{ position:"fixed", inset:0, background:"rgba(15,23,42,.55)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
                          panneauStyle={{ background:"#fff", borderRadius:20, width:"100%", maxWidth:"90vw", maxHeight:"90vh", boxShadow:"0 24px 60px rgba(0,0,0,.25)", overflow:"hidden", display:"flex", flexDirection:"column" }}>
                            <div style={{ display:"flex", alignItems:"center", gap:12, padding:"18px 24px", borderBottom:"1px solid #f1f5f9", flexShrink:0 }}>
                              <h3 style={{ margin:0, fontSize:15, fontWeight:800, color:"#0f172a", flex:1 }}>{offreEditModal.idx === -1 ? "➕ Nouvelle offre à domicile" : "✏️ Modifier l'offre"}</h3>
                              <button onClick={() => setOffreEditModal(null)} style={{ width:30, height:30, borderRadius:8, border:"none", background:"#f1f5f9", cursor:"pointer", fontSize:16, color:"#64748b" }}>✕</button>
                            </div>
                            <div style={{ padding:"20px 24px", display:"flex", flexDirection:"column", gap:12, overflowY:"auto", flex:1 }}>
                              <div>
                                <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Icône</label>
                                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                                  {ICONS.map(ic => <button key={ic} onClick={() => setOffreEditModal(m => ({...m, data:{...m.data, icon:ic}}))} style={{ width:34, height:34, borderRadius:8, border:`2px solid ${offreEditModal.data.icon===ic ? "#059669" : "#e5e7eb"}`, background:offreEditModal.data.icon===ic ? "#f0fdf4" : "#fff", fontSize:18, cursor:"pointer" }}>{ic}</button>)}
                                </div>
                              </div>
                              {[
                                { field:"label",    label:"Intitulé *",     placeholder:"Ex : Cours privé à domicile" },
                                { field:"prix",     label:"Prix",           placeholder:"Ex : Sur devis" },
                                { field:"duration", label:"Durée",          placeholder:"Ex : Sur mesure" },
                              ].map(f => (
                                <div key={f.field}>
                                  <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>{f.label}</label>
                                  <input value={offreEditModal.data[f.field]||""} onChange={e => setOffreEditModal(m => ({...m, data:{...m.data, [f.field]:e.target.value}}))}
                                    placeholder={f.placeholder} style={{ width:"100%", padding:"9px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, boxSizing:"border-box" }} />
                                </div>
                              ))}
                              {/* Type de coaching - un coach à domicile est en 1-pour-1 par nature, "groupe"
                                  reste possible (tarif partagé) mais n'était jusqu'ici jamais configurable :
                                  toutes les offres à domicile retombaient silencieusement sur "groupe" par
                                  défaut, y compris "Cours privé à domicile". */}
                              <div>
                                <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>🔀 Type de coaching <span style={{ color:"#ef4444" }}>*</span></label>
                                <select value={offreEditModal.data.type_coaching||"groupe"} onChange={e => setOffreEditModal(m => ({...m, data:{...m.data, type_coaching:e.target.value}}))}
                                  style={{ width:"100%", padding:"9px 12px", border:"1.5px solid #e5e7eb", borderRadius:8, fontSize:13, boxSizing:"border-box", cursor:"pointer" }}>
                                  <option value="prive">👤 Coaching privé - 1-pour-1 chez le client</option>
                                  <option value="groupe">👥 Groupe chez vous - tarif partagé entre participants</option>
                                </select>
                              </div>
                              <div>
                                <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Description</label>
                                <textarea value={offreEditModal.data.desc||""} onChange={e => setOffreEditModal(m => ({...m, data:{...m.data, desc:e.target.value}}))}
                                  placeholder="Courte description visible par le client…" rows={3}
                                  style={{ width:"100%", padding:"9px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, boxSizing:"border-box", resize:"vertical" }} />
                              </div>
                              <div>
                                <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:6 }}>🖼️ Image de l'offre</label>
                                {offreEditModal.data.img ? (
                                  <div style={{ position:"relative", borderRadius:10, overflow:"hidden", border:"1.5px solid #bbf7d0", marginBottom:0 }}>
                                    <img src={offreEditModal.data.img} alt="aperçu" style={{ width:"100%", height:120, objectFit:"cover", display:"block" }} />
                                    <div style={{ position:"absolute", top:6, right:6, display:"flex", gap:6 }}>
                                      <CloudinaryUpload type="image" compact label="Changer" onSuccess={f => setOffreEditModal(m => ({...m, data:{...m.data, img:f.url}}))} />
                                      <button onClick={() => setOffreEditModal(m => ({...m, data:{...m.data, img:""}}))} style={{ width:26, height:26, borderRadius:6, border:"none", background:"rgba(220,38,38,.9)", color:"#fff", cursor:"pointer", fontSize:13 }}>✕</button>
                                    </div>
                                  </div>
                                ) : (
                                  <CloudinaryUpload type="image" label="Uploader une image" onSuccess={f => setOffreEditModal(m => ({...m, data:{...m.data, img:f.url}}))} />
                                )}
                              </div>
                              <div>
                                <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:6 }}>📄 Brochure PDF</label>
                                {offreEditModal.data.brochure_url ? (
                                  <div style={{ display:"flex", alignItems:"center", gap:8, background:"#f0fdf4", border:"1.5px solid #bbf7d0", borderRadius:8, padding:"8px 12px" }}>
                                    <span style={{ fontSize:16 }}>📄</span>
                                    <a href={offreEditModal.data.brochure_url} target="_blank" rel="noopener noreferrer" style={{ flex:1, fontSize:12, fontWeight:600, color:"#059669", textDecoration:"none", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{offreEditModal.data.brochure_nom || "Brochure.pdf"}</a>
                                    <CloudinaryUpload type="document" compact label="Remplacer" onSuccess={f => setOffreEditModal(m => ({...m, data:{...m.data, brochure_url:f.url, brochure_nom:f.original_name}}))} style={{ flexShrink:0 }} />
                                    <button onClick={() => setOffreEditModal(m => ({...m, data:{...m.data, brochure_url:"", brochure_nom:""}}))} style={{ width:26, height:26, borderRadius:6, border:"none", background:"#fee2e2", color:"#dc2626", cursor:"pointer", fontSize:13, flexShrink:0 }}>✕</button>
                                  </div>
                                ) : (
                                  <CloudinaryUpload type="document" label="Uploader la brochure PDF" onSuccess={f => setOffreEditModal(m => ({...m, data:{...m.data, brochure_url:f.url, brochure_nom:f.original_name}}))} />
                                )}
                              </div>
                              {/* Grille tarifaire coaching privé - offres domicile */}
                              <div style={{ background:"#fef3c7", borderRadius:12, border:"1.5px solid #fde68a", padding:16 }}>
                                <div style={{ fontSize:12, fontWeight:800, color:"#92400e", marginBottom:10 }}>💰 Grille tarifaire - Coaching privé</div>
                                <div style={{ background:"#fff", borderRadius:8, border:"1px solid #fde68a", overflow:"hidden", marginBottom:12 }}>
                                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                                    <thead>
                                      <tr style={{ background:"#78350f" }}>
                                        {["Formule","Fréquence","🏢 Cabinet","🏠 Domicile"].map(h=>(
                                          <th key={h} style={{ padding:"7px 12px", color:"#fff", fontWeight:700, textAlign:"left", fontSize:11 }}>{h}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {Object.entries(grillePrivee).map(([key, g], gi) => (
                                        <tr key={key} style={{ background:gi%2===0?"#fff":"#fffbeb", borderBottom:"1px solid #fef3c7" }}>
                                          <td style={{ padding:"7px 12px", fontWeight:700 }}>{key==="classique"?"🔵":"🔴"} {g.label}</td>
                                          <td style={{ padding:"7px 12px", fontSize:11, color:"#6b7280" }}>{g.frequence}</td>
                                          <td style={{ padding:"7px 12px", fontWeight:700, color:"#1e3a8a" }}>{Number(g.cabinet).toLocaleString("fr-FR")} F</td>
                                          <td style={{ padding:"7px 12px", fontWeight:700, color:"#d97706" }}>{Number(g.domicile).toLocaleString("fr-FR")} F</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                {Object.entries(grillePrivee).map(([key, g]) => (
                                  <div key={key} style={{ background:"#fff", borderRadius:8, padding:"10px 12px", border:"1px solid #fde68a", marginBottom:8 }}>
                                    <div style={{ fontSize:11, fontWeight:800, color:"#0f172a", marginBottom:8 }}>{key==="classique"?"🔵":"🔴"} {g.label}</div>
                                    <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr", gap:8 }}>
                                      <div>
                                        <label style={{ fontSize:10, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3 }}>Fréquence</label>
                                        <input value={g.frequence} onChange={e=>setGrillePrivee(prev=>({...prev,[key]:{...prev[key],frequence:e.target.value}}))}
                                          style={{ width:"100%", padding:"5px 8px", border:"1.5px solid #fde68a", borderRadius:6, fontSize:11, boxSizing:"border-box" }} />
                                      </div>
                                      <div>
                                        <label style={{ fontSize:10, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3 }}>Cabinet (F/mois)</label>
                                        <input type="number" value={g.cabinet} onChange={e=>setGrillePrivee(prev=>({...prev,[key]:{...prev[key],cabinet:Number(e.target.value)}}))}
                                          style={{ width:"100%", padding:"5px 8px", border:"1.5px solid #fde68a", borderRadius:6, fontSize:11, boxSizing:"border-box" }} />
                                      </div>
                                      <div>
                                        <label style={{ fontSize:10, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3 }}>Domicile (F/mois)</label>
                                        <input type="number" value={g.domicile} onChange={e=>setGrillePrivee(prev=>({...prev,[key]:{...prev[key],domicile:Number(e.target.value)}}))}
                                          style={{ width:"100%", padding:"5px 8px", border:"1.5px solid #fde68a", borderRadius:6, fontSize:11, boxSizing:"border-box" }} />
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                {grilleMsg && (
                                  <div style={{ padding:"7px 10px", borderRadius:6, marginBottom:8, background:grilleMsg.type==="success"?"#d1fae5":"#fee2e2", color:grilleMsg.type==="success"?"#065f46":"#dc2626", fontSize:11, fontWeight:600 }}>
                                    {grilleMsg.text}
                                  </div>
                                )}
                                <button onClick={saveGrillePrivee} disabled={grilleSaving}
                                  style={{ padding:"6px 16px", background:"#78350f", color:"#fff", border:"none", borderRadius:6, fontWeight:700, fontSize:11, cursor:"pointer", opacity:grilleSaving?0.7:1 }}>
                                  {grilleSaving ? "⏳…" : "💾 Enregistrer la grille"}
                                </button>
                              </div>
                              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                                <label style={{ fontSize:12, fontWeight:600, color:"#374151" }}>Actif</label>
                                <input type="checkbox" checked={!!offreEditModal.data.actif} onChange={e => setOffreEditModal(m => ({...m, data:{...m.data, actif:e.target.checked}}))} style={{ width:16, height:16, accentColor:"#059669", cursor:"pointer" }} />
                              </div>
                            </div>
                            <div style={{ display:"flex", gap:10, justifyContent:"flex-end", padding:"14px 24px", borderTop:"1px solid #f1f5f9", background:"#fafafa", flexShrink:0 }}>
                              <button onClick={() => setOffreEditModal(null)} style={{ padding:"9px 18px", background:"#f1f5f9", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:13, color:"#374151" }}>Annuler</button>
                              <button onClick={() => {
                                if (!offreEditModal.data.label?.trim()) { toast.error("Intitulé requis"); return; }
                                let u;
                                if (offreEditModal.idx === -1) { u = [...offresDomicile, offreEditModal.data]; }
                                else { u = offresDomicile.map((o,i) => i === offreEditModal.idx ? offreEditModal.data : o); }
                                saveAndSet(u); setOffreEditModal(null);
                              }} disabled={!peut("configuration", offreEditModal.idx===-1?"create":"update")}
                                title={!peut("configuration", offreEditModal.idx===-1?"create":"update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                                style={{ padding:"9px 22px", background:"#059669", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:800, fontSize:13, opacity:peut("configuration", offreEditModal.idx===-1?"create":"update")?1:0.5 }}>
                                {peut("configuration", offreEditModal.idx===-1?"create":"update")?"💾 Enregistrer":"🔒 Enregistrer"}
                              </button>
                            </div>
                        </ModaleAnimee>
                      )}
                      {renderOffreMediaPanel("domicile", "#059669", "Cours à domicile")}
                    </div>
                  );
                })()}

                {/* ── Sous-onglet : Certifications ── */}
                {platformSubTab === "catalogue" && catalogueSubTab === "certifications" && (() => {
                  const CERT_KEYS = ["toeic","ielts","toefl"];
                  const CERT_META = {
                    toeic:{ color:"#1e3a8a", bg:"#eff6ff", route:"/certifications/toeic" },
                    ielts:{ color:"#dc2626", bg:"#fef2f2", route:"/certifications/ielts" },
                    toefl:{ color:"#059669", bg:"#f0fdf4", route:"/certifications/toefl" },
                  };
                  return (
                    <div>
                      {/* Header */}
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18 }}>
                        <div>
                          <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:"#0f172a" }}>🏆 Certifications</h3>
                          <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>
                            Contenu des pages TOEIC, IELTS et TOEFL affichées sur le site web.
                          </p>
                        </div>
                      </div>
                      {/* 3 cartes certifications */}
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16, marginBottom:24 }}>
                        {CERT_KEYS.map(k => {
                          const c = certifConfig[k] || {};
                          const m = CERT_META[k];
                          return (
                            <div key={k} style={{ background:"#fff", borderRadius:16, border:`2px solid ${m.color}22`, overflow:"hidden", boxShadow:"0 2px 10px rgba(0,0,0,.06)" }}>
                              {c.heroImage && (
                                <img src={c.heroImage} alt={k} style={{ width:"100%", height:72, objectFit:"cover", display:"block" }} />
                              )}
                              <div style={{ padding:"14px 16px" }}>
                                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                                  <span style={{ fontWeight:900, fontSize:18, color:m.color }}>{c.name}</span>
                                  <span style={{ fontSize:10, fontWeight:700, background:m.bg, color:m.color, borderRadius:999, padding:"2px 8px" }}>Officiel</span>
                                </div>
                                <div style={{ fontSize:11, color:"#64748b", marginBottom:4, lineHeight:1.4 }}>{c.fullName}</div>
                                <div style={{ fontSize:13, fontWeight:800, color:"#0f172a", marginBottom:4 }}>{c.price}</div>
                                <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginBottom:12 }}>
                                  {c.level && <span style={{ fontSize:10, fontWeight:700, background:"#f1f5f9", color:"#475569", borderRadius:4, padding:"2px 7px" }}>📊 {c.level}</span>}
                                  {c.duration && <span style={{ fontSize:10, fontWeight:700, background:"#f1f5f9", color:"#475569", borderRadius:4, padding:"2px 7px" }}>⏱ {c.duration}</span>}
                                </div>
                                <div style={{ display:"flex", gap:6 }}>
                                  <button onClick={() => { setCertifEditModal({ key:k, data:JSON.parse(JSON.stringify(certifConfig[k]||DEFAULT_CERTIF_CONFIG[k])) }); setCertifEditSection("general"); }}
                                    style={{ flex:1, padding:"8px 0", background:m.color, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>
                                    ✏️ Modifier
                                  </button>
                                  <a href={m.route} target="_blank" rel="noopener noreferrer"
                                    style={{ padding:"8px 10px", background:"#f1f5f9", color:"#374151", borderRadius:8, fontWeight:700, fontSize:11, textDecoration:"none", display:"flex", alignItems:"center" }}>
                                    🔗
                                  </a>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {/* Info */}
                      <div style={{ display:"flex", gap:10, background:"#fffbeb", border:"1px solid #fde68a", borderRadius:10, padding:"12px 16px" }}>
                        <span style={{ flexShrink:0 }}>💡</span>
                        <p style={{ margin:0, fontSize:12, color:"#92400e", lineHeight:1.6 }}>
                          Les modifications sont enregistrées en <strong>localStorage</strong> et synchronisées avec Supabase. Elles sont visibles immédiatement sur le site web.
                        </p>
                      </div>
                      {/* Modal édition certification */}
                      {certifEditModal && (
                        <ModaleAnimee ouvert
                          onClicVoile={e => { if(e.target===e.currentTarget) setCertifEditModal(null); }}
                          style={{ position:"fixed", inset:0, background:"rgba(15,23,42,.55)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
                          panneauStyle={{ background:"#fff", borderRadius:20, width:"100%", maxWidth:"90vw", maxHeight:"90vh", overflow:"hidden", display:"flex", flexDirection:"column", boxShadow:"0 24px 60px rgba(0,0,0,.25)" }}>
                            {/* Modal header */}
                            <div style={{ display:"flex", alignItems:"center", gap:12, padding:"16px 24px", borderBottom:"1px solid #f1f5f9", flexShrink:0 }}>
                              <span style={{ fontSize:20 }}>🏆</span>
                              <h3 style={{ margin:0, fontSize:15, fontWeight:800, color:"#0f172a", flex:1 }}>
                                {certifConfig[certifEditModal.key]?.name || certifEditModal.key.toUpperCase()} - Configuration
                              </h3>
                              <button onClick={() => setCertifEditModal(null)} style={{ width:30, height:30, borderRadius:8, border:"none", background:"#f1f5f9", cursor:"pointer", fontSize:16, color:"#64748b" }}>✕</button>
                            </div>
                            {/* Section tabs */}
                            <div style={{ display:"flex", borderBottom:"1px solid #f1f5f9", overflowX:"auto", flexShrink:0 }}>
                              {[
                                {key:"general",    label:"Général"},
                                {key:"prix",       label:"Prix & Stats"},
                                {key:"contenu",    label:"Contenu"},
                                {key:"examen",     label:"Examen"},
                                {key:"programme",  label:"Programme"},
                                {key:"faq",        label:"FAQ"},
                              ].map(s => (
                                <button key={s.key} onClick={() => setCertifEditSection(s.key)}
                                  style={{ padding:"10px 16px", border:"none", background:"none", cursor:"pointer", fontWeight:certifEditSection===s.key?800:500, fontSize:12, color:certifEditSection===s.key?"#1e3a8a":"#64748b", borderBottom:certifEditSection===s.key?"2px solid #1e3a8a":"2px solid transparent", whiteSpace:"nowrap", transition:"all .15s" }}>
                                  {s.label}
                                </button>
                              ))}
                            </div>
                            {/* Section content */}
                            <div style={{ flex:1, overflowY:"auto", padding:"20px 24px", display:"flex", flexDirection:"column", gap:14 }}>
                              {certifEditSection === "general" && (<>
                                <CertifFieldInput modal={certifEditModal} setModal={setCertifEditModal} label="Nom complet (fullName)" field="fullName" placeholder="Ex : Test of English for International Communication" />
                                <CertifFieldInput modal={certifEditModal} setModal={setCertifEditModal} label="Accroche (tagline)" field="tagline" placeholder="Ex : La certification n°1 en entreprise" />
                                <div>
                                  <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:6 }}>🖼️ Image héro</label>
                                  {certifEditModal.data.heroImage ? (
                                    <div style={{ position:"relative", borderRadius:10, overflow:"hidden", border:"1.5px solid #bae6fd" }}>
                                      <img src={certifEditModal.data.heroImage} alt="héro" style={{ width:"100%", height:130, objectFit:"cover", display:"block" }} />
                                      <div style={{ position:"absolute", top:6, right:6, display:"flex", gap:6 }}>
                                        <CloudinaryUpload type="image" compact label="Changer" onSuccess={f => setCertifEditModal(m => ({...m, data:{...m.data, heroImage:f.url}}))} />
                                        <button onClick={() => setCertifEditModal(m => ({...m, data:{...m.data, heroImage:""}}))} style={{ width:26, height:26, borderRadius:6, border:"none", background:"rgba(220,38,38,.9)", color:"#fff", cursor:"pointer", fontSize:13 }}>✕</button>
                                      </div>
                                    </div>
                                  ) : (
                                    <CloudinaryUpload type="image" label="Uploader l'image héro" onSuccess={f => setCertifEditModal(m => ({...m, data:{...m.data, heroImage:f.url}}))} />
                                  )}
                                </div>
                                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                                  <CertifFieldInput modal={certifEditModal} setModal={setCertifEditModal} label="Niveau" field="level" placeholder="Ex : B1 → C1" />
                                  <CertifFieldInput modal={certifEditModal} setModal={setCertifEditModal} label="Durée de préparation" field="duration" placeholder="Ex : 6 semaines" />
                                </div>
                                <CertifFieldInput modal={certifEditModal} setModal={setCertifEditModal} label="Description" field="description" placeholder="À propos de la certification…" multiline />
                                <CertifFieldInput modal={certifEditModal} setModal={setCertifEditModal} label="Pourquoi choisir BET ?" field="whyChoose" placeholder="Texte affiché dans l'encart bleu…" multiline />
                              </>)}
                              {certifEditSection === "prix" && (<>
                                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
                                  <CertifFieldInput modal={certifEditModal} setModal={setCertifEditModal} label="Prix affiché" field="price" placeholder="Ex : 390 000 FCFA" />
                                  <CertifFieldInput modal={certifEditModal} setModal={setCertifEditModal} label="Ancien prix (barré)" field="oldPrice" placeholder="Ex : 500 000 FCFA" />
                                  <CertifFieldInput modal={certifEditModal} setModal={setCertifEditModal} label="Réduction" field="discount" placeholder="Ex : 22%" />
                                </div>
                                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
                                  <CertifFieldInput modal={certifEditModal} setModal={setCertifEditModal} label="Note (ex: 4.9)" field="rating" type="number" placeholder="4.9" />
                                  <CertifFieldInput modal={certifEditModal} setModal={setCertifEditModal} label="Nb d'avis" field="ratingCount" type="number" placeholder="1248" />
                                  <CertifFieldInput modal={certifEditModal} setModal={setCertifEditModal} label="Nb de certifiés" field="students" type="number" placeholder="5200" />
                                </div>
                              </>)}
                              {certifEditSection === "contenu" && (<>
                                <div>
                                  <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:8 }}>✅ Ce que vous apprendrez</label>
                                  <CertifStringList modal={certifEditModal} setModal={setCertifEditModal} field="whatYouLearn" placeholder="Ex : Maîtriser le Listening" />
                                </div>
                                <div>
                                  <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:8 }}>🎯 Avantages (Pourquoi passer cette certification ?)</label>
                                  <CertifStringList modal={certifEditModal} setModal={setCertifEditModal} field="benefits" placeholder="Ex : Reconnu par 14 000 entreprises" />
                                </div>
                                <div>
                                  <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:8 }}>📦 Ce que comprend la préparation (includes)</label>
                                  <CertifObjList modal={certifEditModal} setModal={setCertifEditModal} field="includes" fields={[{key:"icon",label:"Icône (emoji)"},{key:"label",label:"Libellé"}]} />
                                </div>
                              </>)}
                              {certifEditSection === "examen" && (<>
                                <p style={{ margin:0, fontSize:12, color:"#64748b" }}>Sections de l'examen officiel (structure affichée sur la page)</p>
                                <CertifObjList modal={certifEditModal} setModal={setCertifEditModal} field="examStructure" fields={[
                                  {key:"section",   label:"Section"},
                                  {key:"duration",  label:"Durée"},
                                  {key:"questions", label:"Questions / Tâches"},
                                  {key:"desc",      label:"Description", multiline:true},
                                ]} />
                              </>)}
                              {certifEditSection === "programme" && (<>
                                <p style={{ margin:0, fontSize:12, color:"#64748b" }}>Statistiques du programme de préparation</p>
                                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
                                  <CertifFieldInput modal={certifEditModal} setModal={setCertifEditModal} label="Nombre de semaines" field="weeks" path="preparationProgram" type="number" placeholder="6" />
                                  <CertifFieldInput modal={certifEditModal} setModal={setCertifEditModal} label="Heures / semaine" field="hoursPerWeek" path="preparationProgram" type="number" placeholder="8" />
                                  <CertifFieldInput modal={certifEditModal} setModal={setCertifEditModal} label="Nombre de sessions" field="sessions" path="preparationProgram" type="number" placeholder="48" />
                                </div>
                                <CertifFieldInput modal={certifEditModal} setModal={setCertifEditModal} label="Détails du programme" field="details" path="preparationProgram" placeholder="Description détaillée du déroulé…" multiline />
                              </>)}
                              {certifEditSection === "faq" && (<>
                                <p style={{ margin:0, fontSize:12, color:"#64748b" }}>Questions fréquentes affichées sur la page</p>
                                <CertifObjList modal={certifEditModal} setModal={setCertifEditModal} field="faq" fields={[
                                  {key:"q",label:"Question"},
                                  {key:"a",label:"Réponse",multiline:true},
                                ]} />
                              </>)}
                            </div>
                            {/* Footer */}
                            <div style={{ display:"flex", gap:10, justifyContent:"flex-end", padding:"14px 24px", borderTop:"1px solid #f1f5f9", background:"#fafafa", flexShrink:0 }}>
                              <button onClick={() => setCertifEditModal(null)} style={{ padding:"9px 18px", background:"#f1f5f9", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:13, color:"#374151" }}>Annuler</button>
                              <button onClick={async () => {
                                const updated = { ...certifConfig, [certifEditModal.key]: certifEditModal.data };
                                setCertifConfig(updated);
                                await saveCertifConfig(updated);
                                toast.success(`${certifEditModal.data.name || certifEditModal.key.toUpperCase()} mis à jour`);
                                setCertifEditModal(null);
                              }} disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                                style={{ padding:"9px 22px", background:"#1e3a8a", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:800, fontSize:13, opacity:peut("configuration","update")?1:0.5 }}>{peut("configuration","update")?"💾 Enregistrer":"🔒 Enregistrer"}</button>
                            </div>
                        </ModaleAnimee>
                      )}
                      {/* Sélecteur certif pour les médias */}
                      <div style={{ marginTop:28, borderTop:"1.5px dashed #e2e8f0", paddingTop:20 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
                          <span style={{ fontSize:16 }}>🎬</span>
                          <div>
                            <h4 style={{ margin:0, fontSize:13, fontWeight:800, color:"#0f172a" }}>Vidéo / image de présentation par certification</h4>
                            <p style={{ margin:0, fontSize:11, color:"#9ca3af" }}>Choisissez la certification pour configurer son média hero.</p>
                          </div>
                        </div>
                        <div style={{ display:"flex", gap:6, marginBottom:16 }}>
                          {[{key:"toeic",label:"TOEIC",color:"#1e3a8a"},{key:"toefl",label:"TOEFL",color:"#059669"},{key:"ielts",label:"IELTS",color:"#dc2626"}].map(c => (
                            <button key={c.key} onClick={() => setOffreMediaType(c.key)} style={{
                              padding:"6px 16px", borderRadius:20, border:`2px solid ${(offreMediaType||"toeic")===c.key ? c.color : "#e5e7eb"}`,
                              background:(offreMediaType||"toeic")===c.key ? c.color+"18" : "#f8fafc",
                              color:(offreMediaType||"toeic")===c.key ? c.color : "#64748b",
                              fontWeight:800, fontSize:12, cursor:"pointer", transition:"all .15s",
                            }}>
                              {c.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      {renderOffreMediaPanel(offreMediaType || "toeic", ["toeic","toefl","ielts"].includes(offreMediaType) ? {toeic:"#1e3a8a",toefl:"#059669",ielts:"#dc2626"}[offreMediaType] : "#1e3a8a", (offreMediaType||"toeic").toUpperCase())}
                    </div>
                  );
                })()}

                {/* ── Onglet centralisé Code promo ── */}
                {platformSubTab === "codes_promo" && renderCodesPromoTab(BET_COLOR)}

                {/* ── Sous-onglet : Interprétariat ── */}
                {platformSubTab === "catalogue" && catalogueSubTab === "interpretariat" && (() => {
                  const svc = serviceInterp;
                  const color = "#059669";
                  const renderServicePanel = (svcData, setSvcData, saveSvc, svcKey) => {
                    const addDetail = () => setSvcData(s => ({ ...s, details: [...(s.details||[]), { icon:"📌", label:"", val:"" }] }));
                    const updDetail = (i, field, val) => setSvcData(s => ({ ...s, details: s.details.map((d,idx) => idx===i ? {...d,[field]:val} : d) }));
                    const delDetail = (i) => setSvcData(s => ({ ...s, details: s.details.filter((_,idx) => idx!==i) }));
                    const addPlan   = () => setSvcData(s => ({ ...s, plans: [...(s.plans||[]), { nom:"", prix:"Sur devis", detail:"", popular:false }] }));
                    const updPlan   = (i, field, val) => setSvcData(s => ({ ...s, plans: s.plans.map((p,idx) => idx===i ? {...p,[field]:val} : p) }));
                    const delPlan   = (i) => setSvcData(s => ({ ...s, plans: s.plans.filter((_,idx) => idx!==i) }));
                    return (
                      <div>
                        {/* Description & Tagline */}
                        <div style={{ background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:12, padding:20, marginBottom:18 }}>
                          <div style={{ fontWeight:800, fontSize:14, color:"#0f172a", marginBottom:14 }}>📝 Informations générales</div>
                          <div style={{ marginBottom:12 }}>
                            <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Tagline (sous-titre)</label>
                            <input value={svcData.tagline||""} onChange={e => setSvcData(s=>({...s,tagline:e.target.value}))}
                              style={{ width:"100%", padding:"9px 12px", border:"1.5px solid #e5e7eb", borderRadius:8, fontSize:13, boxSizing:"border-box" }} />
                          </div>
                          <div>
                            <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Description</label>
                            <textarea value={svcData.description||""} onChange={e => setSvcData(s=>({...s,description:e.target.value}))}
                              rows={3} style={{ width:"100%", padding:"9px 12px", border:"1.5px solid #e5e7eb", borderRadius:8, fontSize:13, resize:"vertical", boxSizing:"border-box", fontFamily:"inherit" }} />
                          </div>
                        </div>
                        {/* Details */}
                        <div style={{ background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:12, padding:20, marginBottom:18 }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                            <div style={{ fontWeight:800, fontSize:14, color:"#0f172a" }}>📊 Points clés (détails)</div>
                            <button onClick={addDetail} style={{ padding:"6px 14px", background:color, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>➕ Ajouter</button>
                          </div>
                          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                            {(svcData.details||[]).map((d, i) => (
                              <div key={i} style={{ display:"grid", gridTemplateColumns:"60px 1fr 1fr auto", gap:8, alignItems:"center" }}>
                                <input value={d.icon} onChange={e=>updDetail(i,"icon",e.target.value)} placeholder="🎤" style={{ padding:"7px 8px", border:"1.5px solid #e5e7eb", borderRadius:7, fontSize:18, textAlign:"center", boxSizing:"border-box" }} />
                                <input value={d.label} onChange={e=>updDetail(i,"label",e.target.value)} placeholder="Label" style={{ padding:"7px 10px", border:"1.5px solid #e5e7eb", borderRadius:7, fontSize:12, boxSizing:"border-box" }} />
                                <input value={d.val} onChange={e=>updDetail(i,"val",e.target.value)} placeholder="Valeur" style={{ padding:"7px 10px", border:"1.5px solid #e5e7eb", borderRadius:7, fontSize:12, boxSizing:"border-box" }} />
                                <button onClick={()=>delDetail(i)} style={{ padding:"7px 10px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12 }}>🗑</button>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Plans */}
                        <div style={{ background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:12, padding:20, marginBottom:18 }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                            <div style={{ fontWeight:800, fontSize:14, color:"#0f172a" }}>💼 Formules / Tarifs</div>
                            <button onClick={addPlan} style={{ padding:"6px 14px", background:color, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>➕ Ajouter</button>
                          </div>
                          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                            {(svcData.plans||[]).map((p, i) => (
                              <div key={i} style={{ background:"#f8fafc", border:"1.5px solid #e5e7eb", borderRadius:10, padding:"12px 14px" }}>
                                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto", gap:8, marginBottom:8 }}>
                                  <input value={p.nom} onChange={e=>updPlan(i,"nom",e.target.value)} placeholder="Nom de la formule"
                                    style={{ padding:"7px 10px", border:"1.5px solid #e5e7eb", borderRadius:7, fontSize:12, boxSizing:"border-box" }} />
                                  <input value={p.prix} onChange={e=>updPlan(i,"prix",e.target.value)} placeholder="Prix"
                                    style={{ padding:"7px 10px", border:"1.5px solid #e5e7eb", borderRadius:7, fontSize:12, boxSizing:"border-box" }} />
                                  <button onClick={()=>delPlan(i)} style={{ padding:"7px 10px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12 }}>🗑</button>
                                </div>
                                <input value={p.detail} onChange={e=>updPlan(i,"detail",e.target.value)} placeholder="Détail (ex: Jusqu'à 4h · 1 interprète)"
                                  style={{ width:"100%", padding:"7px 10px", border:"1.5px solid #e5e7eb", borderRadius:7, fontSize:12, boxSizing:"border-box", marginBottom:6 }} />
                                <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, fontWeight:600, color:"#374151", cursor:"pointer" }}>
                                  <input type="checkbox" checked={!!p.popular} onChange={e=>updPlan(i,"popular",e.target.checked)} style={{ width:15, height:15 }} />
                                  Populaire (badge affiché)
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Save */}
                        <button onClick={async () => { await saveSvc(svcData); toast.success("Service mis à jour !"); }}
                          disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                          style={{ padding:"10px 28px", background:color, color:"#fff", border:"none", borderRadius:10, cursor:"pointer", fontWeight:800, fontSize:14, opacity:peut("configuration","update")?1:0.5 }}>
                          {peut("configuration","update")?"💾 Enregistrer les modifications":"🔒 Enregistrer les modifications"}
                        </button>
                      </div>
                    );
                  };
                  return (
                    <div>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18 }}>
                        <div>
                          <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:"#0f172a" }}>🌍 Interprétariat</h3>
                          <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Contenu affiché dans "Nos offres" → Interprétariat de la navbar.</p>
                        </div>
                      </div>
                      {/* Aperçu navbar */}
                      <div style={{ background:`#f0fdf4`, border:`2px solid #059669`, borderRadius:14, padding:"14px 18px", marginBottom:20, display:"flex", gap:14, alignItems:"flex-start" }}>
                        <div style={{ width:52, height:52, borderRadius:12, background:"#05966918", display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, flexShrink:0 }}>🌍</div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:800, fontSize:14, color:"#0f172a" }}>Interprétariat</div>
                          <div style={{ fontSize:12, color:"#059669", fontWeight:600, marginTop:2 }}>{svc.tagline}</div>
                          <div style={{ fontSize:12, color:"#475569", marginTop:4, lineHeight:1.5 }}>{(svc.description||"").slice(0,100)}…</div>
                          <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>{(svc.plans||[]).length} formule(s)</div>
                        </div>
                      </div>
                      {renderServicePanel(serviceInterp, setServiceInterp, saveServiceInterp, "interpretariat")}
                    </div>
                  );
                })()}

                {/* ── Sous-onglet : Traduction ── */}
                {platformSubTab === "catalogue" && catalogueSubTab === "traduction" && (() => {
                  const svc = serviceTrad;
                  const color = "#dc2626";
                  const renderServicePanel = (svcData, setSvcData, saveSvc, svcKey) => {
                    const addDetail = () => setSvcData(s => ({ ...s, details: [...(s.details||[]), { icon:"📌", label:"", val:"" }] }));
                    const updDetail = (i, field, val) => setSvcData(s => ({ ...s, details: s.details.map((d,idx) => idx===i ? {...d,[field]:val} : d) }));
                    const delDetail = (i) => setSvcData(s => ({ ...s, details: s.details.filter((_,idx) => idx!==i) }));
                    const addPlan   = () => setSvcData(s => ({ ...s, plans: [...(s.plans||[]), { nom:"", prix:"Sur devis", detail:"", popular:false }] }));
                    const updPlan   = (i, field, val) => setSvcData(s => ({ ...s, plans: s.plans.map((p,idx) => idx===i ? {...p,[field]:val} : p) }));
                    const delPlan   = (i) => setSvcData(s => ({ ...s, plans: s.plans.filter((_,idx) => idx!==i) }));
                    return (
                      <div>
                        <div style={{ background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:12, padding:20, marginBottom:18 }}>
                          <div style={{ fontWeight:800, fontSize:14, color:"#0f172a", marginBottom:14 }}>📝 Informations générales</div>
                          <div style={{ marginBottom:12 }}>
                            <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Tagline (sous-titre)</label>
                            <input value={svcData.tagline||""} onChange={e => setSvcData(s=>({...s,tagline:e.target.value}))}
                              style={{ width:"100%", padding:"9px 12px", border:"1.5px solid #e5e7eb", borderRadius:8, fontSize:13, boxSizing:"border-box" }} />
                          </div>
                          <div>
                            <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Description</label>
                            <textarea value={svcData.description||""} onChange={e => setSvcData(s=>({...s,description:e.target.value}))}
                              rows={3} style={{ width:"100%", padding:"9px 12px", border:"1.5px solid #e5e7eb", borderRadius:8, fontSize:13, resize:"vertical", boxSizing:"border-box", fontFamily:"inherit" }} />
                          </div>
                        </div>
                        <div style={{ background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:12, padding:20, marginBottom:18 }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                            <div style={{ fontWeight:800, fontSize:14, color:"#0f172a" }}>📊 Points clés (détails)</div>
                            <button onClick={addDetail} style={{ padding:"6px 14px", background:color, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>➕ Ajouter</button>
                          </div>
                          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                            {(svcData.details||[]).map((d, i) => (
                              <div key={i} style={{ display:"grid", gridTemplateColumns:"60px 1fr 1fr auto", gap:8, alignItems:"center" }}>
                                <input value={d.icon} onChange={e=>updDetail(i,"icon",e.target.value)} placeholder="📄" style={{ padding:"7px 8px", border:"1.5px solid #e5e7eb", borderRadius:7, fontSize:18, textAlign:"center", boxSizing:"border-box" }} />
                                <input value={d.label} onChange={e=>updDetail(i,"label",e.target.value)} placeholder="Label" style={{ padding:"7px 10px", border:"1.5px solid #e5e7eb", borderRadius:7, fontSize:12, boxSizing:"border-box" }} />
                                <input value={d.val} onChange={e=>updDetail(i,"val",e.target.value)} placeholder="Valeur" style={{ padding:"7px 10px", border:"1.5px solid #e5e7eb", borderRadius:7, fontSize:12, boxSizing:"border-box" }} />
                                <button onClick={()=>delDetail(i)} style={{ padding:"7px 10px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12 }}>🗑</button>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div style={{ background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:12, padding:20, marginBottom:18 }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                            <div style={{ fontWeight:800, fontSize:14, color:"#0f172a" }}>💼 Formules / Tarifs</div>
                            <button onClick={addPlan} style={{ padding:"6px 14px", background:color, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>➕ Ajouter</button>
                          </div>
                          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                            {(svcData.plans||[]).map((p, i) => (
                              <div key={i} style={{ background:"#f8fafc", border:"1.5px solid #e5e7eb", borderRadius:10, padding:"12px 14px" }}>
                                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto", gap:8, marginBottom:8 }}>
                                  <input value={p.nom} onChange={e=>updPlan(i,"nom",e.target.value)} placeholder="Nom de la formule"
                                    style={{ padding:"7px 10px", border:"1.5px solid #e5e7eb", borderRadius:7, fontSize:12, boxSizing:"border-box" }} />
                                  <input value={p.prix} onChange={e=>updPlan(i,"prix",e.target.value)} placeholder="Prix"
                                    style={{ padding:"7px 10px", border:"1.5px solid #e5e7eb", borderRadius:7, fontSize:12, boxSizing:"border-box" }} />
                                  <button onClick={()=>delPlan(i)} style={{ padding:"7px 10px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12 }}>🗑</button>
                                </div>
                                <input value={p.detail} onChange={e=>updPlan(i,"detail",e.target.value)} placeholder="Détail (ex: 3-5 jours ouvrés)"
                                  style={{ width:"100%", padding:"7px 10px", border:"1.5px solid #e5e7eb", borderRadius:7, fontSize:12, boxSizing:"border-box", marginBottom:6 }} />
                                <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, fontWeight:600, color:"#374151", cursor:"pointer" }}>
                                  <input type="checkbox" checked={!!p.popular} onChange={e=>updPlan(i,"popular",e.target.checked)} style={{ width:15, height:15 }} />
                                  Populaire (badge affiché)
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                        <button onClick={async () => { await saveSvc(svcData); toast.success("Service mis à jour !"); }}
                          disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                          style={{ padding:"10px 28px", background:color, color:"#fff", border:"none", borderRadius:10, cursor:"pointer", fontWeight:800, fontSize:14, opacity:peut("configuration","update")?1:0.5 }}>
                          {peut("configuration","update")?"💾 Enregistrer les modifications":"🔒 Enregistrer les modifications"}
                        </button>
                      </div>
                    );
                  };
                  return (
                    <div>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18 }}>
                        <div>
                          <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:"#0f172a" }}>📄 Traduction</h3>
                          <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Contenu affiché dans "Nos offres" → Traduction de la navbar.</p>
                        </div>
                      </div>
                      <div style={{ background:`#fef2f2`, border:`2px solid #dc2626`, borderRadius:14, padding:"14px 18px", marginBottom:20, display:"flex", gap:14, alignItems:"flex-start" }}>
                        <div style={{ width:52, height:52, borderRadius:12, background:"#dc262618", display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, flexShrink:0 }}>📄</div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:800, fontSize:14, color:"#0f172a" }}>Traduction</div>
                          <div style={{ fontSize:12, color:"#dc2626", fontWeight:600, marginTop:2 }}>{svc.tagline}</div>
                          <div style={{ fontSize:12, color:"#475569", marginTop:4, lineHeight:1.5 }}>{(svc.description||"").slice(0,100)}…</div>
                          <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>{(svc.plans||[]).length} formule(s)</div>
                        </div>
                      </div>
                      {renderServicePanel(serviceTrad, setServiceTrad, saveServiceTrad, "traduction")}
                    </div>
                  );
                })()}

                {platformSubTab === "catalogue" && catalogueSubTab === "catalogue_pdf" && (() => {
                  const pdfFileRef = React.createRef();
                  return (
                    <div>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18 }}>
                        <div>
                          <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:"#0f172a" }}>📥 Catalogue complet BET (PDF)</h3>
                          <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Ce PDF est proposé au téléchargement dans l'espace prospect, onglet "Catalogue formations".</p>
                        </div>
                      </div>

                      {/* Aperçu fichier actuel */}
                      <div style={{ background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:12, padding:20, marginBottom:18 }}>
                        <div style={{ fontWeight:800, fontSize:13, color:"#0f172a", marginBottom:12 }}>📄 Fichier actuel</div>
                        {cataloguePdfUrl ? (
                          <div style={{ display:"flex", alignItems:"center", gap:14, padding:"12px 16px", background:"#f0fdf4", border:"1.5px solid #bbf7d0", borderRadius:10 }}>
                            <span style={{ fontSize:28 }}>📄</span>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontWeight:700, fontSize:13, color:"#0f172a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{cataloguePdfNom || "Catalogue BET.pdf"}</div>
                              <div style={{ fontSize:11, color:"#16a34a", marginTop:2 }}>✅ En ligne - visible par les prospects</div>
                            </div>
                            <a href={cataloguePdfUrl} target="_blank" rel="noopener noreferrer"
                              style={{ padding:"7px 14px", background:"#0891b2", color:"#fff", borderRadius:8, fontWeight:700, fontSize:12, textDecoration:"none" }}>
                              👁 Voir
                            </a>
                          </div>
                        ) : (
                          <div style={{ padding:"16px", background:"#fef9c3", border:"1.5px solid #fde68a", borderRadius:10, fontSize:13, color:"#92400e", fontWeight:600 }}>
                            ⚠️ Aucun catalogue PDF configuré. Le lien de téléchargement ne sera pas fonctionnel.
                          </div>
                        )}
                      </div>

                      {/* Zone upload */}
                      <div style={{ background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:12, padding:20 }}>
                        <div style={{ fontWeight:800, fontSize:13, color:"#0f172a", marginBottom:14 }}>⬆️ Mettre à jour le catalogue</div>
                        <input ref={pdfFileRef} type="file" accept="application/pdf" style={{ display:"none" }}
                          onChange={e => { const f = e.target.files?.[0]; if(f) handleCataloguePdfUpload(f); e.target.value=""; }} />
                        <div
                          onClick={() => !cataloguePdfUploading && peut("configuration","update") && pdfFileRef.current?.click()}
                          onDragOver={e => e.preventDefault()}
                          onDrop={e => { e.preventDefault(); if(!peut("configuration","update")) return; const f=e.dataTransfer.files?.[0]; if(f) handleCataloguePdfUpload(f); }}
                          title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                          style={{ border:"2px dashed #0891b2", borderRadius:12, padding:"36px 24px", textAlign:"center", cursor:(cataloguePdfUploading||!peut("configuration","update"))?"not-allowed":"pointer", background:"#f0f9ff", transition:"background .15s", opacity:peut("configuration","update")?1:0.6 }}
                          onMouseEnter={e => { if(!cataloguePdfUploading) e.currentTarget.style.background="#e0f2fe"; }}
                          onMouseLeave={e => e.currentTarget.style.background="#f0f9ff"}
                        >
                          {cataloguePdfUploading ? (
                            <>
                              <div style={{ fontSize:36, marginBottom:10 }}>⏳</div>
                              <div style={{ fontWeight:700, color:"#0891b2", fontSize:14 }}>Upload en cours…</div>
                            </>
                          ) : (
                            <>
                              <div style={{ fontSize:36, marginBottom:10 }}>📥</div>
                              <div style={{ fontWeight:700, color:"#0891b2", fontSize:14 }}>Cliquez ou glissez votre PDF ici</div>
                              <div style={{ fontSize:12, color:"#9ca3af", marginTop:6 }}>Format PDF uniquement · Taille maximale : <strong>{maxMoPour("document","application/pdf")} Mo</strong></div>
                            </>
                          )}
                        </div>
                        {cataloguePdfMsg && (
                          <div style={{ marginTop:12, padding:"10px 14px", borderRadius:8, fontSize:13, fontWeight:600,
                            background: cataloguePdfMsg.type==="success" ? "#f0fdf4" : "#fef2f2",
                            color:      cataloguePdfMsg.type==="success" ? "#166534"  : "#dc2626",
                            border:     `1px solid ${cataloguePdfMsg.type==="success" ? "#bbf7d0" : "#fca5a5"}` }}>
                            {cataloguePdfMsg.text}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

          {/* ════ MODAL APERÇU COURS ════ */}
          {courseApercuEditModal && (() => {
            const type = courseApercuEditModal.type;
            const accentColor = type === "en_ligne" ? "#1e3a8a" : "#059669";
            const label = type === "en_ligne" ? "Cours en ligne" : "Cours à domicile";
            const tabs = [
              { key:"general",       label:"Général" },
              { key:"avantages",     label:"Avantages" },
              { key:"programme",     label:"Programme" },
              { key:"inclus",        label:"Inclus" },
              { key:"prerequis",     label:"Prérequis" },
              { key:"faq",           label:"FAQ" },
            ];
            return (
              <ModaleAnimee ouvert
                onClicVoile={e => { if(e.target===e.currentTarget) setCourseApercuEditModal(null); }}
                style={{ position:"fixed", inset:0, background:"rgba(15,23,42,.55)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
                panneauStyle={{ background:"#fff", borderRadius:20, width:"100%", maxWidth:"90vw", maxHeight:"90vh", boxShadow:"0 24px 60px rgba(0,0,0,.25)", display:"flex", flexDirection:"column", overflow:"hidden" }}>
                  {/* Header */}
                  <div style={{ display:"flex", alignItems:"center", gap:12, padding:"18px 24px", borderBottom:"1px solid #f1f5f9", background:accentColor, borderRadius:"20px 20px 0 0" }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:11, color:"rgba(255,255,255,.7)", fontWeight:600, textTransform:"uppercase", letterSpacing:1 }}>Onglet Aperçu</div>
                      <h3 style={{ margin:0, fontSize:16, fontWeight:800, color:"#fff" }}>📄 {label} - Contenu de la page</h3>
                    </div>
                    <button onClick={() => setCourseApercuEditModal(null)} style={{ width:32, height:32, borderRadius:8, border:"none", background:"rgba(255,255,255,.2)", cursor:"pointer", fontSize:16, color:"#fff" }}>✕</button>
                  </div>
                  {/* Tabs */}
                  <div style={{ display:"flex", gap:2, padding:"12px 24px 0", background:"#f8fafc", borderBottom:"1px solid #e5e7eb", overflowX:"auto" }}>
                    {tabs.map(t => (
                      <button key={t.key} onClick={() => setCourseApercuSection(t.key)}
                        style={{ padding:"8px 14px", border:"none", borderRadius:"8px 8px 0 0", cursor:"pointer", fontWeight:700, fontSize:12, whiteSpace:"nowrap",
                          background: courseApercuSection===t.key ? "#fff" : "transparent",
                          color: courseApercuSection===t.key ? accentColor : "#64748b",
                          borderBottom: courseApercuSection===t.key ? `3px solid ${accentColor}` : "3px solid transparent" }}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                  {/* Body */}
                  <div style={{ flex:1, overflowY:"auto", padding:"20px 24px" }}>
                    {courseApercuSection === "general" && (
                      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                        <div>
                          <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Description principale</label>
                          <textarea value={courseApercuEditModal.data.description||""} onChange={e => setCourseApercuEditModal(m => ({...m, data:{...m.data, description:e.target.value}}))}
                            rows={4} placeholder="Décrivez ce cours en quelques phrases…"
                            style={{ width:"100%", padding:"9px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, boxSizing:"border-box", resize:"vertical" }} />
                        </div>
                        <div>
                          <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Public cible</label>
                          <ApercuStringList modal={courseApercuEditModal} setModal={setCourseApercuEditModal} field="targetAudience" placeholder="Ex : Professionnels souhaitant…" />
                        </div>
                      </div>
                    )}
                    {courseApercuSection === "avantages" && (
                      <div>
                        <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:8 }}>Points forts / Avantages</label>
                        <ApercuStringList modal={courseApercuEditModal} setModal={setCourseApercuEditModal} field="advantages" placeholder="Ex : Accès 24/7 à la plateforme…" />
                      </div>
                    )}
                    {courseApercuSection === "programme" && (
                      <div>
                        <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:8 }}>Ce que vous apprendrez</label>
                        <ApercuStringList modal={courseApercuEditModal} setModal={setCourseApercuEditModal} field="whatYouLearn" placeholder="Ex : Maîtriser la grammaire avancée…" />
                      </div>
                    )}
                    {courseApercuSection === "inclus" && (
                      <div>
                        <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:8 }}>Ce qui est inclus</label>
                        <ApercuObjList modal={courseApercuEditModal} setModal={setCourseApercuEditModal} field="includes" keys={["icon","label"]} placeholders={["🎓","Accès à la plateforme…"]} />
                      </div>
                    )}
                    {courseApercuSection === "prerequis" && (
                      <div>
                        <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:8 }}>Prérequis</label>
                        <ApercuStringList modal={courseApercuEditModal} setModal={setCourseApercuEditModal} field="requirements" placeholder="Ex : Niveau A2 minimum…" />
                      </div>
                    )}
                    {courseApercuSection === "faq" && (
                      <div>
                        <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:8 }}>Questions fréquentes</label>
                        <ApercuObjList modal={courseApercuEditModal} setModal={setCourseApercuEditModal} field="faq" keys={["q","a"]} placeholders={["Question…","Réponse…"]} />
                      </div>
                    )}
                  </div>
                  {/* Footer */}
                  <div style={{ display:"flex", justifyContent:"flex-end", gap:10, padding:"14px 24px", borderTop:"1px solid #f1f5f9" }}>
                    <button onClick={() => setCourseApercuEditModal(null)}
                      style={{ padding:"9px 20px", background:"#f1f5f9", color:"#374151", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:13 }}>Annuler</button>
                    <button onClick={async () => {
                      const updated = { ...courseApercu, [type]: courseApercuEditModal.data };
                      setCourseApercu(updated);
                      await saveCourseApercu(updated);
                      toast.success(`Aperçu "${label}" mis à jour`);
                      setCourseApercuEditModal(null);
                    }} style={{ padding:"9px 22px", background:accentColor, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:800, fontSize:13 }}>💾 Enregistrer</button>
                  </div>
              </ModaleAnimee>
            );
          })()}

          {/* ════ TÉMOIGNAGES ════ */}
          {platformSubTab === "contenu" && (
            <div style={{ background:"#fff", borderRadius:"0 12px 12px 12px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", overflow:"hidden" }}>

              {/* ── Navigation interne ── */}
              <div style={{ display:"flex", gap:4, padding:"16px 20px 0", borderBottom:"2px solid #e2e8f0", background:"#f8fafc" }}>
                {[
                  { key:"articles",    label:"📝 Articles Blog",  badge: blogArticles.filter(a=>!a.publie).length },
                  { key:"temoignages", label:"⭐ Témoignages",     badge: temosPending, danger: temosPending>0 },
                ].map(t => (
                  <button key={t.key} onClick={() => {
                    setBlogInnerTab(t.key);
                    if (t.key === "temoignages") { setOffreMediaType("temoignages"); fetchOffreMedia("temoignages"); }
                    if (t.key === "articles")    { setOffreMediaType("blog");         fetchOffreMedia("blog"); }
                  }} style={{ padding:"8px 16px", borderRadius:"8px 8px 0 0", border:"none", fontWeight:700, fontSize:12, cursor:"pointer", position:"relative",
                    background: blogInnerTab===t.key ? "#fff" : "transparent",
                    color: blogInnerTab===t.key ? "#0f172a" : "#6b7280",
                    borderBottom: blogInnerTab===t.key ? "2px solid #1e3a8a" : "2px solid transparent",
                    marginBottom: blogInnerTab===t.key ? -2 : 0,
                  }}>
                    {t.label}
                    {t.badge > 0 && <span style={{ marginLeft:6, background: t.danger ? "#dc2626" : "#6366f1", color:"#fff", borderRadius:999, padding:"1px 7px", fontSize:10, fontWeight:800 }}>{t.badge}</span>}
                  </button>
                ))}
              </div>

              <div style={{ padding:24 }}>

              {/* ══ TÉMOIGNAGES ══ */}
              {blogInnerTab === "temoignages" && (() => {
            const EMOJI_LIST = ["🎓","👩🏾‍⚖️","👨🏿‍💼","👩🏽‍💻","👨🏽‍🎓","🌟","💪","🏆","👑","✨"];
            const filtered   = temoFiltre === "tous" ? temos : temos.filter(t => t.statut === temoFiltre);
            const temoTotalPages = Math.max(1, Math.ceil(filtered.length / TEMO_PER_PAGE));
            const displayed  = filtered.slice((temoPage - 1) * TEMO_PER_PAGE, temoPage * TEMO_PER_PAGE);
            const temoGoTo   = (p) => { setTemoPage(Math.min(Math.max(1, p), temoTotalPages)); };
            return (
              <div className="temo-page">

                {/* Header */}
                <div className="temo-header">
                  <div>
                    <h2>⭐ Gestion des Témoignages</h2>
                    <p>{temos.length} témoignage{temos.length>1?"s":""} · {temosPending} en attente de validation</p>
                  </div>
                  <div className="temo-header-actions">
                    <button className="temo-btn temo-btn--outline" onClick={fetchTemos}>🔄 Actualiser</button>
                    <button className="temo-btn temo-btn--primary" onClick={() => { setTemoForm(TEMO_FORM_INIT); setTemoEditId(null); setTemoFormOpen(true); }}
                      disabled={!peut("configuration","create")} title={!peut("configuration","create")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                      style={{ opacity:peut("configuration","create")?1:0.5 }}>
                      {peut("configuration","create")?"➕ Nouveau témoignage":"🔒 Nouveau témoignage"}
                    </button>
                  </div>
                </div>

                {/* Modal création / modification */}
                {temoFormOpen && (
                  <ModaleAnimee ouvert
                    onClicVoile={e => { if (e.target===e.currentTarget) { setTemoFormOpen(false); setTemoEditId(null); setTemoForm(TEMO_FORM_INIT); } }}
                    style={{ position:"fixed", inset:0, background:"rgba(10,17,40,.65)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:16, backdropFilter:"blur(3px)" }}
                    panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:"90vw", maxHeight:"90vh", overflow:"auto", position:"relative", boxShadow:"0 24px 64px rgba(0,0,0,.3)" }}>
                      {/* En-tête */}
                      <div style={{ background:BET_GRADIENT, padding:"20px 28px", borderRadius:"16px 16px 0 0", display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:1 }}>
                        <div>
                          <h3 style={{ margin:0, fontSize:16, fontWeight:800, color:"#fff" }}>{temoEditId ? "✏️ Modifier le témoignage" : "➕ Créer un témoignage"}</h3>
                          {temoEditId && <p style={{ margin:"3px 0 0", fontSize:12, color:"rgba(255,255,255,.6)" }}>ID : {temoEditId}</p>}
                        </div>
                        <button onClick={() => { setTemoFormOpen(false); setTemoEditId(null); setTemoForm(TEMO_FORM_INIT); }}
                          style={{ background:"rgba(255,255,255,.15)", border:"none", borderRadius:"50%", width:34, height:34, cursor:"pointer", fontSize:18, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>✕</button>
                      </div>
                      {/* Corps */}
                      <div className="temo-form" style={{ padding:"24px 28px 28px", border:"none", borderRadius:0, margin:0, animation:"none" }}>
                        <input ref={temoFileRef} type="file" accept={temoFileTarget==="video" ? "video/*,video/mp4,video/quicktime" : "image/*"} style={{ display:"none" }}
                          onChange={e => { const f=e.target.files?.[0]; if(f) temoUploadMedia(f, temoFileTarget); e.target.value=""; }} />
                        <div className="temo-form-grid">
                          <div>
                            <label>Nom *</label>
                            <input value={temoForm.nom} onChange={e => setTemoForm(f=>({...f,nom:e.target.value}))} placeholder="Ex: Awa Koné" />
                          </div>
                          <div>
                            <label>Rôle / Titre</label>
                            <input value={temoForm.role} onChange={e => setTemoForm(f=>({...f,role:e.target.value}))} placeholder="Ex: Étudiante en droit" />
                          </div>
                          <div>
                            <label>Type de certification</label>
                            <select
                              value={temoForm.score.split(" ")[0] || ""}
                              onChange={e => {
                                const type = e.target.value;
                                const scoreNum = temoForm.score.split(" ").slice(1).join(" ");
                                setTemoForm(f => ({ ...f, score: type ? `${type}${scoreNum ? " " + scoreNum : ""}` : scoreNum }));
                              }}
                            >
                              <option value="">- Sélectionner -</option>
                              <option value="TOEIC">TOEIC</option>
                              <option value="TOEFL">TOEFL</option>
                              <option value="IELTS">IELTS</option>
                              <option value="Anglais Pro">Anglais Pro</option>
                              <option value="Anglais Enfant">Anglais Enfant</option>
                            </select>
                          </div>
                          <div>
                            <label>Score obtenu</label>
                            <input
                              value={temoForm.score.split(" ").slice(1).join(" ")}
                              onChange={e => {
                                const type = temoForm.score.split(" ")[0] || "";
                                const val  = e.target.value;
                                setTemoForm(f => ({ ...f, score: type ? `${type}${val ? " " + val : ""}` : val }));
                              }}
                              placeholder="Ex: 850, 7.5, 104…"
                            />
                          </div>
                          <div>
                            <label>Étoiles</label>
                            <select value={temoForm.etoiles} onChange={e => setTemoForm(f=>({...f,etoiles:Number(e.target.value)}))}>
                              {[5,4,3,2,1].map(n => <option key={n} value={n}>{"★".repeat(n)} {n}/5</option>)}
                            </select>
                          </div>
                          <div>
                            <label>Ordre d'affichage</label>
                            <input type="number" value={temoForm.ordre} min={0}
                              onChange={e => setTemoForm(f=>({...f,ordre:Number(e.target.value)}))} />
                          </div>
                          <div className="temo-form-grid--full">
                            <label>Texte *</label>
                            <textarea value={temoForm.texte} onChange={e => setTemoForm(f=>({...f,texte:e.target.value}))} rows={4} placeholder="Le témoignage..." />
                          </div>
                          <div>
                            <label>Avatar</label>
                            <div className="temo-emoji-row">
                              {EMOJI_LIST.map(e => (
                                <div key={e} className={`temo-emoji-btn${temoForm.avatar===e?" temo-emoji-btn--sel":""}`}
                                  onClick={() => setTemoForm(f=>({...f,avatar:e}))}>
                                  {e}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label>Couleur accent</label>
                            <div className="temo-color-row">
                              <input type="color" className="temo-color-swatch" value={temoForm.couleur}
                                onChange={e => setTemoForm(f=>({...f,couleur:e.target.value}))} />
                              <span style={{ fontSize:12, color:"#6b7280" }}>{temoForm.couleur}</span>
                            </div>
                          </div>
                          {/* Médias */}
                          <div className="temo-form-grid--full" style={{ marginTop:4 }}>
                            <label style={{ fontWeight:700, fontSize:12, color:"#374151", marginBottom:8, display:"block" }}>📎 Photo ou vidéo de témoignage</label>
                            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                              <div style={{ background:"#f8fafc", border:"1.5px dashed #cbd5e1", borderRadius:9, padding:12 }}>
                                <div style={{ fontSize:11, fontWeight:700, color:"#374151", marginBottom:8 }}>🖼️ Photo (diplôme, résultat…)</div>
                                {temoForm.photo_url ? (
                                  <div style={{ position:"relative" }}>
                                    <img src={temoForm.photo_url} alt="" style={{ width:"100%", height:80, objectFit:"cover", borderRadius:6, display:"block" }} />
                                    <button onClick={() => setTemoForm(f=>({...f,photo_url:""}))} style={{ position:"absolute",top:4,right:4,background:"rgba(0,0,0,.6)",color:"#fff",border:"none",borderRadius:"50%",width:22,height:22,cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center" }}>✕</button>
                                  </div>
                                ) : (
                                  <button onClick={() => { setTemoFileTarget("photo"); temoFileRef.current?.click(); }} disabled={!!temoUploading}
                                    style={{ width:"100%", padding:"10px 0", background:"#eff6ff", color:"#1e40af", border:"1px solid #bfdbfe", borderRadius:7, fontWeight:700, fontSize:11, cursor:"pointer" }}>
                                    {temoUploading==="photo" ? `Upload… ${temoUploadPct}%` : "☁️ Uploader une photo"}
                                  </button>
                                )}
                                <input value={temoForm.photo_url} onChange={e=>setTemoForm(f=>({...f,photo_url:e.target.value}))}
                                  placeholder="ou coller une URL…" style={{ width:"100%",marginTop:6,padding:"6px 8px",border:"1px solid #e2e8f0",borderRadius:6,fontSize:11,boxSizing:"border-box",outline:"none" }} />
                              </div>
                              <div style={{ background:"#f8fafc", border:"1.5px dashed #cbd5e1", borderRadius:9, padding:12 }}>
                                <div style={{ fontSize:11, fontWeight:700, color:"#374151", marginBottom:8 }}>🎬 Vidéo (témoignage filmé)</div>
                                {temoForm.video_url ? (
                                  <div style={{ position:"relative" }}>
                                    <video src={temoForm.video_url} style={{ width:"100%", height:80, objectFit:"cover", borderRadius:6, display:"block" }} />
                                    <button onClick={() => setTemoForm(f=>({...f,video_url:""}))} style={{ position:"absolute",top:4,right:4,background:"rgba(0,0,0,.6)",color:"#fff",border:"none",borderRadius:"50%",width:22,height:22,cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center" }}>✕</button>
                                  </div>
                                ) : (
                                  <button onClick={() => { setTemoFileTarget("video"); temoFileRef.current?.click(); }} disabled={!!temoUploading}
                                    style={{ width:"100%", padding:"10px 0", background:"#f0fdf4", color:"#15803d", border:"1px solid #bbf7d0", borderRadius:7, fontWeight:700, fontSize:11, cursor:"pointer" }}>
                                    {temoUploading==="video" ? `Upload… ${temoUploadPct}%` : "☁️ Uploader une vidéo"}
                                  </button>
                                )}
                                <input value={temoForm.video_url} onChange={e=>setTemoForm(f=>({...f,video_url:e.target.value}))}
                                  placeholder="ou coller une URL MP4…" style={{ width:"100%",marginTop:6,padding:"6px 8px",border:"1px solid #e2e8f0",borderRadius:6,fontSize:11,boxSizing:"border-box",outline:"none" }} />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="temo-form-footer" style={{ marginTop:20 }}>
                          <button className="temo-btn temo-btn--outline" onClick={() => { setTemoFormOpen(false); setTemoEditId(null); setTemoForm(TEMO_FORM_INIT); }}>Annuler</button>
                          <button className="temo-btn temo-btn--primary" onClick={temoCreate} disabled={temoSaving||!peut("configuration",temoEditId?"update":"create")} title={!peut("configuration",temoEditId?"update":"create")?"🔒 Vous n'avez pas la permission pour cette action":undefined} style={{ opacity: (temoSaving||!peut("configuration",temoEditId?"update":"create"))?0.6:1, cursor: temoSaving?"not-allowed":"pointer" }}>✓ {temoSaving ? "⏳…" : !peut("configuration",temoEditId?"update":"create") ? "🔒" : (temoEditId ? "Sauvegarder" : "Créer")}</button>
                        </div>
                      </div>
                  </ModaleAnimee>
                )}

                {/* Filtres */}
                <div className="temo-filters">
                  {[
                    { key:"tous",       label:`Tous (${temos.length})` },
                    { key:"en_attente", label:`⏳ En attente (${temos.filter(t=>t.statut==="en_attente").length})` },
                    { key:"actif",      label:`✅ Actifs (${temos.filter(t=>t.statut==="actif").length})` },
                    { key:"rejete",     label:`❌ Rejetés (${temos.filter(t=>t.statut==="rejete").length})` },
                  ].map(f => (
                    <button key={f.key}
                      className={`temo-filter-btn ${temoFiltre===f.key?"temo-filter-btn--active":"temo-filter-btn--inactive"}`}
                      onClick={() => { setTemoFiltre(f.key); setTemoPage(1); }}>
                      {f.label}
                    </button>
                  ))}
                </div>

                {/* Modal rejet */}
                {temoRejetId && (
                  <div className="temo-modal-overlay">
                    <div className="temo-modal">
                      <h3>❌ Rejeter ce témoignage</h3>
                      <label>Motif de rejet (optionnel)</label>
                      <textarea value={temoMotif} onChange={e => setTemoMotif(e.target.value)} rows={3}
                        placeholder="Ex: Contenu non conforme à la charte..." />
                      <div className="temo-modal-footer">
                        <button className="temo-btn temo-btn--outline temo-btn--sm"
                          onClick={() => { setTemoRejetId(null); setTemoMotif(""); }}>Annuler</button>
                        <button className="temo-btn temo-btn--danger temo-btn--sm"
                          disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                          onClick={() => temoRejeter(temoRejetId)}>Confirmer le rejet</button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Liste */}
                {temosLoading ? (
                  <div className="temo-loading">Chargement des témoignages…</div>
                ) : filtered.length === 0 ? (
                  <div className="temo-empty">
                    <div className="temo-empty__icon">⭐</div>
                    <div className="temo-empty__text">Aucun témoignage{temoFiltre!=="tous"?` - ${temoFiltre}`:""}</div>
                    <div className="temo-empty__sub">Créez le premier via le bouton ci-dessus</div>
                  </div>
                ) : (
                  <>
                  {/* Info page */}
                  <p style={{ fontSize:12, color:"#9ca3af", marginBottom:12 }}>
                    {filtered.length} témoignage{filtered.length>1?"s":""} · page {temoPage}/{temoTotalPages}
                  </p>
                  <div className="temo-grid">
                    {displayed.map(t => (
                      <div key={t.id} className={`temo-card temo-card--${t.statut}`}>
                        <div className="temo-card__accent" style={{ background: t.couleur || "#1e4080" }} />
                        <div className="temo-card__body">
                          {/* Identité */}
                          <div className="temo-card__identity">
                            <div className="temo-card__avatar-wrap">
                              <div className="temo-card__avatar" style={{ background:`${t.couleur||"#1e4080"}18` }}>
                                {t.avatar || "🎓"}
                              </div>
                              <div>
                                <div className="temo-card__name">{t.nom}</div>
                                {t.role  && <div className="temo-card__role">{t.role}</div>}
                                {t.score && <div className="temo-card__score" style={{ color: t.couleur||"#1e4080" }}>{t.score}</div>}
                              </div>
                            </div>
                            <div className="temo-card__badges">
                              <span className={`temo-badge temo-badge--${t.statut}`}>
                                {t.statut==="actif"?"✅ Actif":t.statut==="en_attente"?"⏳ En attente":"❌ Rejeté"}
                              </span>
                              <span className="temo-card__source">{t.source==="apprenant"?"👤 Apprenant":"🔧 Admin"}</span>
                            </div>
                          </div>

                          {/* Étoiles */}
                          <div className="temo-stars">
                            <span className="temo-stars--filled">{"★".repeat(t.etoiles||5)}</span>
                            <span className="temo-stars--empty">{"☆".repeat(5-(t.etoiles||5))}</span>
                          </div>

                          {/* Texte */}
                          <p className="temo-card__texte">« {t.texte} »</p>

                          {/* Photo diplôme */}
                          {t.photo_url && (
                            <div onClick={() => setTemoLightbox({ type:"photo", url:t.photo_url, nom:t.nom })}
                              style={{ marginBottom:8, borderRadius:8, overflow:"hidden", border:"1px solid #e5e7eb", cursor:"zoom-in", position:"relative" }}>
                              <img src={t.photo_url} alt="Diplôme" style={{ width:"100%", height:90, objectFit:"cover", display:"block", transition:"transform .25s" }}
                                onMouseEnter={e => e.currentTarget.style.transform="scale(1.04)"}
                                onMouseLeave={e => e.currentTarget.style.transform="scale(1)"} />
                              <span style={{ position:"absolute",bottom:5,right:6,background:"rgba(0,0,0,.55)",color:"#fff",fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:4 }}>🔍 Agrandir</span>
                            </div>
                          )}
                          {/* Vidéo témoignage */}
                          {t.video_url && (
                            <div style={{ marginBottom:8, borderRadius:8, overflow:"hidden", border:"1px solid #e5e7eb", position:"relative" }}>
                              <video src={t.video_url} style={{ width:"100%", height:110, objectFit:"cover", display:"block", background:"#000" }} />
                              <div style={{ position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,.35)",cursor:"pointer" }}
                                onClick={() => setTemoLightbox({ type:"video", url:t.video_url, nom:t.nom })}>
                                <div style={{ width:44,height:44,borderRadius:"50%",background:"rgba(255,255,255,.92)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,boxShadow:"0 2px 12px rgba(0,0,0,.3)" }}>▶</div>
                              </div>
                              <span style={{ position:"absolute",top:6,left:6,background:"rgba(0,0,0,.55)",color:"#fff",fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:4 }}>🎬 Vidéo</span>
                            </div>
                          )}

                          {/* Motif rejet */}
                          {t.motif_rejet && <div className="temo-card__motif">💬 {t.motif_rejet}</div>}

                          {/* Info apprenant */}
                          {t.apprenant && (
                            <div className="temo-card__apprenant-info">
                              👤 {t.apprenant.prenom} {t.apprenant.nom} - {t.apprenant.email}
                            </div>
                          )}

                          {/* Contrôles */}
                          <div className="temo-card__controls">
                            <span>Ordre :</span>
                            <input type="number" className="temo-ordre-input" defaultValue={t.ordre} min={0}
                              onBlur={e => temoAction(t.id, { ordre: Number(e.target.value) })} />
                            <span>Visible :</span>
                            <ToggleSwitch on={t.actif} color={peut("configuration","update")?"#1e4080":"#cbd5e1"} onChange={val => peut("configuration","update") && temoAction(t.id, { actif: val })} />
                          </div>
                        </div>

                        {/* Footer actions */}
                        <div className="temo-card__footer">
                          {t.statut === "en_attente" && (
                            <>
                              <button className="temo-btn temo-btn--success temo-btn--sm" style={{ flex:1, opacity:peut("configuration","update")?1:0.5 }}
                                disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                                onClick={() => temoApprouver(t.id)}>✅ Approuver</button>
                              <button className="temo-btn temo-btn--danger temo-btn--sm" style={{ flex:1, opacity:peut("configuration","update")?1:0.5 }}
                                disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                                onClick={() => { setTemoRejetId(t.id); setTemoMotif(""); }}>❌ Rejeter</button>
                            </>
                          )}
                          {t.statut !== "en_attente" && (
                            <button className="temo-btn temo-btn--outline temo-btn--sm" style={{ flex:1, opacity:peut("configuration","update")?1:0.5 }}
                              disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                              onClick={() => temoAction(t.id, { statut: t.statut==="actif"?"rejete":"actif", actif: t.statut!=="actif" })}>
                              {t.statut==="actif" ? "⏸ Désactiver" : "▶ Réactiver"}
                            </button>
                          )}
                          <button className="temo-btn temo-btn--outline temo-btn--sm"
                            disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                            style={{ opacity:peut("configuration","update")?1:0.5 }}
                            onClick={() => {
                              setTemoForm({ nom:t.nom||"", role:t.role||"", score:t.score||"", certType:"", certScore:"", texte:t.texte||"", avatar:t.avatar||"🎓", couleur:t.couleur||"#1e4080", etoiles:t.etoiles||5, ordre:t.ordre||0, photo_url:t.photo_url||"", video_url:t.video_url||"" });
                              setTemoEditId(t.id);
                              setTemoFormOpen(true);
                            }}>✏️</button>
                          <button className="temo-btn temo-btn--danger temo-btn--icon"
                            disabled={!peut("configuration","delete")} title={!peut("configuration","delete")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                            style={{ opacity:peut("configuration","delete")?1:0.5 }}
                            onClick={() => temoDelete(t.id)}>🗑</button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* ── Pagination ── */}
                  {temoTotalPages > 1 && (
                    <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:6, marginTop:28, flexWrap:"wrap" }}>
                      <button onClick={() => temoGoTo(temoPage - 1)} disabled={temoPage === 1}
                        style={{ padding:"7px 16px", borderRadius:999, border:"1.5px solid #e5e7eb", background:temoPage===1?"#f8fafc":"#fff", color:temoPage===1?"#cbd5e1":"#0b1f40", fontWeight:700, fontSize:12, cursor:temoPage===1?"not-allowed":"pointer" }}>
                        ← Précédent
                      </button>
                      {Array.from({ length: temoTotalPages }, (_, i) => i + 1).map(n => {
                        const near = Math.abs(n - temoPage) <= 1 || n === 1 || n === temoTotalPages;
                        if (!near) {
                          if (n === temoPage - 2 || n === temoPage + 2) return <span key={n} style={{ color:"#9ca3af", fontSize:12 }}>…</span>;
                          return null;
                        }
                        return (
                          <button key={n} onClick={() => temoGoTo(n)}
                            style={{ width:34, height:34, borderRadius:"50%", border:"none", background:n===temoPage?"#1e4080":"#f1f5f9", color:n===temoPage?"#fff":"#374151", fontWeight:800, fontSize:13, cursor:"pointer" }}>
                            {n}
                          </button>
                        );
                      })}
                      <button onClick={() => temoGoTo(temoPage + 1)} disabled={temoPage === temoTotalPages}
                        style={{ padding:"7px 16px", borderRadius:999, border:"1.5px solid #e5e7eb", background:temoPage===temoTotalPages?"#f8fafc":"#fff", color:temoPage===temoTotalPages?"#cbd5e1":"#0b1f40", fontWeight:700, fontSize:12, cursor:temoPage===temoTotalPages?"not-allowed":"pointer" }}>
                        Suivant →
                      </button>
                    </div>
                  )}
                  </>
                )}
              {/* ── Lightbox média ── */}
              {temoLightbox && (
                <ModaleAnimee ouvert sansPanneau
                  onClicVoile={() => setTemoLightbox(null)}
                  style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.88)", zIndex:10001, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:16, backdropFilter:"blur(4px)" }}>
                  {/* En-tête */}
                  <div style={{ width:"100%", maxWidth:900, display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }} onClick={e => e.stopPropagation()}>
                    <span style={{ color:"#fff", fontWeight:700, fontSize:15 }}>
                      {temoLightbox.type === "photo" ? "🖼️" : "🎬"} {temoLightbox.nom}
                    </span>
                    <button onClick={() => setTemoLightbox(null)}
                      style={{ background:"rgba(255,255,255,.15)", border:"1.5px solid rgba(255,255,255,.3)", borderRadius:"50%", width:36, height:36, color:"#fff", fontSize:18, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
                  </div>
                  {/* Contenu */}
                  <div style={{ width:"100%", maxWidth:900, borderRadius:14, overflow:"hidden", boxShadow:"0 24px 80px rgba(0,0,0,.6)" }} onClick={e => e.stopPropagation()}>
                    {temoLightbox.type === "photo" ? (
                      <img src={temoLightbox.url} alt={temoLightbox.nom}
                        style={{ width:"100%", maxHeight:"80vh", objectFit:"contain", display:"block", background:"#111" }} />
                    ) : (
                      <video src={temoLightbox.url} controls autoPlay
                        style={{ width:"100%", maxHeight:"80vh", display:"block", background:"#000" }} />
                    )}
                  </div>
                  <p style={{ color:"rgba(255,255,255,.4)", fontSize:12, marginTop:12 }}>Cliquez en dehors pour fermer</p>
                </ModaleAnimee>
              )}

              {/* ── Hero de la page Témoignages ── */}
              <div style={{ marginTop:32, borderTop:"2px dashed #e2e8f0", paddingTop:24 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
                  <span style={{ fontSize:20 }}>🎬</span>
                  <div>
                    <div style={{ fontWeight:800, fontSize:14, color:"#0f172a" }}>Image / vidéo du hero - Page Témoignages</div>
                    <div style={{ fontSize:11, color:"#94a3b8", marginTop:1 }}>Apparaît en grand en haut de la page Témoignages sur le site.</div>
                  </div>
                </div>
                {renderOffreMediaPanel("temoignages", "#e93747", "Page Témoignages")}
              </div>

              </div>
            );
              })()}

              {/* ══ ARTICLES BLOG ══ */}
              {blogInnerTab === "articles" && (
              <div style={{ paddingTop:8 }}>
              {/* Header */}
              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:12, marginBottom:20 }}>
                <div>
                  <h2 style={{ margin:0, fontSize:18, fontWeight:800, color:"#0f172a" }}>📝 Gestion des Articles Blog</h2>
                  <p style={{ margin:"4px 0 0", fontSize:13, color:"#6b7280" }}>{blogArticles.length} article{blogArticles.length>1?"s":""} · {blogArticles.filter(a=>!a.publie).length} brouillon{blogArticles.filter(a=>!a.publie).length>1?"s":""}</p>
                </div>
                <div style={{ display:"flex", gap:10 }}>
                  <button onClick={fetchBlogArticles} style={{ padding:"8px 16px", background:"#f1f5f9", border:"1px solid #e2e8f0", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:13, color:"#374151" }}>🔄 Actualiser</button>
                  <button onClick={() => { setBlogForm(BLOG_FORM_INIT); setBlogEditId(null); setBlogFormOpen(f=>!f); }}
                    disabled={!blogFormOpen && !peut("configuration","create")} title={(!blogFormOpen && !peut("configuration","create"))?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                    style={{ padding:"8px 18px", background:"#1e3a8a", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:13, opacity:(!blogFormOpen && !peut("configuration","create"))?0.5:1 }}>
                    {blogFormOpen && !blogEditId ? "✕ Fermer" : !peut("configuration","create") ? "🔒 Nouvel article" : "➕ Nouvel article"}
                  </button>
                </div>
              </div>
              {blogFormOpen && (
                /* ── Modal article ── */
                <ModaleAnimee ouvert
                  onClicVoile={e=>{ if(e.target===e.currentTarget){ setBlogFormOpen(false); setBlogEditId(null); setBlogForm(BLOG_FORM_INIT); } }}
                  style={{ position:"fixed", inset:0, background:"rgba(10,17,40,.6)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:16, backdropFilter:"blur(3px)" }}
                  panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:"90vw", maxHeight:"90vh", overflow:"auto", position:"relative", boxShadow:"0 24px 64px rgba(0,0,0,.25)" }}>
                    {/* En-tête modal */}
                    <div style={{ background:BET_GRADIENT, padding:"20px 28px", borderRadius:"16px 16px 0 0", display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:1 }}>
                      <div>
                        <h3 style={{ margin:0, fontSize:16, fontWeight:800, color:"#fff" }}>{blogEditId ? "✏️ Modifier l'article" : "➕ Créer un article"}</h3>
                        {blogEditId && <p style={{ margin:"3px 0 0", fontSize:12, color:"rgba(255,255,255,.6)" }}>ID : {blogEditId}</p>}
                      </div>
                      <button onClick={()=>{ setBlogFormOpen(false); setBlogEditId(null); setBlogForm(BLOG_FORM_INIT); }}
                        style={{ background:"rgba(255,255,255,.15)", border:"none", borderRadius:"50%", width:34, height:34, cursor:"pointer", fontSize:18, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>✕</button>
                    </div>
                    {/* Corps du formulaire */}
                    <div style={{ padding:"24px 28px 28px" }}>
                      <input ref={blogFileRef} type="file" accept={blogFileTarget==="video" ? "video/*" : "image/*"} style={{ display:"none" }}
                        onChange={e => { const f=e.target.files?.[0]; if(f) blogUploadMedia(f, blogFileTarget); e.target.value=""; }} />
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
                        <div style={{ gridColumn:"1/-1" }}>
                          <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Titre *</label>
                          <input value={blogForm.titre} onChange={e=>setBlogForm(f=>({...f,titre:e.target.value}))} placeholder="Titre de l'article…"
                            style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e5e7eb", fontSize:13, boxSizing:"border-box" }} />
                        </div>
                        <div>
                          <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Catégorie</label>
                          <select value={blogForm.categorie} onChange={e=>setBlogForm(f=>({...f,categorie:e.target.value}))}
                            style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e5e7eb", fontSize:13, boxSizing:"border-box" }}>
                            {BLOG_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Auteur</label>
                          <input value={blogForm.auteur} onChange={e=>setBlogForm(f=>({...f,auteur:e.target.value}))} placeholder="Admin"
                            style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e5e7eb", fontSize:13, boxSizing:"border-box" }} />
                        </div>
                        <div>
                          <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Temps de lecture</label>
                          <input value={blogForm.read_time} onChange={e=>setBlogForm(f=>({...f,read_time:e.target.value}))} placeholder="Ex: 5 min"
                            style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e5e7eb", fontSize:13, boxSizing:"border-box" }} />
                        </div>
                        <div style={{ gridColumn:"1/-1" }}>
                          <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Extrait / Résumé</label>
                          <textarea value={blogForm.extrait} onChange={e=>setBlogForm(f=>({...f,extrait:e.target.value}))} rows={2} placeholder="Courte description affichée sur la liste…"
                            style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e5e7eb", fontSize:13, resize:"vertical", boxSizing:"border-box", fontFamily:"inherit" }} />
                        </div>
                        <div style={{ gridColumn:"1/-1" }}>
                          <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Contenu (HTML)</label>
                          <textarea value={blogForm.contenu} onChange={e=>setBlogForm(f=>({...f,contenu:e.target.value}))} rows={7} placeholder="Contenu HTML de l'article…"
                            style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e5e7eb", fontSize:13, resize:"vertical", boxSizing:"border-box", fontFamily:"monospace" }} />
                        </div>
                      </div>
                      <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:10 }}>📎 Médias</label>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:20 }}>
                        <div style={{ background:"#f8fafc", border:"1.5px dashed #cbd5e1", borderRadius:9, padding:12 }}>
                          <div style={{ fontSize:11, fontWeight:700, color:"#374151", marginBottom:8 }}>🖼️ Image principale</div>
                          {blogForm.image_url ? (
                            <div style={{ position:"relative", marginBottom:8 }}>
                              <img src={blogForm.image_url} alt="" style={{ width:"100%", height:80, objectFit:"cover", borderRadius:6, display:"block" }} />
                              <button onClick={()=>setBlogForm(f=>({...f,image_url:""}))} style={{ position:"absolute",top:4,right:4,background:"rgba(0,0,0,.6)",color:"#fff",border:"none",borderRadius:"50%",width:22,height:22,cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center" }}>✕</button>
                            </div>
                          ) : (
                            <button onClick={()=>{ setBlogFileTarget("image"); blogFileRef.current?.click(); }} disabled={!!blogUploading}
                              style={{ width:"100%", padding:"9px 0", background:"#eff6ff", color:"#1e40af", border:"1px solid #bfdbfe", borderRadius:7, fontWeight:700, fontSize:11, cursor:"pointer", marginBottom:8 }}>
                              {blogUploading==="image" ? `Upload… ${blogUploadPct}%` : "☁️ Uploader une image"}
                            </button>
                          )}
                          <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:3 }}>🔗 URL image</label>
                          <input value={blogForm.image_url} onChange={e=>setBlogForm(f=>({...f,image_url:e.target.value}))}
                            placeholder="ou coller une URL…" style={{ width:"100%", padding:"7px 9px", border:"1.5px solid #d1d5db", borderRadius:6, fontSize:12, boxSizing:"border-box", outline:"none", background:"#fff" }} />
                        </div>
                        <div style={{ background:"#f8fafc", border:"1.5px dashed #cbd5e1", borderRadius:9, padding:12 }}>
                          <div style={{ fontSize:11, fontWeight:700, color:"#374151", marginBottom:8 }}>🎬 Vidéo (YouTube, Vimeo ou MP4)</div>
                          <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 }}>🔗 URL de la vidéo</label>
                          <input
                            value={blogForm.video_url}
                            onChange={e=>setBlogForm(f=>({...f,video_url:e.target.value}))}
                            placeholder="https://youtube.com/watch?v=… ou Vimeo/MP4"
                            style={{ width:"100%", padding:"7px 9px", border:"1.5px solid #d1d5db", borderRadius:7, fontSize:12, boxSizing:"border-box", outline:"none", background:"#fff", marginBottom:8 }}
                          />
                          {blogForm.video_url ? (
                            <div style={{ position:"relative" }}>
                              <div style={{ width:"100%", height:52, background:"#0f172a", borderRadius:6, display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                                <span style={{ color:"#fff", fontSize:16 }}>▶</span>
                                <span style={{ color:"rgba(255,255,255,.5)", fontSize:10, maxWidth:"75%", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{blogForm.video_url}</span>
                              </div>
                              <button onClick={()=>setBlogForm(f=>({...f,video_url:""}))} title="Supprimer" style={{ position:"absolute",top:4,right:4,background:"rgba(220,38,38,.85)",color:"#fff",border:"none",borderRadius:"50%",width:20,height:20,cursor:"pointer",fontSize:11,display:"flex",alignItems:"center",justifyContent:"center" }}>✕</button>
                            </div>
                          ) : (
                            <button onClick={()=>{ setBlogFileTarget("video"); blogFileRef.current?.click(); }} disabled={!!blogUploading}
                              style={{ width:"100%", padding:"9px 0", background:"#f0fdf4", color:"#15803d", border:"1px solid #bbf7d0", borderRadius:7, fontWeight:700, fontSize:11, cursor:"pointer" }}>
                              {blogUploading==="video" ? `Upload… ${blogUploadPct}%` : "☁️ Uploader un fichier vidéo"}
                            </button>
                          )}
                        </div>
                      </div>
                      {/* Pied de formulaire */}
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12, borderTop:"1.5px solid #f1f5f9", paddingTop:18 }}>
                        <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", fontSize:13, fontWeight:700, color:"#374151" }}>
                          <input type="checkbox" checked={blogForm.publie} onChange={e=>setBlogForm(f=>({...f,publie:e.target.checked}))} style={{ width:16, height:16 }} />
                          Publier immédiatement
                        </label>
                        <div style={{ display:"flex", gap:10 }}>
                          <button onClick={()=>{ setBlogFormOpen(false); setBlogEditId(null); setBlogForm(BLOG_FORM_INIT); }}
                            style={{ padding:"10px 20px", background:"#f1f5f9", border:"1px solid #e2e8f0", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:13, color:"#374151" }}>Annuler</button>
                          <button onClick={blogSave} disabled={blogSaving||!peut("configuration",blogEditId?"update":"create")}
                            title={!peut("configuration",blogEditId?"update":"create")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                            style={{ padding:"10px 24px", background:"#1e3a8a", color:"#fff", border:"none", borderRadius:8, cursor:blogSaving?"not-allowed":"pointer", fontWeight:700, fontSize:13, opacity:(blogSaving||!peut("configuration",blogEditId?"update":"create"))?0.6:1 }}>
                            {blogSaving ? "⏳ Enregistrement…" : !peut("configuration",blogEditId?"update":"create") ? "🔒 Enregistrer" : blogEditId ? "💾 Mettre à jour" : "✓ Créer l'article"}
                          </button>
                        </div>
                      </div>
                    </div>
                </ModaleAnimee>
              )}
              <div style={{ marginBottom:16 }}>
                <input value={blogSearch} onChange={e=>setBlogSearch(e.target.value)} placeholder="🔍 Rechercher un article…"
                  style={{ width:"100%", padding:"9px 14px", borderRadius:9, border:"1.5px solid #e5e7eb", fontSize:13, boxSizing:"border-box", outline:"none" }} />
              </div>
              {blogLoading ? (
                <div style={{ textAlign:"center", padding:40, color:"#9ca3af", fontSize:14 }}>⏳ Chargement…</div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {blogArticles.filter(a => !blogSearch || a.titre?.toLowerCase().includes(blogSearch.toLowerCase()) || a.categorie?.toLowerCase().includes(blogSearch.toLowerCase()) || a.auteur?.toLowerCase().includes(blogSearch.toLowerCase())).map(article => {
                    const isExpanded = blogExpandedArticle === article.id;
                    const articleComments = blogComments.filter(c => c.article_id === article.id);
                    return (
                    <div key={article.id} style={{ background:"#f9fafb", border:"1.5px solid #e5e7eb", borderRadius:12, overflow:"hidden" }}>
                      {/* Carte article */}
                      <div style={{ padding:16, display:"flex", alignItems:"center", gap:14, flexWrap:"wrap" }}>
                        <div style={{ width:64, height:64, borderRadius:8, overflow:"hidden", flexShrink:0, background:"#e2e8f0" }}>
                          {article.image_url ? <img src={article.image_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:24 }}>📄</div>}
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:3 }}>
                            <span style={{ fontWeight:800, fontSize:14, color:"#0f172a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:320 }}>{article.titre}</span>
                            <span style={{ padding:"2px 8px", borderRadius:999, fontSize:11, fontWeight:700, background: article.publie ? "#dcfce7" : "#fef9c3", color: article.publie ? "#166534" : "#854d0e" }}>
                              {article.publie ? "✅ Publié" : "📝 Brouillon"}
                            </span>
                            {article.video_url && <span style={{ padding:"2px 8px", borderRadius:999, fontSize:11, fontWeight:700, background:"#f0fdf4", color:"#15803d" }}>🎬 Vidéo</span>}
                          </div>
                          <div style={{ fontSize:12, color:"#6b7280" }}>
                            {article.categorie} · {article.auteur} · {article.read_time || "-"}
                          </div>
                          <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>{article.created_at ? new Date(article.created_at).toLocaleDateString("fr-FR") : ""}</div>
                        </div>
                        <div style={{ display:"flex", gap:8, flexShrink:0, flexWrap:"wrap" }}>
                          <button
                            onClick={() => {
                              if (!isExpanded && blogComments.length === 0) fetchBlogComments();
                              setBlogExpandedArticle(isExpanded ? null : article.id);
                            }}
                            style={{ padding:"6px 12px", background: isExpanded ? "#1e3a8a" : "#eff6ff", color: isExpanded ? "#fff" : "#1e40af", border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12, position:"relative" }}>
                            💬 {article.nb_commentaires || 0}
                            {article.nb_en_attente > 0 && <span style={{ position:"absolute", top:-5, right:-5, background:"#d97706", color:"#fff", borderRadius:"50%", width:16, height:16, fontSize:9, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800 }}>{article.nb_en_attente}</span>}
                          </button>
                          <button onClick={()=>blogTogglePublie(article.id, article.publie)}
                            disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                            style={{ padding:"6px 12px", background: article.publie ? "#fef9c3" : "#dcfce7", color: article.publie ? "#854d0e" : "#166534", border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12, opacity:peut("configuration","update")?1:0.5 }}>
                            {article.publie ? "↩ Dépublier" : "✅ Publier"}
                          </button>
                          <button onClick={()=>{ setBlogForm({ titre:article.titre, extrait:article.extrait||"", contenu:article.contenu||"", categorie:article.categorie||"Actualités", auteur:article.auteur||"Admin", read_time:article.read_time||"", image_url:article.image_url||"", video_url:article.video_url||"", publie:article.publie }); setBlogEditId(article.id); setBlogFormOpen(true); }}
                            disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                            style={{ padding:"6px 12px", background:"#eff6ff", color:"#1e40af", border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12, opacity:peut("configuration","update")?1:0.5 }}>✏️ Modifier</button>
                          <button onClick={()=>blogDelete(article.id)}
                            disabled={!peut("configuration","delete")} title={!peut("configuration","delete")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                            style={{ padding:"6px 12px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12, opacity:peut("configuration","delete")?1:0.5 }}>🗑</button>
                        </div>
                      </div>
                      {/* Commentaires inline */}
                      {isExpanded && (
                        <div style={{ borderTop:"1.5px solid #e2e8f0", background:"#fff", padding:"16px 20px" }}>
                          <div style={{ fontWeight:800, fontSize:13, color:"#0f172a", marginBottom:12 }}>
                            💬 Commentaires · {articleComments.length} au total
                            {blogCommentsLoading && <span style={{ fontWeight:400, color:"#9ca3af", marginLeft:8 }}>Chargement…</span>}
                          </div>
                          {articleComments.length === 0 && !blogCommentsLoading ? (
                            <p style={{ color:"#9ca3af", fontSize:13, margin:0 }}>Aucun commentaire pour cet article.</p>
                          ) : (
                            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                              {articleComments.map(c => (
                                <div key={c.id} style={{ border:"1.5px solid #e5e7eb", borderRadius:10, padding:"12px 16px", background:"#f8fafc", display:"flex", gap:12, alignItems:"flex-start" }}>
                                  <div style={{ width:36, height:36, borderRadius:"50%", background:"linear-gradient(135deg,#1e3a8a,#0891b2)", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:".85rem", flexShrink:0 }}>
                                    {(c.nom||"?")[0].toUpperCase()}
                                  </div>
                                  <div style={{ flex:1, minWidth:0 }}>
                                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
                                      <div>
                                        <span style={{ fontWeight:800, color:"#0f172a", fontSize:13 }}>{c.nom}</span>
                                        {c.email && <span style={{ fontSize:11, color:"#94a3b8", marginLeft:8 }}>{c.email}</span>}
                                      </div>
                                      <span style={{ fontSize:11, color:"#94a3b8", flexShrink:0 }}>
                                        {new Date(c.created_at).toLocaleDateString("fr-FR", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" })}
                                      </span>
                                    </div>
                                    <p style={{ fontSize:13, color:"#334155", margin:"6px 0 0", lineHeight:1.6, wordBreak:"break-word" }}>{c.commentaire}</p>
                                  </div>
                                  <button
                                    onClick={() => deleteBlogComment(c.id)}
                                    disabled={!peut("configuration","delete")}
                                    title={!peut("configuration","delete")?"🔒 Vous n'avez pas la permission pour cette action":"Supprimer"}
                                    style={{ padding:"5px 10px", background:"#fff1f2", color:"#dc2626", border:"1.5px solid #fecdd3", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12, flexShrink:0, opacity:peut("configuration","delete")?1:0.5 }}>
                                    🗑
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })}
                  {blogArticles.length === 0 && !blogLoading && (
                    <div style={{ textAlign:"center", padding:40, color:"#9ca3af", fontSize:14 }}>Aucun article. Créez-en un avec le bouton "➕ Nouvel article".</div>
                  )}
                </div>
              )}

              {/* ── Hero de la page Blog ── */}
              <div style={{ marginTop:32, borderTop:"2px dashed #e2e8f0", paddingTop:24 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
                  <span style={{ fontSize:20 }}>🎬</span>
                  <div>
                    <div style={{ fontWeight:800, fontSize:14, color:"#0f172a" }}>Image / vidéo du hero - Page Blog / Newsletter</div>
                    <div style={{ fontSize:11, color:"#94a3b8", marginTop:1 }}>Apparaît en grand en haut de la page Blog sur le site.</div>
                  </div>
                </div>
                {renderOffreMediaPanel("blog", "#0891b2", "Page Blog / Newsletter")}
              </div>

              </div>
              )}


            </div>
            </div>
          )}

          {/* ══ AVIS OFFRES ══ */}
          {platformSubTab === "avis_offres" && (
            <div style={{ background:"#fff", borderRadius:"0 12px 12px 12px", boxShadow:"0 2px 8px rgba(0,0,0,.05)", padding:28, overflow:"hidden" }}>
              {/* Header */}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12, marginBottom:22 }}>
                <div>
                  <h2 style={{ margin:0, fontSize:17, fontWeight:800, color:"#0f172a" }}>💬 Avis sur les offres</h2>
                  <p style={{ margin:"4px 0 0", fontSize:12, color:"#9ca3af" }}>
                    {avisOffres.length} avis · {avisOffres.filter(a=>!a.actif).length} désactivé(s)
                  </p>
                </div>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {["tous","cours","certification"].map(f=>(
                    <button key={f} onClick={()=>setAvisFiltre(f)}
                      style={{ padding:"7px 16px", borderRadius:999, border:`1.5px solid ${avisFiltre===f?"#1e3a8a":"#e5e7eb"}`, background:avisFiltre===f?"#1e3a8a":"#fff", color:avisFiltre===f?"#fff":"#374151", fontWeight:700, fontSize:12, cursor:"pointer" }}>
                      {f==="tous"?"Tous":`${f==="cours"?"Cours":"Certifications"} (${avisOffres.filter(a=>a.offre_type===f).length})`}
                    </button>
                  ))}
                  <button onClick={fetchAvisOffres} style={{ padding:"7px 14px", borderRadius:8, border:"1.5px solid #e5e7eb", background:"#f8fafc", cursor:"pointer", fontWeight:700, fontSize:12, color:"#374151" }}>🔄 Actualiser</button>
                </div>
              </div>

              {avisLoading && <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>Chargement…</div>}
              {!avisLoading && avisOffres.length===0 && (
                <div style={{ textAlign:"center", padding:"48px 24px", background:"#f8fafc", borderRadius:12, border:"1.5px dashed #e2e8f0" }}>
                  <div style={{ fontSize:"2.5rem", marginBottom:12 }}>💬</div>
                  <p style={{ color:"#64748b", fontSize:14, margin:0 }}>Aucun avis pour le moment.</p>
                </div>
              )}

              <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                {avisOffres
                  .filter(a => avisFiltre==="tous" || a.offre_type===avisFiltre)
                  .map(a => (
                  <div key={a.id} style={{ border:`1.5px solid ${a.actif?"#e5e7eb":"#fecdd3"}`, borderRadius:12, padding:"16px 18px", background:a.actif?"#fff":"#fff7f7", display:"flex", gap:14, alignItems:"flex-start", flexWrap:"wrap" }}>
                    {/* Avatar initiale */}
                    <div style={{ width:40, height:40, borderRadius:"50%", background:"linear-gradient(135deg,#1e3a8a,#dc2626)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:800, fontSize:16, flexShrink:0 }}>
                      {(a.apprenant_nom||"?")[0].toUpperCase()}
                    </div>
                    {/* Contenu */}
                    <div style={{ flex:1, minWidth:200 }}>
                      <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:4 }}>
                        <span style={{ fontWeight:800, fontSize:14, color:"#0f172a" }}>{a.apprenant_nom||"Apprenant BET"}</span>
                        <span style={{ padding:"2px 10px", borderRadius:999, fontSize:11, fontWeight:700, background:a.offre_type==="cours"?"#dbeafe":"#ede9fe", color:a.offre_type==="cours"?"#1e40af":"#6d28d9" }}>
                          {a.offre_type==="cours"?"📚 Cours":"🏆 Certification"}
                        </span>
                        <span style={{ padding:"2px 10px", borderRadius:999, fontSize:11, fontWeight:700, background:a.actif?"#dcfce7":"#fee2e2", color:a.actif?"#166534":"#991b1b" }}>
                          {a.actif?"✅ Visible":"🚫 Désactivé"}
                        </span>
                        <span style={{ fontSize:11, color:"#9ca3af", marginLeft:"auto" }}>{new Date(a.created_at).toLocaleDateString("fr-FR")}</span>
                      </div>
                      {/* Étoiles */}
                      <div style={{ marginBottom:6 }}>
                        <span style={{ color:"#f59e0b", fontSize:16 }}>{"★".repeat(a.note)}</span>
                        <span style={{ color:"#e5e7eb", fontSize:16 }}>{"★".repeat(5-a.note)}</span>
                      </div>
                      <p style={{ fontSize:13, color:"#374151", lineHeight:1.6, margin:"0 0 6px", fontStyle:"italic" }}>"{a.texte}"</p>
                      <div style={{ fontSize:11, color:"#9ca3af" }}>✉️ {a.apprenant_email}</div>
                    </div>
                    {/* Actions */}
                    <div style={{ display:"flex", flexDirection:"column", gap:6, flexShrink:0 }}>
                      <button onClick={()=>toggleAvis(a.id, !a.actif)}
                        disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                        style={{ padding:"7px 14px", borderRadius:8, border:`1px solid ${a.actif?"#fecdd3":"#bbf7d0"}`, background:a.actif?"#fee2e2":"#dcfce7", color:a.actif?"#991b1b":"#166534", fontWeight:700, fontSize:12, cursor:"pointer", whiteSpace:"nowrap", opacity:peut("configuration","update")?1:0.5 }}>
                        {a.actif?"🚫 Désactiver":"✅ Réactiver"}
                      </button>
                      <button onClick={()=>deleteAvis(a.id)}
                        disabled={!peut("configuration","delete")} title={!peut("configuration","delete")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                        style={{ padding:"7px 14px", borderRadius:8, border:"1px solid #fecdd3", background:"#fff", color:"#dc2626", fontWeight:700, fontSize:12, cursor:"pointer", opacity:peut("configuration","delete")?1:0.5 }}>
                        🗑 Supprimer
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ══ BOUTIQUE ══ */}
          {platformSubTab === "boutique" && (
            <div style={{ background:"#fff", borderRadius:"0 12px 12px 12px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", padding:0, overflow:"hidden" }}>
              {/* Header */}
              <div style={{ background: BET_GRADIENT, padding:"24px 28px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <h2 style={{ color:"#fff", margin:0, fontSize:"1.3rem", fontWeight:800 }}>🛍️ Boutique BET</h2>
                  <p style={{ color:"rgba(255,255,255,0.7)", margin:"4px 0 0", fontSize:".85rem" }}>
                    {produits.length} produit{produits.length!==1?"s":""} · {commandes.length} commande{commandes.length!==1?"s":""}
                    {commandes.filter(c=>c.statut==="en_attente").length > 0 &&
                      <span style={{ marginLeft:10, background:"#fbbf24", color:"#7c2d12", padding:"2px 10px", borderRadius:999, fontWeight:700, fontSize:11 }}>
                        {commandes.filter(c=>c.statut==="en_attente").length} en attente
                      </span>
                    }
                  </p>
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={() => { setBoutiqueSubTab("produits"); setProdFormOpen(true); setSelectedProduit(null); setProdForm(PROD_FORM_INIT); }}
                    disabled={!peut("configuration","create")} title={!peut("configuration","create")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                    style={{ padding:"9px 18px", background:"#fff", color:BET_COLOR, border:"none", borderRadius:8, fontWeight:700, cursor:"pointer", fontSize:13, display:"flex", alignItems:"center", gap:6, opacity:peut("configuration","create")?1:0.5 }}>
                    {peut("configuration","create")?"+ Nouveau produit":"🔒 Nouveau produit"}
                  </button>
                </div>
              </div>

              {/* Sous-onglets */}
              <div style={{ display:"flex", gap:0, borderBottom:"2px solid #e5e7eb", padding:"0 28px" }}>
                {[{ key:"produits", label:"📦 Produits" }, { key:"commandes", label:"🧾 Commandes" }].map(t => (
                  <button key={t.key} onClick={() => setBoutiqueSubTab(t.key)}
                    style={{ padding:"12px 20px", border:"none", background:"none", cursor:"pointer", fontWeight:700, fontSize:13,
                      color: boutiqueSubTab===t.key ? BET_COLOR : "#6b7280",
                      borderBottom: boutiqueSubTab===t.key ? `3px solid ${BET_COLOR}` : "3px solid transparent",
                      marginBottom:-2 }}>
                    {t.label}
                  </button>
                ))}
              </div>

              <div style={{ padding:28 }}>

                {/* ── Formulaire ajout/édition produit ── */}
                {boutiqueSubTab === "produits" && prodFormOpen && (
                  <div style={{ background:"#f0f9ff", border:`1.5px solid ${BET_COLOR}40`, borderRadius:12, padding:24, marginBottom:28 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
                      <h3 style={{ margin:0, color:BET_DARK, fontWeight:800 }}>
                        {selectedProduit ? "✏️ Modifier le produit" : "➕ Nouveau produit"}
                      </h3>
                      <button onClick={() => { setProdFormOpen(false); setSelectedProduit(null); }}
                        style={{ background:"none", border:"none", cursor:"pointer", fontSize:18, color:"#6b7280" }}>✕</button>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                        <label style={{ fontSize:12, fontWeight:700, color:"#374151" }}>Nom du produit *</label>
                        <input value={prodForm.nom} onChange={e=>setProdForm(f=>({...f,nom:e.target.value}))}
                          placeholder="Ex: T-Shirt BET"
                          style={{ padding:"9px 12px", border:`1.5px solid ${BET_COLOR}40`, borderRadius:8, fontSize:13 }} />
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                        <label style={{ fontSize:12, fontWeight:700, color:"#374151" }}>Catégorie</label>
                        <select
                          value={CATEGORIES_BOUTIQUE.includes(prodForm.categorie) ? prodForm.categorie : "__custom__"}
                          onChange={e => {
                            if (e.target.value === "__custom__") setProdForm(f=>({...f, categorie:""}));
                            else setProdForm(f=>({...f, categorie:e.target.value}));
                          }}
                          style={{ padding:"9px 12px", border:`1.5px solid ${BET_COLOR}40`, borderRadius:8, fontSize:13 }}>
                          {CATEGORIES_BOUTIQUE.map(c=><option key={c}>{c}</option>)}
                          <option value="__custom__">✏️ Saisir manuellement…</option>
                        </select>
                        {!CATEGORIES_BOUTIQUE.includes(prodForm.categorie) && (
                          <input
                            autoFocus
                            value={prodForm.categorie}
                            onChange={e=>setProdForm(f=>({...f,categorie:e.target.value}))}
                            placeholder="Nom de la catégorie…"
                            style={{ padding:"9px 12px", border:`1.5px solid ${BET_COLOR}`, borderRadius:8, fontSize:13 }}
                          />
                        )}
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                        <label style={{ fontSize:12, fontWeight:700, color:"#374151" }}>Prix (FCFA) *</label>
                        <input type="number" min="0" value={prodForm.prix} onChange={e=>setProdForm(f=>({...f,prix:e.target.value}))}
                          placeholder="5000"
                          style={{ padding:"9px 12px", border:`1.5px solid ${BET_COLOR}40`, borderRadius:8, fontSize:13 }} />
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                        <label style={{ fontSize:12, fontWeight:700, color:"#374151" }}>Stock</label>
                        <input type="number" min="0" value={prodForm.stock} onChange={e=>setProdForm(f=>({...f,stock:e.target.value}))}
                          placeholder="0"
                          style={{ padding:"9px 12px", border:`1.5px solid ${BET_COLOR}40`, borderRadius:8, fontSize:13 }} />
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", gap:6, gridColumn:"1/-1" }}>
                        <label style={{ fontSize:12, fontWeight:700, color:"#374151" }}>Description</label>
                        <textarea value={prodForm.description} onChange={e=>setProdForm(f=>({...f,description:e.target.value}))}
                          placeholder="Description du produit…" rows={3}
                          style={{ padding:"9px 12px", border:`1.5px solid ${BET_COLOR}40`, borderRadius:8, fontSize:13, resize:"vertical" }} />
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", gap:8, gridColumn:"1/-1" }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                          <label style={{ fontSize:12, fontWeight:700, color:"#374151" }}>
                            Photos du produit <span style={{ color:"#94a3b8", fontWeight:400 }}>({prodForm.images.length} / 6)</span>
                          </label>
                          {prodForm.images.length < 6 && (
                            <label style={{ padding:"6px 14px", background:BET_LIGHT, color:BET_COLOR, borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12, display:"flex", alignItems:"center", gap:5 }}>
                              {prodImageUploading ? "⏳ Upload…" : "📷 Ajouter une photo"}
                              <input type="file" accept="image/*" style={{ display:"none" }}
                                onChange={e=>{ const f=e.target.files?.[0]; if(f) uploadProduitImage(f); e.target.value=""; }} />
                            </label>
                          )}
                        </div>
                        {prodForm.images.length === 0 ? (
                          <label style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:100, border:`2px dashed ${BET_COLOR}40`, borderRadius:10, cursor:"pointer", color:"#94a3b8", fontSize:13, gap:6 }}>
                            <span style={{ fontSize:28 }}>📷</span>
                            Cliquez pour ajouter des photos
                            <input type="file" accept="image/*" style={{ display:"none" }}
                              onChange={e=>{ const f=e.target.files?.[0]; if(f) uploadProduitImage(f); e.target.value=""; }} />
                          </label>
                        ) : (
                          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                            {prodForm.images.map((url, i) => (
                              <div key={i} style={{ position:"relative", width:90, height:90 }}>
                                <img src={url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", borderRadius:8, border: i===0?"2.5px solid "+BET_COLOR:"1.5px solid #e5e7eb" }} />
                                {i===0 && <span style={{ position:"absolute", top:4, left:4, background:BET_COLOR, color:"#fff", fontSize:9, fontWeight:800, padding:"1px 5px", borderRadius:4 }}>PRINCIPALE</span>}
                                <button onClick={()=>setProdForm(f=>({...f, images:f.images.filter((_,j)=>j!==i)}))}
                                  style={{ position:"absolute", top:2, right:2, width:20, height:20, borderRadius:"50%", background:"#dc2626", color:"#fff", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800 }}>✕</button>
                              </div>
                            ))}
                          </div>
                        )}
                        <p style={{ fontSize:11, color:"#94a3b8", margin:0 }}>La 1ère photo est l'image principale. Max 6 photos · 10 Mo par photo.</p>
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:10, marginTop:20, justifyContent:"flex-end" }}>
                      <button onClick={()=>{ setProdFormOpen(false); setSelectedProduit(null); }}
                        style={{ padding:"9px 20px", background:"#f1f5f9", color:"#374151", border:"none", borderRadius:8, cursor:"pointer", fontWeight:600 }}>
                        Annuler
                      </button>
                      <button onClick={saveProduit} disabled={prodSaving||!peut("configuration",selectedProduit?"update":"create")}
                        title={!peut("configuration",selectedProduit?"update":"create")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                        style={{ padding:"9px 20px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, opacity:(prodSaving||!peut("configuration",selectedProduit?"update":"create"))?0.6:1 }}>
                        {prodSaving ? "Enregistrement…" : !peut("configuration",selectedProduit?"update":"create") ? "🔒 Enregistrer" : selectedProduit ? "Mettre à jour" : "Créer le produit"}
                      </button>
                    </div>
                  </div>
                )}

                {/* ── Liste des produits ── */}
                {boutiqueSubTab === "produits" && (() => {
                  const totalPages = Math.max(1, Math.ceil(produits.length / PROD_PER_PAGE));
                  const paginated  = produits.slice((prodPage-1)*PROD_PER_PAGE, prodPage*PROD_PER_PAGE);
                  return (
                  <>
                    {prodLoading ? (
                      <div style={{ textAlign:"center", padding:40, color:"#6b7280" }}>Chargement…</div>
                    ) : produits.length === 0 ? (
                      <div style={{ textAlign:"center", padding:60 }}>
                        <div style={{ fontSize:48, marginBottom:12 }}>📦</div>
                        <p style={{ color:"#6b7280", fontWeight:600 }}>Aucun produit pour l'instant</p>
                        <button onClick={()=>setProdFormOpen(true)} style={{ padding:"10px 22px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, marginTop:8 }}>
                          + Ajouter le premier produit
                        </button>
                      </div>
                    ) : (
                      <>
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))", gap:18 }}>
                        {paginated.map(p => {
                          const imgs = Array.isArray(p.images) && p.images.length > 0 ? p.images : (p.image_url ? [p.image_url] : []);
                          const mainImg = imgs[0] || null;
                          return (
                          <div key={p.id} style={{ border:"1.5px solid #e5e7eb", borderRadius:12, overflow:"hidden", background:"#fff", boxShadow:"0 1px 4px rgba(0,0,0,0.05)", opacity:p.actif?1:0.65 }}>
                            {/* Image principale */}
                            <div style={{ height:150, background:"#f8fafc", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden", position:"relative" }}>
                              {mainImg
                                ? <img src={mainImg} alt={p.nom} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                                : <span style={{ fontSize:48 }}>📦</span>
                              }
                              {imgs.length > 1 && (
                                <span style={{ position:"absolute", bottom:6, right:6, background:"rgba(0,0,0,0.55)", color:"#fff", fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:999 }}>
                                  +{imgs.length-1} photo{imgs.length>2?"s":""}
                                </span>
                              )}
                            </div>
                            {/* Miniatures */}
                            {imgs.length > 1 && (
                              <div style={{ display:"flex", gap:4, padding:"6px 8px", background:"#f8fafc", overflowX:"auto" }}>
                                {imgs.slice(1).map((url, i) => (
                                  <img key={i} src={url} alt="" style={{ width:36, height:36, objectFit:"cover", borderRadius:5, border:"1px solid #e5e7eb", flexShrink:0 }} />
                                ))}
                              </div>
                            )}
                            <div style={{ padding:"12px 14px" }}>
                              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                                <div>
                                  <div style={{ fontWeight:800, color:"#0f172a", fontSize:14 }}>{p.nom}</div>
                                  <div style={{ fontSize:11, color:"#6b7280", marginTop:2 }}>{p.categorie}</div>
                                </div>
                                <span style={{ padding:"2px 8px", borderRadius:999, fontSize:10, fontWeight:700,
                                  background:p.actif?"#dcfce7":"#f1f5f9", color:p.actif?"#166534":"#6b7280" }}>
                                  {p.actif?"Actif":"Inactif"}
                                </span>
                              </div>
                              {p.description && <p style={{ fontSize:12, color:"#64748b", margin:"4px 0 8px", lineHeight:1.4 }}>{p.description.slice(0,70)}{p.description.length>70?"…":""}</p>}
                              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                                <span style={{ fontWeight:800, fontSize:15, color:BET_COLOR }}>{Number(p.prix).toLocaleString("fr-FR")} FCFA</span>
                                <span style={{ fontSize:11, color: p.stock===0?"#dc2626":"#6b7280" }}>
                                  {p.stock===0 ? "⚠ Rupture" : `Stock : ${p.stock}`}
                                </span>
                              </div>
                              <div style={{ display:"flex", gap:7 }}>
                                <button onClick={()=>{ setSelectedProduit(p); setProdForm({ nom:p.nom, description:p.description||"", prix:p.prix, stock:p.stock, categorie:p.categorie, images: imgs }); setProdFormOpen(true); }}
                                  disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                                  style={{ flex:1, padding:"7px 0", background:BET_LIGHT, color:BET_COLOR, border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12, opacity:peut("configuration","update")?1:0.5 }}>
                                  ✏️ Modifier
                                </button>
                                <button onClick={()=>toggleProduitActif(p.id, p.actif)}
                                  disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                                  style={{ padding:"7px 10px", background:p.actif?"#fff7ed":"#f0fdf4", color:p.actif?"#c2410c":"#166534", border:`1.5px solid ${p.actif?"#fed7aa":"#bbf7d0"}`, borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12, opacity:peut("configuration","update")?1:0.5 }}>
                                  {p.actif?"⏸":"▶"}
                                </button>
                                <button onClick={()=>deleteProduit(p.id)}
                                  disabled={!peut("configuration","delete")} title={!peut("configuration","delete")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                                  style={{ padding:"7px 10px", background:"#fff1f2", color:"#dc2626", border:"1.5px solid #fecdd3", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12, opacity:peut("configuration","delete")?1:0.5 }}>
                                  🗑
                                </button>
                              </div>
                            </div>
                          </div>
                        );})}
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:8, marginTop:24 }}>
                          <button onClick={()=>setProdPage(p=>Math.max(1,p-1))} disabled={prodPage===1}
                            style={{ padding:"7px 16px", borderRadius:999, border:"1.5px solid #e5e7eb", background:prodPage===1?"#f8fafc":"#fff", color:prodPage===1?"#cbd5e1":"#0b1f40", fontWeight:700, fontSize:12, cursor:prodPage===1?"not-allowed":"pointer" }}>
                            ← Précédent
                          </button>
                          {Array.from({length:totalPages},(_,i)=>i+1).map(n=>(
                            <button key={n} onClick={()=>setProdPage(n)}
                              style={{ width:34, height:34, borderRadius:"50%", border:"none", background:n===prodPage?BET_COLOR:"#f1f5f9", color:n===prodPage?"#fff":"#374151", fontWeight:800, fontSize:13, cursor:"pointer" }}>
                              {n}
                            </button>
                          ))}
                          <button onClick={()=>setProdPage(p=>Math.min(totalPages,p+1))} disabled={prodPage===totalPages}
                            style={{ padding:"7px 16px", borderRadius:999, border:"1.5px solid #e5e7eb", background:prodPage===totalPages?"#f8fafc":"#fff", color:prodPage===totalPages?"#cbd5e1":"#0b1f40", fontWeight:700, fontSize:12, cursor:prodPage===totalPages?"not-allowed":"pointer" }}>
                            Suivant →
                          </button>
                          <span style={{ fontSize:12, color:"#94a3b8", marginLeft:8 }}>{produits.length} produit{produits.length>1?"s":""} · page {prodPage}/{totalPages}</span>
                        </div>
                      )}
                      </>
                    )}
                  </>
                  );
                })()}


                {/* ── Commandes ── */}
                {boutiqueSubTab === "commandes" && (
                  <>
                    {/* Filtres statut */}
                    <div style={{ display:"flex", gap:8, marginBottom:20, flexWrap:"wrap" }}>
                      {STATUTS_CMD.map(s => (
                        <button key={s.key} onClick={()=>setCmdFiltreStatut(s.key)}
                          style={{ padding:"6px 16px", borderRadius:999, border:`1.5px solid ${cmdFiltreStatut===s.key?s.color:"#e5e7eb"}`,
                            background:cmdFiltreStatut===s.key?s.color:"#fff",
                            color:cmdFiltreStatut===s.key?"#fff":s.color,
                            fontWeight:700, fontSize:12, cursor:"pointer" }}>
                          {s.label}
                          {s.key!=="tous" && <span style={{ marginLeft:6, opacity:.85 }}>({commandes.filter(c=>c.statut===s.key).length})</span>}
                        </button>
                      ))}
                      <button onClick={fetchCommandes} style={{ marginLeft:"auto", padding:"6px 14px", background:BET_LIGHT, color:BET_COLOR, border:"none", borderRadius:999, cursor:"pointer", fontWeight:700, fontSize:12 }}>🔄 Actualiser</button>
                    </div>

                    {cmdLoading ? (
                      <div style={{ textAlign:"center", padding:40, color:"#6b7280" }}>Chargement…</div>
                    ) : commandes.length === 0 ? (
                      <div style={{ textAlign:"center", padding:60 }}>
                        <div style={{ fontSize:48, marginBottom:12 }}>🧾</div>
                        <p style={{ color:"#6b7280", fontWeight:600 }}>Aucune commande{cmdFiltreStatut!=="tous"?" dans ce statut":""}</p>
                      </div>
                    ) : (
                      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                        {commandes.map(cmd => {
                          const statutInfo = STATUTS_CMD.find(s=>s.key===cmd.statut) || STATUTS_CMD[0];
                          return (
                            <div key={cmd.id} style={{ border:"1.5px solid #e5e7eb", borderRadius:12, padding:"16px 20px", background:"#fff", boxShadow:"0 1px 3px rgba(0,0,0,0.04)" }}>
                              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                                <div>
                                  <div style={{ fontWeight:800, color:"#0f172a", fontSize:14 }}>{cmd.client_nom}</div>
                                  <div style={{ fontSize:12, color:"#6b7280", marginTop:2 }}>
                                    {cmd.client_email} {cmd.client_telephone && `· ${cmd.client_telephone}`}
                                  </div>
                                  <div style={{ fontSize:11, color:"#94a3b8", marginTop:2 }}>
                                    {new Date(cmd.created_at).toLocaleDateString("fr-FR", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" })}
                                  </div>
                                </div>
                                <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6 }}>
                                  <span style={{ fontWeight:800, fontSize:16, color:BET_COLOR }}>{Number(cmd.total).toLocaleString("fr-FR")} FCFA</span>
                                  <span style={{ padding:"3px 10px", borderRadius:999, fontSize:11, fontWeight:700, background:`${statutInfo.color}18`, color:statutInfo.color }}>
                                    {statutInfo.label}
                                  </span>
                                </div>
                              </div>
                              {/* Articles */}
                              {Array.isArray(cmd.items) && cmd.items.length > 0 && (
                                <div style={{ background:"#f8fafc", borderRadius:8, padding:"10px 12px", marginBottom:10, fontSize:12 }}>
                                  {cmd.items.map((item, i) => (
                                    <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"2px 0", color:"#374151" }}>
                                      <span>{item.nom} × {item.quantite}</span>
                                      <span style={{ fontWeight:700 }}>{(Number(item.prix_unitaire)*Number(item.quantite)).toLocaleString("fr-FR")} FCFA</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {cmd.notes && <p style={{ fontSize:12, color:"#64748b", margin:"0 0 10px", fontStyle:"italic" }}>📝 {cmd.notes}</p>}
                              {/* Actions statut */}
                              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                                {STATUTS_CMD.filter(s=>s.key!=="tous"&&s.key!==cmd.statut).map(s => (
                                  <button key={s.key} onClick={()=>updateCmdStatut(cmd.id, s.key)}
                                    disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                                    style={{ padding:"5px 12px", borderRadius:6, border:`1.5px solid ${s.color}40`, background:`${s.color}10`, color:s.color, fontWeight:700, fontSize:11, cursor:"pointer", opacity:peut("configuration","update")?1:0.5 }}>
                                    → {s.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}

              </div>
            </div>
          )}

          {/* [old blog block removed - now inside contenu inner tabs] */}
          {false && (
            <div>
              {/* Header */}
              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:12, marginBottom:20 }}>
                <div>
                  <h2 style={{ margin:0, fontSize:18, fontWeight:800, color:"#0f172a" }}>📝 Gestion des Articles Blog</h2>
                  <p style={{ margin:"4px 0 0", fontSize:13, color:"#6b7280" }}>{blogArticles.length} article{blogArticles.length>1?"s":""} · {blogArticles.filter(a=>!a.publie).length} brouillon{blogArticles.filter(a=>!a.publie).length>1?"s":""}</p>
                </div>
                <div style={{ display:"flex", gap:10 }}>
                  <button onClick={fetchBlogArticles} style={{ padding:"8px 16px", background:"#f1f5f9", border:"1px solid #e2e8f0", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:13, color:"#374151" }}>🔄 Actualiser</button>
                  <button onClick={() => { setBlogForm(BLOG_FORM_INIT); setBlogEditId(null); setBlogFormOpen(f=>!f); }}
                    disabled={!blogFormOpen && !peut("configuration","create")} title={(!blogFormOpen && !peut("configuration","create"))?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                    style={{ padding:"8px 18px", background:"#1e3a8a", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:13, opacity:(!blogFormOpen && !peut("configuration","create"))?0.5:1 }}>
                    {blogFormOpen && !blogEditId ? "✕ Fermer" : !peut("configuration","create") ? "🔒 Nouvel article" : "➕ Nouvel article"}
                  </button>
                </div>
              </div>

              {/* Formulaire création / édition */}
              {blogFormOpen && (
                <div style={{ background:"#f8fafc", border:"1.5px solid #e2e8f0", borderRadius:12, padding:22, marginBottom:24 }}>
                  <h3 style={{ margin:"0 0 18px", fontSize:15, fontWeight:800, color:"#0f172a" }}>{blogEditId ? "✏️ Modifier l'article" : "➕ Créer un article"}</h3>

                  {/* Champs principaux */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
                    <div style={{ gridColumn:"1/-1" }}>
                      <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Titre *</label>
                      <input value={blogForm.titre} onChange={e=>setBlogForm(f=>({...f,titre:e.target.value}))} placeholder="Titre de l'article…"
                        style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e5e7eb", fontSize:13, boxSizing:"border-box" }} />
                    </div>
                    <div>
                      <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Catégorie</label>
                      <select value={blogForm.categorie} onChange={e=>setBlogForm(f=>({...f,categorie:e.target.value}))}
                        style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e5e7eb", fontSize:13, boxSizing:"border-box" }}>
                        {BLOG_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Auteur</label>
                      <input value={blogForm.auteur} onChange={e=>setBlogForm(f=>({...f,auteur:e.target.value}))} placeholder="Admin"
                        style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e5e7eb", fontSize:13, boxSizing:"border-box" }} />
                    </div>
                    <div>
                      <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Temps de lecture</label>
                      <input value={blogForm.read_time} onChange={e=>setBlogForm(f=>({...f,read_time:e.target.value}))} placeholder="Ex: 5 min"
                        style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e5e7eb", fontSize:13, boxSizing:"border-box" }} />
                    </div>
                    <div style={{ gridColumn:"1/-1" }}>
                      <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Extrait / Résumé</label>
                      <textarea value={blogForm.extrait} onChange={e=>setBlogForm(f=>({...f,extrait:e.target.value}))} rows={2} placeholder="Courte description affichée sur la liste…"
                        style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e5e7eb", fontSize:13, resize:"vertical", boxSizing:"border-box", fontFamily:"inherit" }} />
                    </div>
                    <div style={{ gridColumn:"1/-1" }}>
                      <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Contenu (HTML)</label>
                      <textarea value={blogForm.contenu} onChange={e=>setBlogForm(f=>({...f,contenu:e.target.value}))} rows={6} placeholder="Contenu HTML de l'article…"
                        style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e5e7eb", fontSize:13, resize:"vertical", boxSizing:"border-box", fontFamily:"monospace" }} />
                    </div>
                  </div>

                  {/* Médias image + vidéo */}
                  <input ref={blogFileRef} type="file" accept={blogFileTarget==="video" ? "video/*" : "image/*"} style={{ display:"none" }}
                    onChange={e => { const f=e.target.files?.[0]; if(f) blogUploadMedia(f, blogFileTarget); e.target.value=""; }} />
                  <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:10 }}>📎 Médias</label>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
                    {/* Image principale */}
                    <div style={{ background:"#fff", border:"1.5px dashed #cbd5e1", borderRadius:9, padding:12 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:"#374151", marginBottom:8 }}>🖼️ Image principale</div>
                      {blogForm.image_url ? (
                        <div style={{ position:"relative" }}>
                          <img src={blogForm.image_url} alt="" style={{ width:"100%", height:80, objectFit:"cover", borderRadius:6, display:"block" }} />
                          <button onClick={()=>setBlogForm(f=>({...f,image_url:""}))} style={{ position:"absolute",top:4,right:4,background:"rgba(0,0,0,.6)",color:"#fff",border:"none",borderRadius:"50%",width:22,height:22,cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center" }}>✕</button>
                        </div>
                      ) : (
                        <button onClick={()=>{ setBlogFileTarget("image"); blogFileRef.current?.click(); }} disabled={!!blogUploading}
                          style={{ width:"100%", padding:"10px 0", background:"#eff6ff", color:"#1e40af", border:"1px solid #bfdbfe", borderRadius:7, fontWeight:700, fontSize:11, cursor:"pointer" }}>
                          {blogUploading==="image" ? `Upload… ${blogUploadPct}%` : "☁️ Uploader une image"}
                        </button>
                      )}
                      <input value={blogForm.image_url} onChange={e=>setBlogForm(f=>({...f,image_url:e.target.value}))}
                        placeholder="ou coller une URL…" style={{ width:"100%",marginTop:6,padding:"6px 8px",border:"1px solid #e2e8f0",borderRadius:6,fontSize:11,boxSizing:"border-box",outline:"none" }} />
                    </div>
                    {/* Vidéo */}
                    <div style={{ background:"#fff", border:"1.5px dashed #cbd5e1", borderRadius:9, padding:12 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:"#374151", marginBottom:8 }}>🎬 Vidéo (YouTube, Vimeo ou MP4)</div>
                      {/* Champ URL - toujours visible et éditable */}
                      <div style={{ marginBottom:8 }}>
                        <label style={{ fontSize:11, fontWeight:700, color:"#6b7280", display:"block", marginBottom:4 }}>🔗 URL de la vidéo</label>
                        <input
                          value={blogForm.video_url}
                          onChange={e=>setBlogForm(f=>({...f,video_url:e.target.value}))}
                          placeholder="https://youtube.com/watch?v=… ou Vimeo/MP4"
                          style={{ width:"100%", padding:"8px 10px", border:"1.5px solid #d1d5db", borderRadius:7, fontSize:12, boxSizing:"border-box", outline:"none", background:"#f9fafb", color:"#111827" }}
                        />
                      </div>
                      {/* Aperçu si URL renseignée, sinon bouton upload fichier */}
                      {blogForm.video_url ? (
                        <div style={{ position:"relative" }}>
                          <div style={{ width:"100%", height:56, background:"#0f172a", borderRadius:6, display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                            <span style={{ color:"#fff", fontSize:16 }}>▶</span>
                            <span style={{ color:"rgba(255,255,255,.55)", fontSize:10, maxWidth:"78%", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{blogForm.video_url}</span>
                          </div>
                          <button onClick={()=>setBlogForm(f=>({...f,video_url:""}))} title="Supprimer" style={{ position:"absolute",top:4,right:4,background:"rgba(220,38,38,.85)",color:"#fff",border:"none",borderRadius:"50%",width:20,height:20,cursor:"pointer",fontSize:11,display:"flex",alignItems:"center",justifyContent:"center" }}>✕</button>
                        </div>
                      ) : (
                        <button onClick={()=>{ setBlogFileTarget("video"); blogFileRef.current?.click(); }} disabled={!!blogUploading}
                          style={{ width:"100%", padding:"9px 0", background:"#f0fdf4", color:"#15803d", border:"1px solid #bbf7d0", borderRadius:7, fontWeight:700, fontSize:11, cursor:"pointer" }}>
                          {blogUploading==="video" ? `Upload… ${blogUploadPct}%` : "☁️ Uploader un fichier vidéo"}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Statut + actions */}
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
                    <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", fontSize:13, fontWeight:700, color:"#374151" }}>
                      <input type="checkbox" checked={blogForm.publie} onChange={e=>setBlogForm(f=>({...f,publie:e.target.checked}))} style={{ width:16, height:16 }} />
                      Publier immédiatement
                    </label>
                    <div style={{ display:"flex", gap:10 }}>
                      <button onClick={()=>{ setBlogFormOpen(false); setBlogEditId(null); setBlogForm(BLOG_FORM_INIT); }}
                        style={{ padding:"9px 18px", background:"#f1f5f9", border:"1px solid #e2e8f0", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:13, color:"#374151" }}>Annuler</button>
                      <button onClick={blogSave} disabled={blogSaving||!peut("configuration",blogEditId?"update":"create")}
                        title={!peut("configuration",blogEditId?"update":"create")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                        style={{ padding:"9px 22px", background:"#1e3a8a", color:"#fff", border:"none", borderRadius:8, cursor:blogSaving?"not-allowed":"pointer", fontWeight:700, fontSize:13, opacity:(blogSaving||!peut("configuration",blogEditId?"update":"create"))?0.6:1 }}>
                        {blogSaving ? "⏳ Enregistrement…" : !peut("configuration",blogEditId?"update":"create") ? "🔒 Enregistrer" : blogEditId ? "💾 Mettre à jour" : "✓ Créer l'article"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Recherche */}
              <div style={{ marginBottom:16 }}>
                <input value={blogSearch} onChange={e=>setBlogSearch(e.target.value)} placeholder="🔍 Rechercher un article…"
                  style={{ width:"100%", padding:"9px 14px", borderRadius:9, border:"1.5px solid #e5e7eb", fontSize:13, boxSizing:"border-box", outline:"none" }} />
              </div>

              {/* Liste articles */}
              {blogLoading ? (
                <div style={{ textAlign:"center", padding:40, color:"#9ca3af", fontSize:14 }}>⏳ Chargement…</div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {blogArticles.filter(a => !blogSearch || a.titre?.toLowerCase().includes(blogSearch.toLowerCase()) || a.categorie?.toLowerCase().includes(blogSearch.toLowerCase()) || a.auteur?.toLowerCase().includes(blogSearch.toLowerCase())).map(article => (
                    <div key={article.id} style={{ background:"#f9fafb", border:"1.5px solid #e5e7eb", borderRadius:12, padding:16, display:"flex", alignItems:"center", gap:14, flexWrap:"wrap" }}>
                      {/* Miniature */}
                      <div style={{ width:64, height:64, borderRadius:8, overflow:"hidden", flexShrink:0, background:"#e2e8f0" }}>
                        {article.image_url ? <img src={article.image_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:24 }}>📄</div>}
                      </div>
                      {/* Info */}
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:3 }}>
                          <span style={{ fontWeight:800, fontSize:14, color:"#0f172a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:320 }}>{article.titre}</span>
                          <span style={{ padding:"2px 8px", borderRadius:999, fontSize:11, fontWeight:700, background: article.publie ? "#dcfce7" : "#fef9c3", color: article.publie ? "#166534" : "#854d0e" }}>
                            {article.publie ? "✅ Publié" : "📝 Brouillon"}
                          </span>
                          {article.video_url && <span style={{ padding:"2px 8px", borderRadius:999, fontSize:11, fontWeight:700, background:"#f0fdf4", color:"#15803d" }}>🎬 Vidéo</span>}
                        </div>
                        <div style={{ fontSize:12, color:"#6b7280" }}>
                          {article.categorie} · {article.auteur} · {article.read_time || "-"}
                          {article.nb_commentaires > 0 && ` · 💬 ${article.nb_commentaires} commentaire${article.nb_commentaires>1?"s":""}`}
                          {article.nb_en_attente > 0 && <span style={{ color:"#d97706" }}> (⏳{article.nb_en_attente})</span>}
                        </div>
                        <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>{article.created_at ? new Date(article.created_at).toLocaleDateString("fr-FR") : ""}</div>
                      </div>
                      {/* Actions */}
                      <div style={{ display:"flex", gap:8, flexShrink:0 }}>
                        <button onClick={()=>blogTogglePublie(article.id, article.publie)}
                          disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                          style={{ padding:"6px 12px", background: article.publie ? "#fef9c3" : "#dcfce7", color: article.publie ? "#854d0e" : "#166534", border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12, opacity:peut("configuration","update")?1:0.5 }}>
                          {article.publie ? "↩ Dépublier" : "✅ Publier"}
                        </button>
                        <button onClick={()=>{ setBlogForm({ titre:article.titre, extrait:article.extrait||"", contenu:article.contenu||"", categorie:article.categorie||"Actualités", auteur:article.auteur||"Admin", read_time:article.read_time||"", image_url:article.image_url||"", video_url:article.video_url||"", publie:article.publie }); setBlogEditId(article.id); setBlogFormOpen(true); window.scrollTo({top:0,behavior:"smooth"}); }}
                          disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                          style={{ padding:"6px 12px", background:"#eff6ff", color:"#1e40af", border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12, opacity:peut("configuration","update")?1:0.5 }}>✏️ Modifier</button>
                        <button onClick={()=>blogDelete(article.id)}
                          disabled={!peut("configuration","delete")} title={!peut("configuration","delete")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                          style={{ padding:"6px 12px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12, opacity:peut("configuration","delete")?1:0.5 }}>🗑</button>
                      </div>
                    </div>
                  ))}
                  {blogArticles.length === 0 && !blogLoading && (
                    <div style={{ textAlign:"center", padding:40, color:"#9ca3af", fontSize:14 }}>Aucun article. Créez-en un avec le bouton "➕ Nouvel article".</div>
                  )}
                </div>
              )}

            </div>
          )}

          {/* [old blog_comments block removed - now inside contenu inner tabs] */}
          {false && (
            <div>
              {/* Header */}
              <div style={{ background: BET_GRADIENT, padding:"24px 28px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <h2 style={{ color:"#fff", margin:0, fontSize:"1.3rem", fontWeight:800 }}>💬 Commentaires Blog</h2>
                  <p style={{ color:"rgba(255,255,255,0.7)", margin:"4px 0 0", fontSize:".85rem" }}>
                    {blogComments.length} commentaire{blogComments.length!==1?"s":""} · supprimez les contenus inappropriés
                  </p>
                </div>
                <button onClick={fetchBlogComments} style={{ padding:"9px 18px", background:"rgba(255,255,255,0.15)", color:"#fff", border:"1px solid rgba(255,255,255,0.3)", borderRadius:8, fontWeight:700, cursor:"pointer", fontSize:13 }}>
                  🔄 Actualiser
                </button>
              </div>

              <div style={{ padding:28 }}>
                {/* Recherche */}
                <div style={{ marginBottom:20 }}>
                  <input
                    value={blogCommentsSearch}
                    onChange={e => setBlogCommentsSearch(e.target.value)}
                    placeholder="🔍 Rechercher par nom, article, contenu…"
                    style={{ width:"100%", padding:"10px 14px", border:"1.5px solid #e5e7eb", borderRadius:10, fontSize:13, boxSizing:"border-box" }}
                  />
                </div>

                {blogCommentsLoading ? (
                  <div style={{ textAlign:"center", padding:40, color:"#6b7280" }}>Chargement…</div>
                ) : blogComments.length === 0 ? (
                  <div style={{ textAlign:"center", padding:60 }}>
                    <div style={{ fontSize:48, marginBottom:12 }}>💬</div>
                    <p style={{ color:"#6b7280", fontWeight:600 }}>Aucun commentaire pour l'instant</p>
                  </div>
                ) : (() => {
                  const q = blogCommentsSearch.toLowerCase();
                  const filtered = blogComments.filter(c =>
                    !q ||
                    c.nom?.toLowerCase().includes(q) ||
                    c.commentaire?.toLowerCase().includes(q) ||
                    c.article_titre?.toLowerCase().includes(q) ||
                    c.email?.toLowerCase().includes(q)
                  );
                  return (
                    <>
                      {filtered.length === 0 ? (
                        <p style={{ color:"#94a3b8", textAlign:"center", padding:32 }}>Aucun résultat pour « {blogCommentsSearch} »</p>
                      ) : (
                        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                          {filtered.map(c => (
                            <div key={c.id} style={{ border:"1.5px solid #e5e7eb", borderRadius:12, padding:"14px 18px", background:"#fff", display:"flex", gap:16, alignItems:"flex-start" }}>
                              {/* Avatar */}
                              <div style={{ width:40, height:40, borderRadius:"50%", background:"linear-gradient(135deg,#1e3a8a,#0891b2)", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:".9rem", flexShrink:0 }}>
                                {(c.nom||"?")[0].toUpperCase()}
                              </div>
                              {/* Contenu */}
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
                                  <div>
                                    <span style={{ fontWeight:800, color:"#0f172a", fontSize:14 }}>{c.nom}</span>
                                    {c.email && <span style={{ fontSize:12, color:"#94a3b8", marginLeft:8 }}>{c.email}</span>}
                                  </div>
                                  <span style={{ fontSize:11, color:"#94a3b8", flexShrink:0 }}>
                                    {new Date(c.created_at).toLocaleDateString("fr-FR", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" })}
                                  </span>
                                </div>
                                <div style={{ fontSize:11, fontWeight:700, margin:"4px 0 8px" }}>
                                  📄{" "}
                                  {c.article_id ? (
                                    <a
                                      href={`${FRONTEND_URL}/blog/${c.article_id}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{ color:BET_COLOR, textDecoration:"none", borderBottom:`1px dashed ${BET_COLOR}` }}
                                    >
                                      {c.article_titre || "Voir l'article"}
                                    </a>
                                  ) : (
                                    <span style={{ color:"#94a3b8" }}>{c.article_titre || "-"}</span>
                                  )}
                                </div>
                                <p style={{ fontSize:13, color:"#334155", margin:0, lineHeight:1.6, wordBreak:"break-word" }}>{c.commentaire}</p>
                              </div>
                              {/* Supprimer */}
                              <button
                                onClick={() => deleteBlogComment(c.id)}
                                disabled={!peut("configuration","delete")}
                                title={!peut("configuration","delete")?"🔒 Vous n'avez pas la permission pour cette action":"Supprimer ce commentaire"}
                                style={{ padding:"6px 10px", background:"#fff1f2", color:"#dc2626", border:"1.5px solid #fecdd3", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12, flexShrink:0, opacity:peut("configuration","delete")?1:0.5 }}>
                                🗑 Supprimer
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <p style={{ textAlign:"right", fontSize:12, color:"#94a3b8", marginTop:14 }}>
                        {filtered.length} / {blogComments.length} commentaire{blogComments.length>1?"s":""}
                      </p>
                    </>
                  );
                })()}

              </div>
            </div>
          )}

          {platformSubTab === "newsletter" && (() => {
            const nlFiltered = nlSubscribers.filter(s =>
              !nlSearch || s.email.toLowerCase().includes(nlSearch.toLowerCase())
            );
            return (
              <div style={{ background:"#fff", borderRadius:"0 12px 12px 12px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", overflow:"hidden" }}>
                {/* Header */}
                <div style={{ background: BET_GRADIENT, padding:"24px 28px", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12 }}>
                  <div>
                    <h2 style={{ color:"#fff", margin:0, fontSize:"1.3rem", fontWeight:800 }}>📩 Newsletter</h2>
                    <p style={{ color:"rgba(255,255,255,0.7)", margin:"4px 0 0", fontSize:".85rem" }}>
                      {nlSubscribers.length} abonné{nlSubscribers.length !== 1 ? "s" : ""} · Envoi de campagnes par email
                    </p>
                  </div>
                  <button
                    onClick={() => { nlFermerModal(); setNlCampaignOpen(true); }}
                    disabled={!peut("configuration","create")}
                    title={!peut("configuration","create")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                    style={{ padding:"10px 22px", background:"#ff2945", color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:".88rem", cursor:"pointer", opacity:peut("configuration","create")?1:0.5 }}
                  >
                    {peut("configuration","create")?"✉️ Nouvelle campagne":"🔒 Nouvelle campagne"}
                  </button>
                </div>

                <div style={{ padding:"24px 28px" }}>
                  {/* Historique des campagnes */}
                  <div style={{ marginBottom:32, paddingBottom:24, borderBottom:"1.5px solid #f1f5f9" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12, marginBottom:16 }}>
                      <h3 style={{ margin:0, fontSize:"1.05rem", fontWeight:800, color:"#0f172a" }}>📊 Historique des campagnes</h3>
                      <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
                        <input type="date" value={nlFiltreDateDebut} onChange={e => setNlFiltreDateDebut(e.target.value)}
                          style={{ padding:"7px 10px", border:"1.5px solid #e2e8f0", borderRadius:8, fontSize:".8rem", outline:"none" }} />
                        <span style={{ color:"#94a3b8", fontSize:".8rem" }}>→</span>
                        <input type="date" value={nlFiltreDateFin} onChange={e => setNlFiltreDateFin(e.target.value)}
                          style={{ padding:"7px 10px", border:"1.5px solid #e2e8f0", borderRadius:8, fontSize:".8rem", outline:"none" }} />
                        <select value={nlFiltreStatut} onChange={e => setNlFiltreStatut(e.target.value)}
                          style={{ padding:"7px 10px", border:"1.5px solid #e2e8f0", borderRadius:8, fontSize:".8rem", outline:"none", background:"#fff" }}>
                          <option value="">Tous les statuts</option>
                          <option value="brouillon">📝 Brouillon</option>
                          <option value="programmee">🕒 Programmée</option>
                          <option value="envoyee">✅ Envoyée</option>
                          <option value="annulee">🚫 Annulée</option>
                        </select>
                        {(nlFiltreDateDebut || nlFiltreDateFin || nlFiltreStatut) && (
                          <button onClick={() => { setNlFiltreDateDebut(""); setNlFiltreDateFin(""); setNlFiltreStatut(""); }}
                            style={{ padding:"7px 12px", background:"#f1f5f9", border:"1.5px solid #e2e8f0", borderRadius:8, fontSize:".78rem", cursor:"pointer", color:"#64748b" }}>
                            ✕ Réinitialiser
                          </button>
                        )}
                      </div>
                    </div>

                    {nlKpis && (
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(140px, 1fr))", gap:10, marginBottom:20 }}>
                        {[
                          { l:"Total campagnes", v:nlKpis.total,          bg:"#f8fafc", c:"#0f172a" },
                          { l:"📝 Brouillons",    v:nlKpis.brouillon,      bg:"#f1f5f9", c:"#64748b" },
                          { l:"🕒 Programmées",   v:nlKpis.programmee,     bg:"#fef3c7", c:"#b45309" },
                          { l:"✅ Envoyées",      v:nlKpis.envoyee,        bg:"#dcfce7", c:"#15803d" },
                          { l:"📤 Emails envoyés",v:nlKpis.total_envoyes,  bg:"#eff6ff", c:"#1d4ed8" },
                        ].map(k => (
                          <div key={k.l} style={{ background:k.bg, borderRadius:10, padding:"12px 14px" }}>
                            <div style={{ fontSize:".68rem", color:k.c, fontWeight:700, textTransform:"uppercase", letterSpacing:".04em", opacity:.85 }}>{k.l}</div>
                            <div style={{ fontSize:"1.3rem", fontWeight:800, color:k.c, marginTop:2 }}>{k.v ?? 0}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {nlCampagnesLoading ? (
                      <p style={{ textAlign:"center", color:"#94a3b8", padding:40 }}>Chargement…</p>
                    ) : nlCampagnes.length === 0 ? (
                      <p style={{ textAlign:"center", color:"#94a3b8", padding:40 }}>Aucune campagne pour cette sélection.</p>
                    ) : (
                      <>
                        <div style={{ overflowX:"auto" }}>
                          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:".85rem" }}>
                            <thead>
                              <tr style={{ background:"#f8fafc", borderBottom:"2px solid #e2e8f0" }}>
                                <th style={{ padding:"11px 14px", textAlign:"left", color:"#64748b", fontWeight:700, fontSize:".76rem", textTransform:"uppercase", letterSpacing:".05em" }}>Sujet</th>
                                <th style={{ padding:"11px 14px", textAlign:"left", color:"#64748b", fontWeight:700, fontSize:".76rem", textTransform:"uppercase", letterSpacing:".05em" }}>Destinataires</th>
                                <th style={{ padding:"11px 14px", textAlign:"left", color:"#64748b", fontWeight:700, fontSize:".76rem", textTransform:"uppercase", letterSpacing:".05em" }}>Statut</th>
                                <th style={{ padding:"11px 14px", textAlign:"left", color:"#64748b", fontWeight:700, fontSize:".76rem", textTransform:"uppercase", letterSpacing:".05em" }}>Date</th>
                                <th style={{ padding:"11px 14px", textAlign:"right", color:"#64748b", fontWeight:700, fontSize:".76rem", textTransform:"uppercase", letterSpacing:".05em" }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {nlCampagnes.map(c => {
                                const badge = {
                                  brouillon:  { l:"📝 Brouillon",  c:"#64748b", bg:"#f1f5f9" },
                                  programmee: { l:"🕒 Programmée", c:"#b45309", bg:"#fef3c7" },
                                  envoyee:    { l:"✅ Envoyée",    c:"#15803d", bg:"#dcfce7" },
                                  annulee:    { l:"🚫 Annulée",    c:"#b91c1c", bg:"#fee2e2" },
                                }[c.statut] || { l:c.statut, c:"#64748b", bg:"#f1f5f9" };
                                const destLabel = c.destinataires?.mode === "segment" ? `🎓 ${c.destinataires.type_coaching === "prive" ? "Coaching privé" : "Coaching groupe"}`
                                  : c.destinataires?.mode === "apprenant" ? "👤 Apprenant précis" : "📩 Abonnés newsletter";
                                const dateAffichee = c.statut === "envoyee" ? c.date_envoi : c.statut === "programmee" ? c.date_programmee : c.created_at;
                                return (
                                  <tr key={c.id} style={{ borderBottom:"1px solid #f1f5f9", cursor:"pointer", transition:"background .15s" }}
                                    onClick={() => nlOuvrirDetail(c.id)}
                                    onMouseEnter={e => e.currentTarget.style.background="#f8fafc"}
                                    onMouseLeave={e => e.currentTarget.style.background=""}>
                                    <td style={{ padding:"11px 14px", fontWeight:600, color:"#0f172a", maxWidth:260, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.sujet}</td>
                                    <td style={{ padding:"11px 14px", color:"#64748b" }}>{destLabel} <span style={{ color:"#94a3b8" }}>({c.total_destinataires})</span></td>
                                    <td style={{ padding:"11px 14px" }}>
                                      <span style={{ padding:"3px 10px", borderRadius:20, fontSize:".74rem", fontWeight:700, background:badge.bg, color:badge.c }}>{badge.l}</span>
                                    </td>
                                    <td style={{ padding:"11px 14px", color:"#64748b", fontSize:".8rem" }}>
                                      {dateAffichee ? new Date(dateAffichee).toLocaleString("fr-FR", { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "-"}
                                    </td>
                                    <td style={{ padding:"11px 14px", textAlign:"right" }} onClick={e => e.stopPropagation()}>
                                      <div style={{ display:"flex", gap:6, justifyContent:"flex-end", flexWrap:"wrap" }}>
                                        {["brouillon","programmee"].includes(c.statut) && (
                                          <>
                                            <button onClick={() => nlOuvrirModif(c)}
                                              style={{ padding:"5px 10px", background:"#eff6ff", color:"#1d4ed8", border:"none", borderRadius:6, fontSize:".76rem", fontWeight:700, cursor:"pointer" }}>
                                              Modifier
                                            </button>
                                            <button onClick={() => nlEnvoyerMaintenant(c.id)}
                                              style={{ padding:"5px 10px", background:"#f0fdf4", color:"#15803d", border:"none", borderRadius:6, fontSize:".76rem", fontWeight:700, cursor:"pointer" }}>
                                              Envoyer
                                            </button>
                                            <button onClick={() => nlAnnulerCampagne(c.id)}
                                              style={{ padding:"5px 10px", background:"#fee2e2", color:BET_RED, border:"none", borderRadius:6, fontSize:".76rem", fontWeight:700, cursor:"pointer" }}>
                                              Annuler
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:14, fontSize:".8rem", color:"#64748b" }}>
                          <span>{nlTotalCampagnes} campagne{nlTotalCampagnes !== 1 ? "s" : ""}</span>
                          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                            <button disabled={nlPage <= 1} onClick={() => fetchNlCampagnes(nlPage - 1)}
                              style={{ padding:"6px 12px", background: nlPage<=1?"#f1f5f9":"#fff", border:"1.5px solid #e2e8f0", borderRadius:6, cursor: nlPage<=1?"not-allowed":"pointer", opacity:nlPage<=1?0.5:1 }}>
                              ← Précédent
                            </button>
                            <span>Page {nlPage} / {nlTotalPages}</span>
                            <button disabled={nlPage >= nlTotalPages} onClick={() => fetchNlCampagnes(nlPage + 1)}
                              style={{ padding:"6px 12px", background: nlPage>=nlTotalPages?"#f1f5f9":"#fff", border:"1.5px solid #e2e8f0", borderRadius:6, cursor: nlPage>=nlTotalPages?"not-allowed":"pointer", opacity:nlPage>=nlTotalPages?0.5:1 }}>
                              Suivant →
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Barre de recherche */}
                  <div style={{ display:"flex", gap:10, marginBottom:20 }}>
                    <input
                      placeholder="Rechercher un email…"
                      value={nlSearch}
                      onChange={e => setNlSearch(e.target.value)}
                      style={{ flex:1, padding:"9px 14px", border:"1.5px solid #e2e8f0", borderRadius:8, fontSize:".88rem", outline:"none" }}
                    />
                    <button
                      onClick={fetchNlSubscribers}
                      style={{ padding:"9px 16px", background:"#f1f5f9", border:"1.5px solid #e2e8f0", borderRadius:8, cursor:"pointer", fontSize:".88rem" }}
                    >
                      🔄 Actualiser
                    </button>
                  </div>

                  {/* Tableau abonnés */}
                  {nlLoading ? (
                    <p style={{ textAlign:"center", color:"#94a3b8", padding:40 }}>Chargement…</p>
                  ) : nlFiltered.length === 0 ? (
                    <p style={{ textAlign:"center", color:"#94a3b8", padding:40 }}>Aucun abonné trouvé.</p>
                  ) : (
                    <div style={{ overflowX:"auto" }}>
                      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:".88rem" }}>
                        <thead>
                          <tr style={{ background:"#f8fafc", borderBottom:"2px solid #e2e8f0" }}>
                            <th style={{ padding:"11px 14px", textAlign:"left", color:"#64748b", fontWeight:700, fontSize:".78rem", textTransform:"uppercase", letterSpacing:".05em" }}>#</th>
                            <th style={{ padding:"11px 14px", textAlign:"left", color:"#64748b", fontWeight:700, fontSize:".78rem", textTransform:"uppercase", letterSpacing:".05em" }}>Email</th>
                            <th style={{ padding:"11px 14px", textAlign:"left", color:"#64748b", fontWeight:700, fontSize:".78rem", textTransform:"uppercase", letterSpacing:".05em" }}>Date d'inscription</th>
                            <th style={{ padding:"11px 14px", textAlign:"right", color:"#64748b", fontWeight:700, fontSize:".78rem", textTransform:"uppercase", letterSpacing:".05em" }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {nlFiltered.map((s, i) => (
                            <tr key={s.id} style={{ borderBottom:"1px solid #f1f5f9", transition:"background .15s" }}
                              onMouseEnter={e => e.currentTarget.style.background="#f8fafc"}
                              onMouseLeave={e => e.currentTarget.style.background=""}
                            >
                              <td style={{ padding:"11px 14px", color:"#94a3b8", fontSize:".8rem" }}>{i + 1}</td>
                              <td style={{ padding:"11px 14px", fontWeight:600, color:"#0f172a" }}>{s.email}</td>
                              <td style={{ padding:"11px 14px", color:"#64748b" }}>
                                {new Date(s.subscribed_at).toLocaleDateString("fr-FR", { day:"numeric", month:"long", year:"numeric" })}
                              </td>
                              <td style={{ padding:"11px 14px", textAlign:"right" }}>
                                <button
                                  onClick={() => nlDelete(s.id)}
                                  disabled={!peut("configuration","delete")}
                                  title={!peut("configuration","delete")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                                  style={{ padding:"5px 12px", background:"#fee2e2", color:BET_RED, border:"none", borderRadius:6, fontSize:".78rem", fontWeight:700, cursor:"pointer", opacity:peut("configuration","delete")?1:0.5 }}
                                >
                                  Supprimer
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p style={{ textAlign:"right", fontSize:".78rem", color:"#94a3b8", marginTop:10 }}>
                        {nlFiltered.length} / {nlSubscribers.length} abonné{nlSubscribers.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                  )}

                </div>

                {/* Modal détail campagne (stats) */}
                {nlDetail && (
                  <ModaleAnimee ouvert
                    onClicVoile={() => setNlDetail(null)}
                    style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
                    panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:560, maxHeight:"90vh", overflow:"auto", boxShadow:"0 24px 64px rgba(0,0,0,0.2)" }}>
                      <div style={{ background: BET_GRADIENT, padding:"20px 24px", borderRadius:"16px 16px 0 0", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <h3 style={{ color:"#fff", margin:0, fontSize:"1.05rem", fontWeight:800 }}>📊 Détail de la campagne</h3>
                        <button onClick={() => setNlDetail(null)}
                          style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", borderRadius:8, padding:"6px 10px", cursor:"pointer", fontSize:16 }}>✕</button>
                      </div>
                      <div style={{ padding:24 }}>
                        {nlDetailLoading || !nlDetail.campagne ? (
                          <p style={{ textAlign:"center", color:"#94a3b8", padding:30 }}>Chargement…</p>
                        ) : (() => {
                          const c = nlDetail.campagne, s = nlDetail.stats;
                          return (
                            <>
                              <h4 style={{ margin:"0 0 6px", fontSize:"1rem", fontWeight:800, color:"#0f172a" }}>{c.sujet}</h4>
                              <p style={{ margin:"0 0 18px", fontSize:".82rem", color:"#94a3b8", whiteSpace:"pre-line" }}>{c.contenu?.slice(0,200)}{c.contenu?.length > 200 ? "…" : ""}</p>

                              <div style={{ display:"grid", gridTemplateColumns:"repeat(2, 1fr)", gap:10, marginBottom:18 }}>
                                {[
                                  { l:"Destinataires", v:c.total_destinataires },
                                  { l:"Envoyés",       v:c.nb_envoyes },
                                  { l:"Échecs",        v:c.nb_echecs },
                                  { l:"Statut",        v:c.statut },
                                ].map(item => (
                                  <div key={item.l} style={{ background:"#f8fafc", borderRadius:10, padding:"12px 14px" }}>
                                    <div style={{ fontSize:".72rem", color:"#94a3b8", textTransform:"uppercase", letterSpacing:".05em", fontWeight:700 }}>{item.l}</div>
                                    <div style={{ fontSize:"1.15rem", fontWeight:800, color:"#0f172a", marginTop:2 }}>{item.v ?? "-"}</div>
                                  </div>
                                ))}
                              </div>

                              {s && c.statut === "envoyee" && (
                                <div style={{ marginBottom:18 }}>
                                  <div style={{ fontWeight:700, fontSize:".83rem", color:"#374151", marginBottom:8 }}>Statistiques d'engagement (Brevo)</div>
                                  <div style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:10 }}>
                                    {[
                                      { l:"👁️ Ouvertures", v:s.opened },
                                      { l:"🖱️ Clics",      v:s.clicks },
                                      { l:"❌ Bounces",    v:(s.hardBounces||0)+(s.softBounces||0) },
                                    ].map(item => (
                                      <div key={item.l} style={{ background:"#eff6ff", borderRadius:10, padding:"12px 10px", textAlign:"center" }}>
                                        <div style={{ fontSize:".72rem", color:"#1d4ed8", fontWeight:700 }}>{item.l}</div>
                                        <div style={{ fontSize:"1.2rem", fontWeight:800, color:"#0f172a", marginTop:4 }}>{item.v ?? 0}</div>
                                      </div>
                                    ))}
                                  </div>
                                  <p style={{ fontSize:".74rem", color:"#94a3b8", marginTop:8 }}>
                                    Cliquer sur "🖱️ Clics" ne donne qu'un total - pour voir le détail des liens cliqués, consultez le tableau de bord Brevo (Campagnes → Statistiques).
                                  </p>
                                </div>
                              )}

                              {c.statut === "programmee" && (
                                <div style={{ background:"#fef3c7", border:"1px solid #fde68a", borderRadius:10, padding:"12px 14px", marginBottom:18, fontSize:".82rem", color:"#92400e" }}>
                                  🕒 Programmée pour le {new Date(c.date_programmee).toLocaleString("fr-FR", { dateStyle:"long", timeStyle:"short" })}
                                </div>
                              )}

                              <div style={{ display:"flex", gap:8, justifyContent:"flex-end", flexWrap:"wrap" }}>
                                {["brouillon","programmee"].includes(c.statut) && (
                                  <>
                                    <button onClick={() => { setNlDetail(null); nlOuvrirModif(c); }}
                                      style={{ padding:"9px 16px", background:"#eff6ff", color:"#1d4ed8", border:"none", borderRadius:8, fontSize:".82rem", fontWeight:700, cursor:"pointer" }}>
                                      Modifier
                                    </button>
                                    <button onClick={() => nlEnvoyerMaintenant(c.id)}
                                      style={{ padding:"9px 16px", background:"#f0fdf4", color:"#15803d", border:"none", borderRadius:8, fontSize:".82rem", fontWeight:700, cursor:"pointer" }}>
                                      Envoyer maintenant
                                    </button>
                                    <button onClick={() => nlAnnulerCampagne(c.id)}
                                      style={{ padding:"9px 16px", background:"#fee2e2", color:BET_RED, border:"none", borderRadius:8, fontSize:".82rem", fontWeight:700, cursor:"pointer" }}>
                                      Annuler
                                    </button>
                                  </>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                  </ModaleAnimee>
                )}

                {/* Modal campagne */}
                {nlCampaignOpen && (
                  <ModaleAnimee ouvert
                    onClicVoile={nlFermerModal}
                    style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
                    panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:"90vw", maxHeight:"90vh", overflow:"auto", boxShadow:"0 24px 64px rgba(0,0,0,0.2)" }}>
                      <div style={{ background: BET_GRADIENT, padding:"20px 24px", borderRadius:"16px 16px 0 0", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <div>
                          <h3 style={{ color:"#fff", margin:0, fontSize:"1.1rem", fontWeight:800 }}>{nlEditingId ? "✏️ Modifier la campagne" : "✉️ Nouvelle campagne newsletter"}</h3>
                          <p style={{ color:"rgba(255,255,255,0.7)", margin:"3px 0 0", fontSize:".8rem" }}>
                            Concerne {nlDestinatairesCount()} destinataire{nlDestinatairesCount() !== 1 ? "s" : ""}
                          </p>
                        </div>
                        <button onClick={nlFermerModal}
                          style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", borderRadius:8, padding:"6px 10px", cursor:"pointer", fontSize:16 }}>✕</button>
                      </div>
                      <div style={{ padding:"24px" }}>
                        {/* Destinataires */}
                        <div style={{ marginBottom:18 }}>
                          <label style={{ display:"block", fontWeight:700, fontSize:".83rem", color:"#374151", marginBottom:8 }}>Destinataires *</label>
                          <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:10 }}>
                            {[
                              { v:"newsletter", l:`📩 Abonnés newsletter (${nlSubscribers.length})` },
                              { v:"segment",    l:"🎓 Par type de coaching" },
                              { v:"apprenant",  l:"👤 Un apprenant précis" },
                            ].map(o => (
                              <button key={o.v} onClick={() => setNlDestMode(o.v)}
                                style={{ padding:"7px 14px", borderRadius:20, border:"1.5px solid", fontSize:".78rem", fontWeight:600, cursor:"pointer",
                                  background: nlDestMode===o.v ? BET_COLOR : "#fff", color: nlDestMode===o.v ? "#fff" : "#374151", borderColor: nlDestMode===o.v ? BET_COLOR : "#e2e8f0" }}>
                                {o.l}
                              </button>
                            ))}
                          </div>

                          {nlDestMode === "segment" && (
                            <div style={{ display:"flex", gap:8 }}>
                              {[
                                { v:"groupe", l:`👥 Coaching de groupe (${nlSegmentCounts.groupe})` },
                                { v:"prive",  l:`🎯 Coaching privé (${nlSegmentCounts.prive})` },
                              ].map(o => (
                                <button key={o.v} onClick={() => setNlDestType(o.v)}
                                  style={{ padding:"7px 14px", borderRadius:8, border:"1.5px solid", fontSize:".8rem", fontWeight:600, cursor:"pointer",
                                    background: nlDestType===o.v ? "#eff6ff" : "#fff", color: nlDestType===o.v ? "#1d4ed8" : "#374151", borderColor: nlDestType===o.v ? "#1d4ed8" : "#e2e8f0" }}>
                                  {o.l}
                                </button>
                              ))}
                            </div>
                          )}

                          {nlDestMode === "apprenant" && (
                            <div>
                              {nlApprenantChoisi ? (
                                <div style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", background:"#eff6ff", border:"1.5px solid #bfdbfe", borderRadius:8 }}>
                                  <span style={{ flex:1, fontSize:".85rem", fontWeight:600, color:"#1d4ed8" }}>{nlApprenantChoisi.nom} - {nlApprenantChoisi.email}</span>
                                  <button onClick={() => setNlApprenantChoisi(null)} style={{ background:"none", border:"none", color:"#1d4ed8", cursor:"pointer", fontSize:13 }}>✕</button>
                                </div>
                              ) : (
                                <>
                                  <input
                                    placeholder="Rechercher un apprenant par nom…"
                                    value={nlApprenantSearch}
                                    onChange={e => setNlApprenantSearch(e.target.value)}
                                    style={{ width:"100%", padding:"9px 14px", border:"1.5px solid #e2e8f0", borderRadius:8, fontSize:".85rem", boxSizing:"border-box", outline:"none", marginBottom:8 }}
                                  />
                                  <div style={{ maxHeight:160, overflowY:"auto", border:"1px solid #e2e8f0", borderRadius:8 }}>
                                    {nlApprenantsLoading ? (
                                      <div style={{ padding:16, textAlign:"center", color:"#94a3b8", fontSize:".8rem" }}>Chargement…</div>
                                    ) : nlApprenants.filter(a => a.nom?.toLowerCase().includes(nlApprenantSearch.toLowerCase())).length === 0 ? (
                                      <div style={{ padding:16, textAlign:"center", color:"#94a3b8", fontSize:".8rem" }}>Aucun apprenant trouvé.</div>
                                    ) : nlApprenants.filter(a => a.nom?.toLowerCase().includes(nlApprenantSearch.toLowerCase())).slice(0,50).map(a => (
                                      <div key={a.id} onClick={() => setNlApprenantChoisi(a)}
                                        style={{ padding:"8px 14px", cursor:"pointer", fontSize:".82rem", borderBottom:"1px solid #f1f5f9" }}
                                        onMouseEnter={e=>e.currentTarget.style.background="#f8fafc"} onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                                        <strong>{a.nom}</strong> <span style={{ color:"#94a3b8" }}>- {a.email} · {a.type_coaching==="prive"?"privé":"groupe"}</span>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>

                        <div style={{ marginBottom:16 }}>
                          <label style={{ display:"block", fontWeight:700, fontSize:".83rem", color:"#374151", marginBottom:6 }}>Sujet de l'email *</label>
                          <input
                            placeholder="Ex : 🎓 Nouveau cours TOEIC disponible !"
                            value={nlCampaign.sujet}
                            onChange={e => setNlCampaign(p => ({ ...p, sujet: e.target.value }))}
                            style={{ width:"100%", padding:"10px 14px", border:"1.5px solid #e2e8f0", borderRadius:8, fontSize:".88rem", boxSizing:"border-box", outline:"none" }}
                          />
                        </div>
                        <div style={{ marginBottom:16 }}>
                          <label style={{ display:"block", fontWeight:700, fontSize:".83rem", color:"#374151", marginBottom:6 }}>Contenu *</label>
                          <textarea
                            rows={7}
                            placeholder="Rédigez le corps de votre newsletter ici…"
                            value={nlCampaign.contenu}
                            onChange={e => setNlCampaign(p => ({ ...p, contenu: e.target.value }))}
                            style={{ width:"100%", padding:"10px 14px", border:"1.5px solid #e2e8f0", borderRadius:8, fontSize:".88rem", boxSizing:"border-box", resize:"vertical", outline:"none", fontFamily:"inherit", lineHeight:1.7 }}
                          />
                        </div>
                        <div style={{ marginBottom:16 }}>
                          <label style={{ display:"block", fontWeight:700, fontSize:".83rem", color:"#374151", marginBottom:6 }}>Pièce jointe (optionnel)</label>
                          {nlAttachment ? (
                            <div style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", background:"#f0f9ff", border:"1.5px solid #bae6fd", borderRadius:8 }}>
                              <span style={{ fontSize:16 }}>📎</span>
                              <input
                                value={nlAttachment.nom}
                                onChange={e => setNlAttachment(p => ({ ...p, nom: e.target.value }))}
                                placeholder="Nom affiché au destinataire"
                                style={{ flex:1, fontSize:".82rem", color:"#0369a1", fontWeight:600, background:"transparent", border:"none", outline:"none", padding:0 }}
                              />
                              <button onClick={() => setNlAttachment(null)} style={{ background:"none", border:"none", color:"#0369a1", cursor:"pointer", fontSize:13 }}>✕</button>
                            </div>
                          ) : (
                            <label style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"9px 16px", background:"#f1f5f9", border:"1.5px dashed #cbd5e1", borderRadius:8, cursor:"pointer", fontSize:".82rem", fontWeight:600, color:"#374151" }}>
                              {nlAttachUploading ? "⏳ Envoi…" : "📎 Joindre un fichier (PDF, image…)"}
                              <input type="file" style={{ display:"none" }} disabled={nlAttachUploading} onChange={e => { const f = e.target.files?.[0]; if (f) nlUploadAttachment(f); e.target.value=""; }} />
                            </label>
                          )}
                        </div>
                        {/* Mode d'envoi */}
                        <div style={{ marginBottom:16 }}>
                          <label style={{ display:"block", fontWeight:700, fontSize:".83rem", color:"#374151", marginBottom:8 }}>Envoi *</label>
                          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                            {[
                              { v:"send",     l:"📤 Envoyer maintenant" },
                              { v:"schedule", l:"🕒 Programmer" },
                              { v:"draft",    l:"📝 Enregistrer en brouillon" },
                            ].map(o => (
                              <button key={o.v} onClick={() => setNlMode(o.v)}
                                style={{ padding:"7px 14px", borderRadius:20, border:"1.5px solid", fontSize:".78rem", fontWeight:600, cursor:"pointer",
                                  background: nlMode===o.v ? BET_COLOR : "#fff", color: nlMode===o.v ? "#fff" : "#374151", borderColor: nlMode===o.v ? BET_COLOR : "#e2e8f0" }}>
                                {o.l}
                              </button>
                            ))}
                          </div>
                          {nlMode === "schedule" && (
                            <input type="datetime-local" value={nlDateProgrammee} onChange={e => setNlDateProgrammee(e.target.value)}
                              min={new Date().toISOString().slice(0,16)}
                              style={{ marginTop:10, padding:"9px 14px", border:"1.5px solid #e2e8f0", borderRadius:8, fontSize:".85rem", outline:"none" }} />
                          )}
                        </div>

                        {nlMode === "send" && (
                          <div style={{ background:"#fef9ec", border:"1px solid #fde68a", borderRadius:8, padding:"12px 14px", marginBottom:20, fontSize:".8rem", color:"#92400e" }}>
                            ⚠️ Cette action est irréversible. L'email sera envoyé à <strong>{nlDestinatairesCount()} destinataire{nlDestinatairesCount() !== 1 ? "s" : ""}</strong>.
                          </div>
                        )}
                        <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
                          <button
                            onClick={nlFermerModal}
                            style={{ padding:"10px 20px", background:"#f1f5f9", border:"none", borderRadius:8, fontWeight:600, cursor:"pointer", fontSize:".88rem" }}
                          >
                            Annuler
                          </button>
                          <button
                            onClick={nlValiderCampagne}
                            disabled={nlSending || !peut("configuration","create")}
                            title={!peut("configuration","create")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                            style={{ padding:"10px 22px", background: nlSending ? "#94a3b8" : "#ff2945", color:"#fff", border:"none", borderRadius:8, fontWeight:700, cursor: nlSending ? "not-allowed" : "pointer", fontSize:".88rem", opacity:peut("configuration","create")?1:0.5 }}
                          >
                            {nlSending
                              ? "En cours…"
                              : !peut("configuration","create")
                              ? "🔒 Valider"
                              : nlMode === "send" ? `📤 Envoyer à ${nlDestinatairesCount()} destinataire${nlDestinatairesCount() !== 1 ? "s" : ""}`
                              : nlMode === "schedule" ? "🕒 Programmer l'envoi"
                              : "📝 Enregistrer le brouillon"}
                          </button>
                        </div>
                      </div>
                  </ModaleAnimee>
                )}
              </div>
            );
          })()}

          {platformSubTab === "carrousel" && (() => {
            const isEdit = !!carouselForm?.id;
            return (
              <div style={{ background:"#fff", borderRadius:"0 12px 12px 12px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", overflow:"hidden" }}>
                {/* Header */}
                <div style={{ background: BET_GRADIENT, padding:"24px 28px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div>
                    <h2 style={{ color:"#fff", margin:0, fontSize:"1.3rem", fontWeight:800 }}>🖼️ Carrousel & Médias</h2>
                    <p style={{ color:"rgba(255,255,255,0.7)", margin:"4px 0 0", fontSize:".85rem" }}>
                      {carouselSlides.length} slide{carouselSlides.length !== 1 ? "s" : ""} · {carouselSlides.filter(s=>s.actif).length} visible{carouselSlides.filter(s=>s.actif).length !== 1 ? "s" : ""} sur le site
                    </p>
                  </div>
                  <div style={{ display:"flex", gap:8 }}>
                    <button onClick={fetchCarousel} style={{ padding:"9px 16px", background:"rgba(255,255,255,0.15)", color:"#fff", border:"1px solid rgba(255,255,255,0.3)", borderRadius:8, fontWeight:700, cursor:"pointer", fontSize:13 }}>🔄 Actualiser</button>
                    <button onClick={() => setCarouselForm({ ...CAROUSEL_BLANK, ordre: carouselSlides.length })}
                      disabled={!peut("configuration","create")} title={!peut("configuration","create")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                      style={{ padding:"9px 18px", background:"#fff", color:BET_COLOR, border:"none", borderRadius:8, fontWeight:800, cursor:"pointer", fontSize:13, opacity:peut("configuration","create")?1:0.5 }}>
                      {peut("configuration","create")?"➕ Ajouter une slide":"🔒 Ajouter une slide"}
                    </button>
                  </div>
                </div>

                <div style={{ padding:"28px" }}>
                  {/* Formulaire add/edit */}
                  {/* ══════════════════════════════════════════════════════════════════
                      LE FORMULAIRE S'OUVRE EN MODALE, ET PLUS EN PLACE.

                      Il s'affichait au-dessus de la liste des slides : cliquer « Modifier »
                      sur la huitième poussait toute la liste vers le bas, et le formulaire
                      apparaissait hors champ — on croyait que le bouton n'avait rien fait,
                      puis on remontait chercher. La modale met l'édition là où le regard est
                      déjà, et rend évident qu'on est en train de modifier UNE slide.

                      Le contenu du formulaire n'a pas bougé d'une ligne : seul son
                      habillage change, ce qui évite de ré-introduire des défauts dans deux
                      cents lignes de champs qui fonctionnent.
                      ══════════════════════════════════════════════════════════════════ */}
                  {carouselForm && (
                    <ModaleAnimee ouvert
                      onClicVoile={() => { if (!carouselSaving && !carouselUploading) setCarouselForm(null); }}
                      style={{ position:"fixed", inset:0, zIndex:9400, background:"rgba(15,23,42,.55)",
                        display:"flex", alignItems:"flex-start", justifyContent:"center",
                        padding:"4vh 16px", overflowY:"auto" }}
                      panneauStyle={{ background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:14,
                        padding:"22px 24px", width:"min(760px, 100%)", boxShadow:"0 24px 64px rgba(15,23,42,.3)" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:4 }}>
                        <h3 style={{ margin:0, fontSize:"1rem", fontWeight:800, color:"#0f172a", flex:1 }}>
                          {isEdit ? "✏️ Modifier la slide" : "➕ Nouvelle slide"}
                        </h3>
                        {/* La croix ne ferme pas pendant un envoi : interrompre un téléversement
                            laisserait une image orpheline sur Cloudinary et un formulaire perdu. */}
                        <button onClick={() => setCarouselForm(null)} disabled={carouselSaving || carouselUploading}
                          title={carouselUploading ? "Téléversement en cours…" : "Fermer"}
                          style={{ background:"none", border:"none", fontSize:20, lineHeight:1,
                            cursor:(carouselSaving||carouselUploading)?"not-allowed":"pointer",
                            color:"#6b7280", opacity:(carouselSaving||carouselUploading)?0.4:1 }}>✕</button>
                      </div>

                      {/* Fiche dimensions recommandées */}
                      <div style={{ display:"flex", gap:10, marginBottom:18, flexWrap:"wrap" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:7, background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:8, padding:"6px 12px", fontSize:12 }}>
                          <span style={{ fontSize:15 }}>🖼️</span>
                          <div>
                            <span style={{ fontWeight:700, color:"#1e40af" }}>Image recommandée</span>
                            <span style={{ color:"#3b82f6", marginLeft:6 }}>1600 × 900 px · 16:9 · JPG/PNG/WebP · max 10 Mo</span>
                          </div>
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:7, background:"#fefce8", border:"1px solid #fde68a", borderRadius:8, padding:"6px 12px", fontSize:12 }}>
                          <span style={{ fontSize:15 }}>🎬</span>
                          <div>
                            <span style={{ fontWeight:700, color:"#92400e" }}>Vidéo</span>
                            <span style={{ color:"#b45309", marginLeft:6 }}>MP4/MOV/WebM · max {maxMoPour("video","video/mp4")} Mo  -  ou lien YouTube/Vimeo (embed)</span>
                          </div>
                        </div>
                      </div>

                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                        {/* Type */}
                        <div>
                          <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Type *</label>
                          <select value={carouselForm.type} onChange={e=>setCarouselForm(p=>({...p,type:e.target.value,url:""}))}
                            style={{ width:"100%", padding:"9px 12px", border:"1.5px solid #d1d5db", borderRadius:8, fontSize:13, background:"#fff" }}>
                            <option value="image">🖼️ Image</option>
                            <option value="video">🎬 Vidéo</option>
                          </select>
                        </div>
                        {/* Ordre */}
                        <div>
                          <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Ordre d'affichage</label>
                          <input type="number" min="0" value={carouselForm.ordre}
                            onChange={e=>setCarouselForm(p=>({...p,ordre:Number(e.target.value)}))}
                            style={{ width:"100%", padding:"9px 12px", border:"1.5px solid #d1d5db", borderRadius:8, fontSize:13, boxSizing:"border-box" }} />
                        </div>

                        {/* Zone upload depuis PC */}
                        <div style={{ gridColumn:"1/-1" }}>
                          <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:8 }}>
                            {carouselForm.type === "video" ? "📁 Uploader une vidéo depuis le PC" : "📁 Uploader une image depuis le PC"}
                          </label>
                          <input
                            ref={carouselFileRef}
                            type="file"
                            accept={carouselForm.type === "video" ? "video/mp4,video/mov,video/avi,video/mkv,video/webm" : "image/jpeg,image/png,image/webp,image/gif"}
                            style={{ display:"none" }}
                            onChange={e => { if (e.target.files?.[0]) uploadCarouselFile(e.target.files[0]); e.target.value=""; }}
                          />
                          <div
                            onClick={() => !carouselUploading && carouselFileRef.current?.click()}
                            onDragOver={e => { e.preventDefault(); setCarouselDragOver(true); }}
                            onDragLeave={() => setCarouselDragOver(false)}
                            onDrop={e => { e.preventDefault(); setCarouselDragOver(false); if (!carouselUploading && e.dataTransfer.files?.[0]) uploadCarouselFile(e.dataTransfer.files[0]); }}
                            style={{
                              border:`2px dashed ${carouselDragOver ? BET_COLOR : "#d1d5db"}`,
                              borderRadius:10, padding:"22px 16px", textAlign:"center",
                              cursor: carouselUploading ? "not-allowed" : "pointer",
                              background: carouselDragOver ? "#fff1f2" : "#fff",
                              transition:"all .2s",
                            }}>
                            {carouselUploading ? (
                              <div>
                                <div style={{ fontSize:13, fontWeight:700, color:BET_COLOR, marginBottom:10 }}>⏳ Upload en cours… {carouselUploadPct}%</div>
                                <div style={{ height:8, background:"#f1f5f9", borderRadius:4, overflow:"hidden" }}>
                                  <div style={{ height:"100%", width:`${carouselUploadPct}%`, background:BET_COLOR, borderRadius:4, transition:"width .2s" }} />
                                </div>
                              </div>
                            ) : (
                              <>
                                <div style={{ fontSize:28, marginBottom:6 }}>{carouselForm.type === "video" ? "🎬" : "🖼️"}</div>
                                <div style={{ fontSize:13, fontWeight:700, color:"#374151" }}>
                                  Glisser-déposer ou <span style={{ color:BET_COLOR, textDecoration:"underline" }}>cliquer pour choisir</span>
                                </div>
                                <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>
                                  {carouselForm.type === "video"
                                    ? `MP4 · MOV · WebM · max ${maxMoPour("video","video/mp4")} Mo`
                                    : "JPG · PNG · WebP · 1600×900 px recommandé · max 10 Mo"}
                                </div>
                              </>
                            )}
                          </div>

                          {/* Séparateur OU + champ URL */}
                          <div style={{ display:"flex", alignItems:"center", gap:10, margin:"12px 0 8px" }}>
                            <div style={{ flex:1, height:1, background:"#e5e7eb" }} />
                            <span style={{ fontSize:11, color:"#9ca3af", fontWeight:700, whiteSpace:"nowrap" }}>ou coller une URL</span>
                            <div style={{ flex:1, height:1, background:"#e5e7eb" }} />
                          </div>
                          <input type="url"
                            placeholder={carouselForm.type === "video" ? "https://youtube.com/embed/xxx  ou  https://...mp4" : "https://..."}
                            value={carouselForm.url}
                            onChange={e=>setCarouselForm(p=>({...p,url:e.target.value}))}
                            style={{ width:"100%", padding:"9px 12px", border:"1.5px solid #d1d5db", borderRadius:8, fontSize:13, boxSizing:"border-box" }} />
                        </div>

                        {/* Titre */}
                        <div>
                          <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Titre superposé</label>
                          <input type="text" placeholder="Votre anglais, votre avenir."
                            value={carouselForm.titre}
                            onChange={e=>setCarouselForm(p=>({...p,titre:e.target.value}))}
                            style={{ width:"100%", padding:"9px 12px", border:"1.5px solid #d1d5db", borderRadius:8, fontSize:13, boxSizing:"border-box" }} />
                        </div>
                        {/* Description */}
                        <div>
                          <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Description courte</label>
                          <input type="text" placeholder="Sous-titre ou accroche…"
                            value={carouselForm.description}
                            onChange={e=>setCarouselForm(p=>({...p,description:e.target.value}))}
                            style={{ width:"100%", padding:"9px 12px", border:"1.5px solid #d1d5db", borderRadius:8, fontSize:13, boxSizing:"border-box" }} />
                        </div>
                        {/* Lien CTA */}
                        <div>
                          <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Lien CTA (optionnel)</label>
                          {/* Même formulaire que dans le SuperAdmin : les deux doivent dire
                              la même chose, sinon la consigne dépend de l'écran par lequel
                              on passe. Voir la note détaillée là-bas. */}
                          <input type="text" placeholder="wa.me/2250779457763, https://… ou /parcours"
                            value={carouselForm.link_url}
                            onChange={e=>setCarouselForm(p=>({...p,link_url:e.target.value}))}
                            style={{ width:"100%", padding:"9px 12px", border:"1.5px solid #d1d5db", borderRadius:8, fontSize:13, boxSizing:"border-box" }} />
                          <div style={{ fontSize:11, color:"#6b7280", marginTop:4, lineHeight:1.5 }}>
                            Un lien WhatsApp (<code style={{ background:"#f1f5f9", padding:"1px 4px", borderRadius:3 }}>wa.me/225…</code>) ou un site
                            s'ouvre dans un nouvel onglet ; un chemin commençant par
                            <code style={{ background:"#f1f5f9", padding:"1px 4px", borderRadius:3, marginLeft:3 }}>/</code> reste sur le site.
                            <br/>Pour WhatsApp, le numéro s'écrit en entier avec l'indicatif
                            <strong> et le préfixe opérateur</strong> : <code style={{ background:"#f1f5f9", padding:"1px 4px", borderRadius:3 }}>225</code>
                            {" "}+ <code style={{ background:"#f1f5f9", padding:"1px 4px", borderRadius:3 }}>0779457763</code>, pas les 8 derniers chiffres.
                          </div>
                        </div>
                        {/* Label CTA */}
                        <div>
                          <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>Texte du bouton CTA</label>
                          <input type="text" placeholder="Découvrir →"
                            value={carouselForm.link_label}
                            onChange={e=>setCarouselForm(p=>({...p,link_label:e.target.value}))}
                            style={{ width:"100%", padding:"9px 12px", border:"1.5px solid #d1d5db", borderRadius:8, fontSize:13, boxSizing:"border-box" }} />
                        </div>
                        {/* Durée d'affichage - images seulement : une vidéo dure ce
                            qu'elle dure, et c'est sa fin qui fait avancer le carrousel. */}
                        {carouselForm.type === "image" && (
                          <div>
                            <label style={{ display:"block", fontSize:12, fontWeight:600, color:"#374151", marginBottom:5 }}>
                              Durée d'affichage
                            </label>
                            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                              <input type="number" min={2} max={30} step={1}
                                value={carouselForm.duree ?? 5}
                                onChange={e=>setCarouselForm(p=>({...p, duree: e.target.value === "" ? "" : Number(e.target.value)}))}
                                style={{ width:90, padding:"9px 12px", border:"1.5px solid #d1d5db", borderRadius:8, fontSize:13, boxSizing:"border-box" }} />
                              <span style={{ fontSize:12, color:"#6b7280" }}>secondes (de 2 à 30)</span>
                            </div>
                          </div>
                        )}
                        {/* Visible */}
                        <div style={{ display:"flex", alignItems:"center", gap:10, gridColumn:"1/-1" }}>
                          <ToggleSwitch on={carouselForm.actif} onChange={v=>setCarouselForm(p=>({...p,actif:v}))} />
                          <span style={{ fontSize:13, fontWeight:600, color:"#374151" }}>Visible sur le site</span>
                        </div>
                      </div>

                      {/* Aperçu après upload ou URL */}
                      {carouselForm.type === "image" && carouselForm.url && (
                        <div style={{ marginTop:14 }}>
                          <p style={{ fontSize:11, color:"#9ca3af", margin:"0 0 6px", fontWeight:600 }}>APERÇU</p>
                          <div style={{ position:"relative", display:"inline-block", width:"100%" }}>
                            <img src={carouselForm.url} alt="preview"
                              style={{ width:"100%", maxHeight:200, objectFit:"cover", borderRadius:8, border:"1px solid #e5e7eb", display:"block" }}
                              onError={e=>{e.target.style.display="none";}} />
                            {/* ratio indicator */}
                            <span style={{ position:"absolute", top:8, right:8, background:"rgba(0,0,0,.55)", color:"#fff", fontSize:10, fontWeight:700, borderRadius:5, padding:"2px 7px" }}>16:9 idéal</span>
                          </div>
                        </div>
                      )}
                      {carouselForm.type === "video" && carouselForm.url && (
                        <div style={{ marginTop:14, background:"#0f172a", borderRadius:8, padding:"10px 14px", fontSize:12, color:"rgba(255,255,255,.6)", display:"flex", alignItems:"center", gap:8 }}>
                          <span style={{ fontSize:20 }}>🎬</span>
                          <span style={{ wordBreak:"break-all", color:"#fff" }}>{carouselForm.url.length > 70 ? carouselForm.url.slice(0,70)+"…" : carouselForm.url}</span>
                        </div>
                      )}

                      <div style={{ display:"flex", gap:10, marginTop:18 }}>
                        <button onClick={saveCarousel} disabled={carouselSaving || carouselUploading || !peut("configuration", isEdit?"update":"create")}
                          title={!peut("configuration", isEdit?"update":"create")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                          style={{ padding:"10px 22px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:9, cursor:"pointer", fontWeight:700, fontSize:13, opacity:(carouselSaving||carouselUploading||!peut("configuration", isEdit?"update":"create"))?0.6:1 }}>
                          {carouselSaving ? "Sauvegarde…" : !peut("configuration", isEdit?"update":"create") ? "🔒 Enregistrer" : isEdit ? "💾 Mettre à jour" : "✅ Ajouter la slide"}
                        </button>
                        <button onClick={() => setCarouselForm(null)} disabled={carouselUploading}
                          style={{ padding:"10px 18px", background:"#f1f5f9", color:"#374151", border:"none", borderRadius:9, cursor:"pointer", fontWeight:600, fontSize:13 }}>
                          Annuler
                        </button>
                      </div>
                    </ModaleAnimee>
                  )}

                  {/* Liste des slides */}
                  {carouselLoading && <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af", fontSize:14 }}>⏳ Chargement…</div>}
                  {!carouselLoading && carouselSlides.length === 0 && (
                    <div style={{ textAlign:"center", padding:"52px 0" }}>
                      <div style={{ fontSize:42, marginBottom:12 }}>🖼️</div>
                      <div style={{ fontWeight:700, color:"#0f172a", marginBottom:6 }}>Aucune slide pour le moment</div>
                      <p style={{ color:"#9ca3af", fontSize:13 }}>Ajoutez votre première slide pour animer le carrousel de la page d'accueil.</p>
                    </div>
                  )}
                  {!carouselLoading && carouselSlides.length > 0 && (
                    <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                      {carouselSlides.map((slide, slidePos) => (
                        <div key={slide.id} style={{ display:"flex", gap:14, alignItems:"flex-start", padding:"14px 16px", background: slide.actif ? "#f8fafc" : "#f1f5f9", border:`1.5px solid ${slide.actif?"#e2e8f0":"#d1d5db"}`, borderRadius:10 }}>
                          {/* Vignette */}
                          <div style={{ width:110, height:70, borderRadius:8, overflow:"hidden", flexShrink:0, background:"#0f172a", display:"flex", alignItems:"center", justifyContent:"center" }}>
                            {slide.type === "image" ? (
                              <img src={slide.url} alt={slide.titre || "slide"}
                                style={{ width:"100%", height:"100%", objectFit:"cover" }}
                                onError={e=>{e.target.style.display="none";e.target.parentNode.innerHTML='<span style="font-size:28px;opacity:.5">🖼️</span>';}} />
                            ) : (
                              <span style={{ fontSize:28, opacity:.7 }}>🎬</span>
                            )}
                          </div>
                          {/* Infos */}
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                              <span style={{ padding:"2px 9px", borderRadius:20, fontSize:11, fontWeight:700, background: slide.type==="video"?"#fef3c7":"#ede9fe", color: slide.type==="video"?"#92400e":"#6d28d9" }}>
                                {slide.type === "video" ? "🎬 Vidéo" : "🖼️ Image"}
                              </span>
                              <span style={{ padding:"2px 9px", borderRadius:20, fontSize:11, fontWeight:800, background:"#f1f5f9", color:"#475569" }}>#{slidePos + 1}</span>
                              {!slide.actif && <span style={{ padding:"2px 8px", borderRadius:20, fontSize:11, background:"#f1f5f9", color:"#9ca3af", fontWeight:600 }}>Masqué</span>}
                            </div>
                            {slide.titre && <p style={{ margin:"0 0 3px", fontSize:13, fontWeight:700, color:"#0f172a" }}>{slide.titre}</p>}
                            {slide.description && <p style={{ margin:"0 0 3px", fontSize:12, color:"#475569", overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis" }}>{slide.description}</p>}
                            {slide.link_url && (
                              <p style={{ margin:0, fontSize:11, color:"#6366f1" }}>
                                🔗 {slide.link_label || slide.link_url}
                              </p>
                            )}
                            <p style={{ margin:"4px 0 0", fontSize:11, color:"#94a3b8", overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis" }}>{slide.url}</p>
                          </div>
                          {/* Actions */}
                          <div style={{ display:"flex", flexDirection:"column", gap:6, flexShrink:0, alignItems:"flex-end" }}>
                            <ToggleSwitch on={slide.actif} onChange={() => peut("configuration","update") && toggleCarouselActif(slide)} />
                            {/* Réorganisation ↑↓ */}
                            <div style={{ display:"flex", gap:3 }}>
                              <button
                                onClick={() => moveCarouselSlide(slide.id, "up")}
                                disabled={carouselSlides.indexOf(slide) === 0 || !peut("configuration","update")}
                                title="Monter"
                                style={{ padding:"5px 9px", background:"#f8fafc", color:"#374151", border:"1px solid #e2e8f0", borderRadius:6, cursor:"pointer", fontSize:12, opacity: (carouselSlides.indexOf(slide)===0||!peut("configuration","update")) ? 0.35 : 1 }}>↑</button>
                              <button
                                onClick={() => moveCarouselSlide(slide.id, "down")}
                                disabled={carouselSlides.indexOf(slide) === carouselSlides.length - 1 || !peut("configuration","update")}
                                title="Descendre"
                                style={{ padding:"5px 9px", background:"#f8fafc", color:"#374151", border:"1px solid #e2e8f0", borderRadius:6, cursor:"pointer", fontSize:12, opacity: (carouselSlides.indexOf(slide)===carouselSlides.length-1||!peut("configuration","update")) ? 0.35 : 1 }}>↓</button>
                            </div>
                            <div style={{ display:"flex", gap:5 }}>
                              <button onClick={() => setCarouselForm({ ...slide })}
                                disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                                style={{ padding:"6px 11px", background:BET_LIGHT, color:BET_COLOR, border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12, opacity:peut("configuration","update")?1:0.5 }}>✏️</button>
                              <button onClick={() => deleteCarousel(slide.id)}
                                disabled={!peut("configuration","delete")} title={!peut("configuration","delete")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                                style={{ padding:"6px 11px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12, opacity:peut("configuration","delete")?1:0.5 }}>🗑️</button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {carouselSlides.length > 0 && (
                    <p style={{ textAlign:"right", fontSize:12, color:"#94a3b8", marginTop:16 }}>
                      {carouselSlides.filter(s=>s.actif).length} slide{carouselSlides.filter(s=>s.actif).length!==1?"s":""} active{carouselSlides.filter(s=>s.actif).length!==1?"s":""} / {carouselSlides.length} total
                    </p>
                  )}
                </div>
              </div>
            );
          })()}

          {platformSubTab === "faq" && (() => {
            const faqCats = ["Tous", ...FAQ_CATEGORIES];
            const filtered = faqItems.filter(item => {
              const matchCat = faqCatFilter === "Tous" || item.categorie === faqCatFilter;
              const q = faqSearch.toLowerCase();
              const matchSearch = !q || item.question.toLowerCase().includes(q) || item.reponse.toLowerCase().includes(q) || item.categorie.toLowerCase().includes(q);
              return matchCat && matchSearch;
            });
            return (
              <div style={{ background:"#fff", borderRadius:"0 12px 12px 12px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", overflow:"hidden" }}>
                {/* Header */}
                <div style={{ background: BET_GRADIENT, padding:"24px 28px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div>
                    <h2 style={{ color:"#fff", margin:0, fontSize:"1.3rem", fontWeight:800 }}>❓ Gestion FAQ</h2>
                    <p style={{ color:"rgba(255,255,255,0.7)", margin:"4px 0 0", fontSize:".85rem" }}>
                      {faqItems.length} question{faqItems.length !== 1 ? "s" : ""} · {faqItems.filter(f => f.actif).length} visible{faqItems.filter(f => f.actif).length !== 1 ? "s" : ""} sur le site
                    </p>
                  </div>
                  <div style={{ display:"flex", gap:8 }}>
                    <button onClick={fetchFaq} style={{ padding:"9px 16px", background:"rgba(255,255,255,0.15)", color:"#fff", border:"1px solid rgba(255,255,255,0.3)", borderRadius:8, fontWeight:700, cursor:"pointer", fontSize:13 }}>🔄 Actualiser</button>
                    <button onClick={() => setFaqForm({ question:"", reponse:"", categorie:"Général", ordre:"", actif:true })}
                      disabled={!peut("configuration","create")} title={!peut("configuration","create")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                      style={{ padding:"9px 18px", background:"#fff", color:BET_COLOR, border:"none", borderRadius:8, cursor:"pointer", fontWeight:800, fontSize:13, opacity:peut("configuration","create")?1:0.5 }}>
                      {peut("configuration","create")?"➕ Nouvelle FAQ":"🔒 Nouvelle FAQ"}
                    </button>
                  </div>
                </div>

                <div style={{ padding:24 }}>
                  {/* Formulaire création / édition */}
                  {faqForm && (
                    <div style={{ background:"#f8fafc", border:`1.5px solid ${BET_COLOR}44`, borderRadius:14, padding:22, marginBottom:24 }}>
                      <h4 style={{ margin:"0 0 16px", fontSize:14, fontWeight:800, color:"#0f172a" }}>
                        {faqForm.id ? "✏️ Modifier la FAQ" : "➕ Nouvelle question"}
                      </h4>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
                        <div style={{ gridColumn:"1 / -1" }}>
                          <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Question *</label>
                          <input
                            value={faqForm.question}
                            onChange={e => setFaqForm(p => ({ ...p, question: e.target.value }))}
                            placeholder="Quels types de cours proposez-vous ?"
                            style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e2e8f0", fontSize:13, boxSizing:"border-box" }}
                          />
                        </div>
                        <div style={{ gridColumn:"1 / -1" }}>
                          <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Réponse *</label>
                          <textarea
                            value={faqForm.reponse}
                            onChange={e => setFaqForm(p => ({ ...p, reponse: e.target.value }))}
                            placeholder="Rédigez une réponse claire et complète…"
                            rows={4}
                            style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e2e8f0", fontSize:13, boxSizing:"border-box", resize:"vertical", fontFamily:"inherit" }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Catégorie</label>
                          <select
                            value={faqForm.categorie}
                            onChange={e => setFaqForm(p => ({ ...p, categorie: e.target.value }))}
                            style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e2e8f0", fontSize:13, boxSizing:"border-box", background:"#fff" }}
                          >
                            {FAQ_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Ordre d'affichage</label>
                          <input
                            type="number"
                            value={faqForm.ordre}
                            onChange={e => setFaqForm(p => ({ ...p, ordre: e.target.value }))}
                            placeholder="0"
                            style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:"1.5px solid #e2e8f0", fontSize:13, boxSizing:"border-box" }}
                          />
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:10, paddingTop:18 }}>
                          <ToggleSwitch on={faqForm.actif !== false} onChange={v => setFaqForm(p => ({ ...p, actif: v }))} color={BET_COLOR} />
                          <span style={{ fontSize:13, color:"#374151" }}>Visible sur le site</span>
                        </div>
                      </div>
                      <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
                        <button onClick={() => setFaqForm(null)} style={{ padding:"9px 18px", background:"#f1f5f9", color:"#374151", border:"none", borderRadius:8, cursor:"pointer", fontWeight:600, fontSize:13 }}>Annuler</button>
                        <button
                          onClick={() => saveFaq(faqForm)}
                          disabled={!faqForm.question?.trim() || !faqForm.reponse?.trim() || !!faqSavingId || !peut("configuration", faqForm.id?"update":"create")}
                          title={!peut("configuration", faqForm.id?"update":"create")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                          style={{ padding:"9px 20px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:13, opacity:(!faqForm.question?.trim() || !faqForm.reponse?.trim() || !!faqSavingId || !peut("configuration", faqForm.id?"update":"create")) ? 0.6 : 1 }}
                        >
                          {faqSavingId ? "⏳ Enregistrement…" : !peut("configuration", faqForm.id?"update":"create") ? "🔒 Enregistrer" : "💾 Enregistrer"}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Filtres */}
                  <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
                    <input
                      value={faqSearch}
                      onChange={e => setFaqSearch(e.target.value)}
                      placeholder="🔍 Rechercher question, réponse…"
                      style={{ flex:1, minWidth:220, padding:"8px 12px", border:"1.5px solid #e5e7eb", borderRadius:8, fontSize:13, boxSizing:"border-box" }}
                    />
                    <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                      {faqCats.map(c => (
                        <button key={c} onClick={() => setFaqCatFilter(c)}
                          style={{ padding:"6px 12px", borderRadius:20, border:"1.5px solid", borderColor: faqCatFilter === c ? BET_COLOR : "#e2e8f0", background: faqCatFilter === c ? BET_COLOR : "#fff", color: faqCatFilter === c ? "#fff" : "#475569", fontWeight:600, fontSize:11, cursor:"pointer", whiteSpace:"nowrap" }}>
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Liste */}
                  {faqLoading ? (
                    <div style={{ textAlign:"center", padding:48, color:"#9ca3af" }}>⏳ Chargement…</div>
                  ) : filtered.length === 0 ? (
                    <div style={{ textAlign:"center", padding:48, color:"#9ca3af", fontSize:13 }}>
                      {faqItems.length === 0 ? "Aucune FAQ. Créez-en une ci-dessus." : "Aucun résultat pour ces filtres."}
                    </div>
                  ) : (
                    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                      {filtered.map(item => (
                        <div key={item.id} style={{ background:"#fff", borderRadius:10, border:`1.5px solid ${item.actif ? "#d1fae5" : "#e5e7eb"}`, padding:"14px 18px", display:"flex", gap:14, alignItems:"flex-start", boxShadow:"0 1px 3px rgba(0,0,0,0.04)" }}>
                          {/* Toggle actif */}
                          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6, paddingTop:2, minWidth:32 }}>
                            <ToggleSwitch on={item.actif} onChange={() => peut("configuration","update") && toggleFaqActif(item)} color={peut("configuration","update")?BET_COLOR:"#cbd5e1"} />
                            <span style={{ fontSize:10, color:"#9ca3af", fontWeight:700 }}>#{item.ordre}</span>
                          </div>
                          {/* Contenu */}
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                              <span style={{ padding:"2px 10px", borderRadius:20, fontSize:11, fontWeight:700, background:BET_LIGHT, color:BET_COLOR }}>{item.categorie}</span>
                              {!item.actif && <span style={{ padding:"2px 8px", borderRadius:20, fontSize:11, background:"#f1f5f9", color:"#9ca3af", fontWeight:600 }}>Masqué</span>}
                            </div>
                            <p style={{ margin:"0 0 6px", fontSize:13, fontWeight:700, color:"#0f172a", lineHeight:1.4 }}>❓ {item.question}</p>
                            <p style={{ margin:0, fontSize:12, color:"#475569", lineHeight:1.6, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>{item.reponse}</p>
                          </div>
                          {/* Actions */}
                          <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                            <button onClick={() => setFaqForm({ ...item })}
                              disabled={!peut("configuration","update")} title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                              style={{ padding:"6px 11px", background:BET_LIGHT, color:BET_COLOR, border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12, opacity:peut("configuration","update")?1:0.5 }}>✏️</button>
                            <button onClick={() => deleteFaq(item.id)}
                              disabled={!peut("configuration","delete")} title={!peut("configuration","delete")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                              style={{ padding:"6px 11px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:7, cursor:"pointer", fontWeight:700, fontSize:12, opacity:peut("configuration","delete")?1:0.5 }}>🗑️</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {filtered.length > 0 && (
                    <p style={{ textAlign:"right", fontSize:12, color:"#94a3b8", marginTop:14 }}>
                      {filtered.length} / {faqItems.length} question{faqItems.length > 1 ? "s" : ""}
                    </p>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ── Sous-onglet : Parcours Entreprise ── */}
          {platformSubTab === "parcours_entreprise" && (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
                <div>
                  <h3 style={{ margin:0, fontSize:16, fontWeight:800, color:"#0f172a" }}>🏢 Configuration Parcours Entreprise</h3>
                  <p style={{ margin:"4px 0 0", fontSize:12, color:"#9ca3af" }}>Options affichées dans le modal "Je suis une entreprise"</p>
                  {peDbState === null && !peLoading && (
                    <span style={{ display:"inline-block", marginTop:6, fontSize:11, fontWeight:700, color:"#b45309", background:"#fef3c7", padding:"2px 10px", borderRadius:20 }}>
                      ⚠️ Pas encore en BDD - cliquez "Enregistrer" pour initialiser
                    </span>
                  )}
                  {peDbState !== null && !peLoading && (
                    <span style={{ display:"inline-block", marginTop:6, fontSize:11, fontWeight:600, color:"#16a34a", background:"#dcfce7", padding:"2px 10px", borderRadius:20 }}>
                      ✓ Synchronisé avec Supabase
                    </span>
                  )}
                </div>
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <button onClick={loadPeConfig} disabled={peLoading} title="Recharger depuis Supabase" style={{ padding:"9px 14px", background:"#f1f5f9", border:"1px solid #e2e8f0", borderRadius:9, cursor:"pointer", fontSize:13, color:"#475569", fontWeight:600 }}>
                    {peLoading ? "⏳" : "🔄"} Recharger
                  </button>
                  <button onClick={savePeConfig} disabled={peSaving || peLoading || !peut("configuration","update")}
                    title={!peut("configuration","update")?"🔒 Vous n'avez pas la permission pour cette action":undefined}
                    style={{ padding:"10px 22px", background: peSaveOk ? "#16a34a" : peSaveErr ? "#dc2626" : BET_COLOR, color:"#fff", border:"none", borderRadius:10, cursor:"pointer", fontWeight:800, fontSize:13, display:"flex", alignItems:"center", gap:8, transition:"background .3s", opacity:peut("configuration","update")?1:0.5 }}>
                    {peSaving ? "⏳ Enregistrement…" : !peut("configuration","update") ? "🔒 Enregistrer dans la BDD" : peSaveOk ? "✅ Enregistré !" : peSaveErr ? "❌ Erreur" : "💾 Enregistrer dans la BDD"}
                  </button>
                </div>
              </div>
              {peSaveErr && (
                <div style={{ marginBottom:16, padding:"12px 16px", background:"#fee2e2", border:"1px solid #fca5a5", borderRadius:8, fontSize:12, color:"#b91c1c", fontWeight:600 }}>
                  ⚠️ {peSaveErr}
                </div>
              )}
              {peLoading && (
                <div style={{ marginBottom:16, padding:"10px 16px", background:"#f0f9ff", border:"1px solid #bae6fd", borderRadius:8, fontSize:12, color:"#0369a1" }}>
                  ⏳ Chargement depuis Supabase…
                </div>
              )}
              {/* ── Étape 2 : Niveau & Objectifs ── */}
              <div style={{ display:"flex", alignItems:"center", gap:12, margin:"4px 0 18px" }}>
                <div style={{ flex:1, height:1, background:"#e5e7eb" }} />
                <span style={{ fontSize:12, fontWeight:700, color:"#6b7280", whiteSpace:"nowrap" }}>🎯 ÉTAPE 2 - Niveau &amp; Objectifs</span>
                <div style={{ flex:1, height:1, background:"#e5e7eb" }} />
              </div>
              <ParcoursSectionCard title="Niveaux CECRL"          sectionIcon="📊" sectionKey="niv" items={peNiveaux}   setItems={setPeNiveaux} />
              <ParcoursSectionCard title="Objectifs de formation"  sectionIcon="🎯" sectionKey="obj" items={peObjectifs} setItems={setPeObjectifs} />

              {/* ── Étape 3 : Format, Rythme & Groupe ── */}
              <div style={{ display:"flex", alignItems:"center", gap:12, margin:"4px 0 18px" }}>
                <div style={{ flex:1, height:1, background:"#e5e7eb" }} />
                <span style={{ fontSize:12, fontWeight:700, color:"#6b7280", whiteSpace:"nowrap" }}>📅 ÉTAPE 3 - Format, Rythme &amp; Groupe</span>
                <div style={{ flex:1, height:1, background:"#e5e7eb" }} />
              </div>
              <ParcoursSectionCard title="Formats de cours"       sectionIcon="🎓" sectionKey="fmt" items={peFormats}  setItems={setPeFormats} />
              <ParcoursSectionCard title="Rythme d'apprentissage" sectionIcon="📅" sectionKey="ryt" items={peRythmes}  setItems={setPeRythmes} />
              <ParcoursSectionCard title="Type de groupe"         sectionIcon="👥" sectionKey="grp" items={peGroupes}  setItems={setPeGroupes} />

              <div style={{ background:"#f0f9ff", border:"1px solid #bae6fd", borderRadius:10, padding:"12px 16px", fontSize:12, color:"#0369a1" }}>
                💡 Cliquez sur <strong>"Enregistrer dans la BDD"</strong> après vos modifications pour les publier sur le site. Les options désactivées (Masqué) n'apparaissent plus dans le formulaire.
              </div>
            </div>
          )}

          {platformSubTab === "test_niveau" && <TestsNiveauTab peut={peut} />}

              </div>
            )}

            {/* ================= GESTION DES PERMISSIONS (identique AdminDashboard mais avec rôle viewer) ================= */}
            {/* ================= ONGLET AUDIT GLOBAL ================= */}
            {activeTab === "audit" && (() => {
              const STATUT_META = {
                success: { bg:"#dcfce7", color:"#166534", dot:"#22c55e", label:"✅ Succès" },
                warning: { bg:"#fef9c3", color:"#854d0e", dot:"#f59e0b", label:"⚠️ Attention" },
                danger:  { bg:"#fee2e2", color:"#991b1b", dot:"#ef4444", label:"🚨 Alerte" },
                info:    { bg:"#e0f2fe", color:"#0369a1", dot:"#0891b2", label:"ℹ️ Info" },
              };
              const MODULE_ICONS = { auth:"🔐", users:"👤", admin:"🔧", paiements:"💳", boutique:"🛍️", inscriptions:"📋", leads:"📞", devis:"📄", blog:"📝", temoignages:"⭐", sondage:"📊", coachs:"🎓", centres:"📍", system:"⚙️", cinetpay:"💳", parcours:"🎯" };
              const CENTRES_LIST = ["angre","bouake","plateaux","yopougon","koumassi","abatta","cocody"];
              const totalPages = Math.max(1, Math.ceil(auditTotal / AUDIT_LIMIT));

              const exportCSV = () => {
                exporterExcelStyle({
                  nomFichier: `audit_${new Date().toISOString().split("T")[0]}`,
                  titre: "📜 Journal d'audit global",
                  sousTitre: `${auditLogs.length} entrée${auditLogs.length>1?'s':''} · Généré le ${new Date().toLocaleDateString('fr-FR')}`,
                  couleurEntete: '0F172A',
                  colonnes: [
                    { label: 'ID', width: 10 }, { label: 'Acteur', width: 20, bold: true },
                    { label: 'Email', width: 26 }, { label: 'Rôle', width: 14 },
                    { label: 'Action', width: 18 }, { label: 'Module', width: 14 },
                    { label: 'Entité', width: 20 }, { label: 'Centre', width: 12 },
                    { label: 'Détail', width: 36 }, { label: 'IP', width: 14 },
                    { label: 'Statut', width: 12 }, { label: 'Date', width: 18 },
                  ],
                  lignes: auditLogs.map(l => [
                    l.id, l.acteur_nom||"", l.acteur_email||"", l.acteur_role||"", l.action_type||"", l.module||"",
                    `${l.entite_type||""}:${l.entite_id||""}`, l.centre||"", l.detail||"", l.ip_address||"", l.statut||"", l.created_at||"",
                  ]),
                });
                toast.success("Export Excel effectué");
              };

              const AuditRow = ({ log }) => {
                const sm = STATUT_META[log.statut] || STATUT_META.info;
                const initiales = (log.acteur_nom||"??").split(" ").map(x=>x[0]||"").join("").toUpperCase().slice(0,2);
                return (
                  <tr
                    onClick={() => setAuditDetailLog(log)}
                    style={{ borderBottom:"1px solid #f1f5f9", background: log.statut==="danger"?"#fff8f8":log.statut==="warning"?"#fffdf0":"#fff", cursor:"pointer", transition:"background .15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#f0f7ff"}
                    onMouseLeave={e => e.currentTarget.style.background = log.statut==="danger"?"#fff8f8":log.statut==="warning"?"#fffdf0":"#fff"}
                  >
                    <td style={{ padding:"10px 8px", width:16 }}>
                      <div style={{ width:8, height:8, borderRadius:"50%", background:sm.dot, boxShadow:`0 0 0 3px ${sm.dot}22`, margin:"auto" }} />
                    </td>
                    <td style={{ padding:"10px 10px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <div style={{ width:30, height:30, borderRadius:"50%", background: log.statut==="danger"?"#fee2e2":log.statut==="warning"?"#fef9c3":"#e0f2fe", color: log.statut==="danger"?"#dc2626":log.statut==="warning"?"#b45309":BET_COLOR, fontWeight:800, fontSize:11, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{initiales}</div>
                        <div>
                          <div style={{ fontWeight:700, fontSize:12, color:"#0f172a" }}>{log.acteur_nom||"Système"}</div>
                          <div style={{ fontSize:10, color:"#9ca3af" }}>{log.acteur_role||"-"}{log.acteur_email ? ` · ${log.acteur_email}` : ""}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding:"10px 10px" }}>
                      <span style={{ padding:"3px 9px", borderRadius:6, fontSize:10, fontWeight:700, background:sm.bg, color:sm.color, whiteSpace:"nowrap" }}>
                        {log.action_type?.replace(/_/g," ")||"-"}
                      </span>
                    </td>
                    <td style={{ padding:"10px 10px" }}>
                      <span style={{ fontSize:11, color:"#6b7280" }}>{MODULE_ICONS[log.module]||"📦"} {log.module||"-"}</span>
                      {log.entite_type && <div style={{ fontSize:10, color:"#94a3b8" }}>{log.entite_type}{log.entite_id ? ` #${String(log.entite_id).slice(0,8)}` : ""}</div>}
                    </td>
                    <td style={{ padding:"10px 10px", fontSize:12, color:"#374151", maxWidth:280 }}>
                      <span title={log.detail}>{log.detail?.length>80 ? log.detail.slice(0,80)+"…" : log.detail||"-"}</span>
                    </td>
                    <td style={{ padding:"10px 10px" }}>
                      {log.centre && <span style={{ fontSize:10, background:"#f1f5f9", padding:"2px 7px", borderRadius:4, color:"#475569", fontWeight:600 }}>📍 {log.centre}</span>}
                    </td>
                    <td style={{ padding:"10px 10px", fontSize:10, color:"#9ca3af", whiteSpace:"nowrap" }}>
                      {log.created_at ? new Date(log.created_at).toLocaleString("fr-FR",{day:"2-digit",month:"2-digit",year:"2-digit",hour:"2-digit",minute:"2-digit",second:"2-digit"}) : "-"}
                    </td>
                    <td style={{ padding:"10px 10px" }}>
                      <code style={{ fontSize:9, padding:"2px 6px", borderRadius:4, background:"#f3f4f6", color:"#374151" }}>{log.ip_address||"-"}</code>
                    </td>
                    <td style={{ padding:"10px 8px" }}>
                      <span style={{ fontSize:10, color:BET_COLOR, fontWeight:600 }}>Voir →</span>
                    </td>
                  </tr>
                );
              };

              return (
                <div>
                  {/* Header */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
                    <div>
                      <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:"#0f172a" }}>📜 Audit global</h2>
                      <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Traçabilité complète - chaque action, chaque profil, chaque centre</p>
                    </div>
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={() => { fetchAuditLogs(1); fetchAuditStats(); }} style={{ padding:"9px 12px", background:"#f1f5f9", color:"#374151", border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:12 }}>🔄 Actualiser</button>
                      <button onClick={exportCSV} style={{ padding:"9px 16px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:12 }}>⬇️ Export CSV</button>
                      <button disabled={purgingLogs} onClick={async () => { if (purgingLogs) return; if (!(await confirm("Supprimer les logs de +90 jours ?", { titre: "Purger les logs" }))) return; setPurgingLogs(true); try { await fetch(`${API_URL}/api/audit/clear`,{method:"DELETE",headers:authHeaders()}); toast.success("Logs purgés"); fetchAuditLogs(1); fetchAuditStats(); } finally { setPurgingLogs(false); } }} style={{ padding:"9px 12px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:6, cursor:purgingLogs?"not-allowed":"pointer", fontWeight:600, fontSize:12, opacity:purgingLogs?0.6:1 }}>{purgingLogs?"⏳ Purge…":"🗑️ Purger +90j"}</button>
                    </div>
                  </div>

                  {/* KPIs */}
                  {auditStats && (
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:12, marginBottom:20 }}>
                      {[
                        { icon:"📊", label:"Aujourd'hui",   value:auditStats.total_today,              color:"#0891b2" },
                        { icon:"📅", label:"7 derniers j",  value:auditStats.total_7j,                 color:"#8b5cf6" },
                        { icon:"📆", label:"30 derniers j", value:auditStats.total_30j,                color:"#059669" },
                        { icon:"🚨", label:"Alertes (j.)",   value:auditStats.alertes_today,            color:BET_RED   },
                        { icon:"✅", label:"Succès",         value:auditStats.par_statut?.success||0,   color:"#22c55e" },
                        { icon:"⚠️", label:"Avertissements", value:auditStats.par_statut?.warning||0,   color:"#f59e0b" },
                      ].map((k,i) => (
                        <div key={i} style={{ background:"#fff", borderRadius:12, padding:"14px 16px", border:"1px solid #f1f5f9", boxShadow:"0 1px 4px rgba(0,0,0,.04)" }}>
                          <div style={{ fontSize:18, marginBottom:4 }}>{k.icon}</div>
                          <div style={{ fontWeight:900, fontSize:20, color:k.color }}>{k.value??0}</div>
                          <div style={{ fontSize:11, color:"#64748b", fontWeight:600 }}>{k.label}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Sous-vues */}
                  <div style={{ display:"flex", gap:4, marginBottom:20, background:"#f1f5f9", borderRadius:10, padding:4, width:"fit-content" }}>
                    {[
                      { key:"logs",   label:"📋 Journal complet" },
                      { key:"stats",  label:"📊 Par module" },
                      { key:"profil", label:"👤 Par profil" },
                      { key:"centre", label:"📍 Par centre" },
                    ].map(v => (
                      <button key={v.key} onClick={() => setAuditView(v.key)} style={{ padding:"7px 14px", borderRadius:8, border:"none", cursor:"pointer", fontWeight:700, fontSize:12,
                        background: auditView===v.key ? "#fff" : "transparent",
                        color:      auditView===v.key ? BET_COLOR : "#6b7280",
                        boxShadow:  auditView===v.key ? "0 1px 4px rgba(0,0,0,.1)" : "none",
                      }}>{v.label}</button>
                    ))}
                  </div>

                  {/* VUE : JOURNAL COMPLET */}
                  {auditView === "logs" && (
                    <div>
                      {/* ── Filtres - modèle pastilles partagé. Pas de compteur ici : le
                           journal est filtré côté serveur (bouton « Filtrer »), donc le
                           nombre par statut n'est pas connu du client - mieux vaut pas de
                           chiffre qu'un chiffre faux. ── */}
                      <BarreFiltres accent={BET_COLOR} style={{ background:"#f8fafc", border:"1px solid #e5e7eb", borderRadius:10, padding:"14px 16px", marginBottom:16 }}>
                        <RangeePastilles label="Statut :" valeur={auditFilters.statut}
                          onChange={(v) => setAuditFilters(f=>({...f, statut:v}))}
                          options={[
                            { v:"",        l:"Tous" },
                            { v:"success", l:"✅ Succès" },
                            { v:"warning", l:"⚠️ Avertissement" },
                            { v:"danger",  l:"🚨 Alerte" },
                            { v:"info",    l:"ℹ️ Info" },
                          ]} />

                        <RangeeChamps label="Recherche :"
                          resume={`${auditTotal.toLocaleString("fr-FR")} entrée${auditTotal>1?"s":""}`}
                          onReinitialiser={() => { const empty={module:"",action_type:"",statut:"",centre:"",search:"",date_debut:"",date_fin:""}; setAuditFilters(empty); setAuditPage(1); fetchAuditLogs(1,empty); }}>
                          <input placeholder="🔍 Acteur, email, action, détail…" value={auditFilters.search}
                            aria-label="Rechercher dans le journal"
                            onChange={e => setAuditFilters(f => ({...f, search:e.target.value}))}
                            style={{ ...champFiltre, minWidth:220, flex:1 }} />

                          <select value={auditFilters.module} aria-label="Filtrer par module"
                            onChange={e => setAuditFilters(f=>({...f,module:e.target.value}))}
                            style={{ ...champFiltre, cursor:"pointer" }}>
                            <option value="">Tous modules</option>
                            {Object.entries(MODULE_ICONS).map(([k,ico]) => <option key={k} value={k}>{ico} {k}</option>)}
                          </select>

                          <select value={auditFilters.centre} aria-label="Filtrer par centre"
                            onChange={e => setAuditFilters(f=>({...f,centre:e.target.value}))}
                            style={{ ...champFiltre, cursor:"pointer" }}>
                            <option value="">Tous centres</option>
                            {CENTRES_LIST.map(c => <option key={c} value={c}>📍 {c}</option>)}
                          </select>

                          <span style={{ fontSize:11, color:"#64748b" }}>Du</span>
                          <input type="date" value={auditFilters.date_debut} aria-label="Date de début"
                            onChange={e => setAuditFilters(f=>({...f,date_debut:e.target.value}))} style={champFiltre} />
                          <span style={{ fontSize:11, color:"#64748b" }}>au</span>
                          <input type="date" value={auditFilters.date_fin} aria-label="Date de fin"
                            onChange={e => setAuditFilters(f=>({...f,date_fin:e.target.value}))} style={champFiltre} />

                          <button onClick={() => { setAuditPage(1); fetchAuditLogs(1); }}
                            style={{ padding:"6px 16px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:999, cursor:"pointer", fontWeight:700, fontSize:12 }}>Filtrer</button>
                        </RangeeChamps>
                      </BarreFiltres>


                      {auditLoading ? (
                        <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>⏳ Chargement…</div>
                      ) : auditLogs.length === 0 ? (
                        <div style={{ textAlign:"center", padding:60, color:"#9ca3af" }}>
                          <div style={{ fontSize:40, marginBottom:12 }}>📭</div>
                          <div style={{ fontWeight:600, marginBottom:4 }}>Aucun événement trouvé</div>
                          <div style={{ fontSize:12 }}>Ajustez vos filtres ou attendez les premières actions</div>
                        </div>
                      ) : (
                        <div style={{ overflowX:"auto", borderRadius:10, border:"1px solid #e5e7eb" }}>
                          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                            <thead>
                              <tr style={{ background:"#f8fafc" }}>
                                {["","Acteur","Action","Module","Détail","Centre","Date & Heure","IP"].map(h => (
                                  <th key={h} style={{ padding:"10px 10px", textAlign:"left", fontSize:11, fontWeight:700, color:"#64748b", borderBottom:"2px solid #e5e7eb", whiteSpace:"nowrap" }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {auditLogs.map(log => <AuditRow key={log.id} log={log} />)}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {totalPages > 1 && (
                        <div style={{ display:"flex", justifyContent:"center", gap:6, marginTop:16, flexWrap:"wrap" }}>
                          <button onClick={() => { const p=Math.max(1,auditPage-1); setAuditPage(p); fetchAuditLogs(p); }} disabled={auditPage===1}
                            style={{ padding:"6px 12px", borderRadius:6, border:"1px solid #e5e7eb", background:auditPage===1?"#f9fafb":"#fff", cursor:auditPage===1?"not-allowed":"pointer", fontSize:13, color:auditPage===1?"#d1d5db":"#374151" }}>‹</button>
                          {Array.from({length:totalPages},(_,i)=>i+1).filter(n=>n===1||n===totalPages||Math.abs(n-auditPage)<=2).reduce((acc,n,i,arr)=>{if(i>0&&arr[i-1]!==n-1)acc.push("…");acc.push(n);return acc;},[]).map((n,i)=>
                            n==="…"?<span key={`e${i}`} style={{padding:"6px 6px",fontSize:13,color:"#9ca3af"}}>…</span>
                            :<button key={n} onClick={()=>{setAuditPage(n);fetchAuditLogs(n);}} style={{padding:"6px 12px",borderRadius:6,border:"1px solid",fontSize:13,cursor:"pointer",fontWeight:n===auditPage?700:400,background:n===auditPage?BET_COLOR:"#fff",color:n===auditPage?"#fff":"#374151",borderColor:n===auditPage?BET_COLOR:"#e5e7eb"}}>{n}</button>
                          )}
                          <button onClick={() => { const p=Math.min(totalPages,auditPage+1); setAuditPage(p); fetchAuditLogs(p); }} disabled={auditPage===totalPages}
                            style={{ padding:"6px 12px", borderRadius:6, border:"1px solid #e5e7eb", background:auditPage===totalPages?"#f9fafb":"#fff", cursor:auditPage===totalPages?"not-allowed":"pointer", fontSize:13, color:auditPage===totalPages?"#d1d5db":"#374151" }}>›</button>
                          <span style={{ alignSelf:"center", fontSize:11, color:"#9ca3af" }}>Page {auditPage}/{totalPages}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* VUE : PAR MODULE */}
                  {auditView === "stats" && auditStats && (
                    <div>
                      <h3 style={{ margin:"0 0 16px", fontSize:14, fontWeight:700, color:"#0f172a" }}>📊 Activité par module</h3>
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:14, marginBottom:28 }}>
                        {(auditStats.par_module||[]).sort((a,b)=>b.count-a.count).map(m => {
                          const maxCount = Math.max(...(auditStats.par_module||[]).map(x=>x.count),1);
                          const pct = Math.round((m.count/maxCount)*100);
                          return (
                            <div key={m.module} style={{ background:"#fff", borderRadius:12, padding:"16px 18px", border:"1px solid #f1f5f9", cursor:"pointer" }}
                              onClick={() => { setAuditFilters(f=>({...f,module:m.module})); setAuditView("logs"); setAuditPage(1); fetchAuditLogs(1,{...auditFilters,module:m.module}); }}>
                              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                                <span style={{ fontSize:22 }}>{MODULE_ICONS[m.module]||"📦"}</span>
                                <span style={{ fontWeight:900, fontSize:18, color:BET_COLOR }}>{m.count}</span>
                              </div>
                              <div style={{ fontWeight:700, fontSize:13, color:"#0f172a", marginBottom:8 }}>{m.module}</div>
                              <div style={{ height:6, borderRadius:3, background:"#f1f5f9" }}>
                                <div style={{ height:"100%", width:`${pct}%`, background:BET_COLOR, borderRadius:3 }} />
                              </div>
                              <div style={{ fontSize:10, color:"#94a3b8", marginTop:4 }}>{pct}% du max · Cliquer pour filtrer</div>
                            </div>
                          );
                        })}
                      </div>
                      <h3 style={{ margin:"0 0 16px", fontSize:14, fontWeight:700, color:"#0f172a" }}>🏆 Top acteurs du jour</h3>
                      <div style={{ background:"#fff", borderRadius:12, border:"1px solid #f1f5f9", overflow:"hidden" }}>
                        {(auditStats.top_acteurs||[]).length === 0
                          ? <div style={{ padding:24, textAlign:"center", color:"#9ca3af", fontSize:13 }}>Aucune activité aujourd'hui</div>
                          : (auditStats.top_acteurs||[]).map((a,i) => (
                            <div key={i} style={{ display:"flex", alignItems:"center", gap:14, padding:"12px 18px", borderBottom:i<(auditStats.top_acteurs.length-1)?"1px solid #f8fafc":"none" }}>
                              <div style={{ width:28, height:28, borderRadius:"50%", background:i===0?"#fef9c3":i===1?"#f1f5f9":"#f8fafc", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:13, flexShrink:0 }}>
                                {i===0?"🥇":i===1?"🥈":i===2?"🥉":`${i+1}`}
                              </div>
                              <div style={{ flex:1 }}>
                                <div style={{ fontWeight:700, fontSize:13, color:"#0f172a" }}>{a.acteur_nom||"Inconnu"}</div>
                                <div style={{ fontSize:11, color:"#9ca3af" }}>{a.acteur_email||"-"}</div>
                              </div>
                              <span style={{ fontWeight:900, fontSize:16, color:BET_COLOR }}>{a.count}</span>
                              <span style={{ fontSize:11, color:"#9ca3af" }}>actions</span>
                            </div>
                          ))
                        }
                      </div>
                      {(auditStats.par_heure||[]).length > 0 && (
                        <div style={{ marginTop:24 }}>
                          <h3 style={{ margin:"0 0 16px", fontSize:14, fontWeight:700, color:"#0f172a" }}>⏱️ Activité par heure (aujourd'hui)</h3>
                          <div style={{ background:"#fff", borderRadius:12, border:"1px solid #f1f5f9", padding:"20px 24px" }}>
                            <div style={{ display:"flex", alignItems:"flex-end", gap:3, height:80 }}>
                              {auditStats.par_heure.map((h,i) => {
                                const maxH = Math.max(...auditStats.par_heure.map(x=>x.count),1);
                                const pct = (h.count/maxH)*100;
                                return (
                                  <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                                    <div style={{ width:"100%", background:pct>0?BET_COLOR:"#f1f5f9", borderRadius:"3px 3px 0 0", height:`${Math.max(pct,4)}%`, minHeight:4 }} title={`${h.heure} : ${h.count}`} />
                                    <span style={{ fontSize:8, color:"#94a3b8" }}>{h.heure}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* VUE : PAR PROFIL */}
                  {auditView === "profil" && (
                    <div>
                      <div style={{ background:"#f8fafc", border:"1px solid #e5e7eb", borderRadius:10, padding:"14px 16px", marginBottom:20 }}>
                        <div style={{ fontSize:13, fontWeight:600, color:"#374151", marginBottom:8 }}>Sélectionner un utilisateur :</div>
                        <select onChange={e => { const u=users.find(x=>x.id===e.target.value); if(!u)return; setAuditSelectedProfile(u); fetchAuditByProfile(u.id); }}
                          style={{ padding:"9px 14px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, background:"#fff", minWidth:300, cursor:"pointer" }}>
                          <option value="">-- Choisir un utilisateur --</option>
                          {users.map(u => <option key={u.id} value={u.id}>{u.prenom} {u.nom} ({u.role})</option>)}
                        </select>
                      </div>
                      {auditSelectedProfile && (
                        auditProfileLogs.length === 0
                          ? <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}><div style={{ fontSize:32, marginBottom:8 }}>📭</div>Aucun log</div>
                          : <div>
                              <div style={{ marginBottom:12, display:"flex", alignItems:"center", gap:10 }}>
                                <div style={{ width:36, height:36, borderRadius:"50%", background:"#e0f2fe", color:BET_COLOR, fontWeight:800, fontSize:13, display:"flex", alignItems:"center", justifyContent:"center" }}>
                                  {(auditSelectedProfile.prenom||"?")[0]}{(auditSelectedProfile.nom||"")[0]}
                                </div>
                                <div>
                                  <div style={{ fontWeight:700, fontSize:14 }}>{auditSelectedProfile.prenom} {auditSelectedProfile.nom}</div>
                                  <div style={{ fontSize:11, color:"#9ca3af" }}>{auditSelectedProfile.email} · {auditSelectedProfile.role} · {auditProfileLogs.length} actions</div>
                                </div>
                              </div>
                              <div style={{ overflowX:"auto", borderRadius:10, border:"1px solid #e5e7eb" }}>
                                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                                  <thead><tr style={{ background:"#f8fafc" }}>{["","Acteur","Action","Module","Détail","Centre","Date","IP"].map(h=><th key={h} style={{ padding:"10px 10px", textAlign:"left", fontSize:11, fontWeight:700, color:"#64748b", borderBottom:"2px solid #e5e7eb" }}>{h}</th>)}</tr></thead>
                                  <tbody>{auditProfileLogs.map(log=><AuditRow key={log.id} log={log}/>)}</tbody>
                                </table>
                              </div>
                            </div>
                      )}
                    </div>
                  )}

                  {/* VUE : PAR CENTRE */}
                  {auditView === "centre" && (
                    <div>
                      <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:20 }}>
                        {CENTRES_LIST.map(c => (
                          <button key={c} onClick={() => { setAuditSelectedCentre(c); fetchAuditByCentre(c); }} style={{
                            padding:"10px 18px", borderRadius:10, border:"2px solid", fontWeight:700, fontSize:13, cursor:"pointer", transition:"all .15s",
                            background: auditSelectedCentre===c ? BET_COLOR : "#fff",
                            color:      auditSelectedCentre===c ? "#fff" : "#374151",
                            borderColor:auditSelectedCentre===c ? BET_COLOR : "#e5e7eb",
                          }}>📍 {c.charAt(0).toUpperCase()+c.slice(1)}</button>
                        ))}
                      </div>
                      {auditSelectedCentre && (
                        auditCentreLogs.length === 0
                          ? <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}><div style={{ fontSize:32, marginBottom:8 }}>📭</div>Aucun log pour ce centre</div>
                          : <div>
                              <div style={{ marginBottom:12, fontWeight:700, fontSize:14, color:"#0f172a" }}>📍 {auditSelectedCentre} - {auditCentreLogs.length} entrée{auditCentreLogs.length>1?"s":""}</div>
                              <div style={{ overflowX:"auto", borderRadius:10, border:"1px solid #e5e7eb" }}>
                                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                                  <thead><tr style={{ background:"#f8fafc" }}>{["","Acteur","Action","Module","Détail","Date","IP"].map(h=><th key={h} style={{ padding:"10px 10px", textAlign:"left", fontSize:11, fontWeight:700, color:"#64748b", borderBottom:"2px solid #e5e7eb" }}>{h}</th>)}</tr></thead>
                                  <tbody>{auditCentreLogs.map(log=><AuditRow key={log.id} log={log}/>)}</tbody>
                                </table>
                              </div>
                            </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}



            {/* ================= ONGLET TRAFIC WEB ================= */}
            {activeTab === "trafic" && (() => {
              const st = traficStats;
              const PERIODES = [
                { k:"aujourd_hui", l:"Aujourd'hui" },
                { k:"7j",          l:"7 jours"     },
                { k:"30j",         l:"30 jours"     },
                { k:"3m",          l:"3 mois"       },
                { k:"1an",         l:"1 an"         },
              ];
              const fmtDuration = (ms) => {
                if (!ms) return "-";
                const s = Math.floor(ms / 1000);
                if (s < 60) return `${s}s`;
                return `${Math.floor(s/60)}m ${s%60}s`;
              };
              const periodeLabel = PERIODES.find(p => p.k === traficPeriode)?.l || traficPeriode;

              const exportTraficExcel = async () => {
                if (!st) { toast.error("Aucune donnée à exporter"); return; }
                try {
                  await exporterExcelStyle({
                    nomFichier: `trafic-web-${traficPeriode}`,
                    couleurEntete: '0891B2',
                    feuilles: [
                      {
                        nom: "KPIs",
                        titre: "🌐 Trafic Web - Indicateurs clés",
                        sousTitre: `Période : ${periodeLabel} · Généré le ${new Date().toLocaleDateString('fr-FR')}`,
                        colonnes: [{ label:"Indicateur", width:28 }, { label:"Valeur", width:20 }],
                        lignes: [
                          ["Pages vues",          st.total_vues || 0],
                          ["Sessions uniques",    st.total_sessions || 0],
                          ["Bounce rate (%)",     st.bounce_rate || 0],
                          ["Durée moy./session",  fmtDuration(st.avg_duration_ms)],
                          ["Pages/session",       st.pages_par_session || 0],
                        ],
                      },
                      {
                        nom: "Pages visitées",
                        colonnes: [{ label:"#", width:6 }, { label:"Page", width:34 }, { label:"Chemin", width:28 }, { label:"Vues", width:12 }, { label:"%", width:10 }],
                        lignes: (st.pages || []).map((p,i) => [i+1, p.label || p.path, p.path, p.vues, p.pct]),
                      },
                      {
                        nom: "Activité par rôle",
                        colonnes: [{ label:"Rôle", width:24 }, { label:"Vues", width:14 }],
                        lignes: (st.roles_stats || []).map(r => [r.role, r.vues]),
                      },
                    ],
                  });
                  toast.success("Export Excel effectué");
                } catch (e) { toast.error("Erreur lors de l'export Excel"); }
              };

              const exportTraficPDF = async () => {
                if (!st) { toast.error("Aucune donnée à exporter"); return; }
                try {
                  const { jsPDF } = await import("jspdf");
                  const autoTable = (await import("jspdf-autotable")).default;
                  const doc = new jsPDF();
                  doc.setFontSize(16); doc.setTextColor(15,23,42);
                  doc.text("Trafic Web - BET Dashboard", 14, 16);
                  doc.setFontSize(10); doc.setTextColor(100);
                  doc.text(`Période : ${periodeLabel}  ·  Généré le ${new Date().toLocaleDateString('fr-FR')}`, 14, 23);

                  autoTable(doc, {
                    startY: 30,
                    head: [["Indicateur","Valeur"]],
                    body: [
                      ["Pages vues",         (st.total_vues || 0).toLocaleString("fr-FR")],
                      ["Sessions uniques",   (st.total_sessions || 0).toLocaleString("fr-FR")],
                      ["Bounce rate",        `${st.bounce_rate || 0}%`],
                      ["Durée moy./session", fmtDuration(st.avg_duration_ms)],
                      ["Pages/session",      st.pages_par_session || "-"],
                    ],
                    theme:"striped", headStyles:{ fillColor:[8,145,178] }, styles:{ fontSize:9 },
                  });

                  autoTable(doc, {
                    startY: doc.lastAutoTable.finalY + 10,
                    head: [["#","Page","Chemin","Vues","%"]],
                    body: (st.pages || []).slice(0,20).map((p,i) => [i+1, p.label || p.path, p.path, p.vues.toLocaleString("fr-FR"), `${p.pct}%`]),
                    theme:"striped", headStyles:{ fillColor:[8,145,178] }, styles:{ fontSize:8 },
                  });

                  autoTable(doc, {
                    startY: doc.lastAutoTable.finalY + 10,
                    head: [["Rôle","Vues"]],
                    body: (st.roles_stats || []).map(r => [r.role, r.vues.toLocaleString("fr-FR")]),
                    theme:"striped", headStyles:{ fillColor:[8,145,178] }, styles:{ fontSize:9 },
                  });

                  doc.save(`trafic-web-${traficPeriode}-${Date.now()}.pdf`);
                  toast.success("Export PDF effectué");
                } catch (e) { toast.error("Erreur lors de l'export PDF"); }
              };

              return (
                <div>
                  {/* Header */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20, flexWrap:"wrap", gap:12 }}>
                    <div>
                      <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:"#0f172a" }}>🌐 Trafic web - Tableau de bord</h2>
                      <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Toutes les pages visitées dans la plateforme /dash</p>
                    </div>
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                      {PERIODES.map(p => (
                        <button key={p.k} onClick={() => setTraficPeriode(p.k)}
                          style={{ padding:"6px 14px", borderRadius:20, border:"none", fontSize:12, fontWeight:700, cursor:"pointer",
                            background: traficPeriode === p.k ? BET_COLOR : "#f1f5f9",
                            color:      traficPeriode === p.k ? "#fff"    : "#374151" }}>
                          {p.l}
                        </button>
                      ))}
                      <button onClick={() => fetchTraficStats(traficPeriode)}
                        style={{ padding:"6px 14px", borderRadius:20, border:"none", fontSize:12, fontWeight:700, cursor:"pointer", background:"#e0f2fe", color:"#0369a1" }}>
                        🔄 Actualiser
                      </button>
                      <button onClick={exportTraficExcel} disabled={!st}
                        style={{ padding:"6px 14px", borderRadius:20, border:"none", fontSize:12, fontWeight:700, cursor: st ? "pointer" : "not-allowed", opacity: st ? 1 : 0.5, background:"#dcfce7", color:"#166534" }}>
                        📊 Export Excel
                      </button>
                      <button onClick={exportTraficPDF} disabled={!st}
                        style={{ padding:"6px 14px", borderRadius:20, border:"none", fontSize:12, fontWeight:700, cursor: st ? "pointer" : "not-allowed", opacity: st ? 1 : 0.5, background:"#fee2e2", color:"#991b1b" }}>
                        📄 Export PDF
                      </button>
                    </div>
                  </div>

                  {traficLoading ? (
                    <div style={{ textAlign:"center", padding:"60px 0", color:"#9ca3af", fontSize:14 }}>⏳ Chargement des statistiques…</div>
                  ) : !st ? (
                    <div style={{ textAlign:"center", padding:"60px 0", color:"#9ca3af", fontSize:13 }}>
                      Aucune donnée disponible.<br/>
                      <span style={{ fontSize:11 }}>Les données s'accumuleront dès que les utilisateurs navigueront dans la plateforme.</span>
                    </div>
                  ) : (
                    <>
                      {/* KPIs */}
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:12, marginBottom:24 }}>
                        {[
                          { icon:"📄", label:"Pages vues",        val: (st.total_vues||0).toLocaleString("fr-FR"),      color:BET_COLOR  },
                          { icon:"👥", label:"Sessions uniques",  val: (st.total_sessions||0).toLocaleString("fr-FR"),   color:"#2563eb"  },
                          { icon:"🔄", label:"Bounce rate",       val: `${st.bounce_rate||0}%`,                          color:"#d97706"  },
                          { icon:"⏱️", label:"Durée moy./session",val: fmtDuration(st.avg_duration_ms),                  color:"#059669"  },
                          { icon:"📑", label:"Pages/session",     val: st.pages_par_session||"-",                        color:"#7c3aed"  },
                        ].map(k => (
                          <div key={k.label} style={{ background:"#fff", borderRadius:12, padding:"16px 14px", boxShadow:"0 1px 4px rgba(0,0,0,0.06)", border:"1px solid #f1f5f9" }}>
                            <div style={{ fontSize:22, marginBottom:6 }}>{k.icon}</div>
                            <div style={{ fontSize:20, fontWeight:900, color:k.color }}>{k.val}</div>
                            <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, marginTop:2 }}>{k.label}</div>
                          </div>
                        ))}
                      </div>

                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:20 }}>

                        {/* Pages les plus visitées */}
                        <div style={{ background:"#fff", borderRadius:12, padding:20, boxShadow:"0 1px 4px rgba(0,0,0,0.06)", border:"1px solid #f1f5f9" }}>
                          <div style={{ fontSize:14, fontWeight:800, color:"#0f172a", marginBottom:14 }}>📊 Pages les plus visitées</div>
                          {(st.pages||[]).length === 0 ? (
                            <div style={{ color:"#9ca3af", fontSize:12, textAlign:"center", padding:"20px 0" }}>Aucune donnée</div>
                          ) : (
                            <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
                              {(st.pages||[]).slice(0,15).map((p, i) => (
                                <div key={p.path} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid #f1f5f9" }}>
                                  <span style={{ width:22, height:22, borderRadius:"50%", background: i < 3 ? BET_COLOR : "#f1f5f9", color: i < 3 ? "#fff" : "#64748b", fontSize:10, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{i+1}</span>
                                  <div style={{ flex:1, minWidth:0 }}>
                                    <div style={{ fontSize:12, fontWeight:700, color:"#0f172a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                                      {p.label || p.path}
                                    </div>
                                    <div style={{ fontSize:10, color:"#9ca3af" }}>{p.path}</div>
                                  </div>
                                  <div style={{ textAlign:"right", flexShrink:0 }}>
                                    <div style={{ fontSize:13, fontWeight:800, color:BET_COLOR }}>{p.vues.toLocaleString("fr-FR")}</div>
                                    <div style={{ fontSize:10, color:"#9ca3af" }}>{p.pct}%</div>
                                  </div>
                                  <div style={{ width:50, flexShrink:0 }}>
                                    <div style={{ height:5, background:"#e5e7eb", borderRadius:3 }}>
                                      <div style={{ height:"100%", width:`${p.pct}%`, background:BET_COLOR, borderRadius:3 }} />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Répartition par rôle */}
                        <div style={{ background:"#fff", borderRadius:12, padding:20, boxShadow:"0 1px 4px rgba(0,0,0,0.06)", border:"1px solid #f1f5f9" }}>
                          <div style={{ fontSize:14, fontWeight:800, color:"#0f172a", marginBottom:14 }}>👤 Activité par rôle</div>
                          {(st.roles_stats||[]).length === 0 ? (
                            <div style={{ color:"#9ca3af", fontSize:12, textAlign:"center", padding:"20px 0" }}>Aucune donnée</div>
                          ) : (
                            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                              {(st.roles_stats||[]).map(r => {
                                const pct = st.total_vues > 0 ? Math.round((r.vues / st.total_vues) * 100) : 0;
                                return (
                                  <div key={r.role}>
                                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:4 }}>
                                      <span style={{ fontWeight:600, color:"#374151", textTransform:"capitalize" }}>{r.role}</span>
                                      <span style={{ fontWeight:700, color:BET_COLOR }}>{r.vues.toLocaleString("fr-FR")} <span style={{ color:"#9ca3af", fontWeight:400 }}>({pct}%)</span></span>
                                    </div>
                                    <div style={{ height:6, background:"#e5e7eb", borderRadius:3 }}>
                                      <div style={{ height:"100%", width:`${pct}%`, background:BET_COLOR, borderRadius:3, transition:"width .3s" }} />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Évolution journalière */}
                      {(st.evolution||[]).length > 0 && (
                        <div style={{ background:"#fff", borderRadius:12, padding:20, boxShadow:"0 1px 4px rgba(0,0,0,0.06)", border:"1px solid #f1f5f9" }}>
                          <div style={{ fontSize:14, fontWeight:800, color:"#0f172a", marginBottom:14 }}>📈 Évolution des pages vues</div>
                          <div style={{ display:"flex", alignItems:"flex-end", gap:3, height:80, overflowX:"auto" }}>
                            {(() => {
                              const maxV = Math.max(...st.evolution.map(e => e.vues), 1);
                              return st.evolution.map(e => (
                                <div key={e.date} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3, flex:"0 0 auto", minWidth:30 }}
                                  title={`${e.date} - ${e.vues} vues`}>
                                  <div style={{ fontSize:9, color:BET_COLOR, fontWeight:700 }}>{e.vues > 0 ? e.vues : ""}</div>
                                  <div style={{ width:24, background:BET_COLOR, borderRadius:"3px 3px 0 0", height: `${Math.max(4, Math.round((e.vues/maxV)*60))}px`, opacity:0.8 }} />
                                  <div style={{ fontSize:8, color:"#9ca3af", whiteSpace:"nowrap", transform:"rotate(-45deg)", transformOrigin:"top left", marginTop:4, marginLeft:4 }}>
                                    {new Date(e.date).toLocaleDateString("fr-FR",{day:"2-digit",month:"2-digit"})}
                                  </div>
                                </div>
                              ));
                            })()}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()}

            {/* ================= ONGLET CLIENTS & PROSPECTS ================= */}
            {activeTab === "clients" && (() => {
              const STATUT_META = {
                en_attente: { label:"En attente", color:"#d97706", bg:"#fef3c7" },
                en_cours:   { label:"En cours",   color:"#0891b2", bg:"#e0f2fe" },
                converti:   { label:"Converti",   color:"#22c55e", bg:"#dcfce7" },
                perdu:      { label:"Perdu",      color:"#ef4444", bg:"#fee2e2" },
              };
              const TYPE_COURS = { en_ligne:"En ligne", presentiel:"Présentiel" };

              const allAssistantes = [...new Set(clientsAssignations.map(a => a.assistante_nom || "-"))].sort();

              const filtered = clientsAssignations.filter(a => {
                if (clientsFiltreStatut !== "tous" && a.statut !== clientsFiltreStatut) return false;
                if (clientsFiltreAssist !== "tous" && (a.assistante_nom || "-") !== clientsFiltreAssist) return false;
                if (clientsSearch) {
                  const q = clientsSearch.toLowerCase();
                  if (!(a.prospect_nom||"").toLowerCase().includes(q) &&
                      !(a.prospect_email||"").toLowerCase().includes(q) &&
                      !(a.prospect_telephone||"").toLowerCase().includes(q)) return false;
                }
                if (clientsDateDebut || clientsDateFin) {
                  const d = a.created_at ? new Date(a.created_at) : null;
                  if (!d) return false;
                  if (clientsDateDebut && d < new Date(clientsDateDebut)) return false;
                  if (clientsDateFin   && d > new Date(clientsDateFin + "T23:59:59")) return false;
                }
                return true;
              });

              const totalPages    = Math.max(1, Math.ceil(filtered.length / CLIENTS_PER_PAGE));
              const safePage      = Math.min(clientsPage, totalPages);
              const paginated     = filtered.slice((safePage - 1) * CLIENTS_PER_PAGE, safePage * CLIENTS_PER_PAGE);

              const total    = clientsAssignations.length;
              const enAtt    = clientsAssignations.filter(a => a.statut === "en_attente").length;
              const enCours  = clientsAssignations.filter(a => a.statut === "en_cours").length;
              const conv     = clientsAssignations.filter(a => a.statut === "converti").length;
              const perdus   = clientsAssignations.filter(a => a.statut === "perdu").length;
              const tauxConv = total > 0 ? Math.round((conv / total) * 100) : 0;

              const parAssistante = {};
              clientsAssignations.forEach(a => {
                const nom = a.assistante_nom || "-";
                if (!parAssistante[nom]) parAssistante[nom] = { total:0, en_attente:0, en_cours:0, converti:0, perdu:0 };
                parAssistante[nom].total++;
                if (parAssistante[nom][a.statut] !== undefined) parAssistante[nom][a.statut]++;
              });
              const assistantesEntries = Object.entries(parAssistante).sort((a,b) => b[1].total - a[1].total);

              return (
                <div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20, flexWrap:"wrap", gap:12 }}>
                    <div>
                      <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:"#0f172a" }}>👥 Clients & Prospects - Plateforme globale</h2>
                      <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Toutes les assignations prospects des assistantes commerciales - données réelles</p>
                    </div>
                    <button onClick={fetchClientsAssignations} style={{ padding:"9px 14px", background:"#f1f5f9", color:"#374151", border:"none", borderRadius:8, cursor:"pointer", fontWeight:600, fontSize:12 }}>🔄 Actualiser</button>
                  </div>

                  <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:12, marginBottom:24 }}>
                    <StatCard label="Total prospects" value={total} color="#6366f1" icon="👥" />
                    <StatCard label="En attente"      value={enAtt}   color="#d97706" icon="⏳" />
                    <StatCard label="En cours"        value={enCours} color="#0891b2" icon="🔄" />
                    <StatCard label="Convertis"       value={conv}    color="#22c55e" icon="✅" />
                    <StatCard label="Perdus"          value={perdus}  color="#ef4444" icon="❌" />
                    <StatCard label="Taux conversion" value={`${tauxConv}%`} color={BET_COLOR} icon="📈" sub="Prospect → Client" />
                  </div>

                  {assistantesEntries.length > 0 && (
                    <div style={{ background:"#f8fafc", borderRadius:12, padding:16, marginBottom:20, border:"1px solid #e5e7eb" }}>
                      <div style={{ fontSize:13, fontWeight:700, color:"#0f172a", marginBottom:12 }}>📊 Répartition par assistante</div>
                      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                        {assistantesEntries.map(([nom, st]) => {
                          const pct = st.total > 0 ? Math.round((st.converti / st.total) * 100) : 0;
                          return (
                            <div key={nom} style={{ background:"#fff", borderRadius:10, padding:"10px 14px", border:"1px solid #e5e7eb" }}>
                              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6, flexWrap:"wrap", gap:6 }}>
                                <span style={{ fontWeight:700, fontSize:13, color:"#0f172a" }}>{nom}</span>
                                <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                                  <span style={{ fontSize:11, fontWeight:700, padding:"1px 8px", borderRadius:999, background:"#fef3c7", color:"#d97706" }}>{st.en_attente} att.</span>
                                  <span style={{ fontSize:11, fontWeight:700, padding:"1px 8px", borderRadius:999, background:"#e0f2fe", color:"#0891b2" }}>{st.en_cours} cours</span>
                                  <span style={{ fontSize:11, fontWeight:700, padding:"1px 8px", borderRadius:999, background:"#dcfce7", color:"#22c55e" }}>{st.converti} conv.</span>
                                  {st.perdu > 0 && <span style={{ fontSize:11, fontWeight:700, padding:"1px 8px", borderRadius:999, background:"#fee2e2", color:"#ef4444" }}>{st.perdu} perdu</span>}
                                  <span style={{ fontSize:11, color:"#6b7280", fontWeight:600 }}>{pct}% conv.</span>
                                </div>
                              </div>
                              <div style={{ background:"#f1f5f9", borderRadius:99, height:5, overflow:"hidden" }}>
                                <div style={{ height:"100%", width:`${pct}%`, background:`linear-gradient(90deg,${BET_COLOR},#22c55e)`, borderRadius:99, transition:"width .4s" }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
                    <input
                      type="text"
                      placeholder="🔍 Rechercher un prospect…"
                      value={clientsSearch}
                      onChange={e => { setClientsSearch(e.target.value); setClientsPage(1); }}
                      style={{ padding:"8px 12px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:12, flex:1, minWidth:180, outline:"none" }}
                    />
                    <select value={clientsFiltreStatut} onChange={e => { setClientsFiltreStatut(e.target.value); setClientsPage(1); }} style={{ padding:"8px 12px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:12, background:"#fff" }}>
                      <option value="tous">Tous les statuts</option>
                      <option value="en_attente">En attente</option>
                      <option value="en_cours">En cours</option>
                      <option value="converti">Converti</option>
                      <option value="perdu">Perdu</option>
                    </select>
                    <select value={clientsFiltreAssist} onChange={e => { setClientsFiltreAssist(e.target.value); setClientsPage(1); }} style={{ padding:"8px 12px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:12, background:"#fff" }}>
                      <option value="tous">Toutes les assistantes</option>
                      {allAssistantes.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ fontSize:11, color:"#6b7280", whiteSpace:"nowrap" }}>Du</span>
                      <input type="date" value={clientsDateDebut} onChange={e => { setClientsDateDebut(e.target.value); setClientsPage(1); }}
                        style={{ padding:"7px 10px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:12, background:"#fff" }} />
                      <span style={{ fontSize:11, color:"#6b7280", whiteSpace:"nowrap" }}>au</span>
                      <input type="date" value={clientsDateFin} onChange={e => { setClientsDateFin(e.target.value); setClientsPage(1); }}
                        style={{ padding:"7px 10px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:12, background:"#fff" }} />
                      {(clientsDateDebut || clientsDateFin) && (
                        <button onClick={() => { setClientsDateDebut(""); setClientsDateFin(""); setClientsPage(1); }}
                          style={{ padding:"5px 8px", border:"none", borderRadius:6, background:"#fee2e2", color:"#dc2626", fontSize:11, cursor:"pointer", fontWeight:700 }}>✕</button>
                      )}
                    </div>
                    <span style={{ fontSize:12, color:"#6b7280", whiteSpace:"nowrap" }}>{filtered.length} résultat{filtered.length !== 1 ? "s" : ""}</span>
                  </div>

                  {clientsLoading ? (
                    <div style={{ textAlign:"center", padding:48, color:"#6b7280", fontSize:13 }}>⏳ Chargement des prospects…</div>
                  ) : filtered.length === 0 ? (
                    <div style={{ textAlign:"center", padding:"60px 24px", background:"#f8fafc", borderRadius:16, border:"1.5px dashed #e2e8f0" }}>
                      <div style={{ fontSize:"2.5rem", marginBottom:12 }}>🎯</div>
                      <div style={{ fontWeight:700, color:"#0f172a", marginBottom:6 }}>Aucun prospect trouvé</div>
                      <div style={{ fontSize:13, color:"#6b7280" }}>Les prospects apparaissent ici dès qu'ils choisissent une assistante sur le site.</div>
                    </div>
                  ) : (
                    <>
                      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                        {paginated.map(a => {
                          const sm = STATUT_META[a.statut] || STATUT_META.en_attente;
                          return (
                            <div key={a.id} style={{ background:"#fff", border:"1.5px solid #e5e7eb", borderRadius:12, padding:"12px 16px", display:"flex", alignItems:"flex-start", gap:12 }}>
                              <div style={{ width:38, height:38, borderRadius:"50%", background:`linear-gradient(135deg,#1e3a8a,${BET_COLOR})`, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:13, flexShrink:0 }}>
                                {(a.prospect_nom||"?")[0].toUpperCase()}
                              </div>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
                                  <span style={{ fontWeight:800, fontSize:14, color:"#0f172a" }}>{a.prospect_nom || "-"}</span>
                                  <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
                                    <span style={{ fontSize:10, color:"#94a3b8" }}>
                                      {a.created_at ? new Date(a.created_at).toLocaleDateString("fr-FR", { day:"2-digit", month:"short", year:"numeric" }) : "-"}
                                    </span>
                                    <span style={{ background:sm.bg, color:sm.color, borderRadius:999, padding:"2px 10px", fontSize:11, fontWeight:700 }}>{sm.label}</span>
                                  </div>
                                </div>
                                <div style={{ display:"flex", gap:10, marginTop:3, flexWrap:"wrap" }}>
                                  {a.prospect_telephone && <span style={{ fontSize:12, color:"#0891b2", fontWeight:600 }}>📞 {a.prospect_telephone}</span>}
                                  {a.prospect_email    && <span style={{ fontSize:12, color:"#64748b" }}>✉️ {a.prospect_email}</span>}
                                </div>
                                <div style={{ display:"flex", gap:6, marginTop:5, flexWrap:"wrap" }}>
                                  {a.type_cours && <span style={{ background:"#eff6ff", color:"#1e3a8a", borderRadius:999, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{TYPE_COURS[a.type_cours] || a.type_cours}</span>}
                                  {a.centre_nom && <span style={{ background:"#f0fdf4", color:"#16a34a", borderRadius:999, padding:"2px 8px", fontSize:11, fontWeight:700 }}>📍 {a.centre_nom}</span>}
                                  <span style={{ background:"#f8fafc", color:"#475569", borderRadius:999, padding:"2px 8px", fontSize:11 }}>👤 {a.assistante_nom || "-"}</span>
                                  {a.montant_paye > 0 && (
                                    <span style={{ background:"#dcfce7", color:"#15803d", borderRadius:999, padding:"2px 8px", fontSize:11, fontWeight:700 }}>
                                      💳 {Number(a.montant_paye).toLocaleString("fr-FR")} FCFA
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {totalPages > 1 && (
                        <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:6, marginTop:20, flexWrap:"wrap" }}>
                          <button onClick={() => setClientsPage(1)} disabled={safePage === 1}
                            style={{ padding:"6px 10px", borderRadius:7, border:"1px solid #e2e8f0", background: safePage===1 ? "#f8fafc" : "#fff", color: safePage===1 ? "#cbd5e1" : "#374151", cursor: safePage===1 ? "not-allowed" : "pointer", fontSize:12, fontWeight:600 }}>«</button>
                          <button onClick={() => setClientsPage(p => Math.max(1, p-1))} disabled={safePage === 1}
                            style={{ padding:"6px 10px", borderRadius:7, border:"1px solid #e2e8f0", background: safePage===1 ? "#f8fafc" : "#fff", color: safePage===1 ? "#cbd5e1" : "#374151", cursor: safePage===1 ? "not-allowed" : "pointer", fontSize:12, fontWeight:600 }}>‹</button>
                          {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
                            .reduce((acc, p, idx, arr) => {
                              if (idx > 0 && p - arr[idx-1] > 1) acc.push("…");
                              acc.push(p);
                              return acc;
                            }, [])
                            .map((p, i) => p === "…" ? (
                              <span key={`e${i}`} style={{ padding:"0 4px", color:"#94a3b8", fontSize:12 }}>…</span>
                            ) : (
                              <button key={p} onClick={() => setClientsPage(p)}
                                style={{ minWidth:32, padding:"6px 10px", borderRadius:7, border:`1px solid ${p===safePage ? BET_COLOR : "#e2e8f0"}`, background: p===safePage ? BET_COLOR : "#fff", color: p===safePage ? "#fff" : "#374151", cursor:"pointer", fontSize:12, fontWeight: p===safePage ? 700 : 500 }}>{p}</button>
                            ))
                          }
                          <button onClick={() => setClientsPage(p => Math.min(totalPages, p+1))} disabled={safePage === totalPages}
                            style={{ padding:"6px 10px", borderRadius:7, border:"1px solid #e2e8f0", background: safePage===totalPages ? "#f8fafc" : "#fff", color: safePage===totalPages ? "#cbd5e1" : "#374151", cursor: safePage===totalPages ? "not-allowed" : "pointer", fontSize:12, fontWeight:600 }}>›</button>
                          <button onClick={() => setClientsPage(totalPages)} disabled={safePage === totalPages}
                            style={{ padding:"6px 10px", borderRadius:7, border:"1px solid #e2e8f0", background: safePage===totalPages ? "#f8fafc" : "#fff", color: safePage===totalPages ? "#cbd5e1" : "#374151", cursor: safePage===totalPages ? "not-allowed" : "pointer", fontSize:12, fontWeight:600 }}>»</button>
                          <span style={{ fontSize:11, color:"#9ca3af", marginLeft:6 }}>Page {safePage}/{totalPages} · {filtered.length} entrée{filtered.length!==1?"s":""}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()}

            {/* ================= ONGLET OFFRES & FORMATIONS ================= */}
            {activeTab === "offres" && (() => {
              const now = new Date();

              // ── Filtre périodique sur les assignations ──
              const assignPeriode = offresAssignations.filter(a => {
                if (offresPeriode === "tout") return true;
                const d = a.created_at ? new Date(a.created_at) : null;
                if (!d || isNaN(d)) return false;
                if (offresPeriode === "semaine") { const debut = new Date(now); debut.setDate(now.getDate()-7); return d >= debut; }
                if (offresPeriode === "mois")     return d.getFullYear()===now.getFullYear() && d.getMonth()===now.getMonth();
                if (offresPeriode === "trimestre"){ const t=Math.floor(now.getMonth()/3); return d.getFullYear()===now.getFullYear()&&Math.floor(d.getMonth()/3)===t; }
                if (offresPeriode === "annee")    return d.getFullYear()===offresAnnee;
                return true;
              });

              // ── Séparer assignations actives (offre dans le catalogue) vs orphelines ──
              // Comparaison insensible à la casse/espaces, et la MÊME partout : inclusion,
              // agrégat et lecture. Une comparaison stricte ici et normalisée là faisait
              // qu'un dossier comptait dans l'en-tête sans jamais rejoindre sa ligne.
              const normLabel = (s) => (s || "").trim().toLowerCase();
              const activeLabels = new Set(offresCatalogue.map(o => normLabel(o.label)));
              const assignActives = assignPeriode.filter(a => activeLabels.has(normLabel(a.inscription_offre)));
              const assignOrphans = assignPeriode.filter(a => !activeLabels.has(normLabel(a.inscription_offre)));
              const orphanCA      = assignOrphans.reduce((s,a) => s+(a.montant_paye||0), 0);

              // ── Stats calculées uniquement sur les offres actives du catalogue ──
              // statut_paiement a plusieurs valeurs pour « soldé » selon le flux qui écrit
              // le champ : paye (CinetPay), payé (TouchPoint), confirme (inscription
              // manuelle et import en masse), reçu (autre flux). N'en reconnaître qu'une
              // faisait afficher 0 dossier payé sur des dossiers pourtant soldés.
              const STATUTS_PAYES_OFFRE = ["paye", "payé", "confirme", "reçu"];
              const estDossierPaye = a => STATUTS_PAYES_OFFRE.includes(a.statut_paiement);
              const statsParLabel = {};
              const statsParCat   = { en_ligne:{ nb:0, ca:0 }, presentiel:{ nb:0, ca:0 }, certification:{ nb:0, ca:0 }, cours_prive:{ nb:0, ca:0 } };
              assignActives.forEach(a => {
                const label = normLabel(a.inscription_offre);
                if (!statsParLabel[label]) statsParLabel[label] = { nb:0, ca:0, nbPaye:0 };
                statsParLabel[label].nb++;
                statsParLabel[label].ca += a.montant_paye || 0;
                if (estDossierPaye(a)) statsParLabel[label].nbPaye++;
                const tc = a.type_cours || "";
                if (tc === "en_ligne") { statsParCat.en_ligne.nb++; statsParCat.en_ligne.ca += a.montant_paye||0; }
                else if (tc === "presentiel" || tc === "les_deux") { statsParCat.presentiel.nb++; statsParCat.presentiel.ca += a.montant_paye||0; }
                else if (tc === "cours_prive" || tc === "domicile" || a.type_coaching === "prive") { statsParCat.cours_prive.nb++; statsParCat.cours_prive.ca += a.montant_paye||0; }
              });

              const totalInscrits = assignActives.length;
              const totalCA       = assignActives.reduce((s,a) => s+(a.montant_paye||0), 0);
              const totalPaye     = assignActives.filter(estDossierPaye).length;
              const totalActives  = offresCatalogue.length;

              const CAT_META = {
                en_ligne:     { label:"En ligne",      icon:"💻", color:"#8b5cf6", bg:"#ede9fe" },
                presentiel:   { label:"Présentiel",    icon:"🏢", color:BET_COLOR,  bg:"#e0f2fe" },
                certification:{ label:"Certifications",icon:"📜", color:"#0891b2", bg:"#e0f7fa" },
                cours_prive:  { label:"Cours privés",  icon:"🎯", color:"#d97706", bg:"#fef3c7" },
              };

              const PERIODES = [
                { v:"semaine", l:"7 derniers jours" },
                { v:"mois",    l:"Ce mois" },
                { v:"trimestre",l:"Ce trimestre" },
                { v:"annee",   l:"Année" },
                { v:"tout",    l:"Tout" },
              ];

              const offresFiltrees = offresCatalogue
                .filter(o => offresCatFiltre === "tous" || o.categorie === offresCatFiltre)
                .filter(o => !offresSearchVal || (o.label||"").toLowerCase().includes(offresSearchVal.toLowerCase()));

              // ── Export Excel ──
              const exportCSV = () => {
                const label = PERIODES.find(p=>p.v===offresPeriode)?.l || "Tout";
                const lignes = offresFiltrees.map(offre => {
                  const stats = statsParLabel[normLabel(offre.label)] || { nb:0, ca:0, nbPaye:0 };
                  const tx = stats.nb > 0 ? Math.round((stats.nbPaye/stats.nb)*100) : 0;
                  return [offre.label, offre.categorie, offre.prix_str, stats.nb, stats.ca, stats.nbPaye, `${tx}%`, offre.actif!==false?"Actif":"Inactif", label];
                });
                lignes.push(["TOTAL", "", "", totalInscrits, totalCA, totalPaye, totalInscrits>0?`${Math.round(totalPaye/totalInscrits*100)}%`:"0%", "", label]);
                exporterExcelStyle({
                  nomFichier: `offres_formations_${offresPeriode}_${new Date().toISOString().slice(0,10)}`,
                  titre: '🎓 Offres de formation',
                  sousTitre: `${offresFiltrees.length} offre${offresFiltrees.length>1?'s':''} · ${label}`,
                  couleurEntete: BET_COLOR.replace('#',''),
                  colonnes: [
                    { label: 'Offre', width: 28, bold: true }, { label: 'Catégorie', width: 16 },
                    { label: 'Prix', width: 14 }, { label: 'Inscrits', width: 12, align: 'center' },
                    { label: 'CA (FCFA)', width: 16, align: 'right' }, { label: 'Dossiers payés', width: 14, align: 'center' },
                    { label: 'Taux paiement', width: 14, align: 'center' }, { label: 'Statut offre', width: 12 },
                    { label: 'Période', width: 16 },
                  ],
                  lignes,
                });
              };

              // ── Export PDF ──
              const exportPDF = () => {
                const periodeLabel = PERIODES.find(p=>p.v===offresPeriode)?.l || "Tout";
                const dateStr = new Date().toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric"});
                const rows = offresFiltrees.map(offre => {
                  const meta  = CAT_META[offre.categorie] || { label:offre.categorie, icon:"📦" };
                  const stats = statsParLabel[normLabel(offre.label)] || { nb:0, ca:0, nbPaye:0 };
                  const tx    = stats.nb > 0 ? Math.round((stats.nbPaye/stats.nb)*100) : 0;
                  return `<tr>
                    <td>${offre.icon||""} ${offre.label}</td>
                    <td>${meta.icon} ${meta.label}</td>
                    <td>${offre.prix_str}</td>
                    <td style="text-align:center;font-weight:700">${stats.nb}</td>
                    <td style="text-align:right;color:#059669;font-weight:700">${stats.ca.toLocaleString("fr-FR")} FCFA</td>
                    <td style="text-align:center;font-weight:700;color:${tx>=80?"#22c55e":tx>=50?"#f59e0b":"#ef4444"}">${tx}%</td>
                    <td style="text-align:center"><span style="padding:2px 8px;border-radius:12px;font-size:11px;background:${offre.actif!==false?"#d1fae5":"#f1f5f9"};color:${offre.actif!==false?"#065f46":"#6b7280"}">${offre.actif!==false?"Actif":"Inactif"}</span></td>
                  </tr>`;
                }).join("");
                const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"/><title>Offres & Formations - BET</title>
                <style>
                  body{font-family:Arial,sans-serif;padding:24px;color:#0f172a;font-size:12px}
                  h1{font-size:18px;margin-bottom:4px}
                  .meta{color:#6b7280;font-size:11px;margin-bottom:20px}
                  .kpi{display:flex;gap:16px;margin-bottom:24px;flex-wrap:wrap}
                  .kpi-card{border:1px solid #e5e7eb;border-radius:8px;padding:12px 16px;min-width:120px}
                  .kpi-card .val{font-size:20px;font-weight:800;color:#0891b2}
                  .kpi-card .lbl{font-size:10px;color:#9ca3af;margin-bottom:4px}
                  table{width:100%;border-collapse:collapse;font-size:12px}
                  th{background:#f8fafc;padding:8px 12px;text-align:left;font-size:10px;text-transform:uppercase;color:#374151;border-bottom:2px solid #e5e7eb}
                  td{padding:8px 12px;border-bottom:1px solid #f1f5f9}
                  tr:last-child td{font-weight:700;background:#f8fafc;border-top:2px solid #e5e7eb}
                  @media print{body{padding:0}}
                </style></head><body>
                <h1>🎓 Offres &amp; Formations - BET Languages</h1>
                <div class="meta">Période : <strong>${periodeLabel}</strong> · Généré le ${dateStr}</div>
                <div class="kpi">
                  <div class="kpi-card"><div class="lbl">Offres actives</div><div class="val">${totalActives}</div></div>
                  <div class="kpi-card"><div class="lbl">Total inscrits</div><div class="val">${totalInscrits}</div></div>
                  <div class="kpi-card"><div class="lbl">CA total</div><div class="val">${totalCA.toLocaleString("fr-FR")} FCFA</div></div>
                  <div class="kpi-card"><div class="lbl">Dossiers payés</div><div class="val">${totalPaye}</div></div>
                </div>
                <table>
                  <thead><tr><th>Offre</th><th>Catégorie</th><th>Prix</th><th>Inscrits</th><th>CA</th><th>Taux paiement</th><th>Statut</th></tr></thead>
                  <tbody>${rows}
                    <tr><td colspan="3"><strong>TOTAL</strong></td><td style="text-align:center;font-weight:700">${totalInscrits}</td><td style="text-align:right;font-weight:700;color:#059669">${totalCA.toLocaleString("fr-FR")} FCFA</td><td style="text-align:center;font-weight:700">${totalInscrits>0?Math.round(totalPaye/totalInscrits*100):0}%</td><td></td></tr>
                  </tbody>
                </table>
                </body></html>`;
                const w = window.open("","_blank","width=900,height=700");
                if (w) { w.document.write(html); w.document.close(); setTimeout(()=>w.print(), 400); }
              };

              return (
              <div>
                {/* Header */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16, flexWrap:"wrap", gap:12 }}>
                  <div>
                    <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:"#0f172a" }}>🎓 Offres & Formations - Catalogue réel</h2>
                    <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>
                      {totalActives} offre{totalActives!==1?"s":""} actives · {totalInscrits.toLocaleString()} inscription{totalInscrits!==1?"s":""} sur la période
                    </p>
                  </div>
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                    <button onClick={fetchOffresData} style={{ padding:"8px 14px", background:"#f1f5f9", color:"#374151", border:"none", borderRadius:8, cursor:"pointer", fontWeight:600, fontSize:12 }}>🔄 Actualiser</button>
                    <button onClick={exportCSV}
                      style={{ padding:"8px 14px", background:"#059669", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>
                      📊 Export Excel
                    </button>
                    <button onClick={exportPDF}
                      style={{ padding:"8px 14px", background:"#dc2626", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>
                      📄 Export PDF
                    </button>
                  </div>
                </div>

                {/* ── Filtre périodique ── */}
                <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:20, alignItems:"center", background:"#f8fafc", borderRadius:10, padding:"10px 14px" }}>
                  <span style={{ fontSize:11, fontWeight:700, color:"#6b7280", marginRight:4 }}>📅 Période :</span>
                  {PERIODES.map(p => (
                    <button key={p.v} onClick={()=>setOffresPeriode(p.v)}
                      style={{ padding:"5px 14px", borderRadius:20, border:`1.5px solid ${offresPeriode===p.v?BET_COLOR:"#e5e7eb"}`, background:offresPeriode===p.v?BET_COLOR:"#fff", color:offresPeriode===p.v?"#fff":"#374151", fontSize:12, fontWeight:600, cursor:"pointer" }}>
                      {p.l}
                    </button>
                  ))}
                  {offresPeriode === "annee" && (
                    <select value={offresAnnee} onChange={e=>setOffresAnnee(Number(e.target.value))}
                      style={{ padding:"5px 10px", borderRadius:8, border:`1.5px solid ${BET_COLOR}`, fontSize:12, background:"#fff", color:BET_COLOR, fontWeight:700 }}>
                      {Array.from({length:5},(_,i)=>now.getFullYear()-i).map(y=><option key={y} value={y}>{y}</option>)}
                    </select>
                  )}
                  {offresPeriode !== "tout" && (
                    <span style={{ marginLeft:"auto", fontSize:11, color:"#9ca3af" }}>
                      {assignActives.length} inscription{assignActives.length!==1?"s":""} (offres actives) sur {offresAssignations.length} total
                    </span>
                  )}
                </div>

                {offresLoading && <div style={{ textAlign:"center", padding:60, color:"#9ca3af", fontSize:13 }}>Chargement des offres…</div>}

                {!offresLoading && assignOrphans.length > 0 && (
                  <div style={{ marginBottom:16, padding:"10px 16px", borderRadius:10, background:"#fffbeb", border:"1px solid #fde68a", display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                    <span style={{ fontSize:16 }}>⚠️</span>
                    <div style={{ fontSize:12, color:"#92400e", flex:1 }}>
                      <strong>{assignOrphans.length} inscription{assignOrphans.length!==1?"s":""}</strong> liée{assignOrphans.length!==1?"s":""} à des offres modifiées ou désactivées sont exclues des calculs
                      {orphanCA > 0 && <> - <strong>{formatMoney(orphanCA)}</strong> non comptabilisé{assignOrphans.length!==1?"s":""}</>}.
                      <span style={{ color:"#b45309", marginLeft:6 }}>Pour les inclure, réactivez ou renommez les offres concernées dans la configuration.</span>
                    </div>
                    <button onClick={async ()=>{
                      const uniqueLabels = [...new Set(assignOrphans.map(a=>a.inscription_offre||"(vide)"))];
                      await alerte("Offres orphelines :\n" + uniqueLabels.join("\n"));
                    }} style={{ padding:"4px 10px", borderRadius:6, border:"1px solid #fde68a", background:"#fff", color:"#92400e", cursor:"pointer", fontSize:11, fontWeight:600, whiteSpace:"nowrap" }}>
                      Voir les offres concernées
                    </button>
                  </div>
                )}

                {!offresLoading && (
                  <>
                    {/* KPI banner */}
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12, marginBottom:24 }}>
                      {[
                        { l:"Offres actives",  v:totalActives,                   c:"#6366f1", icon:"📦" },
                        { l:"Total inscrits",  v:totalInscrits.toLocaleString(), c:BET_COLOR, icon:"🎓" },
                        { l:"CA total",        v:formatMoney(totalCA),           c:"#059669", icon:"💰" },
                        { l:"Dossiers payés",  v:totalPaye.toLocaleString(),     c:"#22c55e", icon:"✅" },
                        { l:"Taux encaissement", v:totalInscrits>0?`${Math.round(totalPaye/totalInscrits*100)}%`:"-", c:"#f59e0b", icon:"📈" },
                      ].map(k=>(
                        <div key={k.l} style={{ background:"#fff", border:`1px solid ${k.c}20`, borderRadius:12, padding:"14px 16px", display:"flex", flexDirection:"column", gap:4 }}>
                          <div style={{ fontSize:11, color:"#9ca3af", display:"flex", gap:6, alignItems:"center" }}><span>{k.icon}</span>{k.l}</div>
                          <div style={{ fontSize:22, fontWeight:800, color:k.c }}>{k.v}</div>
                        </div>
                      ))}
                    </div>

                    {/* Répartition par catégorie */}
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:10, marginBottom:20 }}>
                      {Object.entries(CAT_META).map(([cat, meta])=>{
                        const s = statsParCat[cat] || { nb:0, ca:0 };
                        return (
                          <div key={cat} style={{ textAlign:"center", background:meta.bg, borderRadius:10, padding:"12px 8px", border:`1px solid ${meta.color}30` }}>
                            <div style={{ fontSize:18, marginBottom:4 }}>{meta.icon}</div>
                            <div style={{ fontSize:11, fontWeight:700, color:meta.color, marginBottom:2 }}>{meta.label}</div>
                            <div style={{ fontSize:20, fontWeight:800, color:meta.color }}>{s.nb}</div>
                            <div style={{ fontSize:10, color:"#9ca3af" }}>inscrits · {formatMoney(s.ca)}</div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Filtres catégorie + recherche */}
                    <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
                      <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                        {[{ v:"tous", l:"Toutes les offres" }, ...Object.entries(CAT_META).map(([v,m])=>({ v, l:`${m.icon} ${m.label}` }))].map(f=>(
                          <button key={f.v} onClick={()=>setOffresCatFiltre(f.v)}
                            style={{ padding:"6px 14px", borderRadius:20, border:`1.5px solid ${offresCatFiltre===f.v?BET_COLOR:"#e5e7eb"}`, background:offresCatFiltre===f.v?BET_COLOR:"#fff", color:offresCatFiltre===f.v?"#fff":"#374151", fontSize:12, fontWeight:600, cursor:"pointer" }}>
                            {f.l}
                          </button>
                        ))}
                      </div>
                      <input value={offresSearchVal} onChange={e=>setOffresSearchVal(e.target.value)}
                        placeholder="Rechercher une offre…"
                        style={{ marginLeft:"auto", padding:"7px 12px", border:"1.5px solid #e5e7eb", borderRadius:8, fontSize:12, width:220 }}
                      />
                    </div>

                    {/* Grille des offres réelles */}
                    {offresCatalogue.length === 0 ? (
                      <div style={{ textAlign:"center", padding:60, color:"#9ca3af", fontSize:13 }}>
                        <div style={{ fontSize:40, marginBottom:12 }}>📦</div>
                        Aucune offre dans le catalogue. Configurez-les dans l'onglet Plateforme → Catalogue.
                      </div>
                    ) : offresFiltrees.length === 0 ? (
                      <div style={{ textAlign:"center", padding:40, color:"#9ca3af", fontSize:13 }}>Aucune offre pour ce filtre.</div>
                    ) : (
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:16, marginBottom:24 }}>
                        {offresFiltrees.map(offre => {
                          const meta   = CAT_META[offre.categorie] || { color:"#6b7280", bg:"#f3f4f6", icon:"📦", label:offre.categorie };
                          const stats  = statsParLabel[normLabel(offre.label)] || { nb:0, ca:0, nbPaye:0 };
                          const txPaye = stats.nb > 0 ? Math.round((stats.nbPaye/stats.nb)*100) : 0;
                          return (
                            <div key={offre.id} style={{ border:`1px solid ${meta.color}25`, borderRadius:12, padding:16, background:"#fff" }}>
                              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                                <span style={{ padding:"3px 10px", borderRadius:20, fontSize:10, fontWeight:700, background:meta.bg, color:meta.color }}>{meta.icon} {meta.label}</span>
                                <span style={{ padding:"3px 10px", borderRadius:20, fontSize:10, fontWeight:700, background:offre.actif!==false?"#d1fae5":"#f1f5f9", color:offre.actif!==false?"#065f46":"#6b7280" }}>
                                  {offre.actif!==false?"● Actif":"● Inactif"}
                                </span>
                              </div>
                              <div style={{ fontWeight:700, fontSize:14, color:"#0f172a", marginBottom:2 }}>
                                {offre.icon && <span style={{ marginRight:6 }}>{offre.icon}</span>}{offre.label}
                              </div>
                              <div style={{ fontSize:12, color:"#059669", fontWeight:600, marginBottom:12 }}>{offre.prix_str}</div>
                              <div style={{ borderTop:"1px solid #f1f5f9", paddingTop:10, display:"flex", flexDirection:"column", gap:6 }}>
                                <div style={{ display:"flex", justifyContent:"space-between", fontSize:12 }}>
                                  <span style={{ color:"#6b7280" }}>Inscrits</span>
                                  <strong style={{ color:"#0f172a" }}>{stats.nb.toLocaleString("fr-FR")}</strong>
                                </div>
                                <div style={{ display:"flex", justifyContent:"space-between", fontSize:12 }}>
                                  <span style={{ color:"#6b7280" }}>CA généré</span>
                                  <strong style={{ color:"#059669" }}>{formatMoney(stats.ca)}</strong>
                                </div>
                                {stats.nb > 0 && (
                                  <div>
                                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, marginBottom:3 }}>
                                      <span style={{ color:"#6b7280" }}>Taux paiement</span>
                                      <span style={{ fontWeight:700, color:txPaye>=80?"#22c55e":txPaye>=50?"#f59e0b":"#ef4444" }}>{txPaye}%</span>
                                    </div>
                                    <ProgressBar value={txPaye} color={txPaye>=80?"#22c55e":txPaye>=50?"#f59e0b":"#ef4444"} />
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
              );
            })()}

            {/* ================= ONGLET CHIFFRE D'AFFAIRES ================= */}
            {activeTab === "ca" && (() => {
              const now = new Date();
              const filtreCA = (p) => {
                const d = p.date_paiement ? new Date(p.date_paiement) : null;
                if (!d) return false;
                if (caPeriode === "semaine") {
                  const debut = new Date(now); debut.setDate(now.getDate() - 7);
                  return d >= debut;
                }
                if (caPeriode === "mois") return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
                if (caPeriode === "trimestre") {
                  const trimestre = Math.floor(now.getMonth() / 3);
                  return d.getFullYear() === now.getFullYear() && Math.floor(d.getMonth() / 3) === trimestre;
                }
                if (caPeriode === "annee") return d.getFullYear() === caAnnee;
                return true; // "tout"
              };

              const pFiltered = paiements.filter(filtreCA);
              const totalRecu     = pFiltered.reduce((s,p)=>s+(p.montant_recu||0),0);
              // montant_du est le total dû répété sur chaque versement d'un même dossier, jamais
              // un montant par versement - le sommer brut double-comptait dès qu'un dossier avait
              // plusieurs versements dans la période filtrée (KPI direction faussé).
              const _duParDossierCA = {};
              pFiltered.forEach(p => {
                const key = p.assignation_id || `noass_${(p.email||p.client||"").toLowerCase()}`;
                _duParDossierCA[key] = Math.max(_duParDossierCA[key]||0, p.montant_du||0);
              });
              const totalDu       = Object.values(_duParDossierCA).reduce((s,v)=>s+v,0);
              const totalAttente  = pFiltered.filter(p=>p.statut==="en_attente").reduce((s,p)=>s+(p.montant_du||0),0);
              const totalPartiel  = pFiltered.filter(p=>p.statut==="partiel").reduce((s,p)=>s+(p.montant_du||0)-(p.montant_recu||0),0);
              const nbTransactions = pFiltered.length;
              const nbPayes = pFiltered.filter(p=>p.statut==="paye").length;
              const txEncaissement = totalDu > 0 ? Math.round((totalRecu/totalDu)*100) : 0;
              const panierMoyen = nbTransactions > 0 ? Math.round(totalRecu / nbTransactions) : 0;

              // CA par mois pour la période annuelle
              const MOIS_FR = ["Jan","Fév","Mar","Avr","Mai","Juin","Juil","Août","Sep","Oct","Nov","Déc"];
              const parMois = MOIS_FR.map((label, idx) => {
                const val = paiements.filter(p => {
                  const d = p.date_paiement ? new Date(p.date_paiement) : null;
                  return d && d.getFullYear() === caAnnee && d.getMonth() === idx;
                }).reduce((s,p)=>s+(p.montant_recu||0),0);
                return { label, val };
              });
              const maxMois = Math.max(...parMois.map(m=>m.val), 1);

              // CA par centre - regroupé par dossier d'abord (max du, somme recu) pour ne pas
              // double-compter montant_du sur les dossiers ayant plusieurs versements.
              const _dossiersParCentre = {};
              pFiltered.forEach(p => {
                const key = p.assignation_id || `noass_${(p.email||p.client||"").toLowerCase()}`;
                const cid = p.centre_id || "__aucun__";
                if (!_dossiersParCentre[key]) _dossiersParCentre[key] = { cid, du:0, recu:0 };
                _dossiersParCentre[key].du = Math.max(_dossiersParCentre[key].du, p.montant_du||0);
                _dossiersParCentre[key].recu += p.montant_recu||0;
              });
              const parCentre = {};
              Object.values(_dossiersParCentre).forEach(d => {
                if (!parCentre[d.cid]) parCentre[d.cid] = { recu: 0, du: 0, nb: 0 };
                parCentre[d.cid].recu += d.recu;
                parCentre[d.cid].du   += d.du;
                parCentre[d.cid].nb   += 1;
              });
              const totalCentreRecu = Object.values(parCentre).reduce((s,c)=>s+c.recu,0);
              const centreEntries = Object.entries(parCentre).sort((a,b)=>b[1].recu-a[1].recu);

              // CA par commercial
              const parCommercial = {};
              pFiltered.forEach(p => {
                const nom = p.commercial_nom || "-";
                if (!parCommercial[nom]) parCommercial[nom] = 0;
                parCommercial[nom] += p.montant_recu || 0;
              });
              const topCommercials = Object.entries(parCommercial).sort((a,b)=>b[1]-a[1]).slice(0,6);

              // CA par mode de paiement
              const parMode = {};
              pFiltered.forEach(p => {
                const m = p.mode_paiement || "Autre";
                if (!parMode[m]) parMode[m] = 0;
                parMode[m] += p.montant_recu || 0;
              });
              const totalMode = Object.values(parMode).reduce((a,b)=>a+b,0);

              const PERIODE_OPTIONS = [
                { key:"semaine",   label:"7 derniers jours" },
                { key:"mois",      label:"Ce mois" },
                { key:"trimestre", label:"Ce trimestre" },
                { key:"annee",     label:"Année" },
                { key:"tout",      label:"Tout" },
              ];

              return (
                <div>
                  {/* Header + Filtres */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20, flexWrap:"wrap", gap:12 }}>
                    <div>
                      <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:"#0f172a" }}>💰 Chiffre d'affaires - Plateforme globale</h2>
                      <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Suivi financier en temps réel de toutes les structures</p>
                    </div>
                    <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                      <div style={{ display:"flex", background:"#f1f5f9", borderRadius:10, padding:3, gap:2 }}>
                        {PERIODE_OPTIONS.map(opt => (
                          <button key={opt.key} onClick={()=>setCaPeriode(opt.key)}
                            style={{ padding:"6px 12px", borderRadius:8, border:"none", fontSize:12, fontWeight:600, cursor:"pointer",
                              background: caPeriode===opt.key ? "#fff" : "transparent",
                              color:      caPeriode===opt.key ? "#0891b2" : "#64748b",
                              boxShadow:  caPeriode===opt.key ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                              transition:"all .15s" }}>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {caPeriode === "annee" && (
                        <select value={caAnnee} onChange={e=>setCaAnnee(Number(e.target.value))}
                          style={{ padding:"7px 10px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, cursor:"pointer", background:"#fff" }}>
                          {[now.getFullYear()-1, now.getFullYear()].map(y=><option key={y} value={y}>{y}</option>)}
                        </select>
                      )}
                    </div>
                  </div>

                  {/* KPIs */}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:24 }}>
                    <div style={{ background:"linear-gradient(135deg,#059669,#10b981)", borderRadius:14, padding:"16px 18px", color:"#fff" }}>
                      <div style={{ fontSize:11, opacity:0.8, fontWeight:600, letterSpacing:"0.05em" }}>TOTAL ENCAISSÉ</div>
                      <div style={{ fontSize:24, fontWeight:800, margin:"6px 0 2px" }}>{formatMoney(totalRecu)}</div>
                      <div style={{ fontSize:11, opacity:0.75 }}>sur {formatMoney(totalDu)} dû</div>
                    </div>
                    <div style={{ background:"linear-gradient(135deg,#0891b2,#0e7490)", borderRadius:14, padding:"16px 18px", color:"#fff" }}>
                      <div style={{ fontSize:11, opacity:0.8, fontWeight:600, letterSpacing:"0.05em" }}>TRANSACTIONS</div>
                      <div style={{ fontSize:24, fontWeight:800, margin:"6px 0 2px" }}>{nbTransactions}</div>
                      <div style={{ fontSize:11, opacity:0.75 }}>{nbPayes} soldées</div>
                    </div>
                    <div style={{ background:"linear-gradient(135deg,#d97706,#f59e0b)", borderRadius:14, padding:"16px 18px", color:"#fff" }}>
                      <div style={{ fontSize:11, opacity:0.8, fontWeight:600, letterSpacing:"0.05em" }}>EN ATTENTE</div>
                      <div style={{ fontSize:24, fontWeight:800, margin:"6px 0 2px" }}>{formatMoney(totalAttente + totalPartiel)}</div>
                      <div style={{ fontSize:11, opacity:0.75 }}>à encaisser</div>
                    </div>
                    <div style={{ background:"linear-gradient(135deg,#7c3aed,#8b5cf6)", borderRadius:14, padding:"16px 18px", color:"#fff" }}>
                      <div style={{ fontSize:11, opacity:0.8, fontWeight:600, letterSpacing:"0.05em" }}>TX ENCAISSEMENT</div>
                      <div style={{ fontSize:24, fontWeight:800, margin:"6px 0 2px" }}>{txEncaissement}%</div>
                      <div style={{ fontSize:11, opacity:0.75 }}>Panier moy. {formatMoney(panierMoyen)}</div>
                    </div>
                  </div>

                  {/* Barre d'encaissement globale */}
                  <div style={{ background:"#f8fafc", borderRadius:14, padding:16, marginBottom:20, border:"1px solid #e5e7eb" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                      <span style={{ fontSize:13, fontWeight:600, color:"#0f172a" }}>Taux d'encaissement global</span>
                      <span style={{ fontSize:13, fontWeight:700, color:txEncaissement>=80?"#059669":txEncaissement>=50?"#d97706":"#dc2626" }}>{txEncaissement}%</span>
                    </div>
                    <div style={{ background:"#e5e7eb", borderRadius:99, height:10, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${txEncaissement}%`, background:`linear-gradient(90deg,${txEncaissement>=80?"#059669":txEncaissement>=50?"#d97706":"#dc2626"},${txEncaissement>=80?"#10b981":txEncaissement>=50?"#f59e0b":"#ef4444"})`, borderRadius:99, transition:"width .5s" }} />
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between", marginTop:6, fontSize:11, color:"#9ca3af" }}>
                      <span>{formatMoney(totalRecu)} reçus</span>
                      <span>{formatMoney(totalDu)} total dû</span>
                    </div>
                  </div>

                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:20 }}>
                    {/* Histogramme mensuel */}
                    <div style={{ background:"#f8fafc", borderRadius:14, padding:16, border:"1px solid #e5e7eb" }}>
                      <div style={{ fontSize:13, fontWeight:700, color:"#0f172a", marginBottom:14 }}>📊 Évolution mensuelle {caAnnee}</div>
                      <div style={{ display:"flex", alignItems:"flex-end", gap:4, height:120 }}>
                        {parMois.map(({label,val}) => (
                          <div key={label} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                            <div style={{ fontSize:9, color:"#94a3b8", fontWeight:600 }}>{val>0?(val/1000).toFixed(0)+"k":""}</div>
                            <div style={{ width:"100%", background: val>0?"#0891b2":"#e5e7eb", borderRadius:"4px 4px 0 0", height:`${maxMois>0?Math.max((val/maxMois)*90,val>0?8:2):2}px`, transition:"height .3s", minHeight:2 }} />
                            <div style={{ fontSize:9, color:"#9ca3af", whiteSpace:"nowrap" }}>{label}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Modes de paiement */}
                    <div style={{ background:"#f8fafc", borderRadius:14, padding:16, border:"1px solid #e5e7eb" }}>
                      <div style={{ fontSize:13, fontWeight:700, color:"#0f172a", marginBottom:14 }}>💳 Répartition par mode</div>
                      {Object.keys(parMode).length === 0 ? (
                        <div style={{ textAlign:"center", padding:"30px 0", color:"#9ca3af", fontSize:12 }}>Aucune donnée sur la période</div>
                      ) : (
                        Object.entries(parMode).sort((a,b)=>b[1]-a[1]).map(([mode,val]) => {
                          const pct = totalMode > 0 ? Math.round((val/totalMode)*100) : 0;
                          return (
                            <div key={mode} style={{ marginBottom:10 }}>
                              <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:4 }}>
                                <span style={{ fontWeight:600, color:"#374151" }}>{mode}</span>
                                <span style={{ fontWeight:700, color:"#0891b2" }}>{formatMoney(val)} <span style={{ color:"#9ca3af", fontWeight:400 }}>({pct}%)</span></span>
                              </div>
                              <div style={{ background:"#e5e7eb", borderRadius:99, height:6, overflow:"hidden" }}>
                                <div style={{ height:"100%", width:`${pct}%`, background:"#0891b2", borderRadius:99 }} />
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* CA par centre */}
                  <div style={{ background:"#f8fafc", borderRadius:14, padding:16, marginBottom:20, border:"1px solid #e5e7eb" }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"#0f172a", marginBottom:14 }}>📍 CA par centre</div>
                    {centreEntries.length === 0 ? (
                      <div style={{ textAlign:"center", padding:"20px 0", color:"#9ca3af", fontSize:12 }}>Aucune donnée sur la période sélectionnée</div>
                    ) : (
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))", gap:10 }}>
                        {centreEntries.map(([cid, stats]) => {
                          const cfg   = CENTRES_BET.find(c => c.id === cid);
                          const label = cfg ? cfg.label.replace(/^BET\s*/,"") : (cid === "__aucun__" ? "Non assigné" : cid);
                          const pct   = totalCentreRecu > 0 ? Math.round((stats.recu / totalCentreRecu) * 100) : 0;
                          const tx    = stats.du > 0 ? Math.round((stats.recu / stats.du) * 100) : 0;
                          return (
                            <div key={cid} style={{ background:"#fff", borderRadius:10, padding:"12px 14px", border:"1px solid #e5e7eb" }}>
                              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                                <div style={{ fontWeight:700, fontSize:12, color:"#0f172a", lineHeight:1.3 }}>📍 {label}</div>
                                <span style={{ fontSize:11, background:"#eff6ff", color:"#1d4ed8", borderRadius:6, padding:"2px 7px", fontWeight:700, flexShrink:0, marginLeft:6 }}>{pct}%</span>
                              </div>
                              <div style={{ fontSize:18, fontWeight:900, color:"#059669", marginBottom:2 }}>{formatMoney(stats.recu)}</div>
                              <div style={{ fontSize:11, color:"#9ca3af", marginBottom:6 }}>sur {formatMoney(stats.du)} dû · {stats.nb} dossier{stats.nb>1?"s":""}</div>
                              <div style={{ background:"#e5e7eb", borderRadius:99, height:5, overflow:"hidden" }}>
                                <div style={{ height:"100%", width:`${tx}%`, background: tx>=80?"#059669":tx>=50?"#f59e0b":"#ef4444", borderRadius:99, transition:"width .4s" }} />
                              </div>
                              <div style={{ fontSize:10, color:"#9ca3af", marginTop:3, textAlign:"right" }}>encaissement {tx}%</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Top commerciaux */}
                  <div style={{ background:"#f8fafc", borderRadius:14, padding:16, border:"1px solid #e5e7eb" }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"#0f172a", marginBottom:14 }}>🏆 Classement commerciaux - Encaissements</div>
                    {topCommercials.length === 0 ? (
                      <div style={{ textAlign:"center", padding:"20px 0", color:"#9ca3af", fontSize:12 }}>Aucune donnée sur la période sélectionnée</div>
                    ) : (
                      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                        {topCommercials.map(([nom, val], idx) => {
                          const pct = topCommercials[0][1] > 0 ? Math.round((val/topCommercials[0][1])*100) : 0;
                          const medals = ["🥇","🥈","🥉"];
                          return (
                            <div key={nom} style={{ display:"flex", alignItems:"center", gap:12, background:"#fff", borderRadius:10, padding:"10px 14px", border:"1px solid #e5e7eb" }}>
                              <span style={{ fontSize:18, flexShrink:0 }}>{medals[idx]||`#${idx+1}`}</span>
                              <div style={{ flex:1 }}>
                                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                                  <span style={{ fontWeight:700, fontSize:13, color:"#0f172a" }}>{nom}</span>
                                  <span style={{ fontWeight:800, fontSize:13, color:"#059669" }}>{formatMoney(val)}</span>
                                </div>
                                <div style={{ background:"#e5e7eb", borderRadius:99, height:5 }}>
                                  <div style={{ height:"100%", width:`${pct}%`, background: idx===0?"#059669":idx===1?"#0891b2":"#8b5cf6", borderRadius:99 }} />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* ================= ONGLET PAIEMENTS ================= */}
            {activeTab === "paiements" && (() => {
              const STATUT_PAI = {
                paye:       { label:"✅ Payé",       bg:"#d1fae5", color:"#065f46" },
                en_attente: { label:"⏳ En attente", bg:"#fef9c3", color:"#854d0e" },
                partiel:    { label:"⚡ Partiel",    bg:"#fff7ed", color:"#92400e" },
                refuse:     { label:"❌ Refusé",     bg:"#fee2e2", color:"#dc2626" },
                rembourse:  { label:"↩ Remboursé",  bg:"#f3e8ff", color:"#7c3aed" },
              };

              const modes   = ["Tous", ...new Set(paiements.map(p=>p.mode_paiement).filter(Boolean))];
              const centres = ["Tous", ...new Set(paiements.map(p=>p.centre_id).filter(Boolean))];

              const filtres = paiements.filter(p => {
                if (paiementFiltre.statut !== "Tous" && p.statut !== paiementFiltre.statut) return false;
                if (paiementFiltre.mode   !== "Tous" && p.mode_paiement !== paiementFiltre.mode) return false;
                if (paiementFiltre.centre !== "Tous" && p.centre_id !== paiementFiltre.centre) return false;
                if (paiementFiltre.search) {
                  const q = paiementFiltre.search.toLowerCase();
                  if (!p.client?.toLowerCase().includes(q) &&
                      !p.email?.toLowerCase().includes(q) &&
                      !p.commercial_nom?.toLowerCase().includes(q) &&
                      !p.ref_transaction?.toLowerCase().includes(q)) return false;
                }
                if (paiementFiltre.dateDebut && p.date_paiement < paiementFiltre.dateDebut) return false;
                if (paiementFiltre.dateFin   && p.date_paiement > paiementFiltre.dateFin)   return false;
                return true;
              });

              const totalPages = Math.max(1, Math.ceil(filtres.length / PAIEMENTS_PER_PAGE));
              const page       = Math.min(paiementPage, totalPages);
              const pageItems  = filtres.slice((page-1)*PAIEMENTS_PER_PAGE, page*PAIEMENTS_PER_PAGE);

              const totalRecu   = filtres.reduce((s,p) => s + (p.montant_recu || 0), 0);
              // montant_du est répété sur chaque versement d'un même dossier - le sommer brut
              // double-comptait dès qu'un dossier avait plusieurs versements dans la liste filtrée.
              const _duParDossierListe = {};
              filtres.forEach(p => {
                const key = p.assignation_id || `noass_${(p.email||p.client||"").toLowerCase()}`;
                _duParDossierListe[key] = Math.max(_duParDossierListe[key]||0, p.montant_du||0);
              });
              const totalDu     = Object.values(_duParDossierListe).reduce((s,v)=>s+v, 0);
              const kpiPaye     = filtres.filter(p=>p.statut==="paye").length;
              const kpiAttente  = filtres.filter(p=>p.statut==="en_attente").length;
              const kpiRefuse   = filtres.filter(p=>p.statut==="refuse").length;

              const exportCSV = () => {
                exporterExcelStyle({
                  nomFichier: `paiements_${new Date().toISOString().slice(0,10)}`,
                  titre: '💳 Paiements',
                  sousTitre: `${filtres.length} paiement${filtres.length>1?'s':''} · Généré le ${new Date().toLocaleDateString('fr-FR')}`,
                  couleurEntete: '16A34A',
                  colonnes: [
                    { label: 'Client', width: 22, bold: true }, { label: 'Email', width: 26 },
                    { label: 'Inscription', width: 20 }, { label: 'Montant dû', width: 14, align: 'right' },
                    { label: 'Montant reçu', width: 14, align: 'right' }, { label: 'Mode', width: 14 },
                    { label: 'Statut', width: 12 }, { label: 'Réf transaction', width: 20 },
                    { label: 'Commercial', width: 20 }, { label: 'Date', width: 14 },
                  ],
                  lignes: filtres.map(p => [
                    p.client||"-", p.email||"-", p.inscription||"-",
                    p.montant_du||0, p.montant_recu||0, p.mode_paiement||"-",
                    p.statut||"-", p.ref_transaction||"-", p.commercial_nom||"-",
                    p.date_paiement||"-",
                  ]),
                });
              };

              const hasFilter = paiementFiltre.statut!=="Tous"||paiementFiltre.mode!=="Tous"||paiementFiltre.centre!=="Tous"||paiementFiltre.search||paiementFiltre.dateDebut||paiementFiltre.dateFin;

              return (
                <>
                {/* ── Sous-onglets Paiements ── */}
                <div style={{ display:"flex", gap:4, marginBottom:20, background:"#f1f5f9", borderRadius:10, padding:4, width:"fit-content" }}>
                  {[
                    { key:"manuel",   label:"💳 Paiements manuels" },
                    { key:"cinetpay", label:"🌐 TouchPoint en ligne", badge: touchpointPaiements.filter(p=>p.statut==="en_attente").length },
                  ].map(st => (
                    <button key={st.key} onClick={() => setPaiementsSubTab(st.key)} style={{
                      padding:"8px 16px", borderRadius:8, border:"none", cursor:"pointer", fontWeight:700, fontSize:13, position:"relative",
                      background: paiementsSubTab===st.key ? "#fff" : "transparent",
                      color:      paiementsSubTab===st.key ? BET_COLOR : "#6b7280",
                      boxShadow:  paiementsSubTab===st.key ? "0 1px 4px rgba(0,0,0,.1)" : "none",
                    }}>
                      {st.label}
                      {st.badge > 0 && <span style={{ position:"absolute", top:4, right:4, background:"#dc2626", color:"#fff", borderRadius:"50%", width:16, height:16, fontSize:9, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800 }}>{st.badge}</span>}
                    </button>
                  ))}
                </div>

                {/* ── Panel Manuel ── */}
                {paiementsSubTab === "manuel" && (
                <div>
                  {/* Header */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
                    <div>
                      <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:"#0f172a" }}>💳 Paiements - Historique global</h2>
                      <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Toutes les transactions de toutes les commerciales</p>
                    </div>
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={fetchPaiements} style={{ padding:"9px 14px", background:"#f1f5f9", color:"#374151", border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:12 }}>🔄 Actualiser</button>
                      <button onClick={exportCSV} style={{ padding:"9px 16px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:12 }}>⬇ Export CSV</button>
                    </div>
                  </div>

                  {/* KPIs */}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:20 }}>
                    <StatCard icon="💰" label="Total reçu (filtré)"  value={formatMoney(totalRecu)}        color="#22c55e" sub={`sur ${formatMoney(totalDu)} dû`} />
                    <StatCard icon="✅" label="Payés"                value={kpiPaye}                       color="#22c55e" sub={`${filtres.length} transactions filtrées`} />
                    <StatCard icon="⏳" label="En attente"           value={kpiAttente}                    color="#f59e0b" sub="À relancer" />
                    <StatCard icon="❌" label="Refusés"              value={kpiRefuse}                     color={BET_RED}  sub="Vérifier" />
                  </div>

                  {/* Répartition par centre */}
                  {paiementFiltre.centre === "Tous" && centres.filter(c=>c!=="Tous").length > 0 && (
                    <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:16 }}>
                      {centres.filter(c=>c!=="Tous").map(c => {
                        const cfg   = CENTRES_BET.find(x=>x.id===c);
                        const label = cfg ? cfg.label.replace(/^BET\s*/,"").split("-")[0].trim() : c;
                        const items = paiements.filter(p=>p.centre_id===c);
                        const total = items.reduce((s,p)=>s+(p.montant_recu||0),0);
                        return (
                          <button key={c} onClick={()=>{ setPaiementFiltre(f=>({...f,centre:c})); setPaiementPage(1); }}
                            style={{ padding:"8px 14px", borderRadius:10, border:"1.5px solid #e2e8f0", background:"#fff", cursor:"pointer", textAlign:"left", minWidth:160 }}>
                            <div style={{ fontSize:11, fontWeight:700, color:"#1d4ed8", marginBottom:2 }}>📍 {label}</div>
                            <div style={{ fontSize:13, fontWeight:900, color:"#0f172a" }}>{formatMoney(total)}</div>
                            <div style={{ fontSize:10, color:"#9ca3af" }}>{items.length} paiement{items.length>1?"s":""}</div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* ── Filtres - modèle pastilles partagé. Le statut est un jeu fermé et
                       passe en pastilles ; mode, centre et dates restent des champs, leurs
                       listes étant dérivées des données. ── */}
                  <BarreFiltres accent={BET_COLOR} style={{ background:"#f8fafc", border:"1px solid #e5e7eb", borderRadius:10, padding:"14px 16px", marginBottom:18 }}>
                    <RangeePastilles label="Statut :" valeur={paiementFiltre.statut}
                      onChange={(v)=>{ setPaiementFiltre(p=>({...p,statut:v})); setPaiementPage(1); }}
                      options={[{ v:"Tous", l:"Tous" }, ...Object.entries(STATUT_PAI).map(([k,v])=>({ v:k, l:v.label }))]}
                      compte={(v)=> v==="Tous" ? paiements.length : paiements.filter(p=>p.statut===v).length} />

                    <RangeeChamps label="Recherche :" resume={`${filtres.length} résultat(s)`}
                      onReinitialiser={hasFilter ? ()=>{ setPaiementFiltre({statut:"Tous",mode:"Tous",centre:"Tous",search:"",dateDebut:"",dateFin:""}); setPaiementPage(1); } : null}>
                      <input placeholder="🔍 Client, email, commerciale, réf…" value={paiementFiltre.search}
                        onChange={e=>{ setPaiementFiltre(p=>({...p,search:e.target.value})); setPaiementPage(1); }}
                        style={{ ...champFiltre, minWidth:260, flex:1 }} />

                      <select value={paiementFiltre.mode} aria-label="Filtrer par mode de paiement"
                        onChange={e=>{ setPaiementFiltre(p=>({...p,mode:e.target.value})); setPaiementPage(1); }}
                        style={{ ...champFiltre, cursor:"pointer" }}>
                        {modes.map(m=><option key={m} value={m}>{m==="Tous"?"Tous modes":m}</option>)}
                      </select>

                      <select value={paiementFiltre.centre} aria-label="Filtrer par centre"
                        onChange={e=>{ setPaiementFiltre(p=>({...p,centre:e.target.value})); setPaiementPage(1); }}
                        style={{ ...champFiltre, cursor:"pointer" }}>
                        <option value="Tous">📍 Tous les centres</option>
                        {centres.filter(c=>c!=="Tous").map(c => {
                          const cfg = CENTRES_BET.find(x=>x.id===c);
                          return <option key={c} value={c}>{cfg ? cfg.label : c}</option>;
                        })}
                      </select>

                      <span style={{ fontSize:12, color:"#64748b", whiteSpace:"nowrap" }}>Du</span>
                      <input type="date" value={paiementFiltre.dateDebut} aria-label="Date de début"
                        onChange={e=>{ setPaiementFiltre(p=>({...p,dateDebut:e.target.value})); setPaiementPage(1); }}
                        style={{ ...champFiltre, cursor:"pointer" }} />
                      <span style={{ fontSize:12, color:"#64748b" }}>au</span>
                      <input type="date" value={paiementFiltre.dateFin} aria-label="Date de fin"
                        onChange={e=>{ setPaiementFiltre(p=>({...p,dateFin:e.target.value})); setPaiementPage(1); }}
                        style={{ ...champFiltre, cursor:"pointer" }} />
                    </RangeeChamps>
                  </BarreFiltres>


                  {/* Contenu */}
                  {loadingPaiements ? (
                    <div style={{ textAlign:"center", padding:"60px 0", color:"#9ca3af", fontSize:13 }}>⏳ Chargement des paiements…</div>
                  ) : paiements.length === 0 ? (
                    <div style={{ textAlign:"center", padding:"60px 0" }}>
                      <div style={{ fontSize:40, marginBottom:10 }}>💳</div>
                      <div style={{ fontWeight:700, color:"#0f172a", marginBottom:6 }}>Aucun paiement enregistré</div>
                      <p style={{ color:"#9ca3af", fontSize:13 }}>Les paiements saisis par les commerciales apparaîtront ici.</p>
                    </div>
                  ) : (
                    <>
                      <div style={{ overflowX:"auto", borderRadius:10, border:"1px solid #e5e7eb" }}>
                        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                          <thead>
                            <tr style={{ background:"#f8fafc" }}>
                              {["Client","Inscription","Montant dû","Montant reçu","Mode","Centre","Commercial","Statut","Date","Réf"].map(h=>(
                                <th key={h} style={{ padding:"10px 12px", textAlign:"left", fontSize:11, fontWeight:700, color:"#9ca3af", borderBottom:"1px solid #e5e7eb", whiteSpace:"nowrap" }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {pageItems.map(p => {
                              const st = STATUT_PAI[p.statut] || { label:p.statut||"-", bg:"#f3f4f6", color:"#374151" };
                              const initiales = (p.client||"?").split(" ").map(x=>x[0]||"").join("").slice(0,2).toUpperCase();
                              const solde = (p.montant_du||0) - (p.montant_recu||0);
                              return (
                                <tr key={p.id} onClick={()=>setPaiementModal(p)} style={{ borderBottom:"1px solid #f1f5f9", background:"#fff", cursor:"pointer", transition:"background .15s" }}
                                  onMouseEnter={e=>e.currentTarget.style.background="#f8fafc"} onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                                  <td style={{ padding:"10px 12px" }}>
                                    <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                                      <div style={{ width:32, height:32, borderRadius:"50%", background:"#8b5cf620", color:"#8b5cf6", fontWeight:800, fontSize:11, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{initiales}</div>
                                      <div>
                                        <div style={{ fontWeight:700, color:"#0f172a" }}>{p.client}</div>
                                        <div style={{ fontSize:11, color:"#9ca3af" }}>{p.email||"-"}</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td style={{ padding:"10px 12px", fontSize:12, color:"#475569", maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.inscription||"-"}</td>
                                  <td style={{ padding:"10px 12px", fontWeight:700, color:"#0f172a" }}>{formatMoney(p.montant_du||0)}</td>
                                  <td style={{ padding:"10px 12px" }}>
                                    <span style={{ fontWeight:700, color: solde===0?"#22c55e":solde>0?"#f59e0b":"#9ca3af" }}>{formatMoney(p.montant_recu||0)}</span>
                                    {solde > 0 && <div style={{ fontSize:10, color:"#f59e0b" }}>reste {formatMoney(solde)}</div>}
                                  </td>
                                  <td style={{ padding:"10px 12px" }}><span style={{ padding:"2px 8px", borderRadius:8, background:"#f3f4f6", fontSize:12 }}>{p.mode_paiement||"-"}</span></td>
                                  <td style={{ padding:"10px 12px" }}>
                                    {p.centre_id ? (
                                      <span style={{ padding:"2px 8px", borderRadius:8, background:"#eff6ff", color:"#1d4ed8", fontSize:11, fontWeight:700 }}>
                                        📍 {CENTRES_BET.find(c=>c.id===p.centre_id)?.label?.replace(/^BET\s*/,"").split("-")[0].trim() || p.centre_id}
                                      </span>
                                    ) : <span style={{ fontSize:11, color:"#9ca3af" }}>-</span>}
                                  </td>
                                  <td style={{ padding:"10px 12px", fontSize:12, color:"#475569", fontWeight:600 }}>{p.commercial_nom}</td>
                                  <td style={{ padding:"10px 12px" }}>
                                    <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, background:st.bg, color:st.color }}>{st.label}</span>
                                  </td>
                                  <td style={{ padding:"10px 12px", fontSize:11, color:"#9ca3af", whiteSpace:"nowrap" }}>
                                    {p.date_paiement ? new Date(p.date_paiement).toLocaleDateString("fr-FR") : "-"}
                                  </td>
                                  <td style={{ padding:"10px 12px", fontSize:11, color:"#94a3b8", fontFamily:"monospace" }}>{p.ref_transaction||"-"}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {filtres.length === 0 && (
                          <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af", fontSize:13 }}>Aucun paiement ne correspond aux filtres.</div>
                        )}
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:16, padding:"0 4px" }}>
                          <span style={{ fontSize:12, color:"#9ca3af" }}>
                            {(page-1)*PAIEMENTS_PER_PAGE+1}–{Math.min(page*PAIEMENTS_PER_PAGE, filtres.length)} sur {filtres.length}
                          </span>
                          <div style={{ display:"flex", gap:4 }}>
                            <button onClick={()=>setPaiementPage(p=>Math.max(1,p-1))} disabled={page===1}
                              style={{ padding:"5px 10px", borderRadius:6, border:"1px solid #e5e7eb", background:page===1?"#f9fafb":"#fff", color:page===1?"#d1d5db":"#374151", cursor:page===1?"not-allowed":"pointer", fontSize:13 }}>‹</button>
                            {Array.from({length:totalPages},(_,i)=>i+1).filter(n=>n===1||n===totalPages||Math.abs(n-page)<=1).reduce((acc,n,idx,arr)=>{
                              if(idx>0&&arr[idx-1]!==n-1) acc.push("…");
                              acc.push(n); return acc;
                            },[]).map((n,i)=> n==="…"
                              ? <span key={`e${i}`} style={{ padding:"5px 6px", fontSize:13, color:"#9ca3af" }}>…</span>
                              : <button key={n} onClick={()=>setPaiementPage(n)}
                                  style={{ padding:"5px 10px", borderRadius:6, border:"1px solid", fontSize:13, cursor:"pointer", fontWeight:n===page?700:400,
                                    background:n===page?BET_COLOR:"#fff", color:n===page?"#fff":"#374151", borderColor:n===page?BET_COLOR:"#e5e7eb" }}>{n}</button>
                            )}
                            <button onClick={()=>setPaiementPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}
                              style={{ padding:"5px 10px", borderRadius:6, border:"1px solid #e5e7eb", background:page===totalPages?"#f9fafb":"#fff", color:page===totalPages?"#d1d5db":"#374151", cursor:page===totalPages?"not-allowed":"pointer", fontSize:13 }}>›</button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                )}

                {paiementsSubTab === "cinetpay" && (() => {
                  const STATUT_CP = {
                    "validé":     { label:"✅ Validé",     bg:"#d1fae5", color:"#065f46" },
                    "en_attente": { label:"⏳ En attente", bg:"#fef9c3", color:"#854d0e" },
                    "échoué":     { label:"❌ Échoué",     bg:"#fee2e2", color:"#dc2626" },
                    "annulé":     { label:"↩ Annulé",     bg:"#f3f4f6", color:"#6b7280" },
                  };
                  const filtered = touchpointPaiements.filter(p => {
                    const matchSearch = !touchpointSearch ||
                      (p.client_nom||"").toLowerCase().includes(touchpointSearch.toLowerCase()) ||
                      (p.client_email||"").toLowerCase().includes(touchpointSearch.toLowerCase()) ||
                      (p.client_telephone||"").includes(touchpointSearch) ||
                      (p.offre_label||"").toLowerCase().includes(touchpointSearch.toLowerCase());
                    const matchStatut = touchpointStatutFiltre === "tous" || p.statut === touchpointStatutFiltre;
                    return matchSearch && matchStatut;
                  });

                  return (
                    <div style={{ display:"flex", flexDirection:"column", gap:18 }}>

                      {/* Header */}
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                        <div>
                          <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:"#0f172a" }}>🌐 Paiements TouchPoint - En ligne</h2>
                          <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Transactions initiées via le formulaire en ligne</p>
                        </div>
                        <button onClick={fetchTouchpointPaiements} style={{ padding:"9px 14px", background:"#f1f5f9", color:"#374151", border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:12 }}>🔄 Actualiser</button>
                      </div>

                      {/* KPIs */}
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14 }}>
                        <StatCard icon="💰" label="Total encaissé" value={formatMoney(touchpointPaiements.filter(p=>p.statut==="validé").reduce((s,p)=>s+(p.montant||0),0))} color="#22c55e" sub="Paiements validés" />
                        <StatCard icon="✅" label="Validés" value={touchpointPaiements.filter(p=>p.statut==="validé").length} color="#22c55e" sub="Confirmés par TouchPoint" />
                        <StatCard icon="⏳" label="En attente" value={touchpointPaiements.filter(p=>p.statut==="en_attente").length} color="#f59e0b" sub="À confirmer" />
                      </div>

                      {/* Filtres */}
                      <div style={{ background:"#f8fafc", border:"1px solid #e5e7eb", borderRadius:10, padding:"14px 16px" }}>
                        <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"center" }}>
                          <input placeholder="🔍 Nom, email, téléphone, offre…" value={touchpointSearch}
                            onChange={e => setTouchpointSearch(e.target.value)}
                            style={{ padding:"8px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, minWidth:260, background:"#fff" }} />
                          <select value={touchpointStatutFiltre} onChange={e => setTouchpointStatutFiltre(e.target.value)}
                            style={{ padding:"8px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, cursor:"pointer", background:"#fff" }}>
                            <option value="tous">Tous statuts</option>
                            <option value="validé">✅ Validés</option>
                            <option value="en_attente">⏳ En attente</option>
                            <option value="échoué">❌ Échoués</option>
                            <option value="annulé">↩ Annulés</option>
                          </select>
                          <span style={{ fontSize:12, color:"#9ca3af", marginLeft:"auto" }}>{filtered.length} résultat{filtered.length!==1?"s":""}</span>
                        </div>
                      </div>

                      {/* Table */}
                      {touchpointLoading ? (
                        <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>Chargement…</div>
                      ) : filtered.length === 0 ? (
                        <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>Aucun paiement trouvé</div>
                      ) : (
                        <div style={{ overflowX:"auto", border:"1px solid #e5e7eb", borderRadius:12, overflow:"hidden" }}>
                          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                            <thead>
                              <tr style={{ background:"#f8fafc" }}>
                                {["Client","Contact","Offre / Niveau","Montant","Statut","Assistante","Date"].map(h => (
                                  <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, fontSize:11, color:"#64748b", borderBottom:"2px solid #e5e7eb", whiteSpace:"nowrap", textTransform:"uppercase", letterSpacing:".03em" }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {filtered.map((p, idx) => {
                                const st = STATUT_CP[p.statut] || { label:p.statut||"-", bg:"#f3f4f6", color:"#374151" };
                                const initiales = ((p.client_prenom||"").charAt(0)+(p.client_nom||"").charAt(0)).toUpperCase();
                                return (
                                  <tr key={p.id} onClick={() => setTouchpointDetailModal(p)}
                                    style={{ background: idx%2===0?"#fff":"#f8fafc", borderBottom:"1px solid #f1f5f9", cursor:"pointer" }}
                                    onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                                    onMouseLeave={e=>e.currentTarget.style.background=idx%2===0?"#fff":"#f8fafc"}>

                                    {/* Client */}
                                    <td style={{ padding:"12px 14px" }}>
                                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                        <div style={{ width:34, height:34, borderRadius:"50%", background:"#e0e7ff", color:"#4f46e5", fontWeight:800, fontSize:12, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{initiales||"?"}</div>
                                        <div>
                                          <div style={{ fontWeight:700, color:"#0f172a" }}>{p.client_prenom} {p.client_nom}</div>
                                          <div style={{ fontSize:11, color:"#9ca3af" }}>{p.client_ville||"-"}</div>
                                        </div>
                                      </div>
                                    </td>

                                    {/* Contact */}
                                    <td style={{ padding:"12px 14px" }}>
                                      <div style={{ fontSize:12, color:"#374151" }}>{p.client_email||"-"}</div>
                                      <div style={{ fontSize:11, color:"#9ca3af" }}>{p.client_telephone||"-"}</div>
                                    </td>

                                    {/* Offre */}
                                    <td style={{ padding:"12px 14px" }}>
                                      <div style={{ fontWeight:600, color:"#0f172a" }}>{p.offre_label||"-"}</div>
                                      {p.offre_formule && <div style={{ fontSize:11, color:"#6b7280" }}>{p.offre_formule}</div>}
                                      {p.niveau && <span style={{ fontSize:10, background:"#ede9fe", color:"#7c3aed", padding:"1px 7px", borderRadius:4, fontWeight:700 }}>{p.niveau}</span>}
                                      {p.offre_type && <span style={{ fontSize:10, color:"#6b7280", background:"#f1f5f9", padding:"1px 6px", borderRadius:4, marginLeft:4 }}>{p.offre_type}</span>}
                                    </td>

                                    {/* Montant */}
                                    <td style={{ padding:"12px 14px" }}>
                                      <div style={{ fontWeight:800, fontSize:14, color:"#15803d" }}>{Number(p.montant||0).toLocaleString("fr-FR")} F</div>
                                    </td>

                                    {/* Statut */}
                                    <td style={{ padding:"12px 14px" }}>
                                      <span style={{ padding:"4px 10px", borderRadius:20, fontSize:11, fontWeight:700, background:st.bg, color:st.color, whiteSpace:"nowrap" }}>{st.label}</span>
                                    </td>

                                    {/* Assistante */}
                                    <td style={{ padding:"12px 14px" }}>
                                      {p.assistante_nom ? (
                                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                                          <div style={{ width:24, height:24, borderRadius:"50%", background:"#dcfce7", color:"#16a34a", fontWeight:800, fontSize:10, display:"flex", alignItems:"center", justifyContent:"center" }}>
                                            {(p.assistante_nom||"?").charAt(0).toUpperCase()}
                                          </div>
                                          <span style={{ fontSize:12, fontWeight:700, color:"#15803d" }}>{p.assistante_nom}</span>
                                        </div>
                                      ) : (
                                        <span style={{ fontSize:11, color:"#9ca3af" }}>-</span>
                                      )}
                                    </td>

                                    {/* Date */}
                                    <td style={{ padding:"12px 14px", whiteSpace:"nowrap", fontSize:12, color:"#64748b" }}>
                                      {p.created_at ? new Date(p.created_at).toLocaleDateString("fr-FR",{day:"2-digit",month:"short",year:"numeric"}) : "-"}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                    </div>
                  );
                })()}

                {/* ── Modal détail paiement TouchPoint ── */}
                {touchpointDetailModal && (() => {
                  const p = touchpointDetailModal;
                  const st = { "validé":{label:"✅ Validé",bg:"#d1fae5",color:"#065f46"}, "en_attente":{label:"⏳ En attente",bg:"#fef9c3",color:"#854d0e"}, "échoué":{label:"❌ Échoué",bg:"#fee2e2",color:"#dc2626"}, "annulé":{label:"↩ Annulé",bg:"#f3f4f6",color:"#6b7280"} }[p.statut] || { label:p.statut||"-", bg:"#f3f4f6", color:"#374151" };
                  const exporterPDF = () => {
                    const win = window.open("","_blank","width=700,height=900");
                    win.document.write(`
                      <html><head><title>Paiement TouchPoint - ${p.client_prenom||""} ${p.client_nom||""}</title>
                      <style>body{font-family:sans-serif;padding:30px;color:#0f172a}h1{font-size:18px;margin-bottom:4px}
                      .grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin:20px 0}
                      .card{background:#f8fafc;border-radius:8px;padding:12px}
                      .label{font-size:11px;color:#9ca3af;margin-bottom:4px}
                      .val{font-size:13px;font-weight:700;word-break:break-all}
                      @media print{button{display:none}}</style></head><body>
                      <h1>🌐 Paiement TouchPoint - ${p.client_prenom||""} ${p.client_nom||""}</h1>
                      <p style="color:#6b7280;font-size:12px">Généré le ${new Date().toLocaleDateString("fr-FR",{day:"2-digit",month:"long",year:"numeric",hour:"2-digit",minute:"2-digit"})}</p>
                      <hr/>
                      <div class="card" style="margin-bottom:16px"><div class="label">Statut</div><div class="val" style="font-size:16px;color:#15803d">${st.label} · ${Number(p.montant||0).toLocaleString("fr-FR")} FCFA</div></div>
                      <h3 style="margin:20px 0 8px">Client</h3>
                      <div class="grid">
                        <div class="card"><div class="label">Nom</div><div class="val">${p.client_prenom||""} ${p.client_nom||""}</div></div>
                        <div class="card"><div class="label">Email</div><div class="val">${p.client_email||"-"}</div></div>
                        <div class="card"><div class="label">Téléphone</div><div class="val">${p.client_telephone||"-"}</div></div>
                        <div class="card"><div class="label">Ville</div><div class="val">${p.client_ville||"-"}</div></div>
                      </div>
                      <h3 style="margin:20px 0 8px">Offre</h3>
                      <div class="grid">
                        <div class="card"><div class="label">Offre</div><div class="val">${p.offre_label||"-"}</div></div>
                        <div class="card"><div class="label">Formule</div><div class="val">${p.offre_formule||"-"}</div></div>
                        <div class="card"><div class="label">Type</div><div class="val">${p.offre_type||"-"}</div></div>
                        <div class="card"><div class="label">Niveau</div><div class="val">${p.niveau||"-"}</div></div>
                      </div>
                      <h3 style="margin:20px 0 8px">Transaction TouchPoint</h3>
                      <div class="grid">
                        <div class="card"><div class="label">Référence (order_number)</div><div class="val">${p.order_number||"-"}</div></div>
                        <div class="card"><div class="label">Token TouchPoint</div><div class="val">${p.payment_token||"-"}</div></div>
                        <div class="card"><div class="label">Mode / opérateur</div><div class="val">${p.payment_mode||"-"}</div></div>
                        <div class="card"><div class="label">Assistante</div><div class="val">${p.assistante_nom||"-"}</div></div>
                        <div class="card"><div class="label">Créé le</div><div class="val">${p.created_at?new Date(p.created_at).toLocaleString("fr-FR"):"-"}</div></div>
                        <div class="card"><div class="label">Mis à jour le</div><div class="val">${p.updated_at?new Date(p.updated_at).toLocaleString("fr-FR"):"-"}</div></div>
                      </div>
                      <p style="margin-top:30px;font-size:11px;color:#9ca3af">BET - Binnie English Training · Document généré automatiquement</p>
                      <button onclick="window.print()" style="margin-top:20px;padding:10px 24px;background:#0f172a;color:#fff;border:none;border-radius:8px;cursor:pointer;font-size:14px">🖨️ Imprimer / Exporter en PDF</button>
                      </body></html>`);
                    win.document.close();
                  };
                  return (
                    <ModaleAnimee ouvert
                      onClicVoile={e => { if (e.target===e.currentTarget) setTouchpointDetailModal(null); }}
                      style={{ position:"fixed", inset:0, background:"rgba(15,23,42,0.6)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
                      panneauStyle={{ background:"#fff", borderRadius:18, width:"100%", maxWidth:640, maxHeight:"90vh", boxShadow:"0 25px 60px rgba(0,0,0,0.25)", display:"flex", flexDirection:"column", overflow:"hidden" }}>

                        {/* Header */}
                        <div style={{ background:BET_GRADIENT, padding:"20px 24px", position:"relative", flexShrink:0 }}>
                          <button onClick={() => setTouchpointDetailModal(null)}
                            style={{ position:"absolute", top:14, right:14, background:"rgba(255,255,255,.2)", border:"none", borderRadius:"50%", width:30, height:30, cursor:"pointer", color:"#fff", fontSize:16 }}>✕</button>
                          <div style={{ fontSize:11, color:"#7dd3fc", fontWeight:600, marginBottom:4 }}>🌐 Détail paiement TouchPoint</div>
                          <div style={{ fontSize:17, fontWeight:800, color:"#fff" }}>{p.client_prenom} {p.client_nom}</div>
                        </div>

                        <div style={{ padding:"22px 24px", overflowY:"auto", flex:1, display:"flex", flexDirection:"column", gap:16 }}>

                          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                            <span style={{ padding:"4px 12px", borderRadius:20, fontSize:12, fontWeight:700, background:st.bg, color:st.color }}>{st.label}</span>
                            <span style={{ fontWeight:900, fontSize:18, color:"#15803d" }}>{Number(p.montant||0).toLocaleString("fr-FR")} FCFA</span>
                          </div>

                          <div style={{ background:"#f8fafc", borderRadius:12, padding:"16px 18px", border:"1px solid #e5e7eb" }}>
                            <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:".05em", marginBottom:10 }}>Client</div>
                            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, fontSize:13 }}>
                              {[
                                ["Nom",       `${p.client_prenom||""} ${p.client_nom||""}`.trim()||"-"],
                                ["Email",     p.client_email||"-"],
                                ["Téléphone", p.client_telephone||"-"],
                                ["Ville",     p.client_ville||"-"],
                                ["Pays",      p.client_pays||"-"],
                              ].map(([l,v]) => (
                                <div key={l}>
                                  <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600 }}>{l}</div>
                                  <div style={{ fontWeight:700, color:"#0f172a", marginTop:1 }}>{v}</div>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div style={{ background:"#f8fafc", borderRadius:12, padding:"16px 18px", border:"1px solid #e5e7eb" }}>
                            <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:".05em", marginBottom:10 }}>Offre</div>
                            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, fontSize:13 }}>
                              {[
                                ["Offre",   p.offre_label||"-"],
                                ["Formule", p.offre_formule||"-"],
                                ["Type",    p.offre_type||"-"],
                                ["Niveau",  p.niveau||"-"],
                              ].map(([l,v]) => (
                                <div key={l}>
                                  <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600 }}>{l}</div>
                                  <div style={{ fontWeight:700, color:"#0f172a", marginTop:1 }}>{v}</div>
                                </div>
                              ))}
                            </div>
                            {p.objectif && (
                              <div style={{ marginTop:12, padding:"10px 12px", background:"#ede9fe", borderRadius:8 }}>
                                <div style={{ fontSize:11, color:"#7c3aed", fontWeight:700, marginBottom:3 }}>🎯 Objectif du client</div>
                                <div style={{ fontSize:12, color:"#4c1d95" }}>{p.objectif}</div>
                              </div>
                            )}
                          </div>

                          <div style={{ background:"#f8fafc", borderRadius:12, padding:"16px 18px", border:"1px solid #e5e7eb" }}>
                            <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:".05em", marginBottom:10 }}>Transaction TouchPoint</div>
                            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, fontSize:13 }}>
                              {[
                                ["Référence (order_number)", p.order_number||"-"],
                                ["Token TouchPoint",         p.payment_token||"-"],
                                ["Mode / opérateur",         p.payment_mode||"-"],
                                ["Assistante",               p.assistante_nom||"-"],
                                ["Créé le",                  p.created_at ? new Date(p.created_at).toLocaleString("fr-FR") : "-"],
                                ["Mis à jour le",             p.updated_at ? new Date(p.updated_at).toLocaleString("fr-FR") : "-"],
                              ].map(([l,v]) => (
                                <div key={l}>
                                  <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600 }}>{l}</div>
                                  <div style={{ fontWeight:700, color:"#0f172a", marginTop:1, wordBreak:"break-all" }}>{v}</div>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div style={{ display:"flex", justifyContent:"flex-end" }}>
                            <button onClick={exporterPDF}
                              style={{ padding:"10px 20px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:9, cursor:"pointer", fontWeight:700, fontSize:13, display:"flex", alignItems:"center", gap:8 }}>
                              📄 Exporter en PDF
                            </button>
                          </div>

                        </div>
                    </ModaleAnimee>
                  );
                })()}

                {/* ── Modal détail paiement ── */}
                {paiementModal && (() => {
                  const pm  = paiementModal;
                  const STATUT_PAI_M = {
                    paye:       { label:"✅ Payé",       bg:"#d1fae5", color:"#065f46" },
                    en_attente: { label:"⏳ En attente", bg:"#fef9c3", color:"#854d0e" },
                    partiel:    { label:"⚡ Partiel",    bg:"#fff7ed", color:"#92400e" },
                    refuse:     { label:"❌ Refusé",     bg:"#fee2e2", color:"#dc2626" },
                    rembourse:  { label:"↩ Remboursé",  bg:"#f3e8ff", color:"#7c3aed" },
                  };
                  const st       = STATUT_PAI_M[pm.statut] || { label:pm.statut||"-", bg:"#f3f4f6", color:"#374151" };
                  const solde    = (pm.montant_du||0) - (pm.montant_recu||0);
                  const pct      = pm.montant_du > 0 ? Math.min(100, Math.round(((pm.montant_recu||0) / pm.montant_du) * 100)) : 0;
                  const initiales = (pm.client||"?").split(" ").map(x=>x[0]||"").join("").slice(0,2).toUpperCase();
                  const centreCfg = CENTRES_BET.find(c=>c.id===pm.centre_id);
                  const centreLabel = centreCfg ? centreCfg.label : (pm.centre_id||null);

                  // Tous les versements du même apprenant (même email ou même assignation_id)
                  const versementsLies = paiements.filter(x =>
                    x.id !== pm.id && (
                      (pm.assignation_id && x.assignation_id === pm.assignation_id) ||
                      (pm.email && x.email === pm.email)
                    )
                  ).sort((a,b) => new Date(b.date_paiement) - new Date(a.date_paiement));

                  const totalVersements = [pm, ...versementsLies].reduce((s,x) => s + (x.montant_recu||0), 0);

                  const printModal = () => {
                    const win = window.open("","_blank","width=700,height=900");
                    win.document.write(`
                      <html><head><title>Reçu - ${pm.client}</title>
                      <style>body{font-family:sans-serif;padding:30px;color:#0f172a}h1{font-size:18px;margin-bottom:4px}
                      .grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin:20px 0}
                      .card{background:#f8fafc;border-radius:8px;padding:12px}
                      .label{font-size:11px;color:#9ca3af;margin-bottom:4px}
                      .val{font-size:13px;font-weight:700}
                      .kpi{text-align:center;padding:16px;border-radius:10px;border:1px solid #e5e7eb}
                      .kpi-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin:16px 0}
                      table{width:100%;border-collapse:collapse;margin-top:12px}
                      th,td{padding:8px 12px;text-align:left;border-bottom:1px solid #e5e7eb;font-size:12px}
                      th{background:#f1f5f9;font-weight:700}
                      @media print{button{display:none}}</style></head><body>
                      <h1>💳 Détail paiement - ${pm.client}</h1>
                      <p style="color:#6b7280;font-size:12px">Généré le ${new Date().toLocaleDateString("fr-FR",{day:"2-digit",month:"long",year:"numeric",hour:"2-digit",minute:"2-digit"})}</p>
                      <hr/>
                      <div class="kpi-grid">
                        <div class="kpi"><div class="label">Montant reçu</div><div class="val" style="font-size:18px;color:#15803d">${formatMoney(pm.montant_recu||0)}</div></div>
                        <div class="kpi"><div class="label">Montant dû</div><div class="val" style="font-size:18px;color:#92400e">${formatMoney(pm.montant_du||0)}</div></div>
                        <div class="kpi"><div class="label">Solde</div><div class="val" style="font-size:18px;color:${solde===0?"#15803d":solde>0?"#ea580c":"#64748b"}">${solde===0?"Soldé":solde>0?`-${formatMoney(solde)}`:`+${formatMoney(Math.abs(solde))}`}</div></div>
                      </div>
                      <div class="grid">
                        <div class="card"><div class="label">Formation / Inscription</div><div class="val">${pm.inscription||"-"}</div></div>
                        <div class="card"><div class="label">Mode de paiement</div><div class="val">${pm.mode_paiement||"-"}</div></div>
                        <div class="card"><div class="label">Date du paiement</div><div class="val">${pm.date_paiement?new Date(pm.date_paiement).toLocaleDateString("fr-FR",{day:"2-digit",month:"long",year:"numeric"}):"-"}</div></div>
                        <div class="card"><div class="label">Référence transaction</div><div class="val">${pm.ref_transaction||"-"}</div></div>
                        <div class="card"><div class="label">Commercial(e)</div><div class="val">${pm.commercial_nom||"-"}</div></div>
                        <div class="card"><div class="label">Centre</div><div class="val">${centreLabel||"-"}</div></div>
                        <div class="card"><div class="label">Email</div><div class="val">${pm.email||"-"}</div></div>
                        <div class="card"><div class="label">Téléphone</div><div class="val">${pm.telephone||"-"}</div></div>
                      </div>
                      ${pm.notes?`<div class="card" style="margin-bottom:16px"><div class="label">Notes</div><div class="val">${pm.notes}</div></div>`:""}
                      ${versementsLies.length>0?`<h3 style="margin:20px 0 8px">Historique des versements</h3><table><thead><tr><th>Date</th><th>Montant reçu</th><th>Mode</th><th>Réf.</th><th>Statut</th></tr></thead><tbody>${[pm,...versementsLies].map(v=>`<tr><td>${v.date_paiement?new Date(v.date_paiement).toLocaleDateString("fr-FR"):"-"}</td><td>${formatMoney(v.montant_recu||0)}</td><td>${v.mode_paiement||"-"}</td><td>${v.ref_transaction||"-"}</td><td>${v.statut||"-"}</td></tr>`).join("")}</tbody></table>`:""}
                      <p style="margin-top:30px;font-size:11px;color:#9ca3af">BET - Binnie English Training · Document généré automatiquement</p>
                      <button onclick="window.print()" style="margin-top:20px;padding:10px 24px;background:#0f172a;color:#fff;border:none;border-radius:8px;cursor:pointer;font-size:14px">🖨️ Imprimer</button>
                      </body></html>`);
                    win.document.close();
                  };

                  return (
                    <ModaleAnimee ouvert
                      onClicVoile={()=>setPaiementModal(null)}
                      style={{ position:"fixed", inset:0, background:"rgba(15,23,42,0.6)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
                      panneauStyle={{ background:"#fff", borderRadius:20, width:"100%", maxWidth:"90vw", maxHeight:"90vh", overflowY:"auto", boxShadow:"0 30px 70px rgba(0,0,0,0.3)" }}>

                        {/* ── Header ── */}
                        <div style={{ background:"linear-gradient(135deg,#0f172a 0%,#0891b2 100%)", borderRadius:"20px 20px 0 0", padding:"22px 26px" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:14 }}>
                            <div style={{ width:54, height:54, borderRadius:"50%", background:"rgba(255,255,255,0.15)", color:"#fff", fontWeight:900, fontSize:20, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, border:"2px solid rgba(255,255,255,0.3)" }}>{initiales}</div>
                            <div style={{ flex:1 }}>
                              <div style={{ fontWeight:900, fontSize:18, color:"#fff" }}>{pm.client}</div>
                              <div style={{ fontSize:12, color:"rgba(255,255,255,0.7)", marginTop:2 }}>{pm.email||"-"} · {pm.telephone||"-"}</div>
                              {centreLabel && <div style={{ fontSize:11, color:"#7dd3fc", marginTop:3, fontWeight:600 }}>📍 {centreLabel}</div>}
                            </div>
                            <span style={{ padding:"5px 14px", borderRadius:20, fontSize:12, fontWeight:700, background:st.bg, color:st.color, flexShrink:0 }}>{st.label}</span>
                            <button onClick={()=>setPaiementModal(null)} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:"50%", width:34, height:34, color:"#fff", cursor:"pointer", fontSize:18, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>✕</button>
                          </div>
                          {/* Barre de progression du paiement */}
                          <div>
                            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                              <span style={{ fontSize:11, color:"rgba(255,255,255,0.7)" }}>Progression du paiement</span>
                              <span style={{ fontSize:12, fontWeight:800, color:pct===100?"#86efac":"#fcd34d" }}>{pct}%</span>
                            </div>
                            <div style={{ height:8, background:"rgba(255,255,255,0.2)", borderRadius:4, overflow:"hidden" }}>
                              <div style={{ height:"100%", width:`${pct}%`, background:pct===100?"#22c55e":pct>=50?"#f59e0b":"#ef4444", borderRadius:4, transition:"width 0.5s" }} />
                            </div>
                            <div style={{ display:"flex", justifyContent:"space-between", marginTop:5 }}>
                              <span style={{ fontSize:11, color:"rgba(255,255,255,0.6)" }}>Reçu : {formatMoney(pm.montant_recu||0)}</span>
                              <span style={{ fontSize:11, color:"rgba(255,255,255,0.6)" }}>Total dû : {formatMoney(pm.montant_du||0)}</span>
                            </div>
                          </div>
                        </div>

                        {/* ── Body ── */}
                        <div style={{ padding:"24px 26px" }}>

                          {/* KPIs financiers */}
                          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:22 }}>
                            <div style={{ background:"#f0fdf4", borderRadius:12, padding:"14px 16px", textAlign:"center", border:"1px solid #bbf7d0" }}>
                              <div style={{ fontSize:10, color:"#16a34a", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:4 }}>Reçu</div>
                              <div style={{ fontSize:20, fontWeight:900, color:"#15803d" }}>{formatMoney(pm.montant_recu||0)}</div>
                            </div>
                            <div style={{ background:"#fefce8", borderRadius:12, padding:"14px 16px", textAlign:"center", border:"1px solid #fde68a" }}>
                              <div style={{ fontSize:10, color:"#b45309", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:4 }}>Dû total</div>
                              <div style={{ fontSize:20, fontWeight:900, color:"#92400e" }}>{formatMoney(pm.montant_du||0)}</div>
                            </div>
                            <div style={{ background:solde===0?"#f0fdf4":solde>0?"#fff7ed":"#f8fafc", borderRadius:12, padding:"14px 16px", textAlign:"center", border:`1px solid ${solde===0?"#bbf7d0":solde>0?"#fed7aa":"#e5e7eb"}` }}>
                              <div style={{ fontSize:10, color:solde===0?"#16a34a":solde>0?"#c2410c":"#475569", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:4 }}>Solde</div>
                              <div style={{ fontSize:20, fontWeight:900, color:solde===0?"#15803d":solde>0?"#ea580c":"#64748b" }}>{solde===0?"Soldé":solde>0?`-${formatMoney(solde)}`:`+${formatMoney(Math.abs(solde))}`}</div>
                            </div>
                          </div>

                          {/* Infos apprenant & paiement */}
                          <div style={{ marginBottom:20 }}>
                            <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:10, display:"flex", alignItems:"center", gap:6 }}>
                              <span style={{ width:3, height:14, background:BET_COLOR, borderRadius:2, display:"inline-block" }}/>
                              Informations apprenant
                            </div>
                            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                              {[
                                ["📧 Email",             pm.email||"-"],
                                ["📞 Téléphone",         pm.telephone||"-"],
                                ["📋 Formation",         pm.inscription||"-"],
                                ["💳 Mode de paiement",  pm.mode_paiement||"-"],
                                ["📅 Date du paiement",  pm.date_paiement ? new Date(pm.date_paiement).toLocaleDateString("fr-FR",{day:"2-digit",month:"long",year:"numeric"}) : "-"],
                                ["🔖 Réf. transaction",  pm.ref_transaction||"-"],
                                ["👤 Commercial(e)",     pm.commercial_nom||"-"],
                                ["📍 Centre",            centreLabel||"-"],
                                ["🏷️ ID paiement",       pm.id||"-"],
                                ["📊 Statut",            st.label],
                              ].map(([label, val]) => (
                                <div key={label} style={{ background:"#f8fafc", borderRadius:10, padding:"10px 14px" }}>
                                  <div style={{ fontSize:10, color:"#9ca3af", fontWeight:600, marginBottom:3 }}>{label}</div>
                                  <div style={{ fontSize:12, fontWeight:700, color:"#0f172a", wordBreak:"break-all" }}>{val}</div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Notes */}
                          {pm.notes && (
                            <div style={{ background:"#fefce8", borderRadius:10, padding:"12px 16px", marginBottom:18, border:"1px solid #fde68a" }}>
                              <div style={{ fontSize:11, color:"#92400e", fontWeight:700, marginBottom:4 }}>📝 Notes</div>
                              <div style={{ fontSize:13, color:"#78350f", lineHeight:1.6 }}>{pm.notes}</div>
                            </div>
                          )}

                          {/* Historique versements liés */}
                          {versementsLies.length > 0 && (
                            <div style={{ marginBottom:20 }}>
                              <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:10, display:"flex", alignItems:"center", gap:6 }}>
                                <span style={{ width:3, height:14, background:"#0891b2", borderRadius:2, display:"inline-block" }}/>
                                Historique des versements ({versementsLies.length + 1} au total · {formatMoney(totalVersements)})
                              </div>
                              <div style={{ border:"1px solid #e5e7eb", borderRadius:10, overflow:"hidden" }}>
                                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                                  <thead>
                                    <tr style={{ background:"#f8fafc" }}>
                                      {["Date","Montant reçu","Mode","Réf.","Statut"].map(h=>(
                                        <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontSize:10, fontWeight:700, color:"#9ca3af", borderBottom:"1px solid #e5e7eb" }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {[pm, ...versementsLies].map((v,i) => {
                                      const stV = STATUT_PAI_M[v.statut] || { label:v.statut||"-", bg:"#f3f4f6", color:"#374151" };
                                      return (
                                        <tr key={v.id} style={{ background:i===0?"#f0f9ff":"#fff", borderBottom:"1px solid #f1f5f9" }}>
                                          <td style={{ padding:"8px 12px", whiteSpace:"nowrap" }}>
                                            {i===0 && <span style={{ fontSize:9, background:"#bae6fd", color:"#0369a1", borderRadius:4, padding:"1px 5px", marginRight:4, fontWeight:700 }}>Ce versement</span>}
                                            {v.date_paiement ? new Date(v.date_paiement).toLocaleDateString("fr-FR") : "-"}
                                          </td>
                                          <td style={{ padding:"8px 12px", fontWeight:700, color:"#15803d" }}>{formatMoney(v.montant_recu||0)}</td>
                                          <td style={{ padding:"8px 12px", color:"#475569" }}>{v.mode_paiement||"-"}</td>
                                          <td style={{ padding:"8px 12px", color:"#9ca3af", fontFamily:"monospace", fontSize:10 }}>{v.ref_transaction||"-"}</td>
                                          <td style={{ padding:"8px 12px" }}><span style={{ padding:"2px 7px", borderRadius:99, fontSize:10, fontWeight:700, background:stV.bg, color:stV.color }}>{stV.label}</span></td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* Preuve image */}
                          {pm.preuve_image && (
                            <div style={{ marginBottom:20 }}>
                              <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:8 }}>🖼️ Preuve de paiement</div>
                              <img src={pm.preuve_image} alt="Preuve" style={{ maxWidth:"100%", borderRadius:12, border:"1px solid #e5e7eb", cursor:"pointer" }}
                                onClick={()=>window.open(pm.preuve_image,"_blank")}
                                onError={e=>{e.currentTarget.style.display="none";}} />
                            </div>
                          )}

                          {/* Actions */}
                          <div style={{ display:"flex", gap:8, marginTop:4 }}>
                            <button onClick={printModal}
                              style={{ flex:1, padding:"11px 0", background:"#0f172a", color:"#fff", border:"none", borderRadius:10, fontSize:13, fontWeight:700, cursor:"pointer" }}>
                              🖨️ Imprimer / Exporter
                            </button>
                            <button onClick={()=>setPaiementModal(null)}
                              style={{ flex:1, padding:"11px 0", background:"#f1f5f9", color:"#374151", border:"none", borderRadius:10, fontSize:13, fontWeight:700, cursor:"pointer" }}>
                              Fermer
                            </button>
                          </div>
                        </div>
                    </ModaleAnimee>
                  );
                })()}
                </>
              );
            })()}


            {/* ================= ONGLET APPRENANTS & PROGRESSION ================= */}
            {activeTab === "suivi_apprenants" && apprenantSubTab === "progression" && (
              <div>
                {/* Sous-onglets */}
                <div style={{ display:"flex", gap:4, marginBottom:20, background:"#f1f5f9", borderRadius:10, padding:4, width:"fit-content" }}>
                  {[{ key:"liste", label:"🎓 Liste apprenants" }, { key:"progression", label:"📈 Progression" }].map(st => (
                    <button key={st.key} onClick={()=>setApprenantSubTab(st.key)}
                      style={{ padding:"7px 16px", borderRadius:8, border:"none", fontSize:13, fontWeight:600, cursor:"pointer",
                        background: apprenantSubTab===st.key ? "#fff" : "transparent",
                        color:      apprenantSubTab===st.key ? "#0891b2" : "#64748b",
                        boxShadow:  apprenantSubTab===st.key ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                        transition:"all .15s" }}>
                      {st.label}
                    </button>
                  ))}
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
                  <div><h2 style={{ margin:0, fontSize:17, fontWeight:700, color:"#0f172a" }}>📈 Progression des apprenants - Vue globale</h2><p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Indicateurs pédagogiques de l'ensemble de la plateforme</p></div>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:24 }}>
                  <StatCard label="Apprenants actifs" value={plateformeStats.total_apprenants ?? "…"} color="#2563eb" icon="🎓" sub="via groupes de coaching" />
                  <StatCard label="Certificats délivrés" value={plateformeStats.total_certifications ?? "…"} color={BET_COLOR} icon="🏅" />
                  <StatCard label="Taux de validation" value={plateformeStats.taux_reussite_certifications != null ? `${plateformeStats.taux_reussite_certifications}%` : "-"} color="#059669" icon="✅" sub={plateformeStats.taux_reussite_certifications == null ? "Aucune certification" : undefined} />
                </div>
                <div style={{ background:"#f8fafc", borderRadius:12, padding:16 }}>
                  <div style={{ fontSize:13, fontWeight:600, marginBottom:12 }}>Répartition des apprenants par niveau CECRL</div>
                  {niveauxApprenants === null ? (
                    <div style={{ textAlign:"center", padding:20, color:"#9ca3af", fontSize:13 }}>⏳ Chargement…</div>
                  ) : Object.keys(niveauxApprenants).length === 0 ? (
                    <div style={{ textAlign:"center", padding:20, color:"#9ca3af", fontSize:13 }}>Aucun apprenant avec un niveau renseigné</div>
                  ) : (
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                      {Object.entries(niveauxApprenants).sort(([a],[b])=>a.localeCompare(b)).map(([niveau,count]) => (
                        <div key={niveau} style={{ flex:1, minWidth:60, textAlign:"center", background:"#fff", borderRadius:8, padding:10, border:"1px solid #e5e7eb" }}>
                          <div style={{ fontWeight:700, fontSize:13, color:"#374151" }}>{niveau}</div>
                          <div style={{ fontSize:18, fontWeight:800, color:BET_COLOR }}>{count}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ================= ONGLET PROFESSEURS ================= */}
            {activeTab === "coachs" && (() => {
              const actifs    = adminCoachsReel.filter(c=>c.actif).length;
              const inactifs  = adminCoachsReel.filter(c=>!c.actif).length;
              const filtered  = adminCoachsReel.filter(c => {
                if (!adminCoachSearch.trim()) return true;
                const q = adminCoachSearch.toLowerCase();
                return `${c.prenom} ${c.nom} ${c.email} ${c.coach_info?.filiere||""} ${c.coach_info?.matricule||""}`.toLowerCase().includes(q);
              });

              return (
                <div>
                  {/* Header */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20, flexWrap:"wrap", gap:12 }}>
                    <div>
                      <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:"#0f172a" }}>👨‍🏫 Gestion des Coachs</h2>
                      <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Formateurs BET enregistrés dans la plateforme</p>
                    </div>
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={fetchAdminCoachsReel} style={{ padding:"8px 14px", background:BET_LIGHT, color:BET_COLOR, border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>🔄 Actualiser</button>
                      <button onClick={()=>{
                        if (!adminCoachsReel.length) return;
                        exporterExcelStyle({
                          nomFichier: `coachs_${new Date().toISOString().slice(0,10)}`,
                          titre: '👨‍🏫 Gestion des coachs',
                          sousTitre: `${adminCoachsReel.length} coach${adminCoachsReel.length>1?'s':''} · Généré le ${new Date().toLocaleDateString('fr-FR')}`,
                          couleurEntete: BET_COLOR.replace('#',''),
                          colonnes: [
                            { label: 'Prénom', width: 18 }, { label: 'Nom', width: 18, bold: true },
                            { label: 'Email', width: 26 }, { label: 'Téléphone', width: 16 },
                            { label: 'Filière', width: 16 }, { label: 'Matricule', width: 14 },
                            { label: 'Contrats actifs', width: 14, align: 'center' }, { label: 'Statut', width: 12 },
                          ],
                          lignes: adminCoachsReel.map(c => [c.prenom||"", c.nom||"", c.email||"", c.telephone||"", c.coach_info?.filiere||"", c.coach_info?.matricule||"", c.coach_info?.nb_contrats_actifs||0, c.actif?"Actif":"Inactif"]),
                        });
                      }} style={{ padding:"8px 14px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>⬇ Exporter Excel</button>
                    </div>
                  </div>

                  {/* KPIs */}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12, marginBottom:20 }}>
                    {[
                      { icon:"👨‍🏫", label:"Total coachs",     value: adminCoachsReelLoading?"…":adminCoachsReel.length, color:BET_COLOR },
                      { icon:"✅",   label:"Actifs",           value: adminCoachsReelLoading?"…":actifs,                 color:"#16a34a", sub:`${inactifs} inactif${inactifs!==1?"s":""}` },
                      { icon:"📋",   label:"Avec contrats",    value: adminCoachsReelLoading?"…":adminCoachsReel.filter(c=>(c.coach_info?.nb_contrats_actifs||0)>0).length, color:"#7c3aed" },
                      { icon:"🏆",   label:"Avec certif.",     value: adminCoachsReelLoading?"…":adminCoachsReel.filter(c=>c.coach_info?.certifications?.length>0).length, color:"#f59e0b" },
                    ].map((k,i) => <StatCard key={i} {...k} />)}
                  </div>

                  {/* Barre recherche */}
                  <div style={{ marginBottom:16 }}>
                    <input value={adminCoachSearch} onChange={e=>setAdminCoachSearch(e.target.value)}
                      placeholder="🔍 Rechercher par nom, email, filière, matricule…"
                      style={{ width:"100%", padding:"10px 14px", border:"1.5px solid #e2e8f0", borderRadius:9, fontSize:13, outline:"none", boxSizing:"border-box" }} />
                  </div>

                  {/* Liste */}
                  {adminCoachsReelLoading ? (
                    <div style={{ textAlign:"center", padding:60, color:"#9ca3af" }}>Chargement des coachs…</div>
                  ) : filtered.length === 0 ? (
                    <div style={{ textAlign:"center", padding:60, background:"#fff", borderRadius:12, border:"1.5px dashed #e2e8f0", color:"#9ca3af" }}>
                      <div style={{ fontSize:36, marginBottom:10 }}>👨‍🏫</div>
                      <div style={{ fontWeight:600 }}>{adminCoachSearch ? "Aucun résultat" : "Aucun coach enregistré"}</div>
                      <div style={{ fontSize:12, marginTop:4 }}>Ajoutez des coachs depuis l'onglet Utilisateurs</div>
                    </div>
                  ) : (
                    <div style={{ background:"#fff", borderRadius:14, overflow:"hidden", border:"1px solid #e2e8f0", boxShadow:"0 2px 8px rgba(0,0,0,.04)" }}>
                      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                        <thead>
                          <tr style={{ background:"#f8fafc" }}>
                            {["Coach","Filière / Matricule","Email","Téléphone","Contrats","Certifications","Statut",""].map(h=>(
                              <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:"#9ca3af", borderBottom:"1px solid #e5e7eb", whiteSpace:"nowrap" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.map((coach, idx) => {
                            const initiales = ((coach.prenom?.[0]||"")+(coach.nom?.[0]||"")).toUpperCase() || "CO";
                            const photo     = coach.avatar_url || coach.coach_info?.photo_url;
                            const certs     = (() => {
                              const c = coach.coach_info?.certifications;
                              return Array.isArray(c) ? c : (typeof c==="string" ? c.split(",").map(s=>s.trim()).filter(Boolean) : []);
                            })();
                            return (
                              <tr key={coach.id} style={{ borderBottom:"1px solid #f1f5f9", background: idx%2===0?"#fff":"#fafafa" }}>
                                <td style={{ padding:"11px 14px" }}>
                                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                                    {photo
                                      ? <img src={photo} alt={coach.prenom} style={{ width:38, height:38, borderRadius:"50%", objectFit:"cover", border:`2px solid ${BET_COLOR}30`, flexShrink:0 }} />
                                      : <div style={{ width:38, height:38, borderRadius:"50%", background:BET_COLOR+"18", color:BET_COLOR, fontWeight:800, fontSize:13, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{initiales}</div>
                                    }
                                    <div>
                                      <div style={{ fontWeight:700, color:"#0f172a" }}>{coach.prenom} {coach.nom}</div>
                                      <div style={{ fontSize:11, color:"#9ca3af" }}>{coach.coach_info?.lieu_habitation || "-"}</div>
                                    </div>
                                  </div>
                                </td>
                                <td style={{ padding:"11px 14px" }}>
                                  <div style={{ fontSize:13, fontWeight:600, color:"#475569" }}>{coach.coach_info?.filiere || "-"}</div>
                                  {coach.coach_info?.matricule && <div style={{ fontSize:11, color:"#9ca3af" }}>🪪 {coach.coach_info.matricule}</div>}
                                </td>
                                <td style={{ padding:"11px 14px", fontSize:12, color:"#475569" }}>{coach.email}</td>
                                <td style={{ padding:"11px 14px", fontSize:12, color:"#475569" }}>{coach.telephone || "-"}</td>
                                <td style={{ padding:"11px 14px", textAlign:"center" }}>
                                  <span style={{ fontWeight:800, fontSize:15, color:(coach.coach_info?.nb_contrats_actifs||0)>0?BET_COLOR:"#9ca3af" }}>
                                    {coach.coach_info?.nb_contrats_actifs || 0}
                                  </span>
                                </td>
                                <td style={{ padding:"11px 14px", maxWidth:200 }}>
                                  {certs.length > 0
                                    ? <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                                        {certs.map((c,i)=><span key={i} style={{ padding:"2px 8px", borderRadius:99, background:"#ede9fe", color:"#7c3aed", fontSize:10, fontWeight:700 }}>🏆 {c}</span>)}
                                      </div>
                                    : <span style={{ fontSize:11, color:"#9ca3af" }}>-</span>
                                  }
                                </td>
                                <td style={{ padding:"11px 14px" }}>
                                  <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700,
                                    background: coach.actif?"#d1fae5":"#fee2e2",
                                    color:       coach.actif?"#065f46":"#dc2626" }}>
                                    {coach.actif?"✅ Actif":"❌ Inactif"}
                                  </span>
                                </td>
                                <td style={{ padding:"11px 14px" }}>
                                  <button onClick={()=>openCoachModal(coach)}
                                    style={{ padding:"5px 12px", background:BET_LIGHT, color:BET_COLOR, border:"none", borderRadius:6, cursor:"pointer", fontWeight:700, fontSize:12, whiteSpace:"nowrap" }}>
                                    Détail →
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div style={{ padding:"8px 14px", fontSize:11, color:"#9ca3af", background:"#fafafa", borderTop:"1px solid #f1f5f9" }}>
                        {filtered.length} coach{filtered.length!==1?"s":""} affiché{filtered.length!==1?"s":""}
                        {adminCoachSearch && ` - filtre : "${adminCoachSearch}"`}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ══ ONGLET COURS (Cours privés + Groupes de cours) ══ */}
            {activeTab === "cours" && (
              <div>
                {/* Sous-onglets */}
                <div style={{ display:"flex", gap:4, marginBottom:20, background:"#f1f5f9", borderRadius:10, padding:4, width:"fit-content" }}>
                  {[
                    { k:"groupes",     l:"👥 Groupes cours" },
                    { k:"cours_prives", l:"🎯 Cours privés" },
                  ].map(st=>(
                    <button key={st.k} onClick={()=>{ setCoursSubTab(st.k); setAdminSelectedGroupe(null); }}
                      style={{ padding:"7px 18px", borderRadius:8, border:"none", fontSize:13, fontWeight:600, cursor:"pointer",
                        background:coursSubTab===st.k?"#fff":"transparent",
                        color:coursSubTab===st.k?BET_COLOR:"#64748b",
                        boxShadow:coursSubTab===st.k?"0 1px 4px rgba(0,0,0,0.1)":"none",
                        transition:"all .15s" }}>
                      {st.l}
                    </button>
                  ))}
                </div>

                {/* ── Sous-onglet : Groupes de cours ── */}
                {coursSubTab === "groupes" && (() => {
                  const isGroupePrive = g => {
                    if (g.type_cours === "cours_prive" || g.type_cours === "domicile" || g.type_cours === "prive") return true;
                    const fil = (g.filiere||"").toLowerCase();
                    if (fil.includes("privé") || fil.includes("prive") || fil.includes("coaching privé") || fil.includes("cours privé")) return true;
                    return false;
                  };
                  // On n'affiche que les vrais cours en groupe (pas les cours privés qui ont leur propre sous-onglet)
                  const groupesSansPrives = adminGroupes.filter(g => !isGroupePrive(g));
                  const groupesFiltres = groupesSansPrives.filter(g => {
                    if (adminFiltreCoach !== "tous" && (g.coach_nom||g.nom_coach||"Sans coach") !== adminFiltreCoach) return false;
                    if (adminFiltreTypeGroupe === "en_ligne"  && g.type_cours !== "en_ligne")  return false;
                    if (adminFiltreTypeGroupe === "presentiel" && g.type_cours === "en_ligne") return false;
                    if (adminGroupeSearch) {
                      const q = adminGroupeSearch.toLowerCase();
                      if (!(g.nom||"").toLowerCase().includes(q) && !(g.filiere||"").toLowerCase().includes(q) && !(g.coach_nom||g.nom_coach||"").toLowerCase().includes(q)) return false;
                    }
                    return true;
                  });
                  const grpTotalPages = Math.max(1, Math.ceil(groupesFiltres.length / GROUPES_PER_PAGE));
                  const grpPage = Math.min(adminGroupePage, grpTotalPages - 1);
                  const grpItems = groupesFiltres.slice(grpPage * GROUPES_PER_PAGE, (grpPage + 1) * GROUPES_PER_PAGE);
                  return (
                  <div>
                    {/* Header + search */}
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16, flexWrap:"wrap", gap:10 }}>
                      <div>
                        <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:"#0f172a" }}>👥 Groupes de cours - Vue globale</h2>
                        <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>{groupesFiltres.length} groupe(s) en groupe · Page {grpPage+1}/{grpTotalPages}</p>
                      </div>
                      <input value={adminGroupeSearch} onChange={e=>{ setAdminGroupeSearch(e.target.value); setAdminGroupePage(0); }}
                        placeholder="Rechercher nom, filière, coach…"
                        style={{ padding:"8px 12px", border:"1.5px solid #e5e7eb", borderRadius:8, fontSize:12, width:240, outline:"none" }} />
                    </div>

                    {/* Filtre par type de cours */}
                    {groupesSansPrives.length > 0 && (
                      <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:10 }}>
                        {[
                          { v:"tous",       l:"Tous",          n: groupesSansPrives.length },
                          { v:"en_ligne",   l:"💻 En ligne",   n: groupesSansPrives.filter(g=>g.type_cours==="en_ligne").length },
                          { v:"presentiel", l:"🏢 Présentiel", n: groupesSansPrives.filter(g=>g.type_cours==="presentiel"||g.type_cours==="centre").length },
                          { v:"hybride",    l:"⚡ Hybride",    n: groupesSansPrives.filter(g=>g.type_cours==="hybride").length },
                        ].map(t=>(
                          <button key={t.v} onClick={()=>{ setAdminFiltreTypeGroupe(t.v); setAdminGroupePage(0); }}
                            style={{ padding:"4px 12px", borderRadius:20, border:"1.5px solid", fontSize:11, fontWeight:600, cursor:"pointer",
                              background: adminFiltreTypeGroupe===t.v ? BET_COLOR : "#fff",
                              color:      adminFiltreTypeGroupe===t.v ? "#fff"    : "#374151",
                              borderColor:adminFiltreTypeGroupe===t.v ? BET_COLOR : "#e5e7eb" }}>
                            {t.l} <span style={{ marginLeft:3, fontWeight:800, opacity:.75 }}>{t.n}</span>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Filtre par coach */}
                    {groupesSansPrives.length > 0 && (
                      <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:14 }}>
                        {["tous", ...Array.from(new Set(groupesSansPrives.map(g=>g.coach_nom||g.nom_coach||"Sans coach")))].map(coach=>(
                          <button key={coach} onClick={()=>{ setAdminFiltreCoach(coach); setAdminGroupePage(0); }}
                            style={{ padding:"4px 12px", borderRadius:20, border:"1.5px solid", fontSize:11, fontWeight:600, cursor:"pointer",
                              background:adminFiltreCoach===coach?BET_COLOR:"#fff",
                              color:adminFiltreCoach===coach?"#fff":"#374151",
                              borderColor:adminFiltreCoach===coach?BET_COLOR:"#e5e7eb" }}>
                            {coach==="tous"?"Tous les coachs":coach}
                          </button>
                        ))}
                      </div>
                    )}

                    {adminGroupesLoading && <div style={{ textAlign:"center", padding:40, color:"#9ca3af", fontSize:13 }}>Chargement des groupes…</div>}
                    {!adminGroupesLoading && groupesSansPrives.length === 0 && (
                      <div style={{ textAlign:"center", padding:60, color:"#9ca3af", fontSize:13 }}>Aucun groupe de cours trouvé.</div>
                    )}
                    {!adminGroupesLoading && groupesSansPrives.length > 0 && (
                      <div style={{ overflowX:"auto", borderRadius:12, border:"1px solid #e5e7eb", background:"#fff" }}>
                        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                          <thead>
                            <tr style={{ background:"#f8fafc" }}>
                              {["Nom du groupe","Filière","Niveau","Type","Coach","Apprenants","Statut"].map((h,i)=>(
                                <th key={i} style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, color:"#374151", fontSize:11, borderBottom:"2px solid #e5e7eb", whiteSpace:"nowrap" }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {grpItems.length === 0 && (
                              <tr><td colSpan={7} style={{ textAlign:"center", padding:32, color:"#9ca3af", fontSize:12 }}>Aucun groupe pour ces filtres.</td></tr>
                            )}
                            {grpItems.map((g, idx) => (
                              <tr key={g.id}
                                onClick={()=>fetchAdminGroupeDetail(g)}
                                style={{ background:idx%2===0?"#fff":"#fafafa", borderBottom:"1px solid #f1f5f9", cursor:"pointer", transition:"background .12s" }}
                                onMouseEnter={e=>e.currentTarget.style.background="#eff6ff"}
                                onMouseLeave={e=>e.currentTarget.style.background=idx%2===0?"#fff":"#fafafa"}>
                                <td style={{ padding:"11px 14px", fontWeight:700, color:"#0f172a" }}>{g.nom}</td>
                                <td style={{ padding:"11px 14px", color:"#374151" }}>{g.filiere||"-"}</td>
                                <td style={{ padding:"11px 14px", color:"#374151" }}>{g.niveau||"-"}</td>
                                <td style={{ padding:"11px 14px" }}>
                                  {isGroupePrive(g)
                                    ? <span style={{ padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:700, background:"#fef3c7", color:"#92400e" }}>🎯 Cours privé</span>
                                    : g.type_cours==="en_ligne"
                                      ? <span style={{ padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:700, background:"#ede9fe", color:"#7c3aed" }}>💻 En ligne</span>
                                      : <span style={{ padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:700, background:"#e0f2fe", color:BET_COLOR }}>🏢 Présentiel</span>
                                  }
                                </td>
                                <td style={{ padding:"11px 14px", color:"#374151" }}>{g.coach_nom||g.nom_coach||"-"}</td>
                                <td style={{ padding:"11px 14px", color:"#374151", fontWeight:600 }}>{g.nb_apprenants||0}</td>
                                <td style={{ padding:"11px 14px" }}>
                                  <span style={{ padding:"2px 10px", borderRadius:99, fontSize:10, fontWeight:700,
                                    color:g.statut==="actif"?"#166534":"#6b7280",
                                    background:g.statut==="actif"?"#dcfce7":"#f1f5f9" }}>
                                    {g.statut==="actif"?"Actif":"Inactif"}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Pagination */}
                    {grpTotalPages > 1 && (
                      <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:6, marginTop:14 }}>
                        <button onClick={()=>setAdminGroupePage(p=>Math.max(0,p-1))} disabled={grpPage===0}
                          style={{ padding:"5px 12px", borderRadius:7, border:"1.5px solid #e5e7eb", background:"#fff", color:grpPage===0?"#9ca3af":"#374151", cursor:grpPage===0?"default":"pointer", fontWeight:600, fontSize:12 }}>
                          ←
                        </button>
                        {Array.from({length:grpTotalPages},(_,i)=>i).filter(i=>Math.abs(i-grpPage)<=2||i===0||i===grpTotalPages-1).map((i,idx,arr)=>(
                          <React.Fragment key={i}>
                            {idx>0 && arr[idx-1]!==i-1 && <span style={{ color:"#9ca3af", fontSize:12 }}>…</span>}
                            <button onClick={()=>setAdminGroupePage(i)}
                              style={{ padding:"5px 10px", borderRadius:7, border:"1.5px solid", fontSize:12, fontWeight:700, cursor:"pointer",
                                background:grpPage===i?BET_COLOR:"#fff",
                                color:grpPage===i?"#fff":"#374151",
                                borderColor:grpPage===i?BET_COLOR:"#e5e7eb" }}>
                              {i+1}
                            </button>
                          </React.Fragment>
                        ))}
                        <button onClick={()=>setAdminGroupePage(p=>Math.min(grpTotalPages-1,p+1))} disabled={grpPage===grpTotalPages-1}
                          style={{ padding:"5px 12px", borderRadius:7, border:"1.5px solid #e5e7eb", background:"#fff", color:grpPage===grpTotalPages-1?"#9ca3af":"#374151", cursor:grpPage===grpTotalPages-1?"default":"pointer", fontWeight:600, fontSize:12 }}>
                          →
                        </button>
                      </div>
                    )}

                    {/* ── MODAL DÉTAIL GROUPE (overlay) ── */}
                    {adminSelectedGroupe && (
                      <ModaleAnimee ouvert
                        style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:2000, display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"24px 16px", overflowY:"auto" }}
                        panneauStyle={{ background:"#fff", borderRadius:18, width:"100%", maxWidth:"90vw", boxShadow:"0 20px 60px rgba(0,0,0,0.25)", overflow:"hidden" }}>
                          {/* ── EN-TÊTE GROUPE ── */}
                          {(() => {
                            const g = adminSelectedGroupe;
                            const horaires = Array.isArray(g.horaire) ? g.horaire : (typeof g.horaire==="string" ? (() => { try { return JSON.parse(g.horaire); } catch { return []; } })() : []);
                            const typeLabel = libelleTypeCours(g);
                            const nbActifs = adminGroupeDetail.apprenants.filter(a=>a.statut!=="retire").length || g.nb_apprenants || 0;
                            return (
                              <div style={{ background:"linear-gradient(135deg,#0f172a 0%,#1e3a5f 100%)", padding:"22px 24px 18px", position:"relative" }}>
                                {/* Bouton fermer */}
                                <button onClick={()=>{ setAdminSelectedGroupe(null); setAdminGroupeDetail({ apprenants:[], fichiers:[] }); setAdminGroupeApprenantDetail(null); }}
                                  style={{ position:"absolute", top:14, right:14, background:"rgba(255,255,255,0.12)", border:"none", borderRadius:"50%", width:30, height:30, cursor:"pointer", color:"#fff", fontSize:17, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>

                                {/* Nom + badge statut */}
                                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                                  <h2 style={{ margin:0, fontSize:20, fontWeight:900, color:"#fff" }}>{g.nom}</h2>
                                  <span style={{ padding:"2px 10px", borderRadius:99, fontSize:10, fontWeight:700,
                                    color:g.statut==="actif"?"#166534":"#6b7280",
                                    background:g.statut==="actif"?"#dcfce7":"#f1f5f9" }}>
                                    {g.statut==="actif"?"Actif":"Inactif"}
                                  </span>
                                </div>

                                {/* Infos badges ligne 1 */}
                                <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:10 }}>
                                  {g.niveau && (
                                    <span style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:8, background:"rgba(255,255,255,0.12)", color:"#e0f2fe", fontSize:12, fontWeight:700 }}>
                                      📊 {g.niveau}
                                    </span>
                                  )}
                                  {g.filiere && (
                                    <span style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:8, background:"rgba(255,255,255,0.12)", color:"#ddd6fe", fontSize:12, fontWeight:700 }}>
                                      🎓 {g.filiere}
                                    </span>
                                  )}
                                  <span style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:8, background:"rgba(255,255,255,0.12)", color:"#bbf7d0", fontSize:12, fontWeight:700 }}>
                                    👤 {nbActifs} apprenant{nbActifs!==1?"s":""} actif{nbActifs!==1?"s":""}
                                  </span>
                                  {g.date_debut && (
                                    <span style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:8, background:"rgba(255,255,255,0.12)", color:"#fde68a", fontSize:12, fontWeight:700 }}>
                                      📅 Début : {new Date(g.date_debut).toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric"})}
                                    </span>
                                  )}
                                  <span style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:8, background:"rgba(255,255,255,0.12)", color:"#a5f3fc", fontSize:12, fontWeight:700 }}>
                                    {typeLabel}
                                  </span>
                                  {(g.coach_nom||g.nom_coach) && (
                                    <span style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:8, background:"rgba(255,255,255,0.12)", color:"#fca5a5", fontSize:12, fontWeight:700 }}>
                                      👨‍🏫 {g.coach_nom||g.nom_coach}
                                    </span>
                                  )}
                                </div>

                                {/* Horaires chips */}
                                {horaires.length > 0 && (
                                  <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                                    {horaires.map((h,i)=>(
                                      <span key={i} style={{ padding:"3px 10px", borderRadius:6, background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.15)", color:"#fff", fontSize:11, fontWeight:600 }}>
                                        {h.jour} {hhmm(h.debut)}–{hhmm(h.fin)}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                          <div style={{ padding:"20px 24px 24px" }}>

                          {/* ── Carte infos Coach + Centre ── */}
                          {(() => {
                            const g = adminSelectedGroupe;
                            if (!g.coach_nom && !g.centre_nom) return null;
                            return (
                              <div style={{ display:"grid", gridTemplateColumns: g.coach_nom && g.centre_nom ? "1fr 1fr" : "1fr", gap:12, marginBottom:20 }}>
                                {g.coach_nom && (
                                  <div style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", borderRadius:12, background:"linear-gradient(135deg,#f0fdf4,#dcfce7)", border:"1px solid #86efac" }}>
                                    <div style={{ width:44, height:44, borderRadius:"50%", background:"linear-gradient(135deg,#16a34a,#22c55e)", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, fontWeight:800, flexShrink:0 }}>
                                      {g.coach_photo_url
                                        ? <img src={g.coach_photo_url} alt="" style={{ width:44, height:44, borderRadius:"50%", objectFit:"cover" }} />
                                        : (g.coach_nom[0]||"C").toUpperCase()}
                                    </div>
                                    <div style={{ minWidth:0 }}>
                                      <div style={{ fontSize:10, fontWeight:700, color:"#16a34a", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:2 }}>👨‍🏫 Coach assigné</div>
                                      <div style={{ fontSize:14, fontWeight:800, color:"#0f172a", marginBottom:2 }}>{g.coach_nom}</div>
                                      {g.coach_email     && <div style={{ fontSize:11, color:"#475569" }}>✉️ {g.coach_email}</div>}
                                      {g.coach_telephone && <div style={{ fontSize:11, color:"#0891b2", fontWeight:600 }}>📞 {g.coach_telephone}</div>}
                                    </div>
                                  </div>
                                )}
                                {g.centre_nom && (
                                  <div style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", borderRadius:12, background:"linear-gradient(135deg,#eff6ff,#dbeafe)", border:"1px solid #93c5fd" }}>
                                    <div style={{ width:44, height:44, borderRadius:12, background:"linear-gradient(135deg,#1d4ed8,#3b82f6)", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>🏢</div>
                                    <div style={{ minWidth:0 }}>
                                      <div style={{ fontSize:10, fontWeight:700, color:"#1d4ed8", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:2 }}>Centre</div>
                                      <div style={{ fontSize:14, fontWeight:800, color:"#0f172a", marginBottom:2 }}>{g.centre_nom}</div>
                                      {g.centre_ville   && <div style={{ fontSize:11, color:"#475569" }}>📍 {g.centre_ville}</div>}
                                      {g.centre_adresse && <div style={{ fontSize:11, color:"#64748b" }}>{g.centre_adresse}</div>}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                          {/* Sub-tabs */}
                          <div style={{ display:"flex", gap:4, borderBottom:"2px solid #e5e7eb", marginBottom:20 }}>
                            {[
                              { k:"apprenants", l:"Apprenants" },
                              { k:"cours", l:"Historique cours" },
                              { k:"presences", l:"Présences" },
                            ].map(t=>(
                              <button key={t.k} onClick={()=>setAdminGroupeSubTab(t.k)}
                                style={{ padding:"8px 16px", border:"none", background:"none", fontWeight:700, fontSize:13, cursor:"pointer",
                                  color:adminGroupeSubTab===t.k?BET_COLOR:"#64748b",
                                  borderBottom:adminGroupeSubTab===t.k?`2px solid ${BET_COLOR}`:"2px solid transparent",
                                  marginBottom:-2 }}>
                                {t.l}
                              </button>
                            ))}
                          </div>

                          {/* Apprenants sub-tab */}
                          {adminGroupeSubTab === "apprenants" && (
                            <div style={{ background:"#fff", borderRadius:14, border:"1px solid #e5e7eb", overflow:"hidden", maxHeight:380, overflowY:"auto" }}>
                              <div style={{ padding:"14px 18px", borderBottom:"1px solid #e5e7eb", background:"#fafafa", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, position:"sticky", top:0, zIndex:1 }}>
                                <div>
                                  <span style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>Apprenants ({adminGroupeDetail.apprenants.filter(a=>a.statut!=="retire").length})</span>
                                  <span style={{ fontWeight:400, fontSize:11, color:"#9ca3af", marginLeft:8 }}>- cliquer pour voir le détail</span>
                                </div>
                                {adminGroupeDetail.apprenants.filter(a=>a.statut!=="retire" && !a.apprenant_id && a.email_apprenant).length > 0 && (
                                  <button
                                    disabled={adminBulkLoad}
                                    onClick={async () => {
                                      if (adminBulkLoad) return;
                                      const sansCompte = adminGroupeDetail.apprenants.filter(a => a.statut !== "retire" && !a.apprenant_id && a.email_apprenant);
                                      setAdminBulkLoad(true);
                                      const items = sansCompte.map(a => ({ nom: a.nom_apprenant, prenom: a.prenom_apprenant||"", email: a.email_apprenant, telephone: a.telephone||null, ga_id: a.id }));
                                      try {
                                        const r = await fetch(`${API_URL}/api/apprenants/creer-acces-bulk`, {
                                          method: "POST", headers: { ...authHeaders(), "Content-Type":"application/json" },
                                          body: JSON.stringify({ items }),
                                        });
                                        const d = await r.json().catch(() => null);
                                        if (!r.ok) throw new Error(d?.error || "Échec de la création des accès");
                                        await alerte(`✅ ${d.message}`);
                                      } catch (e) {
                                        toast.error(e.message || "Échec de la création des accès");
                                      } finally {
                                        setAdminBulkLoad(false);
                                      }
                                    }}
                                    style={{ padding:"6px 12px", background:adminBulkLoad?"#e2e8f0":"#f97316", color:adminBulkLoad?"#9ca3af":"#fff", border:"none", borderRadius:7, fontSize:11, fontWeight:700, cursor:adminBulkLoad?"not-allowed":"pointer", whiteSpace:"nowrap", flexShrink:0 }}>
                                    {adminBulkLoad ? "Création…" : `🔑 Créer les accès manquants (${adminGroupeDetail.apprenants.filter(a=>a.statut!=="retire"&&!a.apprenant_id&&a.email_apprenant).length})`}
                                  </button>
                                )}
                              </div>
                              {adminGroupeDetail.apprenants.length === 0 && (
                                <div style={{ textAlign:"center", padding:32, color:"#9ca3af", fontSize:12 }}>Aucun apprenant dans ce groupe</div>
                              )}
                              {adminGroupeDetail.apprenants.filter(a=>a.statut!=="retire").map(a=>{
                                const initA = ((a.prenom_apprenant||a.nom_apprenant||"?")[0]||"?").toUpperCase();
                                return (
                                  <div key={a.id}
                                    onClick={()=>{
                                      setAdminGroupeApprenantDetail(a);
                                      setAdminGroupeApprenantTab("informations");
                                      setAdminApprenantAcces(null);
                                      if (a.email_apprenant) {
                                        setAdminApprenantAccesLoad(true);
                                        fetch(`${API_URL}/api/apprenants/profil-acces?email=${encodeURIComponent(a.email_apprenant)}`, { headers: authHeaders() })
                                          .then(r => r.ok ? r.json() : null)
                                          .then(d => { setAdminApprenantAcces(d); setAdminApprenantAccesLoad(false); })
                                          .catch(() => setAdminApprenantAccesLoad(false));
                                      }
                                    }}
                                    style={{ display:"flex", alignItems:"center", padding:"12px 18px", borderBottom:"1px solid #f1f5f9", gap:10, cursor:"pointer", transition:"background .12s" }}
                                    onMouseEnter={e=>e.currentTarget.style.background="#eff6ff"}
                                    onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                                    <div style={{ width:36, height:36, borderRadius:"50%", background:BET_COLOR+"20", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:13, color:BET_COLOR, flexShrink:0 }}>
                                      {initA}
                                    </div>
                                    <div style={{ flex:1 }}>
                                      <div style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>{a.prenom_apprenant} {a.nom_apprenant}</div>
                                      <div style={{ fontSize:11, color:"#6b7280" }}>{a.email_apprenant||"-"} {a.niveau ? `· Niveau ${a.niveau}` : ""}</div>
                                    </div>
                                    <span style={{ fontSize:11, color:"#9ca3af" }}>→</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* ── MODAL DÉTAIL APPRENANT (nested, z-index 3000) ── */}
                          {adminGroupeApprenantDetail && (
                            <ModaleAnimee ouvert sansPanneau
                              style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:3000, display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"32px 16px", overflowY:"auto" }}>
                              <div style={{ background:"#fff", borderRadius:18, width:"100%", maxWidth:"90vw", boxShadow:"0 24px 70px rgba(0,0,0,0.3)", overflow:"hidden" }}>
                                {/* Header gradient */}
                                <div style={{ background:BET_GRADIENT, padding:"22px 24px 18px", position:"relative" }}>
                                  <button onClick={()=>setAdminGroupeApprenantDetail(null)}
                                    style={{ position:"absolute", top:14, right:14, background:"rgba(255,255,255,0.15)", border:"none", borderRadius:"50%", width:30, height:30, cursor:"pointer", color:"#fff", fontSize:17, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
                                  <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                                    <div style={{ width:50, height:50, borderRadius:"50%", background:"rgba(255,255,255,0.2)", color:"#fff", fontWeight:800, fontSize:17, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, overflow:"hidden", border:"2px solid rgba(255,255,255,0.3)" }}>
                                      {adminApprenantAcces?.profil?.avatar_url
                                        ? <img src={adminApprenantAcces.profil.avatar_url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                                        : ((adminGroupeApprenantDetail.prenom_apprenant||adminGroupeApprenantDetail.nom_apprenant||"?")[0]||"?").toUpperCase()}
                                    </div>
                                    <div>
                                      <div style={{ fontSize:11, color:"#bfdbfe", fontWeight:600, marginBottom:2 }}>🎓 Apprenant - {adminSelectedGroupe?.nom}</div>
                                      <div style={{ fontSize:18, fontWeight:800, color:"#fff" }}>{adminGroupeApprenantDetail.prenom_apprenant} {adminGroupeApprenantDetail.nom_apprenant}</div>
                                      <div style={{ fontSize:12, color:"#bfdbfe", marginTop:1 }}>{adminGroupeApprenantDetail.email_apprenant||"-"}</div>
                                    </div>
                                  </div>
                                </div>

                                {/* Onglets */}
                                <div style={{ display:"flex", gap:0, borderBottom:"2px solid #e5e7eb", background:"#f8fafc" }}>
                                  {[
                                    { k:"informations", l:"👤 Informations" },
                                    { k:"presences",    l:"📅 Présences" },
                                    { k:"cours",        l:"📚 Cours" },
                                  ].map(t=>(
                                    <button key={t.k} onClick={()=>setAdminGroupeApprenantTab(t.k)}
                                      style={{ padding:"11px 18px", border:"none", background:"none", fontWeight:700, fontSize:12, cursor:"pointer",
                                        color:adminGroupeApprenantTab===t.k?BET_COLOR:"#64748b",
                                        borderBottom:adminGroupeApprenantTab===t.k?`2px solid ${BET_COLOR}`:"2px solid transparent",
                                        marginBottom:-2, whiteSpace:"nowrap" }}>
                                      {t.l}
                                    </button>
                                  ))}
                                </div>

                                <div style={{ padding:"20px 24px 24px" }}>
                                  {/* ── Onglet Informations ── */}
                                  {adminGroupeApprenantTab === "informations" && (() => {
                                    const ap = adminGroupeApprenantDetail;
                                    const isPrive = adminSelectedGroupe?.type_cours === "cours_prive" || adminSelectedGroupe?.type_cours === "domicile";
                                    const onbRole = ap.assistante_onboarding_role;
                                    const isPaAdvisor = onbRole === "pedagogical_advisor" || isPrive;
                                    const assistLabel = isPaAdvisor ? "PA Advisor" : "Assist. Onboarding";
                                    const assistIcon  = isPaAdvisor ? "🧑‍🏫" : "🚀";
                                    const commerciale = ap.assistante_commerciale;
                                    const commercialeNom = commerciale ? `${commerciale.prenom||""} ${commerciale.nom||""}`.trim() || "-" : "-";
                                    const commercialeTel = commerciale?.telephone || null;
                                    return (
                                    <div>
                                      {/* Infos identité */}
                                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
                                        {[
                                          { icon:"👤", label:"Prénom",     val: ap.prenom_apprenant||"-" },
                                          { icon:"👤", label:"Nom",        val: ap.nom_apprenant||"-" },
                                          { icon:"📧", label:"Email",      val: ap.email_apprenant||"-" },
                                          { icon:"📞", label:"Téléphone",  val: ap.telephone||ap.telephone_apprenant||"-" },
                                          { icon:"🎓", label:"Niveau",     val: ap.niveau||"-" },
                                          { icon:"📅", label:"Ajouté le",  val: ap.date_ajout||ap.created_at ? new Date(ap.date_ajout||ap.created_at).toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric"}) : "-" },
                                        ].map((r,i)=>(
                                          <div key={i} style={{ background:"#f8fafc", borderRadius:10, padding:"11px 14px" }}>
                                            <div style={{ fontSize:10, color:"#9ca3af", marginBottom:2 }}>{r.icon} {r.label}</div>
                                            <div style={{ fontWeight:700, color:"#0f172a", fontSize:13, wordBreak:"break-word" }}>{r.val}</div>
                                          </div>
                                        ))}
                                      </div>

                                      {/* Référents */}
                                      <div style={{ borderTop:"1px solid #e5e7eb", paddingTop:14, marginBottom:14 }}>
                                        <div style={{ fontSize:11, fontWeight:700, color:"#374151", marginBottom:10, textTransform:"uppercase", letterSpacing:".04em" }}>👥 Référents</div>
                                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                                          {/* Assistante commerciale */}
                                          <div style={{ background:"#fff7ed", borderRadius:10, padding:"12px 14px", border:"1px solid #fed7aa" }}>
                                            <div style={{ fontSize:10, color:"#92400e", marginBottom:4, fontWeight:700 }}>📈 Assistante commerciale</div>
                                            <div style={{ fontWeight:800, color:"#0f172a", fontSize:13 }}>{commercialeNom}</div>
                                            {commercialeTel && <div style={{ fontSize:11, color:"#6b7280", marginTop:3 }}>📞 {commercialeTel}</div>}
                                            {!commerciale && <div style={{ fontSize:11, color:"#d97706", marginTop:3, fontStyle:"italic" }}>Non renseignée</div>}
                                          </div>
                                          {/* Onboarding / PA Advisor */}
                                          <div style={{ background: isPaAdvisor ? "#f5f3ff" : "#f0f9ff", borderRadius:10, padding:"12px 14px", border: isPaAdvisor ? "1px solid #ddd6fe" : "1px solid #bae6fd" }}>
                                            <div style={{ fontSize:10, color: isPaAdvisor ? "#5b21b6" : "#0369a1", marginBottom:4, fontWeight:700 }}>{assistIcon} {assistLabel}</div>
                                            <div style={{ fontWeight:800, color:"#0f172a", fontSize:13 }}>{ap.assistante_onboarding||"-"}</div>
                                            {!ap.assistante_onboarding && <div style={{ fontSize:11, color: isPaAdvisor?"#7c3aed":"#0891b2", marginTop:3, fontStyle:"italic" }}>Non renseigné</div>}
                                          </div>
                                        </div>
                                      </div>

                                      {/* ── Accès & Connexion ── */}
                                      <div style={{ borderTop:"1px solid #e5e7eb", paddingTop:14, marginBottom:14 }}>
                                        <div style={{ fontSize:11, fontWeight:700, color:"#374151", marginBottom:10, textTransform:"uppercase", letterSpacing:".04em" }}>🔑 Accès & Connexion</div>
                                        {adminApprenantAccesLoad ? (
                                          <div style={{ fontSize:12, color:"#9ca3af", padding:"10px 0" }}>Vérification…</div>
                                        ) : adminApprenantAcces?.has_account ? (
                                          <div style={{ background:"#f0fdf4", border:"1px solid #86efac", borderRadius:10, padding:"12px 14px" }}>
                                            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                                              <span style={{ fontSize:16 }}>✅</span>
                                              <span style={{ fontWeight:700, color:"#065f46", fontSize:13 }}>Compte actif</span>
                                              {adminApprenantAcces.profil?.mdp_temporaire && (
                                                <span style={{ padding:"2px 8px", background:"#fef3c7", color:"#92400e", borderRadius:99, fontSize:10, fontWeight:700 }}>Mot de passe temporaire</span>
                                              )}
                                            </div>
                                            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                                              <div style={{ background:"#fff", borderRadius:8, padding:"8px 10px", border:"1px solid #d1fae5" }}>
                                                <div style={{ fontSize:9, color:"#9ca3af", marginBottom:2 }}>EMAIL DE CONNEXION</div>
                                                <div style={{ fontSize:12, fontWeight:700, color:"#0f172a", wordBreak:"break-all" }}>{adminApprenantAcces.profil?.email || ap.email_apprenant}</div>
                                              </div>
                                              {adminApprenantAcces.profil?.mdp_temporaire && adminApprenantAcces.profil?.mdp_initial && (
                                                <div style={{ background:"#fffbeb", borderRadius:8, padding:"8px 10px", border:"1px solid #fde68a" }}>
                                                  <div style={{ fontSize:9, color:"#92400e", marginBottom:2 }}>MOT DE PASSE TEMP.</div>
                                                  <div style={{ fontSize:13, fontWeight:800, color:"#0f172a", fontFamily:"monospace", letterSpacing:"0.05em" }}>{adminApprenantAcces.profil.mdp_initial}</div>
                                                </div>
                                              )}
                                              {!adminApprenantAcces.profil?.mdp_temporaire && (
                                                <div style={{ background:"#f0fdf4", borderRadius:8, padding:"8px 10px", border:"1px solid #86efac" }}>
                                                  <div style={{ fontSize:9, color:"#065f46", marginBottom:2 }}>MOT DE PASSE</div>
                                                  <div style={{ fontSize:12, fontWeight:700, color:"#065f46" }}>✅ Changé par l'apprenant</div>
                                                </div>
                                              )}
                                            </div>
                                            <div style={{ fontSize:11, color:"#6b7280", marginTop:8 }}>
                                              Compte créé le {adminApprenantAcces.profil?.date_creation ? new Date(adminApprenantAcces.profil.date_creation).toLocaleDateString("fr-FR", { day:"numeric", month:"long", year:"numeric" }) : "-"}
                                            </div>
                                          </div>
                                        ) : ap.email_apprenant ? (
                                          <div style={{ background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:10, padding:"12px 14px" }}>
                                            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
                                              <div>
                                                <div style={{ fontWeight:700, color:"#92400e", fontSize:13, marginBottom:3 }}>⚠️ Aucun compte créé</div>
                                                <div style={{ fontSize:11, color:"#78350f" }}>Cet apprenant n'a pas encore d'accès à l'espace apprenant.</div>
                                              </div>
                                              <button
                                                onClick={async (e) => {
                                                  e.stopPropagation();
                                                  setAdminApprenantAccesLoad(true);
                                                  const r = await fetch(`${API_URL}/api/apprenants/creer-acces`, {
                                                    method: "POST", headers: { ...authHeaders(), "Content-Type":"application/json" },
                                                    body: JSON.stringify({ nom: ap.nom_apprenant, prenom: ap.prenom_apprenant||"", email: ap.email_apprenant, telephone: ap.telephone||null, ga_id: ap.id }),
                                                  });
                                                  const d = r.ok ? await r.json() : null;
                                                  if (d?.user_id) {
                                                    setAdminGroupeApprenantDetail(prev => ({ ...prev, apprenant_id: d.user_id }));
                                                    fetch(`${API_URL}/api/apprenants/profil-acces?email=${encodeURIComponent(ap.email_apprenant)}`, { headers: authHeaders() })
                                                      .then(res => res.json()).then(setAdminApprenantAcces).catch(() => {});
                                                  }
                                                  setAdminApprenantAccesLoad(false);
                                                }}
                                                style={{ padding:"7px 14px", background:"#f97316", color:"#fff", border:"none", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}>
                                                🔑 Créer les accès
                                              </button>
                                            </div>
                                          </div>
                                        ) : (
                                          <div style={{ fontSize:12, color:"#9ca3af", fontStyle:"italic" }}>Aucun email renseigné - impossible de créer un compte.</div>
                                        )}
                                      </div>

                                      {/* Statut badge */}
                                      <div style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 14px", background: ap.statut==="actif"?"#d1fae5":ap.statut==="retire"?"#fee2e2":"#fef3c7", borderRadius:10 }}>
                                        <span style={{ fontSize:14 }}>{ap.statut==="actif"?"✅":ap.statut==="retire"?"🚫":"⏳"}</span>
                                        <span style={{ fontWeight:700, fontSize:13, color: ap.statut==="actif"?"#065f46":ap.statut==="retire"?"#991b1b":"#92400e" }}>
                                          {ap.statut==="actif"?"Actif dans ce groupe":ap.statut==="retire"?"Retiré du groupe":ap.statut||"Statut inconnu"}
                                        </span>
                                      </div>
                                    </div>
                                    );
                                  })()}

                                  {/* ── Onglet Présences ── */}
                                  {adminGroupeApprenantTab === "presences" && (() => {
                                    const fullName = `${adminGroupeApprenantDetail.prenom_apprenant||""} ${adminGroupeApprenantDetail.nom_apprenant||""}`.trim().toLowerCase();
                                    const presAppr = adminPresences.filter(p => `${p.prenom_apprenant||""} ${p.nom_apprenant||""}`.trim().toLowerCase() === fullName || p.apprenant_id === adminGroupeApprenantDetail.id);
                                    const total   = presAppr.length;
                                    const presents = presAppr.filter(p=>p.statut==="present").length;
                                    const absents  = presAppr.filter(p=>p.statut==="absent").length;
                                    const retards  = presAppr.filter(p=>p.statut==="retard").length;
                                    const taux = total ? Math.round(presents/total*100) : 0;
                                    return (
                                      <div>
                                        {total === 0 ? (
                                          <div style={{ textAlign:"center", padding:32, color:"#9ca3af", fontSize:13 }}>Aucune présence enregistrée pour cet apprenant.</div>
                                        ) : (
                                          <>
                                            {/* KPIs présence */}
                                            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:16 }}>
                                              {[
                                                { l:"Séances", v:total, bg:"#eff6ff", c:BET_COLOR },
                                                { l:"Présent", v:presents, bg:"#d1fae5", c:"#065f46" },
                                                { l:"Absent",  v:absents,  bg:"#fee2e2", c:"#dc2626" },
                                                { l:"Retard",  v:retards,  bg:"#fef9c3", c:"#854d0e" },
                                              ].map(k=>(
                                                <div key={k.l} style={{ background:k.bg, borderRadius:10, padding:"10px 12px", textAlign:"center" }}>
                                                  <div style={{ fontSize:18, fontWeight:800, color:k.c }}>{k.v}</div>
                                                  <div style={{ fontSize:10, color:k.c, fontWeight:600, marginTop:2 }}>{k.l}</div>
                                                </div>
                                              ))}
                                            </div>
                                            {/* Barre taux */}
                                            <div style={{ background:"#f8fafc", borderRadius:10, padding:"12px 14px", marginBottom:14 }}>
                                              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                                                <span style={{ fontSize:12, color:"#374151", fontWeight:600 }}>Taux de présence</span>
                                                <span style={{ fontSize:14, fontWeight:800, color:taux>=80?"#059669":taux>=60?"#d97706":"#dc2626" }}>{taux}%</span>
                                              </div>
                                              <div style={{ height:8, background:"#e5e7eb", borderRadius:4, overflow:"hidden" }}>
                                                <div style={{ width:`${taux}%`, height:"100%", background:taux>=80?"#059669":taux>=60?"#d97706":"#dc2626", borderRadius:4, transition:"width .4s" }} />
                                              </div>
                                            </div>
                                            {/* Liste séances */}
                                            <div style={{ maxHeight:220, overflowY:"auto" }}>
                                              {presAppr.sort((a,b)=>(b.date_seance||"").localeCompare(a.date_seance||"")).map((p,i)=>(
                                                <div key={p.id||i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 12px", background:i%2===0?"#fff":"#f8fafc", borderRadius:8, marginBottom:4 }}>
                                                  <span style={{ fontSize:12, color:"#374151", fontWeight:600 }}>📅 {p.date_seance ? new Date(p.date_seance).toLocaleDateString("fr-FR",{weekday:"short",day:"numeric",month:"short"}) : "-"}</span>
                                                  <span style={{ padding:"2px 10px", borderRadius:20, fontSize:11, fontWeight:700,
                                                    background:p.statut==="present"?"#d1fae5":p.statut==="absent"?"#fee2e2":p.statut==="retard"?"#fef9c3":"#ede9fe",
                                                    color:p.statut==="present"?"#065f46":p.statut==="absent"?"#991b1b":p.statut==="retard"?"#854d0e":"#5b21b6" }}>
                                                    {p.statut==="present"?"✅ Présent":p.statut==="absent"?"❌ Absent":p.statut==="retard"?"⏰ Retard":"📝 "+p.statut}
                                                  </span>
                                                </div>
                                              ))}
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    );
                                  })()}

                                  {/* ── Onglet Cours ── */}
                                  {adminGroupeApprenantTab === "cours" && (() => {
                                    const STATUT_COURS_CFG2 = {
                                      dispense:{label:"Dispensé",color:"#065f46",bg:"#d1fae5",icon:"✅"},
                                      annule:{label:"Annulé",color:"#991b1b",bg:"#fee2e2",icon:"❌"},
                                      apprenant_absent:{label:"Absent",color:"#92400e",bg:"#fef3c7",icon:"👤"},
                                      coach_absent:{label:"Coach absent",color:"#1e40af",bg:"#dbeafe",icon:"🏃"},
                                      catch_up:{label:"Catch up",color:"#5b21b6",bg:"#ede9fe",icon:"🔄"},
                                      holiday:{label:"Congé",color:"#374151",bg:"#f1f5f9",icon:"🏖️"},
                                    };
                                    return adminCoursLoading ? (
                                      <div style={{ textAlign:"center", padding:32, color:"#9ca3af", fontSize:13 }}>Chargement…</div>
                                    ) : adminCoursListe.length === 0 ? (
                                      <div style={{ textAlign:"center", padding:32, color:"#9ca3af", fontSize:13 }}>Aucun cours enregistré ce mois-ci.</div>
                                    ) : (
                                      <div style={{ maxHeight:300, overflowY:"auto" }}>
                                        {adminCoursListe.map((c,idx)=>{
                                          const s2 = STATUT_COURS_CFG2[c.statut] || STATUT_COURS_CFG2.dispense;
                                          return (
                                            <div key={c.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"9px 12px", background:idx%2===0?"#fff":"#f8fafc", borderRadius:8, marginBottom:4 }}>
                                              <div>
                                                <div style={{ fontSize:12, fontWeight:700, color:"#0f172a" }}>📅 {new Date(c.date_cours).toLocaleDateString("fr-FR",{weekday:"short",day:"numeric",month:"short"})}</div>
                                                {c.objectif && <div style={{ fontSize:11, color:"#6b7280", marginTop:1 }}>{c.objectif}</div>}
                                              </div>
                                              <span style={{ padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:700, background:s2.bg, color:s2.color }}>{s2.icon} {s2.label}</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                            </ModaleAnimee>
                          )}

                          {/* Cours sub-tab */}
                          {adminGroupeSubTab === "cours" && (() => {
                            const STATUT_COURS_CFG = {
                              dispense:{label:"Dispensé",color:"#065f46",bg:"#d1fae5",icon:"✅"},
                              annule:{label:"Annulé",color:"#991b1b",bg:"#fee2e2",icon:"❌"},
                              apprenant_absent:{label:"Apprenant absent",color:"#92400e",bg:"#fef3c7",icon:"👤"},
                              coach_absent:{label:"Coach absent",color:"#1e40af",bg:"#dbeafe",icon:"🏃"},
                              catch_up:{label:"Catch up",color:"#5b21b6",bg:"#ede9fe",icon:"🔄"},
                              holiday:{label:"Congé / Férié",color:"#374151",bg:"#f1f5f9",icon:"🏖️"},
                            };
                            return (
                              <div>
                                <div style={{ display:"flex", gap:8, marginBottom:16, alignItems:"center" }}>
                                  <select value={adminCoursFiltreMois} onChange={e=>setAdminCoursFiltreMois(Number(e.target.value))}
                                    style={{ padding:"7px 10px", border:"1.5px solid #e5e7eb", borderRadius:8, fontSize:12 }}>
                                    {["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"].map((m,i)=>(
                                      <option key={i+1} value={i+1}>{m}</option>
                                    ))}
                                  </select>
                                  <select value={adminCoursAnnee} onChange={e=>setAdminCoursAnnee(Number(e.target.value))}
                                    style={{ padding:"7px 10px", border:"1.5px solid #e5e7eb", borderRadius:8, fontSize:12 }}>
                                    {[2024,2025,2026,2027].map(y=><option key={y} value={y}>{y}</option>)}
                                  </select>
                                </div>
                                {adminCoursLoading && <p style={{ color:"#9ca3af", textAlign:"center" }}>Chargement…</p>}
                                {!adminCoursLoading && adminCoursListe.length === 0 && <p style={{ color:"#9ca3af", textAlign:"center", padding:20 }}>Aucun cours ce mois-ci.</p>}
                                {!adminCoursLoading && adminCoursListe.length > 0 && (
                                  <div style={{ overflowX:"auto", borderRadius:10, border:"1px solid #e5e7eb", maxHeight:340, overflowY:"auto" }}>
                                    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                                      <thead>
                                        <tr style={{ background:"#f8fafc", position:"sticky", top:0 }}>
                                          {["Date","Statut","Objectif","Grammaire","Sujet discussion","Commentaire"].map((h,i)=>(
                                            <th key={i} style={{ padding:"9px 12px", textAlign:"left", fontWeight:700, color:"#374151", fontSize:11, borderBottom:"2px solid #e5e7eb", whiteSpace:"nowrap" }}>{h}</th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {adminCoursListe.map((c,idx)=>{
                                          const s = STATUT_COURS_CFG[c.statut] || STATUT_COURS_CFG.dispense;
                                          return (
                                            <tr key={c.id} style={{ background:idx%2===0?"#fff":"#fafafa", borderBottom:"1px solid #f1f5f9" }}>
                                              <td style={{ padding:"9px 12px", fontWeight:600, whiteSpace:"nowrap" }}>{new Date(c.date_cours).toLocaleDateString("fr-FR",{day:"numeric",month:"short"})}</td>
                                              <td style={{ padding:"9px 12px" }}><span style={{ padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:700, background:s.bg, color:s.color }}>{s.icon} {s.label}</span></td>
                                              <td style={{ padding:"9px 12px", color:"#374151" }}>{c.objectif||"-"}</td>
                                              <td style={{ padding:"9px 12px", color:"#374151" }}>{c.grammaire||"-"}</td>
                                              <td style={{ padding:"9px 12px", color:"#374151" }}>{c.sujet_discussion||"-"}</td>
                                              <td style={{ padding:"9px 12px", color:"#6b7280" }}>{c.commentaire||"-"}</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                          {/* Présences sub-tab */}
                          {adminGroupeSubTab === "presences" && (
                            <div style={{ maxHeight:380, overflowY:"auto" }}>
                              {adminPresences.length === 0 && <p style={{ color:"#9ca3af", textAlign:"center", padding:20 }}>Aucune présence enregistrée.</p>}
                              {(() => {
                                const parDate = {};
                                adminPresences.forEach(p=>{ if(!parDate[p.date_seance]) parDate[p.date_seance]=[]; parDate[p.date_seance].push(p); });
                                return Object.entries(parDate).sort(([a],[b])=>b.localeCompare(a)).map(([date,rows])=>{
                                  const presents = rows.filter(r=>r.statut==="present").length;
                                  const taux = rows.length ? Math.round(presents/rows.length*100) : 0;
                                  return (
                                    <div key={date} style={{ marginBottom:12, background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", overflow:"hidden" }}>
                                      <div style={{ padding:"10px 14px", background:"#f8fafc", borderBottom:"1px solid #e5e7eb", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                                        <span style={{ fontWeight:700, fontSize:13, color:"#0f172a" }}>📅 {new Date(date).toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long"})}</span>
                                        <span style={{ fontSize:11, fontWeight:700, color:taux>=80?"#059669":taux>=60?"#d97706":"#dc2626" }}>{presents}/{rows.length} - {taux}%</span>
                                      </div>
                                      <div style={{ padding:"10px 14px", display:"flex", flexWrap:"wrap", gap:6 }}>
                                        {rows.map(r=>(
                                          <span key={r.id} style={{ padding:"2px 10px", borderRadius:20, fontSize:11, fontWeight:600,
                                            background:r.statut==="present"?"#d1fae5":r.statut==="absent"?"#fee2e2":r.statut==="retard"?"#fef3c7":"#ede9fe",
                                            color:r.statut==="present"?"#065f46":r.statut==="absent"?"#991b1b":r.statut==="retard"?"#92400e":"#5b21b6" }}>
                                            {r.statut==="present"?"✅":r.statut==="absent"?"❌":r.statut==="retard"?"⏰":"📝"} {[r.prenom_apprenant,r.nom_apprenant].filter(Boolean).join(" ")}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          )}
                          </div>{/* fin padding wrapper */}
                      </ModaleAnimee>
                    )}
                  </div>
                  );
                })()} {/* fin coursSubTab === "groupes" */}

                {/* ── Sous-onglet : Cours privés (classes attribuées aux coachs) ── */}
                {coursSubTab === "cours_prives" && (() => {
                  const STATUT_CLASS = {
                    actif:      { bg:"#d1fae5", color:"#065f46",  label:"Actif" },
                    termine:    { bg:"#f1f5f9", color:"#374151",  label:"Terminé" },
                    suspendu:   { bg:"#fee2e2", color:"#dc2626",  label:"Suspendu" },
                    en_attente: { bg:"#fef3c7", color:"#92400e",  label:"En attente" },
                  };
                  const TYPE_LABEL = { domicile:"🏠 Domicile", cours_prive:"🎯 Privé", prive:"🎯 Privé", en_ligne:"💻 En ligne" };
                  const fmtDate = d => d ? new Date(d).toLocaleDateString("fr-FR",{day:"numeric",month:"short",year:"numeric"}) : "-";
                  const isGroupePrive2 = g => {
                    if (g.type_cours === "cours_prive" || g.type_cours === "domicile" || g.type_cours === "prive") return true;
                    const fil = (g.filiere||"").toLowerCase();
                    if (fil.includes("privé") || fil.includes("prive") || fil.includes("coaching privé") || fil.includes("cours privé")) return true;
                    return false;
                  };

                  // Source : assignations privées (type_coaching=prive / domicile)
                  const coursPrives = adminAllContrats; // déjà filtrés dans le useEffect

                  const filteredClasses = coursPrives.filter(a => {
                    if (adminContratFiltre.statut !== "tous" && a.statut !== adminContratFiltre.statut) return false;
                    if (adminContratFiltre.coach  !== "tous" && String(a.coach_id||a.assistante_id) !== adminContratFiltre.coach) return false;
                    if (adminContratFiltre.type !== "tous") {
                      const tc = a.type_cours || "";
                      if (adminContratFiltre.type === "en_ligne"   && tc !== "en_ligne") return false;
                      if (adminContratFiltre.type === "presentiel" && !["presentiel","centre"].includes(tc)) return false;
                      if (adminContratFiltre.type === "domicile"   && tc !== "domicile") return false;
                    }
                    if (adminContratFiltre.search) {
                      const q = adminContratFiltre.search.toLowerCase();
                      const nom = `${a.prospect_nom||a.prospect_prenom||""}`.toLowerCase();
                      if (!nom.includes(q) && !(a.prospect_email||"").toLowerCase().includes(q) && !(a.assistante_nom||a.coach_nom||"").toLowerCase().includes(q)) return false;
                    }
                    return true;
                  });

                  const totalPages = Math.max(1, Math.ceil(filteredClasses.length / CONTRATS_PER_PAGE));
                  const page = Math.min(adminContratPage, totalPages);
                  const pageItems = filteredClasses.slice((page-1)*CONTRATS_PER_PAGE, page*CONTRATS_PER_PAGE);

                  const coachesPrives = Array.from(new Map(
                    coursPrives.filter(a=>a.assistante_id||a.coach_id).map(a=>[a.assistante_id||a.coach_id, a.assistante_nom||a.coach_nom||"-"])
                  ));

                  // Stats
                  const statsByCoach = coachesPrives.map(([id, nom]) => {
                    const entries = coursPrives.filter(a => String(a.assistante_id||a.coach_id) === String(id));
                    const actives = entries.filter(a => a.statut === "actif" || a.statut === "converti" || a.statut === "en_cours").length;
                    return { id, nom, total: entries.length, actives };
                  }).sort((a,b) => b.total - a.total);

                  // KPIs globaux
                  const kpiTotal   = coursPrives.length;
                  const kpiActives = coursPrives.filter(a=>["actif","converti","en_cours"].includes(a.statut)).length;
                  const kpiCoachs  = coachesPrives.length;
                  const niveaux    = Array.from(new Set(coursPrives.map(a=>a.niveau).filter(Boolean)));

                  return (
                    <div>
                      {/* Header */}
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
                        <div>
                          <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:"#0f172a" }}>🎯 Apprenants en cours privés</h2>
                          <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Tous les apprenants ayant un parcours de cours privé / coaching à domicile</p>
                        </div>
                        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                          <button onClick={()=>{ setAdminAllContratsLoad(true); const t=localStorage.getItem("admin_token"); fetch(`${API_URL}/api/parcours/assignations`,{headers:{Authorization:`Bearer ${t}`}}).then(r=>r.ok?r.json():{assignations:[]}).then(d=>{ const privees=(d.assignations||[]).filter(a=>a.type_coaching==="prive"||a.type_cours==="domicile"||a.type_cours==="cours_prive"||a.type_cours==="prive"); setAdminAllContrats(privees); }).catch(()=>{}).finally(()=>setAdminAllContratsLoad(false)); }}
                            style={{ padding:"6px 14px", borderRadius:8, border:"1.5px solid #e5e7eb", background:"#fff", cursor:"pointer", fontSize:12, color:"#374151" }}>
                            🔄 Actualiser
                          </button>
                          <span style={{ padding:"4px 12px", borderRadius:20, background:BET_LIGHT, color:BET_COLOR, fontWeight:700, fontSize:12 }}>
                            {filteredClasses.length} classe(s)
                          </span>
                        </div>
                      </div>

                      {/* KPI cards */}
                      {coursPrives.length > 0 && (
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20 }}>
                          {[
                            { icon:"🎯", label:"Total cours privés",  val:kpiTotal,   color:"#6366f1", bg:"#eef2ff" },
                            { icon:"✅", label:"Actifs / Convertis",   val:kpiActives, color:"#059669", bg:"#d1fae5" },
                            { icon:"👨‍🏫", label:"Coachs/PA assignés", val:kpiCoachs,  color:BET_COLOR, bg:BET_LIGHT },
                            { icon:"📊", label:"Niveaux distincts",    val:niveaux.length, color:"#d97706", bg:"#fef3c7" },
                          ].map((k,i)=>(
                            <div key={i} style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:"14px 16px", display:"flex", alignItems:"center", gap:12 }}>
                              <div style={{ width:40, height:40, borderRadius:10, background:k.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>{k.icon}</div>
                              <div>
                                <div style={{ fontSize:20, fontWeight:800, color:k.color, lineHeight:1 }}>{k.val}</div>
                                <div style={{ fontSize:11, color:"#6b7280", marginTop:2 }}>{k.label}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Vue par coach */}
                      {!adminGroupesLoading && statsByCoach.length > 0 && (
                        <div style={{ marginBottom:20, background:"#f8fafc", borderRadius:12, border:"1px solid #e5e7eb", padding:16 }}>
                          <div style={{ fontSize:13, fontWeight:700, color:"#374151", marginBottom:12 }}>👨‍🏫 Répartition par coach</div>
                          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                            {statsByCoach.map(coach=>(
                              <button key={coach.id}
                                onClick={()=>{ setAdminContratFiltre(p=>({...p, coach: String(adminContratFiltre.coach)===String(coach.id)?"tous":String(coach.id)})); setAdminContratPage(1); }}
                                style={{
                                  padding:"8px 14px", borderRadius:20, border:"1.5px solid",
                                  borderColor: String(adminContratFiltre.coach)===String(coach.id) ? BET_COLOR : "#e5e7eb",
                                  background: String(adminContratFiltre.coach)===String(coach.id) ? BET_LIGHT : "#fff",
                                  cursor:"pointer", fontSize:12, color:"#374151", display:"flex", alignItems:"center", gap:8
                                }}>
                                <span style={{ fontWeight:700, color: String(adminContratFiltre.coach)===String(coach.id) ? BET_COLOR : "#0f172a" }}>{coach.nom}</span>
                                <span style={{ padding:"1px 7px", borderRadius:20, background: coach.actives>0?"#d1fae5":"#f1f5f9", color: coach.actives>0?"#065f46":"#6b7280", fontSize:11, fontWeight:700 }}>
                                  {coach.actives} actif{coach.actives!==1?"s":""}
                                </span>
                                <span style={{ fontSize:11, color:"#9ca3af" }}>{coach.total} classe{coach.total!==1?"s":""}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* ── Filtres - modèle pastilles partagé. Cette barre avait déjà des
                           pastilles pour le type, mais dans un style propre à cet onglet
                           (fond plein) : elle rejoint le modèle commun. ── */}
                      <BarreFiltres accent={BET_COLOR} style={{ marginBottom:16 }}>
                        <RangeePastilles label="Type :" valeur={adminContratFiltre.type}
                          onChange={(v)=>{ setAdminContratFiltre(p=>({...p,type:v})); setAdminContratPage(1); }}
                          options={[
                            { v:"tous",       l:"Tous",          n: coursPrives.length },
                            { v:"en_ligne",   l:"💻 En ligne",   n: coursPrives.filter(a=>a.type_cours==="en_ligne").length },
                            { v:"presentiel", l:"🏢 Présentiel", n: coursPrives.filter(a=>["presentiel","centre"].includes(a.type_cours)).length },
                            { v:"domicile",   l:"🏠 Domicile",   n: coursPrives.filter(a=>a.type_cours==="domicile").length },
                          ]} />

                        <RangeePastilles label="Statut :" valeur={adminContratFiltre.statut}
                          onChange={(v)=>{ setAdminContratFiltre(p=>({...p,statut:v})); setAdminContratPage(1); }}
                          options={[{ v:"tous", l:"Tous" }, ...Object.entries(STATUT_CLASS).map(([k,v])=>({ v:k, l:v.label }))]}
                          compte={(v)=> v==="tous" ? coursPrives.length : coursPrives.filter(a=>a.statut===v).length} />

                        <RangeeChamps label="Recherche :"
                          onReinitialiser={(adminContratFiltre.search || adminContratFiltre.statut !== "tous" || adminContratFiltre.coach !== "tous" || adminContratFiltre.type !== "tous")
                            ? ()=>{ setAdminContratFiltre({coach:"tous",statut:"tous",type:"tous",search:""}); setAdminContratPage(1); }
                            : null}>
                          <input value={adminContratFiltre.search} placeholder="Rechercher apprenant ou coach…"
                            aria-label="Rechercher un apprenant ou un coach"
                            onChange={e=>{ setAdminContratFiltre(p=>({...p,search:e.target.value})); setAdminContratPage(1); }}
                            style={{ ...champFiltre, width:240 }} />

                          {coachesPrives.length > 0 && (
                            <select value={adminContratFiltre.coach} aria-label="Filtrer par coach"
                              onChange={e=>{ setAdminContratFiltre(p=>({...p,coach:e.target.value})); setAdminContratPage(1); }}
                              style={{ ...champFiltre, cursor:"pointer" }}>
                              <option value="tous">Tous les coachs</option>
                              {coachesPrives.map(([id,nom])=><option key={id} value={String(id)}>{nom}</option>)}
                            </select>
                          )}
                        </RangeeChamps>
                      </BarreFiltres>


                      {/* Contenu */}
                      {adminAllContratsLoad && <div style={{ textAlign:"center", padding:40, color:"#9ca3af", fontSize:13 }}>Chargement des cours privés…</div>}
                      {!adminAllContratsLoad && coursPrives.length === 0 && (
                        <div style={{ textAlign:"center", padding:60, color:"#9ca3af", fontSize:13 }}>Aucun apprenant en cours privé trouvé.</div>
                      )}
                      {!adminAllContratsLoad && filteredClasses.length === 0 && coursPrives.length > 0 && (
                        <div style={{ textAlign:"center", padding:40, color:"#9ca3af", fontSize:13 }}>Aucun résultat pour ces filtres.</div>
                      )}
                      {!adminAllContratsLoad && pageItems.length > 0 && (
                        <div style={{ overflowX:"auto", borderRadius:12, border:"1px solid #e5e7eb" }}>
                          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                            <thead>
                              <tr style={{ background:"#f8fafc" }}>
                                {["Apprenant","Contact","Type cours","Niveau","Coach / PA","Statut","Date inscription"].map((h,i)=>(
                                  <th key={i} style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, color:"#374151", fontSize:11, borderBottom:"2px solid #e5e7eb", whiteSpace:"nowrap" }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {pageItems.map((a,idx)=>{
                                const s = STATUT_CLASS[a.statut] || { bg:"#f1f5f9", color:"#6b7280", label: a.statut||"-" };
                                const typeLabel = a.type_coaching==="prive" ? "🎯 Cours privé" : a.type_cours==="domicile" ? "🏠 Domicile" : "🎯 Cours privé";
                                const nom = `${a.prospect_prenom||""} ${a.prospect_nom||""}`.trim() || a.prospect_email || "-";
                                const coach = a.assistante_nom || a.coach_nom || "-";
                                const openDetail = () => {
                                  setSaApprenantTab("profil");
                                  setSaAccesInfo(null);
                                  setSaSeancesPrivees([]);
                                  setSaPresencesPrivees([]);
                                  setSaPaiementsPrivees([]);
                                  const sd = a.suivi_demarrage || {};
                                  const coachNom = a.notes_coach || sd.coach_nom_manuel
                                    || (sd.coach_id ? (()=>{ const c = users.find(u=>String(u.id)===String(sd.coach_id)); return c ? `${c.prenom||""} ${c.nom||""}`.trim() : null; })() : null)
                                    || null;
                                  const paUser  = a.pa_advisor_id  ? users.find(u=>String(u.id)===String(a.pa_advisor_id))  : null;
                                  const onbUser = a.onboarding_id  ? users.find(u=>String(u.id)===String(a.onboarding_id))  : null;
                                  setSelectedApprenant({
                                    id:              a.id,
                                    nom,
                                    email:           a.prospect_email     || "-",
                                    telephone:       a.prospect_telephone || "-",
                                    type_cours:      a.type_cours         || "cours_prive",
                                    type_coaching:   a.type_coaching      || "prive",
                                    niveau:          sd.niveau_prospect   || a.niveau_client || "-",
                                    centre_nom:      a.centre_nom         || sd.centre_coaching || null,
                                    statut:          a.statut             || "nouveau",
                                    statut_paiement: a.statut_paiement    || null,
                                    commercial:      a.assistante_nom     || "-",
                                    coach:           coachNom             || "Non affecté",
                                    pa_advisor:      paUser  ? `${paUser.prenom||""} ${paUser.nom||""}`.trim() : null,
                                    onboarding:      onbUser ? `${onbUser.prenom||""} ${onbUser.nom||""}`.trim() : null,
                                    formation:       sd.formation_souhaitee || null,
                                    lieu_coaching:   sd.lieu_coaching     || null,
                                    cout_formation:  sd.cout_formation    || null,
                                    date_debut:      sd.date_debut_formation || a.date_debut_formation || a.date_debut || null,
                                    date_fin:        a.date_fin           || null,
                                    created_at:      a.created_at         || null,
                                    prix_heure:      a.prix_heure         || null,
                                    _source:         "cours_prive",
                                  });
                                  const emailA = a.prospect_email;
                                  const telA   = a.prospect_telephone;
                                  // Accès compte
                                  if (emailA || telA) {
                                    setSaAccesLoad(true);
                                    const q = emailA
                                      ? `email=${encodeURIComponent(emailA)}`
                                      : `telephone=${encodeURIComponent(telA)}`;
                                    fetch(`${API_URL}/api/apprenants/profil-acces?${q}`, { headers: authHeaders() })
                                      .then(r => r.ok ? r.json() : null)
                                      .then(d => { setSaAccesInfo(d); setSaAccesLoad(false); })
                                      .catch(() => setSaAccesLoad(false));
                                  }
                                  // Paiements
                                  setSaLoadingPaiements(true);
                                  fetch(`${API_URL}/api/paiements/assignation/${a.id}`, { headers: authHeaders() })
                                    .then(r => r.ok ? r.json() : { paiements: [] })
                                    .then(d => { setSaPaiementsPrivees(d.paiements || []); setSaLoadingPaiements(false); })
                                    .catch(() => setSaLoadingPaiements(false));
                                  // Séances & Présences
                                  setSaLoadingSeances(true);
                                  Promise.all([
                                    fetch(`${API_URL}/api/seances-privees?assignation_id=${a.id}`,   { headers: authHeaders() }),
                                    fetch(`${API_URL}/api/presences-privees?assignation_id=${a.id}`, { headers: authHeaders() }),
                                  ])
                                    .then(([rs, rp]) => Promise.all([
                                      rs.ok ? rs.json() : { seances: [] },
                                      rp.ok ? rp.json() : { presences: [] },
                                    ]))
                                    .then(([ds, dp]) => {
                                      setSaSeancesPrivees(ds.seances || []);
                                      setSaPresencesPrivees(dp.presences || []);
                                      setSaLoadingSeances(false);
                                    })
                                    .catch(() => setSaLoadingSeances(false));
                                };
                                return (
                                  <tr key={a.id} style={{ background:idx%2===0?"#fff":"#fafafa", borderBottom:"1px solid #f1f5f9", cursor:"pointer" }}
                                    onClick={openDetail}
                                    onMouseEnter={e=>e.currentTarget.style.background="#eff6ff"}
                                    onMouseLeave={e=>e.currentTarget.style.background=idx%2===0?"#fff":"#fafafa"}>
                                    <td style={{ padding:"10px 14px" }}>
                                      <div style={{ fontWeight:700, color:"#0f172a", fontSize:13 }}>{nom}</div>
                                    </td>
                                    <td style={{ padding:"10px 14px" }}>
                                      <div style={{ fontSize:12, color:"#374151" }}>{a.prospect_email||"-"}</div>
                                      <div style={{ fontSize:11, color:"#9ca3af" }}>{a.prospect_telephone||""}</div>
                                    </td>
                                    <td style={{ padding:"10px 14px" }}>
                                      <span style={{ padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:600, background:"#fef3c7", color:"#92400e" }}>{typeLabel}</span>
                                    </td>
                                    <td style={{ padding:"10px 14px", color:"#374151" }}>{a.niveau||"-"}</td>
                                    <td style={{ padding:"10px 14px", fontWeight:600, color:"#374151" }}>{coach}</td>
                                    <td style={{ padding:"10px 14px" }}>
                                      <span style={{ padding:"2px 10px", borderRadius:20, fontSize:11, fontWeight:700, background:s.bg, color:s.color }}>{s.label}</span>
                                    </td>
                                    <td style={{ padding:"10px 14px", color:"#374151", whiteSpace:"nowrap" }}>{fmtDate(a.created_at||a.date_debut)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div style={{ display:"flex", justifyContent:"center", gap:6, marginTop:16 }}>
                          <button onClick={()=>setAdminContratPage(p=>Math.max(1,p-1))} disabled={page===1}
                            style={{ padding:"6px 12px", border:"1.5px solid #e5e7eb", borderRadius:7, background:"#fff", cursor:page===1?"default":"pointer", fontSize:12, color:"#374151", opacity:page===1?0.5:1 }}>
                            ← Préc.
                          </button>
                          {Array.from({length:totalPages},(_,i)=>i+1).map(n=>(
                            <button key={n} onClick={()=>setAdminContratPage(n)}
                              style={{ padding:"6px 12px", border:"1.5px solid", borderRadius:7, fontSize:12, fontWeight:600, cursor:"pointer",
                                borderColor:n===page?BET_COLOR:"#e5e7eb",
                                background:n===page?BET_COLOR:"#fff",
                                color:n===page?"#fff":"#374151" }}>
                              {n}
                            </button>
                          ))}
                          <button onClick={()=>setAdminContratPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}
                            style={{ padding:"6px 12px", border:"1.5px solid #e5e7eb", borderRadius:7, background:"#fff", cursor:page===totalPages?"default":"pointer", fontSize:12, color:"#374151", opacity:page===totalPages?0.5:1 }}>
                            Suiv. →
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}

              </div>
            )}

            {/* ================= ONGLET APPRENANTS - liste ================= */}
            {activeTab === "suivi_apprenants" && apprenantSubTab === "liste" && (() => {
              const STATUT_LABEL = { nouveau:"Nouveau", contacte:"Contacté", en_cours:"En cours", converti:"Converti", annule:"Annulé", termine:"Terminé" };
              const STATUT_STYLE = {
                nouveau:  { bg:"#eff6ff", color:BET_COLOR },
                contacte: { bg:"#fff7ed", color:"#92400e" },
                en_cours: { bg:"#fef9c3", color:"#854d0e" },
                converti: { bg:"#d1fae5", color:"#065f46" },
                annule:   { bg:"#fee2e2", color:"#dc2626" },
                termine:  { bg:"#f3f4f6", color:"#374151" },
              };
              const PAI_STYLE = {
                paye:      { bg:"#d1fae5", color:"#065f46" },
                en_attente:{ bg:"#fef9c3", color:"#854d0e" },
                partiel:   { bg:"#fff7ed", color:"#92400e" },
                impaye:    { bg:"#fee2e2", color:"#dc2626" },
              };

              // Périodes disponibles calculées dynamiquement depuis created_at
              const periodesDispos = (() => {
                const moisSet = new Set();
                apprenants.forEach(a => {
                  if (a.created_at) {
                    const d = new Date(a.created_at);
                    if (!isNaN(d)) {
                      const label = d.toLocaleDateString("fr-FR",{month:"long",year:"numeric"});
                      const prefix = d.toISOString().slice(0,7); // "2025-06"
                      moisSet.add(`${prefix}|${label.charAt(0).toUpperCase()+label.slice(1)}`);
                    }
                  }
                });
                return Array.from(moisSet)
                  .sort((a,b)=>b.localeCompare(a))
                  .map(s=>({ prefix:s.split("|")[0], label:s.split("|")[1] }));
              })();

              const filtres = apprenants.filter(a => {
                if (apprenantFiltre.type_cours !== "Tous" && a.type_cours !== apprenantFiltre.type_cours) return false;
                if (apprenantFiltre.centre     !== "Tous" && a.centre_id  !== apprenantFiltre.centre)    return false;
                if (apprenantFiltre.statut     !== "Tous" && a.statut     !== apprenantFiltre.statut)    return false;
                if (apprenantFiltre.statut_paiement !== "Tous" && a.statut_paiement !== apprenantFiltre.statut_paiement) return false;
                if (apprenantFiltre.periode !== "Tous") {
                  const p = periodesDispos.find(p=>p.label===apprenantFiltre.periode);
                  if (p && a.created_at) {
                    if (!a.created_at.startsWith(p.prefix)) return false;
                  } else if (p && !a.created_at) return false;
                }
                if (apprenantFiltre.search) {
                  const q = apprenantFiltre.search.toLowerCase();
                  if (!a.nom.toLowerCase().includes(q) && !a.email.toLowerCase().includes(q) && !(a.commercial||"").toLowerCase().includes(q) && !(a.coach||"").toLowerCase().includes(q)) return false;
                }
                return true;
              });

              const totalPages = Math.max(1, Math.ceil(filtres.length / APPRENANTS_PER_PAGE));
              const page       = Math.min(apprenantPage, totalPages);
              const pageItems  = filtres.slice((page-1)*APPRENANTS_PER_PAGE, page*APPRENANTS_PER_PAGE);

              const kpiTotal    = apprenants.length;
              const kpiConvert  = apprenants.filter(a=>a.statut==="converti"||a.statut==="actif").length;
              const kpiEnLigne  = apprenants.filter(a=>a.type_cours==="en_ligne").length;
              const kpiPrives   = apprenants.filter(a=>a.type_cours==="cours_prive").length;
              const kpiPaye     = apprenants.filter(a=>a.statut_paiement==="paye").length;

              const exportCSV = () => {
                exporterExcelStyle({
                  nomFichier: `apprenants_${new Date().toISOString().slice(0,10)}`,
                  titre: '🎓 Apprenants',
                  sousTitre: `${filtres.length} apprenant${filtres.length>1?'s':''} · Généré le ${new Date().toLocaleDateString('fr-FR')}`,
                  couleurEntete: '6366F1',
                  colonnes: [
                    { label: 'Nom', width: 22, bold: true }, { label: 'Email', width: 26 },
                    { label: 'Téléphone', width: 16 }, { label: 'Type cours', width: 14 },
                    { label: 'Coaching', width: 14 }, { label: 'Centre', width: 14 },
                    { label: 'Commercial', width: 18 }, { label: 'Statut', width: 12 },
                    { label: 'Paiement', width: 12 }, { label: 'Inscrit le', width: 14 },
                  ],
                  lignes: filtres.map(a => [a.nom, a.email, a.telephone, a.type_cours, a.type_coaching, a.centre_id||"-", a.commercial, a.statut, a.statut_paiement||"-", a.created_at?.slice(0,10)||"-"]),
                });
              };

              const hasFilter = apprenantFiltre.type_cours!=="Tous"||apprenantFiltre.centre!=="Tous"||apprenantFiltre.statut!=="Tous"||apprenantFiltre.statut_paiement!=="Tous"||apprenantFiltre.periode!=="Tous"||!!apprenantFiltre.search;

              return (
                <div>
                  {/* Sous-onglets */}
                  <div style={{ display:"flex", gap:4, marginBottom:20, background:"#f1f5f9", borderRadius:10, padding:4, width:"fit-content" }}>
                    {[{ key:"liste", label:"🎓 Liste apprenants" }, { key:"progression", label:"📈 Progression" }].map(st => (
                      <button key={st.key} onClick={()=>setApprenantSubTab(st.key)}
                        style={{ padding:"7px 16px", borderRadius:8, border:"none", fontSize:13, fontWeight:600, cursor:"pointer",
                          background: apprenantSubTab===st.key ? "#fff" : "transparent",
                          color:      apprenantSubTab===st.key ? "#0891b2" : "#64748b",
                          boxShadow:  apprenantSubTab===st.key ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                          transition:"all .15s" }}>
                        {st.label}
                      </button>
                    ))}
                  </div>
                  {/* Header */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
                    <div>
                      <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:"#0f172a" }}>🎓 Apprenants & Prospects</h2>
                      <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Toutes les assignations parcours - données réelles</p>
                    </div>
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={fetchApprenants} style={{ padding:"9px 14px", background:"#f1f5f9", color:"#374151", border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:12 }}>🔄 Actualiser</button>
                      <button onClick={exportCSV} style={{ padding:"9px 16px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:12 }}>⬇ Export CSV</button>
                    </div>
                  </div>

                  {/* KPIs */}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:12, marginBottom:20 }}>
                    {[
                      { icon:"🎓", label:"Total inscrits",   value:kpiTotal,   color:BET_COLOR,   sub:"Toutes sources" },
                      { icon:"💻", label:"En ligne",          value:kpiEnLigne, color:"#8b5cf6",   sub:`${apprenants.filter(a=>a.type_cours==="presentiel").length} présentiel` },
                      { icon:"🎯", label:"Cours privés",      value:kpiPrives,  color:"#f59e0b",   sub:"Contrats individuels" },
                      { icon:"✅", label:"Actifs / convertis",value:kpiConvert, color:"#22c55e",   sub:"En cours ou finalisés" },
                      { icon:"💳", label:"Paiements reçus",   value:kpiPaye,    color:"#0891b2",   sub:"Statut = payé" },
                    ].map((k,i) => <StatCard key={i} {...k} />)}
                  </div>

                  {/* ── Filtres - modèle pastilles partagé (Components/BarreFiltres).
                       Type et paiement sont des jeux fermés : ils passent en pastilles, dont
                       le compteur évite d'ouvrir un select pour découvrir qu'une valeur ne
                       ramène rien. Recherche, période, centre et statut gardent leurs champs :
                       leurs listes sont dynamiques ou trop longues pour des pastilles. ── */}
                  <BarreFiltres accent={BET_COLOR} style={{ background:"#f8fafc", border:"1px solid #e5e7eb", borderRadius:10, padding:"14px 16px", marginBottom:18 }}>
                    <RangeePastilles label="Type :" valeur={apprenantFiltre.type_cours}
                      onChange={(v)=>{ setApprenantFiltre(p=>({...p,type_cours:v})); setApprenantPage(1); }}
                      options={[
                        { v:"Tous",        l:"Tous" },
                        { v:"en_ligne",    l:"💻 En ligne" },
                        { v:"presentiel",  l:"🏢 Présentiel" },
                        { v:"cours_prive", l:"🎯 Cours privés" },
                      ]}
                      compte={(v)=> v==="Tous" ? apprenants.length : apprenants.filter(a=>a.type_cours===v).length} />

                    <RangeePastilles label="Paiement :" valeur={apprenantFiltre.statut_paiement}
                      onChange={(v)=>{ setApprenantFiltre(p=>({...p,statut_paiement:v})); setApprenantPage(1); }}
                      options={[
                        { v:"Tous",       l:"Tous" },
                        { v:"paye",       l:"✅ Payé" },
                        { v:"en_attente", l:"⏳ En attente" },
                        { v:"partiel",    l:"🔶 Partiel" },
                        { v:"impaye",     l:"❌ Impayé" },
                      ]}
                      compte={(v)=> v==="Tous" ? apprenants.length : apprenants.filter(a=>a.statut_paiement===v).length} />

                    <RangeeChamps label="Recherche :"
                      resume={`${filtres.length.toLocaleString("fr-FR")} résultat${filtres.length>1?"s":""}${filtres.length !== apprenants.length ? ` / ${apprenants.length.toLocaleString("fr-FR")} total` : ""}`}
                      onReinitialiser={hasFilter ? ()=>{ setApprenantFiltre({type_cours:"Tous",centre:"Tous",statut:"Tous",statut_paiement:"Tous",periode:"Tous",search:""}); setApprenantPage(1); } : null}>
                      <input placeholder="🔍 Nom, email, coach ou commercial…" value={apprenantFiltre.search}
                        onChange={e=>{ setApprenantFiltre(p=>({...p,search:e.target.value})); setApprenantPage(1); }}
                        style={{ ...champFiltre, minWidth:240, flex:1 }} />

                      <select value={apprenantFiltre.periode} aria-label="Filtrer par période"
                        onChange={e=>{ setApprenantFiltre(p=>({...p,periode:e.target.value})); setApprenantPage(1); }}
                        style={{ ...champFiltre, cursor:"pointer" }}>
                        <option value="Tous">📅 Toutes les périodes</option>
                        {periodesDispos.map(p=><option key={p.prefix} value={p.label}>{p.label}</option>)}
                      </select>

                      <select value={apprenantFiltre.centre} aria-label="Filtrer par centre"
                        onChange={e=>{ setApprenantFiltre(p=>({...p,centre:e.target.value})); setApprenantPage(1); }}
                        style={{ ...champFiltre, cursor:"pointer" }}>
                        <option value="Tous">🏢 Tous les centres</option>
                        {CENTRES_BET.map(c=><option key={c.id} value={c.id}>{c.label.replace("BET ","")}</option>)}
                      </select>

                      <select value={apprenantFiltre.statut} aria-label="Filtrer par statut"
                        onChange={e=>{ setApprenantFiltre(p=>({...p,statut:e.target.value})); setApprenantPage(1); }}
                        style={{ ...champFiltre, cursor:"pointer" }}>
                        <option value="Tous">Tous statuts</option>
                        {Object.entries(STATUT_LABEL).map(([k,v])=><option key={k} value={k}>{v}</option>)}
                        <option value="actif">Actif</option>
                        <option value="suspendu">Suspendu</option>
                        <option value="termine">Terminé (privé)</option>
                      </select>
                    </RangeeChamps>
                  </BarreFiltres>


                  {/* Contenu */}
                  {loadingApprenants ? (
                    <div style={{ textAlign:"center", padding:"60px 0", color:"#9ca3af", fontSize:13 }}>⏳ Chargement des apprenants…</div>
                  ) : apprenants.length === 0 ? (
                    <div style={{ textAlign:"center", padding:"60px 0" }}>
                      <div style={{ fontSize:40, marginBottom:10 }}>🎓</div>
                      <div style={{ fontWeight:700, color:"#0f172a", marginBottom:6 }}>Aucun apprenant enregistré</div>
                      <p style={{ color:"#9ca3af", fontSize:13 }}>Les prospects assignés par les commerciales apparaîtront ici.</p>
                    </div>
                  ) : (
                    <>
                      <div style={{ overflowX:"auto", borderRadius:10, border:"1px solid #e5e7eb" }}>
                        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                          <thead>
                            <tr style={{ background:"#f8fafc" }}>
                              {["Apprenant","Type","Coach / Coaching","Centre","Commercial","Statut","Paiement","Inscrit le",""].map(h=>(
                                <th key={h} style={{ padding:"10px 12px", textAlign:"left", fontSize:11, fontWeight:700, color:"#9ca3af", borderBottom:"1px solid #e5e7eb", whiteSpace:"nowrap" }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {pageItems.map(a => {
                              const initiales = a.nom.split(" ").map(p=>p[0]||"").join("").slice(0,2).toUpperCase();
                              const centreLabel = CENTRES_BET.find(c=>c.id===a.centre_id)?.label.replace("BET ","") || a.centre_nom || a.centre_id || "-";
                              const st  = STATUT_STYLE[a.statut] || { bg:"#f3f4f6", color:"#374151" };
                              const pai = PAI_STYLE[a.statut_paiement] || null;
                              const isPriv = a._source === "cours_prive" || a.type_cours === "cours_prive";
                              const typeStyle = isPriv
                                ? { bg:"#fef3c7", color:"#92400e", label:"🎯 Cours privé" }
                                : a.type_cours==="en_ligne"
                                  ? { bg:"#ede9fe", color:"#7c3aed", label:"💻 En ligne" }
                                  : { bg:"#e0f2fe", color:BET_COLOR, label:"🏢 Présentiel" };
                              return (
                                <tr key={a.id} style={{ borderBottom:"1px solid #f1f5f9", background: isPriv?"#fffbeb":"#fff" }}>
                                  <td style={{ padding:"10px 12px" }}>
                                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                                      <div style={{ width:34, height:34, borderRadius:"50%", background: isPriv?"#f59e0b20":"#8b5cf620", color: isPriv?"#92400e":"#8b5cf6", fontWeight:800, fontSize:11, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{initiales}</div>
                                      <div>
                                        <div style={{ fontWeight:700, color:"#0f172a" }}>{a.nom}</div>
                                        <div style={{ fontSize:11, color:"#9ca3af" }}>{a.email}</div>
                                        {a.telephone && a.telephone!=="-" && <div style={{ fontSize:11, color:"#94a3b8" }}>{a.telephone}</div>}
                                        {isPriv && a.niveau && <div style={{ fontSize:10, color:"#d97706", fontWeight:700 }}>Niveau : {a.niveau}</div>}
                                      </div>
                                    </div>
                                  </td>
                                  <td style={{ padding:"10px 12px" }}>
                                    <span style={{ padding:"2px 8px", borderRadius:20, fontSize:11, fontWeight:700, background:typeStyle.bg, color:typeStyle.color }}>
                                      {typeStyle.label}
                                    </span>
                                  </td>
                                  <td style={{ padding:"10px 12px", fontSize:12, color:"#475569" }}>
                                    {isPriv
                                      ? <div><span style={{ fontWeight:700, color:"#0f172a" }}>{a.coach||"-"}</span>{a.prix_heure&&<div style={{ fontSize:10, color:"#9ca3af" }}>{a.prix_heure} F/h · {a.nb_seances||"-"} séances</div>}</div>
                                      : (a.type_coaching || "-")
                                    }
                                  </td>
                                  <td style={{ padding:"10px 12px" }}>
                                    {a.centre_id
                                      ? <span style={{ padding:"2px 8px", borderRadius:20, fontSize:11, fontWeight:700, background:"#e0f2fe", color:BET_COLOR }}>🏢 {centreLabel}</span>
                                      : <span style={{ fontSize:11, color:"#fca5a5", fontWeight:600 }}>⚠ Non assigné</span>}
                                  </td>
                                  <td style={{ padding:"10px 12px", fontSize:12, color:"#475569", fontWeight:600 }}>{a.commercial||"-"}</td>
                                  <td style={{ padding:"10px 12px" }}>
                                    <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, background:st.bg, color:st.color }}>
                                      {STATUT_LABEL[a.statut] || a.statut}
                                    </span>
                                  </td>
                                  <td style={{ padding:"10px 12px" }}>
                                    {pai
                                      ? <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, background:pai.bg, color:pai.color }}>{a.statut_paiement}</span>
                                      : <span style={{ fontSize:11, color:"#d1d5db" }}>-</span>}
                                  </td>
                                  <td style={{ padding:"10px 12px", fontSize:11, color:"#9ca3af", whiteSpace:"nowrap" }}>
                                    {a.created_at ? new Date(a.created_at).toLocaleDateString("fr-FR") : "-"}
                                  </td>
                                  <td style={{ padding:"10px 12px" }}>
                                    <button onClick={()=>{
                                      setSelectedApprenant(a);
                                      setSaApprenantTab("profil");
                                      setSaAccesInfo(null);
                                      const emailSA = a.email || a.prospect_email;
                                      if (emailSA) {
                                        setSaAccesLoad(true);
                                        fetch(`${API_URL}/api/apprenants/profil-acces?email=${encodeURIComponent(emailSA)}`, { headers: authHeaders() })
                                          .then(r=>r.ok?r.json():null)
                                          .then(d=>{setSaAccesInfo(d);setSaAccesLoad(false);})
                                          .catch(()=>setSaAccesLoad(false));
                                      }
                                    }} style={{ padding:"5px 12px", background:BET_LIGHT, color:BET_COLOR, border:"none", borderRadius:6, cursor:"pointer", fontWeight:700, fontSize:12 }}>Détail →</button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {filtres.length === 0 && (
                          <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af", fontSize:13 }}>Aucun apprenant ne correspond aux filtres.</div>
                        )}
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:16, padding:"0 4px" }}>
                          <span style={{ fontSize:12, color:"#9ca3af" }}>
                            {(page-1)*APPRENANTS_PER_PAGE+1}–{Math.min(page*APPRENANTS_PER_PAGE, filtres.length)} sur {filtres.length}
                          </span>
                          <div style={{ display:"flex", gap:4 }}>
                            <button onClick={()=>setApprenantPage(p=>Math.max(1,p-1))} disabled={page===1}
                              style={{ padding:"5px 10px", borderRadius:6, border:"1px solid #e5e7eb", background:page===1?"#f9fafb":"#fff", color:page===1?"#d1d5db":"#374151", cursor:page===1?"not-allowed":"pointer", fontSize:13 }}>‹</button>
                            {Array.from({length:totalPages},(_,i)=>i+1).filter(n=>n===1||n===totalPages||Math.abs(n-page)<=1).reduce((acc,n,idx,arr)=>{
                              if(idx>0&&arr[idx-1]!==n-1) acc.push("…");
                              acc.push(n); return acc;
                            },[]).map((n,i)=> n==="…"
                              ? <span key={`e${i}`} style={{ padding:"5px 6px", fontSize:13, color:"#9ca3af" }}>…</span>
                              : <button key={n} onClick={()=>setApprenantPage(n)}
                                  style={{ padding:"5px 10px", borderRadius:6, border:"1px solid", fontSize:13, cursor:"pointer", fontWeight:n===page?700:400,
                                    background:n===page?BET_COLOR:"#fff", color:n===page?"#fff":"#374151", borderColor:n===page?BET_COLOR:"#e5e7eb" }}>{n}</button>
                            )}
                            <button onClick={()=>setApprenantPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}
                              style={{ padding:"5px 10px", borderRadius:6, border:"1px solid #e5e7eb", background:page===totalPages?"#f9fafb":"#fff", color:page===totalPages?"#d1d5db":"#374151", cursor:page===totalPages?"not-allowed":"pointer", fontSize:13 }}>›</button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()}


            {/* ══ ONGLET SONDAGES (Super Admin - vue globale) ══ */}
            {activeTab === "sondages" && (() => {
              const SOURCE_ICONS = {
                "Bouche à oreille":"🗣️","Facebook / Instagram":"📱","LinkedIn":"💼",
                "Google / Recherche web":"🔍","Radio / Télévision":"📺",
                "Affichage / Flyers":"📋","Recommandé par un ami":"👫",
                "Recommandé par mon entreprise":"🏢","Autre":"✏️",
              };
              const UTM_ICONS = {
                facebook:"📘",instagram:"📸",tiktok:"🎵",linkedin:"💼",
                whatsapp:"💬",google:"🔍",youtube:"▶️",twitter:"🐦",
                email:"📧",sms:"📱",flyer:"📋",qrcode:"⬛",direct:"🌐",
              };
              const total = sondagesAll.length;
              const totalUtm = Object.values(sondageUtmStats).reduce((s,v)=>s+v,0);

              return (
                <div>
                  {/* Header */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
                    <div>
                      <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:"#0f172a" }}>🎯 Sondages & Acquisition - Vue globale</h2>
                      <p style={{ margin:"4px 0 0", fontSize:12, color:"#9ca3af" }}>Comment les prospects ont-ils entendu parler de BET ? Données manuelles + tracking automatique des liens.</p>
                    </div>
                    <button onClick={() => { fetchSondagesAll(); }} style={{ padding:"8px 16px", background:"#f1f5f9", color:"#374151", border:"none", borderRadius:8, cursor:"pointer", fontWeight:600, fontSize:12 }}>
                      🔄 Actualiser
                    </button>
                  </div>

                  {/* Stat cards */}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:24 }}>
                    <div style={{ background:"#fff", borderRadius:12, padding:16, border:"1px solid #e5e7eb", textAlign:"center" }}>
                      <div style={{ fontSize:28, fontWeight:800, color:"#7c3aed" }}>{total}</div>
                      <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>Réponses au sondage</div>
                    </div>
                    <div style={{ background:"#fff", borderRadius:12, padding:16, border:"2px solid #0891b2", textAlign:"center", position:"relative" }}>
                      <div style={{ position:"absolute", top:-8, left:"50%", transform:"translateX(-50%)", background:"#0891b2", color:"#fff", fontSize:9, fontWeight:800, padding:"2px 8px", borderRadius:99, whiteSpace:"nowrap" }}>CLICS RÉELS</div>
                      <div style={{ fontSize:28, fontWeight:800, color:"#0891b2" }}>{totalVisits}</div>
                      <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>Visites depuis liens UTM</div>
                    </div>
                    <div style={{ background:"#fff", borderRadius:12, padding:16, border:"1px solid #e5e7eb", textAlign:"center" }}>
                      <div style={{ fontSize:28, fontWeight:800, color:"#059669" }}>
                        {totalVisits > 0 ? Math.round((total / totalVisits) * 100) : 0}%
                      </div>
                      <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>Taux de réponse sondage</div>
                    </div>
                    <div style={{ background:"#fff", borderRadius:12, padding:16, border:"1px solid #e5e7eb", textAlign:"center" }}>
                      <div style={{ fontSize:28, fontWeight:800, color:"#f59e0b" }}>{Object.keys(visitStats).length}</div>
                      <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>Canaux actifs</div>
                    </div>
                  </div>

                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:24 }}>

                    {/* Réponses manuelles */}
                    <div style={{ background:"#fff", borderRadius:14, border:"1px solid #e5e7eb", padding:"20px 24px" }}>
                      <h4 style={{ margin:"0 0 4px", fontSize:13, fontWeight:800, color:"#0f172a" }}>📋 Sondage manuel</h4>
                      <p style={{ margin:"0 0 16px", fontSize:11, color:"#9ca3af" }}>Réponse choisie par le client dans son espace</p>
                      {Object.keys(sondageSrcStats).length === 0
                        ? <div style={{ textAlign:"center", padding:"30px 0", color:"#cbd5e1", fontSize:13 }}>Aucune réponse encore</div>
                        : Object.entries(sondageSrcStats).sort((a,b)=>b[1]-a[1]).map(([src, count]) => {
                            const pct = total > 0 ? Math.round((count/total)*100) : 0;
                            return (
                              <div key={src} style={{ marginBottom:12 }}>
                                <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"#374151", marginBottom:5 }}>
                                  <span>{SOURCE_ICONS[src] || "•"} {src}</span>
                                  <span style={{ fontWeight:700, color:"#7c3aed" }}>{count} ({pct}%)</span>
                                </div>
                                <div style={{ background:"#f1f5f9", borderRadius:999, height:8, overflow:"hidden" }}>
                                  <div style={{ width:`${pct}%`, height:"100%", background:"linear-gradient(90deg,#7c3aed,#a78bfa)", borderRadius:999 }} />
                                </div>
                              </div>
                            );
                          })
                      }
                    </div>

                    {/* Tracking UTM */}
                    <div style={{ background:"#fff", borderRadius:14, border:"1px solid #e5e7eb", padding:"20px 24px" }}>
                      <h4 style={{ margin:"0 0 4px", fontSize:13, fontWeight:800, color:"#0f172a" }}>🔗 Clics par canal (UTM)</h4>
                      <p style={{ margin:"0 0 16px", fontSize:11, color:"#9ca3af" }}>Comptabilisé dès le clic sur le lien - avant même le test ou le sondage</p>
                      {Object.keys(visitStats).length === 0
                        ? <div style={{ textAlign:"center", padding:"30px 0", color:"#cbd5e1", fontSize:13 }}>Aucun clic tracké encore - partagez vos liens UTM !</div>
                        : Object.entries(visitStats).sort((a,b)=>b[1]-a[1]).map(([src, count]) => {
                            const pct = totalVisits > 0 ? Math.round((count/totalVisits)*100) : 0;
                            return (
                              <div key={src} style={{ marginBottom:12 }}>
                                <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"#374151", marginBottom:5 }}>
                                  <span>{UTM_ICONS[src] || "🔗"} {src}</span>
                                  <span style={{ fontWeight:700, color:"#0891b2" }}>{count} clic{count>1?"s":""} ({pct}%)</span>
                                </div>
                                <div style={{ background:"#f1f5f9", borderRadius:999, height:8, overflow:"hidden" }}>
                                  <div style={{ width:`${pct}%`, height:"100%", background:"linear-gradient(90deg,#0891b2,#38bdf8)", borderRadius:999 }} />
                                </div>
                              </div>
                            );
                          })
                      }

                      {/* Générateur de liens */}
                      <div style={{ marginTop:20, padding:"14px 16px", background:"#f0f9ff", borderRadius:10, border:"1px solid #bae6fd" }}>
                        <div style={{ fontSize:11, fontWeight:700, color:"#0369a1", marginBottom:10 }}>🔗 Liens trackés à partager</div>
                        {["whatsapp","facebook","instagram","tiktok","linkedin","google"].map(src => {
                          /* LE SITE PUBLIC, jamais le tableau de bord. `window.location.origin`
                             renvoyait le domaine depuis lequel le marketing travaille : les liens
                             partagés sur les réseaux emmenaient donc les prospects sur
                             `app.binnie.purepro-technologie.ci` - et sur `localhost:3001` depuis un
                             poste de développement. `/test-niveau` existant dans les DEUX
                             applications, rien ne cassait : le prospect ouvrait le test interne au
                             lieu du tunnel d'inscription, sans balise GA4 ni traceur, et les UTM
                             qu'on venait de composer ne remontaient nulle part. */
                          const link = `${FRONTEND_URL}/test-niveau?utm_source=${src}&utm_medium=social&utm_campaign=bet2025`;
                          return (
                            <div key={src} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                              <span style={{ fontSize:14, flexShrink:0 }}>{UTM_ICONS[src]}</span>
                              <span style={{ fontSize:10, color:"#0369a1", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontFamily:"monospace" }}>{link}</span>
                              <button
                                onClick={() => { navigator.clipboard.writeText(link); }}
                                style={{ padding:"3px 10px", background:"#0891b2", color:"#fff", border:"none", borderRadius:6, fontSize:10, fontWeight:700, cursor:"pointer", flexShrink:0 }}
                              >Copier</button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Tableau détaillé */}
                  <div style={{ background:"#fff", borderRadius:14, border:"1px solid #e5e7eb", overflow:"hidden" }}>
                    <div style={{ padding:"16px 20px", borderBottom:"1px solid #e5e7eb", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <h4 style={{ margin:0, fontSize:13, fontWeight:800, color:"#0f172a" }}>Toutes les réponses ({total})</h4>
                    </div>
                    {sondagesLoading ? (
                      <div style={{ textAlign:"center", padding:"40px", color:"#9ca3af" }}>⏳ Chargement…</div>
                    ) : total === 0 ? (
                      <div style={{ textAlign:"center", padding:"40px", color:"#cbd5e1" }}>Aucune réponse pour l'instant</div>
                    ) : (
                      <div style={{ overflowX:"auto" }}>
                        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                          <thead>
                            <tr style={{ background:"#f9fafb", color:"#6b7280" }}>
                              <th style={{ padding:"10px 14px", textAlign:"left", fontWeight:600 }}>Email</th>
                              <th style={{ padding:"10px 14px", textAlign:"left", fontWeight:600 }}>Source (sondage)</th>
                              <th style={{ padding:"10px 14px", textAlign:"left", fontWeight:600 }}>Canal UTM</th>
                              <th style={{ padding:"10px 14px", textAlign:"left", fontWeight:600 }}>Campagne</th>
                              <th style={{ padding:"10px 14px", textAlign:"left", fontWeight:600 }}>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sondagesAll.map(s => (
                              <tr key={s.id} style={{ borderTop:"1px solid #f1f5f9" }}>
                                <td style={{ padding:"10px 14px", color:"#0f172a" }}>{s.email}</td>
                                <td style={{ padding:"10px 14px" }}>
                                  <span style={{ background:"#f3e8ff", color:"#7c3aed", padding:"2px 8px", borderRadius:6, fontWeight:600 }}>
                                    {SOURCE_ICONS[s.source] || "•"} {s.source}
                                  </span>
                                  {s.source_detail && <span style={{ fontSize:10, color:"#9ca3af", marginLeft:6 }}>{s.source_detail}</span>}
                                </td>
                                <td style={{ padding:"10px 14px" }}>
                                  {s.utm_source
                                    ? <span style={{ background:"#e0f2fe", color:"#0369a1", padding:"2px 8px", borderRadius:6, fontWeight:600 }}>{UTM_ICONS[s.utm_source] || "🔗"} {s.utm_source}</span>
                                    : <span style={{ color:"#d1d5db", fontSize:10 }}>–</span>}
                                </td>
                                <td style={{ padding:"10px 14px", color:"#6b7280", fontSize:11 }}>{s.utm_campaign || "–"}</td>
                                <td style={{ padding:"10px 14px", color:"#9ca3af" }}>
                                  {s.created_at ? new Date(s.created_at).toLocaleDateString("fr-FR", { day:"numeric", month:"short", year:"numeric" }) : "–"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

          </div>
        </div>

        {/* ══ MODAL SÉLECTION PROFIL À IMPORTER ══ */}
        {showImportRoleModal && (() => {
          const sch = SA_IMPORT_SCHEMAS[importRole];
          return (
            <Modal
              title="📥 Import multi-profil - Choisir un type de profil"
              subtitle="Sélectionnez le rôle à importer, téléchargez le modèle, puis choisissez votre fichier Excel"
              onClose={() => setShowImportRoleModal(false)}
              size="lg"
            >
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(170px,1fr))", gap:10, marginBottom:18 }}>
                {Object.entries(SA_IMPORT_SCHEMAS).map(([key, s]) => (
                  <button key={key} onClick={() => setImportRole(key)} style={{
                    padding:"14px 12px", borderRadius:12, border:`2px solid ${importRole===key ? s.couleur : "#e5e7eb"}`,
                    background: importRole===key ? s.bg : "#fff",
                    cursor:"pointer", textAlign:"left", transition:"all .15s",
                  }}>
                    <div style={{ fontSize:20, marginBottom:4 }}>{s.emoji}</div>
                    <div style={{ fontWeight:700, fontSize:12, color: importRole===key ? s.couleur : "#1f2937" }}>{s.label}</div>
                    <div style={{ fontSize:10, color:"#9ca3af", marginTop:3, lineHeight:1.4 }}>
                      {s.colonnes.filter(c=>c.requis).map(c=>c.header).join(", ")}
                    </div>
                  </button>
                ))}
              </div>
              <div style={{ background:"#f8fafc", borderRadius:10, padding:"12px 14px", marginBottom:18, border:"1px solid #e2e8f0" }}>
                <div style={{ fontWeight:700, fontSize:13, color:"#1e3a8a", marginBottom:8 }}>
                  {sch?.emoji} {sch?.label} - colonnes du fichier Excel :
                </div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {sch?.colonnes.map(c => (
                    <span key={c.key} style={{ padding:"3px 10px", borderRadius:12, fontSize:11, fontWeight:600, background: c.requis?"#dbeafe":"#f1f5f9", color: c.requis?"#1e40af":"#64748b", border:`1px solid ${c.requis?"#bfdbfe":"#e2e8f0"}` }}>
                      {c.header}{c.requis?" *":""}
                    </span>
                  ))}
                </div>
                <div style={{ fontSize:11, color:"#94a3b8", marginTop:8 }}>* champ obligatoire · les autres sont facultatifs</div>
              </div>
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", flexWrap:"wrap", alignItems:"center" }}>
                <a
                  href={`/templates/modele_import_${importRole}_bet.csv`}
                  download={`modele_import_${importRole}_bet.csv`}
                  style={{ padding:"9px 14px", background:"#f0fdf4", color:"#16a34a", border:"1.5px solid #bbf7d0", borderRadius:8, fontSize:12, fontWeight:700, textDecoration:"none" }}
                >
                  ⬇ CSV modèle
                </a>
                <button onClick={() => telechargerModeleImport(importRole)} style={{ padding:"9px 16px", background:"#fffbeb", color:"#92400e", border:"1.5px solid #fde68a", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer" }}>
                  ⬇ Modèle Excel - {sch?.label}
                </button>
                <label style={{ padding:"9px 18px", background: sch?.couleur || BET_COLOR, color:"#fff", border:"none", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
                  📂 Choisir un fichier
                  <input ref={importFileRef} type="file" accept=".xlsx,.xls,.csv"
                    onChange={(e) => { setShowImportRoleModal(false); lireEtPrevisualiser(e); }}
                    style={{ display:"none" }} />
                </label>
              </div>
            </Modal>
          );
        })()}

        {/* ══ MODAL PRÉVISUALISATION IMPORT ══ */}
        {showImportPreview && (() => {
          const nbErreurs     = importPreviewRows.filter(l => l._statut === "erreur").length;
          const nbAvertiss    = importPreviewRows.filter(l => l._statut === "avertissement").length;
          const nbOk          = importPreviewRows.filter(l => l._statut === "ok").length;
          const nbImportables = importPreviewRows.filter(l => l._statut !== "erreur").length;
          const sch           = SA_IMPORT_SCHEMAS[importRole];
          const avecCentre    = sch?.avecCentre;
          const extraLabel    = sch?.extraLabel || "Info";
          return (
            <Modal
              title={`🔍 Prévisualisation - ${sch?.emoji} ${sch?.label} - Vérifiez avant de valider`}
              subtitle={`${importPreviewRows.length} ligne(s) · ${nbOk} OK · ${nbAvertiss} avertissement(s) · ${nbErreurs} erreur(s) bloquante(s)`}
              onClose={() => { setShowImportPreview(false); setImportPreviewRows([]); }}
              size="xl"
            >
              <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
                {nbOk > 0 && <div style={{ background:"#dcfce7", color:"#16a34a", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700 }}>✅ {nbOk} valide{nbOk>1?"s":""}</div>}
                {nbAvertiss > 0 && <div style={{ background:"#fef3c7", color:"#92400e", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700 }}>⚠️ {nbAvertiss} avertissement{nbAvertiss>1?"s":""}</div>}
                {nbErreurs > 0 && <div style={{ background:"#fee2e2", color:"#dc2626", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700 }}>🚫 {nbErreurs} erreur{nbErreurs>1?"s":""} bloquante{nbErreurs>1?"s":""}</div>}
              </div>
              {nbErreurs > 0 && (
                <div style={{ background:"#fff1f2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 14px", marginBottom:14, fontSize:12, color:"#991b1b" }}>
                  ⚠️ Les lignes en rouge <strong>ne seront pas importées</strong>. Corrigez le fichier et réimportez.
                </div>
              )}
              <div style={{ overflowX:"auto", maxHeight:420, overflowY:"auto", marginBottom:16, borderRadius:10, border:"1.5px solid #e2e8f0" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                  <thead style={{ position:"sticky", top:0, zIndex:2 }}>
                    <tr style={{ background:"#f8fafc" }}>
                      {["#","Statut","Prénom","Nom","Email","Téléphone",
                        ...(avecCentre ? ["Centre BET"] : []),
                        extraLabel,
                        "Problèmes détectés",
                      ].map(h => (
                        <th key={h} style={{ padding:"9px 10px", textAlign:"left", fontWeight:700, fontSize:11, color:"#64748b", borderBottom:"2px solid #e2e8f0", whiteSpace:"nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {importPreviewRows.map((l, i) => {
                      const bgRow = l._statut==="erreur" ? "#fff1f2" : l._statut==="avertissement" ? "#fffbeb" : "#f0fdf4";
                      const allIssues = [
                        ...l._erreurs.map(e2 => ({ text:e2, type:"erreur" })),
                        ...l._avertissements.map(w => ({ text:w, type:"avertissement" })),
                      ];
                      const extraVal = sch?.extraInfo ? sch.extraInfo(l) : "-";
                      return (
                        <tr key={i} style={{ borderBottom:"1px solid #f1f5f9", background:bgRow }}>
                          <td style={{ padding:"8px 10px", color:"#94a3b8", fontSize:11, fontWeight:600 }}>{l._idx}</td>
                          <td style={{ padding:"8px 10px", whiteSpace:"nowrap" }}>
                            {l._statut==="erreur"
                              ? <span style={{ background:"#fee2e2", color:"#dc2626", borderRadius:20, padding:"2px 8px", fontSize:10, fontWeight:700 }}>🚫 Erreur</span>
                              : l._statut==="avertissement"
                              ? <span style={{ background:"#fef3c7", color:"#92400e", borderRadius:20, padding:"2px 8px", fontSize:10, fontWeight:700 }}>⚠️ Attention</span>
                              : <span style={{ background:"#dcfce7", color:"#16a34a", borderRadius:20, padding:"2px 8px", fontSize:10, fontWeight:700 }}>✅ Valide</span>
                            }
                          </td>
                          <td style={{ padding:"8px 10px", fontWeight:600 }}>{l.prenom || <span style={{ color:"#ef4444" }}>-</span>}</td>
                          <td style={{ padding:"8px 10px", fontWeight:600 }}>{l.nom || <span style={{ color:"#ef4444" }}>-</span>}</td>
                          <td style={{ padding:"8px 10px", color:"#374151", maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{l.email || <span style={{ color:"#ef4444" }}>-</span>}</td>
                          <td style={{ padding:"8px 10px", color:"#64748b", whiteSpace:"nowrap" }}>{l.tel || <span style={{ color:"#d1d5db", fontSize:11 }}>-</span>}</td>
                          {avecCentre && (
                            <td style={{ padding:"8px 10px", whiteSpace:"nowrap" }}>
                              {l.centre_id
                                ? <span style={{ background:"#e0f2fe", color:"#0369a1", borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:600 }}>🏢 {l.centre_id}</span>
                                : <span style={{ background:"#f1f5f9", color:"#64748b", borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:600 }}>🌍 national</span>
                              }
                            </td>
                          )}
                          <td style={{ padding:"8px 10px", color:"#64748b", maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontSize:11 }}>{extraVal}</td>
                          <td style={{ padding:"8px 10px", maxWidth:240 }}>
                            {allIssues.length === 0
                              ? <span style={{ color:"#86efac", fontSize:11 }}>Aucun problème</span>
                              : <ul style={{ margin:0, paddingLeft:14 }}>
                                  {allIssues.map((iss, j) => (
                                    <li key={j} style={{ fontSize:11, color: iss.type==="erreur"?"#dc2626":"#92400e", fontWeight: iss.type==="erreur"?700:500, marginBottom:2 }}>
                                      {iss.text}
                                    </li>
                                  ))}
                                </ul>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", alignItems:"center", flexWrap:"wrap" }}>
                {nbErreurs > 0 && <span style={{ fontSize:11, color:"#94a3b8", marginRight:"auto" }}>{nbImportables} ligne{nbImportables>1?"s":""} importable{nbImportables>1?"s":""} sur {importPreviewRows.length}</span>}
                <button onClick={() => { setShowImportPreview(false); setImportPreviewRows([]); }}
                  style={{ padding:"9px 18px", background:"#f1f5f9", border:"none", borderRadius:8, fontSize:12, color:"#374151", cursor:"pointer", fontWeight:600 }}>
                  ✕ Fermer et corriger
                </button>
                {nbImportables > 0 && (
                  <button onClick={confirmerImport} disabled={importConfirming}
                    style={{ padding:"9px 20px", background: nbErreurs>0 ? "#f59e0b" : (sch?.couleur || "#16a34a"), border:"none", borderRadius:8, fontSize:12, color:"#fff", cursor:"pointer", fontWeight:700, opacity: importConfirming?0.7:1 }}>
                    {importConfirming ? "Import en cours…" : nbErreurs > 0
                      ? `⚠️ Importer uniquement les ${nbImportables} ligne${nbImportables>1?"s":""} valides`
                      : `✅ Valider et importer ${nbImportables} ${sch?.label || "profil"}${nbImportables>1?"s":""}`
                    }
                  </button>
                )}
              </div>
            </Modal>
          );
        })()}

        {/* MODALE RÉCAP IMPORT */}
        {showImportResultat && importResultats.length > 0 && (
          <Modal title={`✅ Import terminé - ${SA_IMPORT_SCHEMAS[importRole]?.label || "Profils"} créés`} subtitle="Un email a été envoyé automatiquement. Conservez ce récap." onClose={()=>setShowImportResultat(false)} size="xl">
            <div style={{ background:"#fef3c7", borderLeft:"4px solid #f59e0b", borderRadius:8, padding:"10px 14px", marginBottom:16, fontSize:12, color:"#92400e" }}>
              ⚠️ <strong>Ces mots de passe sont temporaires.</strong> Chaque coach devra le changer dès sa première connexion. Ils ne seront plus visibles après fermeture.
            </div>
            <div style={{ overflowX:"auto", marginBottom:16 }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <thead>
                  <tr style={{ background:"#f8fafc" }}>
                    {["Prénom","Nom","Email","Mot de passe temporaire","Statut"].map(h=>(
                      <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontWeight:700, fontSize:11, color:"#64748b", borderBottom:"2px solid #e2e8f0", whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {importResultats.map((r,i)=>(
                    <tr key={i} style={{ borderBottom:"1px solid #f1f5f9", background:r.statut==="ok"?"#fff":"#fff1f2" }}>
                      <td style={{ padding:"8px 12px", fontWeight:600 }}>{r.prenom}</td>
                      <td style={{ padding:"8px 12px", fontWeight:600 }}>{r.nom}</td>
                      <td style={{ padding:"8px 12px", color:"#374151" }}>{r.email}</td>
                      <td style={{ padding:"8px 12px" }}>
                        {r.statut==="ok"
                          ? <span style={{ fontFamily:"monospace", fontWeight:800, fontSize:13, color:"#0891b2", background:"#e0f2fe", padding:"3px 9px", borderRadius:6, letterSpacing:1 }}>{r.mdp}</span>
                          : <span style={{ color:"#ef4444", fontSize:11 }}>Erreur : {r.detail}</span>
                        }
                      </td>
                      <td style={{ padding:"8px 12px" }}>
                        {r.statut==="ok"
                          ? <span style={{ background:"#dcfce7", color:"#16a34a", borderRadius:20, padding:"2px 9px", fontSize:10, fontWeight:700 }}>✓ Créé</span>
                          : <span style={{ background:"#fee2e2", color:"#dc2626", borderRadius:20, padding:"2px 9px", fontSize:10, fontWeight:700 }}>✗ Erreur</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={()=>{
                const okRows = importResultats.filter(r=>r.statut==="ok");
                exporterExcelStyle({
                  nomFichier: `identifiants_coachs_${new Date().toISOString().slice(0,10)}`,
                  titre: '🔑 Identifiants coachs créés',
                  sousTitre: `${okRows.length} compte${okRows.length>1?'s':''} créé${okRows.length>1?'s':''} le ${new Date().toLocaleDateString('fr-FR')}`,
                  couleurEntete: '16A34A',
                  colonnes: [
                    { label: 'Prénom', width: 18 }, { label: 'Nom', width: 18, bold: true },
                    { label: 'Email', width: 30 }, { label: 'Mot de passe temporaire', width: 24 },
                    { label: 'Statut', width: 12 },
                  ],
                  lignes: okRows.map(r => [r.prenom, r.nom, r.email, r.mdp, "Créé"]),
                });
                toast.success("Fichier identifiants téléchargé ✓");
              }} style={{ padding:"9px 18px", background:"#16a34a", border:"none", borderRadius:8, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                📥 Exporter identifiants Excel
              </button>
              <button onClick={()=>setShowImportResultat(false)} style={{ padding:"9px 18px", background:"#f1f5f9", border:"none", borderRadius:8, fontSize:12, color:"#374151", cursor:"pointer", fontWeight:600 }}>Fermer</button>
            </div>
          </Modal>
        )}

        {/* MODALES (invitation, utilisateur, révocation, clone, demande) identiques à AdminDashboard */}
        {showInviteModal && (
          <Modal title="Créer un utilisateur" subtitle="Un mot de passe temporaire sera généré automatiquement" onClose={()=>setShowInviteModal(false)}>
            {/* Photo de profil - commune à tous les rôles */}
            <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:16, padding:"14px 16px", background:"#f8fafc", borderRadius:10, border:"1px solid #e5e7eb" }}>
              <div style={{ width:72, height:72, borderRadius:"50%", overflow:"hidden", background:"#e5e7eb", border:"2px solid #d1d5db", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                {inviteForm.photo
                  ? <img src={inviteForm.photo} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                  : <span style={{ fontSize:30 }}>👤</span>
                }
              </div>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:6 }}>Photo de profil</div>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <label style={{ padding:"7px 14px", background:invitePhotoUploading?"#9ca3af":"#0f172a", color:"#fff", borderRadius:7, fontSize:12, fontWeight:600, cursor:invitePhotoUploading?"not-allowed":"pointer", display:"inline-block" }}>
                    {invitePhotoUploading ? "⏳ Upload…" : "📷 Choisir une photo"}
                    <input type="file" accept="image/*" style={{ display:"none" }} disabled={invitePhotoUploading}
                      onChange={e => { if (e.target.files?.[0]) uploadInvitePhoto(e.target.files[0]); }} />
                  </label>
                  {inviteForm.photo && (
                    <button onClick={()=>setInviteForm(p=>({...p,photo:""}))}
                      style={{ padding:"6px 10px", background:"#fee2e2", color:"#dc2626", border:"none", borderRadius:7, fontSize:11, cursor:"pointer", fontWeight:600 }}>
                      ✕ Retirer
                    </button>
                  )}
                </div>
                <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>Optionnel - JPG, PNG recommandé</div>
              </div>
            </div>

            {/* Identité */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
              <div><label style={{ display:"block", fontSize:12, fontWeight:600, color:"#374151", marginBottom:4 }}>Nom complet *</label><input value={inviteForm.nom} onChange={e=>setInviteForm({...inviteForm,nom:e.target.value})} style={{ padding:9, borderRadius:6, border:"1px solid #d1d5db", fontSize:13, width:"100%" }} placeholder="Prénom Nom"/></div>
              <div><label style={{ display:"block", fontSize:12, fontWeight:600, color:"#374151", marginBottom:4 }}>Téléphone</label><input value={inviteForm.telephone} onChange={e=>setInviteForm({...inviteForm,telephone:e.target.value})} style={{ padding:9, borderRadius:6, border:"1px solid #d1d5db", fontSize:13, width:"100%" }} placeholder="+225 07 …"/></div>
            </div>
            <div style={{ marginBottom:14 }}><label style={{ display:"block", fontSize:12, fontWeight:600, color:"#374151", marginBottom:4 }}>Email *</label><input type="email" value={inviteForm.email} onChange={e=>setInviteForm({...inviteForm,email:e.target.value})} style={{ padding:9, borderRadius:6, border:"1px solid #d1d5db", fontSize:13, width:"100%" }} placeholder="email@domaine.ci"/></div>

            {/* Rôle */}
            <label style={{ display:"block", fontSize:12, fontWeight:600, color:"#374151", marginBottom:6 }}>Rôle à attribuer *</label>
            {/* Section Backoffice */}
            <div style={{ fontSize:11, fontWeight:700, color:"#64748b", letterSpacing:"0.05em", marginBottom:6, textTransform:"uppercase" }}>🏢 Backoffice / Administration</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12 }}>
              {/* "assistant_elearning" retiré de la création : un seul rôle pour cette
                  fonction - "Commercial" avec le profil "Corporate" + l'accès e-learning
                  activé. Évite d'avoir deux comptes/rôles physiques pour le même métier. */}
              {Object.values(ROLES_DEF).filter(r=>r.id!=="super_admin" && !r.id.startsWith("admin_") && r.id!=="assistant_elearning").map(r=>(
                <div key={r.id} onClick={()=>setInviteForm({...inviteForm,role:r.id,centre_id:"",couleur:r.color})} style={{ padding:"10px 12px", borderRadius:10, border:`2px solid ${inviteForm.role===r.id?r.color:"#e5e7eb"}`, background:inviteForm.role===r.id?r.color+"08":"#fff", cursor:"pointer" }}>
                  <div style={{ fontWeight:700, color:inviteForm.role===r.id?r.color:"#0f172a", fontSize:13 }}>{r.emoji} {r.label}</div>
                  <div style={{ fontSize:10, color:"#9ca3af", marginTop:2 }}>{r.description.slice(0,55)}…</div>
                </div>
              ))}
            </div>
            {/* Section E-Learning BET - ne garde que les rôles qui n'ont aucun équivalent
                Backoffice ci-dessus (ex: Agent Placement/Tests). Tous les autres "admin_*"
                (admin_onboarding, admin_elearning, admin_customer_care, admin_commercial,
                admin_pa_advisor, admin_rh_paie, admin_comptable) dupliquaient exactement un
                rôle Backoffice existant et créaient un rôle physique séparé à configurer en
                double dans la matrice de permissions bet-elearning - source de confusion. */}
            <div style={{ fontSize:11, fontWeight:700, color:"#7c3aed", letterSpacing:"0.05em", marginBottom:6, textTransform:"uppercase" }}>🎓 Plateforme E-Learning BET</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:14 }}>
              {Object.values(ROLES_DEF).filter(r=>r.id==="admin_placement").map(r=>(
                <div key={r.id} onClick={()=>setInviteForm({...inviteForm,role:r.id,centre_id:"",couleur:r.color})} style={{ padding:"10px 12px", borderRadius:10, border:`2px solid ${inviteForm.role===r.id?r.color:"#e5e7eb"}`, background:inviteForm.role===r.id?r.color+"08":"#fff", cursor:"pointer" }}>
                  <div style={{ fontWeight:700, color:inviteForm.role===r.id?r.color:"#0f172a", fontSize:13 }}>{r.emoji} {r.label}</div>
                  <div style={{ fontSize:10, color:"#9ca3af", marginTop:2 }}>{r.description.slice(0,55)}…</div>
                </div>
              ))}
            </div>

            {/* Centre BET - pour les rôles qui en ont besoin (commerciale en ligne exclue) */}
            {ROLES_AVEC_CENTRE.includes(inviteForm.role) && !(inviteForm.role === "commercial" && inviteForm.type_cours === "en_ligne") && !(inviteForm.role === "commercial" && inviteForm.profil_assistante === "b2b") && (
              <div style={{ marginBottom:14 }}>
                <label style={{ display:"block", fontSize:12, fontWeight:600, color:inviteForm.centre_id?"#1e3a8a":"#dc2626", marginBottom:4 }}>🏢 Centre BET *</label>
                <select value={inviteForm.centre_id} onChange={e=>setInviteForm({...inviteForm,centre_id:e.target.value})}
                  style={{ padding:9, borderRadius:6, border:`2px solid ${inviteForm.centre_id?"#1e3a8a":"#fca5a5"}`, fontSize:13, width:"100%", background:"#fff" }}>
                  <option value="">- Choisir le centre -</option>
                  {centresList.length > 0
                    ? centresList.map(c => <option key={c.id} value={c.id}>{c.nom}{c.ville ? ` - ${c.ville}` : ""}</option>)
                    : CENTRES_BET.map(c => <option key={c.id} value={c.id}>{c.label}</option>)
                  }
                </select>
                {centresList.length === 0 && (
                  <div style={{ fontSize:11, color:"#d97706", marginTop:4 }}>⚠️ Chargement des centres en cours…</div>
                )}
              </div>
            )}

            {/* Info : rôles sans centre physique (transversaux/nationaux) */}
            {["onboarding","superviseur","deputy_superviseur","admin","manager","gestionnaire","pedagogical_advisor"].includes(inviteForm.role) && (
              <div style={{ marginBottom:14, padding:"12px 16px", borderRadius:10, background:"#f0f9ff", border:"1.5px solid #bae6fd", display:"flex", alignItems:"flex-start", gap:10 }}>
                <span style={{ fontSize:18 }}>
                  {inviteForm.role === "onboarding" ? "💻" : ["admin","manager","gestionnaire","pedagogical_advisor"].includes(inviteForm.role) ? "🌍" : "📋"}
                </span>
                <div>
                  <div style={{ fontWeight:700, fontSize:12, color:"#0369a1", marginBottom:2 }}>
                    {inviteForm.role === "onboarding"        && "Assistante Onboarding - travail en ligne"}
                    {inviteForm.role === "superviseur"       && "Superviseur Global - périmètre national"}
                    {inviteForm.role === "deputy_superviseur" && "Deputy Superviseur - périmètre national"}
                    {inviteForm.role === "admin"             && "Administrateur - périmètre national"}
                    {inviteForm.role === "manager"           && "Manager - périmètre national"}
                    {inviteForm.role === "gestionnaire"      && "Gestionnaire - périmètre national"}
                    {inviteForm.role === "pedagogical_advisor" && "PA Advisor - périmètre national"}
                  </div>
                  <div style={{ fontSize:11, color:"#0284c7" }}>
                    Ce profil a un périmètre transversal - aucun centre BET spécifique requis à la création.
                  </div>
                </div>
              </div>
            )}

            {/* ── Section planning (assistante commerciale uniquement) ── */}
            {inviteForm.role === "commercial" && (
              <div style={{ marginBottom:14, padding:"16px 18px", borderRadius:12, background:"#f0fdf4", border:"2px solid #bbf7d0" }}>
                <div style={{ fontWeight:700, fontSize:13, color:"#166534", marginBottom:14, display:"flex", alignItems:"center", gap:6 }}>
                  📅 Planning de l'assistante commerciale
                </div>

                {/* Profil B2C / B2B */}
                <div style={{ marginBottom:14 }}>
                  <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#374151", marginBottom:6 }}>Profil de l'assistante</label>
                  <div style={{ display:"flex", gap:8 }}>
                    {[{v:"b2c",l:"👤 B2C - Particuliers"},{v:"b2b",l:"🏢 Corporate - Entreprises"}].map(opt=>(
                      <button key={opt.v} onClick={()=>setInviteForm({
                        ...inviteForm,
                        profil_assistante: opt.v,
                        // Corporate B2B : toujours en ligne, pas de centre physique
                        ...(opt.v === "b2b" ? { type_cours:"en_ligne", centre_id:"", onboarding_id:"" } : {}),
                      })}
                        style={{ flex:1, padding:"8px 6px", borderRadius:8, border:`2px solid ${inviteForm.profil_assistante===opt.v?"#0891b2":"#e5e7eb"}`,
                          background:inviteForm.profil_assistante===opt.v?"#e0f2fe":"#fff",
                          color:inviteForm.profil_assistante===opt.v?"#0891b2":"#374151",
                          fontWeight:700, fontSize:11, cursor:"pointer" }}>
                        {opt.l}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Type de cours - masqué pour B2B (toujours en ligne) */}
                {inviteForm.profil_assistante !== "b2b" && (
                  <div style={{ marginBottom:12 }}>
                    <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#374151", marginBottom:6 }}>Type de coaching</label>
                    <div style={{ display:"flex", gap:8 }}>
                      {[{v:"en_ligne",l:"💻 En ligne"},{v:"presentiel",l:"🏢 Présentiel"}].map(opt=>(
                        <button key={opt.v} onClick={()=>setInviteForm({...inviteForm,type_cours:opt.v,centre_id:""})}
                          style={{ flex:1, padding:"8px 6px", borderRadius:8, border:`2px solid ${inviteForm.type_cours===opt.v?"#22c55e":"#e5e7eb"}`,
                            background:inviteForm.type_cours===opt.v?"#dcfce7":"#fff",
                            color:inviteForm.type_cours===opt.v?"#166534":"#374151",
                            fontWeight:700, fontSize:11, cursor:"pointer" }}>
                          {opt.l}
                        </button>
                      ))}
                    </div>
                    {inviteForm.type_cours === "en_ligne" && (
                      <div style={{ marginTop:8, padding:"8px 12px", borderRadius:8, background:"#f0f9ff", border:"1px solid #bae6fd", fontSize:11, color:"#0369a1" }}>
                        💻 Assistante en ligne - aucun centre physique requis
                      </div>
                    )}
                  </div>
                )}

                {/* Jours de service */}
                <div>
                  <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#374151", marginBottom:8 }}>
                    Jours de service · <span style={{ color:"#16a34a" }}>{inviteForm.jours_travail.length} sélectionné(s)</span>
                  </label>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {["lundi","mardi","mercredi","jeudi","vendredi","samedi","dimanche"].map(jour=>{
                      const COURT = { lundi:"L", mardi:"M", mercredi:"Me", jeudi:"J", vendredi:"V", samedi:"S", dimanche:"D" };
                      const actif = inviteForm.jours_travail.includes(jour);
                      return (
                        <button key={jour} title={jour.charAt(0).toUpperCase()+jour.slice(1)}
                          onClick={()=>{
                            const nv = actif ? inviteForm.jours_travail.filter(j=>j!==jour) : [...inviteForm.jours_travail,jour];
                            setInviteForm({...inviteForm,jours_travail:nv});
                          }}
                          style={{ width:34, height:34, borderRadius:999, border:`1.5px solid ${actif?"#22c55e":"#e2e8f0"}`,
                            background:actif?"#22c55e":"#f8fafc", color:actif?"#fff":"#94a3b8",
                            fontWeight:800, fontSize:11, cursor:"pointer" }}>
                          {COURT[jour]}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Assignation assistante onboarding - masquée pour B2B et Assistante centre */}
                {inviteForm.profil_assistante !== "b2b" && inviteForm.type_cours !== "presentiel" ? (
                  <div style={{ marginTop:14 }}>
                    <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#374151", marginBottom:4 }}>
                      🎯 Assistante Onboarding assignée <span style={{ color:"#dc2626" }}>*</span>
                    </label>
                    {onboardingList.length === 0 ? (
                      <div style={{ fontSize:11, color:"#9ca3af", padding:"8px 10px", background:"#f8fafc", borderRadius:7 }}>Aucune assistante onboarding créée pour l'instant.</div>
                    ) : (
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:8 }}>
                        <button type="button" onClick={()=>setInviteForm({...inviteForm,onboarding_id:""})}
                          style={{ textAlign:"left", padding:"10px 12px", borderRadius:9, cursor:"pointer",
                            border:`2px solid ${inviteForm.onboarding_id===""?"#0891b2":"#e2e8f0"}`,
                            background:inviteForm.onboarding_id===""?"#e0f2fe":"#fff" }}>
                          <div style={{ fontSize:12, fontWeight:700, color:"#374151" }}>- Aucune assignation -</div>
                        </button>
                        {onboardingList.map(o=>{
                          const selected = inviteForm.onboarding_id === o.id;
                          const initiales = `${o.prenom?.[0]||""}${o.nom?.[0]||""}`.toUpperCase();
                          const nb = o.nb_commerciaux_assignes || 0;
                          return (
                            <button key={o.id} type="button" onClick={()=>setInviteForm({...inviteForm,onboarding_id:o.id})}
                              style={{ display:"flex", alignItems:"center", gap:10, textAlign:"left", padding:"10px 12px", borderRadius:9, cursor:"pointer",
                                border:`2px solid ${selected?"#0891b2":"#e2e8f0"}`,
                                background:selected?"#e0f2fe":"#fff", opacity:o.actif===false?0.55:1 }}>
                              <div style={{ width:36, height:36, borderRadius:"50%", flexShrink:0, background:o.couleur||"#0891b2", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:13 }}>
                                {initiales || "?"}
                              </div>
                              <div style={{ minWidth:0 }}>
                                <div style={{ fontSize:12, fontWeight:700, color:"#0f172a", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                                  {o.prenom} {o.nom} {o.actif===false && <span style={{ color:"#dc2626", fontWeight:600 }}>⏸</span>}
                                </div>
                                <div style={{ fontSize:10, color:"#64748b" }}>📞 {o.telephone || "-"}</div>
                                <div style={{ fontSize:10, color:"#0891b2", fontWeight:600 }}>📋 {nb} suivi{nb>1?"es":"e"}</div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : inviteForm.type_cours === "presentiel" ? (
                  <div style={{ marginTop:14, padding:"10px 14px", borderRadius:8, background:"#f0fdf4", border:"1px solid #bbf7d0", fontSize:11, color:"#166534" }}>
                    🏢 <strong>Assistante centre (présentiel)</strong> - elle gère ses prospects et classes directement, sans assistante onboarding.
                  </div>
                ) : (
                  <div style={{ marginTop:14, padding:"10px 14px", borderRadius:8, background:"#eff6ff", border:"1px solid #bfdbfe", fontSize:11, color:"#1d4ed8" }}>
                    🏢 <strong>Assistante Corporate B2B</strong> - travail entièrement en ligne, sans centre physique ni onboarding BET. Elle gère les entreprises et leurs collaborateurs.
                  </div>
                )}
              </div>
            )}

            {/* ── Section Coach ── */}
            {inviteForm.role === "coach" && (
              <div style={{ marginBottom:14, padding:"16px 18px", borderRadius:12, background:"#eef2ff", border:"2px solid #c7d2fe" }}>
                <div style={{ fontWeight:700, fontSize:13, color:"#3730a3", marginBottom:14, display:"flex", alignItems:"center", gap:6 }}>
                  🎓 Profil coach
                </div>

                {/* Matricule + Filière */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                  <div>
                    <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#374151", marginBottom:4 }}>Matricule</label>
                    <input value={inviteForm.coach_matricule} onChange={e=>setInviteForm(p=>({...p,coach_matricule:e.target.value}))}
                      style={{ padding:"8px 10px", border:"1.5px solid #c7d2fe", borderRadius:7, fontSize:13, width:"100%", boxSizing:"border-box" }}
                      placeholder="EX : BET-C-001" />
                  </div>
                  <div>
                    <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#374151", marginBottom:4 }}>Filière / Spécialité</label>
                    <select value={inviteForm.coach_filiere} onChange={e=>setInviteForm(p=>({...p,coach_filiere:e.target.value}))}
                      style={{ padding:"8px 10px", border:"1.5px solid #c7d2fe", borderRadius:7, fontSize:13, width:"100%", background:"#fff", boxSizing:"border-box" }}>
                      <option value="">- Choisir -</option>
                      {["Anglais Général","Anglais Business","Préparation TOEIC","Préparation IELTS","Préparation TOEFL","Anglais Enfants","Prise de parole","Traduction","Autre"].map(f=>(
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Lieu d'habitation + Date début */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                  <div>
                    <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#374151", marginBottom:4 }}>Lieu d'habitation</label>
                    <input value={inviteForm.coach_lieu_habitation} onChange={e=>setInviteForm(p=>({...p,coach_lieu_habitation:e.target.value}))}
                      style={{ padding:"8px 10px", border:"1.5px solid #c7d2fe", borderRadius:7, fontSize:13, width:"100%", boxSizing:"border-box" }}
                      placeholder="Ex : Cocody, Abidjan" />
                  </div>
                  <div>
                    <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#374151", marginBottom:4 }}>Date de début à BET</label>
                    <input type="date" value={inviteForm.coach_date_debut} onChange={e=>setInviteForm(p=>({...p,coach_date_debut:e.target.value}))}
                      style={{ padding:"8px 10px", border:"1.5px solid #c7d2fe", borderRadius:7, fontSize:13, width:"100%", boxSizing:"border-box" }} />
                  </div>
                </div>

                {/* Nb contrats actifs */}
                <div style={{ marginBottom:14 }}>
                  <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#374151", marginBottom:4 }}>Nombre de contrats actifs</label>
                  <input type="number" min={0} value={inviteForm.coach_nb_contrats}
                    onChange={e=>setInviteForm(p=>({...p,coach_nb_contrats:parseInt(e.target.value)||0}))}
                    style={{ width:100, padding:"8px 10px", border:"1.5px solid #c7d2fe", borderRadius:7, fontSize:14, fontWeight:700, textAlign:"center" }} />
                </div>

                {/* Niveaux CECRL enseignés */}
                <div style={{ marginBottom:14 }}>
                  <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#374151", marginBottom:6 }}>📊 Niveaux enseignés (CECRL)</label>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                    {["A1","A2","B1","B2","C1","C2"].map(niv => {
                      const CCOL = { A1:"#6b7280",A2:"#d97706",B1:"#1a4fd6",B2:"#7c3aed",C1:"#059669",C2:"#dc2626" };
                      const CBGC = { A1:"#f3f4f6",A2:"#fef3c7",B1:"#dbeafe",B2:"#ede9fe",C1:"#dcfce7",C2:"#fee2e2" };
                      const active = inviteForm.coach_niveaux.includes(niv);
                      return (
                        <button key={niv} type="button"
                          onClick={() => setInviteForm(p => ({
                            ...p,
                            coach_niveaux: active ? p.coach_niveaux.filter(n=>n!==niv) : [...p.coach_niveaux, niv]
                          }))}
                          style={{ padding:"5px 14px", borderRadius:8, fontSize:12, fontWeight:800, cursor:"pointer",
                            border:`2px solid ${active ? CCOL[niv] : "#c7d2fe"}`,
                            background: active ? CBGC[niv] : "#fff",
                            color: active ? CCOL[niv] : "#9ca3af" }}>
                          {active ? "✓ " : ""}{niv}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Certifications */}
                <div>
                  <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#374151", marginBottom:6 }}>🏆 Certifications</label>
                  {/* Quick-add badges */}
                  <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:8 }}>
                    {["CELTA","DELTA","TEFL","TOEIC Expert","IELTS 7+","TOEFL iBT","Cambridge TKT","Diplôme universitaire"].map(cert => {
                      const active = inviteForm.coach_certifications.includes(cert);
                      return (
                        <button key={cert} type="button"
                          onClick={() => setInviteForm(p => ({
                            ...p,
                            coach_certifications: active
                              ? p.coach_certifications.filter(c=>c!==cert)
                              : [...p.coach_certifications, cert]
                          }))}
                          style={{ padding:"4px 10px", borderRadius:20, fontSize:11, fontWeight:600, cursor:"pointer", border:`1.5px solid ${active?"#6366f1":"#c7d2fe"}`, background:active?"#6366f1":"#fff", color:active?"#fff":"#6366f1" }}>
                          {active ? "✓ " : "+ "}{cert}
                        </button>
                      );
                    })}
                  </div>
                  {/* Saisie libre */}
                  <div style={{ display:"flex", gap:6 }}>
                    <input value={inviteForm.coach_certif_input}
                      onChange={e=>setInviteForm(p=>({...p,coach_certif_input:e.target.value}))}
                      onKeyDown={e=>{
                        if ((e.key==="Enter"||e.key===",") && inviteForm.coach_certif_input.trim()) {
                          e.preventDefault();
                          const v=inviteForm.coach_certif_input.trim().replace(/,$/,"");
                          if (v && !inviteForm.coach_certifications.includes(v))
                            setInviteForm(p=>({...p,coach_certifications:[...p.coach_certifications,v],coach_certif_input:""}));
                          else setInviteForm(p=>({...p,coach_certif_input:""}));
                        }
                      }}
                      placeholder="Autre certification… (Entrée pour ajouter)"
                      style={{ flex:1, padding:"7px 10px", border:"1.5px solid #c7d2fe", borderRadius:7, fontSize:12 }} />
                    <button type="button"
                      onClick={()=>{
                        const v=inviteForm.coach_certif_input.trim();
                        if (v && !inviteForm.coach_certifications.includes(v))
                          setInviteForm(p=>({...p,coach_certifications:[...p.coach_certifications,v],coach_certif_input:""}));
                      }}
                      style={{ padding:"7px 12px", background:"#6366f1", color:"#fff", border:"none", borderRadius:7, fontSize:12, fontWeight:600, cursor:"pointer" }}>+ Ajouter</button>
                  </div>
                  {/* Tags ajoutés */}
                  {inviteForm.coach_certifications.length > 0 && (
                    <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:8 }}>
                      {inviteForm.coach_certifications.map(c => (
                        <span key={c} style={{ display:"flex", alignItems:"center", gap:4, padding:"3px 10px", borderRadius:20, background:"#ede9fe", color:"#5b21b6", fontSize:11, fontWeight:600 }}>
                          🏆 {c}
                          <button type="button" onClick={()=>setInviteForm(p=>({...p,coach_certifications:p.coach_certifications.filter(x=>x!==c)}))}
                            style={{ background:"none", border:"none", cursor:"pointer", color:"#7c3aed", fontWeight:800, fontSize:12, padding:0, lineHeight:1 }}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Note */}
            <label style={{ display:"block", fontSize:12, fontWeight:600, color:"#374151", marginBottom:4 }}>Note interne (optionnel)</label>
            <textarea value={inviteForm.note} onChange={e=>setInviteForm({...inviteForm,note:e.target.value})} style={{ padding:9, borderRadius:6, border:"1px solid #d1d5db", fontSize:13, width:"100%", minHeight:52, resize:"vertical", marginBottom:14 }} placeholder="Note visible uniquement par les admins…"/>

            {/* ── Couleur du profil ── */}
            {(() => {
              const PALETTE = [
                { hex:"#0891b2", label:"Cyan (BET)" },
                { hex:"#7c3aed", label:"Violet" },
                { hex:"#10b981", label:"Émeraude" },
                { hex:"#f59e0b", label:"Ambre" },
                { hex:"#dc2626", label:"Rouge" },
                { hex:"#0d9488", label:"Teal" },
                { hex:"#d97706", label:"Or" },
                { hex:"#0284c7", label:"Bleu" },
                { hex:"#6366f1", label:"Indigo" },
                { hex:"#db2777", label:"Rose" },
                { hex:"#b45309", label:"Brun" },
                { hex:"#0f172a", label:"Ardoise" },
              ];
              const c = inviteForm.couleur || "#0891b2";
              const roleLabel = ROLES_DEF[inviteForm.role]?.label || inviteForm.role;
              const roleEmoji = ROLES_DEF[inviteForm.role]?.emoji || "👤";
              return (
                <div style={{ marginBottom:16, padding:"16px 18px", borderRadius:12, background:"#f8fafc", border:"1.5px solid #e5e7eb" }}>
                  <label style={{ display:"block", fontSize:12, fontWeight:700, color:"#374151", marginBottom:12 }}>🎨 Couleur du dashboard</label>

                  {/* Preview header */}
                  <div style={{ borderRadius:10, overflow:"hidden", marginBottom:14, boxShadow:"0 2px 8px rgba(0,0,0,0.12)" }}>
                    <div style={{ background:`linear-gradient(135deg, #0f172a 0%, ${c} 100%)`, padding:"12px 16px" }}>
                      <div style={{ fontSize:11, color:"rgba(255,255,255,0.6)", marginBottom:3 }}>Aperçu du header</div>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ width:36, height:36, borderRadius:"50%", background:`${c}40`, border:`2px solid ${c}80`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>{roleEmoji}</div>
                        <div>
                          <div style={{ fontWeight:800, color:"#fff", fontSize:14 }}>{inviteForm.nom || "Prénom Nom"}</div>
                          <div style={{ fontSize:11, color:"rgba(255,255,255,0.7)" }}>{roleLabel}</div>
                        </div>
                        <div style={{ marginLeft:"auto", display:"flex", gap:6 }}>
                          {["Tableau de bord","Clients","Rapports"].slice(0,2).map(t=>(
                            <span key={t} style={{ padding:"4px 10px", borderRadius:6, background:`${c}30`, color:"#fff", fontSize:10, fontWeight:600 }}>{t}</span>
                          ))}
                          <span style={{ padding:"4px 10px", borderRadius:6, background:`${c}`, color:"#fff", fontSize:10, fontWeight:700 }}>Actif</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Palette */}
                  <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:10 }}>
                    {PALETTE.map(p=>(
                      <button key={p.hex} title={p.label} onClick={()=>setInviteForm(f=>({...f,couleur:p.hex}))}
                        style={{ width:32, height:32, borderRadius:"50%", background:p.hex, border:`3px solid ${c===p.hex?"#0f172a":"transparent"}`, cursor:"pointer", boxShadow:c===p.hex?`0 0 0 2px ${p.hex}`:"none", outline:"none", transition:"all .15s", transform:c===p.hex?"scale(1.15)":"scale(1)" }} />
                    ))}
                    {/* Custom hex */}
                    <label title="Couleur personnalisée" style={{ width:32, height:32, borderRadius:"50%", background:`conic-gradient(red,yellow,lime,cyan,blue,magenta,red)`, border:"2px solid #e5e7eb", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, overflow:"hidden" }}>
                      <input type="color" value={c} onChange={e=>setInviteForm(f=>({...f,couleur:e.target.value}))}
                        style={{ width:0, height:0, opacity:0, position:"absolute" }} />
                      🎨
                    </label>
                  </div>

                  {/* Hex input */}
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <div style={{ width:20, height:20, borderRadius:4, background:c, flexShrink:0, border:"1px solid #e5e7eb" }} />
                    <input value={c} onChange={e=>{ if(/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) setInviteForm(f=>({...f,couleur:e.target.value})); }}
                      style={{ padding:"5px 10px", border:"1.5px solid #e5e7eb", borderRadius:7, fontSize:12, fontFamily:"monospace", width:90, color:"#374151" }}
                      placeholder="#000000" maxLength={7} />
                    <span style={{ fontSize:11, color:"#9ca3af" }}>Code hexadécimal personnalisé</span>
                  </div>
                </div>
              );
            })()}

            <div style={{ padding:"10px 14px", borderRadius:8, background:"#f0f9ff", border:"1px solid #bae6fd", fontSize:12, color:BET_COLOR, marginBottom:16 }}>
              🔑 Un mot de passe temporaire sera généré automatiquement et affiché après création.
              {inviteForm.role === "commercial" && <><br/><span style={{ color:"#166534" }}>✅ Le profil planning de l'assistante sera créé en même temps.</span></>}
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={sendInvite} disabled={sendingInvite||!peut("roles","manage")} title={permBtnTitle("Créer l'utilisateur",peut("roles","manage"))} style={permBtnStyle({ padding:"10px 20px", background:sendingInvite?"#9ca3af":BET_COLOR, color:"#fff", border:"none", borderRadius:8, cursor:sendingInvite?"not-allowed":"pointer", fontWeight:700, fontSize:13 },peut("roles","manage"))}>{sendingInvite?"⏳ Création…":peut("roles","manage")?"✅ Créer l'utilisateur":"🔒 Créer l'utilisateur"}</button>
              <button onClick={()=>setShowInviteModal(false)} disabled={sendingInvite} style={{ padding:"10px 16px", background:"#e5e7eb", color:"#374151", border:"none", borderRadius:8, cursor:"pointer", fontWeight:600, fontSize:13 }}>Annuler</button>
            </div>
          </Modal>
        )}

        {showCredentialsModal && createdCredentials && (
          <Modal title="✅ Utilisateur créé avec succès" subtitle="Transmettez ces accès à l'utilisateur de manière sécurisée" onClose={()=>setShowCredentialsModal(false)}>
            <div style={{ padding:"18px 20px", borderRadius:12, background:"#f0fdf4", border:"2px solid #bbf7d0", marginBottom:18 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
                <div style={{ width:44, height:44, borderRadius:"50%", background:"#dcfce7", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>
                  {ROLES_DEF[createdCredentials.role]?.emoji || "👤"}
                </div>
                <div>
                  <div style={{ fontWeight:700, fontSize:15, color:"#0f172a" }}>{createdCredentials.nom}</div>
                  <div style={{ fontSize:12, color:"#6b7280" }}><RoleBadge role={createdCredentials.role}/></div>
                </div>
              </div>
              <div style={{ marginBottom:12 }}>
                <div style={{ fontSize:11, fontWeight:600, color:"#6b7280", marginBottom:4 }}>EMAIL DE CONNEXION</div>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <code style={{ flex:1, padding:"9px 12px", borderRadius:8, background:"#fff", border:"1px solid #d1d5db", fontSize:14, color:"#0f172a", fontWeight:600 }}>{createdCredentials.email}</code>
                  <button onClick={()=>{navigator.clipboard.writeText(createdCredentials.email);toast.success("Email copié !");}} style={{ padding:"8px 12px", background:"#e5e7eb", border:"none", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:600, flexShrink:0 }}>📋 Copier</button>
                </div>
              </div>
              <div>
                <div style={{ fontSize:11, fontWeight:600, color:"#6b7280", marginBottom:4 }}>MOT DE PASSE TEMPORAIRE</div>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <code style={{ flex:1, padding:"9px 12px", borderRadius:8, background:"#fff", border:"2px solid #22c55e", fontSize:16, color:"#166534", fontWeight:800, letterSpacing:"0.05em" }}>{createdCredentials.mdp}</code>
                  <button onClick={()=>{navigator.clipboard.writeText(createdCredentials.mdp);toast.success("Mot de passe copié !");}} style={{ padding:"8px 12px", background:"#22c55e", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:600, flexShrink:0 }}>📋 Copier</button>
                </div>
              </div>
            </div>
            <div style={{ padding:"10px 14px", borderRadius:8, background:"#fff7ed", border:"1px solid #fed7aa", fontSize:12, color:"#92400e", marginBottom:16 }}>
              ⚠️ Ce mot de passe temporaire ne sera plus affiché. L'utilisateur devra le changer à sa première connexion.
            </div>
            <button onClick={()=>setShowCredentialsModal(false)} style={{ width:"100%", padding:"10px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:14 }}>J'ai noté les accès ✓</button>
          </Modal>
        )}

        {showUserModal && editingUser && (
          <Modal title={`Modifier - ${editingUser.nom}`} subtitle="Modifier le rôle et les paramètres de cet utilisateur" onClose={()=>setShowUserModal(false)}>
            {/* Avatar + identité + champs de base */}
            <div style={{ padding:"14px 16px", borderRadius:10, background:`${ROLES_DEF[editingUser.role]?.bg}`, border:`1px solid ${ROLES_DEF[editingUser.role]?.border}`, marginBottom:18 }}>
              <div style={{ display:"flex", gap:14, alignItems:"center", marginBottom:12 }}>
                <AvatarUpload
                  currentUrl={editingUser.avatar_url || null}
                  nom={editingUser.nom || ""}
                  size={56}
                  onSuccess={(file) => setEditingUser(u => ({ ...u, avatar_url: file.url }))}
                />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:15, marginBottom:2 }}>{editingUser.nom}</div>
                  <div style={{ fontSize:12, color:"#9ca3af", marginBottom:4 }}>{editingUser.email}</div>
                  <RoleBadge role={editingUser.role}/>
                </div>
              </div>
              {/* Prénom / Nom / Téléphone directement dans le bloc */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
                <div>
                  <label style={{ display:"block", fontSize:10, fontWeight:700, color:"#6b7280", textTransform:"uppercase", marginBottom:3 }}>Prénom</label>
                  <input value={editingUser.prenom||""} onChange={e=>setEditingUser(u=>({...u,prenom:e.target.value}))} style={{ padding:"6px 9px", borderRadius:6, border:"1.5px solid #fbbf24", fontSize:12, width:"100%", background:"#fffef5" }} placeholder="Prénom"/>
                </div>
                <div>
                  <label style={{ display:"block", fontSize:10, fontWeight:700, color:"#6b7280", textTransform:"uppercase", marginBottom:3 }}>Nom</label>
                  <input value={editingUser.nom||""} onChange={e=>setEditingUser(u=>({...u,nom:e.target.value}))} style={{ padding:"6px 9px", borderRadius:6, border:"1.5px solid #fbbf24", fontSize:12, width:"100%", background:"#fffef5" }} placeholder="Nom"/>
                </div>
                <div>
                  <label style={{ display:"block", fontSize:10, fontWeight:700, color:"#6b7280", textTransform:"uppercase", marginBottom:3 }}>Téléphone</label>
                  <input value={editingUser.telephone||""} onChange={e=>setEditingUser(u=>({...u,telephone:e.target.value}))} style={{ padding:"6px 9px", borderRadius:6, border:"1px solid #d1d5db", fontSize:12, width:"100%", background:"#fff" }} placeholder="+224 6xx…"/>
                </div>
              </div>
            </div>

            {/* Email - modifiable avec déverrouillage explicite */}
            {(()=>{
              const emailUnlocked = !!editingUser._emailUnlocked;
              return (
                <div style={{ marginBottom:14 }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
                    <label style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, fontWeight:600, color: emailUnlocked ? "#dc2626" : "#9ca3af" }}>
                      {emailUnlocked ? "⚠️" : "🔒"} Email
                      {!emailUnlocked && <span style={{ fontWeight:400, fontStyle:"italic" }}>- cliquer sur Modifier pour changer</span>}
                      {emailUnlocked && <span style={{ fontWeight:400, fontStyle:"italic", color:"#dc2626" }}>- modification de l'identité de connexion</span>}
                    </label>
                    {!emailUnlocked
                      ? <button type="button" onClick={()=>setEditingUser(u=>({...u,_emailUnlocked:true,_emailNew:u.email}))}
                          style={{ padding:"3px 10px", background:"#fff7ed", color:"#d97706", border:"1px solid #fed7aa", borderRadius:6, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                          ✏️ Modifier l'email
                        </button>
                      : <button type="button" onClick={()=>setEditingUser(u=>({...u,_emailUnlocked:false,_emailNew:""}))}
                          style={{ padding:"3px 10px", background:"#f1f5f9", color:"#64748b", border:"1px solid #e2e8f0", borderRadius:6, fontSize:11, fontWeight:600, cursor:"pointer" }}>
                          Annuler
                        </button>
                    }
                  </div>
                  {!emailUnlocked ? (
                    <div style={{ padding:"9px 12px", borderRadius:6, border:"1px solid #e5e7eb", background:"#f9fafb", fontSize:13, color:"#9ca3af", userSelect:"none" }}>
                      {editingUser.email}
                    </div>
                  ) : (
                    <>
                      <div style={{ padding:"8px 12px", borderRadius:7, background:"#fef2f2", border:"1px solid #fecaca", fontSize:11, color:"#991b1b", marginBottom:6 }}>
                        🚨 Changer l'email <strong>modifie l'identité de connexion Supabase</strong>. L'utilisateur devra utiliser le nouvel email pour se connecter.
                      </div>
                      <input type="email" value={editingUser._emailNew||""}
                        onChange={e=>setEditingUser(u=>({...u,_emailNew:e.target.value}))}
                        style={{ padding:"9px 12px", borderRadius:6, border:"2px solid #dc2626", fontSize:13, width:"100%", background:"#fff5f5" }}
                        placeholder="Nouvel email…"/>
                    </>
                  )}
                </div>
              );
            })()}

            {/* Avertissement nom/prénom */}
            <div style={{ padding:"8px 12px", borderRadius:8, background:"#fffbeb", border:"1px solid #fde68a", marginBottom:14, fontSize:11, color:"#92400e", display:"flex", alignItems:"center", gap:6 }}>
              ⚠️ <span>Le <strong>nom</strong> et le <strong>prénom</strong> apparaissent sur les factures, contrats et communications. Modifier avec précaution.</span>
            </div>

            {/* Changer le rôle - verrouillé sans exception à l'édition, pour éviter tout effet de
                bord sur les accès/données déjà liés au compte (groupes, dossiers, permissions...).
                Seule la création d'un nouvel utilisateur permet de choisir le rôle. */}
            {(() => {
              const groupesActifs = editingUserGroupes.filter(g => g.statut === "actif" || !g.statut || g.statut === "en_cours");
              return (
                <>
                  <div style={{ padding:"10px 14px", borderRadius:8, background:"#fff7ed", border:"1px solid #fed7aa", marginBottom:8, fontSize:11, color:"#92400e", display:"flex", alignItems:"flex-start", gap:6 }}>
                    ⚠️
                    <span>
                      {editingUser.role === "coach" && groupesActifs.length > 0
                        ? <>Ce coach est assigné à <strong>{groupesActifs.length} groupe{groupesActifs.length > 1 ? "s" : ""} actif{groupesActifs.length > 1 ? "s" : ""}</strong> ({groupesActifs.map(g => g.nom).join(", ")}). </>
                        : null}
                      Le rôle ne peut pas être modifié depuis cet écran - il <strong>engage les accès et permissions</strong> de cet utilisateur dans toute l'application. Pour changer de rôle, supprimez le compte et recréez-le avec le bon rôle.
                    </span>
                  </div>
                  <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:600, color:"#9ca3af", marginBottom:4 }}>
                    🔒 Rôle
                    <span style={{ fontWeight:400, fontStyle:"italic", fontSize:11 }}>- modification bloquée</span>
                  </label>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:14, opacity:0.45, pointerEvents:"none" }}>
                    {Object.values(ROLES_DEF).filter(r=>r.id!=="super_admin").map(r=>(
                      <div key={r.id} style={{ padding:"10px 12px", borderRadius:9, border:`2px solid ${editingUser.role===r.id?r.color:"#e5e7eb"}`, background:editingUser.role===r.id?r.color+"08":"#fff", cursor:"not-allowed" }}>
                        <div style={{ fontWeight:700, fontSize:13, color:editingUser.role===r.id?r.color:"#374151" }}>{r.emoji} {r.label}</div>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}

            {/* Champs spécifiques coach */}
            {editingUser.role === "coach" && (() => {
              const ci = editingUser.coach_info || {};
              const setCI = (patch) => setEditingUser(u => ({ ...u, coach_info: { ...(u.coach_info||{}), ...patch } }));
              const NIVEAU_COLORS = { A1:"#6b7280",A2:"#d97706",B1:"#1a4fd6",B2:"#7c3aed",C1:"#059669",C2:"#dc2626" };
              const NIVEAU_BG     = { A1:"#f3f4f6",A2:"#fef3c7",B1:"#dbeafe",B2:"#ede9fe",C1:"#dcfce7",C2:"#fee2e2" };
              const niveaux = Array.isArray(ci.niveaux_enseignes) ? ci.niveaux_enseignes : [];
              const certifs = Array.isArray(ci.certifications) ? ci.certifications : [];
              return (
                <div style={{ marginBottom:14, padding:"14px 16px", borderRadius:10, background:"#f0f9ff", border:"1.5px solid #bae6fd" }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#0369a1", marginBottom:12 }}>🎓 Profil coach</div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                    <div>
                      <label style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, fontWeight:600, color:"#9ca3af", marginBottom:3 }}>
                        🔒 Matricule <span style={{ fontWeight:400, fontStyle:"italic" }}>- identifiant unique</span>
                      </label>
                      <div style={{ padding:"9px 12px", borderRadius:6, border:"1px solid #e5e7eb", background:"#f9fafb", fontSize:13, color:"#9ca3af", userSelect:"none" }}>
                        {ci.matricule || "Non renseigné"}
                      </div>
                    </div>
                    <div>
                      <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#6b7280", marginBottom:3 }}>Filière / Spécialité</label>
                      <input value={ci.filiere||""} onChange={e=>setCI({filiere:e.target.value})} style={{ padding:8, borderRadius:6, border:"1px solid #d1d5db", fontSize:13, width:"100%" }} placeholder="Ex : Mathématiques"/>
                    </div>
                    <div>
                      <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#6b7280", marginBottom:3 }}>Lieu d'habitation</label>
                      <input value={ci.lieu_habitation||""} onChange={e=>setCI({lieu_habitation:e.target.value})} style={{ padding:8, borderRadius:6, border:"1px solid #d1d5db", fontSize:13, width:"100%" }} placeholder="Ex : Cocody, Abidjan"/>
                    </div>
                    <div>
                      <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#6b7280", marginBottom:3 }}>Date de début à BET</label>
                      <input type="date" value={ci.date_debut_bet||""} onChange={e=>setCI({date_debut_bet:e.target.value||null})} style={{ padding:8, borderRadius:6, border:"1px solid #d1d5db", fontSize:13, width:"100%" }}/>
                    </div>
                    <div>
                      <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#6b7280", marginBottom:3 }}>Nb contrats actifs</label>
                      <input type="number" min={0} value={ci.nb_contrats_actifs||0} onChange={e=>setCI({nb_contrats_actifs:Number(e.target.value)})} style={{ padding:8, borderRadius:6, border:"1px solid #d1d5db", fontSize:13, width:"100%" }}/>
                    </div>
                    <div style={{ gridColumn:"1/-1" }}>
                      <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#6b7280", marginBottom:3 }}>Biographie</label>
                      <textarea value={ci.bio||""} onChange={e=>setCI({bio:e.target.value})} rows={3} style={{ padding:8, borderRadius:6, border:"1px solid #d1d5db", fontSize:13, width:"100%", resize:"vertical" }} placeholder="Courte biographie…"/>
                    </div>
                  </div>
                  {/* Niveaux CECRL */}
                  <div style={{ marginBottom:10 }}>
                    <div style={{ fontSize:11, fontWeight:600, color:"#6b7280", marginBottom:6 }}>Niveaux enseignés (CECRL)</div>
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                      {["A1","A2","B1","B2","C1","C2"].map(niv => {
                        const active = niveaux.includes(niv);
                        const groupesUtilisant = editingUserGroupes.filter(g => g.niveau === niv && (g.statut === "actif" || !g.statut || g.statut === "en_cours"));
                        const verrouille = groupesUtilisant.length > 0;
                        return (
                          <div key={niv} style={{ position:"relative" }} title={verrouille ? `🔒 Utilisé dans : ${groupesUtilisant.map(g=>g.nom).join(", ")}` : ""}>
                            <button type="button"
                              disabled={verrouille}
                              onClick={() => !verrouille && setCI({ niveaux_enseignes: active ? niveaux.filter(n=>n!==niv) : [...niveaux, niv] })}
                              style={{
                                padding:"5px 14px", borderRadius:20, fontWeight:700, fontSize:13,
                                border: verrouille ? "2px solid #d1d5db" : `2px solid ${NIVEAU_COLORS[niv]}`,
                                background: verrouille ? "#f3f4f6" : active ? NIVEAU_BG[niv] : "#fff",
                                color: verrouille ? "#9ca3af" : active ? NIVEAU_COLORS[niv] : "#9ca3af",
                                cursor: verrouille ? "not-allowed" : "pointer",
                                opacity: verrouille ? 0.7 : 1,
                              }}>
                              {verrouille ? "🔒 " : active ? "✓ " : ""}{niv}
                            </button>
                            {verrouille && (
                              <div style={{ fontSize:9, color:"#9ca3af", textAlign:"center", marginTop:2, maxWidth:60, lineHeight:1.2 }}>
                                {groupesUtilisant[0]?.nom?.slice(0,12)}{groupesUtilisant[0]?.nom?.length > 12 ? "…" : ""}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {editingUserGroupes.some(g => ["A1","A2","B1","B2","C1","C2"].includes(g.niveau) && (g.statut === "actif" || !g.statut || g.statut === "en_cours")) && (
                      <div style={{ marginTop:8, fontSize:10, color:"#9ca3af", fontStyle:"italic" }}>
                        🔒 Les niveaux verrouillés sont déjà utilisés dans des groupes actifs. Pour les modifier, réassignez d'abord ces groupes.
                      </div>
                    )}
                  </div>
                  {/* Certifications */}
                  <div>
                    <div style={{ fontSize:11, fontWeight:600, color:"#6b7280", marginBottom:6 }}>🏆 Certifications</div>
                    {/* Badges quick-add */}
                    <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:8 }}>
                      {["CELTA","DELTA","TEFL","TOEIC Expert","IELTS 7+","TOEFL iBT","Cambridge TKT","Diplôme universitaire"].map(cert => {
                        const certNom = typeof certifs[0] === "string" ? certifs : certifs.map(c => c.nom || c);
                        const active = certNom.includes(cert);
                        return (
                          <button key={cert} type="button"
                            onClick={() => setCI({ certifications: active ? certifs.filter(c => (typeof c === "string" ? c : c.nom) !== cert) : [...certifs, cert] })}
                            style={{ padding:"4px 10px", borderRadius:20, fontSize:11, fontWeight:600, cursor:"pointer", border:`1.5px solid ${active?"#6366f1":"#c7d2fe"}`, background:active?"#6366f1":"#fff", color:active?"#fff":"#6366f1" }}>
                            {active ? "✓ " : "+ "}{cert}
                          </button>
                        );
                      })}
                    </div>
                    {/* Tags ajoutés */}
                    {certifs.length > 0 && (
                      <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:8 }}>
                        {certifs.map((c,i) => {
                          const nom = typeof c === "string" ? c : c.nom;
                          return (
                            <span key={i} style={{ display:"flex", alignItems:"center", gap:4, padding:"3px 10px", borderRadius:20, background:"#ede9fe", color:"#5b21b6", fontSize:11, fontWeight:600 }}>
                              🏆 {nom}
                              <button type="button" onClick={()=>setCI({ certifications: certifs.filter((_,j)=>j!==i) })} style={{ background:"none", border:"none", cursor:"pointer", color:"#7c3aed", fontWeight:800, fontSize:12, padding:0, lineHeight:1 }}>×</button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {/* Saisie libre */}
                    <div style={{ display:"flex", gap:6 }}>
                      <input id="edit_certif_input" placeholder="Autre certification… (Entrée pour ajouter)"
                        style={{ flex:1, padding:"7px 10px", border:"1.5px solid #c7d2fe", borderRadius:7, fontSize:12 }}
                        onKeyDown={e => {
                          if (e.key !== "Enter") return;
                          e.preventDefault();
                          const v = e.target.value.trim();
                          if (v) { setCI({ certifications: [...certifs, v] }); e.target.value = ""; }
                        }}
                      />
                      <button type="button" onClick={() => {
                        const inp = document.getElementById("edit_certif_input");
                        const v = inp?.value?.trim();
                        if (v) { setCI({ certifications: [...certifs, v] }); inp.value = ""; }
                      }} style={{ padding:"7px 14px", background:"#6366f1", color:"#fff", border:"none", borderRadius:7, fontSize:12, fontWeight:600, cursor:"pointer" }}>+ Ajouter</button>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Accès temporaire + sécurité */}
            <label style={{ display:"block", fontSize:12, fontWeight:600, color:"#374151", marginBottom:4 }}>Accès temporaire</label>
            <input type="date" value={editingUser.accessTemp||""} onChange={e=>setEditingUser({...editingUser,accessTemp:e.target.value||null})} style={{ padding:9, borderRadius:6, border:"1px solid #d1d5db", fontSize:13, width:"100%", marginBottom:14 }}/>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
              <div style={{ display:"flex", justifyContent:"space-between", padding:"10px 14px", borderRadius:8, background:"#f8fafc", border:"1px solid #e5e7eb" }}><span style={{ fontSize:13, fontWeight:500 }}>2FA activé</span><ToggleSwitch on={editingUser.twofa} onChange={v=>setEditingUser({...editingUser,twofa:v})} color={BET_COLOR}/></div>
              <div style={{ display:"flex", justifyContent:"space-between", padding:"10px 14px", borderRadius:8, background:"#f8fafc", border:"1px solid #e5e7eb" }}><span style={{ fontSize:13, fontWeight:500 }}>Restr. IP</span><ToggleSwitch on={editingUser.ipRestr} onChange={v=>setEditingUser({...editingUser,ipRestr:v})} color={BET_COLOR}/></div>
            </div>

            {/* Couleur du dashboard */}
            {(()=>{
              const PALETTE_DASH = [
                { hex:"#0891b2", label:"Cyan BET" },
                { hex:"#7c3aed", label:"Violet" },
                { hex:"#10b981", label:"Émeraude" },
                { hex:"#f59e0b", label:"Ambre" },
                { hex:"#dc2626", label:"Rouge" },
                { hex:"#0d9488", label:"Teal" },
                { hex:"#d97706", label:"Or" },
                { hex:"#0284c7", label:"Bleu" },
                { hex:"#6366f1", label:"Indigo" },
                { hex:"#db2777", label:"Rose" },
                { hex:"#b45309", label:"Brun" },
                { hex:"#0f172a", label:"Ardoise" },
              ];
              const ec = editingUser.couleur || "#0891b2";
              return (
                <div style={{ marginBottom:16, padding:"14px 16px", borderRadius:10, background:"#f8fafc", border:"1.5px solid #e5e7eb" }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:10 }}>🎨 Couleur du dashboard</div>
                  <div style={{ borderRadius:8, overflow:"hidden", marginBottom:12, height:36, background:`linear-gradient(135deg,#0f172a 0%,${ec} 100%)`, display:"flex", alignItems:"center", paddingLeft:14 }}>
                    <span style={{ color:"#fff", fontSize:11, fontWeight:600, opacity:.85 }}>Aperçu - header du dashboard de {editingUser.prenom||editingUser.nom}</span>
                  </div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:10 }}>
                    {PALETTE_DASH.map(p=>(
                      <button key={p.hex} title={p.label} onClick={()=>setEditingUser(u=>({...u,couleur:p.hex}))}
                        style={{ width:26, height:26, borderRadius:"50%", background:p.hex, border:ec===p.hex?"3px solid #0f172a":"3px solid transparent", cursor:"pointer", outline:ec===p.hex?`2px solid ${p.hex}`:"none", outlineOffset:2, transform:ec===p.hex?"scale(1.2)":"scale(1)", transition:"transform .1s" }} />
                    ))}
                    <label title="Couleur personnalisée" style={{ width:26, height:26, borderRadius:"50%", background:"conic-gradient(red,yellow,lime,aqua,blue,magenta,red)", border:"3px solid #e5e7eb", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
                      <input type="color" value={ec} onChange={e=>setEditingUser(u=>({...u,couleur:e.target.value}))} style={{ width:1, height:1, opacity:0, position:"absolute", pointerEvents:"none" }} />
                    </label>
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <div style={{ width:20, height:20, borderRadius:4, background:ec, border:"1px solid #e5e7eb", flexShrink:0 }} />
                    <input value={ec} onChange={e=>setEditingUser(u=>({...u,couleur:e.target.value}))} placeholder="#0891b2" maxLength={7}
                      style={{ flex:1, padding:"5px 10px", borderRadius:6, border:"1.5px solid #e2e8f0", fontSize:12, fontFamily:"monospace", outline:"none" }} />
                  </div>
                </div>
              );
            })()}

            {/* Assignation onboarding pour les commerciales */}
            {editingUser.role === "commercial" && (() => {
              const ass = assistantesAdmin.find(a => a.email === editingUser.email);
              const assistanteId = ass?.id || null;
              // _onboardingId = valeur modifiée en cours, sinon valeur existante dans ass
              const currentVal = editingUser._onboardingId !== undefined
                ? editingUser._onboardingId
                : (ass?.onboarding_id || "");
              return (
                <div style={{ marginBottom:16, padding:"14px 16px", borderRadius:10, background:"#f0fdf4", border:"1.5px solid #86efac" }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#166534", marginBottom:8 }}>🎯 Assistante Onboarding assignée</div>
                  {assistantesLoading ? (
                    <div style={{ fontSize:11, color:"#9ca3af" }}>Chargement…</div>
                  ) : !assistanteId ? (
                    <div style={{ padding:"8px 12px", borderRadius:8, background:"#fff7ed", border:"1px solid #fed7aa", fontSize:11, color:"#92400e" }}>
                      ⚠️ Profil assistante introuvable pour cette commerciale. Utilisez d'abord le bouton "⚠️ Sync" dans la liste des utilisateurs.
                    </div>
                  ) : onboardingList.length === 0 ? (
                    <div style={{ fontSize:11, color:"#9ca3af" }}>Chargement de la liste onboarding…</div>
                  ) : (
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:8 }}>
                      <button type="button" onClick={()=>setEditingUser(u => ({ ...u, _onboardingId: "" }))}
                        style={{ textAlign:"left", padding:"10px 12px", borderRadius:9, cursor:"pointer",
                          border:`2px solid ${currentVal===""?"#16a34a":"#e2e8f0"}`,
                          background:currentVal===""?"#dcfce7":"#fff" }}>
                        <div style={{ fontSize:12, fontWeight:700, color:"#374151" }}>- Aucune onboarding assignée -</div>
                      </button>
                      {onboardingList.map(o => {
                        const selected = currentVal === o.id;
                        const initiales = `${o.prenom?.[0]||""}${o.nom?.[0]||""}`.toUpperCase();
                        const nb = o.nb_commerciaux_assignes || 0;
                        return (
                          <button key={o.id} type="button" onClick={() => setEditingUser(u => ({ ...u, _onboardingId: o.id }))}
                            style={{ display:"flex", alignItems:"center", gap:10, textAlign:"left", padding:"10px 12px", borderRadius:9, cursor:"pointer",
                              border:`2px solid ${selected?"#16a34a":"#e2e8f0"}`,
                              background:selected?"#dcfce7":"#fff", opacity:o.actif===false?0.55:1 }}>
                            <div style={{ width:36, height:36, borderRadius:"50%", flexShrink:0, background:o.couleur||"#16a34a", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:13 }}>
                              {initiales || "?"}
                            </div>
                            <div style={{ minWidth:0 }}>
                              <div style={{ fontSize:12, fontWeight:700, color:"#0f172a", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                                {o.prenom} {o.nom} {o.actif===false && <span style={{ color:"#dc2626", fontWeight:600 }}>⏸</span>}
                              </div>
                              <div style={{ fontSize:10, color:"#64748b" }}>📞 {o.telephone || "-"}</div>
                              <div style={{ fontSize:10, color:"#166534", fontWeight:600 }}>📋 {nb} suivi{nb>1?"es":"e"}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── Accès E-Learning ── */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 14px", borderRadius:10, background: editingUser.acces_elearning ? "#e0f2fe" : "#f8fafc", border:`1.5px solid ${editingUser.acces_elearning ? "#0891b2" : "#e2e8f0"}`, marginBottom:14 }}>
              <div>
                <div style={{ fontWeight:700, fontSize:13, color: editingUser.acces_elearning ? "#0891b2" : "#374151" }}>🎓 Accès Plateforme E-Learning</div>
                <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>
                  {editingUser.acces_elearning ? "Cet utilisateur peut accéder à la plateforme e-learning BET" : "Accès à la plateforme e-learning non accordé"}
                </div>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                {editingUser.acces_elearning && (
                  <button
                    type="button"
                    onClick={() => openElearningSSO(ELEARNING_URL, editingUser.id)}
                    style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 12px", background:"#0891b2", color:"#fff", border:"none", borderRadius:7, cursor:"pointer", fontSize:11, fontWeight:700 }}
                  >
                    <span>🎓</span> Ouvrir E-Learning
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setEditingUser(u => ({ ...u, acces_elearning: !u.acces_elearning }))}
                  style={{ width:46, height:26, borderRadius:13, border:"none", cursor:"pointer", background: editingUser.acces_elearning ? "#0891b2" : "#d1d5db", position:"relative", transition:"background 0.2s", flexShrink:0 }}
                >
                  <div style={{ position:"absolute", top:3, width:20, height:20, borderRadius:"50%", background:"#fff", left: editingUser.acces_elearning ? 23 : 3, transition:"left 0.2s", boxShadow:"0 1px 3px rgba(0,0,0,0.2)" }} />
                </button>
              </div>
            </div>

            {/* Boutons */}
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={async()=>{
                const old=users.find(u=>u.id===editingUser.id);
                try {
                  const body = {
                    nom: editingUser.nom,
                    prenom: editingUser.prenom,
                    telephone: editingUser.telephone,
                    avatar_url: editingUser.avatar_url,
                    role: editingUser.role,
                    couleur: editingUser.couleur||null,
                    acces_elearning: editingUser.acces_elearning ?? false,
                  };
                  if (editingUser.role === "coach") body.coach_info = editingUser.coach_info || {};
                  if (editingUser._emailUnlocked && editingUser._emailNew && editingUser._emailNew !== editingUser.email) {
                    body.email = editingUser._emailNew.trim().toLowerCase();
                  }
                  const res = await fetch(`${API_URL}/api/admin/utilisateurs/${editingUser.id}`, {
                    method:"PATCH",
                    headers:{ ...authHeaders(), "Content-Type":"application/json" },
                    body: JSON.stringify(body),
                  });
                  if (!res.ok) { const e=await res.json().catch(()=>({})); toast.error(e.error||"Erreur lors de la sauvegarde"); return; }

                  // Si commerciale → mettre à jour assistantes.onboarding_id
                  const _assForSave = assistantesAdmin.find(a => a.email === editingUser.email);
                  if (editingUser.role === "commercial" && _assForSave?.id && editingUser._onboardingId !== undefined) {
                    const newOb = editingUser._onboardingId || null;
                    const rAss = await fetch(`${API_URL}/api/parcours/assistantes/${_assForSave.id}`, {
                      method: "PATCH",
                      headers: { ...authHeaders(), "Content-Type":"application/json" },
                      body: JSON.stringify({ onboarding_id: newOb }),
                    });
                    if (rAss.ok) {
                      const chosen = onboardingList.find(o => o.id === newOb);
                      toast.success(chosen ? `✅ Onboarding "${chosen.prenom} ${chosen.nom}" assignée` : "✅ Assignation onboarding retirée");
                      fetchAssistantesAdmin();
                    } else {
                      toast.error("Profil mis à jour mais assignation onboarding échouée - vérifiez la migration SQL");
                    }
                  }

                  setUsers(users.map(u=>u.id===editingUser.id?editingUser:u));
                  if(old.role!==editingUser.role) addAuditEntry("ROLE_MODIFIE",`${editingUser.nom} : ${old.role} → ${editingUser.role}`);
                  try {
                    const myProfil=JSON.parse(localStorage.getItem("admin_profil")||"null");
                    if(myProfil&&myProfil.id===editingUser.id){ localStorage.setItem("admin_profil",JSON.stringify({...myProfil,couleur:editingUser.couleur||null})); }
                  } catch(e2){}
                  toast.success("Modifications enregistrées ✓");
                  setShowUserModal(false);
                } catch(err){ toast.error("Erreur réseau"); }
              }} style={{ padding:"9px 16px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:12 }}>💾 Enregistrer</button>
              <button onClick={()=>setShowUserModal(false)} style={{ padding:"9px 16px", background:"#e5e7eb", color:"#374151", border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:12 }}>Annuler</button>
            </div>
          </Modal>
        )}

        {showRevokeModal && userToRevoke && (
          <Modal title="Révoquer les sessions" subtitle="Cette action déconnectera l'utilisateur immédiatement" onClose={()=>setShowRevokeModal(false)} danger>
            <div style={{ textAlign:"center", padding:"16px 0" }}><div style={{ fontSize:48, marginBottom:12 }}>⚠️</div><p style={{ fontSize:14, color:"#374151", lineHeight:1.7 }}>Vous êtes sur le point de déconnecter <strong>{userToRevoke.nom}</strong> ({ROLES_DEF[userToRevoke.role]?.label}) de toutes ses sessions actives ({userToRevoke.sessions} session(s)).</p><p style={{ fontSize:13, color:"#9ca3af", marginTop:8 }}>L'utilisateur devra se reconnecter pour continuer.</p></div>
            <div style={{ display:"flex", gap:10, justifyContent:"center", marginTop:16 }}><button onClick={()=>revokeSession(userToRevoke.id)} style={{ padding:"9px 16px", background:BET_RED, color:"#fff", border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:12 }}>🔌 Révoquer les sessions</button><button onClick={()=>setShowRevokeModal(false)} style={{ padding:"9px 16px", background:"#e5e7eb", color:"#374151", border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:12 }}>Annuler</button></div>
          </Modal>
        )}


        {/* ── MODALE DÉTAIL COACH ── */}
        {selectedCoach && (() => {
          const coach = selectedCoach;
          const photoUrl = coach.avatar_url || coach.coach_info?.photo_url || null;
          const initiales = ((coach.prenom?.[0]||"")+(coach.nom?.[0]||"")).toUpperCase() || "CO";
          const fmtDate  = d => d ? new Date(d).toLocaleDateString("fr-FR",{day:"numeric",month:"short",year:"numeric"}) : "-";
          const fmtMoney = v => v != null ? Number(v).toLocaleString("fr-FR")+" F" : "-";
          const couleur  = BET_COLOR;

          const certifs = Array.isArray(coach.coach_info?.certifications)
            ? coach.coach_info.certifications
            : (typeof coach.coach_info?.certifications === "string" && coach.coach_info.certifications
                ? coach.coach_info.certifications.split(",").map(s=>s.trim()).filter(Boolean) : []);
          const exps = Array.isArray(coach.coach_info?.experiences) ? coach.coach_info.experiences : [];
          const contratsActifs = coachContrats.filter(c=>c.statut==="actif").length;
          const centre = (coach.scope?.filter(s=>s!=="national")||[]).join(", ") || "National";
          const honoraires = coachContrats.map(c => ({ ...c, montant_total:(c.prix_h||0)*(c.duree_seance_h||1.5)*(c.nb_seances_total||0), montant_realise:(c.prix_h||0)*(c.duree_seance_h||1.5)*(c.nb_seances_realisees||0) }));
          const totalDu = honoraires.reduce((s,h)=>s+h.montant_total,0);
          const totalRealise = honoraires.reduce((s,h)=>s+h.montant_realise,0);
          const today = new Date(); today.setHours(0,0,0,0);
          const contratsARenouveler = coachContrats.filter(c => { if(!c.date_fin) return false; const fin=new Date(c.date_fin); return (Math.ceil((fin-today)/86400000)<=60 && c.statut==="actif")||c.renouvellement_statut; });
          const CONTRAT_TYPE = { en_ligne:{label:"En ligne",icon:"💻",color:"#0891b2",bg:"#e0f2fe"}, presentiel_bet:{label:"Présentiel BET",icon:"🏢",color:"#7c3aed",bg:"#ede9fe"}, domicile:{label:"À domicile",icon:"🏠",color:"#d97706",bg:"#fef3c7"} };
          const CONTRAT_STATUT = { en_attente:{label:"En attente",color:"#d97706",bg:"#fef3c7"}, actif:{label:"Actif",color:"#16a34a",bg:"#dcfce7"}, suspendu:{label:"Suspendu",color:"#9ca3af",bg:"#f3f4f6"}, termine:{label:"Terminé",color:"#6b7280",bg:"#f3f4f6"}, renouvele:{label:"Renouvelé",color:"#2563eb",bg:"#dbeafe"}, non_renouvele:{label:"Non renouvelé",color:"#dc2626",bg:"#fee2e2"} };
          const RENOUV_STATUT = { en_attente:{label:"En attente",color:"#d97706",bg:"#fef3c7"}, confirme:{label:"Confirmé",color:"#16a34a",bg:"#dcfce7"}, refuse:{label:"Refusé",color:"#dc2626",bg:"#fee2e2"} };

          /* ── MOCKS (aperçu) - actifs uniquement si les vraies données sont vides ── */
          /* Données réelles uniquement - pas de mock */
          const evalsAffichees   = coachEvals;
          const contratsAffiches = coachContrats;
          const groupesAffiches  = coachGroupes;
          const sessionsAffichees = [];
          const certifsAffichees  = certifs.length > 0 ? certifs : (coach.coach_info?.certifications || []).map(c => typeof c === "string" ? { nom:c, organisme:"", date:"" } : c);
          const expsAffichees     = exps;

          // Transformer les données brutes DB en { jour, creneaux[] }
          // EspaceProfesseur stocke les jours avec majuscule initiale : "Lundi", "Mardi", etc.
          const JOURS_ORDER = ["Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi","Dimanche"];
          const disposByJour = {};
          coachDispoData.forEach(d => {
            if (!d.jour) return;
            const key = d.jour; // ex: "Lundi"
            if (!disposByJour[key]) disposByJour[key] = [];
            disposByJour[key].push(`${d.debut || ""}–${d.fin || ""}`);
          });
          const disposAffichees = JOURS_ORDER
            .filter(j => (disposByJour[j] || []).length > 0)
            .map(j => ({ jour: j, creneaux: disposByJour[j] }));

          const MODAL_TABS = [
            { id:"infos",    lib:"Informations",   icone:"👤" },
            { id:"evals",    lib:"Évaluations",    icone:"⭐" },
            { id:"contrat",  lib:"Contrat",        icone:"📝" },
            { id:"groupes",  lib:"Groupes",        icone:"🏫" },
            { id:"sessions", lib:"Sessions",       icone:"🎥" },
            { id:"certifs",  lib:"Certifications", icone:"🎓" },
            { id:"exp",      lib:"Expériences",    icone:"💼" },
            { id:"dispos",   lib:"Disponibilités", icone:"📅" },
            { id:"statut",   lib:"Statut",         icone:"📊" },
          ];

          return (
          <ModaleAnimee ouvert
            onClicVoile={e=>{ if(e.target===e.currentTarget) setSelectedCoach(null); }}
            style={{ position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.55)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}
            panneauStyle={{ background:"#fff", borderRadius:16, width:"90vw", maxWidth:"90vw", maxHeight:"90vh", display:"flex", flexDirection:"column", overflow:"hidden", boxShadow:"0 24px 60px rgba(0,0,0,0.25)" }}>

              {/* ── HEADER ── */}
              <div style={{ background:BET_GRADIENT, padding:"20px 24px", borderRadius:"16px 16px 0 0", flexShrink:0, position:"relative" }}>
                <button onClick={()=>setSelectedCoach(null)} style={{ position:"absolute", top:14, right:14, background:"rgba(255,255,255,0.2)", border:"none", borderRadius:"50%", width:32, height:32, cursor:"pointer", color:"#fff", fontSize:18 }}>✕</button>
                <div style={{ display:"flex", alignItems:"center", gap:16 }}>
                  {photoUrl
                    ? <img src={photoUrl} alt={coach.prenom} style={{ width:64, height:64, borderRadius:"50%", objectFit:"cover", border:"3px solid rgba(255,255,255,0.4)", flexShrink:0 }} />
                    : <div style={{ width:64, height:64, borderRadius:"50%", background:"rgba(255,255,255,0.2)", color:"#fff", fontWeight:800, fontSize:22, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{initiales}</div>
                  }
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:11, color:"#7dd3fc", fontWeight:600, marginBottom:2 }}>👨‍🏫 Formateur BET</div>
                    <div style={{ fontSize:20, fontWeight:800, color:"#fff" }}>{coach.prenom} {coach.nom}</div>
                    <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginTop:4, fontSize:12, color:"rgba(255,255,255,.75)" }}>
                      <span>{coach.email}</span>
                      <span style={{ background:"rgba(255,255,255,.18)", borderRadius:6, padding:"1px 8px", color:"#fff", fontWeight:700 }}>
                        {coach.actif ? "✅ Actif" : "🔴 Inactif"}
                      </span>
                      {contratsActifs > 0 && <span>📋 {contratsActifs} contrat{contratsActifs!==1?"s":""} actif{contratsActifs!==1?"s":""}</span>}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── BODY: sidebar gauche + contenu ── */}
              <div style={{ display:"flex", flex:1, overflow:"hidden" }}>

                {/* SIDEBAR */}
                <div style={{ width:220, flexShrink:0, background:"#f8fafc", borderRight:"1px solid #e5e7eb", overflowY:"auto", padding:"12px 10px", display:"flex", flexDirection:"column", gap:2 }}>
                  {MODAL_TABS.map(t => (
                    <button key={t.id} onClick={()=>setCoachModalTab(t.id)}
                      style={{
                        display:"flex", alignItems:"center", gap:9, padding:"10px 12px",
                        border:"none", borderRadius:10, width:"100%", textAlign:"left",
                        background: coachModalTab===t.id ? couleur+"18" : "transparent",
                        color: coachModalTab===t.id ? couleur : "#4b5563",
                        fontWeight: coachModalTab===t.id ? 700 : 500,
                        fontSize:13, cursor:"pointer",
                        borderLeft: coachModalTab===t.id ? `3px solid ${couleur}` : "3px solid transparent",
                        transition:"background .15s",
                      }}>
                      <span style={{ fontSize:15 }}>{t.icone}</span>
                      <span style={{ flex:1 }}>{t.lib}</span>
                      {t.badge != null && t.badge > 0 && (
                        <span style={{ background:couleur, color:"#fff", borderRadius:20, fontSize:10, fontWeight:700, padding:"1px 6px", minWidth:16, textAlign:"center" }}>{t.badge}</span>
                      )}
                    </button>
                  ))}
                </div>

              {/* ── CONTENU ── */}
              <div style={{ flex:1, overflowY:"auto", padding:"20px 24px" }}>

                {/* ════ INFOS ════ */}
                {coachModalTab === "infos" && (() => {
                  const bio = coach.coach_info?.bio || null;
                  const langues = Array.isArray(coach.coach_info?.langues) ? coach.coach_info.langues : [];
                  const niveaux = coach.coach_info?.niveaux_enseignes || [];
                  const NIVEL_COLORS = { A1:"#6b7280",A2:"#d97706",B1:"#1a4fd6",B2:"#7c3aed",C1:"#059669",C2:"#dc2626" };
                  const NIVEL_BG    = { A1:"#f3f4f6",A2:"#fef3c7",B1:"#dbeafe",B2:"#ede9fe",C1:"#dcfce7",C2:"#fee2e2" };
                  return (
                  <div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
                      {[
                        ["Prénom",      coach.prenom||"-"],
                        ["Nom",         coach.nom||"-"],
                        ["Email",       coach.email||"-"],
                        ["Téléphone",   coach.telephone||coach.coach_info?.telephone||"-"],
                        ["Filière",     coach.coach_info?.filiere||coach.departement||"-"],
                        ["Niveau",      coach.coach_info?.niveau||"-"],
                        ["Spécialité",  coach.coach_info?.specialite||"-"],
                        ["Périmètre",   (coach.scope?.filter(s=>s!=="national")||[]).join(", ")||"-"],
                        ["Matricule",   coach.coach_info?.matricule||"-"],
                        ["Lieu",        coach.coach_info?.lieu_habitation||"-"],
                        ["Date début BET", coach.coach_info?.date_debut_bet||"-"],
                        ["Nb contrats actifs", coach.coach_info?.nb_contrats_actifs != null ? String(coach.coach_info.nb_contrats_actifs) : "-"],
                      ].map(([l,v]) => (
                        <div key={l} style={{ background:"#f8fafc", borderRadius:10, padding:"11px 14px" }}>
                          <div style={{ fontSize:10, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:3 }}>{l}</div>
                          <div style={{ fontSize:13, fontWeight:600, color: v==="-" ? "#9ca3af" : "#1e293b" }}>{v}</div>
                        </div>
                      ))}
                    </div>
                    {niveaux.length > 0 && (
                      <div style={{ marginBottom:14 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:"#6b7280", marginBottom:8 }}>🎓 Niveaux enseignés (CECRL)</div>
                        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                          {niveaux.map(niv => (
                            <span key={niv} style={{ background: NIVEL_BG[niv]||"#f3f4f6", color: NIVEL_COLORS[niv]||"#374151", borderRadius:20, padding:"4px 14px", fontSize:13, fontWeight:700, border:`1.5px solid ${NIVEL_COLORS[niv]||"#9ca3af"}` }}>{niv}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div style={{ background:"#f0f9ff", borderRadius:10, padding:"14px 16px", marginBottom:14 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:couleur, marginBottom:6 }}>📝 Biographie</div>
                      {bio
                        ? <p style={{ fontSize:13, color:"#374151", margin:0, lineHeight:1.7 }}>{bio}</p>
                        : <p style={{ fontSize:13, color:"#9ca3af", margin:0, fontStyle:"italic" }}>Aucune biographie renseignée.</p>
                      }
                    </div>
                    {langues.length > 0 && (
                      <div>
                        <div style={{ fontSize:11, fontWeight:700, color:"#6b7280", marginBottom:8 }}>🌐 Langues parlées</div>
                        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                          {langues.map((lg,i) => (
                            <span key={i} style={{ background:"#e0f2fe", color:"#0369a1", borderRadius:20, padding:"4px 14px", fontSize:12, fontWeight:600 }}>{lg}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })()}

                {/* ════ ÉVALUATIONS ════ */}
                {coachModalTab === "evals" && (
                  <div>
                    {coachEvalsLoading ? (
                      <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>Chargement…</div>
                    ) : (() => {
                      const CRITERES = [
                        { key:"ponctualite", icon:"⏰", label:"Ponctualité" },
                        { key:"preparation", icon:"📚", label:"Préparation" },
                        { key:"pedagogie",   icon:"💡", label:"Pédagogie" },
                        { key:"materiel",    icon:"🖥",  label:"Matériel" },
                        { key:"suivi",       icon:"✏️", label:"Suivi" },
                      ];
                      const now = new Date();
                      const filtered = evalsAffichees.filter(ev => {
                        if (coachEvalPeriod === "toutes") return true;
                        const d = new Date(ev.date || ev.date_evaluation || ev.created_at);
                        const moisDiff = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
                        if (coachEvalPeriod === "mois")    return moisDiff === 0;
                        if (coachEvalPeriod === "3mois")   return moisDiff < 3;
                        if (coachEvalPeriod === "6mois")   return moisDiff < 6;
                        return true;
                      });
                      const notees = filtered.filter(e => e.note != null);
                      const moy = notees.length ? (notees.reduce((s,e) => s + Number(e.note), 0) / notees.length).toFixed(1) : null;
                      const now2 = new Date();
                      const evsMois = evalsAffichees.filter(ev => {
                        const d = new Date(ev.date || ev.date_evaluation || ev.created_at);
                        return d.getMonth() === now2.getMonth() && d.getFullYear() === now2.getFullYear();
                      });
                      const moyMois = evsMois.filter(e=>e.note!=null).length
                        ? (evsMois.filter(e=>e.note!=null).reduce((s,e)=>s+Number(e.note),0)/evsMois.filter(e=>e.note!=null).length).toFixed(1)
                        : null;
                      /* groupement par mois */
                      const groups = {};
                      filtered.forEach(ev => {
                        const d = new Date(ev.date || ev.date_evaluation || ev.created_at);
                        const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
                        const label = d.toLocaleDateString("fr-FR",{month:"long",year:"numeric"}).replace(/^\w/,c=>c.toUpperCase());
                        if (!groups[key]) groups[key] = { label, evals:[] };
                        groups[key].evals.push(ev);
                      });
                      const groupKeys = Object.keys(groups).sort((a,b)=>b.localeCompare(a));

                      const Stars = ({ n }) => {
                        const full = Math.round(n || 0);
                        return (
                          <span style={{ fontSize:11, color:"#f59e0b", letterSpacing:1 }}>
                            {"★".repeat(full)}{"☆".repeat(Math.max(0, 5 - full))}
                          </span>
                        );
                      };

                      return (
                        <div>
                          {/* KPIs */}
                          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:16 }}>
                            {[
                              { label:"Total évals",    val:evalsAffichees.length, color:"#1e293b" },
                              { label:"Ce mois",        val:evsMois.length,         color:evsMois.length>0?"#0891b2":"#9ca3af" },
                              { label:"Séances dispo.", val: coachGroupes.length > 0 ? coachGroupes.reduce((s,g)=>s+(g.nb_seances_restantes||0),0) : 2, color:"#7c3aed" },
                              { label:"Note moy. mois", val:moyMois ? `${moyMois}/5` : "-", color:moyMois?(Number(moyMois)>=4?"#16a34a":Number(moyMois)>=3?"#d97706":"#dc2626"):"#9ca3af" },
                            ].map(k => (
                              <div key={k.label} style={{ background:"#fff", border:"1.5px solid #e5e7eb", borderRadius:12, padding:"14px 12px", textAlign:"center" }}>
                                <div style={{ fontSize:22, fontWeight:900, color:k.color }}>{k.val}</div>
                                <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>{k.label}</div>
                              </div>
                            ))}
                          </div>

                          {/* Badge lecture seule */}
                          <div style={{ marginBottom:12 }}>
                            <span style={{ background:"#f1f5f9", border:"1px solid #e2e8f0", borderRadius:20, padding:"4px 12px", fontSize:11, color:"#64748b", fontWeight:600 }}>👁 Vue lecture seule</span>
                          </div>

                          {/* Filtres période */}
                          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14, flexWrap:"wrap" }}>
                            <span style={{ fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.05em" }}>Période :</span>
                            {[
                              { id:"toutes", label:"Toutes" },
                              { id:"mois",   label:"Ce mois" },
                              { id:"3mois",  label:"3 derniers mois" },
                              { id:"6mois",  label:"6 derniers mois" },
                            ].map(p => (
                              <button key={p.id} onClick={() => setCoachEvalPeriod(p.id)}
                                style={{ padding:"5px 14px", borderRadius:20, border:"none", cursor:"pointer", fontSize:12, fontWeight: coachEvalPeriod===p.id ? 700 : 500, background: coachEvalPeriod===p.id ? couleur : "#f1f5f9", color: coachEvalPeriod===p.id ? "#fff" : "#374151" }}>
                                {p.label}
                              </button>
                            ))}
                          </div>

                          {filtered.length === 0 ? (
                            <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af", fontSize:13 }}>Aucune évaluation sur cette période.</div>
                          ) : groupKeys.map(gk => {
                            const g = groups[gk];
                            const gNotees = g.evals.filter(e=>e.note!=null);
                            const gMoy = gNotees.length ? (gNotees.reduce((s,e)=>s+Number(e.note),0)/gNotees.length).toFixed(1) : null;
                            const expanded = !coachEvalExpanded.has(gk);
                            const noteColor = gMoy ? (Number(gMoy)>=4.5?"#16a34a":Number(gMoy)>=3.5?"#d97706":"#dc2626") : "#9ca3af";
                            return (
                              <div key={gk} style={{ marginBottom:12 }}>
                                {/* En-tête du groupe mois */}
                                <div onClick={() => setCoachEvalExpanded(s => { const n=new Set(s); n.has(gk)?n.delete(gk):n.add(gk); return n; })}
                                  style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius: expanded?"10px 10px 0 0":"10px", cursor:"pointer", userSelect:"none" }}>
                                  <span style={{ color: couleur, fontSize:13 }}>{expanded ? "▼" : "▶"}</span>
                                  <span style={{ fontWeight:800, fontSize:13, color:"#1e293b" }}>{g.label}</span>
                                  <span style={{ fontSize:11, color:"#9ca3af" }}>{g.evals.length} éval{g.evals.length>1?"s":""}</span>
                                  {gMoy && (
                                    <span style={{ marginLeft:"auto", background: Number(gMoy)>=4.5?"#dcfce7":Number(gMoy)>=3.5?"#fef3c7":"#fee2e2", color:noteColor, borderRadius:8, padding:"2px 10px", fontSize:12, fontWeight:900 }}>{gMoy}/5</span>
                                  )}
                                  {!gMoy && <span style={{ marginLeft:"auto", fontSize:11, color:"#9ca3af", fontStyle:"italic" }}>note auto</span>}
                                </div>

                                {/* Tableau */}
                                {expanded && (
                                  <div style={{ border:"1px solid #e2e8f0", borderTop:"none", borderRadius:"0 0 10px 10px", overflow:"hidden" }}>
                                    <table style={{ width:"100%", borderCollapse:"collapse" }}>
                                      <thead>
                                        <tr style={{ background:"#f1f5f9" }}>
                                          <th style={{ padding:"8px 12px", fontSize:10, fontWeight:700, color:"#64748b", textTransform:"uppercase", textAlign:"left", whiteSpace:"nowrap" }}>Date</th>
                                          <th style={{ padding:"8px 12px", fontSize:10, fontWeight:700, color:"#64748b", textTransform:"uppercase", textAlign:"left", whiteSpace:"nowrap" }}>Sém.</th>
                                          <th style={{ padding:"8px 12px", fontSize:10, fontWeight:700, color:"#64748b", textTransform:"uppercase", textAlign:"left" }}>Séance observée</th>
                                          {CRITERES.map(c => (
                                            <th key={c.key} style={{ padding:"8px 8px", fontSize:14, textAlign:"center", whiteSpace:"nowrap" }} title={c.label}>{c.icon}</th>
                                          ))}
                                          <th style={{ padding:"8px 10px", fontSize:10, fontWeight:700, color:"#64748b", textTransform:"uppercase", textAlign:"center", whiteSpace:"nowrap" }}>Moy.</th>
                                          <th style={{ padding:"8px 12px", fontSize:10, fontWeight:700, color:"#64748b", textTransform:"uppercase", textAlign:"left" }}>Commentaire</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {g.evals.map((ev, i) => {
                                          const note = ev.note != null ? Number(ev.note) : null;
                                          const nColor = note ? (note>=4.5?"#16a34a":note>=3.5?"#d97706":"#dc2626") : "#9ca3af";
                                          return (
                                            <tr key={i} style={{ background: i%2===0?"#fff":"#fafbfc", borderBottom:"1px solid #f1f5f9" }}>
                                              <td style={{ padding:"10px 12px", fontSize:12, color:"#374151", whiteSpace:"nowrap", fontWeight:600 }}>{ev.date || ev.date_evaluation || "-"}</td>
                                              <td style={{ padding:"10px 12px", fontSize:11, color:"#6b7280" }}>{ev.semaine || "-"}</td>
                                              <td style={{ padding:"10px 12px", fontSize:12, color:"#1e293b", fontWeight:600 }}>{ev.seance_observee || ev.apprenant_nom || ev.nom || `Éval #${i+1}`}</td>
                                              {CRITERES.map(c => (
                                                <td key={c.key} style={{ padding:"10px 8px", textAlign:"center" }}>
                                                  {ev[c.key] != null ? (
                                                    <div>
                                                      <div style={{ fontSize:13, fontWeight:900, color:"#f59e0b" }}>{ev[c.key]}</div>
                                                      <Stars n={ev[c.key]} />
                                                    </div>
                                                  ) : <span style={{ color:"#9ca3af" }}>-</span>}
                                                </td>
                                              ))}
                                              <td style={{ padding:"10px 10px", textAlign:"center" }}>
                                                {note != null ? (
                                                  <div>
                                                    <div style={{ fontSize:14, fontWeight:900, color:nColor }}>{note.toFixed(1)}</div>
                                                    <div style={{ fontSize:9, color:"#9ca3af" }}>/5</div>
                                                  </div>
                                                ) : <span style={{ color:"#9ca3af" }}>-</span>}
                                              </td>
                                              <td style={{ padding:"10px 12px", fontSize:11, color:"#4b5563", fontStyle:"italic", maxWidth:200 }}>
                                                {ev.commentaire ? `"${ev.commentaire.slice(0,60)}${ev.commentaire.length>60?"…":""}"` : "-"}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* ════ CONTRAT ════ */}
                {coachModalTab === "contrat" && (
                  <div>
                    {coachContratsLoading ? (
                      <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>Chargement…</div>
                    ) : (() => {
                      const c = contratsAffiches[0] || {};
                      const typeRH = selectedCoach?.coach_info?.type_contrat || "Vacataire";
                      const tarifs = c.tarifs || {};
                      const CATS = [
                        { key:"enfants",  icon:"👦", label:"Enfants" },
                        { key:"ados",     icon:"👦", label:"Ados" },
                        { key:"adultes",  icon:"👔", label:"Adultes" },
                        { key:"business", icon:"💼", label:"Business" },
                      ];
                      const tarifsMoy = Object.values(tarifs).filter(v=>v!=null && v>0);
                      const moyTarif = tarifsMoy.length ? Math.round(tarifsMoy.reduce((s,v)=>s+v,0)/tarifsMoy.length) : 0;
                      const sessTotal = c.sessions_totales ?? c.nb_seances_total ?? 0;
                      const sessMois  = c.sessions_mois   ?? c.nb_seances_realisees ?? 0;
                      const estim = sessMois * 2 * moyTarif;
                      const dateFin = c.date_fin ? fmtDate(c.date_fin) : <span style={{ color:"#16a34a", fontWeight:700 }}>Indéterminé</span>;
                      return (
                        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                          <div style={{ fontSize:15, fontWeight:800, color:"#1e293b" }}>Contrat &amp; rémunération</div>

                          {/* Type de contrat */}
                          <div style={{ background:"#fff", border:`1.5px solid ${coachContratEditOpen?"#0891b2":"#e2e8f0"}`, borderRadius:12, padding:"16px 18px", transition:"border-color .15s" }}>
                            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                              <div style={{ fontSize:10, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:"0.06em" }}>Type de contrat</div>
                              {!coachContratEditOpen ? (
                                <button onClick={() => { setCoachContratTypeEdit(typeRH); setCoachContratEditOpen(true); }}
                                  style={{ padding:"4px 12px", border:`1.5px solid ${couleur}`, borderRadius:8, background:"#fff", color:couleur, fontSize:11, fontWeight:700, cursor:"pointer" }}>✏️ Modifier</button>
                              ) : (
                                <div style={{ display:"flex", gap:6 }}>
                                  <button onClick={() => setCoachContratEditOpen(false)}
                                    style={{ padding:"4px 12px", border:"1.5px solid #e2e8f0", borderRadius:8, background:"#fff", color:"#6b7280", fontSize:11, fontWeight:700, cursor:"pointer" }}>Annuler</button>
                                  <button disabled={savingCoachContratType} onClick={async () => {
                                    if (savingCoachContratType) return;
                                    setSavingCoachContratType(true);
                                    try {
                                    const newCoachInfo = { ...(selectedCoach.coach_info || {}), type_contrat: coachContratTypeEdit };
                                    const r = await fetch(`${API_URL}/api/admin/utilisateurs/${selectedCoach.id}`, {
                                      method: "PATCH",
                                      headers: authHeaders(),
                                      body: JSON.stringify({ coach_info: newCoachInfo }),
                                    });
                                    if (r.ok) {
                                      setSelectedCoach(prev => ({ ...prev, coach_info: newCoachInfo }));
                                      setCoachContratEditOpen(false);
                                    } else {
                                      const err = await r.json().catch(() => ({}));
                                      await alerte(`❌ Erreur ${r.status} : ${err.error || "impossible de sauvegarder"}`, { danger: true });
                                    }
                                    } finally { setSavingCoachContratType(false); }
                                  }}
                                    style={{ padding:"4px 12px", border:"none", borderRadius:8, background:couleur, color:"#fff", fontSize:11, fontWeight:700, cursor:savingCoachContratType?"not-allowed":"pointer", opacity:savingCoachContratType?0.6:1 }}>{savingCoachContratType?"⏳":"✅ Enregistrer"}</button>
                                </div>
                              )}
                            </div>

                            {/* Vue lecture */}
                            {!coachContratEditOpen && (
                              <span style={{ background:"#f1f5f9", borderRadius:8, padding:"5px 14px", fontSize:13, fontWeight:800, color:"#1e293b" }}>📝 {typeRH}</span>
                            )}

                            {/* Sélecteur types de contrat */}
                            {coachContratEditOpen && (
                              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginTop:4 }}>
                                {[
                                  { id:"CDI",        icon:"🏢", label:"Permanent",   desc:"Contrat à durée indéterminée" },
                                  { id:"CDD",        icon:"📋", label:"CDD",         desc:"Contrat à durée déterminée" },
                                  { id:"Freelance",  icon:"🔖", label:"Freelance",   desc:"Prestataire indépendant" },
                                  { id:"Consultant", icon:"💼", label:"Consultant",  desc:"Mission ponctuelle" },
                                  { id:"Stagiaire",  icon:"🎓", label:"Stagiaire",   desc:"Convention de stage" },
                                  { id:"Vacataire",  icon:"📅", label:"Vacataire",   desc:"Intervenant occasionnel" },
                                ].map(opt => {
                                  const sel = coachContratTypeEdit === opt.id;
                                  return (
                                    <div key={opt.id} onClick={() => setCoachContratTypeEdit(opt.id)}
                                      style={{ display:"flex", alignItems:"center", gap:10, padding:"11px 14px", borderRadius:10, border:`2px solid ${sel?couleur:"#e2e8f0"}`, background:sel?couleur+"0d":"#fafafa", cursor:"pointer", transition:"all .12s" }}>
                                      <span style={{ fontSize:20, flexShrink:0 }}>{opt.icon}</span>
                                      <div>
                                        <div style={{ fontWeight:700, fontSize:13, color:sel?couleur:"#1e293b" }}>{opt.label}</div>
                                        <div style={{ fontSize:10, color:"#9ca3af", marginTop:1 }}>{opt.desc}</div>
                                      </div>
                                      {sel && <span style={{ marginLeft:"auto", fontSize:16, color:couleur }}>✓</span>}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* Honoraires - grille tarifaire */}
                          <div style={{ background:"#f8fafc", border:"1.5px solid #e2e8f0", borderRadius:12, padding:"16px 18px" }}>
                            <div style={{ fontSize:10, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:12 }}>
                              💰 Honoraires - Grille tarifaire ({typeRH})
                            </div>
                            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:14 }}>
                              {CATS.map(cat => (
                                <div key={cat.key} style={{ background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:10, padding:"14px 10px", textAlign:"center" }}>
                                  <div style={{ fontSize:22, marginBottom:6 }}>{cat.icon}</div>
                                  <div style={{ fontSize:12, color:"#374151", fontWeight:600, marginBottom:8 }}>{cat.label}</div>
                                  <div style={{ fontSize:16, fontWeight:900, color: tarifs[cat.key] ? couleur : "#9ca3af" }}>
                                    {tarifs[cat.key] ? tarifs[cat.key].toLocaleString("fr-FR") : "-"}
                                  </div>
                                  <div style={{ fontSize:10, color:"#9ca3af" }}>/heure</div>
                                </div>
                              ))}
                            </div>
                            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", background:"#fff", border:"1px solid #e2e8f0", borderRadius:10, padding:"12px 16px" }}>
                              <div>
                                <div style={{ fontWeight:700, fontSize:13, color:"#1e293b" }}>Estimation du mois en cours</div>
                                <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>
                                  {sessMois} sessions × ~2h × tarif moyen {moyTarif.toLocaleString("fr-FR")} FCFA/h
                                </div>
                              </div>
                              <div style={{ textAlign:"right" }}>
                                <div style={{ fontSize:18, fontWeight:900, color: estim > 0 ? couleur : "#9ca3af" }}>
                                  {estim > 0 ? estim.toLocaleString("fr-FR") + " FCFA" : "0 FCFA"}
                                </div>
                                <div style={{ fontSize:10, color:"#9ca3af" }}>estimation indicative</div>
                              </div>
                            </div>
                          </div>

                          {/* Infos contrat */}
                          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                            {[
                              ["DATE DE DÉBUT",    fmtDate(c.date_debut) || "-"],
                              ["DATE DE FIN",      dateFin],
                              ["STATUT",           c.statut === "actif" ? <span style={{ color:"#16a34a", fontWeight:700 }}>✅ Actif</span> : (c.statut || "-")],
                              ["EN CONGÉ",         c.en_conge ? <span style={{ color:"#d97706", fontWeight:700 }}>🌴 Oui</span> : "Non"],
                              ["SESSIONS TOTALES", <span style={{ fontWeight:900, fontSize:15, color:"#1e293b" }}>{sessTotal}</span>],
                              ["SESSIONS CE MOIS", <span style={{ fontWeight:900, fontSize:15, color:couleur }}>{sessMois}</span>],
                            ].map(([lbl, val]) => (
                              <div key={lbl} style={{ background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:10, padding:"14px 16px" }}>
                                <div style={{ fontSize:10, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6 }}>{lbl}</div>
                                <div style={{ fontSize:13, fontWeight:600, color:"#1e293b" }}>{val}</div>
                              </div>
                            ))}
                          </div>

                          {/* ── Liste des contrats ── */}
                          {/* ── Historique contrats : tableau dépliable + filtre + pagination ── */}
                          {(() => {
                            const CT_DEF = {
                              en_ligne:      { label:"En ligne",       icon:"💻", color:"#0891b2", bg:"#e0f2fe" },
                              presentiel_bet:{ label:"Présentiel BET", icon:"🏢", color:"#7c3aed", bg:"#ede9fe" },
                              domicile:      { label:"À domicile",     icon:"🏠", color:"#d97706", bg:"#fef3c7" },
                            };
                            const CS_DEF = {
                              en_attente:    { label:"En attente",    color:"#d97706", bg:"#fef3c7" },
                              actif:         { label:"Actif",         color:"#16a34a", bg:"#dcfce7" },
                              suspendu:      { label:"Suspendu",      color:"#9ca3af", bg:"#f3f4f6" },
                              termine:       { label:"Terminé",       color:"#6b7280", bg:"#f3f4f6" },
                              renouvele:     { label:"Renouvelé",     color:"#2563eb", bg:"#dbeafe" },
                              non_renouvele: { label:"Non renouvelé", color:"#dc2626", bg:"#fee2e2" },
                            };
                            const CPG = 2;
                            const filtered = contratsAffiches.filter(ct => {
                              if (coachContratDateFrom && ct.date_debut < coachContratDateFrom) return false;
                              if (coachContratDateTo   && ct.date_debut > coachContratDateTo)   return false;
                              return true;
                            });
                            const nPages  = Math.max(1, Math.ceil(filtered.length / CPG));
                            const safePg  = Math.min(coachContratPage, nPages - 1);
                            const pageCts = filtered.slice(safePg * CPG, (safePg + 1) * CPG);
                            const thS = { padding:"9px 14px", fontSize:10, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.05em", textAlign:"left", background:"#f1f5f9", whiteSpace:"nowrap", borderBottom:"2px solid #e2e8f0" };
                            const tdS = { padding:"11px 14px", fontSize:12, color:"#374151", verticalAlign:"middle", borderBottom:"1px solid #f1f5f9" };
                            return (
                              <div>
                                {/* En-tête + filtres */}
                                <div style={{ background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:"10px 10px 0 0", padding:"11px 14px", display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                                  <span style={{ fontSize:13, fontWeight:800, color:"#1e293b" }}>📋 Historique des contrats</span>
                                  <span style={{ fontSize:11, color:"#9ca3af" }}>({filtered.length}/{contratsAffiches.length})</span>
                                  <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                                    <span style={{ fontSize:11, color:"#64748b", fontWeight:600 }}>🗓 Début du :</span>
                                    <input type="date" value={coachContratDateFrom} onChange={e=>{ setCoachContratDateFrom(e.target.value); setCoachContratPage(0); }}
                                      style={{ border:"1px solid #d1d5db", borderRadius:6, padding:"3px 7px", fontSize:11, color:"#374151", outline:"none" }} />
                                    <span style={{ fontSize:11, color:"#64748b" }}>au</span>
                                    <input type="date" value={coachContratDateTo} onChange={e=>{ setCoachContratDateTo(e.target.value); setCoachContratPage(0); }}
                                      style={{ border:"1px solid #d1d5db", borderRadius:6, padding:"3px 7px", fontSize:11, color:"#374151", outline:"none" }} />
                                    {(coachContratDateFrom||coachContratDateTo) && (
                                      <button onClick={()=>{ setCoachContratDateFrom(""); setCoachContratDateTo(""); setCoachContratPage(0); }}
                                        style={{ fontSize:10, color:"#dc2626", background:"#fee2e2", border:"none", borderRadius:6, padding:"3px 8px", cursor:"pointer", fontWeight:700 }}>✕</button>
                                    )}
                                  </div>
                                </div>

                                {/* Tableau */}
                                <div style={{ border:"1px solid #e2e8f0", borderTop:"none", borderRadius: nPages>1?"0":"0 0 10px 10px", overflow:"hidden" }}>
                                  {filtered.length === 0 ? (
                                    <div style={{ textAlign:"center", padding:"30px 0", color:"#9ca3af", fontSize:12 }}>Aucun contrat sur cette période.</div>
                                  ) : (
                                    <table style={{ width:"100%", borderCollapse:"collapse" }}>
                                      <thead>
                                        <tr>
                                          <th style={thS}>Type</th>
                                          <th style={thS}>Matière</th>
                                          <th style={thS}>Début</th>
                                          <th style={thS}>Fin</th>
                                          <th style={{ ...thS, textAlign:"center" }}>Prix/h</th>
                                          <th style={{ ...thS, textAlign:"center" }}>Séances</th>
                                          <th style={{ ...thS, textAlign:"center" }}>Statut</th>
                                          <th style={{ ...thS, textAlign:"center" }}>Détails</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {pageCts.map((ct, i) => {
                                          const st  = CS_DEF[ct.statut] || { label:ct.statut||"-", color:"#6b7280", bg:"#f3f4f6" };
                                          const typ = CT_DEF[ct.type_contrat] || { label:ct.type_contrat||"Autre", icon:"📄", color:"#6b7280", bg:"#f3f4f6" };
                                          const pct = ct.nb_seances_total > 0 ? Math.round(((ct.nb_seances_realisees||0)/ct.nb_seances_total)*100) : 0;
                                          const rowKey = ct.id || i;
                                          const open = coachContratExpanded.has(rowKey);
                                          return (
                                            <React.Fragment key={rowKey}>
                                              {/* Ligne principale */}
                                              <tr style={{ background: i%2===0?"#fff":"#fafbfc", cursor:"pointer" }}
                                                  onClick={() => setCoachContratExpanded(s => { const n=new Set(s); n.has(rowKey)?n.delete(rowKey):n.add(rowKey); return n; })}>
                                                <td style={tdS}>
                                                  <span style={{ background:typ.bg, color:typ.color, borderRadius:7, padding:"2px 9px", fontSize:10, fontWeight:700 }}>{typ.icon} {typ.label}</span>
                                                  {ct.type_contrat_rh && <span style={{ marginLeft:5, background:"#f1f5f9", color:"#475569", borderRadius:5, padding:"1px 7px", fontSize:10, fontWeight:700 }}>{ct.type_contrat_rh}</span>}
                                                </td>
                                                <td style={{ ...tdS, fontWeight:600, color:"#1e293b" }}>{ct.matiere || "-"}</td>
                                                <td style={{ ...tdS, whiteSpace:"nowrap" }}>{fmtDate(ct.date_debut)||"-"}</td>
                                                <td style={{ ...tdS, whiteSpace:"nowrap", color: ct.date_fin?"#374151":"#16a34a", fontWeight: ct.date_fin?400:700 }}>{ct.date_fin ? fmtDate(ct.date_fin) : "Indéterminé"}</td>
                                                <td style={{ ...tdS, textAlign:"center", fontWeight:700, color:couleur }}>{ct.prix_h ? ct.prix_h.toLocaleString("fr-FR")+" F" : "-"}</td>
                                                <td style={{ ...tdS, textAlign:"center" }}>
                                                  <span style={{ fontWeight:700, color:"#1e293b" }}>{ct.nb_seances_realisees||0}</span>
                                                  <span style={{ color:"#9ca3af" }}>/{ct.nb_seances_total||"-"}</span>
                                                </td>
                                                <td style={{ ...tdS, textAlign:"center" }}>
                                                  <span style={{ background:st.bg, color:st.color, borderRadius:7, padding:"2px 9px", fontSize:10, fontWeight:700 }}>{st.label}</span>
                                                </td>
                                                <td style={{ ...tdS, textAlign:"center", fontSize:14, color:open?couleur:"#9ca3af" }}>{open?"▲":"▼"}</td>
                                              </tr>

                                              {/* Ligne dépliée */}
                                              {open && (
                                                <tr>
                                                  <td colSpan={8} style={{ padding:"0", background:"#f0f9ff", borderBottom:"2px solid "+couleur+"30" }}>
                                                    <div style={{ padding:"14px 20px", display:"flex", flexDirection:"column", gap:10 }}>
                                                      {/* Mini grille */}
                                                      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 }}>
                                                        {[
                                                          ["Durée/séance", ct.duree_seance_h ? ct.duree_seance_h+"h" : "-"],
                                                          ["Séances totales", ct.nb_seances_total||"-"],
                                                          ["Séances réalisées", ct.nb_seances_realisees||0],
                                                        ].map(([l,v])=>(
                                                          <div key={l} style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:8, padding:"8px 12px" }}>
                                                            <div style={{ fontSize:10, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{l}</div>
                                                            <div style={{ fontSize:13, fontWeight:700, color:"#1e293b", marginTop:2 }}>{v}</div>
                                                          </div>
                                                        ))}
                                                      </div>
                                                      {/* Barre avancement */}
                                                      {ct.nb_seances_total > 0 && (
                                                        <div>
                                                          <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"#6b7280", marginBottom:4 }}>
                                                            <span>Avancement</span>
                                                            <span style={{ fontWeight:700, color:pct>=80?"#16a34a":pct>=40?"#d97706":"#dc2626" }}>{pct}%</span>
                                                          </div>
                                                          <div style={{ height:6, background:"#e2e8f0", borderRadius:99 }}>
                                                            <div style={{ height:6, width:`${pct}%`, background:pct>=80?"#16a34a":pct>=40?"#d97706":couleur, borderRadius:99, transition:"width .3s" }} />
                                                          </div>
                                                        </div>
                                                      )}
                                                    </div>
                                                  </td>
                                                </tr>
                                              )}
                                            </React.Fragment>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  )}
                                </div>

                                {/* Pagination */}
                                {filtered.length > 0 && (
                                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"9px 14px", border:"1px solid #e2e8f0", borderTop:"none", borderRadius:"0 0 10px 10px", background:"#f8fafc" }}>
                                    <span style={{ fontSize:11, color:"#94a3b8" }}>Page {safePg+1}/{nPages} · {safePg*CPG+1}–{Math.min((safePg+1)*CPG,filtered.length)} sur {filtered.length}</span>
                                    <div style={{ display:"flex", gap:5 }}>
                                      <button onClick={()=>setCoachContratPage(p=>Math.max(0,p-1))} disabled={safePg===0}
                                        style={{ padding:"3px 10px", border:"1px solid #e2e8f0", borderRadius:6, fontSize:12, fontWeight:700, cursor:safePg===0?"not-allowed":"pointer", background:safePg===0?"#f1f5f9":"#fff", color:safePg===0?"#cbd5e1":"#374151" }}>‹</button>
                                      {Array.from({length:nPages},(_,idx)=>(
                                        <button key={idx} onClick={()=>setCoachContratPage(idx)}
                                          style={{ padding:"3px 9px", border:`1.5px solid ${safePg===idx?couleur:"#e2e8f0"}`, borderRadius:6, fontSize:12, fontWeight:700, cursor:"pointer", background:safePg===idx?couleur:"#fff", color:safePg===idx?"#fff":"#374151" }}>{idx+1}</button>
                                      ))}
                                      <button onClick={()=>setCoachContratPage(p=>Math.min(nPages-1,p+1))} disabled={safePg===nPages-1}
                                        style={{ padding:"3px 10px", border:"1px solid #e2e8f0", borderRadius:6, fontSize:12, fontWeight:700, cursor:safePg===nPages-1?"not-allowed":"pointer", background:safePg===nPages-1?"#f1f5f9":"#fff", color:safePg===nPages-1?"#cbd5e1":"#374151" }}>›</button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* ════ GROUPES ════ */}
                {coachModalTab === "groupes" && (() => {
                  const GPG = 3;
                  const filtered = groupesAffiches.filter(g => {
                    if (coachGroupeDateFrom && g.date_debut && g.date_debut < coachGroupeDateFrom) return false;
                    if (coachGroupeDateTo   && g.date_debut && g.date_debut > coachGroupeDateTo)   return false;
                    return true;
                  });
                  const nPages = Math.max(1, Math.ceil(filtered.length / GPG));
                  const safePg = Math.min(coachGroupePage, nPages - 1);
                  const pageGrp = filtered.slice(safePg * GPG, (safePg + 1) * GPG);
                  const thS = { padding:"12px 20px", fontSize:10, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.05em", textAlign:"left", background:"#f1f5f9", whiteSpace:"nowrap", borderBottom:"2px solid #e2e8f0" };
                  const tdS = { padding:"14px 20px", fontSize:13, color:"#374151", verticalAlign:"middle", borderBottom:"1px solid #f1f5f9" };
                  return (
                    <div>
                      {/* Modal apprenants du groupe */}
                      {coachGroupeDetail && (
                        <ModaleAnimee ouvert sansPanneau
                          onClicVoile={() => setCoachGroupeDetail(null)}
                          style={{ position:"fixed", inset:0, background:"rgba(15,23,42,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:299999, backdropFilter:"blur(4px)" }}>
                          <div style={{ background:"#fff", borderRadius:16, width:"90vw", maxHeight:"90vh", display:"flex", flexDirection:"column", boxShadow:"0 24px 60px rgba(0,0,0,0.3)", overflow:"hidden" }}
                               onClick={e => e.stopPropagation()}>
                            {/* Header */}
                            <div style={{ background:`linear-gradient(135deg,${couleur}dd,${couleur})`, padding:"18px 22px", display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexShrink:0 }}>
                              <div>
                                <div style={{ fontWeight:800, fontSize:16, color:"#fff" }}>🏫 {coachGroupeDetail.nom}</div>
                                <div style={{ fontSize:12, color:"rgba(255,255,255,0.75)", marginTop:4, display:"flex", gap:14, flexWrap:"wrap" }}>
                                  {coachGroupeDetail.niveau && <span>📊 {coachGroupeDetail.niveau}</span>}
                                  {coachGroupeDetail.matiere && <span>📚 {coachGroupeDetail.matiere}</span>}
                                  <span>👥 {coachGroupeDetailLoading ? "…" : `${(coachGroupeDetail.apprenants||[]).length} apprenant${(coachGroupeDetail.apprenants||[]).length!==1?"s":""}`}</span>
                                  {coachGroupeDetail.date_debut && <span>📅 {coachGroupeDetail.date_debut} → {coachGroupeDetail.date_fin || "En cours"}</span>}
                                </div>
                              </div>
                              <button onClick={() => setCoachGroupeDetail(null)}
                                style={{ background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)", borderRadius:8, width:32, height:32, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:18, color:"#fff", flexShrink:0 }}>×</button>
                            </div>
                            {/* Stats rapides */}
                            {!coachGroupeDetailLoading && (coachGroupeDetail.apprenants||[]).length > 0 && (() => {
                              const apps = coachGroupeDetail.apprenants;
                              const actifs   = apps.filter(a => a.statut === "actif").length;
                              const retires  = apps.filter(a => a.statut === "retire" || a.statut === "transfere").length;
                              return (
                                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:0, borderBottom:"1px solid #e2e8f0", flexShrink:0 }}>
                                  {[
                                    ["Total",    apps.length,  "#0891b2"],
                                    ["Actifs",   actifs,       "#16a34a"],
                                    ["Retirés",  retires,      retires>0?"#dc2626":"#9ca3af"],
                                  ].map(([l,v,c])=>(
                                    <div key={l} style={{ padding:"14px", textAlign:"center", borderRight:"1px solid #e2e8f0" }}>
                                      <div style={{ fontSize:22, fontWeight:900, color:c }}>{v}</div>
                                      <div style={{ fontSize:10, color:"#9ca3af", fontWeight:700, marginTop:3 }}>{l}</div>
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                            {/* Tableau apprenants */}
                            <div style={{ overflowY:"auto", flex:1 }}>
                              {coachGroupeDetailLoading ? (
                                <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af", fontSize:13 }}>⏳ Chargement des apprenants…</div>
                              ) : (coachGroupeDetail.apprenants||[]).length === 0 ? (
                                <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af", fontSize:13 }}>Aucun apprenant dans ce groupe.</div>
                              ) : (
                                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                                  <thead style={{ position:"sticky", top:0, zIndex:1 }}>
                                    <tr>
                                      <th style={{ ...thS, paddingLeft:20 }}>#</th>
                                      <th style={thS}>Nom</th>
                                      <th style={thS}>Niveau</th>
                                      <th style={thS}>Email</th>
                                      <th style={thS}>Téléphone</th>
                                      <th style={{ ...thS, textAlign:"center" }}>Date ajout</th>
                                      <th style={{ ...thS, textAlign:"center" }}>Statut</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {coachGroupeDetail.apprenants.map((a, i) => {
                                      const STA = {
                                        actif:      { label:"Actif",      bg:"#dcfce7", color:"#16a34a" },
                                        periode_test:{ label:"Période test",bg:"#e0f2fe", color:"#0891b2" },
                                        retire:     { label:"Retiré",     bg:"#fee2e2", color:"#dc2626" },
                                        transfere:  { label:"Transféré",  bg:"#fef3c7", color:"#d97706" },
                                      };
                                      const sta = STA[a.statut] || { label: a.statut||"-", bg:"#f3f4f6", color:"#6b7280" };
                                      const nomComplet = [a.prenom_apprenant, a.nom_apprenant].filter(Boolean).join(" ") || "-";
                                      const dateAjout = a.date_ajout ? new Date(a.date_ajout).toLocaleDateString("fr-FR") : "-";
                                      return (
                                        <tr key={i} style={{ background: a.statut==="retire"?"#fff5f5":a.statut==="transfere"?"#fffbeb":i%2===0?"#fff":"#fafbfc" }}>
                                          <td style={{ ...tdS, paddingLeft:20, color:"#9ca3af", fontWeight:700 }}>{i+1}</td>
                                          <td style={{ ...tdS, fontWeight:700, color:"#1e293b" }}>{nomComplet}</td>
                                          <td style={{ ...tdS, color:"#6b7280" }}>{a.niveau||"-"}</td>
                                          <td style={{ ...tdS, color:"#6b7280", fontSize:11 }}>{a.email_apprenant||"-"}</td>
                                          <td style={{ ...tdS, color:"#6b7280" }}>{a.telephone||"-"}</td>
                                          <td style={{ ...tdS, textAlign:"center", color:"#6b7280" }}>{dateAjout}</td>
                                          <td style={{ ...tdS, textAlign:"center" }}>
                                            <span style={{ background:sta.bg, color:sta.color, borderRadius:7, padding:"2px 9px", fontSize:10, fontWeight:700 }}>{sta.label}</span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </div>
                        </ModaleAnimee>
                      )}

                      {/* En-tête + filtres */}
                      <div style={{ background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:"10px 10px 0 0", padding:"11px 14px", display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                        <span style={{ fontSize:13, fontWeight:800, color:"#1e293b" }}>🏫 Groupes</span>
                        <span style={{ fontSize:11, color:"#9ca3af" }}>({filtered.length}/{groupesAffiches.length})</span>
                        <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                          <span style={{ fontSize:11, color:"#64748b", fontWeight:600 }}>🗓 Début du :</span>
                          <input type="date" value={coachGroupeDateFrom} onChange={e=>{ setCoachGroupeDateFrom(e.target.value); setCoachGroupePage(0); }}
                            style={{ border:"1px solid #d1d5db", borderRadius:6, padding:"3px 7px", fontSize:11, color:"#374151", outline:"none" }} />
                          <span style={{ fontSize:11, color:"#64748b" }}>au</span>
                          <input type="date" value={coachGroupeDateTo} onChange={e=>{ setCoachGroupeDateTo(e.target.value); setCoachGroupePage(0); }}
                            style={{ border:"1px solid #d1d5db", borderRadius:6, padding:"3px 7px", fontSize:11, color:"#374151", outline:"none" }} />
                          {(coachGroupeDateFrom||coachGroupeDateTo) && (
                            <button onClick={()=>{ setCoachGroupeDateFrom(""); setCoachGroupeDateTo(""); setCoachGroupePage(0); }}
                              style={{ fontSize:10, color:"#dc2626", background:"#fee2e2", border:"none", borderRadius:6, padding:"3px 8px", cursor:"pointer", fontWeight:700 }}>✕</button>
                          )}
                        </div>
                      </div>

                      {/* Tableau */}
                      <div style={{ border:"1px solid #e2e8f0", borderTop:"none", borderRadius: nPages>1?"0":"0 0 10px 10px", overflow:"hidden" }}>
                        {coachGroupesLoading ? (
                          <div style={{ textAlign:"center", padding:"30px 0", color:"#9ca3af" }}>Chargement…</div>
                        ) : filtered.length === 0 ? (
                          <div style={{ textAlign:"center", padding:"30px 0", color:"#9ca3af", fontSize:12 }}>Aucun groupe sur cette période.</div>
                        ) : (
                          <table style={{ width:"100%", borderCollapse:"collapse" }}>
                            <thead>
                              <tr>
                                <th style={thS}>Groupe</th>
                                <th style={thS}>Niveau</th>
                                <th style={thS}>Matière</th>
                                <th style={thS}>Début</th>
                                <th style={thS}>Fin</th>
                                <th style={{ ...thS, textAlign:"center" }}>Apprenants</th>
                                <th style={{ ...thS, textAlign:"center" }}>Statut</th>
                                <th style={{ ...thS, textAlign:"center" }}>Détails</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pageGrp.map((g, i) => (
                                <tr key={g.id||i}
                                    onClick={async () => {
                                      setCoachGroupeDetail(g);
                                      setCoachGroupeDetailLoading(true);
                                      try {
                                        const r = await fetch(`${API_URL}/api/groupes/${g.id}`, { headers: authHeaders() });
                                        const d = await r.json();
                                        setCoachGroupeDetail(prev => prev && prev.id === g.id
                                          ? { ...prev, apprenants: d.apprenants || [], fichiers: d.fichiers || [] }
                                          : prev
                                        );
                                      } catch {}
                                      finally { setCoachGroupeDetailLoading(false); }
                                    }}
                                    style={{ background: i%2===0?"#fff":"#fafbfc", cursor:"pointer", transition:"background .1s" }}
                                    onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                                    onMouseLeave={e=>e.currentTarget.style.background=i%2===0?"#fff":"#fafbfc"}>
                                  <td style={{ ...tdS, fontWeight:700, color:couleur, textDecoration:"underline", textDecorationStyle:"dotted" }}>
                                    {g.nom||g.name||`Groupe #${i+1}`}
                                  </td>
                                  <td style={{ ...tdS, color:"#6b7280" }}>{g.niveau||"-"}</td>
                                  <td style={{ ...tdS, color:"#6b7280" }}>{g.matiere||"-"}</td>
                                  <td style={{ ...tdS, whiteSpace:"nowrap" }}>{g.date_debut||"-"}</td>
                                  <td style={{ ...tdS, whiteSpace:"nowrap", color:g.date_fin?"#374151":"#16a34a", fontWeight:g.date_fin?400:700 }}>{g.date_fin||"En cours"}</td>
                                  <td style={{ ...tdS, textAlign:"center", fontWeight:800, color:couleur }}>{g.nb_apprenants||0}</td>
                                  <td style={{ ...tdS, textAlign:"center" }}>
                                    <span style={{ background:g.statut==="actif"?"#dcfce7":"#f3f4f6", color:g.statut==="actif"?"#16a34a":"#6b7280", borderRadius:7, padding:"2px 9px", fontSize:10, fontWeight:700 }}>
                                      {g.statut==="actif"?"✅ Actif":"⏹ Terminé"}
                                    </span>
                                  </td>
                                  <td style={{ ...tdS, textAlign:"center", fontSize:13, color:couleur }}>👁</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>

                      {/* Pagination */}
                      {filtered.length > 0 && (
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"9px 14px", border:"1px solid #e2e8f0", borderTop:"none", borderRadius:"0 0 10px 10px", background:"#f8fafc" }}>
                          <span style={{ fontSize:11, color:"#94a3b8" }}>Page {safePg+1}/{nPages} · {safePg*GPG+1}–{Math.min((safePg+1)*GPG,filtered.length)} sur {filtered.length}</span>
                          <div style={{ display:"flex", gap:5 }}>
                            <button onClick={()=>setCoachGroupePage(p=>Math.max(0,p-1))} disabled={safePg===0}
                              style={{ padding:"3px 10px", border:"1px solid #e2e8f0", borderRadius:6, fontSize:12, fontWeight:700, cursor:safePg===0?"not-allowed":"pointer", background:safePg===0?"#f1f5f9":"#fff", color:safePg===0?"#cbd5e1":"#374151" }}>‹</button>
                            {Array.from({length:nPages},(_,idx)=>(
                              <button key={idx} onClick={()=>setCoachGroupePage(idx)}
                                style={{ padding:"3px 9px", border:`1.5px solid ${safePg===idx?couleur:"#e2e8f0"}`, borderRadius:6, fontSize:12, fontWeight:700, cursor:"pointer", background:safePg===idx?couleur:"#fff", color:safePg===idx?"#fff":"#374151" }}>{idx+1}</button>
                            ))}
                            <button onClick={()=>setCoachGroupePage(p=>Math.min(nPages-1,p+1))} disabled={safePg===nPages-1}
                              style={{ padding:"3px 10px", border:"1px solid #e2e8f0", borderRadius:6, fontSize:12, fontWeight:700, cursor:safePg===nPages-1?"not-allowed":"pointer", background:safePg===nPages-1?"#f1f5f9":"#fff", color:safePg===nPages-1?"#cbd5e1":"#374151" }}>›</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ════ SESSIONS ════ */}
                {coachModalTab === "sessions" && (() => {
                  const CFG = {
                    dispense: { label:"Dispensée", color:"#16a34a", bg:"#dcfce7", icon:"✅" },
                    a_venir:  { label:"À venir",   color:"#0891b2", bg:"#e0f2fe", icon:"📅" },
                    annule:   { label:"Annulée",   color:"#dc2626", bg:"#fee2e2", icon:"❌" },
                  };
                  const SPG = 4;
                  const filtered = sessionsAffichees.filter(s => {
                    if (coachSessionDateFrom && s.date < coachSessionDateFrom) return false;
                    if (coachSessionDateTo   && s.date > coachSessionDateTo)   return false;
                    return true;
                  });
                  const nPages = Math.max(1, Math.ceil(filtered.length / SPG));
                  const safePg = Math.min(coachSessionPage, nPages - 1);
                  const pageSess = filtered.slice(safePg * SPG, (safePg + 1) * SPG);
                  const thS = { padding:"9px 14px", fontSize:10, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.05em", textAlign:"left", background:"#f1f5f9", whiteSpace:"nowrap", borderBottom:"2px solid #e2e8f0" };
                  const tdS = { padding:"11px 14px", fontSize:12, color:"#374151", verticalAlign:"middle", borderBottom:"1px solid #f1f5f9" };
                  return (
                    <div>
                      {/* En-tête + filtres */}
                      <div style={{ background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:"10px 10px 0 0", padding:"11px 14px", display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                        <span style={{ fontSize:13, fontWeight:800, color:"#1e293b" }}>🎥 Sessions</span>
                        <span style={{ fontSize:11, color:"#9ca3af" }}>({filtered.length}/{sessionsAffichees.length})</span>
                        <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                          <span style={{ fontSize:11, color:"#64748b", fontWeight:600 }}>🗓 Du :</span>
                          <input type="date" value={coachSessionDateFrom} onChange={e=>{ setCoachSessionDateFrom(e.target.value); setCoachSessionPage(0); }}
                            style={{ border:"1px solid #d1d5db", borderRadius:6, padding:"3px 7px", fontSize:11, color:"#374151", outline:"none" }} />
                          <span style={{ fontSize:11, color:"#64748b" }}>au</span>
                          <input type="date" value={coachSessionDateTo} onChange={e=>{ setCoachSessionDateTo(e.target.value); setCoachSessionPage(0); }}
                            style={{ border:"1px solid #d1d5db", borderRadius:6, padding:"3px 7px", fontSize:11, color:"#374151", outline:"none" }} />
                          {(coachSessionDateFrom||coachSessionDateTo) && (
                            <button onClick={()=>{ setCoachSessionDateFrom(""); setCoachSessionDateTo(""); setCoachSessionPage(0); }}
                              style={{ fontSize:10, color:"#dc2626", background:"#fee2e2", border:"none", borderRadius:6, padding:"3px 8px", cursor:"pointer", fontWeight:700 }}>✕</button>
                          )}
                        </div>
                      </div>

                      {/* Tableau */}
                      <div style={{ border:"1px solid #e2e8f0", borderTop:"none", borderRadius: nPages>1?"0":"0 0 10px 10px", overflow:"hidden" }}>
                        {filtered.length === 0 ? (
                          <div style={{ textAlign:"center", padding:"30px 0", color:"#9ca3af", fontSize:12 }}>Aucune session sur cette période.</div>
                        ) : (
                          <table style={{ width:"100%", borderCollapse:"collapse" }}>
                            <thead>
                              <tr>
                                <th style={thS}>Titre</th>
                                <th style={thS}>Groupe</th>
                                <th style={thS}>Date</th>
                                <th style={thS}>Heure</th>
                                <th style={thS}>Salle</th>
                                <th style={{ ...thS, textAlign:"center" }}>Statut</th>
                                <th style={{ ...thS, textAlign:"center" }}>Détails</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pageSess.map((s, i) => {
                                const cfg = CFG[s.statut] || { label:s.statut||"-", color:"#6b7280", bg:"#f3f4f6", icon:"📌" };
                                const rowKey = s.id || i;
                                const open = coachSessionExpanded.has(rowKey);
                                return (
                                  <React.Fragment key={rowKey}>
                                    <tr style={{ background: i%2===0?"#fff":"#fafbfc", cursor:"pointer" }}
                                        onClick={() => setCoachSessionExpanded(prev => { const n=new Set(prev); n.has(rowKey)?n.delete(rowKey):n.add(rowKey); return n; })}>
                                      <td style={{ ...tdS, fontWeight:700, color:"#1e293b", maxWidth:200 }}>{s.titre}</td>
                                      <td style={{ ...tdS, color:"#6b7280" }}>{s.groupe}</td>
                                      <td style={{ ...tdS, whiteSpace:"nowrap", fontWeight:600 }}>{s.date}</td>
                                      <td style={{ ...tdS, whiteSpace:"nowrap", color:"#6b7280" }}>{s.heure}</td>
                                      <td style={{ ...tdS, color:"#6b7280" }}>{s.salle || "-"}</td>
                                      <td style={{ ...tdS, textAlign:"center" }}>
                                        <span style={{ background:cfg.bg, color:cfg.color, borderRadius:7, padding:"2px 9px", fontSize:10, fontWeight:700 }}>{cfg.icon} {cfg.label}</span>
                                      </td>
                                      <td style={{ ...tdS, textAlign:"center", fontSize:14, color:open?couleur:"#9ca3af" }}>{open?"▲":"▼"}</td>
                                    </tr>

                                    {/* Ligne dépliée */}
                                    {open && (
                                      <tr>
                                        <td colSpan={7} style={{ padding:0, background:"#f0f9ff", borderBottom:"2px solid "+couleur+"30" }}>
                                          <div style={{ padding:"14px 20px", display:"flex", flexDirection:"column", gap:10 }}>
                                            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 }}>
                                              {[
                                                ["Présents", s.nb_presents != null ? s.nb_presents : "-"],
                                                ["Absents",  s.nb_absents  != null ? s.nb_absents  : "-"],
                                                ["Taux présence", s.nb_presents != null && (s.nb_presents + (s.nb_absents||0)) > 0
                                                  ? Math.round(s.nb_presents / (s.nb_presents + s.nb_absents) * 100) + "%"
                                                  : "-"],
                                              ].map(([l,v]) => (
                                                <div key={l} style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:8, padding:"8px 12px" }}>
                                                  <div style={{ fontSize:10, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{l}</div>
                                                  <div style={{ fontSize:14, fontWeight:800, color:"#1e293b", marginTop:2 }}>{v}</div>
                                                </div>
                                              ))}
                                            </div>
                                            {s.observation && (
                                              <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#4b5563", fontStyle:"italic", borderLeft:"3px solid "+couleur }}>
                                                💬 {s.observation}
                                              </div>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>

                      {/* Pagination */}
                      {filtered.length > 0 && (
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"9px 14px", border:"1px solid #e2e8f0", borderTop:"none", borderRadius:"0 0 10px 10px", background:"#f8fafc" }}>
                          <span style={{ fontSize:11, color:"#94a3b8" }}>Page {safePg+1}/{nPages} · {safePg*SPG+1}–{Math.min((safePg+1)*SPG,filtered.length)} sur {filtered.length}</span>
                          <div style={{ display:"flex", gap:5 }}>
                            <button onClick={()=>setCoachSessionPage(p=>Math.max(0,p-1))} disabled={safePg===0}
                              style={{ padding:"3px 10px", border:"1px solid #e2e8f0", borderRadius:6, fontSize:12, fontWeight:700, cursor:safePg===0?"not-allowed":"pointer", background:safePg===0?"#f1f5f9":"#fff", color:safePg===0?"#cbd5e1":"#374151" }}>‹</button>
                            {Array.from({length:nPages},(_,idx)=>(
                              <button key={idx} onClick={()=>setCoachSessionPage(idx)}
                                style={{ padding:"3px 9px", border:`1.5px solid ${safePg===idx?couleur:"#e2e8f0"}`, borderRadius:6, fontSize:12, fontWeight:700, cursor:"pointer", background:safePg===idx?couleur:"#fff", color:safePg===idx?"#fff":"#374151" }}>{idx+1}</button>
                            ))}
                            <button onClick={()=>setCoachSessionPage(p=>Math.min(nPages-1,p+1))} disabled={safePg===nPages-1}
                              style={{ padding:"3px 10px", border:"1px solid #e2e8f0", borderRadius:6, fontSize:12, fontWeight:700, cursor:safePg===nPages-1?"not-allowed":"pointer", background:safePg===nPages-1?"#f1f5f9":"#fff", color:safePg===nPages-1?"#cbd5e1":"#374151" }}>›</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ════ CERTIFICATIONS ════ */}
                {coachModalTab === "certifs" && (
                  <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                    {certifsAffichees.map((cert,i) => (
                      <div key={i} style={{ background:"linear-gradient(135deg,#f0f9ff,#e0f2fe)", border:"1.5px solid #bae6fd", borderRadius:12, padding:"14px 16px", display:"flex", alignItems:"center", gap:14 }}>
                        <div style={{ width:44, height:44, borderRadius:10, background:"#0891b2", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>🎓</div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700, fontSize:14, color:"#0c4a6e" }}>
                            {typeof cert === "string" ? cert : (cert.nom||cert.titre||cert.name||"-")}
                          </div>
                          {typeof cert === "object" && (cert.organisme||cert.date) && (
                            <div style={{ fontSize:12, color:"#0369a1", marginTop:3, display:"flex", gap:10 }}>
                              {cert.organisme && <span>🏛️ {cert.organisme}</span>}
                              {cert.date && <span>📅 {fmtDate(cert.date)}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ════ EXPÉRIENCES ════ */}
                {coachModalTab === "exp" && (
                  <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                    {expsAffichees.map((exp,i) => (
                      <div key={i} style={{ background:"#fff", border:"1.5px solid #e5e7eb", borderRadius:12, padding:"16px 18px", borderLeft:`4px solid ${couleur}` }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4 }}>
                          <div style={{ fontWeight:700, fontSize:14, color:"#1e293b" }}>
                            {typeof exp === "string" ? exp : (exp.poste||exp.titre||exp.title||`Expérience #${i+1}`)}
                          </div>
                          {typeof exp === "object" && (exp.date_debut||exp.annee_debut) && (
                            <span style={{ fontSize:11, color:"#9ca3af", flexShrink:0, marginLeft:8 }}>
                              {exp.date_debut||exp.annee_debut} → {exp.date_fin||exp.annee_fin||"Présent"}
                            </span>
                          )}
                        </div>
                        {typeof exp === "object" && exp.entreprise && (
                          <div style={{ fontSize:12, color:"#6b7280", marginBottom:4 }}>🏢 {exp.entreprise}</div>
                        )}
                        {typeof exp === "object" && exp.description && (
                          <p style={{ margin:0, fontSize:12, color:"#4b5563", lineHeight:1.6 }}>{exp.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* ════ DISPONIBILITÉS ════ */}
                {coachModalTab === "dispos" && (
                  <div>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                      <div>
                        <div style={{ fontWeight:700, fontSize:14, color:"#0f172a" }}>📅 Créneaux renseignés par le coach</div>
                        <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>Disponibilités déclarées dans l'Espace Professeur</div>
                      </div>
                      <button onClick={()=>fetchCoachDispos(coach.id)}
                        style={{ padding:"5px 12px", borderRadius:7, border:"1.5px solid #e5e7eb", background:"#fff", cursor:"pointer", fontSize:12, color:"#374151" }}>
                        🔄 Actualiser
                      </button>
                    </div>

                    {coachDispoLoading && (
                      <div style={{ textAlign:"center", padding:40, color:"#9ca3af", fontSize:13 }}>Chargement des disponibilités…</div>
                    )}
                    {!coachDispoLoading && disposAffichees.length === 0 && (
                      <div style={{ textAlign:"center", padding:40, color:"#9ca3af", fontSize:13, background:"#f8fafc", borderRadius:12, border:"1px dashed #e5e7eb" }}>
                        <div style={{ fontSize:32, marginBottom:8 }}>📭</div>
                        <div style={{ fontWeight:600, color:"#374151", marginBottom:4 }}>Aucun créneau renseigné</div>
                        <div style={{ fontSize:12 }}>Le coach n'a pas encore configuré ses disponibilités dans son espace.</div>
                      </div>
                    )}
                    {!coachDispoLoading && disposAffichees.length > 0 && (
                      <>
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:12, marginBottom:16 }}>
                          {disposAffichees.map((d,i) => (
                            <div key={i} style={{ background:"#fff", border:`1.5px solid ${couleur}30`, borderRadius:12, padding:"14px 16px" }}>
                              <div style={{ fontWeight:700, fontSize:13, color:couleur, marginBottom:8 }}>📅 {d.jour}</div>
                              <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                                {d.creneaux.map((c,j) => (
                                  <span key={j} style={{ background:couleur+"12", color:couleur, borderRadius:8, padding:"4px 12px", fontSize:12, fontWeight:600, width:"fit-content" }}>🕐 {c}</span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div style={{ fontSize:11, color:"#9ca3af", textAlign:"right" }}>
                          {coachDispoData.length} créneau{coachDispoData.length!==1?"x":""} sur {JOURS_ORDER.length} jours possibles
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* ════ STATUT ════ */}
                {coachModalTab === "statut" && (
                  <div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:20 }}>
                      <div style={{ background:coach.actif?"#dcfce7":"#fee2e2", borderRadius:14, padding:20, textAlign:"center" }}>
                        <div style={{ fontSize:32 }}>{coach.actif?"✅":"🔴"}</div>
                        <div style={{ fontWeight:700, fontSize:16, color:coach.actif?"#16a34a":"#dc2626", marginTop:6 }}>{coach.actif?"Actif":"Inactif"}</div>
                        <div style={{ fontSize:11, color:"#6b7280", marginTop:2 }}>Statut général</div>
                      </div>
                      <div style={{ background:"#f0f9ff", borderRadius:14, padding:20, textAlign:"center" }}>
                        <div style={{ fontSize:32 }}>📋</div>
                        <div style={{ fontWeight:700, fontSize:16, color:couleur, marginTop:6 }}>{contratsAffiches.filter(c=>c.statut==="actif").length}</div>
                        <div style={{ fontSize:11, color:"#6b7280", marginTop:2 }}>Contrats actifs</div>
                      </div>
                      <div style={{ background:"#f0fdf4", borderRadius:14, padding:20, textAlign:"center" }}>
                        <div style={{ fontSize:32 }}>🏫</div>
                        <div style={{ fontWeight:700, fontSize:16, color:"#16a34a", marginTop:6 }}>{groupesAffiches.length}</div>
                        <div style={{ fontSize:11, color:"#6b7280", marginTop:2 }}>Groupes assignés</div>
                      </div>
                      <div style={{ background:"#fffbeb", borderRadius:14, padding:20, textAlign:"center" }}>
                        <div style={{ fontSize:32 }}>⭐</div>
                        <div style={{ fontWeight:700, fontSize:16, color:"#d97706", marginTop:6 }}>
                          {(() => { const n=evalsAffichees.filter(e=>e.note!=null); return n.length?(n.reduce((s,e)=>s+Number(e.note),0)/n.length).toFixed(1):"-"; })()}
                        </div>
                        <div style={{ fontSize:11, color:"#6b7280", marginTop:2 }}>Note moyenne</div>
                      </div>
                    </div>
                    <div style={{ background:"#f8fafc", borderRadius:12, padding:"14px 16px" }}>
                      <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:10 }}>Informations de compte</div>
                      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                        {[["ID",coach.id],["Créé le",fmtDate(coach.created_at)],["Dernière connexion",fmtDate(coach.last_sign_in_at||coach.derniere_connexion)],["Rôle",coach.profil_type||coach.role||"coach"]].map(([l,v])=>(
                          <div key={l} style={{ display:"flex", justifyContent:"space-between", fontSize:12, padding:"4px 0", borderBottom:"1px solid #e5e7eb" }}>
                            <span style={{ color:"#6b7280" }}>{l}</span>
                            <span style={{ fontWeight:600, color:"#1e293b" }}>{v||"-"}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* ════ INFOS (old - removed) ════ */}
                {false && (
                  <div>
                    <div style={{ background:"#f8fafc", borderRadius:12, padding:18 }}>
                      <h3 style={{ margin:"0 0 14px", fontSize:14, fontWeight:800 }}>Informations personnelles</h3>
                      {[
                        ["Prénom",           coach.prenom || "-"],
                        ["Nom",              coach.nom    || "-"],
                        ["Email",            coach.email  || "-"],
                        ["Téléphone",        coach.telephone || "-"],
                        ["Matricule",        coach.coach_info?.matricule || "-"],
                        ["Filière",          coach.coach_info?.filiere || coach.departement || "-"],
                        ["Lieu habitation",  coach.coach_info?.lieu_habitation || "-"],
                        ["Date début BET",   fmtDate(coach.coach_info?.date_debut_bet || coach.date_creation)],
                        ["Centre",           centre],
                        ["Statut",           coach.actif ? "✅ Actif" : "🔴 Inactif"],
                      ].map(([l,v])=>(
                        <div key={l} style={{ display:"flex", padding:"6px 0", borderBottom:"1px solid #f1f5f9", fontSize:13 }}>
                          <span style={{ color:"#9ca3af", width:140, flexShrink:0, fontWeight:500 }}>{l}</span>
                          <span style={{ fontWeight:600 }}>{v}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                      <div style={{ background:"#f8fafc", borderRadius:12, padding:18 }}>
                        <h3 style={{ margin:"0 0 12px", fontSize:14, fontWeight:800 }}>Certifications</h3>
                        {(() => {
                          const certs = coach.coach_info?.certifications;
                          const list = Array.isArray(certs) ? certs : (typeof certs === "string" ? certs.split(",").map(s=>s.trim()).filter(Boolean) : []);
                          return list.length ? (
                            <div style={{ display:"flex", flexWrap:"wrap", gap:7 }}>
                              {list.map((c,i) => <span key={i} style={{ padding:"4px 12px", borderRadius:99, background:"#ede9fe", color:"#5b21b6", fontSize:12, fontWeight:700 }}>🏆 {c}</span>)}
                            </div>
                          ) : <p style={{ fontSize:13, color:"#9ca3af" }}>Aucune certification</p>;
                        })()}
                      </div>
                      <div style={{ background:"#f8fafc", borderRadius:12, padding:18 }}>
                        <h3 style={{ margin:"0 0 12px", fontSize:14, fontWeight:800 }}>Résumé activité</h3>
                        {[
                          { l:"Contrats actifs",   v:contratsActifs,            c:BET_COLOR },
                          { l:"Total contrats",    v:coachContrats.length,       c:"#6b7280" },
                          { l:"Groupes de cours",  v:coachGroupes.length,        c:"#7c3aed" },
                          { l:"Montant total dû",  v:fmtMoney(totalDu),          c:"#16a34a" },
                          { l:"Montant réalisé",   v:fmtMoney(totalRealise),     c:"#0891b2" },
                        ].map(s => (
                          <div key={s.l} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:"1px solid #f1f5f9", fontSize:13 }}>
                            <span style={{ color:"#6b7280" }}>{s.l}</span>
                            <span style={{ fontWeight:800, color:s.c }}>{s.v}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* ════ CONTRATS ════ */}
                {coachModalTab === "contrats" && (
                  <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div>
                        <h3 style={{ margin:0, fontSize:16, fontWeight:800 }}>Contrats privés</h3>
                        <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>En ligne · Présentiel BET · À domicile</p>
                      </div>
                      <button onClick={()=>{ setShowContratForm(true); setContratEditId(null); setContratForm({ apprenant_nom:"", apprenant_prenom:"", apprenant_email:"", apprenant_telephone:"", type_contrat:"en_ligne", niveau:"B1", objectif:"", prix_h:"", nb_seances_total:"", duree_seance_h:"1.5", date_debut:"", date_fin:"", note:"" }); }}
                        style={{ padding:"9px 18px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:13 }}>
                        ➕ Nouveau contrat
                      </button>
                    </div>

                    {/* Formulaire nouveau/edit contrat */}
                    {showContratForm && (
                      <div style={{ background:"#f0f9ff", border:"1.5px solid #bae6fd", borderRadius:12, padding:18 }}>
                        <h4 style={{ margin:"0 0 14px", fontSize:14, fontWeight:800, color:BET_COLOR }}>{contratEditId ? "✏️ Modifier le contrat" : "➕ Nouveau contrat"}</h4>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                          <div><label style={{ fontSize:11, fontWeight:600, display:"block", marginBottom:3 }}>Nom apprenant *</label>
                            <input value={contratForm.apprenant_nom} onChange={e=>setContratForm(p=>({...p,apprenant_nom:e.target.value}))} style={{ width:"100%", padding:"8px 10px", border:"1.5px solid #bae6fd", borderRadius:7, fontSize:13, boxSizing:"border-box" }} /></div>
                          <div><label style={{ fontSize:11, fontWeight:600, display:"block", marginBottom:3 }}>Prénom apprenant</label>
                            <input value={contratForm.apprenant_prenom} onChange={e=>setContratForm(p=>({...p,apprenant_prenom:e.target.value}))} style={{ width:"100%", padding:"8px 10px", border:"1.5px solid #bae6fd", borderRadius:7, fontSize:13, boxSizing:"border-box" }} /></div>
                          <div><label style={{ fontSize:11, fontWeight:600, display:"block", marginBottom:3 }}>Email apprenant</label>
                            <input value={contratForm.apprenant_email} onChange={e=>setContratForm(p=>({...p,apprenant_email:e.target.value}))} style={{ width:"100%", padding:"8px 10px", border:"1.5px solid #bae6fd", borderRadius:7, fontSize:13, boxSizing:"border-box" }} /></div>
                          <div><label style={{ fontSize:11, fontWeight:600, display:"block", marginBottom:3 }}>Téléphone apprenant</label>
                            <input value={contratForm.apprenant_telephone} onChange={e=>setContratForm(p=>({...p,apprenant_telephone:e.target.value}))} style={{ width:"100%", padding:"8px 10px", border:"1.5px solid #bae6fd", borderRadius:7, fontSize:13, boxSizing:"border-box" }} /></div>
                          <div><label style={{ fontSize:11, fontWeight:600, display:"block", marginBottom:3 }}>Type de contrat</label>
                            <select value={contratForm.type_contrat} onChange={e=>setContratForm(p=>({...p,type_contrat:e.target.value}))} style={{ width:"100%", padding:"8px 10px", border:"1.5px solid #bae6fd", borderRadius:7, fontSize:13, background:"#fff", boxSizing:"border-box" }}>
                              <option value="en_ligne">💻 En ligne</option>
                              <option value="presentiel_bet">🏢 Présentiel BET</option>
                              <option value="domicile">🏠 À domicile</option>
                            </select></div>
                          <div><label style={{ fontSize:11, fontWeight:600, display:"block", marginBottom:3 }}>Niveau</label>
                            <select value={contratForm.niveau} onChange={e=>setContratForm(p=>({...p,niveau:e.target.value}))} style={{ width:"100%", padding:"8px 10px", border:"1.5px solid #bae6fd", borderRadius:7, fontSize:13, background:"#fff", boxSizing:"border-box" }}>
                              {["A1","A2","B1","B2","C1","C2"].map(n=><option key={n} value={n}>{n}</option>)}
                            </select></div>
                          <div><label style={{ fontSize:11, fontWeight:600, display:"block", marginBottom:3 }}>Prix / heure (F CFA) *</label>
                            <input type="number" min="0" value={contratForm.prix_h} onChange={e=>setContratForm(p=>({...p,prix_h:e.target.value}))} style={{ width:"100%", padding:"8px 10px", border:"1.5px solid #bae6fd", borderRadius:7, fontSize:13, boxSizing:"border-box" }} /></div>
                          <div><label style={{ fontSize:11, fontWeight:600, display:"block", marginBottom:3 }}>Nb séances total</label>
                            <input type="number" min="0" value={contratForm.nb_seances_total} onChange={e=>setContratForm(p=>({...p,nb_seances_total:e.target.value}))} style={{ width:"100%", padding:"8px 10px", border:"1.5px solid #bae6fd", borderRadius:7, fontSize:13, boxSizing:"border-box" }} /></div>
                          <div><label style={{ fontSize:11, fontWeight:600, display:"block", marginBottom:3 }}>Durée / séance (h)</label>
                            <select value={contratForm.duree_seance_h} onChange={e=>setContratForm(p=>({...p,duree_seance_h:e.target.value}))} style={{ width:"100%", padding:"8px 10px", border:"1.5px solid #bae6fd", borderRadius:7, fontSize:13, background:"#fff", boxSizing:"border-box" }}>
                              {["0.5","1","1.5","2","2.5","3"].map(v=><option key={v} value={v}>{v}h</option>)}
                            </select></div>
                          <div><label style={{ fontSize:11, fontWeight:600, display:"block", marginBottom:3 }}>Objectif</label>
                            <input value={contratForm.objectif} onChange={e=>setContratForm(p=>({...p,objectif:e.target.value}))} placeholder="TOEIC, Général…" style={{ width:"100%", padding:"8px 10px", border:"1.5px solid #bae6fd", borderRadius:7, fontSize:13, boxSizing:"border-box" }} /></div>
                          <div><label style={{ fontSize:11, fontWeight:600, display:"block", marginBottom:3 }}>Date début</label>
                            <input type="date" value={contratForm.date_debut} onChange={e=>setContratForm(p=>({...p,date_debut:e.target.value}))} style={{ width:"100%", padding:"8px 10px", border:"1.5px solid #bae6fd", borderRadius:7, fontSize:13, boxSizing:"border-box" }} /></div>
                          <div><label style={{ fontSize:11, fontWeight:600, display:"block", marginBottom:3 }}>Date fin</label>
                            <input type="date" value={contratForm.date_fin} onChange={e=>setContratForm(p=>({...p,date_fin:e.target.value}))} style={{ width:"100%", padding:"8px 10px", border:"1.5px solid #bae6fd", borderRadius:7, fontSize:13, boxSizing:"border-box" }} /></div>
                        </div>
                        {contratForm.prix_h && contratForm.nb_seances_total && (
                          <div style={{ marginTop:10, padding:"8px 14px", background:"#dcfce7", borderRadius:8, fontSize:13, fontWeight:700, color:"#15803d" }}>
                            💰 Montant total : {fmtMoney(parseFloat(contratForm.prix_h) * parseFloat(contratForm.duree_seance_h||1.5) * parseInt(contratForm.nb_seances_total))}
                          </div>
                        )}
                        <div style={{ marginTop:10 }}><label style={{ fontSize:11, fontWeight:600, display:"block", marginBottom:3 }}>Note interne</label>
                          <textarea value={contratForm.note} onChange={e=>setContratForm(p=>({...p,note:e.target.value}))} rows={2} style={{ width:"100%", padding:"8px 10px", border:"1.5px solid #bae6fd", borderRadius:7, fontSize:13, resize:"vertical", boxSizing:"border-box" }} /></div>
                        <div style={{ display:"flex", gap:8, marginTop:12 }}>
                          <button onClick={saveContrat} disabled={contratSaving} style={{ padding:"9px 20px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:7, fontWeight:700, fontSize:13, cursor:"pointer", opacity:contratSaving?.6:1 }}>{contratSaving?"⏳ Sauvegarde…":"✅ Enregistrer"}</button>
                          <button onClick={()=>setShowContratForm(false)} style={{ padding:"9px 16px", background:"#f3f4f6", color:"#374151", border:"none", borderRadius:7, fontSize:13, cursor:"pointer" }}>Annuler</button>
                        </div>
                      </div>
                    )}

                    {coachContratsLoading ? (
                      <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af" }}>Chargement…</div>
                    ) : coachContrats.length === 0 ? (
                      <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af" }}>
                        <div style={{ fontSize:36, marginBottom:8 }}>📋</div>
                        <div style={{ fontWeight:600 }}>Aucun contrat privé</div>
                        <div style={{ fontSize:12, marginTop:4 }}>Cliquez sur "Nouveau contrat" pour en créer un.</div>
                      </div>
                    ) : (
                      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                        {coachContrats.map(c => {
                          const type = CONTRAT_TYPE[c.type_contrat] || CONTRAT_TYPE.en_ligne;
                          const stCfg = CONTRAT_STATUT[c.statut] || CONTRAT_STATUT.en_attente;
                          const montant = (c.prix_h||0) * (c.duree_seance_h||1.5) * (c.nb_seances_total||0);
                          return (
                            <div key={c.id} style={{ background:"#fff", border:"1.5px solid #e5e7eb", borderRadius:12, overflow:"hidden" }}>
                              <div style={{ height:4, background:type.color }} />
                              <div style={{ padding:"14px 16px" }}>
                                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                                  <div>
                                    <div style={{ fontSize:15, fontWeight:800, color:"#0f172a" }}>{c.apprenant_prenom || ""} {c.apprenant_nom}</div>
                                    <div style={{ display:"flex", gap:8, marginTop:4 }}>
                                      <span style={{ background:type.bg, color:type.color, borderRadius:99, padding:"2px 10px", fontSize:11, fontWeight:700 }}>{type.icon} {type.label}</span>
                                      {c.niveau && <span style={{ background:"#f3f4f6", color:"#374151", borderRadius:99, padding:"2px 8px", fontSize:11, fontWeight:700 }}>Niv. {c.niveau}</span>}
                                      {c.objectif && <span style={{ fontSize:11, color:"#6b7280" }}>🎯 {c.objectif}</span>}
                                    </div>
                                  </div>
                                  <span style={{ background:stCfg.bg, color:stCfg.color, borderRadius:99, padding:"3px 12px", fontSize:11, fontWeight:800 }}>{stCfg.label}</span>
                                </div>
                                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, fontSize:12, color:"#6b7280", marginBottom:10 }}>
                                  <div><div style={{ fontSize:10, color:"#9ca3af", marginBottom:2 }}>Prix/heure</div><div style={{ fontWeight:700, color:"#0f172a" }}>{fmtMoney(c.prix_h)}</div></div>
                                  <div><div style={{ fontSize:10, color:"#9ca3af", marginBottom:2 }}>Séances</div><div style={{ fontWeight:700, color:"#0f172a" }}>{c.nb_seances_realisees||0}/{c.nb_seances_total||0}</div></div>
                                  <div><div style={{ fontSize:10, color:"#9ca3af", marginBottom:2 }}>Durée/séance</div><div style={{ fontWeight:700, color:"#0f172a" }}>{c.duree_seance_h||1.5}h</div></div>
                                  <div><div style={{ fontSize:10, color:"#9ca3af", marginBottom:2 }}>Montant total</div><div style={{ fontWeight:700, color:"#16a34a" }}>{fmtMoney(montant)}</div></div>
                                </div>
                                {(c.date_debut || c.date_fin) && (
                                  <div style={{ fontSize:12, color:"#6b7280", marginBottom:10 }}>
                                    📅 {fmtDate(c.date_debut)} → {fmtDate(c.date_fin)}
                                  </div>
                                )}
                                {/* Paiement */}
                                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:c.paiement_confirme?"#dcfce7":"#fef3c7", borderRadius:8, marginBottom:10 }}>
                                  <span style={{ fontSize:12, fontWeight:700, color:c.paiement_confirme?"#15803d":"#92400e" }}>
                                    {c.paiement_confirme ? "✅ Paiement confirmé" : "⏳ Paiement en attente"}
                                    {c.paiement_date ? ` - ${fmtDate(c.paiement_date)}` : ""}
                                  </span>
                                  {!c.paiement_confirme && (
                                    <button onClick={()=>patchContrat(c.id,{ paiement_confirme:true, paiement_date:new Date().toISOString().slice(0,10), paiement_montant:montant })}
                                      style={{ padding:"5px 12px", background:"#16a34a", color:"#fff", border:"none", borderRadius:6, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                                      ✅ Confirmer paiement
                                    </button>
                                  )}
                                </div>
                                {/* Actions statut */}
                                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                                  {c.statut === "en_attente" && c.paiement_confirme && (
                                    <button onClick={()=>patchContrat(c.id,{statut:"actif"})} style={{ padding:"5px 12px", background:"#dcfce7", color:"#15803d", border:"1px solid #86efac", borderRadius:6, fontSize:11, fontWeight:700, cursor:"pointer" }}>▶ Activer</button>
                                  )}
                                  {c.statut === "actif" && (
                                    <button onClick={()=>patchContrat(c.id,{statut:"suspendu"})} style={{ padding:"5px 12px", background:"#f3f4f6", color:"#374151", border:"1px solid #d1d5db", borderRadius:6, fontSize:11, fontWeight:700, cursor:"pointer" }}>⏸ Suspendre</button>
                                  )}
                                  {c.statut === "suspendu" && (
                                    <button onClick={()=>patchContrat(c.id,{statut:"actif"})} style={{ padding:"5px 12px", background:"#dcfce7", color:"#15803d", border:"1px solid #86efac", borderRadius:6, fontSize:11, fontWeight:700, cursor:"pointer" }}>▶ Reprendre</button>
                                  )}
                                  {["actif","suspendu"].includes(c.statut) && (
                                    <button onClick={()=>patchContrat(c.id,{statut:"termine"})} style={{ padding:"5px 12px", background:"#f1f5f9", color:"#475569", border:"1px solid #cbd5e1", borderRadius:6, fontSize:11, fontWeight:700, cursor:"pointer" }}>⏹ Terminer</button>
                                  )}
                                  <button onClick={()=>{ setContratEditId(c.id); setContratForm({ apprenant_nom:c.apprenant_nom||"", apprenant_prenom:c.apprenant_prenom||"", apprenant_email:c.apprenant_email||"", apprenant_telephone:c.apprenant_telephone||"", type_contrat:c.type_contrat||"en_ligne", niveau:c.niveau||"B1", objectif:c.objectif||"", prix_h:c.prix_h||"", nb_seances_total:c.nb_seances_total||"", duree_seance_h:c.duree_seance_h||"1.5", date_debut:c.date_debut||"", date_fin:c.date_fin||"", note:c.note||"" }); setShowContratForm(true); }}
                                    style={{ padding:"5px 12px", background:"#eff6ff", color:"#2563eb", border:"1px solid #bfdbfe", borderRadius:6, fontSize:11, fontWeight:700, cursor:"pointer" }}>✏️ Modifier</button>
                                  <button onClick={()=>deleteContrat(c.id)} style={{ padding:"5px 12px", background:"#fee2e2", color:"#dc2626", border:"1px solid #fca5a5", borderRadius:6, fontSize:11, fontWeight:700, cursor:"pointer" }}>🗑 Supprimer</button>
                                  <button onClick={()=>{ const seances = parseInt(window.prompt("Nb séances réalisées :", c.nb_seances_realisees||0)); if (!isNaN(seances)) patchContrat(c.id,{nb_seances_realisees:seances}); }}
                                    style={{ padding:"5px 12px", background:"#fef3c7", color:"#92400e", border:"1px solid #fcd34d", borderRadius:6, fontSize:11, fontWeight:700, cursor:"pointer" }}>📝 Séances réalisées</button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* ════ HONORAIRES ════ */}
                {coachModalTab === "honoraires" && (
                  <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                    <h3 style={{ margin:0, fontSize:16, fontWeight:800 }}>Honoraires - {coach.prenom} {coach.nom}</h3>
                    {/* KPIs */}
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
                      {[
                        { l:"Montant total dû",       v:fmtMoney(totalDu),       c:"#16a34a", bg:"#dcfce7" },
                        { l:"Montant réalisé",        v:fmtMoney(totalRealise),  c:"#0891b2", bg:"#e0f2fe" },
                        { l:"Reste à réaliser",       v:fmtMoney(totalDu - totalRealise), c:"#d97706", bg:"#fef3c7" },
                      ].map(s => (
                        <div key={s.l} style={{ background:s.bg, borderRadius:12, padding:"14px 16px" }}>
                          <div style={{ fontSize:11, color:s.c, fontWeight:600, marginBottom:4 }}>{s.l}</div>
                          <div style={{ fontSize:22, fontWeight:900, color:s.c }}>{s.v}</div>
                        </div>
                      ))}
                    </div>
                    {honoraires.length === 0 ? (
                      <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af" }}>Aucun contrat - pas d'honoraires calculés.</div>
                    ) : (
                      <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, overflow:"hidden" }}>
                        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                          <thead>
                            <tr style={{ background:"#f8fafc" }}>
                              {["Apprenant","Type","Prix/h","Durée/séance","Séances","Montant total","Réalisé","Statut"].map(h=>(
                                <th key={h} style={{ padding:"10px 12px", textAlign:"left", fontSize:11, fontWeight:700, color:"#6b7280", borderBottom:"1px solid #e5e7eb" }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {honoraires.map(h => {
                              const type = CONTRAT_TYPE[h.type_contrat] || CONTRAT_TYPE.en_ligne;
                              const stCfg = CONTRAT_STATUT[h.statut] || CONTRAT_STATUT.en_attente;
                              return (
                                <tr key={h.id} style={{ borderBottom:"1px solid #f1f5f9" }}>
                                  <td style={{ padding:"10px 12px", fontWeight:600 }}>{h.apprenant_prenom||""} {h.apprenant_nom}</td>
                                  <td style={{ padding:"10px 12px" }}><span style={{ background:type.bg, color:type.color, borderRadius:99, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{type.icon} {type.label}</span></td>
                                  <td style={{ padding:"10px 12px", fontWeight:700 }}>{fmtMoney(h.prix_h)}</td>
                                  <td style={{ padding:"10px 12px" }}>{h.duree_seance_h||1.5}h</td>
                                  <td style={{ padding:"10px 12px" }}>{h.nb_seances_realisees||0}/{h.nb_seances_total||0}</td>
                                  <td style={{ padding:"10px 12px", fontWeight:800, color:"#16a34a" }}>{fmtMoney(h.montant_total)}</td>
                                  <td style={{ padding:"10px 12px", fontWeight:700, color:"#0891b2" }}>{fmtMoney(h.montant_realise)}</td>
                                  <td style={{ padding:"10px 12px" }}><span style={{ background:stCfg.bg, color:stCfg.color, borderRadius:99, padding:"2px 8px", fontSize:10, fontWeight:700 }}>{stCfg.label}</span></td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr style={{ background:"#f0fdf4" }}>
                              <td colSpan={5} style={{ padding:"10px 12px", fontWeight:800, fontSize:13 }}>Total</td>
                              <td style={{ padding:"10px 12px", fontWeight:900, color:"#16a34a", fontSize:14 }}>{fmtMoney(totalDu)}</td>
                              <td style={{ padding:"10px 12px", fontWeight:900, color:"#0891b2", fontSize:14 }}>{fmtMoney(totalRealise)}</td>
                              <td />
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* ════ GROUPES (old - hidden) ════ */}
                {false && (
                  <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <h3 style={{ margin:0, fontSize:16, fontWeight:800 }}>👥 Groupes de cours - Vue d'ensemble</h3>
                      <span style={{ fontSize:12, color:"#9ca3af" }}>{coachGroupes.length} groupe{coachGroupes.length!==1?"s":""}</span>
                    </div>
                    {coachGroupesLoading ? (
                      <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af" }}>Chargement…</div>
                    ) : coachGroupes.length === 0 ? (
                      <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af" }}>
                        <div style={{ fontSize:36, marginBottom:8 }}>👥</div>
                        <div style={{ fontWeight:600 }}>Aucun groupe assigné</div>
                      </div>
                    ) : (
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:14 }}>
                        {coachGroupes.map(g => {
                          const statutCfg = g.statut==="actif"
                            ? { label:"Actif",    color:"#16a34a", bg:"#dcfce7" }
                            : g.statut==="terminé"
                            ? { label:"Terminé",  color:"#6b7280", bg:"#f3f4f6" }
                            : { label:"Suspendu", color:"#f59e0b", bg:"#fef3c7" };
                          return (
                            <div key={g.id}
                              style={{ background:"#fff", borderRadius:14, boxShadow:"0 2px 8px rgba(0,0,0,.06)", border:"1px solid #f1f5f9", overflow:"hidden", transition:"box-shadow .15s" }}
                              onMouseOver={e=>e.currentTarget.style.boxShadow="0 4px 16px rgba(14,165,233,.12)"}
                              onMouseOut={e=>e.currentTarget.style.boxShadow="0 2px 8px rgba(0,0,0,.06)"}>
                              <div style={{ height:5, background:BET_COLOR }} />
                              <div style={{ padding:"16px 18px" }}>
                                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                                  <div>
                                    <div style={{ fontSize:14, fontWeight:800, color:"#0f172a" }}>{g.nom || "-"}</div>
                                    {g.niveau && <div style={{ fontSize:11, color:BET_COLOR, fontWeight:700, marginTop:2 }}>Niveau {g.niveau}</div>}
                                  </div>
                                  <span style={{ display:"inline-block", padding:"3px 10px", borderRadius:99, fontSize:10, fontWeight:700, color:statutCfg.color, background:statutCfg.bg }}>
                                    {statutCfg.label}
                                  </span>
                                </div>
                                <div style={{ display:"flex", flexDirection:"column", gap:5, fontSize:12, color:"#64748b" }}>
                                  {g.filiere && <span>📂 {g.filiere}</span>}
                                  {g.type_cours && <span>{libelleTypeCours(g)}</span>}
                                  {g.nb_apprenants !== undefined && g.nb_apprenants !== null && (
                                    <span>👤 {g.nb_apprenants} apprenant{g.nb_apprenants!==1?"s":""}</span>
                                  )}
                                  {g.date_debut && <span>📅 Début : {fmtDate(g.date_debut)}</span>}
                                </div>
                                {g.horaires && (
                                  <div style={{ marginTop:10, display:"flex", gap:4, flexWrap:"wrap" }}>
                                    {(Array.isArray(g.horaires) ? g.horaires : [g.horaires]).map((h,i) => (
                                      <span key={i} style={{ background:"#e0f2fe", color:"#0369a1", borderRadius:6, padding:"2px 8px", fontSize:10, fontWeight:700 }}>{h}</span>
                                    ))}
                                  </div>
                                )}
                                <button onClick={()=>openCoachGroupeModal(g)}
                                  style={{ marginTop:14, width:"100%", padding:"8px 0", background:"transparent", border:`1.5px solid ${BET_COLOR}`, borderRadius:8, color:BET_COLOR, fontWeight:700, fontSize:12, cursor:"pointer" }}>
                                  Voir le détail →
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* ════ ÉVALUATIONS ════ */}
                {coachModalTab === "evaluations" && (() => {
                  const CRIT = {
                    ponctualite:             { label:"Ponctualité",            icon:"⏰" },
                    qualite_pedagogique:     { label:"Qualité pédagogique",    icon:"📚" },
                    satisfaction_apprenants: { label:"Satisfaction apprenants",icon:"😊" },
                    communication:           { label:"Communication",          icon:"💬" },
                  };
                  const nc  = n => !n ? "#9ca3af" : n >= 4.5 ? "#16a34a" : n >= 3.5 ? "#0891b2" : n >= 2.5 ? "#f59e0b" : "#ef4444";
                  const nbg = n => !n ? "#f3f4f6" : n >= 4.5 ? "#dcfce7" : n >= 3.5 ? "#e0f2fe" : n >= 2.5 ? "#fef3c7" : "#fee2e2";
                  const fmtD = d => d ? new Date(d).toLocaleDateString("fr-FR",{day:"numeric",month:"short",year:"numeric"}) : "-";
                  const toggle = id => setCoachEvalExpanded(prev => {
                    const next = new Set(prev);
                    next.has(id) ? next.delete(id) : next.add(id);
                    return next;
                  });
                  const moyenne = coachEvals.length > 0
                    ? (coachEvals.reduce((s,e) => s + (e.note_finale || 0), 0) / coachEvals.length)
                    : null;

                  return (
                    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

                      {/* KPI banner */}
                      {moyenne !== null && (
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
                          {[
                            { l:"Note moyenne",   v: noteSur10(moyenne)+"/10", c: nc(moyenne), bg: nbg(moyenne) },
                            { l:"Évaluations",    v: coachEvals.length,               c:"#7c3aed",    bg:"#ede9fe" },
                            { l:"Dernière éval.", v: fmtD(coachEvals[0]?.date_evaluation), c:"#0891b2", bg:"#e0f2fe" },
                          ].map(s => (
                            <div key={s.l} style={{ background:s.bg, borderRadius:12, padding:"14px 16px" }}>
                              <div style={{ fontSize:11, color:s.c, fontWeight:600, marginBottom:4 }}>{s.l}</div>
                              <div style={{ fontSize:22, fontWeight:900, color:s.c }}>{s.v}</div>
                            </div>
                          ))}
                        </div>
                      )}

                      {coachEvalsLoading ? (
                        <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af" }}>Chargement des évaluations…</div>
                      ) : coachEvals.length === 0 ? (
                        <div style={{ textAlign:"center", padding:"48px 0", color:"#9ca3af" }}>
                          <div style={{ fontSize:40, marginBottom:10 }}>⭐</div>
                          <div style={{ fontWeight:700, fontSize:14 }}>Aucune évaluation</div>
                          <div style={{ fontSize:12, marginTop:4 }}>Ce coach n'a pas encore été évalué.</div>
                        </div>
                      ) : (
                        <div style={{ border:"1.5px solid #e2e8f0", borderRadius:14, overflow:"hidden" }}>
                          {/* Contrôles tout déplier/replier */}
                          <div style={{ padding:"10px 16px", background:"#f8fafc", borderBottom:"1px solid #e2e8f0", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                            <span style={{ fontSize:12, fontWeight:700, color:"#64748b" }}>
                              {coachEvals.length} évaluation{coachEvals.length > 1 ? "s" : ""}
                            </span>
                            <div style={{ display:"flex", gap:8 }}>
                              <button onClick={() => setCoachEvalExpanded(new Set(coachEvals.map(e => e.id)))}
                                style={{ fontSize:11, fontWeight:700, color:BET_COLOR, background:"transparent", border:"none", cursor:"pointer", padding:"2px 6px" }}>
                                Tout déplier ↓
                              </button>
                              <button onClick={() => setCoachEvalExpanded(new Set())}
                                style={{ fontSize:11, fontWeight:700, color:"#94a3b8", background:"transparent", border:"none", cursor:"pointer", padding:"2px 6px" }}>
                                Tout replier ↑
                              </button>
                            </div>
                          </div>

                          {coachEvals.map((ev, idx) => {
                            const open = coachEvalExpanded.has(ev.id);
                            const hasTxt = ev.rapport || ev.points_forts || ev.axes_amelioration;
                            return (
                              <div key={ev.id} style={{ borderBottom: idx < coachEvals.length - 1 ? "1px solid #f1f5f9" : "none" }}>

                                {/* En-tête cliquable */}
                                <div onClick={() => toggle(ev.id)}
                                  style={{ padding:"12px 16px", cursor:"pointer", display:"flex", alignItems:"center", gap:12,
                                    background: open ? "#f0f9ff" : "#fff", transition:"background .15s" }}
                                  onMouseEnter={e => { if (!open) e.currentTarget.style.background = "#f8fafc"; }}
                                  onMouseLeave={e => { if (!open) e.currentTarget.style.background = "#fff"; }}>

                                  <span style={{ fontSize:12, color:"#94a3b8", flexShrink:0, transition:"transform .2s",
                                    display:"inline-block", transform: open ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>

                                  <div style={{ flex:1, minWidth:0 }}>
                                    <div style={{ fontSize:13, fontWeight:800, color:"#0f172a" }}>
                                      {ev.periode || fmtD(ev.date_evaluation)}
                                    </div>
                                    <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>
                                      {ev.evaluateur_nom || "Superviseur"} · {fmtD(ev.date_evaluation)}
                                      {hasTxt && <span style={{ marginLeft:6, color:BET_COLOR }}>📝</span>}
                                    </div>
                                  </div>

                                  {/* Mini-barres critères */}
                                  <div style={{ display:"flex", gap:3, alignItems:"flex-end", height:20, flexShrink:0 }}>
                                    {Object.keys(CRIT).map(k => {
                                      const v = ev[k];
                                      return (
                                        <div key={k} title={CRIT[k].label}
                                          style={{ width:6, borderRadius:2, background: v ? nc(v) : "#e5e7eb", height: v ? `${v / 5 * 100}%` : "20%" }} />
                                      );
                                    })}
                                  </div>

                                  <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:999,
                                    background: ev.statut === "validée" ? "#dcfce7" : "#fef3c7",
                                    color:      ev.statut === "validée" ? "#16a34a"  : "#f59e0b", flexShrink:0 }}>
                                    {ev.statut === "validée" ? "✅" : "🕐"}
                                  </span>

                                  {ev.note_finale && (
                                    <div style={{ width:40, height:40, borderRadius:9, background:nbg(ev.note_finale),
                                      display:"flex", alignItems:"center", justifyContent:"center",
                                      fontWeight:900, fontSize:15, color:nc(ev.note_finale), flexShrink:0 }}>
                                      {Number(ev.note_finale).toFixed(1)}
                                    </div>
                                  )}
                                </div>

                                {/* Contenu déplié */}
                                {open && (
                                  <div style={{ padding:"16px 20px", background:"#f0f9ff", borderTop:"1px solid #bae6fd" }}>

                                    {/* Grille critères */}
                                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))", gap:8, marginBottom: hasTxt ? 14 : 0 }}>
                                      {Object.entries(CRIT).map(([key, meta]) => {
                                        const val = ev[key];
                                        return (
                                          <div key={key} style={{ background:"#fff", borderRadius:9, padding:"10px 12px", border:"1px solid #e2e8f0" }}>
                                            <div style={{ fontSize:11, color:"#64748b", fontWeight:600, marginBottom:6 }}>{meta.icon} {meta.label}</div>
                                            <div style={{ display:"flex", gap:3 }}>
                                              {[1,2,3,4,5].map(n => (
                                                <div key={n} style={{ width:18, height:18, borderRadius:3, fontSize:9, fontWeight:700,
                                                  display:"flex", alignItems:"center", justifyContent:"center",
                                                  background: val >= n ? nc(val) : "#e5e7eb",
                                                  color:      val >= n ? "#fff"  : "#9ca3af" }}>{n}</div>
                                              ))}
                                              {val && <span style={{ marginLeft:4, fontSize:11, fontWeight:800, color:nc(val), alignSelf:"center" }}>{val}/5</span>}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>

                                    {/* Textes */}
                                    {hasTxt && (
                                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                                        {ev.rapport && (
                                          <div style={{ gridColumn:"1/-1", background:"#f0f9ff", borderRadius:9, padding:"10px 13px", border:"1px solid #bae6fd" }}>
                                            <div style={{ fontSize:11, fontWeight:800, color:"#0369a1", marginBottom:5 }}>📝 Rapport d'observation</div>
                                            <p style={{ margin:0, fontSize:12, color:"#0c4a6e", lineHeight:1.6 }}>{ev.rapport}</p>
                                          </div>
                                        )}
                                        {ev.points_forts && (
                                          <div style={{ background:"#f0fdf4", borderRadius:9, padding:"10px 13px", border:"1px solid #bbf7d0" }}>
                                            <div style={{ fontSize:11, fontWeight:800, color:"#16a34a", marginBottom:5 }}>✅ Points forts</div>
                                            <p style={{ margin:0, fontSize:12, color:"#14532d", lineHeight:1.6 }}>{ev.points_forts}</p>
                                          </div>
                                        )}
                                        {ev.axes_amelioration && (
                                          <div style={{ background:"#fff7ed", borderRadius:9, padding:"10px 13px", border:"1px solid #fed7aa" }}>
                                            <div style={{ fontSize:11, fontWeight:800, color:"#ea580c", marginBottom:5 }}>🎯 Axes d'amélioration</div>
                                            <p style={{ margin:0, fontSize:12, color:"#7c2d12", lineHeight:1.6 }}>{ev.axes_amelioration}</p>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ════ RENOUVELLEMENT ════ */}
                {coachModalTab === "renouvellement" && (
                  <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                    <div>
                      <h3 style={{ margin:0, fontSize:16, fontWeight:800 }}>Gestion des renouvellements</h3>
                      <p style={{ margin:"4px 0 0", fontSize:12, color:"#9ca3af" }}>Contrats arrivant à échéance dans les 60 jours ou en cours de renouvellement</p>
                    </div>
                    {contratsARenouveler.length === 0 ? (
                      <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af" }}>
                        <div style={{ fontSize:36, marginBottom:8 }}>✅</div>
                        <div style={{ fontWeight:600 }}>Aucun contrat à renouveler</div>
                        <div style={{ fontSize:12, marginTop:4 }}>Tous les contrats actifs ont plus de 60 jours restants.</div>
                      </div>
                    ) : contratsARenouveler.map(c => {
                      const fin = new Date(c.date_fin);
                      const joursRestants = Math.ceil((fin - today) / 86400000);
                      const rCfg = c.renouvellement_statut ? (RENOUV_STATUT[c.renouvellement_statut] || RENOUV_STATUT.en_attente) : null;
                      const urgence = joursRestants <= 0 ? "expired" : joursRestants <= 14 ? "critical" : joursRestants <= 30 ? "warning" : "ok";
                      const urgCfg = { expired:{bg:"#fee2e2",c:"#dc2626",label:"Expiré"}, critical:{bg:"#fee2e2",c:"#dc2626",label:`${joursRestants}j restants`}, warning:{bg:"#fef3c7",c:"#d97706",label:`${joursRestants}j restants`}, ok:{bg:"#dcfce7",c:"#16a34a",label:`${joursRestants}j restants`} }[urgence];
                      return (
                        <div key={c.id} style={{ background:"#fff", border:`1.5px solid ${urgence==="expired"||urgence==="critical"?"#fca5a5":"#e5e7eb"}`, borderRadius:12, padding:"16px 18px" }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                            <div>
                              <div style={{ fontSize:15, fontWeight:800 }}>{c.apprenant_prenom||""} {c.apprenant_nom}</div>
                              <div style={{ fontSize:12, color:"#6b7280", marginTop:3 }}>
                                📅 {fmtDate(c.date_debut)} → {fmtDate(c.date_fin)} · {CONTRAT_TYPE[c.type_contrat]?.label||c.type_contrat}
                              </div>
                            </div>
                            <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                              <span style={{ background:urgCfg.bg, color:urgCfg.c, borderRadius:99, padding:"3px 12px", fontSize:11, fontWeight:800 }}>{urgCfg.label}</span>
                              {rCfg && <span style={{ background:rCfg.bg, color:rCfg.color, borderRadius:99, padding:"3px 12px", fontSize:11, fontWeight:800 }}>🔄 {rCfg.label}</span>}
                            </div>
                          </div>
                          {/* Workflow renouvellement */}
                          <div style={{ background:"#f8fafc", borderRadius:8, padding:"12px 14px", marginBottom:10 }}>
                            <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:8 }}>Décision de renouvellement</div>
                            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                              {!c.renouvellement_statut && (
                                <button onClick={()=>patchContrat(c.id,{ renouvellement_statut:"en_attente", renouvellement_demande_date:new Date().toISOString().slice(0,10) })}
                                  style={{ padding:"6px 14px", background:"#fef3c7", color:"#92400e", border:"1px solid #fcd34d", borderRadius:7, fontSize:12, fontWeight:700, cursor:"pointer" }}>
                                  ⏳ Demander renouvellement
                                </button>
                              )}
                              {c.renouvellement_statut === "en_attente" && (<>
                                <button onClick={()=>patchContrat(c.id,{ renouvellement_statut:"confirme", renouvellement_decision_date:new Date().toISOString().slice(0,10), statut:"renouvele" })}
                                  style={{ padding:"6px 14px", background:"#dcfce7", color:"#15803d", border:"1px solid #86efac", borderRadius:7, fontSize:12, fontWeight:700, cursor:"pointer" }}>
                                  ✅ Confirmer - Renouveler
                                </button>
                                <button onClick={()=>patchContrat(c.id,{ renouvellement_statut:"refuse", renouvellement_decision_date:new Date().toISOString().slice(0,10), statut:"non_renouvele" })}
                                  style={{ padding:"6px 14px", background:"#fee2e2", color:"#dc2626", border:"1px solid #fca5a5", borderRadius:7, fontSize:12, fontWeight:700, cursor:"pointer" }}>
                                  ❌ Refuser - Arrêter le contrat
                                </button>
                              </>)}
                              {c.renouvellement_statut === "confirme" && (
                                <div style={{ fontSize:12, color:"#15803d", fontWeight:700 }}>✅ Contrat renouvelé - cours en cours · Décidé le {fmtDate(c.renouvellement_decision_date)}</div>
                              )}
                              {c.renouvellement_statut === "refuse" && (
                                <div style={{ fontSize:12, color:"#dc2626", fontWeight:700 }}>❌ Contrat non renouvelé - cours arrêtés · Décidé le {fmtDate(c.renouvellement_decision_date)}</div>
                              )}
                            </div>
                          </div>
                          {c.renouvellement_note !== undefined && (
                            <div style={{ marginTop:6 }}>
                              <label style={{ fontSize:11, fontWeight:600, color:"#374151", display:"block", marginBottom:3 }}>Note renouvellement</label>
                              <textarea defaultValue={c.renouvellement_note||""}
                                onBlur={e=>patchContrat(c.id,{renouvellement_note:e.target.value})}
                                rows={2} placeholder="Motif, conditions…"
                                style={{ width:"100%", padding:"7px 10px", border:"1.5px solid #e5e7eb", borderRadius:7, fontSize:12, resize:"vertical", boxSizing:"border-box" }} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

              </div>{/* fin contenu */}
              </div>{/* fin body (sidebar + contenu) */}
          </ModaleAnimee>
          );
        })()}

        {/* ── MODALE DÉTAIL GROUPE (depuis modal coach) ── */}
        {coachGroupeModal && (() => {
          const g = coachGroupeModal;
          const statutCfg = g.statut==="actif"
            ? { label:"Actif",    color:"#16a34a", bg:"#dcfce7" }
            : g.statut==="terminé"
            ? { label:"Terminé",  color:"#6b7280", bg:"#f3f4f6" }
            : { label:"Suspendu", color:"#f59e0b", bg:"#fef3c7" };
          const MOIS = ["","Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"];
          const STATUT_COURS_CFG = {
            dispense:         { label:"Dispensé",         color:"#065f46", bg:"#d1fae5", icon:"✅" },
            annule:           { label:"Annulé",           color:"#991b1b", bg:"#fee2e2", icon:"❌" },
            apprenant_absent: { label:"Apprenant absent", color:"#92400e", bg:"#fef3c7", icon:"👤" },
            coach_absent:     { label:"Coach absent",     color:"#7c3aed", bg:"#ede9fe", icon:"👨‍🏫" },
            reporte:          { label:"Reporté",          color:"#0369a1", bg:"#e0f2fe", icon:"📅" },
          };

          return (
            <ModaleAnimee ouvert
              onClicVoile={e => { if (e.target===e.currentTarget) setCoachGroupeModal(null); }}
              style={{ position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1100 }}
              panneauStyle={{ background:"#fff", borderRadius:16, width:"90vw", maxHeight:"90vh", display:"flex", flexDirection:"column", overflow:"hidden" }}>

                {/* Header */}
                <div style={{ background:`linear-gradient(135deg,#0f172a 0%,${BET_COLOR} 100%)`, padding:"20px 24px", borderRadius:"16px 16px 0 0", flexShrink:0, position:"relative" }}>
                  <button onClick={()=>setCoachGroupeModal(null)}
                    style={{ position:"absolute", top:14, right:14, background:"rgba(255,255,255,0.2)", border:"none", borderRadius:"50%", width:30, height:30, cursor:"pointer", color:"#fff", fontSize:16 }}>✕</button>
                  <div style={{ fontSize:11, color:"#7dd3fc", fontWeight:600, marginBottom:4 }}>👥 Groupe de cours</div>
                  <div style={{ fontSize:20, fontWeight:800, color:"#fff", marginBottom:6 }}>{g.nom || "-"}</div>
                  <div style={{ display:"flex", gap:10, flexWrap:"wrap", fontSize:12, color:"rgba(255,255,255,.75)", alignItems:"center" }}>
                    {g.niveau && <span style={{ background:"rgba(255,255,255,.15)", borderRadius:6, padding:"2px 10px", fontWeight:700, color:"#fff" }}>Niveau {g.niveau}</span>}
                    <span style={{ background:statutCfg.bg, color:statutCfg.color, borderRadius:6, padding:"2px 10px", fontWeight:700, fontSize:10 }}>{statutCfg.label}</span>
                    {g.filiere && <span>📂 {g.filiere}</span>}
                    {g.nb_apprenants !== undefined && <span>👤 {g.nb_apprenants} apprenant{g.nb_apprenants!==1?"s":""}</span>}
                    {g.type_cours && <span>{libelleTypeCours(g)}</span>}
                  </div>
                </div>

                {/* Sous-onglets */}
                <div style={{ display:"flex", borderBottom:"2px solid #e5e7eb", background:"#fafafa", flexShrink:0, overflowX:"auto" }}>
                  {[
                    { id:"apprenants", label:"🎓 Apprenants", count: coachGroupeModalDetail.apprenants.filter(a=>a.statut!=="retire").length },
                    { id:"cours",      label:"📚 Historique cours" },
                    { id:"presences",  label:"✅ Présences" },
                  ].map(t => (
                    <button key={t.id}
                      onClick={() => {
                        setCoachGroupeModalSubTab(t.id);
                        if (t.id==="cours") fetchCoachGroupeModalCours(g.id, coachGroupeModalCoursFiltreMois, coachGroupeModalCoursAnnee);
                        if (t.id==="presences") fetchCoachGroupeModalPresences(g.id);
                      }}
                      style={{ padding:"12px 18px", border:"none", borderBottom:`3px solid ${coachGroupeModalSubTab===t.id?BET_COLOR:"transparent"}`,
                        background:"transparent", cursor:"pointer", fontWeight:coachGroupeModalSubTab===t.id?700:400,
                        color:coachGroupeModalSubTab===t.id?BET_COLOR:"#6b7280", fontSize:12, whiteSpace:"nowrap" }}>
                      {t.label}{t.count !== undefined ? ` (${t.count})` : ""}
                    </button>
                  ))}
                </div>

                {/* Contenu */}
                <div style={{ flex:1, overflowY:"auto", padding:"20px 24px" }}>

                  {/* ── Apprenants ── */}
                  {coachGroupeModalSubTab === "apprenants" && (
                    <div>
                      {coachGroupeModalDetail.apprenants.filter(a=>a.statut!=="retire").length === 0 ? (
                        <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af" }}>
                          <div style={{ fontSize:36, marginBottom:8 }}>🎓</div>
                          <div style={{ fontWeight:600 }}>Aucun apprenant dans ce groupe</div>
                        </div>
                      ) : (
                        <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, overflow:"auto" }}>
                          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                            <thead>
                              <tr style={{ background:"#f8fafc", borderBottom:"2px solid #e2e8f0" }}>
                                {["Prénom Nom","Email","Niveau","Assistante","Statut"].map(h=>(
                                  <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:".04em" }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {coachGroupeModalDetail.apprenants.filter(a=>a.statut!=="retire").map((ap,i)=>{
                                const sCfg = ap.statut==="actif"
                                  ? { label:"Actif",   color:"#16a34a", bg:"#dcfce7" }
                                  : ap.statut==="terminé"
                                  ? { label:"Terminé", color:"#6b7280", bg:"#f3f4f6" }
                                  : { label:ap.statut||"-", color:"#f59e0b", bg:"#fef3c7" };
                                return (
                                  <tr key={ap.id||i} style={{ borderBottom:"1px solid #f1f5f9" }}>
                                    <td style={{ padding:"10px 14px", fontWeight:600, color:"#0f172a" }}>{ap.prenom||""} {ap.nom||""}</td>
                                    <td style={{ padding:"10px 14px", color:"#6b7280" }}>{ap.email||"-"}</td>
                                    <td style={{ padding:"10px 14px" }}>
                                      {ap.niveau
                                        ? <span style={{ background:"#e0f2fe", color:"#0369a1", borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{ap.niveau}</span>
                                        : "-"}
                                    </td>
                                    <td style={{ padding:"10px 14px", color:"#6b7280" }}>{ap.assistante_nom||"-"}</td>
                                    <td style={{ padding:"10px 14px" }}>
                                      <span style={{ background:sCfg.bg, color:sCfg.color, borderRadius:99, padding:"2px 10px", fontSize:10, fontWeight:700 }}>{sCfg.label}</span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Historique cours ── */}
                  {coachGroupeModalSubTab === "cours" && (
                    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                      <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
                        <select value={coachGroupeModalCoursFiltreMois}
                          onChange={e => { const m=Number(e.target.value); setCoachGroupeModalCoursFiltreMois(m); fetchCoachGroupeModalCours(g.id, m, coachGroupeModalCoursAnnee); }}
                          style={{ padding:"7px 12px", border:"1.5px solid #e2e8f0", borderRadius:8, fontSize:13, outline:"none", background:"#fff", cursor:"pointer" }}>
                          {MOIS.slice(1).map((m,i)=><option key={i+1} value={i+1}>{m}</option>)}
                        </select>
                        <select value={coachGroupeModalCoursAnnee}
                          onChange={e => { const a=Number(e.target.value); setCoachGroupeModalCoursAnnee(a); fetchCoachGroupeModalCours(g.id, coachGroupeModalCoursFiltreMois, a); }}
                          style={{ padding:"7px 12px", border:"1.5px solid #e2e8f0", borderRadius:8, fontSize:13, outline:"none", background:"#fff", cursor:"pointer" }}>
                          {[2024,2025,2026,2027].map(y=><option key={y} value={y}>{y}</option>)}
                        </select>
                      </div>
                      {coachGroupeModalCoursLoading ? (
                        <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af" }}>Chargement…</div>
                      ) : coachGroupeModalCours.length === 0 ? (
                        <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af" }}>Aucun cours pour cette période.</div>
                      ) : (
                        <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, overflow:"auto" }}>
                          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                            <thead>
                              <tr style={{ background:"#f8fafc", borderBottom:"2px solid #e2e8f0" }}>
                                {["Date","Durée","Statut","Notes"].map(h=>(
                                  <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:".04em" }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {coachGroupeModalCours.map((c,i)=>{
                                const cfg = STATUT_COURS_CFG[c.statut] || { label:c.statut||"-", color:"#6b7280", bg:"#f3f4f6", icon:"" };
                                return (
                                  <tr key={c.id||i} style={{ borderBottom:"1px solid #f1f5f9" }}>
                                    <td style={{ padding:"10px 14px", fontWeight:600 }}>{c.date ? new Date(c.date).toLocaleDateString("fr-FR",{day:"numeric",month:"short",year:"numeric"}) : "-"}</td>
                                    <td style={{ padding:"10px 14px", color:"#6b7280" }}>{c.duree_h ? `${c.duree_h}h` : "-"}</td>
                                    <td style={{ padding:"10px 14px" }}>
                                      <span style={{ background:cfg.bg, color:cfg.color, borderRadius:99, padding:"2px 10px", fontSize:10, fontWeight:700 }}>{cfg.icon} {cfg.label}</span>
                                    </td>
                                    <td style={{ padding:"10px 14px", color:"#9ca3af", fontSize:12 }}>{c.notes||"-"}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Présences ── */}
                  {coachGroupeModalSubTab === "presences" && (
                    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                      {coachGroupeModalPresences.length === 0 ? (
                        <div style={{ textAlign:"center", padding:"40px 0", color:"#9ca3af" }}>Aucune donnée de présence disponible.</div>
                      ) : (
                        coachGroupeModalPresences.map((session, si) => {
                          const apprenants = session.apprenants || [];
                          const presents = apprenants.filter(a=>a.statut==="present").length;
                          const total    = apprenants.length;
                          const taux     = total > 0 ? Math.round((presents/total)*100) : null;
                          return (
                            <div key={si} style={{ background:"#fff", borderRadius:12, border:"1px solid #e5e7eb", overflow:"hidden" }}>
                              <div style={{ padding:"12px 18px", background:"#f8fafc", borderBottom:"1px solid #e2e8f0", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
                                <span style={{ fontWeight:800, fontSize:13, color:"#0f172a" }}>📅 {session.date ? new Date(session.date).toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric"}) : "-"}</span>
                                {taux !== null && (
                                  <span style={{ fontSize:12, fontWeight:700, color: taux>=80?"#16a34a":taux>=50?"#f59e0b":"#ef4444" }}>
                                    Taux : {taux}% ({presents}/{total})
                                  </span>
                                )}
                              </div>
                              <div style={{ padding:"8px 0" }}>
                                {apprenants.map((ap,ai)=>{
                                  const pCfg = ap.statut==="present"
                                    ? { label:"Présent", color:"#16a34a", bg:"#dcfce7" }
                                    : ap.statut==="absent"
                                    ? { label:"Absent",  color:"#ef4444", bg:"#fee2e2" }
                                    : { label:ap.statut||"-", color:"#6b7280", bg:"#f3f4f6" };
                                  return (
                                    <div key={ai} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 18px", borderBottom: ai<apprenants.length-1?"1px solid #f1f5f9":"none" }}>
                                      <span style={{ fontSize:13, color:"#0f172a" }}>{ap.prenom||""} {ap.nom||""}</span>
                                      <span style={{ background:pCfg.bg, color:pCfg.color, borderRadius:99, padding:"2px 10px", fontSize:10, fontWeight:700 }}>{pCfg.label}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}

                </div>
            </ModaleAnimee>
          );
        })()}

        {/* ── MODALE DÉTAIL APPRENANT ── */}
        {selectedApprenant && (() => {
          const sa = selectedApprenant;
          const emailSA = sa.email || sa.prospect_email || "-";
          const initSA  = (sa.nom||"?").split(" ").map(p=>p[0]).slice(0,2).join("").toUpperCase();
          const SA_TABS = [
            { k:"profil",    l:"👤 Profil" },
            { k:"seances",   l:`📆 Séances${saSeancesPrivees.length > 0 ? ` (${saSeancesPrivees.length})` : ""}` },
            // Le niveau et son historique : la fiche n'affichait que `niveau_prospect`, un
            // seul niveau écrasé au test suivant. Un transfert en ajoute un, et c'est la
            // suite des deux qui explique le changement de classe qu'on lit ailleurs.
            { k:"niveau",    l:"🎓 Tests de niveau" },
            { k:"paiements", l:"💳 Paiements" },
            { k:"acces",     l:"🔑 Accès" },
          ];
          const ST_LABELS = { nouveau:"Nouveau", en_cours:"En cours", converti:"Converti", actif:"Actif", termine:"Terminé", inactif:"Inactif" };
          const ST_COLORS = { nouveau:{bg:"#fef3c7",c:"#92400e"}, en_cours:{bg:"#dbeafe",c:"#1d4ed8"}, converti:{bg:"#d1fae5",c:"#065f46"}, actif:{bg:"#d1fae5",c:"#065f46"}, termine:{bg:"#f1f5f9",c:"#6b7280"} };
          const stC = ST_COLORS[sa.statut] || {bg:"#f1f5f9",c:"#6b7280"};

          return (
            <ModaleAnimee ouvert
              onClicVoile={()=>setSelectedApprenant(null)}
              style={{ position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}
              panneauStyle={{ background:"#fff", borderRadius:18, width:"90%", maxWidth:"90vw", maxHeight:"90vh", overflow:"hidden", display:"flex", flexDirection:"column" }}>

                {/* Header */}
                <div style={{ background:BET_GRADIENT, padding:"20px 24px", position:"relative", flexShrink:0 }}>
                  <button onClick={()=>setSelectedApprenant(null)} style={{ position:"absolute", top:14, right:14, background:"rgba(255,255,255,0.2)", border:"none", borderRadius:"50%", width:30, height:30, cursor:"pointer", color:"#fff", fontSize:17, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
                  {/* EXPORT PDF DE LA FICHE. Cet écran n'en avait aucun pour un apprenant -
                      seulement des exports de listes. Bâti sur utils/fichePdfApprenant.js :
                      la charte et le tableau des tests ne se réécrivent pas par dashboard. */}
                  <button
                    onClick={() => exporterFicheApprenantPDF({
                      nom: sa.nom,
                      id: sa.id,
                      tests: { apiUrl: API_URL, authHeaders, assignationId: sa.id || null, email: emailSA !== "-" ? emailSA : null },
                      sections: [
                        { titre: "👤 Informations personnelles", lignes: [
                          ligneInfo("Nom complet", sa.nom),
                          ligneInfo("Email", emailSA),
                          ligneInfo("Téléphone", sa.telephone),
                          ligneInfo("Statut", ST_LABELS[sa.statut] || sa.statut),
                        ]},
                        { titre: "🎓 Formation", lignes: [
                          ligneInfo("Niveau", sa.niveau),
                          ligneInfo("Type de cours", libelleCanal(sa.type_cours)),
                          ligneInfo("Type de coaching", sa.type_coaching === "prive" ? "Privé" : "Groupe"),
                          ligneInfo("Centre", sa.centre_nom),
                          ligneInfo("Coach", sa.coach),
                          ligneInfo("Conseillère", sa.commercial),
                          ligneInfo("Conseiller PA", sa.pa_advisor),
                          ligneInfo("Onboarding", sa.onboarding),
                          ligneInfo("Formation souhaitée", sa.formation),
                          ligneInfo("Début", sa.date_debut ? fmtDatePDF(sa.date_debut) : null),
                          ligneInfo("Fin", sa.date_fin ? fmtDatePDF(sa.date_fin) : null),
                        ]},
                      ],
                      tableaux: [
                        { titre: "📆 Séances", entetes: ["Date","Heure","Durée","Statut"],
                          lignes: (saSeancesPrivees || []).map(x => [
                            x.date_seance ? fmtDatePDF(x.date_seance) : "-",
                            x.heure_debut || "-",
                            x.duree_minutes ? `${x.duree_minutes} min` : "-",
                            x.statut || "-",
                          ]) },
                      ],
                    }).catch(() => {})}
                    style={{ position:"absolute", top:14, right:52, display:"flex", alignItems:"center", gap:6, padding:"6px 12px", background:"rgba(255,255,255,0.2)", border:"none", borderRadius:7, cursor:"pointer", color:"#fff", fontSize:12, fontWeight:700, whiteSpace:"nowrap" }}
                  >📄 Export PDF</button>
                  <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:52, height:52, borderRadius:"50%", background:"rgba(255,255,255,0.18)", color:"#fff", fontWeight:800, fontSize:18, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, overflow:"hidden", border:"2px solid rgba(255,255,255,0.3)" }}>
                      {saAccesInfo?.profil?.avatar_url
                        ? <img src={saAccesInfo.profil.avatar_url} alt={initSA} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                        : initSA}
                    </div>
                    <div>
                      <div style={{ fontSize:11, color:"#c4b5fd", fontWeight:600, marginBottom:2 }}>🎓 Apprenant BET</div>
                      <div style={{ fontSize:19, fontWeight:800, color:"#fff" }}>{sa.nom}</div>
                      <div style={{ fontSize:12, color:"rgba(255,255,255,0.65)", display:"flex", gap:10, marginTop:2 }}>
                        {emailSA !== "-" && <span>📧 {emailSA}</span>}
                        {sa.telephone && <span>📞 {sa.telephone}</span>}
                      </div>
                    </div>
                    <div style={{ marginLeft:"auto", flexShrink:0 }}>
                      <span style={{ padding:"4px 12px", borderRadius:99, fontSize:11, fontWeight:700, background:stC.bg, color:stC.c }}>
                        {ST_LABELS[sa.statut]||sa.statut||"-"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Onglets */}
                <div style={{ display:"flex", borderBottom:"2px solid #e5e7eb", background:"#f8fafc", flexShrink:0 }}>
                  {SA_TABS.map(t=>(
                    <button key={t.k} onClick={()=>setSaApprenantTab(t.k)}
                      style={{ padding:"11px 20px", border:"none", background:"none", fontWeight:700, fontSize:12, cursor:"pointer", color:saApprenantTab===t.k?BET_COLOR:"#64748b", borderBottom:saApprenantTab===t.k?`2px solid ${BET_COLOR}`:"2px solid transparent", marginBottom:-2, whiteSpace:"nowrap" }}>
                      {t.l}
                    </button>
                  ))}
                </div>

                {/* Contenu scrollable */}
                <div style={{ flex:1, overflowY:"auto", padding:"20px 24px" }}>

                  {/* ── PROFIL ── */}
                  {saApprenantTab==="profil" && (
                    <div>
                      {/* Formation suivie */}
                      {sa.formation && (
                        <div style={{ background:"linear-gradient(135deg,#f5f3ff,#ede9fe)", borderRadius:12, padding:"14px 16px", border:"1.5px solid #c4b5fd", marginBottom:14 }}>
                          <div style={{ fontSize:10, fontWeight:700, color:BET_COLOR, textTransform:"uppercase", letterSpacing:".06em", marginBottom:6 }}>📚 Formation suivie</div>
                          <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                            <span style={{ fontSize:15, fontWeight:900, color:"#0f172a" }}>{sa.formation}</span>
                            {sa.niveau && sa.niveau !== "-" && <span style={{ fontSize:12, fontWeight:800, padding:"3px 10px", borderRadius:999, background:BET_COLOR, color:"#fff" }}>Niveau {sa.niveau}</span>}
                            {sa.lieu_coaching && (
                              <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:999, background: sa.lieu_coaching==="domicile"?"#fef3c7":"#e0f2fe", color: sa.lieu_coaching==="domicile"?"#d97706":"#0369a1" }}>
                                {sa.lieu_coaching==="domicile" ? "🏠 À domicile" : sa.lieu_coaching==="cabinet"?"🏢 Au cabinet":"📍 "+sa.lieu_coaching}
                              </span>
                            )}
                            {sa.cout_formation && (
                              <span style={{ fontSize:12, fontWeight:700, color:"#7c3aed" }}>{Number(sa.cout_formation).toLocaleString("fr-FR")} FCFA</span>
                            )}
                          </div>
                        </div>
                      )}

                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
                        {[
                          { icon:"📧", label:"Email",                   val: emailSA },
                          { icon:"📞", label:"Téléphone",               val: sa.telephone||"-" },
                          { icon:"📊", label:"Niveau",                  val: sa.niveau||"-" },
                          { icon:"📍", label:"Centre",                  val: sa.centre_nom||"-" },
                          { icon:"💳", label:"Statut paiement",         val: sa.statut_paiement||"-" },
                          { icon:"📅", label:"Inscrit le",              val: sa.created_at ? new Date(sa.created_at).toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric"}) : "-" },
                          ...(sa.date_debut ? [{ icon:"🗓", label:"Début formation", val: new Date(sa.date_debut).toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric"}) }] : []),
                          ...(sa.date_fin   ? [{ icon:"🏁", label:"Fin prévue",      val: new Date(sa.date_fin).toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric"}) }] : []),
                        ].filter(r=>r.val && r.val!=="-").map((r,i)=>(
                          <div key={i} style={{ background:"#f8fafc", borderRadius:10, padding:"11px 14px", border:"1px solid #e5e7eb" }}>
                            <div style={{ fontSize:10, color:"#9ca3af", marginBottom:2 }}>{r.icon} {r.label}</div>
                            <div style={{ fontWeight:700, color:"#0f172a", fontSize:13, wordBreak:"break-word" }}>{r.val}</div>
                          </div>
                        ))}
                      </div>

                      {/* Référents */}
                      <div style={{ borderTop:"1px solid #e5e7eb", paddingTop:14, marginBottom:14 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:"#374151", marginBottom:10, textTransform:"uppercase", letterSpacing:".04em" }}>👥 Référents</div>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                          <div style={{ background:"#fff7ed", borderRadius:10, padding:"12px 14px", border:"1px solid #fed7aa" }}>
                            <div style={{ fontSize:10, color:"#92400e", marginBottom:4, fontWeight:700 }}>📈 Assistante commerciale</div>
                            <div style={{ fontWeight:800, color:"#0f172a", fontSize:13 }}>{sa.commercial||"-"}</div>
                          </div>
                          <div style={{ background:"#f5f3ff", borderRadius:10, padding:"12px 14px", border:"1px solid #ddd6fe" }}>
                            <div style={{ fontSize:10, color:"#5b21b6", marginBottom:4, fontWeight:700 }}>🧑‍🏫 Conseiller PA / Onboarding</div>
                            <div style={{ fontWeight:800, color:"#0f172a", fontSize:13 }}>{sa.pa_advisor || sa.onboarding || "-"}</div>
                          </div>
                          <div style={{ background:"#f0fdf4", borderRadius:10, padding:"12px 14px", border:"1px solid #86efac", gridColumn:"1 / -1" }}>
                            <div style={{ fontSize:10, color:"#065f46", marginBottom:4, fontWeight:700 }}>👨‍🏫 Coach affecté</div>
                            <div style={{ fontWeight:800, color:"#0f172a", fontSize:13 }}>{sa.coach||"Non affecté"}</div>
                          </div>
                        </div>
                      </div>

                      {/* Source */}
                      <div style={{ padding:"10px 14px", background:"#f0f9ff", borderRadius:10, border:"1px solid #bae6fd", fontSize:12, color:"#0369a1", marginBottom:14 }}>
                        📂 Source : <strong>{sa._source === "cours_prive" ? "Cours privé / Domicile" : "Parcours assigné"}</strong>
                        {sa.prix_heure && <> · Tarif : <strong>{new Intl.NumberFormat("fr-FR").format(sa.prix_heure)} F/h</strong></>}
                      </div>

                      <button onClick={()=>setSelectedApprenant(null)}
                        style={{ width:"100%", padding:"11px", background:"#f1f5f9", color:"#374151", border:"none", borderRadius:10, fontSize:13, fontWeight:600, cursor:"pointer" }}>
                        Fermer
                      </button>
                    </div>
                  )}

                  {/* ── SÉANCES & PRÉSENCES ── */}
                  {saApprenantTab==="seances" && (() => {
                    const statutColors = {
                      planifiee: { bg:"#eff6ff", color:"#2563eb", label:"Planifiée" },
                      effectuee: { bg:"#f0fdf4", color:"#22c55e", label:"Effectuée" },
                      annulee:   { bg:"#fef2f2", color:"#ef4444", label:"Annulée"   },
                    };
                    const statutPresence = {
                      present: { bg:"#f0fdf4", color:"#22c55e", label:"Présent" },
                      absent:  { bg:"#fef2f2", color:"#ef4444", label:"Absent"  },
                      retard:  { bg:"#fffbeb", color:"#f59e0b", label:"Retard"  },
                      excuse:  { bg:"#f0f9ff", color:"#0891b2", label:"Excusé"  },
                    };
                    const nbEffectuees = saSeancesPrivees.filter(s=>s.statut==="effectuee").length;
                    const nbAvenir     = saSeancesPrivees.filter(s=>s.statut==="planifiee").length;
                    const nbPresent    = saPresencesPrivees.filter(p=>p.statut==="present").length;
                    const nbAbsent     = saPresencesPrivees.filter(p=>p.statut==="absent").length;
                    const nbRetard     = saPresencesPrivees.filter(p=>p.statut==="retard").length;
                    const totalPres    = saPresencesPrivees.length;
                    const tauxPres = totalPres > 0 ? Math.round((nbPresent / totalPres) * 100) : null;
                    const tauxAbs  = totalPres > 0 ? Math.round((nbAbsent  / totalPres) * 100) : null;
                    return (
                      <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
                        {saLoadingSeances ? (
                          <div style={{ textAlign:"center", padding:32, color:"#9ca3af", fontSize:13 }}>⏳ Chargement…</div>
                        ) : (
                          <>
                            {/* KPIs */}
                            <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10 }}>
                              {[
                                { l:"Effectuées",  v:nbEffectuees,                              bg:"#f0fdf4", c:"#22c55e" },
                                { l:"À venir",     v:nbAvenir,                                  bg:"#eff6ff", c:"#2563eb" },
                                { l:"Total",       v:saSeancesPrivees.length,                   bg:"#f8fafc", c:"#64748b" },
                                { l:"Taux prés.",  v:tauxPres!=null?`${tauxPres}%`:"-",         bg:BET_LIGHT, c:BET_COLOR },
                                { l:"Absences",    v:nbAbsent,                                  bg:"#fef2f2", c:"#ef4444" },
                              ].map(s=>(
                                <div key={s.l} style={{ background:s.bg, borderRadius:10, padding:"10px 8px", textAlign:"center", border:"1px solid #e2e8f0" }}>
                                  <div style={{ fontSize:20, fontWeight:900, color:s.c }}>{s.v}</div>
                                  <div style={{ fontSize:10, color:"#64748b", fontWeight:600, marginTop:2 }}>{s.l}</div>
                                </div>
                              ))}
                            </div>

                            {/* Barre taux de présence */}
                            {tauxPres != null && (
                              <div style={{ background:"#f8fafc", borderRadius:10, padding:"12px 14px" }}>
                                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                                  <span style={{ fontSize:12, color:"#374151", fontWeight:600 }}>Taux de présence</span>
                                  <span style={{ fontSize:14, fontWeight:800, color:tauxPres>=80?"#059669":tauxPres>=60?"#d97706":"#dc2626" }}>{tauxPres}%</span>
                                </div>
                                <div style={{ height:8, background:"#e5e7eb", borderRadius:4, overflow:"hidden" }}>
                                  <div style={{ height:"100%", width:`${tauxPres}%`, background:tauxPres>=80?"#22c55e":tauxPres>=60?"#f59e0b":"#ef4444", borderRadius:4, transition:"width .4s" }} />
                                </div>
                                {tauxAbs != null && (
                                  <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>
                                    {nbAbsent} absence{nbAbsent>1?"s":""} ({tauxAbs}%) · {nbRetard} retard{nbRetard>1?"s":""}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Tableau séances */}
                            <div>
                              <div style={{ fontSize:13, fontWeight:800, color:"#0f172a", marginBottom:8 }}>📆 Séances planifiées</div>
                              {saSeancesPrivees.length === 0 ? (
                                <div style={{ background:"#f8fafc", borderRadius:10, padding:"20px", textAlign:"center", border:"1.5px dashed #e2e8f0", color:"#94a3b8", fontSize:13 }}>
                                  Aucune séance enregistrée par le coach
                                </div>
                              ) : (
                                <div style={{ border:"1px solid #e2e8f0", borderRadius:10, overflow:"hidden" }}>
                                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                                    <thead>
                                      <tr style={{ background:"#f8fafc" }}>
                                        {["Date","Heure","Durée","Lieu","Statut","Notes"].map(h=>(
                                          <th key={h} style={{ padding:"9px 12px", fontWeight:700, color:"#64748b", fontSize:11, textAlign:"left", borderBottom:"1px solid #e2e8f0" }}>{h}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {[...saSeancesPrivees].sort((a,b)=>b.date_seance.localeCompare(a.date_seance)).map(s=>{
                                        const sc = statutColors[s.statut] || { bg:"#f8fafc", color:"#64748b", label:s.statut };
                                        const lieu = s.lieu==="domicile"?"🏠 Domicile":s.lieu==="en_ligne"?"💻 En ligne":"🏢 Cabinet";
                                        return (
                                          <tr key={s.id} style={{ borderBottom:"1px solid #f1f5f9" }}>
                                            <td style={{ padding:"9px 12px", fontWeight:600 }}>{new Date(s.date_seance+"T12:00:00").toLocaleDateString("fr-FR",{day:"numeric",month:"short",year:"numeric"})}</td>
                                            <td style={{ padding:"9px 12px", color:"#64748b" }}>{s.heure_debut?s.heure_debut.slice(0,5):"-"}</td>
                                            <td style={{ padding:"9px 12px", color:"#64748b" }}>{s.duree_h?`${s.duree_h}h`:"-"}</td>
                                            <td style={{ padding:"9px 12px", fontSize:11 }}>{lieu}</td>
                                            <td style={{ padding:"9px 12px" }}>
                                              <span style={{ padding:"2px 8px", borderRadius:99, fontSize:11, fontWeight:700, background:sc.bg, color:sc.color }}>{sc.label}</span>
                                            </td>
                                            <td style={{ padding:"9px 12px", color:"#64748b", maxWidth:120, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.notes||"-"}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>

                            {/* Feuille de présences */}
                            <div>
                              <div style={{ fontSize:13, fontWeight:800, color:"#0f172a", marginBottom:8 }}>✅ Feuille de présences</div>
                              {saPresencesPrivees.length === 0 ? (
                                <div style={{ background:"#f8fafc", borderRadius:10, padding:"20px", textAlign:"center", border:"1.5px dashed #e2e8f0", color:"#94a3b8", fontSize:13 }}>
                                  Aucune présence enregistrée par le coach
                                </div>
                              ) : (
                                <div style={{ border:"1px solid #e2e8f0", borderRadius:10, overflow:"hidden" }}>
                                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                                    <thead>
                                      <tr style={{ background:"#f8fafc" }}>
                                        {["Date","Statut","Motif","Note"].map(h=>(
                                          <th key={h} style={{ padding:"9px 12px", fontWeight:700, color:"#64748b", fontSize:11, textAlign:"left", borderBottom:"1px solid #e2e8f0" }}>{h}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {[...saPresencesPrivees].sort((a,b)=>b.date_presence.localeCompare(a.date_presence)).map(p=>{
                                        const sp = statutPresence[p.statut] || { bg:"#f8fafc", color:"#64748b", label:p.statut };
                                        return (
                                          <tr key={p.id} style={{ borderBottom:"1px solid #f1f5f9" }}>
                                            <td style={{ padding:"9px 12px", fontWeight:600 }}>{new Date(p.date_presence+"T12:00:00").toLocaleDateString("fr-FR",{day:"numeric",month:"short",year:"numeric"})}</td>
                                            <td style={{ padding:"9px 12px" }}>
                                              <span style={{ padding:"2px 8px", borderRadius:99, fontSize:11, fontWeight:700, background:sp.bg, color:sp.color }}>{sp.label}</span>
                                            </td>
                                            <td style={{ padding:"9px 12px", color:"#64748b" }}>{p.motif||"-"}</td>
                                            <td style={{ padding:"9px 12px", color:"#64748b" }}>{p.note||"-"}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                        <button onClick={()=>setSelectedApprenant(null)}
                          style={{ width:"100%", marginTop:14, padding:"11px", background:"#f1f5f9", color:"#374151", border:"none", borderRadius:10, fontSize:13, fontWeight:600, cursor:"pointer" }}>
                          Fermer
                        </button>
                      </div>
                    );
                  })()}

                  {/* ── PAIEMENTS ── */}
                  {saApprenantTab==="paiements" && (() => {
                    const pais      = saPaiementsPrivees;
                    const totalDu   = pais.reduce((s,p)=>s+(Number(p.montant_du)||0),0);
                    const totalRecu = pais.reduce((s,p)=>s+(Number(p.montant_recu)||0),0);
                    const reste     = totalDu - totalRecu;
                    return (
                      <div>
                        {saLoadingPaiements ? (
                          <div style={{ textAlign:"center", padding:40, color:"#9ca3af", fontSize:13 }}>⏳ Chargement des paiements…</div>
                        ) : (
                          <>
                            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:16 }}>
                              {[
                                { l:"Montant dû",    v:`${new Intl.NumberFormat("fr-FR").format(totalDu)} F`,    bg:"#eff6ff", c:BET_COLOR },
                                { l:"Montant reçu",  v:`${new Intl.NumberFormat("fr-FR").format(totalRecu)} F`,  bg:"#d1fae5", c:"#065f46" },
                                { l:"Reste à payer", v:`${new Intl.NumberFormat("fr-FR").format(Math.max(0,reste))} F`, bg:reste>0?"#fee2e2":"#d1fae5", c:reste>0?"#dc2626":"#065f46" },
                              ].map(k=>(
                                <div key={k.l} style={{ background:k.bg, borderRadius:10, padding:"10px 12px", textAlign:"center" }}>
                                  <div style={{ fontSize:14, fontWeight:800, color:k.c }}>{k.v}</div>
                                  <div style={{ fontSize:10, color:k.c, fontWeight:600, marginTop:2 }}>{k.l}</div>
                                </div>
                              ))}
                            </div>
                            {pais.length === 0 ? (
                              <div style={{ textAlign:"center", padding:32, color:"#9ca3af", fontSize:13 }}>Aucun paiement enregistré.</div>
                            ) : (
                              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                                {pais.map((p,i)=>{
                                  const statC = p.statut==="reçu"||p.statut==="validé"||p.statut==="confirmé" ? {bg:"#d1fae5",c:"#065f46"} : p.statut==="en_attente" ? {bg:"#fef9c3",c:"#854d0e"} : {bg:"#f1f5f9",c:"#6b7280"};
                                  return (
                                    <div key={p.id||i} style={{ background:"#f8fafc", borderRadius:10, padding:"12px 14px", border:"1px solid #e5e7eb" }}>
                                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                                        <span style={{ fontWeight:700, fontSize:13, color:"#0f172a" }}>
                                          {p.date_paiement ? new Date(p.date_paiement).toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric"}) : "Date inconnue"}
                                        </span>
                                        <span style={{ padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:700, background:statC.bg, color:statC.c }}>{p.statut||"-"}</span>
                                      </div>
                                      <div style={{ display:"flex", gap:16, fontSize:12, color:"#374151", flexWrap:"wrap" }}>
                                        <span>💰 Reçu : <strong>{new Intl.NumberFormat("fr-FR").format(p.montant_recu||0)} F</strong></span>
                                        {p.montant_du ? <span>📋 Dû : <strong>{new Intl.NumberFormat("fr-FR").format(p.montant_du)} F</strong></span> : null}
                                        {p.mode_paiement && <span>🏦 {p.mode_paiement}</span>}
                                      </div>
                                      {p.notes && <div style={{ fontSize:11, color:"#9ca3af", marginTop:4, fontStyle:"italic" }}>{p.notes}</div>}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        )}
                        <button onClick={()=>setSelectedApprenant(null)}
                          style={{ width:"100%", marginTop:14, padding:"11px", background:"#f1f5f9", color:"#374151", border:"none", borderRadius:10, fontSize:13, fontWeight:600, cursor:"pointer" }}>
                          Fermer
                        </button>
                      </div>
                    );
                  })()}

                  {/* ── ACCÈS ── */}
                  {/* ── TESTS DE NIVEAU ── tous les passages, l'ancien comme le nouveau */}
                  {saApprenantTab==="niveau" && (
                    <HistoriqueTestsNiveau
                      apiUrl={API_URL}
                      authHeaders={authHeaders}
                      assignationId={sa.id || null}
                      email={emailSA !== "-" ? emailSA : null}
                      mode="complet"
                      titre="🎓 Tests de niveau"
                    />
                  )}

                  {saApprenantTab==="acces" && (
                    <div>
                      {saAccesLoad ? (
                        <div style={{ textAlign:"center", padding:40, color:"#9ca3af", fontSize:13 }}>⏳ Vérification du compte…</div>
                      ) : saAccesInfo?.has_account ? (
                        <div style={{ background:"#f0fdf4", border:"1px solid #86efac", borderRadius:12, padding:"16px 18px", marginBottom:14 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
                            <span style={{ fontSize:18 }}>✅</span>
                            <span style={{ fontWeight:800, color:"#065f46", fontSize:14 }}>Compte actif</span>
                            {saAccesInfo.profil?.mdp_temporaire && (
                              <span style={{ padding:"2px 8px", background:"#fef3c7", color:"#92400e", borderRadius:99, fontSize:10, fontWeight:700 }}>Mot de passe temporaire</span>
                            )}
                            {saAccesInfo.profil?.acces_bloque && (
                              <span style={{ padding:"2px 8px", background:"#fee2e2", color:"#dc2626", borderRadius:99, fontSize:10, fontWeight:700 }}>🔒 Accès bloqué</span>
                            )}
                          </div>
                          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                            <div style={{ background:"#fff", borderRadius:8, padding:"10px 12px", border:"1px solid #d1fae5" }}>
                              <div style={{ fontSize:9, color:"#9ca3af", marginBottom:3 }}>EMAIL DE CONNEXION</div>
                              <div style={{ fontSize:12, fontWeight:700, color:"#0f172a", wordBreak:"break-all" }}>{saAccesInfo.profil?.email || emailSA}</div>
                            </div>
                            {saAccesInfo.profil?.mdp_temporaire && saAccesInfo.profil?.mdp_initial ? (
                              <div style={{ background:"#fffbeb", borderRadius:8, padding:"10px 12px", border:"1px solid #fde68a" }}>
                                <div style={{ fontSize:9, color:"#92400e", marginBottom:3 }}>MOT DE PASSE TEMP.</div>
                                <div style={{ fontSize:14, fontWeight:800, color:"#0f172a", fontFamily:"monospace" }}>{saAccesInfo.profil.mdp_initial}</div>
                              </div>
                            ) : (
                              <div style={{ background:"#f0fdf4", borderRadius:8, padding:"10px 12px", border:"1px solid #86efac" }}>
                                <div style={{ fontSize:9, color:"#065f46", marginBottom:3 }}>MOT DE PASSE</div>
                                <div style={{ fontSize:12, fontWeight:700, color:"#065f46" }}>✅ Changé par l'apprenant</div>
                              </div>
                            )}
                          </div>
                          {saAccesInfo.profil?.date_creation && (
                            <div style={{ fontSize:11, color:"#6b7280", marginTop:10 }}>
                              Compte créé le {new Date(saAccesInfo.profil.date_creation).toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric"})}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:12, padding:"16px 18px", marginBottom:14 }}>
                          <div style={{ fontWeight:700, color:"#92400e", fontSize:13, marginBottom:4 }}>⚠️ Aucun compte créé</div>
                          <div style={{ fontSize:12, color:"#78350f" }}>Cet apprenant n'a pas encore d'accès à l'espace apprenant.</div>
                          {emailSA && emailSA !== "-" && (
                            <div style={{ fontSize:11, color:"#9ca3af", marginTop:6 }}>Email : {emailSA}</div>
                          )}
                        </div>
                      )}
                      <button onClick={()=>setSelectedApprenant(null)}
                        style={{ width:"100%", padding:"11px", background:"#f1f5f9", color:"#374151", border:"none", borderRadius:10, fontSize:13, fontWeight:600, cursor:"pointer" }}>
                        Fermer
                      </button>
                    </div>
                  )}

                </div>
            </ModaleAnimee>
          );
        })()}

          {/* ════ ARCHIVES (désactivé temporairement) ════ */}
          {/* {activeTab === "archives" && (
            <div style={{ background:"#fff", borderRadius:"0 12px 12px 12px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)" }}>
              <ArchiveTab accentColor="#0f172a" />
            </div>
          )} */}

          {/* ════ MESSAGES ════ */}
          {/* ══════════════ BET FOR BUSINESS ══════════════
              Même panneau qu'avant, remonté au premier niveau. Il ne pilote que le média
              du hero : avantages, secteurs, étapes et FAQ de la page restent écrits en dur
              dans `Frontend/src/Pages/BetForBusiness/BetForBusiness.jsx` jusqu'à la refonte.
              L'encart le dit, plutôt que de laisser croire que tout se règle ici. ══ */}
          {activeTab === "bet_business" && (
            <div style={{ background:"#fff", borderRadius:16, padding:"22px 26px", border:"1px solid #e5e7eb" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:16, marginBottom:18, flexWrap:"wrap" }}>
                <div>
                  <h3 style={{ margin:0, fontSize:17, fontWeight:800, color:"#0f172a" }}>🏢 Page BET for Business</h3>
                  <p style={{ margin:"4px 0 0", fontSize:12.5, color:"#9ca3af" }}>
                    Contenus et médias de la page <code>/bet-for-business</code>, destinée aux entreprises.
                  </p>
                </div>
                <a href="/bet-for-business" target="_blank" rel="noopener noreferrer"
                  style={{ padding:"8px 14px", background:"#f1f5f9", color:"#374151", borderRadius:8, fontWeight:700, fontSize:12, textDecoration:"none", display:"flex", alignItems:"center", gap:5 }}>
                  🔗 Voir la page
                </a>
              </div>

              {/* ── Sous-onglets ── */}
              <div style={{ display:"flex", gap:8, marginBottom:18, borderBottom:"1px solid #e5e7eb", paddingBottom:0 }}>
                {[
                  { k:"hero", l:"Hero & Carrousel", i:"🎬" },
                  { k:"contenus", l:"Étude de cas & contenus", i:"📄" },
                  { k:"faq",  l:"FAQ",              i:"❓" },
                ].map(t => (
                  <button key={t.k} onClick={() => setBfbSubTab(t.k)}
                    style={{ padding:"9px 18px", border:"none", background:"transparent", cursor:"pointer",
                      fontSize:12.5, fontWeight: bfbSubTab===t.k ? 800 : 600, fontFamily:"inherit",
                      color: bfbSubTab===t.k ? "#0c33ac" : "#64748b",
                      borderBottom: bfbSubTab===t.k ? "3px solid #0c33ac" : "3px solid transparent", marginBottom:-1 }}>
                    {t.i} {t.l}
                  </button>
                ))}
              </div>

              <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:10, padding:"12px 16px", fontSize:12.5, color:"#92400e" }}>
                <strong>Modifiable ici :</strong> le carrousel du hero, ses textes et ses boutons, et la FAQ de la page.
                <div style={{ marginTop:6, color:"#a16207" }}>
                  Les avantages, les secteurs et les étapes du parcours sont encore écrits dans le code et ne
                  peuvent pas être modifiés depuis cet écran. Ils le deviendront avec la refonte de la page.
                </div>
              </div>



              {/* ══════════════ LES SLIDES DU CARROUSEL ══════════════
                  UNE SLIDE = UN MÉDIA + SON MESSAGE. Le contenu éditorial n'est plus unique
                  et posé au-dessus du carrousel : chaque slide porte son sur-titre, son
                  titre, son texte et ses deux boutons, et ils défilent avec elle.
                  TOUT PASSE PAR LA MODALE — création comme modification. Le formulaire
                  permanent qui occupait la moitié de l'écran a disparu : on ne saisit que
                  lorsqu'on a décidé de saisir. */}
              {bfbSubTab === "hero" && (
                <div style={{ marginTop:22 }}>

                  {/* Réglage global : il appartient au carrousel, pas à une slide. */}
                  <div style={{ display:"flex", alignItems:"flex-end", gap:16, flexWrap:"wrap", background:"#f8fafc", border:"1px solid #e5e7eb", borderRadius:12, padding:"14px 18px", marginBottom:18 }}>
                    <div>
                      <label style={lblBfb}>Défilement du carrousel</label>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <input type="number" min={2} max={30} value={bfbHero.defilement}
                          onChange={e => champBfb("defilement", e.target.value)} style={{ ...inpBfb, width:90 }} />
                        <span style={{ fontSize:11.5, color:"#64748b" }}>secondes par image</span>
                      </div>
                      <div style={{ marginTop:5, fontSize:10.5, color:"#9ca3af" }}>
                        Entre 2 et 30 s. Une vidéo n'est jamais coupée : c'est sa fin qui fait avancer le carrousel.
                      </div>
                    </div>
                    <button onClick={enregistrerBfbHero} disabled={bfbHeroSaving}
                      style={{ marginLeft:"auto", padding:"9px 18px", borderRadius:9, border:"none", color:"#fff", fontWeight:800, fontSize:12.5,
                        cursor: bfbHeroSaving ? "wait" : "pointer", background: bfbHeroSaving ? "#9ca3af" : "#0c33ac" }}>
                      {bfbHeroSaving ? "⏳…" : "💾 Enregistrer le réglage"}
                    </button>
                  </div>

                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, marginBottom:14, flexWrap:"wrap" }}>
                    <div>
                      <h4 style={{ margin:0, fontSize:13, fontWeight:800, color:"#0f172a" }}>🎞 Slides du hero</h4>
                      <p style={{ margin:"3px 0 0", fontSize:11, color:"#9ca3af" }}>
                        Elles défilent dans cet ordre. Une slide sans texte affiche son média seul.
                      </p>
                    </div>
                    <button onClick={() => { setOffreMediaForm(OFFRE_MEDIA_BLANK); setOffreMediaEditingId(null); setSlideModal("new"); }}
                      style={{ padding:"9px 18px", borderRadius:9, border:"none", background:"#0c33ac", color:"#fff", fontSize:12.5, fontWeight:800, cursor:"pointer" }}>
                      ＋ Nouveau contenu
                    </button>
                  </div>

                  {offreMediaLoading ? (
                    <div style={{ fontSize:12, color:"#94a3b8", padding:"16px 0" }}>Chargement…</div>
                  ) : offreMediaList.length === 0 ? (
                    <div style={{ background:"#f8fafc", border:"1px dashed #cbd5e1", borderRadius:12, padding:"28px 20px", textAlign:"center", fontSize:12.5, color:"#64748b" }}>
                      Aucune slide. Le hero affiche alors son dégradé bleu, sans média ni texte.
                    </div>
                  ) : (
                    <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
                      {offreMediaList.map((m, i) => {
                        const aDuTexte = !!(m.surtitre || m.titre || m.description || m.bouton1_libelle);
                        const nbBoutons = (m.bouton1_libelle ? 1 : 0) + (m.bouton2_libelle ? 1 : 0);
                        return (
                          <div key={m.id} style={{ display:"flex", gap:14, alignItems:"center", background:"#fff",
                            border:"1px solid #e5e7eb", borderLeft:`3px solid ${m.actif ? "#15803d" : "#cbd5e1"}`, borderRadius:10, padding:"11px 14px" }}>
                            <span style={{ fontSize:12, fontWeight:800, color:"#94a3b8", minWidth:18 }}>{i + 1}</span>
                            <div style={{ width:64, height:40, borderRadius:7, background:"#0b1120", flexShrink:0, overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center" }}>
                              {m.type === "image"
                                ? <img src={m.url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                                : <span style={{ fontSize:16 }}>🎬</span>}
                            </div>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontSize:13, fontWeight:700, color:"#0f172a", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                                {m.titre || <span style={{ color:"#94a3b8", fontWeight:500 }}>Sans titre</span>}
                              </div>
                              <div style={{ fontSize:11, color:"#94a3b8", marginTop:2 }}>
                                {aDuTexte
                                  ? <>{m.surtitre ? `« ${m.surtitre} »` : "sur-titre manquant"} · {nbBoutons} bouton{nbBoutons > 1 ? "s" : ""}</>
                                  : <span style={{ color:"#b45309" }}>média seul — aucun texte</span>}
                              </div>
                            </div>
                            <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                              <button onClick={() => { setOffreMediaForm({ ...OFFRE_MEDIA_BLANK, ...m }); setOffreMediaEditingId(m.id); setSlideModal(m.id); }}
                                title="Modifier" style={{ padding:"5px 9px", borderRadius:7, border:"1px solid #e2e8f0", background:"#fff", cursor:"pointer", fontSize:12 }}>✏️</button>
                              <button onClick={() => toggleOffreMediaActif(m)} title={m.actif ? "Retirer du carrousel" : "Remettre dans le carrousel"}
                                style={{ padding:"5px 9px", borderRadius:7, border:"1px solid #e2e8f0", background:"#fff", cursor:"pointer", fontSize:12 }}>{m.actif ? "✅" : "🚫"}</button>
                              <button onClick={() => moveOffreMedia(m.id, "up")} disabled={i === 0} title="Monter"
                                style={{ padding:"5px 9px", borderRadius:7, border:"1px solid #e2e8f0", background:"#fff", cursor: i === 0 ? "not-allowed" : "pointer", opacity: i === 0 ? .4 : 1, fontSize:12 }}>↑</button>
                              <button onClick={() => moveOffreMedia(m.id, "down")} disabled={i === offreMediaList.length - 1} title="Descendre"
                                style={{ padding:"5px 9px", borderRadius:7, border:"1px solid #e2e8f0", background:"#fff", cursor: i === offreMediaList.length - 1 ? "not-allowed" : "pointer", opacity: i === offreMediaList.length - 1 ? .4 : 1, fontSize:12 }}>↓</button>
                              <button onClick={() => deleteOffreMedia(m.id)} title="Supprimer"
                                style={{ padding:"5px 9px", borderRadius:7, border:"1px solid #fecaca", background:"#fef2f2", color:"#b91c1c", cursor:"pointer", fontSize:12 }}>🗑</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}


              {/* ══════════════ MODALE D'UNE SLIDE ══════════════
                  Création et modification dans le MÊME formulaire : ce sont les mêmes champs,
                  et en dédoubler un garantit qu'on n'en corrigera qu'une moitié.
                  LE MÉDIA EST EN HAUT, LE MESSAGE EN DESSOUS : on choisit d'abord ce qu'on
                  montre, ensuite ce qu'on dit. ══ */}
              {slideModal && (
                <ModaleAnimee ouvert
                  onClicVoile={e => { if (e.target === e.currentTarget) setSlideModal(null); }}
                  style={{ position:"fixed", inset:0, background:"rgba(15,23,42,.55)", zIndex:10000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
                  panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:760, maxHeight:"92vh", display:"flex", flexDirection:"column", overflow:"hidden" }}>

                    <div style={{ padding:"16px 22px", borderBottom:"1px solid #f1f5f9", display:"flex", alignItems:"center", gap:12 }}>
                      <span style={{ fontSize:18 }}>🎞</span>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:14, fontWeight:800, color:"#0f172a" }}>
                          {slideModal === "new" ? "Nouveau contenu" : "Modifier la slide"}
                        </div>
                        <div style={{ fontSize:11, color:"#64748b" }}>Un média et le message qui l'accompagne.</div>
                      </div>
                      <button onClick={() => setSlideModal(null)}
                        style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#9ca3af", lineHeight:1 }}>✕</button>
                    </div>

                    <div style={{ padding:"18px 22px", overflowY:"auto", flex:1 }}>
                      {/* ── Le média ── */}
                      <div style={{ fontSize:11, fontWeight:800, color:"#64748b", textTransform:"uppercase", letterSpacing:".06em", marginBottom:10 }}>1 · Le média</div>
                      <div style={{ background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:9, padding:"8px 12px", fontSize:11, color:"#1e40af", marginBottom:12 }}>
                        Images : 1280×720 px · Vidéos : MP4 max 100 Mo — ou une URL YouTube/Vimeo
                      </div>
                      <div
                        onDragOver={e => { e.preventDefault(); setOffreMediaDragOver(true); }}
                        onDragLeave={() => setOffreMediaDragOver(false)}
                        onDrop={e => { e.preventDefault(); setOffreMediaDragOver(false); const f = e.dataTransfer.files[0]; if (f) uploadOffreMediaFile(f); }}
                        style={{ border:`2px dashed ${offreMediaDragOver ? "#0c33ac" : "#cbd5e1"}`, borderRadius:10, padding:"18px", textAlign:"center", marginBottom:10, background: offreMediaDragOver ? "#eff6ff" : "#f8fafc" }}>
                        <div style={{ fontSize:20, marginBottom:4 }}>☁️</div>
                        <div style={{ fontSize:12, color:"#64748b" }}>
                          Glissez un fichier ou{" "}
                          <label style={{ color:"#0c33ac", fontWeight:700, cursor:"pointer", textDecoration:"underline" }}>
                            parcourir
                            <input type="file" accept="image/*,video/*" style={{ display:"none" }}
                              onChange={e => { const f = e.target.files?.[0]; if (f) uploadOffreMediaFile(f); e.target.value = ""; }} />
                          </label>
                        </div>
                        {offreMediaUploading && (
                          <div style={{ marginTop:8, fontSize:11, color:"#0c33ac", fontWeight:700 }}>Envoi… {offreMediaUploadPct}%</div>
                        )}
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"150px 1fr", gap:10, marginBottom:18 }}>
                        <div>
                          <label style={lblBfb}>Type</label>
                          <select value={offreMediaForm.type} onChange={e => setOffreMediaForm(f => ({ ...f, type:e.target.value }))} style={inpBfb}>
                            <option value="video">🎬 Vidéo</option>
                            <option value="image">🖼 Image</option>
                          </select>
                        </div>
                        <div>
                          <label style={lblBfb}>URL du média</label>
                          <input value={offreMediaForm.url} onChange={e => setOffreMediaForm(f => ({ ...f, url:e.target.value }))}
                            placeholder="https://youtube.com/… ou https://…" style={inpBfb} />
                        </div>
                      </div>

                      {/* ── Le message ── */}
                      <div style={{ fontSize:11, fontWeight:800, color:"#64748b", textTransform:"uppercase", letterSpacing:".06em", marginBottom:4 }}>2 · Le message</div>
                      <p style={{ margin:"0 0 12px", fontSize:11, color:"#9ca3af" }}>
                        Tout est facultatif. Une slide laissée sans texte affiche son média seul.
                      </p>
                      <label style={lblBfb}>Sur-titre</label>
                      <input value={offreMediaForm.surtitre || ""} onChange={e => setOffreMediaForm(f => ({ ...f, surtitre:e.target.value }))}
                        placeholder="FORMATIONS D'ANGLAIS PROFESSIONNEL POUR LES ENTREPRISES" style={inpBfb} />
                      <label style={{ ...lblBfb, marginTop:10 }}>Titre</label>
                      <input value={offreMediaForm.titre || ""} onChange={e => setOffreMediaForm(f => ({ ...f, titre:e.target.value }))}
                        placeholder="Vos équipes ont les compétences…" style={inpBfb} />
                      <label style={{ ...lblBfb, marginTop:10 }}>Texte</label>
                      <textarea value={offreMediaForm.description || ""} rows={3} onChange={e => setOffreMediaForm(f => ({ ...f, description:e.target.value }))}
                        style={{ ...inpBfb, resize:"vertical", fontFamily:"inherit" }} />

                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginTop:16 }}>
                        {[
                          { l:"Bouton principal",  cle:"bouton1", aide:"Sans lien, il ouvre la demande de devis entreprise." },
                          { l:"Bouton secondaire", cle:"bouton2", aide:"Sans lien, il n'apparaît pas sur la slide." },
                        ].map(b => (
                          <div key={b.cle} style={{ background:"#f8fafc", border:"1px solid #e5e7eb", borderRadius:10, padding:"12px 14px" }}>
                            <div style={{ fontSize:11.5, fontWeight:800, color:"#0f172a", marginBottom:2 }}>{b.l}</div>
                            <div style={{ fontSize:10.5, color:"#9ca3af", marginBottom:10 }}>{b.aide}</div>
                            <label style={lblBfb}>Libellé</label>
                            <input value={offreMediaForm[`${b.cle}_libelle`] || ""} style={inpBfb}
                              onChange={e => setOffreMediaForm(f => ({ ...f, [`${b.cle}_libelle`]: e.target.value }))} />
                            <label style={{ ...lblBfb, marginTop:8 }}>Lien</label>
                            <input value={offreMediaForm[`${b.cle}_lien`] || ""} style={inpBfb}
                              placeholder="/parcours/entreprise ou https://…"
                              onChange={e => setOffreMediaForm(f => ({ ...f, [`${b.cle}_lien`]: e.target.value }))} />
                          </div>
                        ))}
                      </div>

                      <label style={{ display:"flex", alignItems:"center", gap:8, marginTop:16, fontSize:12.5, color:"#334155", fontWeight:600 }}>
                        <input type="checkbox" checked={offreMediaForm.actif !== false}
                          onChange={e => setOffreMediaForm(f => ({ ...f, actif:e.target.checked }))} />
                        Visible dans le carrousel
                      </label>
                    </div>

                    <div style={{ padding:"14px 22px", borderTop:"1px solid #f1f5f9", display:"flex", justifyContent:"flex-end", gap:10 }}>
                      <button onClick={() => setSlideModal(null)}
                        style={{ padding:"9px 18px", borderRadius:9, border:"1px solid #e2e8f0", background:"#fff", color:"#334155", fontSize:12.5, fontWeight:700, cursor:"pointer" }}>
                        Annuler
                      </button>
                      <button onClick={async () => { await saveOffreMedia(); setSlideModal(null); }}
                        disabled={!offreMediaForm.url?.trim() || offreMediaSaving}
                        style={{ padding:"9px 22px", borderRadius:9, border:"none", color:"#fff", fontSize:12.5, fontWeight:800,
                          cursor: offreMediaSaving ? "wait" : "pointer",
                          background: !offreMediaForm.url?.trim() ? "#cbd5e1" : "#0c33ac" }}>
                        {offreMediaSaving ? "⏳…" : slideModal === "new" ? "＋ Ajouter la slide" : "💾 Enregistrer"}
                      </button>
                    </div>
                </ModaleAnimee>
              )}

              {/* ══════════════ ÉTUDE DE CAS & CONTENUS ══════════════ */}
              {bfbSubTab === "contenus" && (
                bfbContCharge ? (
                  <div style={{ fontSize:12, color:"#94a3b8", padding:"18px 0" }}>Chargement…</div>
                ) : (
                <div style={{ marginTop:22, display:"flex", flexDirection:"column", gap:22 }}>

                  {/* ── Étude de cas ── */}
                  <div style={{ background:"#f8fafc", border:"1px solid #e5e7eb", borderRadius:12, padding:"18px 20px" }}>
                    <div style={{ fontSize:13, fontWeight:800, color:"#0f172a", marginBottom:3 }}>📄 Étude de cas</div>
                    <p style={{ margin:"0 0 14px", fontSize:11, color:"#9ca3af" }}>
                      Section « Nos références » de la page. Laissez un champ vide pour conserver le texte par défaut.
                    </p>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 2fr", gap:12, marginBottom:12 }}>
                      <div>
                        <label style={lblBfb}>Sur-titre</label>
                        <input value={bfbCont.wave?.eyebrow || ""} onChange={e => champWave("eyebrow", e.target.value)} placeholder="Nos références" style={inpBfb} />
                      </div>
                      <div>
                        <label style={lblBfb}>Titre</label>
                        <input value={bfbCont.wave?.titre || ""} onChange={e => champWave("titre", e.target.value)} placeholder="BET accompagne les équipes de…" style={inpBfb} />
                      </div>
                    </div>
                    {(bfbCont.wave?.blocs || []).map((b, i) => (
                      <div key={i} style={{ display:"grid", gridTemplateColumns:"180px 1fr", gap:12, marginBottom:10 }}>
                        <div>
                          <label style={lblBfb}>Intitulé</label>
                          <input value={b.label || ""} onChange={e => champWaveBloc(i, "label", e.target.value)} style={inpBfb} />
                        </div>
                        <div>
                          <label style={lblBfb}>Texte</label>
                          <textarea value={b.texte || ""} rows={2} onChange={e => champWaveBloc(i, "texte", e.target.value)}
                            style={{ ...inpBfb, resize:"vertical", fontFamily:"inherit" }} />
                        </div>
                      </div>
                    ))}
                    <div style={{ marginTop:10 }}>
                      <label style={lblBfb}>Titre de l'appel à l'action</label>
                      <input value={bfbCont.wave?.ctaTitre || ""} onChange={e => champWave("ctaTitre", e.target.value)}
                        placeholder="Et si BET accompagnait aussi vos équipes ?" style={inpBfb} />
                      <div style={{ marginTop:5, fontSize:10.5, color:"#9ca3af" }}>
                        Les deux boutons reprennent les liens du hero : un seul endroit à corriger.
                      </div>
                    </div>
                  </div>

                  {/* ── Espace DRH ── */}
                  <div style={{ background:"#f8fafc", border:"1px solid #e5e7eb", borderRadius:12, padding:"18px 20px" }}>
                    <div style={{ fontSize:13, fontWeight:800, color:"#0f172a", marginBottom:3 }}>👔 Espace DRH</div>
                    <p style={{ margin:"0 0 14px", fontSize:11, color:"#9ca3af" }}>
                      Accès conseil vers une personne nommée — volontairement distinct des formulaires.
                    </p>
                    <label style={lblBfb}>Titre</label>
                    <input value={bfbCont.drh?.titre || ""} onChange={e => champDrh("titre", e.target.value)} style={inpBfb} />
                    <label style={{ ...lblBfb, marginTop:10 }}>Texte</label>
                    <textarea value={bfbCont.drh?.texte || ""} rows={3} onChange={e => champDrh("texte", e.target.value)}
                      style={{ ...inpBfb, resize:"vertical", fontFamily:"inherit" }} />
                    <div style={{ marginTop:10 }}>
                      <label style={lblBfb}>Libellé du bouton</label>
                      <input value={bfbCont.drh?.bouton || ""} onChange={e => champDrh("bouton", e.target.value)} style={inpBfb} />
                      {/* PLUS DE CHAMP TÉLÉPHONE ICI. Le bouton ouvre désormais le choix des six
                          assistantes Corporate, il ne mène plus à un numéro unique : garder la
                          case aurait laissé croire qu'elle pilote encore quelque chose. */}
                      <div style={{ marginTop:5, fontSize:10.5, color:"#9ca3af" }}>
                        Ce bouton ouvre la liste des assistantes Corporate — le visiteur choisit son interlocuteur.
                      </div>
                    </div>
                  </div>

                  {/* ── Crédibilité ── */}
                  <div style={{ background:"#f8fafc", border:"1px solid #e5e7eb", borderRadius:12, padding:"18px 20px" }}>
                    <div style={{ fontSize:13, fontWeight:800, color:"#0f172a", marginBottom:3 }}>🏛️ Bandeau crédibilité</div>
                    <p style={{ margin:"0 0 14px", fontSize:11, color:"#b45309" }}>
                      N'y portez que des faits vérifiables : ce bandeau est exactement ce qu'un DRH contrôle.
                    </p>
                    {(bfbCont.credibilite || []).map((c, i) => (
                      <div key={i} style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr auto", gap:10, marginBottom:9, alignItems:"end" }}>
                        <div><label style={lblBfb}>Valeur</label><input value={c.valeur || ""} onChange={e => champCred(i, "valeur", e.target.value)} style={inpBfb} /></div>
                        <div><label style={lblBfb}>Libellé</label><input value={c.label || ""} onChange={e => champCred(i, "label", e.target.value)} style={inpBfb} /></div>
                        <div><label style={lblBfb}>Précision</label><input value={c.detail || ""} onChange={e => champCred(i, "detail", e.target.value)} style={inpBfb} /></div>
                        <button onClick={() => setBfbCont(x => ({ ...x, credibilite: x.credibilite.filter((_, j) => j !== i) }))}
                          title="Retirer" style={{ padding:"8px 11px", borderRadius:8, border:"1px solid #fecaca", background:"#fef2f2", color:"#b91c1c", cursor:"pointer", fontSize:12 }}>🗑</button>
                      </div>
                    ))}
                    <button onClick={() => setBfbCont(x => ({ ...x, credibilite: [...(x.credibilite || []), { valeur:"", label:"", detail:"" }] }))}
                      style={{ marginTop:4, padding:"7px 14px", borderRadius:8, border:"1px dashed #cbd5e1", background:"#fff", color:"#334155", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                      ＋ Ajouter une mention
                    </button>
                  </div>

                  <div style={{ display:"flex", justifyContent:"flex-end" }}>
                    <button onClick={enregistrerBfbContenus} disabled={bfbContSaving}
                      style={{ padding:"10px 22px", borderRadius:9, border:"none", color:"#fff", fontWeight:800, fontSize:12.5,
                        cursor: bfbContSaving ? "wait" : "pointer", background: bfbContSaving ? "#9ca3af" : "#0c33ac" }}>
                      {bfbContSaving ? "⏳ Enregistrement…" : "💾 Enregistrer les contenus"}
                    </button>
                  </div>

                  {/* ── Les preuves : photos de la cérémonie et vidéo ── */}
                  <div style={{ background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:10, padding:"11px 15px", fontSize:12, color:"#1e40af" }}>
                    🖼 <strong>Photos et vidéo de l'étude de cas.</strong> Elles s'affichent sous les textes ci-dessus.
                    Tant qu'aucune n'est déposée, la galerie n'apparaît pas — une section « preuves » vide dit le
                    contraire de ce qu'elle annonce.
                  </div>
                  {renderOffreMediaPanel("bet_business_wave", "#0c33ac", "Étude de cas")}
                </div>
                )
              )}

              {/* ══════════════ FAQ DE LA PAGE ══════════════ */}
              {bfbSubTab === "faq" && (() => {
                /* MÊME TABLE, MÊME CRUD que l'écran FAQ général — simplement filtré sur la
                   catégorie de cette page. Un second système aurait garanti deux FAQ qui
                   divergent, et c'est exactement ce qu'on vient de réparer : les cinq
                   questions de /bet-for-business vivaient dans le code, invisibles ici. */
                const items = faqItems
                  .filter(f => f.categorie === CATEGORIE_BFB)
                  .sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0));
                return (
                  <div style={{ marginTop:22 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, marginBottom:14, flexWrap:"wrap" }}>
                      <div>
                        <h4 style={{ margin:0, fontSize:13, fontWeight:800, color:"#0f172a" }}>❓ Questions fréquentes de la page</h4>
                        <p style={{ margin:"3px 0 0", fontSize:11, color:"#9ca3af" }}>
                          Catégorie « {CATEGORIE_BFB} » — séparée d'« Entreprises », qui alimente la page /faq.
                        </p>
                      </div>
                      <button onClick={() => setFaqForm({ question:"", reponse:"", categorie: CATEGORIE_BFB, ordre: items.length + 1, actif:true })}
                        style={{ padding:"9px 18px", borderRadius:9, border:"none", background:"#0c33ac", color:"#fff", fontSize:12.5, fontWeight:800, cursor:"pointer" }}>
                        ＋ Ajouter une question
                      </button>
                    </div>

                    {faqForm && faqForm.categorie === CATEGORIE_BFB && (
                      <div style={{ background:"#f8fafc", border:"1px solid #e5e7eb", borderRadius:12, padding:"16px 18px", marginBottom:16 }}>
                        <div style={{ fontSize:12, fontWeight:800, color:"#0f172a", marginBottom:12 }}>
                          {faqForm.id ? "Modifier la question" : "Nouvelle question"}
                        </div>
                        <label style={lblBfb}>Question</label>
                        <input value={faqForm.question} onChange={e => setFaqForm(f => ({ ...f, question:e.target.value }))}
                          placeholder="Le financement FDFP est-il vraiment accessible ?" style={inpBfb} />
                        <label style={{ ...lblBfb, marginTop:10 }}>Réponse</label>
                        <textarea value={faqForm.reponse} onChange={e => setFaqForm(f => ({ ...f, reponse:e.target.value }))} rows={4}
                          style={{ ...inpBfb, resize:"vertical", fontFamily:"inherit" }} />
                        <div style={{ display:"flex", alignItems:"flex-end", gap:16, marginTop:12, flexWrap:"wrap" }}>
                          <div style={{ width:120 }}>
                            <label style={lblBfb}>Ordre</label>
                            <input type="number" min={1} value={faqForm.ordre ?? 1}
                              onChange={e => setFaqForm(f => ({ ...f, ordre:e.target.value }))} style={inpBfb} />
                          </div>
                          <label style={{ display:"flex", alignItems:"center", gap:7, fontSize:12, color:"#334155", fontWeight:600 }}>
                            <input type="checkbox" checked={faqForm.actif !== false}
                              onChange={e => setFaqForm(f => ({ ...f, actif:e.target.checked }))} />
                            Visible sur la page
                          </label>
                          <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
                            <button onClick={() => setFaqForm(null)}
                              style={{ padding:"9px 18px", borderRadius:9, border:"1px solid #e2e8f0", background:"#fff", color:"#334155", fontSize:12.5, fontWeight:700, cursor:"pointer" }}>
                              Annuler
                            </button>
                            <button onClick={() => saveFaq({ ...faqForm, categorie: CATEGORIE_BFB })}
                              disabled={!faqForm.question?.trim() || !faqForm.reponse?.trim() || faqSavingId}
                              style={{ padding:"9px 20px", borderRadius:9, border:"none", color:"#fff", fontSize:12.5, fontWeight:800,
                                cursor: faqSavingId ? "wait" : "pointer",
                                background: (!faqForm.question?.trim() || !faqForm.reponse?.trim()) ? "#cbd5e1" : "#0c33ac" }}>
                              {faqSavingId ? "⏳…" : "💾 Enregistrer"}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {faqLoading ? (
                      <div style={{ fontSize:12, color:"#94a3b8", padding:"14px 0" }}>Chargement…</div>
                    ) : items.length === 0 ? (
                      <div style={{ background:"#f8fafc", border:"1px dashed #cbd5e1", borderRadius:12, padding:"26px 20px", textAlign:"center", fontSize:12.5, color:"#64748b" }}>
                        Aucune question dans cette catégorie.
                        <div style={{ marginTop:6, fontSize:11.5, color:"#94a3b8" }}>
                          La page affiche alors ses cinq questions d'origine, écrites dans le code.
                          Exécutez <code>Backend/sql/faq_bet_for_business.sql</code> pour les reprendre ici.
                        </div>
                      </div>
                    ) : (
                      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                        {items.map(f => (
                          <div key={f.id} style={{ background:"#fff", border:"1px solid #e5e7eb", borderLeft:`3px solid ${f.actif ? "#15803d" : "#cbd5e1"}`, borderRadius:10, padding:"12px 15px" }}>
                            <div style={{ display:"flex", justifyContent:"space-between", gap:12, alignItems:"flex-start" }}>
                              <div style={{ minWidth:0, flex:1 }}>
                                <div style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>
                                  <span style={{ color:"#94a3b8", marginRight:6 }}>{f.ordre}.</span>{f.question}
                                </div>
                                <div style={{ fontSize:11.5, color:"#64748b", marginTop:4, lineHeight:1.5 }}>{f.reponse}</div>
                              </div>
                              <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                                <button onClick={() => setFaqForm({ ...f })} title="Modifier"
                                  style={{ padding:"5px 9px", borderRadius:7, border:"1px solid #e2e8f0", background:"#fff", color:"#334155", cursor:"pointer", fontSize:12 }}>✏️</button>
                                <button onClick={() => toggleFaqActif(f)} title={f.actif ? "Masquer" : "Afficher"}
                                  style={{ padding:"5px 9px", borderRadius:7, border:"1px solid #e2e8f0", background:"#fff", cursor:"pointer", fontSize:12 }}>{f.actif ? "✅" : "🚫"}</button>
                                <button onClick={() => deleteFaq(f.id)} title="Supprimer"
                                  style={{ padding:"5px 9px", borderRadius:7, border:"1px solid #fecaca", background:"#fef2f2", color:"#b91c1c", cursor:"pointer", fontSize:12 }}>🗑</button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {activeTab === "messages" && (
            <div style={{ background:"#fff", padding:24, borderRadius:"0 12px 12px 12px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)" }}>
              <MessagerieTab />
            </div>
          )}
          {activeTab === "notifications" && (
            <div style={{ background:"#fff", padding:24, borderRadius:"0 12px 12px 12px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)" }}>
              <NotificationsTab
                userId={JSON.parse(localStorage.getItem("admin_profil") || "null")?.id}
                accentColor="#0f172a"
              />
            </div>
          )}
          {activeTab === "messages_old_DISABLED" && (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                <div>
                  <h2 style={{ margin:0, fontSize:17, fontWeight:800 }}>💬 Messagerie interne</h2>
                  <p style={{ margin:"3px 0 0", fontSize:12, color:"#9ca3af" }}>Échangez directement avec tous les profils de la plateforme BET.</p>
                </div>
                <button onClick={() => { fetchMsgContacts(); setShowNewConv(true); }}
                  style={{ padding:"9px 18px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:13 }}>
                  ✏️ Nouvelle conversation
                </button>
              </div>

              {/* Modal sélection contact */}
              {showNewConv && (
                <ModaleAnimee ouvert
                  style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center" }}
                  panneauStyle={{ background:"#fff", borderRadius:16, padding:28, width:440, maxHeight:"90vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.25)" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
                      <h3 style={{ margin:0, fontSize:16, fontWeight:800 }}>Choisir un contact</h3>
                      <button onClick={() => setShowNewConv(false)} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer" }}>✕</button>
                    </div>
                    {msgContacts.length === 0 ? (
                      <div style={{ textAlign:"center", padding:30, color:"#9ca3af", fontSize:13 }}>⏳ Chargement…</div>
                    ) : (
                      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                        {msgContacts.map(c => (
                          <div key={c.id} onClick={() => startConv(c.id)} style={{
                            display:"flex", alignItems:"center", gap:12, padding:"10px 14px",
                            borderRadius:10, border:"1px solid #e5e7eb", cursor:"pointer",
                          }}
                            onMouseEnter={e => e.currentTarget.style.background="#f0f9ff"}
                            onMouseLeave={e => e.currentTarget.style.background="#fff"}
                          >
                            <div style={{ width:38, height:38, borderRadius:"50%", background:BET_COLOR, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:12, flexShrink:0 }}>
                              {(c.prenom?.[0]||"")+(c.nom?.[0]||"")}
                            </div>
                            <div style={{ flex:1 }}>
                              <div style={{ fontWeight:700, fontSize:13, color:"#0f172a" }}>{c.prenom} {c.nom}</div>
                              <div style={{ fontSize:11, color:"#9ca3af" }}>{ROLE_META_SA[c.role]||c.role} · {c.email}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                </ModaleAnimee>
              )}

              {/* Layout messagerie */}
              <div style={{ display:"flex", gap:0, height:580, border:"1px solid #e5e7eb", borderRadius:14, overflow:"hidden", background:"#fff" }}>
                {/* Liste conversations */}
                <div style={{ width:270, borderRight:"1px solid #e5e7eb", display:"flex", flexDirection:"column", background:"#fafafa" }}>
                  <div style={{ padding:"12px 14px", borderBottom:"1px solid #e5e7eb", fontSize:12, fontWeight:700, color:"#374151" }}>
                    Conversations {msgNonLuTotal > 0 && <span style={{ marginLeft:6, padding:"1px 7px", borderRadius:10, background:"#ef4444", color:"#fff", fontSize:10, fontWeight:700 }}>{msgNonLuTotal}</span>}
                  </div>
                  <div style={{ flex:1, overflowY:"auto" }}>
                    {msgConvs.length === 0 ? (
                      <div style={{ padding:20, fontSize:12, color:"#9ca3af", textAlign:"center", marginTop:40 }}>
                        Aucune conversation.<br/>Cliquez sur "Nouvelle conversation"
                      </div>
                    ) : (
                      msgConvs.map(conv => {
                        const isActive = msgActiveId === conv.id;
                        const name = saPartnerName(conv);
                        const role = saPartnerRole(conv);
                        const initiales = name.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase();
                        return (
                          <div key={conv.id} onClick={() => { setMsgActiveId(conv.id); fetchMsgMessages(conv.id); }} style={{
                            padding:"12px 14px", borderBottom:"1px solid #f1f5f9", cursor:"pointer",
                            background: isActive ? "#e0f2fe" : "transparent", display:"flex", alignItems:"center", gap:10,
                          }}>
                            <div style={{ width:36, height:36, borderRadius:"50%", background:BET_COLOR, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, flexShrink:0 }}>{initiales}</div>
                            <div style={{ minWidth:0, flex:1 }}>
                              <div style={{ fontSize:12, fontWeight:700, color:"#0f172a" }}>{name}</div>
                              <div style={{ fontSize:10, color:"#9ca3af" }}>{ROLE_META_SA[role]||role}</div>
                              {conv.last_message && <div style={{ fontSize:10, color:"#9ca3af", overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis", marginTop:1 }}>{conv.last_message}</div>}
                            </div>
                            {conv.non_lus > 0 && (
                              <span style={{ background:"#ef4444", color:"#fff", borderRadius:99, fontSize:9, fontWeight:800, padding:"2px 6px", flexShrink:0 }}>{conv.non_lus}</span>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Zone messages */}
                {msgActiveConv ? (
                  <div style={{ flex:1, display:"flex", flexDirection:"column" }}>
                    <div style={{ padding:"12px 18px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", gap:12, background:"#fff" }}>
                      <div style={{ width:36, height:36, borderRadius:"50%", background:BET_COLOR, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800 }}>
                        {saPartnerName(msgActiveConv).split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontSize:13, fontWeight:700 }}>{saPartnerName(msgActiveConv)}</div>
                        <div style={{ fontSize:11, color:"#9ca3af" }}>{ROLE_META_SA[saPartnerRole(msgActiveConv)]||saPartnerRole(msgActiveConv)}</div>
                      </div>
                    </div>
                    <div style={{ flex:1, overflowY:"auto", padding:16, display:"flex", flexDirection:"column", gap:10, background:"#f8fafc" }}>
                      {msgMessages.length === 0 && (
                        <div style={{ textAlign:"center", marginTop:60, color:"#9ca3af", fontSize:13 }}>Commencez la conversation !</div>
                      )}
                      {msgMessages.map((msg, i) => {
                        const isMe = msg.from_id === saMyId;
                        return (
                          <div key={i} style={{ display:"flex", justifyContent: isMe ? "flex-end" : "flex-start" }}>
                            <div style={{
                              maxWidth:"70%", padding:"10px 14px", fontSize:12,
                              borderRadius: isMe ? "12px 12px 0 12px" : "12px 12px 12px 0",
                              background: isMe ? BET_COLOR : "#fff",
                              color: isMe ? "#fff" : "#111827", boxShadow:"0 1px 4px rgba(0,0,0,0.07)"
                            }}>
                              {!isMe && <div style={{ fontSize:10, fontWeight:700, marginBottom:3, opacity:.7 }}>{msg.from_name}</div>}
                              <div style={{ lineHeight:1.5 }}>{msg.content}</div>
                              <div style={{ fontSize:9, marginTop:4, opacity:.6, textAlign:"right" }}>
                                {new Date(msg.created_at).toLocaleTimeString("fr-FR", { hour:"2-digit", minute:"2-digit" })}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ padding:"12px 16px", borderTop:"1px solid #e5e7eb", display:"flex", gap:8, background:"#fff" }}>
                      <input
                        value={msgInput}
                        onChange={e => setMsgInput(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMsg()}
                        placeholder="Écrire un message…"
                        style={{ flex:1, padding:"9px 14px", borderRadius:20, border:"1px solid #e5e7eb", fontSize:12, outline:"none" }}
                      />
                      <button onClick={sendMsg} style={{ padding:"9px 18px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:20, cursor:"pointer", fontWeight:700, fontSize:12 }}>Envoyer</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:12, color:"#9ca3af" }}>
                    <div style={{ fontSize:44 }}>💬</div>
                    <div style={{ fontSize:14, fontWeight:600 }}>Sélectionnez une conversation</div>
                    <div style={{ fontSize:12 }}>ou démarrez-en une nouvelle via le bouton ci-dessus</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ══ PLANNING ASSISTANTES ══ */}
          {/* ════ ADMINISTRATION BET ════ */}
          {activeTab === "administration" && (() => {
            const allRoles = [...new Set(users.map(u => u.role))].sort((a, b) => {
              const na = ROLES_DEF[a]?.niveau ?? 0, nb = ROLES_DEF[b]?.niveau ?? 0;
              return nb - na;
            });

            const filteredUsers = users
              .filter(u => adminRoleFilter === "tous" || u.role === adminRoleFilter)
              .filter(u => adminSearch === "" || `${u.prenom||""} ${u.nom||""} ${u.email||""}`.toLowerCase().includes(adminSearch.toLowerCase()));

            const nbActifs    = users.filter(u => u.actif !== false).length;
            const nbInactifs  = users.filter(u => u.actif === false).length;
            const nbTwofa     = users.filter(u => u.twofa).length;

            const STATS = [
              { label:"Total profils", val:users.length,  color:BET_COLOR,  bg:"#e0f2fe" },
              { label:"Actifs",        val:nbActifs,       color:"#16a34a",  bg:"#dcfce7" },
              { label:"Inactifs",      val:nbInactifs,     color:"#dc2626",  bg:"#fee2e2" },
              { label:"2FA activé",    val:nbTwofa,        color:"#7c3aed",  bg:"#ede9fe" },
            ];

            return (
              <div style={{ padding:"24px 28px 40px" }}>

                {/* ── En-tête ── */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20, flexWrap:"wrap", gap:12 }}>
                  <div>
                    <div style={{ fontSize:20, fontWeight:800, color:"#0f172a", marginBottom:3 }}>🏛️ Administration BET</div>
                    <div style={{ fontSize:13, color:"#64748b" }}>{filteredUsers.length} profil{filteredUsers.length !== 1 ? "s" : ""} affiché{filteredUsers.length !== 1 ? "s" : ""} · {users.length} au total</div>
                  </div>
                  <input
                    value={adminSearch}
                    onChange={e => setAdminSearch(e.target.value)}
                    placeholder="🔍 Rechercher un profil…"
                    style={{ padding:"9px 14px", borderRadius:8, border:"1.5px solid #e2e8f0", fontSize:13, outline:"none", minWidth:260, fontFamily:"inherit" }}
                  />
                </div>

                {/* ── Statistiques ── */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:20 }}>
                  {STATS.map(s => (
                    <div key={s.label} style={{ background:"#fff", borderRadius:12, padding:"14px 18px", border:"1px solid #e2e8f0", boxShadow:"0 1px 3px rgba(0,0,0,0.04)" }}>
                      <div style={{ fontSize:22, fontWeight:900, color:s.color }}>{s.val}</div>
                      <div style={{ fontSize:11, color:"#94a3b8", fontWeight:600, marginTop:2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* ── Filtres par rôle (chips) ── */}
                <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:16 }}>
                  <button
                    onClick={() => setAdminRoleFilter("tous")}
                    style={{ padding:"6px 14px", borderRadius:20, border:`1.5px solid ${adminRoleFilter === "tous" ? BET_COLOR : "#e2e8f0"}`, background: adminRoleFilter === "tous" ? BET_COLOR : "#fff", color: adminRoleFilter === "tous" ? "#fff" : "#64748b", fontSize:12, fontWeight:700, cursor:"pointer", transition:"all .15s" }}>
                    Tous · {users.length}
                  </button>
                  {allRoles.map(role => {
                    const rd  = ROLES_DEF[role] || { label:role, emoji:"👤", color:"#64748b" };
                    const cnt = users.filter(u => u.role === role).length;
                    const isA = adminRoleFilter === role;
                    return (
                      <button key={role} onClick={() => setAdminRoleFilter(role)}
                        style={{ padding:"6px 14px", borderRadius:20, border:`1.5px solid ${isA ? rd.color : "#e2e8f0"}`, background: isA ? rd.color : "#fff", color: isA ? "#fff" : "#64748b", fontSize:12, fontWeight:700, cursor:"pointer", transition:"all .15s", display:"flex", alignItems:"center", gap:5 }}>
                        <span>{rd.emoji}</span><span>{rd.label}</span>
                        <span style={{ background: isA ? "rgba(255,255,255,0.25)" : "#f1f5f9", color: isA ? "#fff" : "#64748b", borderRadius:20, padding:"0 6px", fontSize:11 }}>{cnt}</span>
                      </button>
                    );
                  })}
                </div>

                {/* ── Tableau des profils ── */}
                <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", overflow:"hidden", boxShadow:"0 2px 8px rgba(0,0,0,0.04)" }}>

                  {/* En-tête colonnes */}
                  <div style={{ display:"grid", gridTemplateColumns:"2.2fr 1.6fr 2fr 1.1fr 1.1fr 1fr", padding:"10px 20px", background:"#f8fafc", borderBottom:"1px solid #e2e8f0", fontSize:11, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.05em" }}>
                    <span>Collaborateur</span>
                    <span>Rôle</span>
                    <span>Email</span>
                    <span>Périmètre</span>
                    <span>2FA</span>
                    <span>Statut</span>
                  </div>

                  {filteredUsers.length === 0 && (
                    <div style={{ padding:"56px 20px", textAlign:"center", color:"#94a3b8" }}>
                      <div style={{ fontSize:36, marginBottom:12 }}>🏛️</div>
                      <div style={{ fontSize:15, fontWeight:700, marginBottom:6 }}>Aucun profil trouvé</div>
                      <div style={{ fontSize:13 }}>Modifiez votre recherche ou le filtre de rôle</div>
                    </div>
                  )}

                  {filteredUsers.map((u, idx) => {
                    const rd      = ROLES_DEF[u.role] || { label:u.role, emoji:"👤", color:"#64748b", niveau:0 };
                    const initls  = ((u.prenom?.[0] || "") + (u.nom?.[0] || "")).toUpperCase() || "?";
                    const isActif = u.actif !== false;
                    const centre  = u.scope?.length > 0 ? (u.scope.length > 1 ? `${u.scope.length} centres` : u.scope[0]) : "National";

                    return (
                      <div
                        key={u.id}
                        style={{ display:"grid", gridTemplateColumns:"2.2fr 1.6fr 2fr 1.1fr 1.1fr 1fr", padding:"13px 20px", borderBottom: idx < filteredUsers.length - 1 ? "1px solid #f1f5f9" : "none", alignItems:"center", cursor:"pointer", transition:"background .12s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        onClick={() => setAdminDetail(u)}
                      >
                        {/* Collaborateur */}
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <div style={{ width:36, height:36, borderRadius:"50%", background:rd.color+"20", border:`2px solid ${rd.color}40`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:800, color:rd.color, flexShrink:0 }}>
                            {rd.emoji || initls}
                          </div>
                          <div>
                            <div style={{ fontWeight:700, color:"#0f172a", fontSize:13 }}>{u.prenom} {u.nom}</div>
                            <div style={{ fontSize:11, color:"#94a3b8", marginTop:1 }}>Niveau {rd.niveau ?? "-"}</div>
                          </div>
                        </div>

                        {/* Rôle */}
                        <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", borderRadius:20, background:rd.color+"18", color:rd.color, fontSize:11, fontWeight:700, width:"fit-content" }}>
                          {rd.emoji} {rd.label}
                        </span>

                        {/* Email */}
                        <span style={{ fontSize:12, color:BET_COLOR, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{u.email}</span>

                        {/* Périmètre */}
                        <span style={{ fontSize:12, color:"#64748b", fontWeight:600 }}>{centre}</span>

                        {/* 2FA */}
                        <span style={{ fontSize:12, fontWeight:700, color: u.twofa ? "#16a34a" : "#94a3b8" }}>
                          {u.twofa ? "✅ Oui" : "-"}
                        </span>

                        {/* Statut */}
                        <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", borderRadius:20, background: isActif ? "#d1fae5" : "#fee2e2", color: isActif ? "#065f46" : "#991b1b", fontSize:11, fontWeight:700 }}>
                          <span style={{ width:7, height:7, borderRadius:"50%", background: isActif ? "#16a34a" : "#dc2626", display:"inline-block" }} />
                          {isActif ? "Actif" : "Inactif"}
                        </span>

                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {activeTab === "assistantes" && (() => {
            const JOURS = ["lundi","mardi","mercredi","jeudi","vendredi","samedi","dimanche"];
            const JOURS_COURT = { lundi:"L", mardi:"M", mercredi:"Me", jeudi:"J", vendredi:"V", samedi:"S", dimanche:"D" };
            const jourCourant = (() => {
              const j = ["dimanche","lundi","mardi","mercredi","jeudi","vendredi","samedi"];
              return j[new Date().getDay()];
            })();
            const TYPE_LABEL = { en_ligne:"💻 En ligne", presentiel:"🏢 Présentiel", les_deux:"🔀 Les deux" };

            return (
              <div style={{ width:"100%", padding:"0 24px" }}>
                {/* Header */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16, flexWrap:"wrap", gap:10 }}>
                  <div>
                    <h2 style={{ margin:"0 0 4px", fontSize:17, fontWeight:800, color:"#0f172a" }}>📅 Planning des assistantes</h2>
                    <p style={{ margin:0, fontSize:12, color:"#6b7280", maxWidth:520 }}>
                      Modifiez les jours de service des assistantes. Seules les assistantes actives présentes aujourd'hui ({jourCourant}) sont proposées aux prospects.
                    </p>
                  </div>
                  <div style={{ display:"flex", gap:8, flexShrink:0 }}>
                    <button disabled={!peut("roles","manage")} title={permBtnTitle(assistanteProfilFilter==="b2b"?"Nouvelle assistante Corporate":"Nouvelle assistante",peut("roles","manage"))} onClick={()=>{ setInviteForm(f=>({...f,profil_assistante:assistanteProfilFilter==="b2b"?"b2b":"b2c"})); setShowInviteModal(true); }}
                      style={permBtnStyle({ padding:"8px 14px", background: assistanteProfilFilter==="b2b" ? "#eff6ff" : "#f0fdf4", color: assistanteProfilFilter==="b2b" ? "#1d4ed8" : "#166534", border: `2px solid ${assistanteProfilFilter==="b2b" ? "#bfdbfe" : "#bbf7d0"}`, borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 },peut("roles","manage"))}>
                      {!peut("roles","manage")?"🔒 Nouvelle assistante":assistanteProfilFilter==="b2b" ? "➕ Nouvelle assistante Corporate" : "➕ Nouvelle assistante"}
                    </button>
                    <button onClick={fetchAssistantesAdmin} style={{ padding:"8px 14px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:12 }}>
                      🔄 Actualiser
                    </button>
                  </div>
                </div>
                <div style={{ marginBottom:16, padding:"10px 14px", borderRadius:10, background:"#fffbeb", border:"1px solid #fde68a", fontSize:12, color:"#92400e" }}>
                  💡 Pour <strong>créer une assistante</strong> (B2C ou Corporate B2B), allez dans <strong>Gestion des droits → Utilisateurs → Créer</strong> et choisissez le rôle <strong>Commercial</strong> - sélectionnez ensuite le profil <strong>B2C</strong> ou <strong>Corporate B2B</strong> dans le planning.
                </div>

                {/* Légende */}
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:18, padding:"10px 14px", background:"#eff6ff", borderRadius:10, border:"1px solid #bae6fd", fontSize:12, color:"#1e40af", flexWrap:"wrap" }}>
                  <span style={{ fontWeight:700 }}>📍 Aujourd'hui :</span>
                  <span style={{ background:"#1e3a8a", color:"#fff", borderRadius:999, padding:"3px 10px", fontWeight:800, textTransform:"capitalize" }}>{jourCourant}</span>
                  <span style={{ color:"#64748b" }}>Les assistantes absentes ce jour ne sont pas visibles sur le site.</span>
                </div>

                {/* Toggle B2C / Corporate */}
                <div style={{ display:"flex", gap:8, marginBottom:16, alignItems:"center" }}>
                  <span style={{ fontSize:12, fontWeight:700, color:"#374151", marginRight:4 }}>Afficher :</span>
                  {[{v:"b2c",l:"👤 Commerciales B2C"},{v:"b2b",l:"🏢 Corporate B2B"}].map(opt=>(
                    <button key={opt.v} onClick={()=>setAssistanteProfilFilter(opt.v)}
                      style={{ padding:"7px 16px", borderRadius:999, border:`2px solid ${assistanteProfilFilter===opt.v ? (opt.v==="b2b"?"#2563eb":"#0891b2") : "#e5e7eb"}`,
                        background: assistanteProfilFilter===opt.v ? (opt.v==="b2b"?"#eff6ff":"#e0f2fe") : "#fff",
                        color: assistanteProfilFilter===opt.v ? (opt.v==="b2b"?"#1d4ed8":"#0891b2") : "#374151",
                        fontWeight: assistanteProfilFilter===opt.v ? 800 : 600, fontSize:12, cursor:"pointer", transition:"all .15s" }}>
                      {opt.l}
                    </button>
                  ))}
                </div>

                {(() => {
                  const CENTRES_CONFIG = [
                    { nom:"Angré",         couleur:"#0891b2" },
                    { nom:"II Plateaux",   couleur:"#6366f1" },
                    { nom:"Yopougon",      couleur:"#a855f7" },
                    { nom:"Koumassi",      couleur:"#f97316" },
                    { nom:"Bouaké",        couleur:"#eab308" },
                    { nom:"Abatta",        couleur:"#ef4444" },
                    { nom:"__sans_centre", couleur:"#0891b2", label:"💻 En ligne" },
                  ];

                  // Filtrer par profil sélectionné (b2c ou b2b) - "les_deux" apparaît dans les deux vues
                  const assistantesFiltrees = assistantesAdmin.filter(a => {
                    const p = a.profil || "b2c";
                    if (assistanteProfilFilter === "b2b") return p === "b2b" || p === "les_deux";
                    return p === "b2c" || p === "les_deux";
                  });

                  // Grouper par centre_nom - on retire le préfixe "BET " pour matcher CENTRES_CONFIG
                  const normaliseCentre = (nom) => (nom || "").replace(/^BET\s+/i, "") || "__sans_centre";
                  const grouped = {};
                  assistantesFiltrees.forEach(a => {
                    const key = normaliseCentre(a.centre_nom);
                    if (!grouped[key]) grouped[key] = [];
                    grouped[key].push(a);
                  });

                  const cfg = CENTRES_CONFIG.find(c => c.nom === centreTab) || CENTRES_CONFIG[0];
                  const liste = grouped[cfg.nom] || [];
                  const centreColor = cfg.couleur;

                  const renderCard = (a) => {
                    const da = getDraftedA(a);
                    const jours = da.jours_travail || ["lundi","mardi","mercredi","jeudi","vendredi"];
                    const travailleAujourdHui = jours.includes(jourCourant);
                    const initiales = `${(a.prenom||"")[0]||""}${(a.nom||"")[0]||""}`.toUpperCase();
                    const hasDraft = !!(assistantesDrafts[a.id] && Object.keys(assistantesDrafts[a.id]).length > 0);
                    const isSaving = !!assistantesSaving[a.id];
                    // photo : priorité photo_url de la table assistantes, sinon avatar_url du compte utilisateur lié
                    const userLie = a.email ? users.find(u => u.email === a.email) : null;
                    const photoSrc = a.photo_url || userLie?.avatar_url || null;
                    return (
                      <div key={a.id} style={{ background:"#fff", borderRadius:12, border:`2px solid ${hasDraft ? "#fde68a" : travailleAujourdHui && da.actif ? `${centreColor}40` : "#e5e7eb"}`, overflow:"hidden" }}>
                        <div style={{ height:3, background: hasDraft ? "#f59e0b" : travailleAujourdHui && da.actif ? centreColor : "#e5e7eb" }} />
                        <div style={{ padding:"14px 18px" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                            <div style={{ position:"relative", flexShrink:0 }}>
                              <input type="file" accept="image/*" id={`photo-assist-${a.id}`} style={{ display:"none" }}
                                onChange={async e => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const fd = new FormData(); fd.append("file", file);
                                  try {
                                    const r = await fetchUpload(`${API_URL}/api/upload/avatar`, { method:"POST", headers: authHeaders(), body: fd });
                                    const d = await r.json();
                                    const url = d.file?.url; if (!url) return;
                                    await fetch(`${API_URL}/api/parcours/assistantes/${a.id}`, { method:"PATCH", headers:{ ...authHeaders(), "Content-Type":"application/json" }, body: JSON.stringify({ photo_url: url }) });
                                    setAssistantesAdmin(prev => prev.map(x => x.id === a.id ? { ...x, photo_url: url } : x));
                                    toast.success("Photo mise à jour ✓");
                                  } catch { toast.error("Erreur upload photo"); }
                                  e.target.value = "";
                                }}
                              />
                              <div title="Changer la photo" onClick={() => document.getElementById(`photo-assist-${a.id}`).click()}
                                style={{ cursor:"pointer", position:"relative", width:42, height:42 }}>
                                {photoSrc
                                  ? <img src={photoSrc} alt={a.prenom} style={{ width:42, height:42, borderRadius:"50%", objectFit:"cover", display:"block" }}/>
                                  : <div style={{ width:42, height:42, borderRadius:"50%", background:`linear-gradient(135deg,#0f172a,${centreColor})`, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:13 }}>{initiales}</div>
                                }
                                <div style={{ position:"absolute", inset:0, borderRadius:"50%", background:"rgba(0,0,0,0.35)", display:"flex", alignItems:"center", justifyContent:"center", opacity:0, transition:"opacity .15s" }}
                                  onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=0}>
                                  <span style={{ fontSize:14 }}>📷</span>
                                </div>
                              </div>
                              <div style={{ position:"absolute", bottom:-1, right:-1, width:12, height:12, borderRadius:"50%", background: travailleAujourdHui && da.actif ? "#22c55e" : "#d1d5db", border:"2px solid #fff" }} />
                            </div>
                            <div style={{ flex:1, minWidth:130 }}>
                              <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", marginBottom:2 }}>
                                <span style={{ fontWeight:800, fontSize:13, color:"#0f172a" }}>{a.prenom} {a.nom}</span>
                                <span style={{ fontSize:10, padding:"2px 7px", borderRadius:999, background:`${centreColor}18`, color:centreColor, fontWeight:700 }}>{TYPE_LABEL[da.type_cours] || da.type_cours}</span>
                                {(da.profil === "b2b" || da.profil === "les_deux") && (
                                  <span style={{ fontSize:10, padding:"2px 7px", borderRadius:999, background:"#eff6ff", color:"#2563eb", fontWeight:700 }}>
                                    {da.profil === "les_deux" ? "🔀 B2C+B2B" : "🏢 Corporate B2B"}
                                  </span>
                                )}
                                {!travailleAujourdHui && da.actif && <span style={{ fontSize:10, padding:"2px 6px", borderRadius:999, background:"#fef3c7", color:"#d97706", fontWeight:700 }}>⚠ Absente aujourd'hui</span>}
                                {!da.actif && <span style={{ fontSize:10, padding:"2px 6px", borderRadius:999, background:"#fee2e2", color:"#dc2626", fontWeight:700 }}>● Inactive</span>}
                              </div>
                              {a.email && <div style={{ fontSize:11, color:"#94a3b8" }}>{a.email}</div>}
                            </div>
                            <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0, flexWrap:"wrap" }}>
                              {/* Mode coaching */}
                              <div style={{ textAlign:"center" }}>
                                <div style={{ fontSize:10, color:"#9ca3af", marginBottom:3 }}>Mode</div>
                                <select value={da.type_cours || "en_ligne"}
                                  onChange={e => setDraft(a.id, { type_cours: e.target.value })}
                                  style={{ padding:"5px 7px", borderRadius:7, border:`1.5px solid ${hasDraft ? "#fde68a" : "#d1d5db"}`, fontSize:11, color:"#0f172a", cursor:"pointer", background:"#fff" }}>
                                  <option value="en_ligne">💻 En ligne</option>
                                  <option value="presentiel">🏢 Présentiel</option>
                                  {/* « Les deux » manquait ici alors que le reste du code le gère depuis
                                      longtemps : c'est le profil d'une assistante qui couvre les DEUX canaux,
                                      donc la seule qui puisse suivre une classe hybride de bout en bout.
                                      Aucune ne pouvait le recevoir, faute de cette seule ligne. */}
                                  <option value="les_deux">🔀 Les deux</option>
                                </select>
                              </div>
                              {/* Profil */}
                              <div style={{ textAlign:"center" }}>
                                <div style={{ fontSize:10, color:"#9ca3af", marginBottom:3 }}>Profil</div>
                                <select value={da.profil || "b2c"}
                                  onChange={e => setDraft(a.id, { profil: e.target.value })}
                                  style={{ padding:"5px 7px", borderRadius:7, border:`1.5px solid ${hasDraft ? "#fde68a" : "#d1d5db"}`, fontSize:11, color:"#0f172a", cursor:"pointer", background:"#fff" }}>
                                  <option value="b2c">👤 B2C</option>
                                  <option value="b2b">🏢 B2B</option>
                                  <option value="les_deux">🔀 Les deux</option>
                                </select>
                              </div>
                              {/* Actif */}
                              <div style={{ textAlign:"center" }}>
                                <div style={{ fontSize:10, color:"#9ca3af", marginBottom:5 }}>Actif site</div>
                                <ToggleSwitch on={!!da.actif} color={centreColor} onChange={() => setDraft(a.id, { actif: !da.actif })} />
                              </div>
                            </div>
                          </div>
                          <div style={{ marginTop:12, paddingTop:10, borderTop:"1px solid #f1f5f9" }}>
                            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                              <span style={{ fontSize:11, fontWeight:700, color:"#64748b" }}>Jours de service :</span>
                              <span style={{ fontSize:11, color:"#94a3b8" }}>{jours.length}j/sem</span>
                            </div>
                            <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                              {JOURS.map(jour => {
                                const actif = jours.includes(jour);
                                const estAujourd = jour === jourCourant;
                                return (
                                  <button key={jour} disabled={!peut("roles","manage")} onClick={() => toggleJourTravailDraft(a.id, jour)}
                                    title={permBtnTitle(jour.charAt(0).toUpperCase() + jour.slice(1),peut("roles","manage"))}
                                    style={{ width:32, height:32, borderRadius:999,
                                      border: estAujourd ? `2.5px solid ${centreColor}` : `1.5px solid ${actif ? "#22c55e" : "#e2e8f0"}`,
                                      background: actif ? (estAujourd ? centreColor : "#22c55e") : "#f8fafc",
                                      color: actif ? "#fff" : "#cbd5e1",
                                      fontWeight:800, fontSize:11, cursor:peut("roles","manage")?"pointer":"not-allowed", transition:"all .15s", flexShrink:0,
                                      opacity: peut("roles","manage") ? 1 : 0.45,
                                    }}
                                  >{JOURS_COURT[jour]}</button>
                                );
                              })}
                            </div>
                          </div>
                          {/* Assignation onboarding - visible pour en_ligne seulement.
                              GRISÉE POUR LE CORPORATE : une assistante B2B travaille seule, sans
                              onboarding. Grisée plutôt que masquée, pour que l'absence se lise
                              comme voulue et non comme un oubli d'affichage.
                              Identique au même bloc du SuperAdmin : les deux écrans montrent la
                              même fiche, et une seule des deux corrigée serait un piège. */}
                          {a.type_cours !== "presentiel" && (() => {
                          /* NI LE CORPORATE NI LE PA n'ont d'onboarding : la première
                             travaille seule sur ses comptes entreprises, le second gère le
                             coaching privé de bout en bout. Les huit comptes concernés sont
                             d'ailleurs exactement les huit sans onboarding rattaché - la case
                             n'a jamais été remplie parce qu'elle n'a pas de sens pour eux. */
                          const estCorporate = a.profil === "b2b" || a.profil === "pa";
                          const libelleProfil = a.profil === "pa" ? "PA Advisor" : "assistante Corporate";
                          return (
                          <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid #f1f5f9",
                            opacity: estCorporate ? 0.45 : 1, pointerEvents: estCorporate ? "none" : "auto" }}
                            title={estCorporate ? `${libelleProfil === "PA Advisor" ? "Les PA Advisors travaillent" : "Les assistantes Corporate travaillent"} sans onboarding.` : undefined}>
                            <div style={{ fontSize:10, color:"#9ca3af", marginBottom:6, fontWeight:600 }}>
                              🎯 Onboarding assignée
                              {estCorporate && (
                                <span style={{ marginLeft:6, fontWeight:700, color:"#64748b" }}>
                                  — sans objet pour {libelleProfil === "PA Advisor" ? "un" : "une"} {libelleProfil}, qui travaille seul{libelleProfil === "PA Advisor" ? "" : "e"}
                                </span>
                              )}
                            </div>
                            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:6 }}>
                              <button type="button" onClick={() => setDraft(a.id, { onboarding_id: null })}
                                style={{ textAlign:"left", padding:"7px 9px", borderRadius:8, cursor:"pointer",
                                  border:`2px solid ${!da.onboarding_id?centreColor:"#e2e8f0"}`,
                                  background:!da.onboarding_id?`${centreColor}14`:"#fff" }}>
                                <div style={{ fontSize:11, fontWeight:700, color:"#374151" }}>- Aucune assignation -</div>
                              </button>
                              {onboardingList.map(o => {
                                const selected = da.onboarding_id === o.id;
                                const initiales = `${o.prenom?.[0]||""}${o.nom?.[0]||""}`.toUpperCase();
                                const nb = o.nb_commerciaux_assignes || 0;
                                return (
                                  <button key={o.id} type="button" onClick={() => setDraft(a.id, { onboarding_id: o.id })}
                                    style={{ display:"flex", alignItems:"center", gap:7, textAlign:"left", padding:"6px 9px", borderRadius:8, cursor:"pointer",
                                      border:`2px solid ${selected?centreColor:"#e2e8f0"}`,
                                      background:selected?`${centreColor}14`:"#fff", opacity:o.actif===false?0.55:1 }}>
                                    <div style={{ width:26, height:26, borderRadius:"50%", flexShrink:0, background:o.couleur||centreColor, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:10 }}>
                                      {initiales || "?"}
                                    </div>
                                    <div style={{ minWidth:0 }}>
                                      <div style={{ fontSize:11, fontWeight:700, color:"#0f172a", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                                        {o.prenom} {o.nom} {o.actif===false && <span style={{ color:"#dc2626" }}>⏸</span>}
                                      </div>
                                      <div style={{ fontSize:9, color:"#64748b" }}>📞 {o.telephone || "-"} · {nb} suivi{nb>1?"es":"e"}</div>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          );
                          })()}
                          <div style={{ marginTop:12, paddingTop:10, borderTop:"1px solid #f1f5f9", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:6 }}>
                            <span style={{ fontSize:11, color: hasDraft ? "#d97706" : "#94a3b8", fontWeight: hasDraft ? 700 : 400 }}>
                              {hasDraft ? "⚠ Modifications non enregistrées" : "✓ À jour"}
                            </span>
                            <button onClick={() => saveDraftAssistante(a.id)} disabled={!hasDraft || isSaving || !peut("roles","manage")}
                              title={permBtnTitle("Enregistrer",peut("roles","manage"))}
                              style={{ padding:"7px 18px", borderRadius:8, border:"none", cursor: (hasDraft && !isSaving && peut("roles","manage")) ? "pointer" : "default",
                                background: hasDraft ? (isSaving ? "#e2e8f0" : centreColor) : "#f1f5f9",
                                color: hasDraft ? (isSaving ? "#94a3b8" : "#fff") : "#94a3b8",
                                fontWeight:700, fontSize:12, display:"flex", alignItems:"center", gap:5,
                                opacity: peut("roles","manage") ? 1 : 0.45, filter: peut("roles","manage") ? "none" : "grayscale(70%)",
                              }}
                            >
                              {isSaving ? <><div style={{ width:11,height:11,border:"2px solid #94a3b8",borderTopColor:centreColor,borderRadius:"50%",animation:"spin .7s linear infinite" }} />Enregistrement…</> : peut("roles","manage") ? "💾 Enregistrer" : "🔒 Enregistrer"}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  };

                  return (
                    <div>
                      {/* Sous-onglets centres */}
                      <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:20 }}>
                        {CENTRES_CONFIG.map(c => {
                          const grp = grouped[c.nom] || [];
                          const dispoDuJour = grp.filter(a => {
                            const da = getDraftedA(a);
                            const j = da.jours_travail || ["lundi","mardi","mercredi","jeudi","vendredi"];
                            return da.actif && j.includes(jourCourant);
                          }).length;
                          const hasPendingDraft = grp.some(a => assistantesDrafts[a.id] && Object.keys(assistantesDrafts[a.id]).length > 0);
                          const isActive = centreTab === c.nom;
                          return (
                            <button key={c.nom} onClick={() => setCentreTab(c.nom)}
                              style={{
                                display:"flex", alignItems:"center", gap:6,
                                padding:"7px 13px", borderRadius:999, border:`2px solid ${isActive ? c.couleur : "#e5e7eb"}`,
                                background: isActive ? c.couleur : "#fff",
                                color: isActive ? "#fff" : "#374151",
                                fontWeight: isActive ? 800 : 600, fontSize:12,
                                cursor:"pointer", transition:"all .15s", position:"relative",
                              }}
                            >
                              {c.label || c.nom}
                              {/* badge nb assistantes */}
                              <span style={{ background: isActive ? "rgba(255,255,255,0.25)" : `${c.couleur}20`, color: isActive ? "#fff" : c.couleur, borderRadius:999, padding:"1px 7px", fontSize:11, fontWeight:800 }}>
                                {grp.length}
                              </span>
                              {/* point dispo */}
                              {grp.length > 0 && (
                                <span style={{ width:7, height:7, borderRadius:"50%", background: dispoDuJour > 0 ? "#22c55e" : "#e5e7eb", flexShrink:0 }} />
                              )}
                              {/* point draft non sauvegardé */}
                              {hasPendingDraft && (
                                <span style={{ position:"absolute", top:2, right:2, width:8, height:8, borderRadius:"50%", background:"#f59e0b", border:"1.5px solid #fff" }} />
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* Infos centre actif */}
                      {assistantesLoading ? (
                        <div style={{ textAlign:"center", padding:40, color:"#94a3b8" }}>Chargement…</div>
                      ) : liste.length === 0 ? (
                        <div style={{ padding:"24px", borderRadius:12, border:"1.5px dashed #e2e8f0", background:"#fafafa", color:"#94a3b8", fontSize:13, textAlign:"center" }}>
                          {cfg.nom === "__sans_centre"
                            ? <>💻 Aucune assistante en ligne pour l'instant.<br/><span style={{ fontSize:11 }}>Les assistantes commerciales créées avec le type "En ligne" apparaissent ici.</span></>
                            : <>Aucune assistante assignée au centre <strong>{cfg.label || cfg.nom}</strong>.<br/><span style={{ fontSize:11 }}>Vérifiez que les assistantes ont un <code>centre_id</code> correct dans Supabase.</span></>
                          }
                        </div>
                      ) : (
                        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                          {liste.map(a => renderCard(a))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                <p style={{ marginTop:24, fontSize:11, color:"#94a3b8", textAlign:"center" }}>
                  🔒 La gestion complète du planning (horaires, congés, rotations) sera transférée vers le service RH.
                </p>
              </div>
            );
          })()}

        <FooterSaaS couleurTheme={BET_COLOR} />
      </div>
    </div>

    {/* ── Modal détail profil Administration BET ── */}
    {adminDetail && (
      <ModalDetailAdmin
        profil={adminDetail}
        onFermer={() => setAdminDetail(null)}
        onProfilUpdate={(updated) => {
          setUsers(prev => prev.map(u => u.id === updated.id ? { ...u, ...updated } : u));
          setAdminDetail(updated);
        }}
      />
    )}

    {/* ── Modal détail audit - rendu via portal pour éviter les contraintes overflow/transform ── */}
    {auditDetailLog && createPortal((() => {
      const log = auditDetailLog;
      const M_STATUT = { success:{bg:"#d1fae5",color:"#065f46"}, warning:{bg:"#fef9c3",color:"#854d0e"}, danger:{bg:"#fee2e2",color:"#dc2626"}, info:{bg:"#dbeafe",color:"#1d4ed8"} };
      const M_ICONS  = { auth:"🔐", admin:"🔧", paiements:"💳", boutique:"🛍️", inscriptions:"📋", leads:"📞", devis:"📄", blog:"📝", temoignages:"⭐", sondage:"📊", coachs:"🎓", centres:"📍", system:"⚙️", cinetpay:"💳", parcours:"🎯" };
      const sm = M_STATUT[log.statut] || M_STATUT.info;
      const initiales = (log.acteur_nom||"??").split(" ").map(x=>x[0]||"").join("").toUpperCase().slice(0,2);
      const meta = log.metadata && typeof log.metadata === "object" ? log.metadata : {};
      const dateStr = log.created_at ? new Date(log.created_at).toLocaleString("fr-FR",{weekday:"long",day:"2-digit",month:"long",year:"numeric",hour:"2-digit",minute:"2-digit",second:"2-digit"}) : "-";
      const close = () => setAuditDetailLog(null);
      const Sec = ({ title, children }) => (
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:10, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:8 }}>{title}</div>
          <div style={{ background:"#f8fafc", borderRadius:10, padding:"12px 14px", border:"1px solid #e2e8f0" }}>{children}</div>
        </div>
      );
      const Row = ({ label, value, mono }) => value ? (
        <div style={{ display:"flex", gap:8, marginBottom:6, alignItems:"flex-start" }}>
          <span style={{ fontSize:11, color:"#64748b", minWidth:130, flexShrink:0 }}>{label}</span>
          <span style={{ fontSize:11, color:"#0f172a", fontWeight:600, fontFamily:mono?"monospace":"inherit", wordBreak:"break-all" }}>{value}</span>
        </div>
      ) : null;
      return (
        <ModaleAnimee ouvert
          onClicVoile={close}
          style={{ position:"fixed", inset:0, background:"rgba(15,23,42,0.6)", zIndex:99999, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
          panneauStyle={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:"90vw", maxHeight:"90vh", overflowY:"auto", boxShadow:"0 24px 60px rgba(0,0,0,0.25)" }}>
            {/* Header sticky */}
            <div style={{ padding:"20px 24px 16px", borderBottom:"1px solid #f1f5f9", display:"flex", alignItems:"center", gap:14, position:"sticky", top:0, background:"#fff", zIndex:1, borderRadius:"16px 16px 0 0" }}>
              <div style={{ width:44, height:44, borderRadius:"50%", background:sm.bg, color:sm.color, fontWeight:800, fontSize:14, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{initiales}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, fontSize:15, color:"#0f172a" }}>{log.acteur_nom||"Système"}</div>
                <div style={{ fontSize:11, color:"#64748b" }}>{log.acteur_role||"-"}{log.acteur_email ? ` · ${log.acteur_email}` : ""}</div>
              </div>
              <span style={{ padding:"4px 12px", borderRadius:20, fontSize:11, fontWeight:700, background:sm.bg, color:sm.color }}>{log.statut?.toUpperCase()}</span>
              <button onClick={close} style={{ marginLeft:8, background:"none", border:"none", fontSize:22, cursor:"pointer", color:"#94a3b8", lineHeight:1, padding:"0 4px" }}>✕</button>
            </div>
            {/* Corps */}
            <div style={{ padding:"20px 24px" }}>
              {/* Badges */}
              <div style={{ display:"flex", gap:8, marginBottom:18, flexWrap:"wrap" }}>
                <span style={{ padding:"5px 14px", borderRadius:8, fontSize:12, fontWeight:700, background:sm.bg, color:sm.color }}>{log.action_type?.replace(/_/g," ")||"-"}</span>
                <span style={{ padding:"5px 14px", borderRadius:8, fontSize:12, fontWeight:600, background:"#f1f5f9", color:"#475569" }}>{M_ICONS[log.module]||"📦"} {log.module||"-"}</span>
                {log.centre && <span style={{ padding:"5px 14px", borderRadius:8, fontSize:12, fontWeight:600, background:"#f0fdf4", color:"#166534" }}>📍 {log.centre}</span>}
              </div>
              <Sec title="Description de l'action">
                <p style={{ margin:0, fontSize:13, color:"#1e293b", lineHeight:1.7 }}>{log.detail||"Aucun détail disponible"}</p>
              </Sec>
              <Sec title="Auteur de l'action">
                <Row label="Nom"   value={log.acteur_nom} />
                <Row label="Email" value={log.acteur_email} />
                <Row label="Rôle"  value={log.acteur_role} />
                <Row label="ID"    value={log.acteur_id} mono />
              </Sec>
              {(log.entite_nom||log.entite_type||log.entite_id) && (
                <Sec title="Élément ciblé">
                  {log.entite_nom && <Row label="Nom" value={log.entite_nom} />}
                  <Row label="Type" value={log.entite_type} />
                  <Row label="ID"   value={log.entite_id} mono />
                </Sec>
              )}
              {Object.keys(meta).length > 0 && (
                <Sec title="Données supplémentaires">
                  {meta.body_keys?.length > 0 && <Row label="Champs modifiés"  value={meta.body_keys.join(", ")} />}
                  {meta.prospect_email       && <Row label="Email prospect"    value={meta.prospect_email} />}
                  {meta.type_cours           && <Row label="Type de cours"     value={meta.type_cours} />}
                  {meta.champs?.length > 0   && <Row label="Champs"            value={meta.champs.join(", ")} />}
                  {meta.params && Object.keys(meta.params).length > 0 && <Row label="Paramètres URL" value={JSON.stringify(meta.params)} mono />}
                  {Object.entries(meta).filter(([k])=>!["body_keys","prospect_email","type_cours","champs","params"].includes(k)).map(([k,v])=>(
                    <Row key={k} label={k} value={typeof v==="object"?JSON.stringify(v):String(v)} mono={typeof v==="object"} />
                  ))}
                </Sec>
              )}
              <Sec title="Informations techniques">
                <Row label="Date exacte" value={dateStr} />
                <Row label="Adresse IP"  value={log.ip_address} mono />
                <Row label="Navigateur"  value={log.user_agent} />
                <Row label="ID du log"   value={log.id} mono />
              </Sec>
            </div>
        </ModaleAnimee>
      );
    })(), document.body)}
    </>
  );
}

const btnPrimary = { padding:"9px 16px", background:BET_COLOR, color:"#fff", border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:12 };
const btnSecondary = { padding:"9px 16px", background:"#e5e7eb", color:"#374151", border:"none", borderRadius:6, cursor:"pointer", fontWeight:600, fontSize:12 };